function safeSetGridOrdersFromResponse(r, setOrdersFn) {
  const arr =
    r?.orders ??
    r?.data?.orders ??
    r?.grid?.orders ??
    r?.gridMeta?.orders ??
    r?.data?.grid?.orders ??
    r?.data?.gridMeta?.orders;
  if (Array.isArray(arr) && typeof setOrdersFn === "function") {
    setOrdersFn(arr);
  }
}

function getGridOrdersFromResponse(r) {
  return (
    r?.orders ??
    r?.data?.orders ??
    r?.grid?.orders ??
    r?.gridMeta?.orders ??
    r?.data?.grid?.orders ??
    r?.data?.gridMeta?.orders ??
    null
  );
}

function getGridSingleOrderFromResponse(r) {
  return (
    r?.order ??
    r?.data?.order ??
    r?.grid?.order ??
    r?.gridMeta?.order ??
    r?.data?.grid?.order ??
    r?.data?.gridMeta?.order ??
    null
  );
}

function getGridMetaFromResponse(r, fallback = {}) {
  const gm =
    r?.gridMeta ??
    r?.grid_meta ??
    r?.grid ??
    r?.data?.gridMeta ??
    r?.data?.grid_meta ??
    r?.data?.grid ??
    {};

  return {
    tick:
      gm?.current_tick ??
      gm?.tick ??
      r?.data?.gridMeta?.current_tick ??
      r?.data?.grid_meta?.current_tick ??
      r?.data?.grid?.current_tick ??
      r?.data?.gridMeta?.tick ??
      r?.data?.grid_meta?.tick ??
      r?.data?.grid?.tick ??
      r?.data?.tick ??
      r?.tick ??
      fallback?.tick ??
      null,
    price:
      r?.price ??
      r?.data?.price ??
      gm?.price ??
      gm?.current_price ??
      gm?.last_price ??
      fallback?.price ??
      null,
    gridItemId:
      r?.gridItemId ??
      r?.itemId ??
      r?.item ??
      gm?.gridItemId ??
      gm?.itemId ??
      gm?.item ??
      gm?.id ??
      r?.data?.gridItemId ??
      r?.data?.itemId ??
      r?.data?.item ??
      fallback?.gridItemId ??
      fallback?.itemId ??
      fallback?.item ??
      fallback?.id ??
      null,
  };
}

function getGridVaultStatsFromResponse(r, fallback = {}) {
  const data = r?.data ?? {};
  const vaultTotal =
    r?.vault_total ??
    r?.vaultTotal ??
    data?.vault_total ??
    data?.vaultTotal ??
    fallback?.vault_total ??
    fallback?.vault ??
    0;
  const reserved =
    r?.reserved ??
    data?.reserved ??
    fallback?.reserved ??
    0;
  const free =
    r?.free ??
    data?.free ??
    fallback?.free ??
    Math.max(0, Number(vaultTotal || 0) - Number(reserved || 0));

  return {
    vault: Number(vaultTotal) || 0,
    reserved: Number(reserved) || 0,
    free: Number(free) || 0,
  };
}

// -------------------------
// Nexus Demo / Live Network Support
// -------------------------
const DEMO_ALL_EVM_ENABLED = true;

const ENABLE_VAULT_SUBSCRIBE = false; // Set true when vault subscription is ready and audited.

const LIVE_ENABLED_CHAINS = ["ETH", "BNB", "POL", "BASE", "ARB"];
const PRIMARY_EVM_CHAINS = ["ETH", "BNB", "POL", "BASE", "ARB"];
/** Native gas symbol per chain key — used by Withdraw for every live/future EVM vault. */
const NATIVE_SYMBOL_BY_CHAIN = {
  ETH: "ETH", ETHEREUM: "ETH",
  BNB: "BNB", BSC: "BNB",
  POL: "POL", POLYGON: "POL", MATIC: "POL",
  BASE: "ETH", ARB: "ETH", ARBITRUM: "ETH", OP: "ETH", OPTIMISM: "ETH",
  AVAX: "AVAX", AVALANCHE: "AVAX",
  FTM: "FTM", FANTOM: "FTM",
  LINEA: "ETH", SCROLL: "ETH", ZKSYNC: "ETH",
};
/** Wrapped native ERC-20 per chain — always queried, only shown if balance > 0. */
const WNATIVE_BY_CHAIN = {
  ETH: { address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", symbol: "WETH", name: "Wrapped Ether" },
  ETHEREUM: { address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", symbol: "WETH", name: "Wrapped Ether" },
  BNB: { address: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c", symbol: "WBNB", name: "Wrapped BNB" },
  BSC: { address: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c", symbol: "WBNB", name: "Wrapped BNB" },
  POL: { address: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270", symbol: "WPOL", name: "Wrapped POL" },
  POLYGON: { address: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270", symbol: "WPOL", name: "Wrapped POL" },
  MATIC: { address: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270", symbol: "WPOL", name: "Wrapped POL" },
  BASE: { address: "0x4200000000000000000000000000000000000006", symbol: "WETH", name: "Wrapped Ether" },
  ARB: { address: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1", symbol: "WETH", name: "Wrapped Ether" },
  ARBITRUM: { address: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1", symbol: "WETH", name: "Wrapped Ether" },
  OP: { address: "0x4200000000000000000000000000000000000006", symbol: "WETH", name: "Wrapped Ether" },
  OPTIMISM: { address: "0x4200000000000000000000000000000000000006", symbol: "WETH", name: "Wrapped Ether" },
  AVAX: { address: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7", symbol: "WAVAX", name: "Wrapped AVAX" },
  AVALANCHE: { address: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7", symbol: "WAVAX", name: "Wrapped AVAX" },
  LINEA: { address: "0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f", symbol: "WETH", name: "Wrapped Ether" },
  SCROLL: { address: "0x5300000000000000000000000000000000000004", symbol: "WETH", name: "Wrapped Ether" },
  ZKSYNC: { address: "0x5AEa5775959fBC2557Cc8789bC1bf90A239D9a91", symbol: "WETH", name: "Wrapped Ether" },
};
function wnativeSpecForChain(chainKey) {
  const k = String(chainKey || "").toUpperCase().trim();
  return WNATIVE_BY_CHAIN[k] || null;
}

function nativeSymbolForChain(chainKey) {
  const k = String(chainKey || "").toUpperCase().trim();
  if (!k) return "";
  if (NATIVE_SYMBOL_BY_CHAIN[k]) return NATIVE_SYMBOL_BY_CHAIN[k];
  // Fallback: treat the chain key itself as the native ticker when unknown
  return k;
}


const DEMO_MODE_NOTICE =
  "Demo Mode: All EVM networks can be simulated with real market data. Live execution is currently limited to ETH, BNB and POL.";

function getChainExecutionMode(chainKey, isLiveMode) {
  const ck = String(chainKey || "").toUpperCase();

  if (!isLiveMode) {
    return {
      mode: "demo",
      allowed: true,
      note: DEMO_MODE_NOTICE,
    };
  }

  if (LIVE_ENABLED_CHAINS.includes(ck)) {
    return {
      mode: "live",
      allowed: true,
      note: "Live execution enabled.",
    };
  }

  return {
    mode: "simulation_only",
    allowed: false,
    note: "Live execution for this chain is not active yet. Simulation only.",
  };
}

// -------------------------
// End Nexus Demo / Live Support
// -------------------------

import { Buffer } from "buffer";

if (typeof window !== "undefined") {
  window.Buffer = window.Buffer || Buffer;
}

function loadSetLS(key) {
  try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); }
  catch { return new Set(); }
}
function saveSetLS(key, setVal) {
  localStorage.setItem(key, JSON.stringify(Array.from(setVal)));
}

function tB(de, en) {
  // simple bilingual label: shows DE + EN (user requested both)
  if (!de) return en || "";
  if (!en) return de || "";
  return `${de} / ${en}`;
}

function _cleanAiInsightSentence(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .replace(/\b(?:you should|consider buying|buy now|sell now|enter now|exit now)\b/gi, "")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}


  const aiRiskTone = (level) => {
    const v = String(level || "").toLowerCase();
    if (v.includes("high")) return { border: "rgba(255,82,82,0.45)", bg: "rgba(255,82,82,0.10)", color: "#ff6b6b", label: "HIGH RISK" };
    if (v.includes("medium")) return { border: "rgba(255,193,7,0.45)", bg: "rgba(255,193,7,0.10)", color: "#ffc107", label: "WATCH" };
    return { border: "rgba(0,255,136,0.35)", bg: "rgba(0,255,136,0.08)", color: "#21d07a", label: "CONTROLLED" };
  };

  const aiConfidenceTone = (value) => {
    const n = Number(value || 0);
    if (n >= 8) return { color: "#21d07a", label: "HIGH" };
    if (n >= 6) return { color: "#ffc107", label: "MEDIUM" };
    return { color: "#ff6b6b", label: "LOW" };
  };

  const aiTagLabel = (tag) =>
    String(tag || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());


function _cleanAiCardText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

function _pickAiSectionValue(source, keys = []) {
  if (!source || typeof source !== "object") return "";
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return _cleanAiCardText(value);
  }
  return "";
}

function _parseAiConclusionSections(aiExplainData = {}) {
  const structured =
    aiExplainData.analysis ||
    aiExplainData.market_intelligence ||
    aiExplainData.marketIntelligence ||
    aiExplainData.sections ||
    aiExplainData.ai_sections ||
    {};

  const base = {
    market_structure: _pickAiSectionValue(aiExplainData, ["market_structure", "marketStructure"]) || _pickAiSectionValue(structured, ["market_structure", "marketStructure", "structure"]),
    liquidity_state: _pickAiSectionValue(aiExplainData, ["liquidity_state", "liquidityState"]) || _pickAiSectionValue(structured, ["liquidity_state", "liquidityState", "liquidity"]),
    warnings: _pickAiSectionValue(aiExplainData, ["warnings", "liquidity_warnings", "liquidityWarnings"]) || _pickAiSectionValue(structured, ["warnings", "liquidity_warnings", "liquidityWarnings"]),
    risk_posture: _pickAiSectionValue(aiExplainData, ["risk_posture", "riskPosture"]) || _pickAiSectionValue(structured, ["risk_posture", "riskPosture", "risk"]),
    pair_relationship: _pickAiSectionValue(aiExplainData, ["pair_relationship", "pairRelationship"]) || _pickAiSectionValue(structured, ["pair_relationship", "pairRelationship", "relationship"]),
    tactical_read: _pickAiSectionValue(aiExplainData, ["tactical_read", "tacticalRead"]) || _pickAiSectionValue(structured, ["tactical_read", "tacticalRead", "tactical"]),
    invalidations: _pickAiSectionValue(aiExplainData, ["invalidations", "invalidation"]) || _pickAiSectionValue(structured, ["invalidations", "invalidation"]),
    edge: _pickAiSectionValue(aiExplainData, ["edge"]) || _pickAiSectionValue(structured, ["edge"]),
    setup_bias: _pickAiSectionValue(aiExplainData, ["setup_bias", "setupBias"]) || _pickAiSectionValue(structured, ["setup_bias", "setupBias"]),
    signal_context: _pickAiSectionValue(aiExplainData, ["signal_context", "signalContext"]) || _pickAiSectionValue(structured, ["signal_context", "signalContext"]),
  };

  const raw = String(aiExplainData.verdictText || aiExplainData.ai_conclusion || aiExplainData.conclusion || "").trim();
  if (raw) {
    const labels = [
      ["market_structure", "Market Structure"],
      ["liquidity_state", "Liquidity State"],
      ["warnings", "Warnings"],
      ["risk_posture", "Risk Posture"],
      ["pair_relationship", "Pair Relationship"],
      ["tactical_read", "Tactical Read"],
      ["invalidations", "Invalidations"],
      ["edge", "Edge"],
      ["setup_bias", "Setup bias"],
      
    ];
    labels.forEach(([key, label], idx) => {
      if (base[key]) return;
      const nextLabels = labels.slice(idx + 1).map(([, l]) => l.replace(/[.*+?^${}()|[\]\\]/g, "\$&")).join("|");
      const re = nextLabels
        ? new RegExp(`${label}:\\s*([\\s\\S]*?)(?=\\s*(?:${nextLabels}):|$)`, "i")
        : new RegExp(`${label}:\\s*([\\s\\S]*)$`, "i");
      const m = raw.match(re);
      if (m && m[1]) base[key] = _cleanAiCardText(m[1]);
    });
  }

  return base;
}

function AiInsightCard({ title, value, tone = "neutral" }) {
  if (!value) return null;
  const tones = {
    structure: { border: "rgba(64,196,255,0.22)", bg: "rgba(64,196,255,0.055)", color: "#8bdcff" },
    liquidity: { border: "rgba(0,255,136,0.20)", bg: "rgba(0,255,136,0.050)", color: "#54f0a4" },
    warning: { border: "rgba(255,193,7,0.30)", bg: "rgba(255,193,7,0.075)", color: "#ffd166" },
    risk: { border: "rgba(255,107,107,0.26)", bg: "rgba(255,107,107,0.060)", color: "#ff8a8a" },
    tactical: { border: "rgba(187,134,252,0.24)", bg: "rgba(187,134,252,0.055)", color: "#c9a7ff" },
    edge: { border: "rgba(0,255,200,0.22)", bg: "rgba(0,255,200,0.050)", color: "#70ffe0" },
    neutral: { border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.025)", color: "#d8fff1" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: "8px 10px", background: t.bg }}>
      <div className="muted tiny" style={{ color: t.color, fontWeight: 900, letterSpacing: 0.3, textTransform: "uppercase", marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.45, color: "rgba(235,255,247,0.96)" }}>{value}</div>
    </div>
  );
}


function buildCompactAiInsight({ backendText = "", trendStructure = "", momentumShift = "", insightSummary = "" }) {
  const raw = String(backendText || "").trim();

  // Important for AI Insight:
  // Do not cut the smart part away. The backend is now instructed to include
  // rating + community + on-chain context. Keeping up to 5 clean sentences
  // makes those signals visible without turning the card into a long report.
  const splitSentences = (value) =>
    String(value || "")
      .replace(/\n+/g, " ")
      .split(/(?<=[.!?])\s+/)
      .map(_cleanAiInsightSentence)
      .filter(Boolean);

  const backendSentences = splitSentences(raw)
    .filter((s) => s.length >= 16)
    .slice(0, 7);

  if (backendSentences.length >= 1) {
    return backendSentences.join(" ");
  }

  const parts = [];
  if (trendStructure) parts.push(_cleanAiInsightSentence(trendStructure));
  if (momentumShift) parts.push(_cleanAiInsightSentence(momentumShift));
  if (insightSummary) parts.push(_cleanAiInsightSentence(insightSummary));

  const unique = [];
  for (const part of parts) {
    const key = part.toLowerCase();
    if (!key || unique.some((x) => x.toLowerCase() === key)) continue;
    unique.push(part);
  }

  return unique.slice(0, 4).join(" ") || "The current structure is mixed and does not show a fully clear edge yet.";
}



const LS_WATCH_REMOVED = "nexus_watch_removed";

// BUILD432: Nexus must never open as an empty market for guests or brand-new wallets.
// These are presentation/onboarding defaults only. Once a wallet has a saved watchlist,
// that wallet's backend state is authoritative and every default coin remains removable.
const NEXUS_DEFAULT_WATCH_ITEMS = Object.freeze([
  Object.freeze({ symbol: "ETH", mode: "market", coingecko_id: "ethereum", id: "ethereum", name: "Ethereum" }),
  Object.freeze({ symbol: "BNB", mode: "market", coingecko_id: "binancecoin", id: "binancecoin", name: "BNB" }),
  Object.freeze({ symbol: "POL", mode: "market", coingecko_id: "polygon-ecosystem-token", id: "polygon-ecosystem-token", name: "POL" }),
  Object.freeze({ symbol: "LINK", mode: "market", coingecko_id: "chainlink", id: "chainlink", name: "Chainlink" }),
  Object.freeze({ symbol: "UNI", mode: "market", coingecko_id: "uniswap", id: "uniswap", name: "Uniswap" }),
]);
const NEXUS_DEFAULT_COMPARE_SYMBOLS = Object.freeze(NEXUS_DEFAULT_WATCH_ITEMS.map((x) => x.symbol));
const cloneNexusDefaultWatchItems = () => NEXUS_DEFAULT_WATCH_ITEMS.map((x) => ({ ...x }));

/** BUILD396: map internal/backend text to short user-facing copy. System Info stays raw. */
function toUserFacingMessage(raw, fallback = "") {
  if (raw && typeof raw === "object") {
    raw = raw.reason || raw.message || raw.detail || raw.error || raw.status || raw.text || "";
    if (raw && typeof raw === "object") raw = "";
  }
  const s = String(raw ?? "").trim();
  if (!s) return String(fallback || "");
  const lower = s.toLowerCase();
  // Already friendly short phrases
  if (/^(paused|waiting|watching|position active|scanning|running|stopped|closed)\b/i.test(s) && s.length < 80 && !/corevault|0x[a-f0-9]{8}|sessionof|rpc_|worker|privy_|gunicorn|ops_/i.test(s)) {
    return s;
  }
  // Known technical → friendly maps
  if (/no recoverable .+ corevault session|no_registered_session|no_active_session/i.test(s)) return "No open session.";
  if (/observation|earliestentry|observe.?window/i.test(s)) return "Observation window — entry not open yet.";
  if (/score_blocked|below entry/i.test(s)) return "Best candidate score is still below entry.";
  if (/chain_no_candidate|no tradable/i.test(s)) return "No tradable asset on this network yet.";
  if (/waiting_entry|\bwait\b|watching/i.test(s) && /entry|buy|candidate/i.test(s)) return "Watching market — no buy yet.";
  if (/submitting|execution|pending.?confirm/i.test(s)) return "Trade submitting — waiting for confirmation.";
  if (/position_active|\bhold\b/i.test(s) && /position|open/i.test(s)) return "Position active.";
  if (/user.?stop|stop.?exit|start.?closing|closing mode|finaliz/i.test(s)) return "Closing session…";
  if (/session_finalized|allocation released|stale_reservation/i.test(s)) return "Session finished.";
  if (/paused.?by.?user|setSessionPaused|user_paused/i.test(s)) return "Paused.";
  if (/insufficient.?free|insufficient_free_vault/i.test(s)) return "Not enough free balance in the vault. Deposit and try again.";
  if (/session_budget_limit|budget.*exceed/i.test(s)) return "Budget exceeds the vault session limit. Lower the amount.";
  if (/privy_live_execution_not_ready|privy_not_ready|privy_wallet/i.test(s)) return "Live execution is not ready yet. Check wallet connection.";
  if (/privy_insufficient_native_gas/i.test(s)) return "Not enough native token for gas on this network. Add a little BNB/ETH/POL and try again.";
  if (/privy_policy_denied/i.test(s)) return "Transaction blocked by wallet policy. Check Privy allow-list for the vault contract.";
  if (/privy_gas_allowance|gas required exceeds allowance/i.test(s)) return "Network rejected the transaction gas estimate. Retry in a moment; if it persists, check BNB balance and Privy wallet policy.";
  if (/privy_broadcast_failure|transaction_broadcast_failure/i.test(s)) return "Transaction could not be broadcast. Check network gas balance and try again.";
  if (/verified_trade_route|trade route/i.test(s)) return "A verified trade route is required on this network.";
  if (/corevault is not connected|not connected/i.test(s)) return "Vault is not connected on this network yet.";
  if (/rpc_|session_decode|trade_route_error|live_execution_disabled|settlement_low/i.test(s)) return "Temporary network issue — will retry automatically.";
  if (/worker tick|backend worker|reconcile|ops_|idempotenc|gunicorn|payload|request_body/i.test(s)) return "Working in the background.";
  if (/corevault|sessionof|onchain_session|tx_hash|pending_tx|0x[a-f0-9]{10,}/i.test(s)) return "Working in the background.";
  if (/tick_failed|worker_failed|start_failed|stop_failed/i.test(s)) return "Action could not be completed. Try again in a moment.";
  if (/aborted|aborterror|timeout/i.test(s)) return "Request timed out. Please try again.";
  // Strip long stack-ish or code-ish
  if (s.length > 160 || /traceback|exception|stack|typeerror|referenceerror/i.test(s)) {
    const short = s.replace(/\s+/g, " ").slice(0, 140);
    return String(fallback || "Something went wrong.") + (short ? " · " + short : "");
  }
  // Soft cleanup of underscores from enum-like tokens
  if (/^[A-Z0-9_]{3,}$/.test(s)) {
    const mapped = {
      IDLE: "Idle",
      WAIT: "Waiting",
      HOLD: "Position active",
      PAUSED: "Paused",
      RUNNING: "Running",
      STOPPING: "Stopping…",
      FINALIZED: "Finished",
      NO_REGISTERED_SESSION: "No open session",
      NO_ACTIVE_SESSION: "No open session",
      SESSION_FINALIZED: "Session finished",
      POSITION_ACTIVE: "Position active",
      WAITING_ENTRY: "Watching — no buy yet",
      SCORE_BLOCKED: "Score below entry",
    };
    if (mapped[s]) return mapped[s];
    return s.replaceAll("_", " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
  return s;
}


function isGateObject(v) {
  return !!(v && typeof v === "object" && !Array.isArray(v) && ("allowed" in v) && ("reason" in v) && (("sessionChain" in v) || ("resolvedChain" in v) || ("tokenContract" in v)));
}
function stripGateObjects(v) {
  if (isGateObject(v)) {
    const reason = v.reason || v.message || v.detail || "";
    return typeof reason === "object" ? "" : String(reason || "");
  }
  if (Array.isArray(v)) return v.map(stripGateObjects);
  if (v && typeof v === "object") {
    const out = Array.isArray(v) ? [] : { ...v };
    Object.keys(v).forEach((k) => { out[k] = stripGateObjects(v[k]); });
    return out;
  }
  return v;
}

function toUserFacingReason(raw) {
  return toUserFacingMessage(raw, "Watching market for entry.");
}



function _watchKeyFromItem(it) {
  const mode = String(it?.mode || "market").toLowerCase();
  if (mode === "dex") {
    const addr = String(it?.contract || it?.tokenAddress || "").toLowerCase();
    return `dex|${addr}`;
  }
  const sym = String(it?.symbol || "").toUpperCase();
  const cg = String(it?.coingecko_id || it?.id || "").toLowerCase();
  return `market|${sym}|${cg}`;
}
function _watchKeyFromRow(r) {
  const mode = String(r?.mode || "market").toLowerCase();
  if (mode === "dex") {
    const addr = String(r?.contract || r?.tokenAddress || "").toLowerCase();
    return `dex|${addr}`;
  }
  const sym = String(r?.symbol || "").toUpperCase();
  const cg = String(r?.coingecko_id || r?.id || "").toLowerCase();
  return `market|${sym}|${cg}`;
}
function _loadTombstones() {
  try { return JSON.parse(localStorage.getItem(LS_WATCH_REMOVED) || "{}") || {}; } catch { return {}; }
}
function _saveTombstones(obj) {
  try { localStorage.setItem(LS_WATCH_REMOVED, JSON.stringify(obj || {})); } catch {}
}
function _setTombstone(key) {
  if (!key) return;
  const ts = _loadTombstones();
  ts[key] = Date.now();
  _saveTombstones(ts);
}
function _clearTombstone(key) {
  if (!key) return;
  const ts = _loadTombstones();
  if (ts[key] != null) {
    delete ts[key];
    _saveTombstones(ts);
  }
}



import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as JsxRuntime from "react/jsx-runtime";
(function installNexusJsxGuard() {
  const fix = (c) => {
    if (c && typeof c === "object" && !Array.isArray(c) && !(c && c.$$typeof) && ("allowed" in c) && ("reason" in c)) {
      const r = c.reason;
      return typeof r === "string" ? r : "blocked";
    }
    if (Array.isArray(c)) return c.map(fix);
    return c;
  };
  const wrap = (fn) => {
    if (typeof fn !== "function") return fn;
    return function(type, props, key) {
      if (props && props.children != null) {
        props = { ...props, children: fix(props.children) };
      }
      return fn(type, props, key);
    };
  };
  try {
    JsxRuntime.jsx = wrap(JsxRuntime.jsx);
    JsxRuntime.jsxs = wrap(JsxRuntime.jsxs);
    JsxRuntime.jsxDEV = wrap(JsxRuntime.jsxDEV);
  } catch (_e) {}
})();

(function installNexusSafeChildren() {
  if (React.__nexusSafeChildren) return;
  const orig = React.createElement.bind(React);
  const fixChild = (c) => {
    if (c && typeof c === "object" && !Array.isArray(c) && !React.isValidElement(c)) {
      if ("$$typeof" in c) return c;
      if (c.allowed !== undefined && (c.reason !== undefined || c.sessionChain !== undefined || c.resolvedChain !== undefined)) {
        const r = c.reason || c.detail || c.message || "";
        return typeof r === "string" ? r : "blocked";
      }
      if (c.reason !== undefined && c.symbol !== undefined && (c.sessionChain !== undefined || c.tokenContract !== undefined)) {
        return String(c.reason || "");
      }
    }
    return c;
  };
  React.createElement = function(type, props, ...children) {
    const next = children.map((ch) => (Array.isArray(ch) ? ch.map(fixChild) : fixChild(ch)));
    return orig(type, props, ...next);
  };
  React.__nexusSafeChildren = true;
})();

import { usePrivy, useWallets, useSigners } from "@privy-io/react-auth";

import { Alchemy, Network, Utils } from "alchemy-sdk";

import "./App.css";

// Chart palette (20 colors). Kept inline (no external dep) to avoid runtime ReferenceError.
// Used for consistent compare/index chart series coloring.
const PALETTE20 = [
  "#22c55e", // green
  "#60a5fa", // blue
  "#f59e0b", // amber
  "#a78bfa", // violet
  "#f472b6", // pink
  "#34d399", // emerald
  "#fb7185", // rose
  "#38bdf8", // sky
  "#eab308", // yellow
  "#c084fc", // purple
  "#14b8a6", // teal
  "#f97316", // orange
  "#818cf8", // indigo
  "#06b6d4", // cyan
  "#84cc16", // lime
  "#ef4444", // red
  "#10b981", // green-alt
  "#3b82f6", // blue-alt
  "#d946ef", // fuchsia
  "#facc15", // gold
];

// Local cache (stale-while-revalidate) so live refresh/cold-start won't blank the UI
const LS_WATCH_ROWS_CACHE = "na_watch_rows_cache_v1";
const LS_COMPARE_SERIES_CACHE = "na_compare_series_cache_v1";
const LS_APP_VERSION = "na_app_version";
const LS_COMPARE_STORE = "na_compare_store_v2";
const LS_GRID_COIN_PREFIX = "na_grid_coin";
const COMPARE_CACHE_TTL_MS = 20 * 60 * 1000; // 20 minutes
const COMPARE_CACHE_MAX_ENTRIES = 20;
const APP_VERSION = "2026-07-29-v5";
const FRONTEND_BUILD_ID = "F-2026.08.30-BUILD507-SYSINFO-STRIP";
/** Settlement / Grid payout: only USDC or USDT (token payout removed). */
const NEXUS_STABLE_PAYOUT_ASSETS = Object.freeze(["USDC", "USDT"]);
const normalizeStablePayoutAsset = (value, fallback = "USDC") => {
  let a = String(value || fallback || "USDC").toUpperCase().trim();
  if (a === "USDT.E" || a === "USDTE") a = "USDT";
  if (a === "USDC.E" || a === "USDCE") a = "USDC";
  return NEXUS_STABLE_PAYOUT_ASSETS.includes(a) ? a : (String(fallback || "USDC").toUpperCase() === "USDT" ? "USDT" : "USDC");
};
const CORE_VAULT_ETH_ADDRESS = "0xBFf20fe9c109C3E533C2549C50F617c4fA9e5Fb6";
const CORE_VAULT_BNB_ADDRESS = "0x5155214eeC9971F984dec1b01916967b2821f6fb";
const CORE_VAULT_POL_ADDRESS = "0x97aA0d7C3508620B5ad841d20eDFAd637Fc8DE9A";
const CORE_VAULT_ADDRESS_BY_CHAIN = { ETH: CORE_VAULT_ETH_ADDRESS, BNB: CORE_VAULT_BNB_ADDRESS, POL: CORE_VAULT_POL_ADDRESS, BASE: "", ARB: "" };
const ETH_USDC_ADDRESS = "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48";
const ETH_WETH_ADDRESS = "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2";
const UNISWAP_SWAP_ROUTER_02 = "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const UNISWAP_EXACT_INPUT_SINGLE_SELECTOR = "0x04e45aaf";
const ETH_USDC_WETH_BUY_ROUTE_ID = "0x4554485f555344435f574554485f425559000000000000000000000000000000";
const ETH_WETH_USDC_SELL_ROUTE_ID = "0x4554485f574554485f555344435f53454c4c0000000000000000000000000000";
const CORE_VAULT_OWNER_ADDRESS = "0x3318b6A608b3873962B17DAe069Fc7317D88d68f";
const NKR_MAX_ACTIVE_SESSIONS_LIMIT = null; // user-defined, no enforced hard cap
const AGGRESSIVE_WARNING_VERSION = "AGGRESSIVE_WARNING_V1";

function normalizeNkrChainKey(value) {
  const raw = String(value ?? "").trim().toUpperCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (["1", "ETH", "ETHEREUM", "ETHEREUM MAINNET"].includes(raw)) return "ETH";
  if (["56", "BNB", "BSC", "BNB CHAIN", "BINANCE SMART CHAIN"].includes(raw)) return "BNB";
  if (["137", "POL", "POLYGON", "MATIC", "POLYGON POS"].includes(raw)) return "POL";
  if (["8453", "BASE"].includes(raw)) return "BASE";
  if (["42161", "ARB", "ARBITRUM", "ARBITRUM ONE"].includes(raw)) return "ARB";
  return raw;
}

function nkrSessionControlKey(sessionOrChain, maybeOnchainId = "") {
  if (sessionOrChain && typeof sessionOrChain === "object") {
    const meta = sessionOrChain?.meta && typeof sessionOrChain.meta === "object" ? sessionOrChain.meta : {};
    const chain = normalizeNkrChainKey(sessionOrChain?.chain || sessionOrChain?.chainKey || meta?.chain || meta?.chain_key || sessionOrChain?.chainId || sessionOrChain?.chain_id || meta?.chain_id || "");
    const oid = String(sessionOrChain?.onchainSessionId ?? sessionOrChain?.onchain_session_id ?? sessionOrChain?.coreVaultSessionId ?? meta?.onchain_session_id ?? meta?.core_vault_session_id ?? "").trim();
    return `${chain}:${oid}`;
  }
  return `${normalizeNkrChainKey(sessionOrChain)}:${String(maybeOnchainId || "").trim()}`;
}

const API_BASE = ((import.meta.env.VITE_API_BASE ?? "").trim()) || (() => {
  // Default backend for production builds.
  // - In local dev (localhost), keep it empty so Vite proxy can handle /api/*.
  // - In production, route /api/* to the backend (Render) unless explicitly overridden by VITE_API_BASE.
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (["localhost", "127.0.0.1"].includes(host)) return "";
  return "https://nexus-analyt-pro.onrender.com";
})();
const ALCHEMY_KEY = (import.meta.env.VITE_ALCHEMY_KEY ?? "").trim();
// Canonical verified Ethereum NexusCoreVaultV3Slim ABI and NKR Liquidity Vault ABI.
const NEXUS_CORE_VAULT_ABI_VERSION = "NEXUS_CORE_MULTIVAULT_V5_NATIVE_FINAL_ABI_V1";
const NEXUS_CORE_VAULT_ABI = [{"inputs":[{"internalType":"address","name":"initialOwner","type":"address"},{"internalType":"address","name":"initialGuardian","type":"address"},{"internalType":"address","name":"initialRevenueWallet","type":"address"},{"internalType":"address","name":"initialLiquidityDestination","type":"address"}],"stateMutability":"nonpayable","type":"constructor"},{"inputs":[],"name":"EnforcedPause","type":"error"},{"inputs":[],"name":"ExpectedPause","type":"error"},{"inputs":[],"name":"InsufficientBalance","type":"error"},{"inputs":[],"name":"InvalidConfig","type":"error"},{"inputs":[],"name":"InvalidState","type":"error"},{"inputs":[],"name":"LimitExceeded","type":"error"},{"inputs":[{"internalType":"address","name":"owner","type":"address"}],"name":"OwnableInvalidOwner","type":"error"},{"inputs":[{"internalType":"address","name":"account","type":"address"}],"name":"OwnableUnauthorizedAccount","type":"error"},{"inputs":[],"name":"ReentrancyGuardReentrantCall","type":"error"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"SafeERC20FailedOperation","type":"error"},{"inputs":[],"name":"Unauthorized","type":"error"},{"inputs":[],"name":"UnsafeExecution","type":"error"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":true,"internalType":"address","name":"recipient","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"BaseWithdrawn","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"Deposited","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"guardian","type":"address"}],"name":"GuardianUpdated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"destination","type":"address"}],"name":"LiquidityDestinationUpdated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"tokenIn","type":"address"},{"indexed":true,"internalType":"address","name":"tokenOut","type":"address"},{"indexed":true,"internalType":"address","name":"router","type":"address"},{"indexed":false,"internalType":"uint256","name":"amountIn","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"amountOut","type":"uint256"}],"name":"LiquidityReserveSettled","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"loss","type":"uint256"}],"name":"LossRealized","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"recipient","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"NativeBaseWithdrawn","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"NativeDeposited","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":true,"internalType":"address","name":"recipient","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"OwnerReserveWithdrawn","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"previousOwner","type":"address"},{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}],"name":"OwnershipTransferStarted","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"previousOwner","type":"address"},{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}],"name":"OwnershipTransferred","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Paused","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"uint256","name":"grossProfitBatch","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"liquidityAmount","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"ownerAmount","type":"uint256"}],"name":"ProfitFeeAllocated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"grossProfit","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"netProfit","type":"uint256"}],"name":"ProfitRealized","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":true,"internalType":"address","name":"recipient","type":"address"},{"indexed":false,"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SystemId","name":"system","type":"uint8"},{"indexed":false,"internalType":"uint256","name":"gross","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"net","type":"uint256"}],"name":"ProfitWithdrawn","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"wallet","type":"address"}],"name":"RevenueWalletUpdated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"router","type":"address"},{"indexed":false,"internalType":"bool","name":"enabled","type":"bool"},{"indexed":true,"internalType":"address","name":"oracle","type":"address"}],"name":"RouterConfigured","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"router","type":"address"},{"indexed":true,"internalType":"bytes4","name":"selector","type":"bytes4"},{"indexed":false,"internalType":"bool","name":"allowed","type":"bool"}],"name":"RouterSelectorConfigured","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SystemId","name":"system","type":"uint8"},{"indexed":false,"internalType":"address","name":"settlementToken","type":"address"},{"indexed":false,"internalType":"uint256","name":"budget","type":"uint256"},{"indexed":false,"internalType":"uint64","name":"endsAt","type":"uint64"}],"name":"SessionCreated","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"returnedBase","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"securedProfit","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"realizedLoss","type":"uint256"}],"name":"SessionFinalized","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":false,"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SessionStatus","name":"status","type":"uint8"}],"name":"SessionStatusChanged","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":true,"internalType":"bytes32","name":"planId","type":"bytes32"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"},{"indexed":false,"internalType":"address","name":"revenueWallet","type":"address"}],"name":"SubscriptionPaid","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"bool","name":"depositEnabled","type":"bool"},{"indexed":false,"internalType":"bool","name":"withdrawEnabled","type":"bool"},{"indexed":false,"internalType":"bool","name":"executionEnabled","type":"bool"},{"indexed":false,"internalType":"bool","name":"paymentEnabled","type":"bool"}],"name":"TokenConfigured","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"sessionId","type":"uint256"},{"indexed":true,"internalType":"address","name":"router","type":"address"},{"indexed":true,"internalType":"address","name":"tokenIn","type":"address"},{"indexed":false,"internalType":"address","name":"tokenOut","type":"address"},{"indexed":false,"internalType":"uint256","name":"amountIn","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"amountOut","type":"uint256"}],"name":"TradeExecuted","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Unpaused","type":"event"},{"inputs":[],"name":"BPS","outputs":[{"internalType":"uint16","name":"","type":"uint16"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"LIQUIDITY_SHARE_BPS","outputs":[{"internalType":"uint16","name":"","type":"uint16"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"MAX_SESSION_DURATION","outputs":[{"internalType":"uint32","name":"","type":"uint32"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"MAX_SLIPPAGE_BPS","outputs":[{"internalType":"uint16","name":"","type":"uint16"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"NATIVE_TOKEN","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"OWNER_SHARE_BPS","outputs":[{"internalType":"uint16","name":"","type":"uint16"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"PROFIT_FEE_BPS","outputs":[{"internalType":"uint16","name":"","type":"uint16"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"acceptOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"user","type":"address"},{"internalType":"address","name":"token","type":"address"}],"name":"accountOf","outputs":[{"components":[{"internalType":"uint128","name":"baseCapital","type":"uint128"},{"internalType":"uint128","name":"securedNkrProfit","type":"uint128"},{"internalType":"uint128","name":"securedTraderProfit","type":"uint128"},{"internalType":"uint128","name":"securedGridProfit","type":"uint128"},{"internalType":"uint128","name":"allocatedNkr","type":"uint128"},{"internalType":"uint128","name":"allocatedTrader","type":"uint128"},{"internalType":"uint128","name":"allocatedGrid","type":"uint128"},{"internalType":"uint128","name":"lifetimeRealizedProfit","type":"uint128"},{"internalType":"uint128","name":"unchargedProfit","type":"uint128"},{"internalType":"bool","name":"feeActivated","type":"bool"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.Account","name":"","type":"tuple"}],"stateMutability":"view","type":"function"},{"inputs":[{"components":[{"internalType":"bool","name":"configured","type":"bool"},{"internalType":"bool","name":"depositEnabled","type":"bool"},{"internalType":"bool","name":"withdrawEnabled","type":"bool"},{"internalType":"bool","name":"executionEnabled","type":"bool"},{"internalType":"bool","name":"paymentEnabled","type":"bool"},{"internalType":"bool","name":"settlementToken","type":"bool"},{"internalType":"uint8","name":"decimals","type":"uint8"},{"internalType":"uint128","name":"maxSingleDeposit","type":"uint128"},{"internalType":"uint128","name":"maxSessionBudget","type":"uint128"},{"internalType":"uint128","name":"profitThreshold","type":"uint128"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.TokenConfig","name":"cfg","type":"tuple"}],"name":"configureNative","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"router","type":"address"},{"components":[{"internalType":"bool","name":"enabled","type":"bool"},{"internalType":"address","name":"oracle","type":"address"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.RouterConfig","name":"cfg","type":"tuple"}],"name":"configureRouter","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"router","type":"address"},{"internalType":"bytes4","name":"selector","type":"bytes4"},{"internalType":"bool","name":"allowed","type":"bool"}],"name":"configureRouterSelector","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"components":[{"internalType":"bool","name":"configured","type":"bool"},{"internalType":"bool","name":"depositEnabled","type":"bool"},{"internalType":"bool","name":"withdrawEnabled","type":"bool"},{"internalType":"bool","name":"executionEnabled","type":"bool"},{"internalType":"bool","name":"paymentEnabled","type":"bool"},{"internalType":"bool","name":"settlementToken","type":"bool"},{"internalType":"uint8","name":"decimals","type":"uint8"},{"internalType":"uint128","name":"maxSingleDeposit","type":"uint128"},{"internalType":"uint128","name":"maxSessionBudget","type":"uint128"},{"internalType":"uint128","name":"profitThreshold","type":"uint128"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.TokenConfig","name":"cfg","type":"tuple"}],"name":"configureToken","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"components":[{"internalType":"address","name":"settlementToken","type":"address"},{"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SystemId","name":"system","type":"uint8"},{"internalType":"uint128","name":"budget","type":"uint128"},{"internalType":"uint32","name":"duration","type":"uint32"},{"internalType":"uint16","name":"maxSlippageBps","type":"uint16"},{"internalType":"uint16","name":"maxLossBps","type":"uint16"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.SessionRequest","name":"r","type":"tuple"}],"name":"createSession","outputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"deposit","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"depositNative","outputs":[],"stateMutability":"payable","type":"function"},{"inputs":[{"internalType":"address","name":"router","type":"address"}],"name":"disableRouter","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"disableToken","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"components":[{"internalType":"uint256","name":"sessionId","type":"uint256"},{"internalType":"address","name":"router","type":"address"},{"internalType":"address","name":"tokenIn","type":"address"},{"internalType":"address","name":"tokenOut","type":"address"},{"internalType":"uint256","name":"amountIn","type":"uint256"},{"internalType":"uint256","name":"minAmountOut","type":"uint256"},{"internalType":"uint256","name":"deadline","type":"uint256"},{"internalType":"bytes","name":"data","type":"bytes"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.TradeRequest","name":"t","type":"tuple"}],"name":"executeTrade","outputs":[{"internalType":"uint256","name":"amountOut","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"}],"name":"finalizeSession","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"flushLiquidityReserve","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"user","type":"address"},{"internalType":"address","name":"token","type":"address"}],"name":"freeBase","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"guardian","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"liquidityDestination","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"liquidityReserve","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"nextSessionId","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"owner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"ownerReserve","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"pause","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"paused","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"bytes32","name":"planId","type":"bytes32"}],"name":"paySubscription","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"bytes32","name":"planId","type":"bytes32"}],"name":"paySubscriptionNative","outputs":[],"stateMutability":"payable","type":"function"},{"inputs":[],"name":"pendingOwner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"},{"internalType":"address","name":"token","type":"address"}],"name":"positionOf","outputs":[{"components":[{"internalType":"uint128","name":"amount","type":"uint128"},{"internalType":"uint128","name":"costBasis","type":"uint128"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.Position","name":"","type":"tuple"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"renounceOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"revenueWallet","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"routerConfig","outputs":[{"internalType":"bool","name":"enabled","type":"bool"},{"internalType":"address","name":"oracle","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"},{"internalType":"bytes4","name":"","type":"bytes4"}],"name":"routerSelectorAllowed","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"}],"name":"sessionOf","outputs":[{"components":[{"internalType":"address","name":"owner","type":"address"},{"internalType":"address","name":"settlementToken","type":"address"},{"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SystemId","name":"system","type":"uint8"},{"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SessionStatus","name":"status","type":"uint8"},{"internalType":"uint64","name":"startsAt","type":"uint64"},{"internalType":"uint64","name":"endsAt","type":"uint64"},{"internalType":"uint16","name":"maxSlippageBps","type":"uint16"},{"internalType":"uint16","name":"maxLossBps","type":"uint16"},{"internalType":"uint128","name":"budget","type":"uint128"},{"internalType":"uint128","name":"settlementCash","type":"uint128"},{"internalType":"uint128","name":"realizedProfit","type":"uint128"},{"internalType":"uint128","name":"realizedLoss","type":"uint128"},{"internalType":"uint16","name":"openAssetCount","type":"uint16"}],"internalType":"struct NexusCoreMultiVaultV5_Native_Final.Session","name":"","type":"tuple"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"value","type":"address"}],"name":"setGuardian","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"value","type":"address"}],"name":"setLiquidityDestination","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"value","type":"address"}],"name":"setRevenueWallet","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"},{"internalType":"bool","name":"value","type":"bool"}],"name":"setSessionPaused","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"bytes32","name":"planId","type":"bytes32"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"setSubscriptionPrice","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"tokenIn","type":"address"},{"internalType":"address","name":"tokenOut","type":"address"},{"internalType":"uint256","name":"amountIn","type":"uint256"},{"internalType":"uint256","name":"minAmountOut","type":"uint256"},{"internalType":"address","name":"router","type":"address"},{"internalType":"uint256","name":"deadline","type":"uint256"},{"internalType":"bytes","name":"data","type":"bytes"}],"name":"settleLiquidityReserve","outputs":[{"internalType":"uint256","name":"amountOut","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"solvency","outputs":[{"internalType":"uint256","name":"assets","type":"uint256"},{"internalType":"uint256","name":"obligations","type":"uint256"},{"internalType":"bool","name":"solvent","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"sessionId","type":"uint256"}],"name":"startClosing","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"},{"internalType":"bytes32","name":"","type":"bytes32"}],"name":"subscriptionPrice","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"tokenConfig","outputs":[{"internalType":"bool","name":"configured","type":"bool"},{"internalType":"bool","name":"depositEnabled","type":"bool"},{"internalType":"bool","name":"withdrawEnabled","type":"bool"},{"internalType":"bool","name":"executionEnabled","type":"bool"},{"internalType":"bool","name":"paymentEnabled","type":"bool"},{"internalType":"bool","name":"settlementToken","type":"bool"},{"internalType":"uint8","name":"decimals","type":"uint8"},{"internalType":"uint128","name":"maxSingleDeposit","type":"uint128"},{"internalType":"uint128","name":"maxSessionBudget","type":"uint128"},{"internalType":"uint128","name":"profitThreshold","type":"uint128"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"totalPositionCostBasis","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"totalUserLiability","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"newOwner","type":"address"}],"name":"transferOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"unpause","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"address","name":"recipient","type":"address"}],"name":"withdrawBase","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"address payable","name":"recipient","type":"address"}],"name":"withdrawNative","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"address","name":"recipient","type":"address"}],"name":"withdrawOwnerReserve","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"enum NexusCoreMultiVaultV5_Native_Final.SystemId","name":"system","type":"uint8"},{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"address","name":"recipient","type":"address"}],"name":"withdrawProfit","outputs":[],"stateMutability":"nonpayable","type":"function"},{"stateMutability":"payable","type":"receive"}];
const NEXUS_NKR_LIQUIDITY_VAULT_ABI_VERSION = "NEXUS_NKR_LIQUIDITY_VAULT_ABI_V1";
const NEXUS_NKR_LIQUIDITY_VAULT_ABI = [{"inputs":[{"internalType":"address","name":"initialOwner","type":"address"},{"internalType":"address","name":"initialOperator","type":"address"},{"internalType":"address","name":"nkrToken","type":"address"},{"internalType":"address","name":"wethToken","type":"address"},{"internalType":"address","name":"usdcToken","type":"address"},{"internalType":"address","name":"usdtToken","type":"address"},{"internalType":"address","name":"swapRouter","type":"address"},{"internalType":"address","name":"nftPositionManager","type":"address"},{"internalType":"uint24","name":"nkrWethPoolFee","type":"uint24"}],"stateMutability":"nonpayable","type":"constructor"},{"inputs":[],"name":"BelowThreshold","type":"error"},{"inputs":[],"name":"EnforcedPause","type":"error"},{"inputs":[],"name":"ExpectedPause","type":"error"},{"inputs":[],"name":"InvalidConfig","type":"error"},{"inputs":[],"name":"InvalidState","type":"error"},{"inputs":[{"internalType":"address","name":"owner","type":"address"}],"name":"OwnableInvalidOwner","type":"error"},{"inputs":[{"internalType":"address","name":"account","type":"address"}],"name":"OwnableUnauthorizedAccount","type":"error"},{"inputs":[],"name":"ProtectedAsset","type":"error"},{"inputs":[],"name":"ReentrancyGuardReentrantCall","type":"error"},{"inputs":[{"internalType":"address","name":"token","type":"address"}],"name":"SafeERC20FailedOperation","type":"error"},{"inputs":[],"name":"Unauthorized","type":"error"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"ethSide","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"buybackSide","type":"uint256"}],"name":"LiquidityFundingReceived","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"tokenId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"nkrAmount","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"wethAmount","type":"uint256"},{"indexed":false,"internalType":"uint128","name":"liquidity","type":"uint128"}],"name":"LockedPositionIncreased","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"tokenId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"nkrAmount","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"wethAmount","type":"uint256"},{"indexed":false,"internalType":"uint128","name":"liquidity","type":"uint128"}],"name":"LockedPositionMinted","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"uint256","name":"amountIn","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"nkrOut","type":"uint256"}],"name":"NkrBoughtBack","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"user","type":"address"},{"indexed":true,"internalType":"bytes32","name":"planId","type":"bytes32"},{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"NkrSubscriptionPaid","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"operator","type":"address"}],"name":"OperatorUpdated","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"uint256","name":"amount","type":"uint256"}],"name":"OriginalReserveFunded","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"previousOwner","type":"address"},{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}],"name":"OwnershipTransferStarted","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"previousOwner","type":"address"},{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}],"name":"OwnershipTransferred","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Paused","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"uint256","name":"tokenId","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"amount0","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"amount1","type":"uint256"}],"name":"PositionFeesCollected","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"token","type":"address"},{"indexed":false,"internalType":"uint256","name":"amountIn","type":"uint256"},{"indexed":false,"internalType":"uint256","name":"wethOut","type":"uint256"}],"name":"StableConvertedToWeth","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Unpaused","type":"event"},{"inputs":[],"name":"USD_100_E18","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"USD_50_E18","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"acceptOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amountIn","type":"uint256"},{"internalType":"uint256","name":"minWethOut","type":"uint256"},{"internalType":"uint256","name":"minNkrOut","type":"uint256"},{"internalType":"uint256","name":"deadline","type":"uint256"}],"name":"buyBackNkr","outputs":[{"internalType":"uint256","name":"amountOut","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"buybackStable","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"tokenId","type":"uint256"}],"name":"collectPositionFees","outputs":[{"internalType":"uint256","name":"amount0","type":"uint256"},{"internalType":"uint256","name":"amount1","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amountIn","type":"uint256"},{"internalType":"uint256","name":"minWethOut","type":"uint256"},{"internalType":"uint256","name":"deadline","type":"uint256"}],"name":"convertStableToWeth","outputs":[{"internalType":"uint256","name":"amountOut","type":"uint256"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"}],"name":"ethSideStable","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"fundOriginalReserve","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"tokenId","type":"uint256"},{"internalType":"uint256","name":"nkrDesired","type":"uint256"},{"internalType":"uint256","name":"wethDesired","type":"uint256"},{"internalType":"uint256","name":"nkrMin","type":"uint256"},{"internalType":"uint256","name":"wethMin","type":"uint256"},{"internalType":"uint256","name":"deadline","type":"uint256"}],"name":"increaseLockedPosition","outputs":[{"internalType":"uint128","name":"liquidity","type":"uint128"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"uint256","name":"","type":"uint256"}],"name":"lockedPosition","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"int24","name":"tickLower","type":"int24"},{"internalType":"int24","name":"tickUpper","type":"int24"},{"internalType":"uint256","name":"nkrDesired","type":"uint256"},{"internalType":"uint256","name":"wethDesired","type":"uint256"},{"internalType":"uint256","name":"nkrMin","type":"uint256"},{"internalType":"uint256","name":"wethMin","type":"uint256"},{"internalType":"uint256","name":"deadline","type":"uint256"}],"name":"mintLockedPosition","outputs":[{"internalType":"uint256","name":"tokenId","type":"uint256"},{"internalType":"uint128","name":"liquidity","type":"uint128"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"nkr","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"bytes32","name":"","type":"bytes32"}],"name":"nkrSubscriptionPrice","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"},{"internalType":"address","name":"","type":"address"},{"internalType":"uint256","name":"","type":"uint256"},{"internalType":"bytes","name":"","type":"bytes"}],"name":"onERC721Received","outputs":[{"internalType":"bytes4","name":"","type":"bytes4"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"operator","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"originalReserveFunded","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"originalReserveRemaining","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"owner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"pause","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"paused","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"bytes32","name":"planId","type":"bytes32"}],"name":"payNkrSubscription","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"pendingOwner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"poolFee","outputs":[{"internalType":"uint24","name":"","type":"uint24"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"positionIds","outputs":[{"internalType":"uint256[]","name":"","type":"uint256[]"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"positionManager","outputs":[{"internalType":"contract INonfungiblePositionManager","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"receiveLiquidityFunding","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"renounceOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"token","type":"address"},{"internalType":"address","name":"recipient","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"rescueUnsupportedToken","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"router","outputs":[{"internalType":"contract ISwapRouterV3","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"bytes32","name":"planId","type":"bytes32"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"setNkrSubscriptionPrice","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"value","type":"address"}],"name":"setOperator","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"tokenIn","type":"address"},{"internalType":"address","name":"tokenOut","type":"address"},{"internalType":"uint24","name":"fee","type":"uint24"}],"name":"setSwapFee","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"","type":"address"},{"internalType":"address","name":"","type":"address"}],"name":"swapFee","outputs":[{"internalType":"uint24","name":"","type":"uint24"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"newOwner","type":"address"}],"name":"transferOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"unpause","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"usdc","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"usdt","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"weth","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"}];
const NEXUS_EXECUTION_ENGINES = Object.freeze(["GRID", "NKR", "TRADER"]);

function getCoreVaultAbiFunction(name) {
  return NEXUS_CORE_VAULT_ABI.find((item) => item?.type === "function" && item?.name === name) || null;
}

async function fetchNexusAssetRouter() {
  return api(`/api/nexus/asset-router`);
}

async function validateNexusExecutionRoute({ engine, chain, symbol, token, wallet }) {
  const normalizedEngine = String(engine || "").toUpperCase() === "TRADING" ? "TRADER" : String(engine || "").toUpperCase();
  if (!NEXUS_EXECUTION_ENGINES.includes(normalizedEngine)) throw new Error("Unsupported Nexus execution engine.");
  return api(`/api/nexus/asset-route/validate`, {
    method: "POST", token, wallet,
    body: { engine: normalizedEngine, chain: String(chain || "").toUpperCase(), symbol: String(symbol || "").toUpperCase() },
  });
}

const TOKEN_WHITELIST = {
  ETH: [
    { symbol: "USDC", address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", decimals: 6 },
    { symbol: "USDT", address: "0xdAC17F958D2ee523a2206206994597C13D831ec7", decimals: 6 },
  ],
  POL: [
    // The deployed Polygon CoreVault V5 is configured for bridged Polygon USDC
    // (0x2791...). Keep native Circle USDC visible as a separate exact contract;
    // symbols are display-only and deposits are matched by contract address.
    { symbol: "USDC", address: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174", decimals: 6, name: "USDC (Polygon PoS)" },
    { symbol: "USDC_NATIVE", address: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359", decimals: 6, name: "USDC (Circle native)" },
    { symbol: "USDT", address: "0xC2132D05D31c914a87C6611C10748AEb04B58e8F", decimals: 6 },
  ],
  BNB: [
    // BNB Chain (BEP-20) pegged versions
    { symbol: "USDC", address: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d", decimals: 18 },
    { symbol: "USDT", address: "0x55d398326f99059fF775485246999027B3197955", decimals: 18 },
  ],
};
const getAssetNote = (coin) => {
  if (coin === "BTC") return "via WBTC (ETH) / BTCB (BNB)";
  if (coin === "SOL") return "via WSOL";
  return null;
};
const normalizeWalletChainKey = (chain) => {
  const raw = String(chain || "").trim().toUpperCase().replace(/^"|"$/g, "");
  const map = {
    ETH: "ETH",
    ETHEREUM: "ETH",
    1: "ETH",
    POL: "POL",
    POLYGON: "POL",
    MATIC: "POL",
    137: "POL",
    BNB: "BNB",
    BSC: "BNB",
    BINANCE: "BNB",
    56: "BNB",
  };
  return map[raw] || raw || "ETH";
};

const walletChainDisplayName = (chain) => {
  const c = normalizeWalletChainKey(chain);
  if (c === "ETH") return "Ethereum";
  if (c === "POL") return "Polygon";
  if (c === "BNB") return "BNB Chain";
  return c;
};

const getStableWhitelistForChain = (chain) => TOKEN_WHITELIST[normalizeWalletChainKey(chain)] || [];

const getTokenSpecKey = (t) => String(t?.address || "").toLowerCase();

const CHAIN_ID_BY_KEY = { ETH: 1, POL: 137, BNB: 56 };
const CHAIN_KEY_BY_ID = { 1: "ETH", 137: "POL", 56: "BNB" };

function decimalStringToUnits(value, decimals) {
  const dec = Number(decimals || 6);
  const s = String(value ?? "0").trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error("Invalid payment amount");
  const [whole, fracRaw = ""] = s.split(".");
  const frac = (fracRaw + "0".repeat(dec)).slice(0, dec);
  return BigInt(whole || "0") * (10n ** BigInt(dec)) + BigInt(frac || "0");
}

function getSubscribeTokenSpec(config, chainKey, symbol) {
  const c = normalizeWalletChainKey(chainKey);
  const sym = String(symbol || "").toUpperCase();
  const cfgToken = config?.tokens?.[c]?.[sym];
  if (cfgToken?.address) {
    return { symbol: sym, address: String(cfgToken.address), decimals: Number(cfgToken.decimals ?? 6) };
  }
  const local = (TOKEN_WHITELIST[c] || []).find((t) => String(t.symbol).toUpperCase() === sym);
  if (local?.address) return local;
  throw new Error(`${sym} is not supported on ${c}.`);
}

// ------------------------
// Alchemy (wallet balances)
// ------------------------
// You can override any URL via env vars:
//   VITE_ALCHEMY_RPC_ETH, VITE_ALCHEMY_RPC_POL, VITE_ALCHEMY_RPC_BNB
// Defaults below follow Alchemy's common network hostnames.
// IMPORTANT: Alchemy RPC auth is done via the /v2/<API_KEY> path.
// If you still get 401 "Must be authenticated", it usually means the key is not
// the *Alchemy API Key* (or it contains hidden whitespace) OR your env isn't loaded.
// In that case, copy the full HTTPS endpoint from Alchemy dashboard and set:
//   VITE_ALCHEMY_RPC_ETH / _POL / _BNB
// (those override everything below).
const ALCHEMY_RPC = {
  ETH:
    import.meta.env.VITE_ALCHEMY_RPC_ETH ||
    (ALCHEMY_KEY ? `https://eth-mainnet.g.alchemy.com/v2/${encodeURIComponent(ALCHEMY_KEY)}` : ""),
  POL:
    import.meta.env.VITE_ALCHEMY_RPC_POL ||
    (ALCHEMY_KEY ? `https://polygon-mainnet.g.alchemy.com/v2/${encodeURIComponent(ALCHEMY_KEY)}` : ""),
  BNB:
    import.meta.env.VITE_ALCHEMY_RPC_BNB ||
    (ALCHEMY_KEY ? `https://bnb-mainnet.g.alchemy.com/v2/${encodeURIComponent(ALCHEMY_KEY)}` : ""),
};

function maskAlchemyUrl(url) {
  if (!url) return "";
  // hide API key segment if present
  return url.replace(/\/v2\/[^/?#]+/i, "/v2/");
}

async function alchemyRpc(chainKey, method, params = []) {
  const url = ALCHEMY_RPC[chainKey];
  if (!url) throw new Error("Alchemy RPC URL missing. Set VITE_ALCHEMY_KEY or VITE_ALCHEMY_RPC_*.");

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: Date.now(), method, params }),
  });

  const text = await res.text().catch(() => "");
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    const hint = (json?.error?.message || json?.message || text || "").toString().slice(0, 180);
    throw new Error(`Alchemy HTTP ${res.status}${hint ? ": " + hint : ""}`);
  }

  if (json?.error) throw new Error(json.error?.message || "Alchemy error");
  return json?.result;
}

function hexToBigInt(hex) {
  try {
    return BigInt(hex || "0x0");
  } catch {
    return 0n;
  }
}

function formatNativeFromWei(weiBig, decimals = 18, maxFrac = 6) {
  // Convert BigInt wei to a decimal string without depending on big number libs.
  const base = 10n ** BigInt(decimals);
  const whole = weiBig / base;
  const frac = weiBig % base;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, maxFrac);
  return stripTrailingZeros(`${whole.toString()}.${fracStr}`);
}

// ------------------------
// utils
// ------------------------
function _cmpKey(symbols, tf) {
  const arr = Array.isArray(symbols) ? symbols : [];
  const keySyms = arr.map((s) => String(s || "").toUpperCase()).filter(Boolean).sort().join(",");
  return `${String(tf || "1Y")}:${keySyms}`;
}

function _cmpStoreRead() {
  try {
    const raw = localStorage.getItem(LS_COMPARE_STORE);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _cmpStoreWrite(store) {
  try { localStorage.setItem(LS_COMPARE_STORE, JSON.stringify(store || {})); } catch {}
}

function _cmpGetCached(symbols, tf) {
  try {
    const store = _cmpStoreRead();
    const k = _cmpKey(symbols, tf);
    const entry = store?.[k];
    if (!entry || !entry.ts || !entry.data) return null;
    if (Date.now() - Number(entry.ts) > COMPARE_CACHE_TTL_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function _cmpPutCached(symbols, tf, data) {
  try {
    const k = _cmpKey(symbols, tf);
    const store = _cmpStoreRead();
    store[k] = { ts: Date.now(), data };

    // trim oldest
    const keys = Object.keys(store || {});
    if (keys.length > COMPARE_CACHE_MAX_ENTRIES) {
      keys.sort((a, b) => (Number(store[a]?.ts || 0) - Number(store[b]?.ts || 0)));
      const toDel = keys.slice(0, Math.max(0, keys.length - COMPARE_CACHE_MAX_ENTRIES));
      for (const dk of toDel) delete store[dk];
    }
    _cmpStoreWrite(store);
  } catch {}
}

// CoinGecko search cache (client-side) to keep search snappy even if backend/CG is slow
const LS_CG_SEARCH_CACHE = "na_cg_search_cache_v1";
const CG_SEARCH_TTL_MS = 5 * 60 * 1000; // 5 minutes
const CG_SEARCH_MAX_ENTRIES = 40;

function _cgSearchRead() {
  try {
    const raw = localStorage.getItem(LS_CG_SEARCH_CACHE);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _cgSearchWrite(store) {
  try { localStorage.setItem(LS_CG_SEARCH_CACHE, JSON.stringify(store || {})); } catch {}
}

function _cgSearchGet(q) {
  try {
    const store = _cgSearchRead();
    const key = String(q || "").toLowerCase();
    const e = store?.[key];
    if (!e || !e.ts || !Array.isArray(e.data)) return null;
    if (Date.now() - Number(e.ts) > CG_SEARCH_TTL_MS) return null;
    return e.data;
  } catch {
    return null;
  }
}

function _cgSearchPut(q, data) {
  try {
    const key = String(q || "").toLowerCase();
    const store = _cgSearchRead();
    store[key] = { ts: Date.now(), data: Array.isArray(data) ? data : [] };

    // trim oldest entries
    const keys = Object.keys(store || {});
    if (keys.length > CG_SEARCH_MAX_ENTRIES) {
      keys.sort((a, b) => Number(store[a]?.ts || 0) - Number(store[b]?.ts || 0));
      for (const dk of keys.slice(0, keys.length - CG_SEARCH_MAX_ENTRIES)) delete store[dk];
    }
    _cgSearchWrite(store);
  } catch {}
}

const stripTrailingZeros = (value) => {
  const s = String(value ?? "");
  if (!s.includes(".")) return s;
  return s.replace(/0+$/, "").replace(/\.$/, "");
};

const fmtUsd = (n) => {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);

  // Handle tiny numbers like 0.00000001
  if (abs > 0 && abs < 0.0001) return "$" + stripTrailingZeros(n.toFixed(10));
  if (abs < 0.01) return "$" + stripTrailingZeros(n.toFixed(6));
  if (abs < 1) return "$" + stripTrailingZeros(n.toFixed(4));

  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

const fmtCompactUsd = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000_000) return `$${stripTrailingZeros((v / 1_000_000_000_000).toFixed(1))}T`;
  if (abs >= 1_000_000_000) return `$${stripTrailingZeros((v / 1_000_000_000).toFixed(1))}B`;
  if (abs >= 1_000_000) return `$${stripTrailingZeros((v / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `$${stripTrailingZeros((v / 1_000).toFixed(1))}K`;
  return fmtUsd(v);
};

const fmtQty = (n, maxDp = 6) => {
  if (n == null || !Number.isFinite(n)) return "—";
  // show more decimals for tiny values
  const abs = Math.abs(n);
  const dp = abs > 0 && abs < 0.0001 ? 10 : (abs < 0.01 ? 8 : maxDp);
  return stripTrailingZeros(n.toFixed(dp));
};

// ------------------------
// CoinGecko price helpers (Wallet total value)
// ------------------------
const CG = {
  nativeIds: { ETH: "ethereum", POL: "polygon-ecosystem-token", BNB: "binancecoin" },
  platforms: { ETH: "ethereum", POL: "polygon-pos", BNB: "binance-smart-chain" },
};

// CoinGecko fetch helper (browser) with short-lived caching + gentle backoff.
// This prevents UI lag + 429 rate-limits during rapid refreshes.
const __cgCache = new Map(); // url -> {ts:number, data:any}
let __cgCooldownUntil = 0;

async function cgFetchJson(url, { ttlMs = 120_000 } = {}) {
  const now = Date.now();

  // cooldown after 429
  if (now < __cgCooldownUntil) {
    const hit = __cgCache.get(url);
    if (hit && now - hit.ts < ttlMs) return hit.data;
    throw new Error(`CoinGecko cooldown`);
  }

  // cache hit
  const hit = __cgCache.get(url);
  if (hit && now - hit.ts < ttlMs) return hit.data;

  const res = await fetch(url, { cache: "no-store" });

  if (res.status === 429) {
    __cgCooldownUntil = now + 120_000; // 120s
    // return stale if present
    const stale = __cgCache.get(url);
    if (stale) return stale.data;
    throw new Error(`CoinGecko HTTP 429`);
  }

  if (!res.ok) {
    // return stale if present
    const stale = __cgCache.get(url);
    if (stale) return stale.data;
    throw new Error(`CoinGecko HTTP ${res.status}`);
  }

  const data = await res.json();
  __cgCache.set(url, { ts: now, data });
  return data;
}

async function fetchNativeUsdPrices(chains = []) {
  // IMPORTANT: Do NOT call CoinGecko directly from the browser (CORS + 429).
  // Use backend proxy: GET /api/coingecko/simple_price
  const ids = Array.from(new Set((chains || []).map((c) => CG.nativeIds[c]).filter(Boolean)));
  if (!ids.length) return {};
  const qs = new URLSearchParams({ ids: ids.join(","), vs_currencies: "usd" }).toString();
  const json = await api(`/api/coingecko/simple_price?${qs}`);
  const out = {};
  for (const c of chains || []) {
    const id = CG.nativeIds[c];
    const px = id ? Number(json?.[id]?.usd) : NaN;
    if (Number.isFinite(px)) out[c] = px;
  }
  return out;
}

async function fetchTokenUsdPrices(chainKey, addresses = []) {
  // IMPORTANT: Do NOT call CoinGecko directly from the browser (CORS + 429).
  // Use backend proxy: GET /api/coingecko/token_price/<platform>
  const platform = CG.platforms[chainKey];
  const addrs = Array.from(new Set((addresses || []).map((a) => String(a || "").toLowerCase()).filter(Boolean)));
  if (!platform || !addrs.length) return {};
  // CoinGecko allows many addresses; keep it safe-ish.
  const qs = new URLSearchParams({ contract_addresses: addrs.join(","), vs_currencies: "usd" }).toString();
  const json = await api(`/api/coingecko/token_price/${encodeURIComponent(platform)}?${qs}`);
  const out = {};
  for (const a of addrs) {
    const px = Number(json?.[a]?.usd);
    if (Number.isFinite(px)) out[a] = px;
  }
  return out;
}

const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  const s = (x >= 0 ? "+" : "") + x.toFixed(2) + "%";
  return s;
};

function _nexusNumberOrNull(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function _nexusChangeOrNull(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function getTradingSessionLivePriceInfo(session, slots = [], asset = "", watchRows = []) {
  const sess = session && typeof session === "object" ? session : {};
  const sessionMeta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
  const rows = Array.isArray(watchRows) ? watchRows : [];
  const symbol = String(
    asset ||
    sess?.symbol || sess?.asset || sess?.target_symbol || sess?.targetSymbol || sess?.coin ||
    sessionMeta?.symbol || sessionMeta?.asset || sessionMeta?.target_symbol || ""
  ).trim().toUpperCase();
  const row = rows.find((r) => String(r?.symbol || r?.sym || r?.asset || "").trim().toUpperCase() === symbol) || {};

  let slotPrice = null;
  let slotPrevPrice = null;
  let slotChange = null;
  for (const slot of Array.isArray(slots) ? slots : []) {
    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
    const px = _nexusNumberOrNull(
      slot?.live_price_usd, slot?.livePriceUsd,
      slot?.current_price_usd, slot?.currentPriceUsd,
      slot?.paper_mark_price, meta?.paper_mark_price,
      slot?.mark_price_usd, meta?.mark_price_usd,
      meta?.live_price_usd, meta?.livePriceUsd,
      meta?.current_price_usd, meta?.currentPriceUsd
    );
    if (px !== null) {
      slotPrice = px;
      slotPrevPrice = _nexusNumberOrNull(
        slot?.previous_price_usd, slot?.previousPriceUsd,
        slot?.prev_price_usd, slot?.prevPriceUsd,
        meta?.previous_price_usd, meta?.previousPriceUsd,
        meta?.prev_price_usd, meta?.prevPriceUsd
      );
      slotChange = _nexusChangeOrNull(
        slot?.live_change_pct, slot?.liveChangePct,
        slot?.price_change_pct, slot?.priceChangePct,
        meta?.live_change_pct, meta?.liveChangePct,
        meta?.price_change_pct, meta?.priceChangePct
      );
      break;
    }
  }

  // ENGINE-094 ROOT FIX:
  // Trading live display must use the backend paper mark price first. Session/card
  // values and watchlist rows can be stale or cached; using them above the slot mark
  // made Trader live prices look wrong even when the backend tick had the correct mark.
  const price = _nexusNumberOrNull(
    slotPrice,
    sess?.paper_mark_price, sess?.paperMarkPrice,
    sessionMeta?.paper_mark_price, sessionMeta?.paperMarkPrice,
    sess?.live_price_usd, sess?.livePriceUsd,
    sess?.current_price_usd, sess?.currentPriceUsd,
    sess?.mark_price_usd, sess?.markPriceUsd,
    sessionMeta?.live_price_usd, sessionMeta?.livePriceUsd,
    sessionMeta?.current_price_usd, sessionMeta?.currentPriceUsd,
    sessionMeta?.mark_price_usd, sessionMeta?.markPriceUsd,
    row?.price, row?.usd, row?.current_price, row?.currentPrice
  );
  if (price === null) return null;

  const prevPrice = _nexusNumberOrNull(
    sess?.previous_price_usd, sess?.previousPriceUsd,
    sess?.prev_price_usd, sess?.prevPriceUsd,
    sessionMeta?.previous_price_usd, sessionMeta?.previousPriceUsd,
    sessionMeta?.prev_price_usd, sessionMeta?.prevPriceUsd,
    slotPrevPrice
  );
  let changePct = _nexusChangeOrNull(
    sess?.live_change_pct, sess?.liveChangePct,
    sess?.price_change_pct, sess?.priceChangePct,
    sessionMeta?.live_change_pct, sessionMeta?.liveChangePct,
    sessionMeta?.price_change_pct, sessionMeta?.priceChangePct,
    slotChange,
    row?.change24h, row?.chg_24h, row?.usd_24h_change, row?.change_24h
  );
  if (changePct === null && prevPrice && prevPrice > 0) {
    changePct = ((price - prevPrice) / prevPrice) * 100;
  }

  const tone = changePct === null ? "neutral" : changePct >= 0 ? "up" : "down";
  return {
    price,
    changePct,
    tone,
    arrow: tone === "up" ? "▲" : tone === "down" ? "▼" : "•",
    color: tone === "up" ? "#22c55e" : tone === "down" ? "#ff6b6b" : "rgba(216,255,241,.58)",
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function resolveWalletAddress(walletLike = "") {
  try {
    const candidates = [
      walletLike?.address,
      walletLike?.wallet?.address,
      walletLike?.ethereum?.address,
      walletLike,
      localStorage.getItem("nexus_wallet"),
      localStorage.getItem("wallet"),
    ];
    for (const c of candidates) {
      const raw = String(c || "").trim();
      const m = raw.match(/0x[a-fA-F0-9]{40}/);
      if (m) return m[0].toLowerCase();
    }
  } catch {}
  return "";
}

function withWalletQuery(path, walletLike = "") {
  const wa = resolveWalletAddress(walletLike);
  if (!wa || !String(path || "").startsWith("/api/")) return path;
  const url = String(path || "");
  if (/[?&](wallet|wallet_address|addr|address)=/i.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`;
}

async function api(
  path,
  { method = "GET", token, body, signal, wallet } = {}
) {
  // Always send the wallet context (backend binds sessions to wallet).
  const wa = resolveWalletAddress(wallet);

  // Auth strategy:
  // - Frontend must NOT contain backend/API secrets.
  // - Use the user/session token when available, plus credentials: "include" for cookies.
  const candidates = [];
  const t = (token || "").trim();
  if (t) candidates.push(t);

  // Deduplicate while preserving order
  const seen = new Set();
  const bearers = candidates.filter((x) => {
    const k = String(x || "").trim();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const makeHeaders = (bearer, requestPath = "") => {
    const headers = { Accept: "application/json" };

    // Only send Content-Type when we actually send a JSON body.
    if (body != null && method !== "GET") {
      headers["Content-Type"] = "application/json";
    }

    if (bearer) headers["Authorization"] = `Bearer ${bearer}`;

    // Important for request volume:
    // GET requests already receive wallet + wallet_address as query params via withWalletQuery().
    // Adding a custom X-Wallet-Address header to every GET turns simple GETs into CORS preflight
    // pairs (OPTIONS + GET). That doubled/throttled the app with thousands of network rows.
    // Keep the wallet header only for non-GET requests where JSON body actions may need it.
    if (wa && method !== "GET") {
      headers["X-Wallet-Address"] = wa;
    }

    return headers;
  };

  const doFetch = async (bearer) => {
    // Hard safety timeout so UI never gets stuck due to Render sleep/hanging connections.
    const ctrl = new AbortController();
    const timeoutMs =
      path?.includes("/api/access/redeem") ? 60000 :
      path?.includes("/api/access/status") ? 4500 :
      path?.includes("/api/grid/") ? 60000 :
      path?.includes("/api/nkr/control") ? 90000 :
      method === "GET" ? 15000 : 60000;

    const tm = setTimeout(() => {
      try {
        ctrl.abort();
      } catch {}
    }, timeoutMs);

    // Merge external signal with our timeout.
    const merged = new AbortController();
    const onAbort = () => {
      try {
        merged.abort();
      } catch {}
    };
    try {
      if (signal?.aborted) onAbort();
      if (signal) signal.addEventListener("abort", onAbort, { once: true });
      ctrl.signal.addEventListener("abort", onAbort, { once: true });
    } catch {}

    try {
      const requestPath = withWalletQuery(path, wa);
      const safeBody =
        body && typeof body === "object" && !Array.isArray(body)
          ? (() => {
              const next = { ...body };
              for (const key of ["wallet", "wallet_address", "walletAddress"]) {
                if (next[key]) next[key] = resolveWalletAddress(next[key]) || wa || "";
              }
              return next;
            })()
          : body;

      return await fetch(`${API_BASE}${requestPath}`, {
        method,
        signal: merged.signal,
        headers: makeHeaders(bearer, requestPath),
        credentials: "include",
        body: safeBody ? JSON.stringify(safeBody) : undefined,
      });
    } finally {
      clearTimeout(tm);
      try {
        if (signal) signal.removeEventListener("abort", onAbort);
      } catch {}
    }
  };

  // Try bearer candidates in order. This avoids the old behavior where we dropped Authorization
  // entirely on retry (which caused repeated 401s).
  let lastRes = null;
  let lastText = "";
  let lastData = null;

  const attempts = bearers.length ? bearers : [null];
  for (const b of attempts) {
    const res = await doFetch(b);
    lastRes = res;

    const txt = await res.text();
    lastText = txt;

    let data = null;
    try {
      data = txt ? JSON.parse(txt) : null;
    } catch {
      data = { raw: txt };
    }
    lastData = data;

    if (res.ok) return data;

    // If unauthorized and we have more candidates, try next.
    if (res.status === 401 || res.status === 403) {
      continue;
    }

    // For non-auth errors, break early.
    break;
  }

  const status = lastRes ? lastRes.status : 0;
  const data = lastData;
  const msg = (data && (data.error || data.message)) || `HTTP ${status}`;
  const err = new Error(msg);
  err.status = status;
  err.data = data ?? { raw: lastText };
  throw err;
}

// Search helper: backend endpoint name may differ across deployments.
// Search helper: backend endpoint name may differ across deployments.
// backend endpoint name may differ across deployments.
async function securityPrecheckForDeposit({ chainKey, tokenAddress = "", symbol = "", isNative = false, token }) {
  const body = {
    chain: String(chainKey || "").toUpperCase(),
    token_address: tokenAddress || "",
    address: tokenAddress || "",
    symbol: symbol || "",
    is_native: !!isNative,
  };
  const res = await api(`/api/security/token-check`, { method: "POST", body, token });
  return res || {};
}

async function apiSearchCoins(query, { signal } = {}) {
  const q = String(query || "").trim();
  if (!q) return [];
  const enc = encodeURIComponent(q);

  // IMPORTANT:
  // Our backend canonical search endpoints are:
  //   - /api/search (alias)
  //   - /api/coins/search (real implementation)
  // Try both first to avoid long waits on wrong endpoints.
  const candidates = [
    `/api/search?q=${enc}`,
    `/api/coins/search?q=${enc}`,
    `/api/coingecko/search?q=${enc}`,
    `/api/coingecko_search?q=${enc}`,
    `/search?q=${enc}`,
  ];

  let lastErr = null;
  for (const path of candidates) {
    try {
      return await api(path, { signal });
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Search failed");
}

// ---- Payments (ERC20 transfer) helpers (no external deps) ----
function _hexPad64(hexNo0x) {
  const h = (hexNo0x || "").replace(/^0x/i, "").toLowerCase();
  return h.padStart(64, "0");
}
function _toHexAmount(amountUnits) {
  // amountUnits: BigInt
  let h = amountUnits.toString(16);
  if (h.length % 2) h = "0" + h;
  return "0x" + h;
}
function _erc20TransferData(to, amountUnits) {
  // transfer(address,uint256) selector
  const selector = "0xa9059cbb";
  const addr = (to || "").toLowerCase().replace(/^0x/, "");
  if (addr.length !== 40) throw new Error("Invalid recipient address");
  const amtHex = amountUnits.toString(16);
  return selector + _hexPad64(addr) + _hexPad64(amtHex);
}
async function _ensureChain(chainKey) {
  if (!window?.ethereum?.request) throw new Error("No injected wallet found (MetaMask).");
  const want = String(chainKey || "").toUpperCase();
  const map = { ETH: "0x1", BNB: "0x38", POL: "0x89" };
  const wantHex = map[want];
  if (!wantHex) throw new Error("Unsupported chain.");

  const chainHex = await window.ethereum.request({ method: "eth_chainId" });
  if (String(chainHex).toLowerCase() === String(wantHex).toLowerCase()) return;

  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: wantHex }],
    });
  } catch (e) {
    const name = want === "ETH" ? "Ethereum (ETH)" : want === "BNB" ? "BNB Chain (BNB)" : "Polygon (POL)";
    throw new Error(`Please switch your wallet to ${name}.`);
  }
}

async function fetchSubscribeConfig() {
  const res = await fetch(`${API_BASE}/api/access/subscribe/config`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const txt = await res.text();
  let data = null;
  try { data = txt ? JSON.parse(txt) : null; } catch { data = { raw: txt }; }
  if (!res.ok || !data || data.status === "error") {
    throw new Error(data?.error || data?.message || `Payment config failed (HTTP ${res.status})`);
  }
  const treasury = String(data.treasury || "").trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(treasury)) {
    throw new Error("Treasury address is not configured in backend.");
  }
  return data;
}

function useInterval(fn, ms, enabled = true) {
  const fnRef = useRef(fn);
  const inFlightRef = useRef(false);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    if (!enabled || !ms) return;

    const tick = async () => {
      // Do not poll aggressively while the browser tab is hidden.
      if (typeof document !== "undefined" && document.hidden) return;

      // Prevent stacked requests when the previous async poll is still pending.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await fnRef.current?.();
      } finally {
        inFlightRef.current = false;
      }
    };

    const id = setInterval(tick, ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
}

function useLocalStorageState(key, initialValue) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);
  return [v, setV];
}


// Stable top-level helper: avoids React TDZ crashes when session ids are used in early hooks/dependency arrays.
function makeNexusSessionId(prefix = "NS") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();
}

// ------------------------
// Info button + modal (opaque, bilingual content)
// ------------------------
function formatNkrUsd(price) {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return "";
  if (p >= 0.01) return p.toFixed(4);
  let s = p.toFixed(10);
  s = s.replace(/0+$/, "").replace(/\.$/, "");
  return s;
}

function formatNkrMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v >= 1000) return `$${Math.round(v).toLocaleString("en-US")}`;
  if (v >= 100) return `$${v.toFixed(0)}`;
  return `$${v.toFixed(0)}`;
}

function Help({ de, en }) {
  return (
    <div className="helpBlock">
      <div className="helpLangTitle">DE</div>
      <div className="helpText">{de}</div>
      <div className="helpLangTitle" style={{ marginTop: 10 }}>
        EN
      </div>
      <div className="helpText">{en}</div>
    </div>
  );
}


function RotationInfoTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        style={{ marginTop: 14 }}
      >
        Nexus NKR Info
      </button>

      {open && (
        <div
          className="modalBackdrop"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
          style={{ zIndex: 99999 }}
        >
          <div
            className="modal modalHelp"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
              maxHeight: "82vh",
              overflowY: "auto",
            }}
          >
            <div className="modalHead">
              <div className="cardTitle">Nexus NKR</div>
              <button
                className="iconBtn"
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
              >
                ×
              </button>
            </div>
            <div className="helpBody">
              <Help
                de={
                  <>
                    <p><b>Nexus NKR</b> ist der Kapitalmanager von Nexus. Er beobachtet die erlaubten Assets laufend und verteilt Kapital nur dorthin, wo Momentum, Score, Volumen, Relative Strength und Risiko zusammenpassen.</p>
                    <p><b>Max Sessions:</b> Die Zahl ist nur die Obergrenze des Users. Nexus NKR muss nicht alle Slots füllen und darf Sessions stoppen, wenn bessere Chancen entstehen.</p>
                    <p><b>Rotation:</b> Schwache oder rote Sessions können in den Watch Pool zurückfallen. Kapital kann zu stärkeren Assets wie BTC, BNB oder anderen aktuellen Gewinnern verschoben werden.</p>
                    <p><b>Ausführung:</b> Nexus NKR entscheidet Asset und Kapital. Der Executor verarbeitet Entry, Tracking, Kosten, Net Edge und Exit.</p>
                    <p><b>Live später:</b> Nicht-EVM Assets werden für den User einfach als BTC, SOL oder XRP angezeigt. Im Vault darf intern nur der geprüfte, erlaubte Wrapped Contract genutzt werden.</p>
                    <p><b>Wichtig:</b> Nexus NKR garantiert keinen Gewinn. Es ist ein Risiko-, Kapital- und Rebalancing-System auf Basis von Marktdaten.</p>
                  </>
                }
                en={
                  <>
                    <p><b>Nexus NKR</b> is the Nexus capital manager. It continuously watches allowed assets and allocates capital only where momentum, score, volume, relative strength and risk fit together.</p>
                    <p><b>Max Sessions:</b> The number is only the user's ceiling. Nexus NKR does not have to fill all slots and may stop sessions when better opportunities appear.</p>
                    <p><b>Rotation:</b> Weak or red sessions can return to the watch pool. Capital can be redirected toward stronger assets such as BTC, BNB or other current winners.</p>
                    <p><b>Execution:</b> Nexus NKR decides asset and capital. The executor handles entry, tracking, costs, net edge and exit.</p>
                    <p><b>Later live:</b> Non-EVM assets remain simple for the user as BTC, SOL or XRP. Internally the Vault may only use the verified allowlisted wrapped contract.</p>
                    <p><b>Important:</b> Nexus NKR does not guarantee profit. It is a risk, capital and rebalancing system based on market data.</p>
                  </>
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InfoButton({ title = "Info", children }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button className="infoBtn" onClick={() => setOpen(true)} title={title} aria-label={title}>
        i
      </button>

      {open && (
        <div className="modalBackdrop" onClick={() => setOpen(false)}>
          <div
            className="modal modalHelp"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
              maxHeight: "82vh",
              overflowY: "auto",
            }}
          >
            <div className="modalHead">
              <div className="cardTitle">{title}</div>
              <button className="iconBtn" type="button" style={{ pointerEvents: "auto" }} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}>
                ×
              </button>
            </div>
            <div className="helpBody">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}

// ------------------------
// Health color (ampel)
// ------------------------
function bandFromScore(score) {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return "na";
  const s = Number(score);
  if (s >= 71) return "good";
  if (s >= 51) return "mid";
  return "bad";
}
function bandFromSnapshot(row) {
  const ch = Number(row?.change24h);
  const vol = Number(row?.volume24h);
  if (!Number.isFinite(ch) && !Number.isFinite(vol)) return "na";
  if (Number.isFinite(ch) && ch <= -4) return "bad";
  if (Number.isFinite(ch) && ch >= 3) return "good";
  if (Number.isFinite(vol) && vol > 0) return "mid";
  return "mid";
}

function watchSystemScore(row) {
  const ch = Number(row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? row?.change_24h);
  const vol = Number(row?.volume24h ?? row?.total_volume ?? row?.volume_24h);
  const mcap = Number(row?.marketCap ?? row?.market_cap ?? row?.mcap ?? row?.marketcap);
  let score = 55;
  if (Number.isFinite(ch)) {
    if (ch >= 10) score += 24;
    else if (ch >= 5) score += 18;
    else if (ch >= 2) score += 10;
    else if (ch >= 0) score += 4;
    else if (ch <= -10) score -= 24;
    else if (ch <= -5) score -= 16;
    else if (ch <= -2) score -= 8;
    else score -= 3;
  }
  if (Number.isFinite(vol) && vol > 0) {
    const v = Math.log10(Math.max(1, vol));
    score += Math.max(0, Math.min(14, (v - 5) * 3));
  }
  if (Number.isFinite(mcap) && mcap > 0) {
    const m = Math.log10(Math.max(1, mcap));
    score += Math.max(0, Math.min(10, (m - 7) * 2));
  }
  return Math.max(0, Math.min(100, Math.round(score)));
}

function watchSystemRating(row) {
  return ratingFromScore(watchSystemScore(row));
}

const USER_RATING_POINTS = {
  AAA: 98,
  AA: 90,
  A: 80,
  BBB: 70,
  BB: 60,
  B: 50,
  CCC: 40,
  CC: 30,
  C: 20,
  RISK: 5,
};

function ratingFromScore(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "RISK";
  if (s >= 95) return "AAA";
  if (s >= 85) return "AA";
  if (s >= 75) return "A";
  if (s >= 65) return "BBB";
  if (s >= 55) return "BB";
  if (s >= 45) return "B";
  if (s >= 35) return "CCC";
  if (s >= 25) return "CC";
  if (s >= 15) return "C";
  return "RISK";
}

function userRatingAverageScore(summary) {
  const ratings = summary?.ratings || {};
  let total = 0;
  let count = 0;
  for (const [rating, votes] of Object.entries(ratings)) {
    const points = USER_RATING_POINTS[String(rating || "").toUpperCase()];
    const c = Number(votes || 0);
    if (!Number.isFinite(points) || !Number.isFinite(c) || c <= 0) continue;
    total += points * c;
    count += c;
  }
  return count > 0 ? total / count : null;
}

function watchFinalScore(row, summary) {
  // System Rating stays objective and must not be mixed with User Rating.
  return watchSystemScore(row);
}

function watchOnchainScoreDelta(onchain) {
  const n = Number(onchain?.score_delta ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-5, Math.min(5, Math.round(n)));
}


function formatWhaleTimeAgo(timestamp) {
  if (!timestamp) return "recent";
  const t = new Date(timestamp).getTime();
  if (!Number.isFinite(t)) return "recent";
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getWhaleNewsSignal(onchain) {
  // IMPORTANT:
  // NEWS must only appear for a REAL Bitquery whale buy/sell signal.
  // No text guessing, no score_delta guessing, no fake NEWS.
  const whale = onchain?.whale && typeof onchain.whale === "object" ? onchain.whale : onchain;
  const source = String(whale?.source || "").toLowerCase();
  const action = String(whale?.action || "").toLowerCase();
  const amount = Number(whale?.amountUsd || whale?.latest?.amountUsd || 0);
  const dex = String(whale?.latest?.dex || whale?.dex || "").trim();
  const time = String(whale?.latest?.time || whale?.time || "").trim();

  const isRealBitqueryWhale =
    source === "bitquery" &&
    (action === "buy" || action === "sell") &&
    Number.isFinite(amount) &&
    amount > 0 &&
    !!dex &&
    dex.toLowerCase() !== "pending" &&
    !!time;

  if (!isRealBitqueryWhale) {
    return { type: "neutral", label: "🔥", color: "#aaa", title: "No fresh whale activity" };
  }

  if (action === "buy") {
    return { type: "buy", label: "NEWS", color: "#22c55e", title: "Whale bought recently" };
  }

  return { type: "sell", label: "NEWS", color: "#ef4444", title: "Whale sold recently" };
}

function marketConditionUi(state) {
  const s = String(state || "").toUpperCase();
  if (s === "REAL_BREAKOUT") {
    return {
      code: "B",
      text: "Breakout",
      color: "#16c784",
      border: "rgba(22,199,132,.45)",
      help: "Breakout: price is extended, but strong relative volume confirms the move."
    };
  }
  if (s === "FAKE_MOVE") {
    return {
      code: "W",
      text: "Weak",
      color: "#ea3943",
      border: "rgba(234,57,67,.45)",
      help: "Weak: price is extended, but volume does not confirm the move. Fake-move risk is higher."
    };
  }
  if (s === "EARLY_ACCUMULATION") {
    return {
      code: "E",
      text: "Early",
      color: "#f5b300",
      border: "rgba(245,179,0,.45)",
      help: "Early: relative volume is high while price is not yet heavily extended."
    };
  }
  if (s === "OVEREXTENDED") {
    return {
      code: "H",
      text: "Hot",
      color: "#ff8a3d",
      border: "rgba(255,138,61,.45)",
      help: "Hot: price is far above its 20-day average. Pullback risk can increase."
    };
  }
  return {
    code: "N",
    text: "Normal",
    color: "var(--muted)",
    border: "rgba(255,255,255,.12)",
    help: "Normal: no strong Overextension/RVOL anomaly detected."
  };
}

function normalizeMarketConditionForAi(mc) {
  if (!mc || typeof mc !== "object") return null;
  const ctx = mc.ai_context || {};
  return {
    state: mc.state || ctx.market_condition_state || "",
    label: mc.label || ctx.market_condition_label || "",
    level: mc.level || "",
    confidence: mc.confidence || "",
    oe_pct: Number.isFinite(Number(mc.oe_pct ?? ctx.overextension_pct)) ? Number(mc.oe_pct ?? ctx.overextension_pct) : null,
    rvol: Number.isFinite(Number(mc.rvol ?? ctx.relative_volume)) ? Number(mc.rvol ?? ctx.relative_volume) : null,
    score_delta: Number.isFinite(Number(mc.score_delta)) ? Number(mc.score_delta) : 0,
    interpretation: ctx.interpretation || mc.interpretation || mc.condition?.insight || "",
  };
}

function watchFinalRating(row, summary, onchain) {
  const base = watchFinalScore(row, summary);
  const delta = watchOnchainScoreDelta(onchain);
  return ratingFromScore(Math.max(0, Math.min(100, base + delta)));
}

function buildAiSignalContext({ syms, watchRows, ratingSummaryBySymbol, onchainBySymbol, marketConditionBySymbol, bestPairsToShow, compareWeights, aiMode, nexusTradingContext }) {
  const explicitSelected = Array.isArray(syms) ? syms.map((s) => String(s || "").toUpperCase()).filter(Boolean) : [];
  const fallbackSymbols = [];
  const addFallbackSym = (sym) => {
    const s = String(sym || "").trim().toUpperCase();
    if (s && /^[A-Z0-9]{2,12}$/.test(s) && !fallbackSymbols.includes(s)) fallbackSymbols.push(s);
  };
  for (const p of Array.isArray(bestPairsToShow) ? bestPairsToShow.slice(0, 8) : []) {
    String(p?.pair || "").split("/").slice(0, 2).forEach(addFallbackSym);
  }
  for (const r of Array.isArray(watchRows) ? watchRows.slice(0, 12) : []) {
    addFallbackSym(r?.symbol);
  }
  const selected = explicitSelected.length ? explicitSelected : fallbackSymbols.slice(0, 8);
  const rowBySym = new Map();
  for (const r of Array.isArray(watchRows) ? watchRows : []) {
    const sym = String(r?.symbol || "").toUpperCase();
    if (sym) rowBySym.set(sym, r);
  }

  const coins = selected.map((sym) => {
    const row = rowBySym.get(sym) || {};
    const summary = ratingSummaryBySymbol?.[sym] || null;
    const onchain = onchainBySymbol?.[sym] || null;
    const marketCondition = normalizeMarketConditionForAi(marketConditionBySymbol?.[sym]);
    const baseScore = watchFinalScore(row, summary);
    const onchainDelta = watchOnchainScoreDelta(onchain);
    const finalScore = Math.max(0, Math.min(100, baseScore + onchainDelta));

    return {
      symbol: sym,
      rating: ratingFromScore(finalScore),
      score: finalScore,
      base_score: baseScore,
      onchain_delta: onchainDelta,
      user_rating_votes: summary?.count ?? 0,
      user_rating_avg_score: userRatingAverageScore(summary),
      change_24h_pct: Number.isFinite(Number(row?.change24h)) ? Number(row.change24h) : null,
      price: Number.isFinite(Number(row?.price)) ? Number(row.price) : null,
      volume_24h: Number.isFinite(Number(row?.volume24h)) ? Number(row.volume24h) : null,
      market_cap: Number.isFinite(Number(row?.marketCap ?? row?.market_cap)) ? Number(row?.marketCap ?? row?.market_cap) : null,
      market_condition: marketCondition,
      onchain: onchain ? {
        icon: onchain.icon || "",
        label: onchain.label || "",
        score_delta: onchainDelta,
        summary: onchain.summary || "",
        source: onchain.source || "",
        signals: onchain.signals || {},
        contract: onchain.contract || null,
      } : null,
    };
  });

  const selectedSet = new Set(selected);
  const normalizePairForAi = (p) => ({
    pair: p?.pair,
    score: Number.isFinite(Number(p?.score)) ? Number(p.score) : null,
    corr: Number.isFinite(Number(p?.corr)) ? Number(p.corr) : null,
    spread_pct: Number.isFinite(Number(p?.spreadPct)) ? Number(p.spreadPct) : null,
    rsi_gap: Number.isFinite(Number(p?.rsiGap)) ? Number(p.rsiGap) : null,
    rsi_a: Number.isFinite(Number(p?.rsiA)) ? Number(p.rsiA) : null,
    rsi_b: Number.isFinite(Number(p?.rsiB)) ? Number(p.rsiB) : null,
    momentum_score: Number.isFinite(Number(p?.momentumScore)) ? Number(p.momentumScore) : null,
    opportunity_score: Number.isFinite(Number(p?.opportunityScore)) ? Number(p.opportunityScore) : null,
    stability_score: Number.isFinite(Number(p?.stabilityScore)) ? Number(p.stabilityScore) : null,
  });

  const allComparePairs = (Array.isArray(bestPairsToShow) ? bestPairsToShow : [])
    .slice(0, 20)
    .map(normalizePairForAi);

  const pairs = (Array.isArray(bestPairsToShow) ? bestPairsToShow : [])
    .filter((p) => {
      const pair = String(p?.pair || "");
      const parts = pair.split("/").map((x) => x.trim().toUpperCase());
      return parts.length === 2 && selectedSet.has(parts[0]) && selectedSet.has(parts[1]);
    })
    .slice(0, 8)
    .map(normalizePairForAi);

  return {
    version: "strategist_context_v3_coingecko_max",
    selected_origin: explicitSelected.length ? "explicit_symbols" : "hidden_watchlist_compare_scope",
    analysis_symbols: selected,
    ai_mode: String(aiMode || "standard").toLowerCase() === "extreme" ? "extreme" : "standard",
    compare_weights: sanitizeCompareWeights(compareWeights || DEFAULT_COMPARE_WEIGHTS),
    coins,
    relevant_pairs: pairs,
    all_compare_pairs: allComparePairs,
    nexus_trading: nexusTradingContext || null,
    notes: [
      "Rating is one visible rating built from market/system score, user rating average, and small on-chain delta.",
      "On-chain signals are supporting evidence only and capped to a small score impact.",
      "Missing on-chain icon means neutral/no strong signal, not an error.",
    ],
  };
}


function buildLocalPairAlertsForUi(pairs, compareWeights, aiMode) {
  const rows = Array.isArray(pairs) ? pairs : [];
  if (!rows.length) return [];
  const mode = String(aiMode || "standard").toLowerCase() === "extreme" ? "extreme" : "standard";
  const w = sanitizeCompareWeights(compareWeights || DEFAULT_COMPARE_WEIGHTS);
  const sens = mode === "extreme" ? 0.82 : 1.0;
  const alerts = [];

  rows.forEach((p, sourceIndex) => {
    if (!p || typeof p !== "object") return;
    const pair = String(p.pair || "").toUpperCase();
    if (!pair || !pair.includes("/")) return;

    const corr = Number(p.corr);
    const spread = Math.abs(Number(p.spreadPct));
    const rsiGap = Math.abs(Number(p.rsiGap));
    const score = Number(p.score);
    const momentumScore = Number(p.momentumScore);
    const opportunityScore = Number(p.opportunityScore);

    const reasons = [];
    let type = "";
    let base = Number.isFinite(score) ? score : 0;

    if (Number.isFinite(corr) && Number.isFinite(spread) && corr >= 0.72 * sens && spread >= 5.0 * sens) {
      type = "hidden_opportunity";
      reasons.push("spread movement");
      base += Number(w.opportunity || 25) * 0.28;
    }
    if (Number.isFinite(rsiGap) && rsiGap >= 14.0 * sens) {
      type = type || "rsi_divergence";
      reasons.push("RSI divergence");
      base += Number(w.momentum || 25) * 0.24;
    }
    if (Number.isFinite(momentumScore) && momentumScore >= 72 * sens) {
      type = type || "momentum_shift";
      reasons.push("momentum shift");
      base += Number(w.momentum || 25) * 0.12;
    }
    if (Number.isFinite(corr) && Number.isFinite(spread) && corr >= 0.82 * sens && spread >= 2.0 * sens && spread <= 7.5 / sens) {
      type = type || "rebound_watch";
      reasons.push("rebound watch");
      base += Number(w.corr || 35) * 0.14;
    }
    if (Number.isFinite(score) && score < 70 && ((Number.isFinite(spread) && spread >= 6.0 * sens) || (Number.isFinite(rsiGap) && rsiGap >= 16.0 * sens))) {
      type = type || "low_rank_unusual_activity";
      reasons.push("low-rank unusual activity");
      base += 8;
    }
    if (Number.isFinite(opportunityScore) && opportunityScore >= 78 * sens && !reasons.length) {
      type = "hidden_opportunity";
      reasons.push("opportunity score spike");
      base += Number(w.opportunity || 25) * 0.18;
    }

    if (!reasons.length) return;
    const strengthScore = Math.max(0, Math.min(100, Math.round(base * 10) / 10));
    const strength = strengthScore >= 82 ? "high" : strengthScore >= 65 ? "medium" : "low";
    alerts.push({
      pair,
      type: type || "pair_alert",
      strength,
      score: strengthScore,
      corr: Number.isFinite(corr) ? corr : null,
      spread_pct: Number.isFinite(spread) ? spread : null,
      rsi_gap: Number.isFinite(rsiGap) ? rsiGap : null,
      reasons: reasons.slice(0, 3),
      source_rank: Number.isFinite(sourceIndex) ? sourceIndex + 1 : null,
    });
  });

  const byPair = new Map();
  for (const alert of alerts) {
    const key = String(alert?.pair || "").toUpperCase().trim();
    if (!key) continue;
    const prev = byPair.get(key);
    if (!prev || Number(alert.score || 0) > Number(prev.score || 0)) {
      byPair.set(key, alert);
    }
  }

  return Array.from(byPair.values()).sort((a, b) => {
    const scoreDiff = Number(b.score || 0) - Number(a.score || 0);
    if (scoreDiff) return scoreDiff;
    const spreadDiff = Number(b.spread_pct || 0) - Number(a.spread_pct || 0);
    if (spreadDiff) return spreadDiff;
    return Number(a.source_rank || 9999) - Number(b.source_rank || 9999);
  });
}

function formatAiSignalContextForPrompt(ctx) {
  const coins = Array.isArray(ctx?.coins) ? ctx.coins : [];
  const lines = coins.map((c) => {
    const oc = c.onchain;
    const mc = c.market_condition;
    const ocText = oc?.summary ? `on-chain: ${oc.summary}` : "on-chain: neutral/no strong signal";
    const mcText = mc?.state
      ? `market condition: ${mc.label || mc.state} (OE=${mc.oe_pct ?? "n/a"}%, RVOL=${mc.rvol ?? "n/a"}x, confidence=${mc.confidence || "n/a"}; ${mc.interpretation || "no interpretation"})`
      : "market condition: unavailable";
    const voteText = Number(c.user_rating_votes || 0) > 0 ? `community votes=${c.user_rating_votes}, avg=${c.user_rating_avg_score ?? "n/a"}` : "community votes=0";
    const ch = Number.isFinite(Number(c.change_24h_pct)) ? `${Number(c.change_24h_pct).toFixed(2)}% 24h` : "24h n/a";
    return `${c.symbol}: rating=${c.rating}, score=${c.score}, base=${c.base_score}, onchain_delta=${c.onchain_delta}, ${ch}, ${voteText}, ${ocText}, ${mcText}`;
  });

  const pairLines = (Array.isArray(ctx?.relevant_pairs) ? ctx.relevant_pairs : []).map((p) =>
    `${p.pair}: score=${p.score ?? "n/a"}, corr=${p.corr ?? "n/a"}, spread=${p.spread_pct ?? "n/a"}%, rsi_gap=${p.rsi_gap ?? "n/a"}`
  );

  const scopeLine = Array.isArray(ctx?.analysis_symbols) && ctx.analysis_symbols.length
    ? `Hidden Strategist analysis scope (${ctx.selected_origin || "context"}): ${ctx.analysis_symbols.join(", ")}`
    : "";
  const weightLine = ctx?.compare_weights ? `AI mode=${ctx.ai_mode || "standard"}; Compare weights corr=${ctx.compare_weights.corr}, momentum=${ctx.compare_weights.momentum}, opportunity=${ctx.compare_weights.opportunity}, stability=${ctx.compare_weights.stability}, sentiment=${ctx.compare_weights.sentiment}` : "";
  const tradingLine = ctx?.nexus_trading
    ? `Nexus Trading context: prepared=${ctx.nexus_trading?.prepared_setup?.symbol || "none"}, executable=${ctx.nexus_trading?.prepared_setup?.executable ?? "n/a"}, learning_setups=${ctx.nexus_trading?.learning_count ?? 0}, budget=${ctx.nexus_trading?.configured_budget_usd || "not set"}, risk_mode=${ctx.nexus_trading?.risk_mode || "n/a"}`
    : "";

  return [
    scopeLine,
    weightLine,
    tradingLine,
    lines.length ? `AI Signal Context:\n${lines.join("\n")}` : "",
    pairLines.length ? `Relevant pair context:\n${pairLines.join("\n")}` : "",
  ].filter(Boolean).join("\n\n");
}


// ------------------------
// Timeframes UI
// ------------------------
const TIMEFRAMES = [
  { key: "1D", label: "1D" },
  { key: "7D", label: "7D" },
  { key: "30D", label: "30D" },
  { key: "90D", label: "90D" },
  { key: "1Y", label: "1Y" },
  { key: "2Y", label: "2Y" },
];

// ------------------------
// Compare batching
// ------------------------
function mergeCompareBatches(batches) {
  const merged = { series: {}, health: {}, symbols: [] };
  for (const b of batches) {
    const syms = b?.symbols || [];
    for (const s of syms) {
      if (!merged.symbols.includes(s)) merged.symbols.push(s);
    }
    const series = b?.series || {};
    for (const [sym, pts] of Object.entries(series)) merged.series[sym] = pts;
    const health = b?.health || {};
    for (const [sym, h] of Object.entries(health)) merged.health[sym] = h;
  }
  return merged;
}

// Normalize backend series to UI format: {SYM: [{t, v}, ...]}
function normalizeBackendSeries(seriesLike) {
  const out = {};
  for (const [sym, pts] of Object.entries(seriesLike || {})) {
    if (!Array.isArray(pts)) {
      out[sym] = [];
      continue;
    }
    // backend often returns [[ts_ms, price], ...]
    if (pts.length && Array.isArray(pts[0])) {
      out[sym] = pts
        .map((p) => ({ t: Number(p?.[0] ?? 0), v: Number(p?.[1] ?? 0) }))
        .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t > 0);
      continue;
    }
    // backend may return objects already
    out[sym] = pts
      .map((p) => ({
        t: Number(p?.t ?? p?.time ?? p?.x ?? 0),
        v: Number(p?.v ?? p?.p ?? p?.y ?? 0),
      }))
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t > 0);
  }
  return out;
}

function _tfDays(tf) {
  const k = String(tf || "").toUpperCase();
  if (k === "1D") return 1;
  if (k === "7D") return 7;
  if (k === "30D") return 30;
  if (k === "90D") return 90;
  if (k === "1Y") return 365;
  return null; // e.g. 2Y handled separately
}

// Slice a full-series dict {SYM:[{t,v}...]} into the selected view timeframe.
// Uses per-series max timestamp as anchor (so it works even if coins have different ranges).
function sliceCompareSeries(full, timeframe) {
  const tf = String(timeframe || "90D").toUpperCase();
  if (!full || typeof full !== "object") return {};
  if (tf === "2Y" || tf === "1Y") return full;
  const days = _tfDays(tf);
  if (!days) return full;
  const windowMs = days * 24 * 60 * 60 * 1000;
  const out = {};
  for (const [sym, pts] of Object.entries(full)) {
    if (!Array.isArray(pts) || pts.length === 0) { out[sym] = []; continue; }
    const maxT = Number(pts[pts.length - 1]?.t ?? pts[pts.length - 1]?.[0] ?? 0);
    const cutoff = maxT ? (maxT - windowMs) : 0;
    out[sym] = pts.filter((p) => Number(p?.t ?? p?.[0] ?? 0) >= cutoff);
  }
  return out;
}

function _compareFetchRange(timeframe) {
  const tf = String(timeframe || "90D").toUpperCase();
  if (tf === "2Y") return "2Y";
  if (tf === "1D") return "1D"; // keep intraday fidelity
  return "1Y"; // fetch once, slice for 7D/30D/90D/1Y views
}

function _extractExplicitTfFromQuestion(q) {
  const s = String(q || "").toLowerCase();
  if (!s) return null;

  const patterns = [
    ["2Y", [/\b2\s*(year|years|yr|yrs|jahre|jahr|jahren)\b/i, /\btwo\s+years\b/i]],
    ["1Y", [/\b1\s*(year|years|yr|yrs|jahr|jahre|jahren)\b/i, /\bone\s+year\b/i]],
    ["90D", [/\b90\s*(day|days|tage|tagen)\b/i]],
    ["30D", [/\b30\s*(day|days|tage|tagen)\b/i]],
    ["7D", [/\b7\s*(day|days|tage|tagen)\b/i, /\b1\s*(week|weeks|woche|wochen)\b/i]],
    ["1D", [/\b1\s*(day|days|tag|tage)\b/i, /\b24\s*h\b/i, /\b24\s*hours?\b/i]],
  ];
  for (const [tf, regs] of patterns) {
    if (regs.some((rx) => rx.test(s))) return tf;
  }
  return null;
}

function _seriesStatsFromSeriesMap(seriesMap, syms) {
  const out = {};
  const statsForPts = (pts) => {
    const vals = (Array.isArray(pts) ? pts : [])
      .map((p) => (p && Number.isFinite(p.v) ? p.v : null))
      .filter((v) => v != null);
    if (vals.length < 2) return null;

    const first = vals[0];
    const last = vals[vals.length - 1];
    const changePct = first !== 0 ? ((last / first) - 1) * 100 : null;

    const rets = [];
    for (let i = 1; i < vals.length; i++) {
      const a = vals[i - 1], b = vals[i];
      if (a && b && a !== 0) rets.push((b / a) - 1);
    }
    const mean = rets.length ? rets.reduce((s, x) => s + x, 0) / rets.length : 0;
    const variance = rets.length ? rets.reduce((s, x) => s + (x - mean) ** 2, 0) / rets.length : 0;
    const volPct = Math.sqrt(variance) * 100;

    let peak = vals[0];
    let maxDD = 0;
    for (const v of vals) {
      if (v > peak) peak = v;
      const dd = peak ? (v / peak) - 1 : 0;
      if (dd < maxDD) maxDD = dd;
    }
    const min = Math.min(...vals);
    const max = Math.max(...vals);

    return { first, last, changePct, volPct, maxDDPct: maxDD * 100, min, max, points: vals.length };
  };

  for (const s of (syms || [])) {
    const pts = ((seriesMap && seriesMap[s]) || []).slice().sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
    const stats = statsForPts(pts);
    if (stats) out[s] = stats;
  }
  return out;
}

function _buildInsightWindows(compareSeries, syms) {
  const windows = ["7D", "30D", "90D", "1Y"];
  const out = {};
  for (const tf of windows) {
    const sliced = sliceCompareSeries(compareSeries || {}, tf);
    const stats = _seriesStatsFromSeriesMap(sliced, syms);
    if (Object.keys(stats).length) out[tf] = stats;
  }
  return out;
}

function buildUnifiedChart(seriesBySym) {
  const syms = Object.keys(seriesBySym || {});
  if (!syms.length) return { x: [], lines: {}, order: [] };

  const tsSet = new Set();
  for (const sym of syms) {
    const pts = seriesBySym[sym] || [];
    for (const p of pts) if (p && typeof p.t === "number") tsSet.add(p.t);
  }
  const x = Array.from(tsSet).sort((a, b) => a - b);
  if (!x.length) return { x: [], lines: {}, order: [] };

  const lines = {};
  for (const sym of syms) {
    const pts = (seriesBySym[sym] || []).slice().sort((a, b) => a.t - b.t);
    let i = 0;
    let last = null;
    const arr = [];
    for (const t of x) {
      while (i < pts.length && pts[i].t <= t) {
        last = pts[i].v;
        i += 1;
      }
      arr.push(last);
    }
    lines[sym] = arr;
  }

  return { x, lines, order: syms.slice() };
}

function normalizeToIndex(lines) {
  const out = {};
  for (const [sym, arr] of Object.entries(lines || {})) {
    let base = null;
    for (const v of arr) {
      if (v !== null && v !== undefined && Number.isFinite(v)) {
        base = v;
        break;
      }
    }
    if (!Number.isFinite(base) || base === 0) {
      out[sym] = arr.map(() => null);
      continue;
    }
    out[sym] = arr.map((v) => {
      if (v === null || v === undefined || !Number.isFinite(v)) return null;
      return (v / base) * 100.0;
    });
  }
  return out;
}

function formatXAxisLabel(t, timeframe) {
  const d = new Date(t);
  const tf = String(timeframe || "").toUpperCase();
  if (tf === "1D") {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  if (tf === "2Y") {
    // Month labels with year (makes the long range obvious)
    return d.toLocaleDateString("de-DE", { month: "short", year: "numeric" });
  }
  if (tf === "1Y") {
    return d.toLocaleDateString("de-DE", { month: "short", year: "numeric" });
  }
  return d.toLocaleDateString("de-DE", { month: "2-digit", day: "2-digit" });
}

function _monthStartTs(ms) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
}

function buildMonthlyTicks(tMin, tMax) {
  const a = _monthStartTs(tMin);
  const b = _monthStartTs(tMax);
  const out = [];
  let d = new Date(a);
  const end = new Date(b);
  // include start..end inclusive
  while (d <= end) {
    out.push(d.getTime());
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  // If we somehow end up with no ticks, fall back.
  return out.length ? out : [tMin, tMax];
}

// ------------------------
// SVG chart with y-scale + x labels
// ------------------------
function SvgChart({ chart, height = 320, highlightedSyms = [], onHoverSym, indexMode, timeframe, colorForSym, lineClassForSym }) {
  const { x, lines, order } = chart || { x: [], lines: {}, order: [] };
  const syms = order?.length ? order : Object.keys(lines || {});

  if (!x?.length || !syms.length) {
    return (
      <div className="chartEmpty" style={{ height }}>
        <div className="muted">No chart data yet.</div>
      </div>
    );
  }

    const plotLines = indexMode ? normalizeToIndex(lines) : lines;

  // Hover: show crosshair + tooltip for ALL series (like finanzen.net)
  const [hoverIdx, setHoverIdx] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 }); // position in actual CSS pixels inside the chart overlay
  const [tooltipSize, setTooltipSize] = useState({ w: 220, h: 0 });
  const tooltipRef = useRef(null);

  const n = x.length;
  const clampIdx = (i) => Math.max(0, Math.min(i, n - 1));

  const fmtDeltaPct = (idxVal) => {
    if (idxVal === null || idxVal === undefined || !Number.isFinite(idxVal)) return "—";
    const d = idxVal - 100.0;
    const s = d >= 0 ? "+" : "";
    return `${s}${d.toFixed(2)} %`;
  };

  const fmtHoverVal = (sym) => {
    if (indexMode) return fmtDeltaPct(plotLines?.[sym]?.[hoverIdx]);
    const v = lines?.[sym]?.[hoverIdx];
    if (v === null || v === undefined || !Number.isFinite(v)) return "—";
    return fmtUsd(v);
  };

  const hoverDate = (t) => {
    try {
      const d = new Date(t);
      // show full date for longer ranges; time for 1D
      if (timeframe === "1D") {
        return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      }
      return d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return String(t);
    }
  };

  useEffect(() => {
    if (!tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    setTooltipSize((prev) => {
      if (prev.w === rect.width && prev.h === rect.height) return prev;
      return { w: rect.width, h: rect.height };
    });
  }, [hoverIdx, syms.length, indexMode, timeframe, highlightedSyms]);

    // Overlay + Price: use log scale so vastly different price magnitudes remain readable (BTC vs small coins).
    // Index 100 stays linear (already normalized).
    const logMode = !indexMode && syms.length > 1;
    const EPS = 1e-12;
    const tVal = (v) => (logMode ? Math.log10(Math.max(EPS, v)) : v);
    const invTVal = (tv) => (logMode ? Math.pow(10, tv) : tv);

    let min = Infinity,
      max = -Infinity;
    for (const sym of syms) {
      for (const v0 of plotLines[sym] || []) {
        if (v0 === null || v0 === undefined) continue;
        if (!Number.isFinite(v0)) continue;
        const tv = tVal(v0);
        if (!Number.isFinite(tv)) continue;
        min = Math.min(min, tv);
        max = Math.max(max, tv);
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      // safe fallback for empty/flat charts
      min = logMode ? 0 : 0.8;
      max = logMode ? 1 : 1.2;
    }

    const w = 1000;
    const h = height;
    const padL = 70;
    const padR = 12;
    const padT = 12;
    const padB = 34;
    const innerW = w - padL - padR;
    const innerH = h - padT - padB;

    const sx = (i) => padL + (i * innerW) / Math.max(1, x.length - 1);

    const syTV = (tv) => {
      const t = (tv - min) / (max - min);
      return padT + (1 - t) * innerH;
    };
    const sy = (v) => syTV(tVal(v));
    const GRID_TICKS = 6;
    const ticksTV = Array.from({ length: GRID_TICKS }, (_, i) => min + ((max - min) * i) / (GRID_TICKS - 1));
const tMin = x[0];
    const tMid = x[Math.floor(x.length / 2)];
    const tMax = x[x.length - 1];

    const makePath = (sym) => {
      const arr = plotLines[sym] || [];
      let d = "";
      for (let i = 0; i < arr.length; i++) {
        const v = arr[i];
        if (v === null || v === undefined || !Number.isFinite(v)) continue;
        const X = sx(i);
        const Y = sy(v);
        d += d ? ` L ${X} ${Y}` : `M ${X} ${Y}`;
      }
      return d;
    };

    const yFmt = (tv) => (indexMode ? fmtDeltaPct(tv) : fmtUsd(invTVal(tv)));
    // Tooltip positioning must use real CSS pixels, not the SVG viewBox units.
    // Otherwise it will disappear on narrower rendered charts when moving right.
    const tooltipGap = 16;
    const chartPad = 12;
    const chartCssW = hoverPos.chartW || w;
    const chartCssH = hoverPos.chartH || h;

    const placeRight = hoverPos.x + tooltipGap;
    const placeLeft = hoverPos.x - tooltipSize.w - tooltipGap;
    const placeBelow = hoverPos.y + tooltipGap;
    const placeAbove = hoverPos.y - tooltipSize.h - tooltipGap;

    const tooltipLeftSafe =
      placeRight + tooltipSize.w <= chartCssW - chartPad
        ? placeRight
        : Math.max(chartPad, Math.min(placeLeft, chartCssW - tooltipSize.w - chartPad));

    const tooltipTopSafe =
      placeBelow + tooltipSize.h <= chartCssH - chartPad
        ? placeBelow
        : Math.max(chartPad, Math.min(placeAbove, chartCssH - tooltipSize.h - chartPad));

  return (
    <div className="chartWrap" style={{ width: "100%", position: "relative" }}>
      <svg className="chartSvg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} className="chartAxis" />
        <line x1={padL} y1={padT} x2={padL} y2={h - padB} className="chartAxis" />
        {/* grid + y labels */}
        {ticksTV.map((tv, i) => (
          <g key={i}>
            <line x1={padL} y1={syTV(tv)} x2={w - padR} y2={syTV(tv)} className="chartGrid" />
            <text x={8} y={syTV(tv) + 5} className="chartLabel">
              {yFmt(tv)}
            </text>
          </g>
        ))}
{/* X labels */}
        <text x={padL} y={h - 8} className="chartLabel">
          {formatXAxisLabel(tMin, timeframe)}
        </text>
        <text x={padL + innerW / 2} y={h - 8} textAnchor="middle" className="chartLabel">
          {formatXAxisLabel(tMid, timeframe)}
        </text>
        <text x={w - padR} y={h - 8} textAnchor="end" className="chartLabel">
          {formatXAxisLabel(tMax, timeframe)}
        </text>

{/* lines */}

        {syms.map((sym, idx) => {
          const d = makePath(sym);
          if (!d) return null;

          // IMPORTANT: only dim when user CLICK-highlighted; hover must not change visibility
          const hasHighlights = Array.isArray(highlightedSyms) && highlightedSyms.length > 0;
          const isHi = !hasHighlights || highlightedSyms.includes(sym);
          const opacity = hasHighlights ? (isHi ? 0.95 : 0.18) : 0.90;
          const strokeWidth = hasHighlights ? (isHi ? 3.2 : 2.2) : 3.0;

          return (
            <path
              key={sym}
              d={d}
              className={`chartLine ${lineClassForSym ? lineClassForSym(sym) : `line${(idx % 10) + 1}`}`}
              style={{ opacity, strokeWidth, stroke: (colorForSym ? colorForSym(sym) : PALETTE20[idx % 10]) }}
              onMouseEnter={() => onHoverSym?.(sym)}
              onMouseLeave={() => onHoverSym?.(null)}
            />
          );
        })}
        {hoverIdx !== null && (
          <line
            x1={sx(hoverIdx)}
            x2={sx(hoverIdx)}
            y1={padT}
            y2={h - padB}
            stroke="rgba(255,255,255,.25)"
            strokeDasharray="4 4"
          />
        )}

        {hoverIdx !== null && syms.map((sym, idx) => {
          const arr = plotLines?.[sym] || [];
          const v = arr?.[hoverIdx];
          if (v === null || v === undefined || !Number.isFinite(v)) return null;

          const hasHighlights = Array.isArray(highlightedSyms) && highlightedSyms.length > 0;
          const isHi = !hasHighlights || highlightedSyms.includes(sym);
          if (!isHi) return null;

          const c = colorForSym ? colorForSym(sym) : PALETTE20[idx % 10];
          return (
            <g key={`hover-dot-${sym}`} pointerEvents="none">
              <circle
                cx={sx(hoverIdx)}
                cy={sy(v)}
                r={6.4}
                fill="rgba(7,24,22,0.92)"
                stroke={c}
                strokeWidth={2.6}
              />
              <circle
                cx={sx(hoverIdx)}
                cy={sy(v)}
                r={2.6}
                fill={c}
              />
            </g>
          );
        })}
      </svg>

      <div
        style={{ position: "absolute", inset: 0, cursor: "crosshair" }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const px = e.clientX - rect.left;
          const py = e.clientY - rect.top;
          const svgX = (px / rect.width) * w;

          // Map mouse X onto plot area (between padL and w-padR)
          const clampedSvgX = Math.max(padL, Math.min(svgX, w - padR));
          const frac = (clampedSvgX - padL) / (w - padL - padR);
          const i = clampIdx(Math.round(frac * (n - 1)));
          setHoverIdx(i);
          setHoverPos({
            x: px,
            y: Math.max(12, Math.min(py, rect.height - 12)),
            chartW: rect.width,
            chartH: rect.height,
          });
        }}
        onMouseLeave={() => {
          setHoverIdx(null);
          setHoverPos({ x: 0, y: 0 });
        }}
      />

      {hoverIdx !== null && (
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            left: tooltipLeftSafe,
            top: tooltipTopSafe,
            background: "rgba(7,24,22,0.98)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: "8px 10px",
            minWidth: 190,
            boxShadow: "0 18px 50px rgba(0,0,0,.45)",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 4 }}>
            {hoverDate(x[hoverIdx])}
          </div>

          {syms.map((sym) => (
            <div
              key={sym}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                fontWeight: 800,
                opacity: highlightedSyms.length > 0 && !highlightedSyms.includes(sym) ? 0.55 : 1,
              }}
            >
              <span>{sym}</span>
              <span
                style={{
                  color: indexMode
                    ? (() => {
                        const v = plotLines?.[sym]?.[hoverIdx];
                        if (!Number.isFinite(v)) return "rgba(232,242,240,.85)";
                        return v - 100 >= 0 ? "var(--green)" : "var(--red)";
                      })()
                    : "rgba(232,242,240,.92)",
                }}
              >
                {fmtHoverVal(sym)}
              </span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

function Legend({ symbols, highlightedSyms = [], setHighlightedSyms, colorForSym, lineClassForSym }) {
  const highlightedSet = useMemo(() => new Set((highlightedSyms || []).map((s) => String(s || "").toUpperCase())), [highlightedSyms]);

  const toggleHighlight = (sym) => {
    const S = String(sym || "").toUpperCase();
    setHighlightedSyms((prev) => {
      const prevArr = Array.isArray(prev) ? prev.map((s) => String(s || "").toUpperCase()) : [];
      return prevArr.includes(S)
        ? prevArr.filter((x) => x !== S)
        : [...prevArr, S];
    });
  };

  const showAll = () => setHighlightedSyms([]);

  return (
    <div
      className="chartLegend"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        flexWrap: "nowrap",
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",
        maxWidth: "100%",
        paddingBottom: 2,
        scrollbarWidth: "thin",
      }}
    >
      {symbols.map((sym, idx) => {
        const hasHighlights = highlightedSet.size > 0;
        const active = !hasHighlights || highlightedSet.has(String(sym || "").toUpperCase());
        return (
          <button
            key={sym}
            className={`legendItem ${active ? "active" : ""}`}
            onClick={() => toggleHighlight(sym)}
            title={active ? "Click to fade this coin" : "Click to highlight this coin"}
            type="button"
            style={{
              height: 20,
              minHeight: 20,
              minWidth: 0,
              flex: "0 0 auto",
              padding: "0 5px",
              gap: 3,
              fontSize: 10,
              lineHeight: 1,
              borderRadius: 999,
              whiteSpace: "nowrap",
            }}
          >
            <span
              className={`legendDot ${lineClassForSym ? lineClassForSym(sym) : `line${(idx % 10) + 1}`}`}
              style={{
                backgroundColor: (colorForSym ? colorForSym(sym) : PALETTE20[idx % 10]),
                opacity: active ? 1 : 0.35,
                width: 6,
                height: 6,
                minWidth: 6,
              }}
            />
            <span className="legendSym" style={{ opacity: active ? 1 : 0.5, fontSize: 10 }}>{sym}</span>
          </button>
        );
      })}
      {symbols.length > 0 ? (
        <button
          className="legendItem active"
          onClick={showAll}
          title="Show all coins"
          type="button"
          style={{
            height: 20,
            minHeight: 20,
            minWidth: 0,
            flex: "0 0 auto",
            padding: "0 6px",
            fontSize: 10,
            lineHeight: 1,
            borderRadius: 999,
            whiteSpace: "nowrap",
          }}
        >
          <span className="legendSym" style={{ fontSize: 10 }}>All</span>
        </button>
      ) : null}
      {symbols.length === 0 ? <span className="muted">No coins selected.</span> : null}
    </div>
  );
}

function InlineWatchSpark({ sym, row, seriesMap, colorForSym, lineClassForSym, idx = 0 }) {
  // BUILD451: Watchlist mini-chart must be independent from Compare checkbox state.
  // The backend watchlist snapshot already returns CoinGecko's real sparkline_in_7d
  // as row.sparkline7d. Prefer that source first so checking/unchecking Compare
  // can never change the Watchlist 7D chart.
  const rowSpark7d = Array.isArray(row?.sparkline7d)
    ? row.sparkline7d.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
    : [];
  const fullSeries = Array.isArray(seriesMap?.[sym]) ? (seriesMap[sym] || []) : [];

  const values = (() => {
    if (rowSpark7d.length >= 8) {
      const src = rowSpark7d.slice();
      const current = Number(row?.price);
      if (Number.isFinite(current) && current > 0) {
        const last = src[src.length - 1];
        if (!Number.isFinite(last) || Math.abs(last - current) > Math.max(current * 1e-10, 1e-12)) src.push(current);
      }
      const maxPoints = 120;
      const step = Math.max(1, Math.ceil(src.length / maxPoints));
      const sampled = src.filter((_, i) => i % step === 0);
      const last = src[src.length - 1];
      if (Number.isFinite(last) && sampled[sampled.length - 1] !== last) sampled.push(last);
      return sampled;
    }

    // Legacy fallback only when the watchlist snapshot has no 7D CoinGecko history.
    // This fallback may use Compare history, but normal market watch rows should not need it.
    const normalize = (pts) => (pts || [])
      .map((pt) => {
        if (Array.isArray(pt)) {
          const t = Number(pt[0]);
          const v = Number(pt[1]);
          return Number.isFinite(t) && Number.isFinite(v) && v > 0 ? { t, v } : null;
        }
        if (pt && typeof pt === "object") {
          const t = Number(pt.t ?? pt.time ?? pt.ts ?? pt.x ?? 0);
          const v = Number(pt.v ?? pt.price ?? pt.value ?? pt.close ?? pt.y);
          return Number.isFinite(t) && Number.isFinite(v) && v > 0 ? { t, v } : null;
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t);

    const pts = normalize(fullSeries);

    if (pts.length) {
      const maxT = Number(pts[pts.length - 1]?.t || 0);
      const sevenDayMs = 7 * 24 * 60 * 60 * 1000;
      const chosen = maxT > 0 ? pts.filter((p) => p.t >= (maxT - sevenDayMs)) : pts;
      const src = chosen.length >= 12 ? chosen : pts;

      const maxPoints = 120;
      const step = Math.max(1, Math.ceil(src.length / maxPoints));
      const sampled = src
        .filter((_, i) => i % step === 0)
        .map((p) => p.v)
        .filter((v) => Number.isFinite(v) && v > 0);

      const last = src[src.length - 1]?.v;
      if (Number.isFinite(last) && last > 0 && sampled[sampled.length - 1] !== last) sampled.push(last);

      if (sampled.length >= 8) return sampled;
    }

    // Fallback only if no history is available. Keep it subtle, not zig-zaggy.
    const price = Number(row?.price);
    const chg = Number(row?.change24h);
    if (!Number.isFinite(price) || price <= 0) return [];
    const start = Number.isFinite(chg) ? price / (1 + chg / 100) : price * 0.985;
    return Array.from({ length: 42 }, (_, i) => {
      const t = i / 41;
      const drift = start + (price - start) * t;
      const wave = Math.sin(i * 0.55) * Math.abs(price) * 0.0018;
      return drift + wave;
    }).filter((v) => Number.isFinite(v) && v > 0);
  })();

  if (values.length < 2) {
    return <div className="watchMiniSpark empty" aria-hidden="true">—</div>;
  }

  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    min = 0;
    max = 1;
  }
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.01, 1e-6);
    min -= pad;
    max += pad;
  }

  const w = 150;
  const h = 44;
  const padX = 2;
  const padY = 3;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const sx = (i) => padX + (i * innerW) / Math.max(1, values.length - 1);
  const sy = (v) => {
    const t = (v - min) / Math.max(1e-9, (max - min));
    return padY + (1 - t) * innerH;
  };

  let d = "";
  values.forEach((v, i) => {
    const X = sx(i);
    const Y = sy(v);
    d += d ? ` L ${X.toFixed(2)} ${Y.toFixed(2)}` : `M ${X.toFixed(2)} ${Y.toFixed(2)}`;
  });

  const trendUp = values[values.length - 1] >= values[0];
  const stroke = trendUp ? "var(--green)" : "var(--red)";

  return (
    <div className="watchMiniSpark watchMiniSparkCg" title={`${sym} 7D chart`}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <path
          d={d}
          style={{
            fill: "none",
            stroke,
            strokeWidth: 2.05,
            strokeLinecap: "round",
            strokeLinejoin: "round",
            opacity: 0.98,
            vectorEffect: "non-scaling-stroke",
          }}
        />
      </svg>
    </div>
  );
}

function SmallSpark({ sym, chart, idx, indexMode, timeframe, active, onClick, colorForSym, lineClassForSym }) {
  const { x, lines } = chart || { x: [], lines: {} };
  const arr = (lines?.[sym] || []).slice();
  if (!x?.length || !arr.length) {
    return (
      <button className={`sparkTile ${active ? "active" : ""}`} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={onClick} type="button">
        <div className="sparkTop">
          <span className={`legendDot ${lineClassForSym ? lineClassForSym(sym) : `line${(idx % 10) + 1}`}`} style={{ backgroundColor: (colorForSym ? colorForSym(sym) : PALETTE20[idx % 10]) }} />
          <span className="sparkSym">{sym}</span>
          <span className="muted tiny">—</span>
        </div>
        <div className="sparkEmpty muted tiny">No data</div>
      </button>
    );
  }

  const values = indexMode
    ? (() => {
        let base = null;
        for (const v of arr) if (Number.isFinite(v)) { base = v; break; }
        if (!Number.isFinite(base) || base === 0) return arr.map(() => null);
        return arr.map((v) => (Number.isFinite(v) ? (v / base) * 100.0 : null));
      })()
    : arr;

  let min = Infinity, max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    min = 0.9; max = 1.1;
  }

  const w = 360, h = 110;
  const padL = 10, padR = 10, padT = 12, padB = 18;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const sx = (i) => padL + (i * innerW) / Math.max(1, x.length - 1);
  const sy = (v) => {
    const t = (v - min) / (max - min);
    return padT + (1 - t) * innerH;
  };

  let d = "";
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) continue;
    const X = sx(i), Y = sy(v);
    d += d ? ` L ${X} ${Y}` : `M ${X} ${Y}`;
  }

  const last = [...values].reverse().find((v) => Number.isFinite(v));
  const [hovered, setHovered] = useState(false);

  const deltaPct = (() => {
    if (!indexMode) return null;
    const first = values.find((v) => Number.isFinite(v));
    const lastv = [...values].reverse().find((v) => Number.isFinite(v));
    if (!Number.isFinite(first) || !Number.isFinite(lastv)) return null;
    return ((lastv - first) / first) * 100;
  })();

  const lastTxt = last == null ? "—" : (indexMode ? last.toFixed(1) : fmtUsd(last));

  return (
    <button
      className={`sparkTile ${active ? "active" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      type="button"
      title="Click to open chart"
    >
      <div className="sparkTop">
        <span className={`legendDot ${lineClassForSym ? lineClassForSym(sym) : `line${(idx % 10) + 1}`}`} style={{ backgroundColor: (colorForSym ? colorForSym(sym) : PALETTE20[idx % 10]) }} />
        <span className="sparkSym">{sym}</span>
        <span className="sparkVal mono">{hovered && deltaPct != null ? fmtPct(deltaPct) : lastTxt}</span>
      </div>
      <svg className="sparkSvg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path d={d} className={`chartLine ${lineClassForSym ? lineClassForSym(sym) : `line${(idx % 10) + 1}`}`} style={{ opacity: 0.95, strokeWidth: 2.8, stroke: (colorForSym ? colorForSym(sym) : PALETTE20[idx % 10]) }} />
      </svg>
      <div className="sparkFoot muted tiny">{indexMode ? "Index 100" : "Price"} · 30D</div>
    </button>
  );
}

// ------------------------
// Best pairs
// ------------------------
// Build aligned daily lines from raw series map: {SYM: [{t,v}, ...]}
// This avoids "union timestamp" artifacts and ensures every pair compares over real overlapping days.
function buildAlignedDailyLines(seriesBySym, opts = {}) {
  const minDays = Number(opts.minDays ?? 20); // require at least this many overlapping days
  const syms = Object.keys(seriesBySym || {}).filter(Boolean);
  if (syms.length < 2) return { dates: [], lines: {} };

  // 1) Build per-symbol map: YYYY-MM-DD -> close price (last point of the day)
  const maps = {};
  const dateSets = [];

  for (const sym of syms) {
    const pts = Array.isArray(seriesBySym?.[sym]) ? seriesBySym[sym] : [];
    const m = new Map();
    for (const p of pts) {
      const t = Number(p?.t ?? p?.[0] ?? 0);
      const v = Number(p?.v ?? p?.[1] ?? NaN);
      if (!Number.isFinite(t) || !Number.isFinite(v) || t <= 0) continue;

      const d = new Date(t);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
      // overwrite -> last value of that UTC day becomes the close
      m.set(key, v);
    }
    maps[sym] = m;
    dateSets.push(new Set(m.keys()));
  }

  // 2) Intersection of dates across all symbols (common overlap window)
  let common = null;
  for (const s of dateSets) {
    if (!common) common = new Set(s);
    else {
      for (const k of Array.from(common)) if (!s.has(k)) common.delete(k);
    }
  }
  const dates = Array.from(common || []).sort();
  if (dates.length < minDays) return { dates: [], lines: {} };

  // 3) Build aligned arrays (same length for all syms)
  const lines = {};
  for (const sym of syms) {
    const m = maps[sym];
    const arr = dates.map((d) => {
      const v = m.get(d);
      return Number.isFinite(v) ? v : null;
    });
    lines[sym] = arr;
  }
  return { dates, lines };
}

const DEFAULT_COMPARE_WEIGHTS = { corr: 40, momentum: 20, opportunity: 20, stability: 10, sentiment: 10 };
const COMPARE_WEIGHT_KEYS = ["corr", "momentum", "opportunity", "stability", "sentiment"];
const COMPARE_WEIGHT_LABELS = {
  corr: "Correlation",
  momentum: "Momentum / RSI Gap",
  opportunity: "Spread / Opportunity",
  stability: "Stability / Volatility",
  sentiment: "Community Sentiment",
};
const COMPARE_WEIGHT_HELP = {
  corr: "How strongly pair relationship similarity influences ranking.",
  momentum: "Weights short-term movement pressure and relative momentum imbalance.",
  opportunity: "Prioritizes larger divergence and higher movement potential.",
  stability: "Controls preference for stable versus highly volatile setups.",
  sentiment: "Weights user ratings and community voting context when available.",
};
function sanitizeCompareWeights(input) {
  const out = { ...DEFAULT_COMPARE_WEIGHTS };
  const src = input && typeof input === "object" ? input : {};
  for (const k of COMPARE_WEIGHT_KEYS) {
    const n = Math.round(Number(src[k]));
    out[k] = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : DEFAULT_COMPARE_WEIGHTS[k];
  }
  let total = COMPARE_WEIGHT_KEYS.reduce((sum, k) => sum + out[k], 0);
  if (total > 100) {
    // Defensive cleanup for older/corrupt localStorage values. User edits still stay manual.
    for (const k of [...COMPARE_WEIGHT_KEYS].reverse()) {
      if (total <= 100) break;
      const cut = Math.min(out[k], total - 100);
      out[k] -= cut;
      total -= cut;
    }
  }
  return out;
}
function compareWeightTotal(weights) {
  const w = sanitizeCompareWeights(weights);
  return COMPARE_WEIGHT_KEYS.reduce((sum, k) => sum + Number(w[k] || 0), 0);
}

function computeBestPairsFromSeries(seriesBySym, limit = 30, scoreWeights = DEFAULT_COMPARE_WEIGHTS, ratingSummaryBySymbol = {}) {
  const { lines } = buildAlignedDailyLines(seriesBySym, { minDays: 20 });
  const syms = Object.keys(lines || {});
  if (syms.length < 2) return [];
  const normLines = normalizeToIndex(lines);

  const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, Number(v)));

  const calcSimpleRsiLocal = (arr, period = 14) => {
    const pts = Array.isArray(arr) ? arr : [];
    if (pts.length < period + 1) return null;
    let gain = 0;
    let loss = 0;
    for (let i = pts.length - period; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const v0 = prev && typeof prev === "object" ? Number(prev.v) : Number(prev);
      const v1 = curr && typeof curr === "object" ? Number(curr.v) : Number(curr);
      if (!Number.isFinite(v0) || !Number.isFinite(v1)) continue;
      const diff = v1 - v0;
      if (diff > 0) gain += diff;
      else loss += Math.abs(diff);
    }
    const avgGain = gain / period;
    const avgLoss = loss / period;
    if (!Number.isFinite(avgGain) || !Number.isFinite(avgLoss)) return null;
    if (avgLoss === 0) return 100;
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  };

  const lastReturnPct = (arr, days = 30) => {
    const vals = (Array.isArray(arr) ? arr : [])
      .map((p) => (p && typeof p === "object") ? Number(p.v) : Number(p))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (vals.length < 2) return null;
    const start = vals[Math.max(0, vals.length - Math.max(2, days))];
    const end = vals[vals.length - 1];
    if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0) return null;
    return ((end / start) - 1) * 100;
  };

  const dailyVolPct = (arr, days = 30) => {
    const vals = (Array.isArray(arr) ? arr : [])
      .map((p) => (p && typeof p === "object") ? Number(p.v) : Number(p))
      .filter((v) => Number.isFinite(v) && v > 0)
      .slice(-Math.max(3, days));
    if (vals.length < 4) return null;
    const rets = [];
    for (let k = 1; k < vals.length; k++) {
      if (vals[k - 1] > 0) rets.push((vals[k] / vals[k - 1]) - 1);
    }
    if (rets.length < 3) return null;
    const mean = rets.reduce((sum, x) => sum + x, 0) / rets.length;
    const variance = rets.reduce((sum, x) => sum + ((x - mean) ** 2), 0) / rets.length;
    return Math.sqrt(variance) * 100;
  };

  const rsiGapScore = (gap) => {
    if (!Number.isFinite(gap)) return 50;
    // Best zone for pair ideas: enough momentum difference to matter, but not so extreme that it is pure chaos.
    if (gap <= 5) return 30 + gap * 5;          // 30..55
    if (gap <= 15) return 55 + (gap - 5) * 4;   // 55..95
    if (gap <= 30) return 95 - (gap - 15) * 1;  // 95..80
    return clamp(80 - (gap - 30) * 2, 35, 80);
  };

  const weights = sanitizeCompareWeights(scoreWeights);
  const res = [];
  for (let i = 0; i < syms.length; i++) {
    for (let j = i + 1; j < syms.length; j++) {
      const a = syms[i], b = syms[j];
      const r = pearson(normLines[a] || [], normLines[b] || []);
      if (r === null) continue;

      const rsiA = calcSimpleRsiLocal((seriesBySym && seriesBySym[a]) || [], 14);
      const rsiB = calcSimpleRsiLocal((seriesBySym && seriesBySym[b]) || [], 14);
      const rsiGap = Number.isFinite(rsiA) && Number.isFinite(rsiB) ? Math.abs(rsiA - rsiB) : null;

      const retA = lastReturnPct((seriesBySym && seriesBySym[a]) || [], 30);
      const retB = lastReturnPct((seriesBySym && seriesBySym[b]) || [], 30);
      const spreadPct = Number.isFinite(retA) && Number.isFinite(retB) ? Math.abs(retA - retB) : null;
      const volA = dailyVolPct((seriesBySym && seriesBySym[a]) || [], 30);
      const volB = dailyVolPct((seriesBySym && seriesBySym[b]) || [], 30);
      const volGap = Number.isFinite(volA) && Number.isFinite(volB) ? Math.abs(volA - volB) : null;

      const corrScore = Math.round(clamp(Math.abs(r) * 100));
      const momentumScore = Math.round(rsiGapScore(rsiGap));
      const opportunityScore = Math.round(Number.isFinite(spreadPct) ? clamp(spreadPct * 3.5, 0, 100) : 50);
      const stabilityScore = Math.round(Number.isFinite(volGap) ? clamp(100 - (volGap * 10), 25, 100) : 60);
      const sentimentA = userRatingAverageScore(ratingSummaryBySymbol?.[a]);
      const sentimentB = userRatingAverageScore(ratingSummaryBySymbol?.[b]);
      const sentimentScore = Math.round(
        Number.isFinite(sentimentA) && Number.isFinite(sentimentB)
          ? clamp((sentimentA + sentimentB) / 2, 0, 100)
          : 50
      );

      // Composite: correlation still matters, but the score now also reflects momentum gap,
      // real 30D opportunity spread, volatility stability, and optional user/community sentiment.
      const score = Math.round(
        (corrScore * (Number(weights.corr || 0) / 100)) +
        (momentumScore * (Number(weights.momentum || 0) / 100)) +
        (opportunityScore * (Number(weights.opportunity || 0) / 100)) +
        (stabilityScore * (Number(weights.stability || 0) / 100)) +
        (sentimentScore * (Number(weights.sentiment || 0) / 100))
      );

      res.push({
        pair: `${a}/${b}`,
        a,
        b,
        corr: r,
        score,
        corrScore,
        momentumScore,
        opportunityScore,
        stabilityScore,
        sentimentScore,
        rsiA,
        rsiB,
        rsiGap,
        retA,
        retB,
        spreadPct,
        volA,
        volB,
        volGap,
      });
    }
  }
  res.sort((x, y) => (y.score - x.score) || (y.corrScore - x.corrScore));
  return res.slice(0, limit);
}

function pearson(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 10) return null;
  let sx = 0,
    sy = 0,
    sxx = 0,
    syy = 0,
    sxy = 0,
    m = 0;
  for (let i = 0; i < n; i++) {
    const x = a[i],
      y = b[i];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    m++;
    sx += x;
    sy += y;
    sxx += x * x;
    syy += y * y;
    sxy += x * y;
  }
  if (m < 10) return null;
  const num = m * sxy - sx * sy;
  const den = Math.sqrt((m * sxx - sx * sx) * (m * syy - sy * sy));
  if (!den) return null;
  return num / den;
}

function computeBestPairs(chart, limit = 30) {
  const lines = chart?.lines || {};
  const syms = Object.keys(lines);
  if (syms.length < 2) return [];
  const normLines = normalizeToIndex(lines);

  const weights = sanitizeCompareWeights(scoreWeights);
  const res = [];
  for (let i = 0; i < syms.length; i++) {
    for (let j = i + 1; j < syms.length; j++) {
      const a = syms[i],
        b = syms[j];
      const r = pearson(normLines[a] || [], normLines[b] || []);
      if (r === null) continue;
      const score = Math.round(Math.abs(r) * 100);
      res.push({ pair: `${a}/${b}`, corr: r, score });
    }
  }
  res.sort((x, y) => y.score - x.score);
  return res.slice(0, limit);
}

// ------------------------
// App (inner)
// ------------------------


function EngineEventHistory({ engine, events = [] }) {
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const rows = Array.isArray(events) ? events : [];
  const filtered = rows.filter((ev) => {
    const status = String(ev?.status || ev?.eventType || ev?.action || "EVENT").toUpperCase();
    if (filter === "ALL") return true;
    if (filter === "OPEN") return status.includes("OPEN") || status.includes("ACTIVE") || status.includes("RUNNING");
    if (filter === "CLOSED") return status.includes("CLOSED") || status.includes("FILLED") || status.includes("SETTLED") || status.includes("STOPPED");
    if (filter === "PROFIT") return Number(ev?.netUsd ?? ev?.profitUsd ?? ev?.pnlUsd ?? 0) > 0;
    if (filter === "LOSS") return Number(ev?.netUsd ?? ev?.profitUsd ?? ev?.pnlUsd ?? 0) < 0;
    if (filter === "ERROR") return status.includes("ERROR") || status.includes("FAIL") || status.includes("BLOCKED");
    return true;
  });
  const visible = showAll ? filtered : filtered.slice(0, 10);
  const exportFile = (kind) => {
    const stamp = Date.now();
    if (kind === "json") {
      const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${engine.toLowerCase()}-event-history-${stamp}.json`; a.click(); URL.revokeObjectURL(url); return;
    }
    const cols = ["ts","eventType","status","chain","asset","side","priceUsd","quantity","budgetUsd","grossUsd","costsUsd","netUsd","reason","txHash"];
    const esc = (v) => `"${String(v ?? "").replaceAll('"','""')}"`;
    const lines = [cols.join(","), ...filtered.map((ev) => cols.map((c) => esc(ev?.[c] ?? "")).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${engine.toLowerCase()}-event-history-${stamp}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <details style={{ order: 9999, gridColumn: "1 / -1", width: "100%", marginTop: 10, marginBottom: 0, borderRadius: 12, border: "1px solid rgba(139,220,255,.18)", background: "rgba(0,0,0,.13)", overflow: "hidden" }}>
      <summary style={{ cursor: "pointer", padding: "9px 10px", fontWeight: 950, color: "#8bdcff", listStyle: "none" }}>{engine} Event History · {rows.length} saved</summary>
      <div style={{ display: "grid", gap: 7, padding: "0 10px 10px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {["ALL","OPEN","CLOSED","PROFIT","LOSS","ERROR"].map((x) => <button key={x} type="button" className="miniBtn" onClick={() => setFilter(x)} style={{ opacity: filter === x ? 1 : .65 }}>{x}</button>)}
          <button type="button" className="miniBtn" onClick={() => setShowAll((v) => !v)}>{showAll ? "Show latest 10" : "Show Full History"}</button>
          <button type="button" className="miniBtn" onClick={() => exportFile("csv")}>Export CSV</button>
          <button type="button" className="miniBtn" onClick={() => exportFile("json")}>Export JSON</button>
        </div>
        {visible.length ? visible.map((ev, i) => {
          const ts = Number(ev?.ts || ev?.updatedTs || ev?.createdTs || 0);
          const status = String(ev?.status || ev?.eventType || ev?.action || "EVENT").toUpperCase();
          const net = Number(ev?.netUsd ?? ev?.profitUsd ?? ev?.pnlUsd ?? 0);
          return <div key={ev?.eventKey || `${engine}-${ts}-${i}`} className="muted tiny" style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 6 }}>
            <div><b style={{ color: net > 0 ? "#86efac" : net < 0 ? "#ff8a8a" : "#8bdcff" }}>{ts ? new Date(ts * (ts < 1000000000000 ? 1000 : 1)).toLocaleString() : "—"} · {ev?.asset || ev?.symbol || "ASSET"} · {status}</b></div>
            <div>{ev?.chain || "—"} · {ev?.side || ev?.action || "—"}{Number(ev?.priceUsd || 0) > 0 ? ` @ ${fmtUsd(Number(ev.priceUsd))}` : ""}{Number(ev?.quantity || 0) > 0 ? ` · Qty ${fmtQty(Number(ev.quantity))}` : ""}</div>
            <div>Budget {fmtUsd(Number(ev?.budgetUsd || ev?.amountUsd || 0))} · Gross {fmtUsd(Number(ev?.grossUsd || 0))} · Costs {fmtUsd(Number(ev?.costsUsd || 0))} · Net {net >= 0 ? "+" : ""}{fmtUsd(net)}</div>
            {ev?.reason ? <div>Reason: {toUserFacingReason(ev.reason)}</div> : null}{ev?.txHash ? <div className="mono">Tx: {ev.txHash}</div> : null}
          </div>;
        }) : <div className="muted tiny">No saved {engine.toLowerCase()} events yet.</div>}
      </div>
    </details>
  );
}

function AppInner({ coreVaultSessionPreview = null, coreVaultRecoveryJobs: coreVaultRecoveryJobsProp = null, onRecoverExactCoreVaultSession = null, exactOrphanRecoveryBusyKey = "" , refreshOnchainSessionsSilent = null }) {
  const coreVaultRecoveryJobs = (coreVaultRecoveryJobsProp && typeof coreVaultRecoveryJobsProp === 'object') ? coreVaultRecoveryJobsProp : {};

  // Multi-chain config: five primary EVM networks are always visible; configured V5 Vaults activate automatically.
  const CHAIN_ID = { ETH: 1, POL: 137, BNB: 56, ARB: 42161, OP: 10, BASE: 8453, AVAX: 43114, FTM: 250 };
  const CHAIN_LABELS = {
    ETH: "ETH (Ethereum)",
    POL: "POL (Polygon)",
    BNB: "BNB (BNB Chain)",
    ARB: "ARB (Arbitrum)",
    OP: "OP (Optimism)",
    BASE: "BASE (Base)",
    AVAX: "AVAX (Avalanche)",
    FTM: "FTM (Fantom)",
  };
  const ENABLED_CHAINS = PRIMARY_EVM_CHAINS;
  const DEFAULT_CHAIN = "ETH";

// One-time storage version gate: clears *derived* caches after deployments (keeps user selections)
useEffect(() => {
  try {
    const v = localStorage.getItem(LS_APP_VERSION);
    if (v !== APP_VERSION) {
      // Only clear volatile caches; never wipe user choices like compare set/watch items
      const keysToClear = [
        LS_WATCH_ROWS_CACHE,
        LS_COMPARE_SERIES_CACHE,
        LS_COMPARE_STORE,
      ];
      for (const k of keysToClear) {
        try { localStorage.removeItem(k); } catch {}
      }
      localStorage.setItem(LS_APP_VERSION, APP_VERSION);
    }
  } catch (e) {
    // ignore storage errors (private mode, blocked, etc.)
  }
}, []);

  
  const [watchErr, setWatchErr] = useState("");
const [errorMsg, setErrorMsg] = useState("");

  const isDesktopWide = typeof window !== "undefined" ? window.innerWidth >= 981 : true;
  const [activePanel, setActivePanel] = useState(null);

  const isCompactMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const isCompactWatchNumbers = typeof window !== "undefined" && window.innerWidth <= 1280;
  const compactGridChipStyle = {
    minHeight: isCompactMobile ? "28px" : "34px",
    height: isCompactMobile ? "28px" : "34px",
    padding: isCompactMobile ? "0 10px" : "6px 12px",
    borderRadius: isCompactMobile ? "10px" : "12px",
    fontSize: isCompactMobile ? "12px" : "13px",
    lineHeight: 1,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Privy (Auth + embedded wallet). IMPORTANT: We do NOT trigger MetaMask here.
  // External wallets must be optional and only enabled explicitly elsewhere.
  const { ready, authenticated, login, logout, getAccessToken, user: privyUser } = usePrivy();
  const { wallets: privyWallets } = useWallets();
  const { addSigners, removeSigners } = useSigners();
  const _privySignerSetupInFlight = useRef(false);

  // Prevent duplicate Privy login/sign flows (can cause AbortError / "already logged in")
  const _loginInFlight = useRef(false);

  // Prevent order flicker when backend GET lags behind POST/DB writes

  const _loginRetryUsed = useRef(false);
  const _backendAuthInFlight = useRef(false);

  const _autoAuthStarted = useRef(false);

  // auth
  // NOTE: Backend expects its OWN Bearer token (issued by /api/auth/verify),
  // not the Privy JWT. We keep both:
  const [privyJwt, setPrivyJwt] = useState("");
  const [token, setToken] = useLocalStorageState("nexus_token", ""); // backend token
  const [wallet, setWallet] = useLocalStorageState("nexus_wallet", "");
  const walletAddress = wallet; // alias for older handlers / debug

  const [marketBannerItems, setMarketBannerItems] = useState([
    { label: "Trader Pulse", value: "Loading live liquidity, volatility and risk…", detail: "Waiting for real market data from the backend.", metric: "LIVE", tone: "neutral", chartType: "line", chartData: [] },
  ]);
  const [marketBannerIndex, setMarketBannerIndex] = useState(0);
  const [shadowMarketContext, setShadowMarketContext] = useState({
    market_score: 50,
    market_risk_score: 50,
    breadth_pct: 50,
    mcap_change_pct: 0,
    vol_cap: 0,
    liquidity_score: 50,
    source: "frontend_default",
    updated_ts: 0,
  });

  useEffect(() => {
    let alive = true;

    const fmtBannerUsd = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      if (Math.abs(n) >= 1_000_000_000_000) return `$${stripTrailingZeros((n / 1_000_000_000_000).toFixed(2))}T`;
      if (Math.abs(n) >= 1_000_000_000) return `$${stripTrailingZeros((n / 1_000_000_000).toFixed(2))}B`;
      if (Math.abs(n) >= 1_000_000) return `$${stripTrailingZeros((n / 1_000_000).toFixed(2))}M`;
      return fmtUsd(n);
    };

    const pctText = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
    };

    const cleanSeries = (arr = []) => (Array.isArray(arr) ? arr : [])
      .map((x) => Array.isArray(x) ? Number(x[1]) : Number(x))
      .filter((n) => Number.isFinite(n));

    const pctSeries = (values = []) => {
      const v = cleanSeries(values);
      if (v.length < 2) return [];
      const base = v[0] || 1;
      return v.map((x) => ((x - base) / base) * 100);
    };

    const returnSeries = (values = []) => {
      const v = cleanSeries(values);
      const out = [];
      for (let i = 1; i < v.length; i += 1) {
        const prev = v[i - 1];
        if (prev) out.push(((v[i] - prev) / prev) * 100);
      }
      return out;
    };

    const sumAlignedSeries = (seriesList = []) => {
      const cleaned = seriesList.map(cleanSeries).filter((v) => v.length >= 2);
      if (!cleaned.length) return [];
      const len = Math.min(...cleaned.map((v) => v.length));
      return Array.from({ length: len }, (_, i) => cleaned.reduce((sum, v) => sum + Number(v[v.length - len + i] || 0), 0));
    };

    const avgAbsReturnSeries = (seriesList = []) => {
      const returns = seriesList.map(returnSeries).filter((v) => v.length >= 1);
      if (!returns.length) return [];
      const len = Math.min(...returns.map((v) => v.length));
      return Array.from({ length: len }, (_, i) => {
        const vals = returns.map((v) => Math.abs(Number(v[v.length - len + i] || 0)));
        return vals.reduce((a, b) => a + b, 0) / Math.max(1, vals.length);
      });
    };

    const latestPctChange = (values = []) => {
      const v = cleanSeries(values);
      if (v.length < 2 || !v[0]) return NaN;
      return ((v[v.length - 1] - v[0]) / v[0]) * 100;
    };


    const KNOWN_MARKET_IDS = {
      BTC: "bitcoin",
      ETH: "ethereum",
      BNB: "binancecoin",
      XRP: "ripple",
      SOL: "solana",
      POL: "polygon-ecosystem-token",
      MATIC: "polygon-ecosystem-token",
      ADA: "cardano",
      AVAX: "avalanche-2",
      LINK: "chainlink",
      TON: "the-open-network",
      DOGE: "dogecoin",
      TRX: "tron",
      DOT: "polkadot",
      LTC: "litecoin",
      BCH: "bitcoin-cash",
      UNI: "uniswap",
      NEAR: "near",
      APT: "aptos",
      ARB: "arbitrum",
      OP: "optimism",
      SUI: "sui",
      INJ: "injective-protocol",
      FET: "fetch-ai",
      RENDER: "render-token",
      RNDR: "render-token",
      TAO: "bittensor",
    };

    const readJsonLS = (key, fallback) => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch {
        return fallback;
      }
    };

    const isProbablyCgId = (value) => {
      const v = String(value || "").trim().toLowerCase();
      if (!v || v.startsWith("0x")) return false;
      if (/^[a-z0-9-]{3,80}$/.test(v) && !/^[a-z]{1,6}$/.test(v)) return true;
      return false;
    };

    const pushAdaptiveCoin = (map, id, sym, source = "market") => {
      const coinId = String(id || "").trim().toLowerCase();
      const symbol = String(sym || "").trim().toUpperCase();
      if (!coinId || !symbol || coinId.startsWith("0x")) return;
      if (!map.has(coinId)) map.set(coinId, { id: coinId, sym: symbol, sources: new Set() });
      map.get(coinId).sources.add(source);
    };

    const buildAdaptiveCoinUniverse = () => {
      const map = new Map();
      const core = [
        ["bitcoin", "BTC", "core"],
        ["ethereum", "ETH", "core"],
      ];
      const broadMarket = [
        ["binancecoin", "BNB", "major"],
        ["ripple", "XRP", "major"],
        ["solana", "SOL", "major"],
        ["polygon-ecosystem-token", "POL", "major"],
        ["cardano", "ADA", "major"],
        ["avalanche-2", "AVAX", "major"],
        ["chainlink", "LINK", "major"],
        ["the-open-network", "TON", "major"],
        ["dogecoin", "DOGE", "major"],
        ["arbitrum", "ARB", "major"],
        ["optimism", "OP", "major"],
        ["sui", "SUI", "major"],
      ];

      [...core, ...broadMarket].forEach(([id, sym, source]) => pushAdaptiveCoin(map, id, sym, source));

      const addFromObj = (obj, source) => {
        if (!obj || typeof obj !== "object") return;
        const sym = String(obj.symbol || obj.sym || obj.coin || obj.ticker || "").trim().toUpperCase();
        const directId = obj.coingecko_id || obj.coingeckoId || obj.cg_id || obj.coin_id || obj.coinId;
        const id = isProbablyCgId(directId) ? directId : KNOWN_MARKET_IDS[sym];
        if (id && sym) pushAdaptiveCoin(map, id, sym, source);
      };

      const watchItemsLS = readJsonLS("nexus_watch_items", []);
      const watchRowsLS = readJsonLS(LS_WATCH_ROWS_CACHE, []);
      if (Array.isArray(watchItemsLS)) watchItemsLS.forEach((x) => addFromObj(x, "watchlist"));
      if (Array.isArray(watchRowsLS)) watchRowsLS.forEach((x) => addFromObj(x, "watchlist"));

      return Array.from(map.values()).slice(0, 24).map((x) => ({ ...x, sources: Array.from(x.sources || []) }));
    };

    const selectAdaptiveCoins = (universe, prices) => {
      const safe = Array.isArray(universe) ? universe : [];
      const coreIds = new Set(["bitcoin", "ethereum"]);
      const core = safe.filter((c) => coreIds.has(c.id));
      const scored = safe
        .filter((c) => !coreIds.has(c.id))
        .map((c) => {
          const p = prices?.[c.id] || {};
          const ch = Number(p.usd_24h_change);
          const vol = Number(p.usd_24h_vol);
          const cap = Number(p.usd_market_cap);
          const sourceBoost = c.sources?.includes("watchlist") ? 18 : c.sources?.includes("major") ? 7 : 0;
          const volatilityScore = Number.isFinite(ch) ? Math.min(45, Math.abs(ch) * 4) : 0;
          const volumeScore = Number.isFinite(vol) && vol > 0 ? Math.min(25, Math.log10(vol) * 3) : 0;
          const capScore = Number.isFinite(cap) && cap > 0 ? Math.min(15, Math.log10(cap) * 1.4) : 0;
          return { ...c, score: sourceBoost + volatilityScore + volumeScore + capScore, ch, vol, cap };
        })
        .sort((a, b) => b.score - a.score);

      const picked = [...core, ...scored.slice(0, 6)];
      const seen = new Set();
      return picked.filter((c) => {
        if (!c?.id || seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
    };

    const loadGlobalMarketBanner = async () => {
      try {
        const universe = buildAdaptiveCoinUniverse();
        const universeIds = universe.map((c) => c.id).filter(Boolean).join(",");

        const [globalRes, priceRes] = await Promise.allSettled([
          api("/api/coingecko/global"),
          api(`/api/coingecko/simple_price?ids=${encodeURIComponent(universeIds)}&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true&include_market_cap=true`),
        ]);

        const globalRaw = globalRes.status === "fulfilled" ? globalRes.value : {};
        const globalData = globalRaw?.data || globalRaw || {};
        const prices = priceRes.status === "fulfilled" ? priceRes.value || {} : {};

        const selectedCoins = selectAdaptiveCoins(universe, prices);
        const coins = selectedCoins.map((c) => [c.id, c.sym, c.sources || []]);

        const chartResults = await Promise.allSettled(
          coins.map(([id]) => api(`/api/coingecko/market_chart/${encodeURIComponent(id)}?vs_currency=usd&days=7&interval=daily`))
        );

        const chartById = {};
        chartResults.forEach((res, idx) => {
          if (res.status === "fulfilled" && res.value) chartById[coins[idx][0]] = res.value;
        });

        const priceSeriesById = {};
        const volumeSeriesById = {};
        coins.forEach(([id]) => {
          priceSeriesById[id] = cleanSeries(chartById[id]?.prices || []);
          volumeSeriesById[id] = cleanSeries(chartById[id]?.total_volumes || []);
        });

        const priceSeriesList = Object.values(priceSeriesById).filter((v) => v.length >= 2);
        const volumeSeriesList = Object.values(volumeSeriesById).filter((v) => v.length >= 2);

        const next = [];
        const capChange = Number(globalData?.market_cap_change_percentage_24h_usd);
        const totalCap = Number(globalData?.total_market_cap?.usd);
        const totalVol = Number(globalData?.total_volume?.usd);
        const btcDom = Number(globalData?.market_cap_percentage?.btc);
        const ethDom = Number(globalData?.market_cap_percentage?.eth);
        const liquidityRatio = Number.isFinite(totalVol) && Number.isFinite(totalCap) && totalCap > 0
          ? (totalVol / totalCap) * 100
          : NaN;

        const majorChanges = coins
          .map(([id]) => {
            const live24h = Number(prices?.[id]?.usd_24h_change);
            return Number.isFinite(live24h) ? live24h : latestPctChange(priceSeriesById[id]);
          })
          .filter((n) => Number.isFinite(n));
        const avgAbsVolatility = majorChanges.length
          ? majorChanges.reduce((a, b) => a + Math.abs(b), 0) / majorChanges.length
          : NaN;
        const gainers = majorChanges.filter((n) => n > 0).length;
        const breadthPct = majorChanges.length ? (gainers / majorChanges.length) * 100 : NaN;

        const volatilityCurve = avgAbsReturnSeries(priceSeriesList);
        const liquidityCurve = sumAlignedSeries(volumeSeriesList);
        const btcCurve = pctSeries(priceSeriesById.bitcoin || []);
        const ethCurve = pctSeries(priceSeriesById.ethereum || []);
        const rotationCurve = (() => {
          const len = Math.min(btcCurve.length, ethCurve.length);
          if (!len) return [];
          return Array.from({ length: len }, (_, i) => ethCurve[ethCurve.length - len + i] - btcCurve[btcCurve.length - len + i]);
        })();
        const riskCurve = (() => {
          const len = Math.max(volatilityCurve.length, liquidityCurve.length, btcCurve.length);
          if (!len) return [];
          return Array.from({ length: len }, (_, i) => {
            const vol = Number(volatilityCurve[volatilityCurve.length - len + i] ?? avgAbsVolatility ?? 0);
            const liqBase = liquidityCurve[0] || 1;
            const liqNow = Number(liquidityCurve[liquidityCurve.length - len + i] ?? liquidityCurve[liquidityCurve.length - 1] ?? liqBase);
            const liqPct = liqBase ? ((liqNow - liqBase) / liqBase) * 100 : 0;
            const btcMove = Number(btcCurve[btcCurve.length - len + i] ?? 0);
            let score = 50;
            score += Math.max(-12, Math.min(12, liqPct / 2));
            score += Math.max(-10, Math.min(10, btcMove));
            score -= Math.max(0, Math.min(18, vol * 3));
            return Math.max(0, Math.min(100, score));
          });
        })();

        const riskScore = (() => {
          let score = 50;
          if (Number.isFinite(capChange)) score += Math.max(-18, Math.min(18, capChange * 4));
          if (Number.isFinite(liquidityRatio)) score += liquidityRatio >= 4 ? 8 : liquidityRatio < 2.5 ? -8 : 0;
          if (Number.isFinite(avgAbsVolatility)) score += avgAbsVolatility > 5 ? -12 : avgAbsVolatility > 3 ? -6 : 4;
          if (Number.isFinite(breadthPct)) score += breadthPct >= 66 ? 8 : breadthPct <= 34 ? -8 : 0;
          if (Number.isFinite(btcDom) && Number.isFinite(capChange) && btcDom > 58 && capChange < 0) score -= 6;
          return Math.max(0, Math.min(100, Math.round(score)));
        })();
        const riskLabel = riskScore >= 62 ? "Risk-On" : riskScore <= 42 ? "Risk-Off" : "Neutral";
        const riskTone = riskScore >= 62 ? "positive" : riskScore <= 42 ? "negative" : "warning";

        // Shadow/Strategist must use the same market context the user sees in the header.
        // Do not let backend runtime infer market regime from slot scores or stale session meta.
        setShadowMarketContext({
          market_score: riskScore,
          marketScore: riskScore,
          market_risk_score: riskScore,
          marketRiskScore: riskScore,
          breadth_pct: Number.isFinite(breadthPct) ? breadthPct : 50,
          breadthPct: Number.isFinite(breadthPct) ? breadthPct : 50,
          market_breadth: Number.isFinite(breadthPct) ? breadthPct : 50,
          mcap_change_pct: Number.isFinite(capChange) ? capChange : 0,
          mcapChangePct: Number.isFinite(capChange) ? capChange : 0,
          market_cap_change_pct: Number.isFinite(capChange) ? capChange : 0,
          vol_cap: Number.isFinite(liquidityRatio) ? liquidityRatio : 0,
          volCap: Number.isFinite(liquidityRatio) ? liquidityRatio : 0,
          liquidity_score: Number.isFinite(liquidityRatio) ? Math.max(0, Math.min(100, liquidityRatio * 12.5)) : 50,
          liquidityScore: Number.isFinite(liquidityRatio) ? Math.max(0, Math.min(100, liquidityRatio * 12.5)) : 50,
          avg_abs_volatility: Number.isFinite(avgAbsVolatility) ? avgAbsVolatility : 0,
          btc_dominance: Number.isFinite(btcDom) ? btcDom : null,
          eth_dominance: Number.isFinite(ethDom) ? ethDom : null,
          source: "frontend_market_banner",
          updated_ts: Date.now(),
        });

        next.push({
          label: "Market Regime",
          value: `${riskLabel} ${riskScore}/100${Number.isFinite(capChange) ? ` · MCap ${pctText(capChange)}` : ""}`,
          detail: Number.isFinite(capChange) ? `Live global market cap ${pctText(capChange)} · breadth ${Number.isFinite(breadthPct) ? breadthPct.toFixed(0) + "%" : "—"}` : "Live risk state from market cap, breadth, liquidity and volatility.",
          metric: `${riskScore}/100`,
          tone: riskTone,
          chartType: "line",
          chartData: riskCurve,
        });

        if (Number.isFinite(liquidityRatio) || Number.isFinite(totalVol) || liquidityCurve.length) {
          next.push({
            label: "Global Liquidity",
            value: `${Number.isFinite(liquidityRatio) ? `${liquidityRatio.toFixed(2)}% vol/cap` : "—"}${Number.isFinite(totalVol) ? ` · Vol ${fmtBannerUsd(totalVol)}` : ""}`,
            detail: "Live volume curve from major market charts plus global vol/cap pressure.",
            metric: Number.isFinite(liquidityRatio) ? `${liquidityRatio.toFixed(2)}%` : fmtBannerUsd(totalVol),
            tone: Number.isFinite(liquidityRatio) && liquidityRatio >= 4 ? "positive" : Number.isFinite(liquidityRatio) && liquidityRatio < 2.5 ? "negative" : "neutral",
            chartType: "bars",
            chartData: liquidityCurve,
          });
        }

        if (Number.isFinite(avgAbsVolatility) || volatilityCurve.length) {
          next.push({
            label: "Volatility Pulse",
            value: `${Number.isFinite(avgAbsVolatility) ? avgAbsVolatility.toFixed(2) + "% avg 24h move" : "Live volatility"} · ${Number.isFinite(breadthPct) ? `${breadthPct.toFixed(0)}% green` : "breadth —"}`,
            detail: `Live adaptive 7D return-volatility from BTC/ETH core plus ${Math.max(0, coins.length - 2)} active market/watchlist assets.`,
            metric: Number.isFinite(avgAbsVolatility) ? `${avgAbsVolatility.toFixed(2)}%` : "LIVE",
            tone: avgAbsVolatility > 5 ? "negative" : avgAbsVolatility > 3 ? "warning" : "positive",
            chartType: "bars",
            chartData: volatilityCurve,
          });
        }

        if (Number.isFinite(btcDom) || btcCurve.length) {
          next.push({
            label: "Dominance Risk",
            value: `BTC ${Number.isFinite(btcDom) ? btcDom.toFixed(1) + "%" : "—"}${Number.isFinite(ethDom) ? ` · ETH ${ethDom.toFixed(1)}%` : ""}`,
            detail: btcDom > 58 ? "High BTC dominance can signal defensive positioning and lower alt risk appetite." : "Dominance is not showing extreme defensive pressure.",
            metric: Number.isFinite(btcDom) ? `${btcDom.toFixed(1)}%` : "LIVE",
            tone: btcDom > 58 ? "warning" : "neutral",
            chartType: "line",
            chartData: btcCurve,
          });
        }

        const btcCh = Number.isFinite(Number(prices?.bitcoin?.usd_24h_change)) ? Number(prices?.bitcoin?.usd_24h_change) : latestPctChange(priceSeriesById.bitcoin || []);
        const ethCh = Number.isFinite(Number(prices?.ethereum?.usd_24h_change)) ? Number(prices?.ethereum?.usd_24h_change) : latestPctChange(priceSeriesById.ethereum || []);
        if ((Number.isFinite(btcCh) && Number.isFinite(ethCh)) || rotationCurve.length) {
          const spread = Number.isFinite(btcCh) && Number.isFinite(ethCh) ? ethCh - btcCh : Number(rotationCurve[rotationCurve.length - 1] || 0);
          next.push({
            label: "Rotation Signal",
            value: `ETH-BTC ${spread >= 0 ? "+" : ""}${spread.toFixed(2)}%${Number.isFinite(btcCh) && Number.isFinite(ethCh) ? ` · BTC ${pctText(btcCh)} / ETH ${pctText(ethCh)}` : ""}`,
            detail: spread >= 0 ? "Live ETH curve is outperforming BTC in the current window." : "Live BTC curve is outperforming ETH in the current window.",
            metric: `${spread >= 0 ? "+" : ""}${spread.toFixed(2)}%`,
            tone: Math.abs(spread) >= 2 ? "warning" : "neutral",
            chartType: "line",
            chartData: rotationCurve,
          });
        }

        const strongest = coins
          .map(([id, sym]) => ({ sym, ch: Number.isFinite(Number(prices?.[id]?.usd_24h_change)) ? Number(prices?.[id]?.usd_24h_change) : latestPctChange(priceSeriesById[id]) }))
          .filter((x) => Number.isFinite(x.ch))
          .sort((a, b) => b.ch - a.ch);
        if (strongest.length) {
          const top = strongest[0];
          const weak = strongest[strongest.length - 1];
          next.push({
            label: "Relative Strength",
            value: `Strong ${top.sym} ${pctText(top.ch)} · Weak ${weak.sym} ${pctText(weak.ch)}`,
            detail: `Live top/weak adaptive spread: ${pctText(top.ch - weak.ch)} across core, active market and watchlist assets.`,
            metric: `${top.sym}`,
            tone: Math.abs(top.ch - weak.ch) >= 4 ? "warning" : "neutral",
            chartType: "line",
            chartData: strongest.map((x) => x.ch),
          });
        }

        if (alive && next.length) {
          setMarketBannerItems(next.slice(0, 8));
          setMarketBannerIndex((i) => i % next.length);
        }
      } catch (e) {
        if (alive) {
          setMarketBannerItems([{ label: "Trader Pulse", value: "Live trader market data unavailable", detail: "No dummy chart is shown. Waiting for real market data from the backend.", metric: "—", tone: "neutral", chartType: "line", chartData: [] }]);
          setMarketBannerIndex(0);
        }
      }
    };

    loadGlobalMarketBanner();
    const refreshTimer = setInterval(loadGlobalMarketBanner, 120000);
    return () => {
      alive = false;
      clearInterval(refreshTimer);
    };
  }, [token, wallet]);

  useEffect(() => {
    const t = setInterval(() => {
      setMarketBannerIndex((i) => (marketBannerItems.length ? (i + 1) % marketBannerItems.length : 0));
    }, 45000);
    return () => clearInterval(t);
  }, [marketBannerItems.length]);

  const activeMarketBanner = marketBannerItems[marketBannerIndex] || marketBannerItems[0] || {
    label: "Trader Pulse",
    value: "Loading liquidity, volatility and risk…",
    detail: "Preparing trader intelligence feed.",
    metric: "—",
    tone: "neutral",
    chartType: "line",
    chartData: [],
  };

  const renderMarketDeskChart = (item) => {
    const values = Array.isArray(item?.chartData) && item.chartData.length ? item.chartData.map(Number).filter(Number.isFinite) : [];
    if (!values.length) {
      return <div className="marketDeskNoChart">LIVE DATA</div>;
    }
    const w = 210;
    const h = 76;
    const pad = 8;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1, max - min);
    const points = values.map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / Math.max(1, values.length - 1);
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return [x, y];
    });
    const path = points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const area = `${path} L${(w - pad).toFixed(1)} ${(h - pad).toFixed(1)} L${pad.toFixed(1)} ${(h - pad).toFixed(1)} Z`;
    const tone = item?.tone || "neutral";
    if (item?.chartType === "bars") {
      const bw = Math.max(4, (w - pad * 2) / Math.max(1, values.length) - 3);
      return (
        <svg className={`marketDeskChartSvg ${tone}`} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${item?.label || "Market"} mini chart`}>
          <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} className="marketDeskAxis" />
          {values.map((v, i) => {
            const bh = Math.max(5, ((v - min) / range) * (h - pad * 2));
            const x = pad + i * ((w - pad * 2) / Math.max(1, values.length));
            const y = h - pad - bh;
            return <rect key={i} x={x.toFixed(1)} y={y.toFixed(1)} width={bw.toFixed(1)} height={bh.toFixed(1)} rx="3" className="marketDeskBar" />;
          })}
        </svg>
      );
    }
    return (
      <svg className={`marketDeskChartSvg ${tone}`} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${item?.label || "Market"} mini chart`}>
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} className="marketDeskAxis" />
        <path d={area} className="marketDeskArea" />
        <path d={path} className="marketDeskLine" />
        {points.slice(-1).map(([x, y], i) => <circle key={i} cx={x} cy={y} r="4" className="marketDeskDot" />)}
      </svg>
    );
  };

  const visibleMarketBannerItems = useMemo(() => {
    const arr = Array.isArray(marketBannerItems) && marketBannerItems.length
      ? marketBannerItems
      : [activeMarketBanner];
    const maxItems = Math.min(5, arr.length);
    return Array.from({ length: maxItems }, (_, idx) => arr[(marketBannerIndex + idx) % arr.length]);
  }, [marketBannerItems, marketBannerIndex, activeMarketBanner]);
  // Trading policy is UI-only for now (no Vault/Allowance yet).
  // Keep it local to avoid backend auth/CORS coupling during early UX work.
const [walletModalOpen, setWalletModalOpen] = useState(false);
  const walletPanelRef = useRef(null);
  const walletTriggerRef = useRef(null);

  const [withdrawSendOpen, setWithdrawSendOpen] = useState(false);
  const [balActiveChain, setBalActiveChain] = useState(() => {
    try { return normalizeWalletChainKey(localStorage.getItem("nexus_wallet_bal_chain") || DEFAULT_CHAIN); } catch (_) { return DEFAULT_CHAIN; }
  });
  

useEffect(() => {
    try { localStorage.setItem("nexus_wallet_bal_chain", normalizeWalletChainKey(balActiveChain || DEFAULT_CHAIN)); } catch (_) {}
  }, [balActiveChain]);
const [wsChainKey, setWsChainKey] = useState(() => {
    try { return localStorage.getItem("nexus_wallet_bal_chain") || DEFAULT_CHAIN; } catch (_) { return DEFAULT_CHAIN; }
  });
  const [wsInfoOpen, setWsInfoOpen] = useState(false);
  const wsInfoRef = useRef(null);

  // Wallet actions (Vault withdraw + native send)
  const [txBusy, setTxBusy] = useState(false);
  const [txMsg, setTxMsg] = useState("");
  const [securityState, setSecurityState] = useState(null); // null | loading | ok | blocked | cancelled
  const [securityMsg, setSecurityMsg] = useState("");
  const [withdrawAmt, setWithdrawAmt] = useState(""); // in native units (e.g., POL)
  const [depositAmt, setDepositAmt] = useState(""); // deposit into vault (native units, e.g., POL)
  const [sendTo, setSendTo] = useState("");
  const [sendAmt, setSendAmt] = useState(""); // in native units
  const [profitPayoutEnabled, setProfitPayoutEnabled] = useState(false);
  const [profitPayoutPct, setProfitPayoutPct] = useState("50");
  const [profitPayoutAsset, setProfitPayoutAsset] = useState("USDT");
  const [profitPayoutSchedule, setProfitPayoutSchedule] = useState("EVERY_DAYS");
  const [profitPayoutEveryDays, setProfitPayoutEveryDays] = useState("7");
  const [profitPayoutMinUsd, setProfitPayoutMinUsd] = useState("25");
  const [profitPayoutPreview, setProfitPayoutPreview] = useState(null);
  const [profitPayoutBusy, setProfitPayoutBusy] = useState(false);
  const [coreDepositAsset, setCoreDepositAsset] = useState("USDC");
  const [coreDepositAmount, setCoreDepositAmount] = useState("");
  const [coreDepositBusy, setCoreDepositBusy] = useState(false);
  const [coreDepositMsg, setCoreDepositMsg] = useState("");
  const [walletSendAssetKey, setWalletSendAssetKey] = useState("");
  const [walletSendTo, setWalletSendTo] = useState("");
  const [walletSendAmount, setWalletSendAmount] = useState("");
  const [walletSendBusy, setWalletSendBusy] = useState(false);
  const [walletSendMsg, setWalletSendMsg] = useState("");
  // BUILD454: manual Privy wallet swap (user-initiated)
  const [walletSwapFromKey, setWalletSwapFromKey] = useState("");
  const [walletSwapToSym, setWalletSwapToSym] = useState("USDC");
  const [walletSwapAmount, setWalletSwapAmount] = useState("");
  const [walletSwapBusy, setWalletSwapBusy] = useState(false);
  const [walletSwapMsg, setWalletSwapMsg] = useState("");
  const [walletViewTab, setWalletViewTab] = useState("ASSETS");
  const [walletTransactionsStore, setWalletTransactionsStore] = useLocalStorageState("nexus_privy_wallet_transactions_v1", {});

  const walletTransactions = useMemo(() => {
    const key = String(wallet || "").toLowerCase();
    const rows = key && Array.isArray(walletTransactionsStore?.[key]) ? walletTransactionsStore[key] : [];
    return [...rows].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
  }, [wallet, walletTransactionsStore]);

  const walletExplorerTxUrl = useCallback((chainKey, hash) => {
    const chain = normalizeWalletChainKey(chainKey || DEFAULT_CHAIN);
    const base = chain === "ETH"
      ? "https://etherscan.io/tx/"
      : chain === "BNB"
        ? "https://bscscan.com/tx/"
        : "https://polygonscan.com/tx/";
    return hash ? `${base}${hash}` : "";
  }, []);

  const saveWalletTransaction = useCallback((entry) => {
    const walletKey = String(wallet || "").toLowerCase();
    if (!walletKey) return;
    setWalletTransactionsStore((prev) => {
      const current = Array.isArray(prev?.[walletKey]) ? prev[walletKey] : [];
      const hash = String(entry?.txHash || "").toLowerCase();
      const id = String(entry?.id || hash || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
      const existingIndex = current.findIndex((row) =>
        String(row?.id || "") === id ||
        (hash && String(row?.txHash || "").toLowerCase() === hash)
      );
      const previous = existingIndex >= 0 ? current[existingIndex] : {};
      const nextEntry = {
        ...previous,
        ...entry,
        id,
        createdAt: Number(previous?.createdAt || entry?.createdAt || Date.now()),
        updatedAt: Date.now(),
        chain: normalizeWalletChainKey(entry?.chain || previous?.chain || DEFAULT_CHAIN),
        status: String(entry?.status || previous?.status || "SUBMITTED").toUpperCase(),
        txHash: String(entry?.txHash || previous?.txHash || ""),
        from: String(entry?.from || previous?.from || wallet || ""),
      };
      const merged = existingIndex >= 0
        ? current.map((row, index) => index === existingIndex ? nextEntry : row)
        : [nextEntry, ...current];
      return { ...(prev || {}), [walletKey]: merged.slice(0, 250) };
    });
  }, [wallet, setWalletTransactionsStore]);

  const [coreWithdrawSource, setCoreWithdrawSource] = useState("BASE_CAPITAL");
  const [coreWithdrawAsset, setCoreWithdrawAsset] = useState("ETH");
  const [coreWithdrawAmount, setCoreWithdrawAmount] = useState("");
  const [coreWithdrawAddress, setCoreWithdrawAddress] = useState("");
  const [coreWithdrawQuote, setCoreWithdrawQuote] = useState(null);
  const [coreWithdrawQuoteBusy, setCoreWithdrawQuoteBusy] = useState(false);
  const [coreWithdrawExecuteBusy, setCoreWithdrawExecuteBusy] = useState(false);

  // Vault state (on-chain) + operator authorization
  const [vaultState, setVaultState] = useState({
    polBalanceWei: null,
    polBalance: null,
    inCycle: false,
    heldToken: null,
    heldTokenBalWei: null,
    heldTokenBal: null,
    operatorEnabled: false,
  });
  // Sync Withdraw&Send chain with Wallet chain selection
  useEffect(() => {
    if (!withdrawSendOpen) return;
    if (!balActiveChain) return;
    setWsChainKey((prev) => (prev === balActiveChain ? prev : balActiveChain));
  }, [balActiveChain, withdrawSendOpen]);

// Withdraw & Send info tooltip
  useEffect(() => {
    if (!withdrawSendOpen) setWsInfoOpen(false);
  }, [withdrawSendOpen]);

  useEffect(() => {
    if (!wsInfoOpen) return;
    const onDown = (e) => {
      if (wsInfoRef.current && !wsInfoRef.current.contains(e.target)) {
        setWsInfoOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [wsInfoOpen]);

  useEffect(() => {
    setSecurityState(null);
    setSecurityMsg("");
  }, [withdrawSendOpen, balActiveChain, wsChainKey, depositAmt]);

  // Contracts (Vault/Executor/Router) fetched from backend ENV so UI stays in sync after deploys
  const [contracts, setContracts] = useState(null);
  // Fetch contract addresses from backend (Render ENV) once
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/api/contracts", { method: "GET" });
        if (r && r.chains) setContracts(r);
      } catch (e) {
        // don't hard-fail UI if backend doesn't have this endpoint
      }
    })();
  }, []);

  // -------------------------
  // Wallet actions: Vault withdraw + native send
  // -------------------------
  const _getPrimaryEmbeddedWallet = () => {
    return (
      privyWallets?.find((w) =>
        ["privy", "embedded"].includes(String(w?.walletClientType || "").toLowerCase()) ||
        String(w?.connectorType || "").toLowerCase() === "embedded"
      ) || null
    );
  };

  const _getEmbeddedProvider = async () => {
    const embedded = _getPrimaryEmbeddedWallet();
    if (!embedded) throw new Error("Privy embedded wallet is not available.");

    const provider = await embedded.getEthereumProvider?.();
    if (!provider?.request) throw new Error("Privy embedded wallet provider is not available.");

    const embeddedAddress = String(embedded.address || "").toLowerCase();
    const activeAddress = String(wallet || "").toLowerCase();
    if (!/^0x[0-9a-f]{40}$/.test(embeddedAddress)) {
      throw new Error("Privy embedded wallet address is invalid.");
    }
    if (activeAddress && activeAddress !== embeddedAddress) {
      throw new Error("Connected Nexus wallet does not match the Privy embedded wallet. Reconnect Privy and try again.");
    }

    // Verify that the provider authorizes the same account before any signing call.
    // This prevents Privy from rejecting eth_sendTransaction with
    // "The requested account and/or method has not been authorized by the user."
    let providerAccounts = [];
    try {
      providerAccounts = await provider.request({ method: "eth_accounts" });
    } catch (_) {
      providerAccounts = [];
    }
    const normalizedAccounts = Array.isArray(providerAccounts)
      ? providerAccounts.map((a) => String(a || "").toLowerCase())
      : [];
    if (normalizedAccounts.length && !normalizedAccounts.includes(embeddedAddress)) {
      throw new Error("Privy provider is connected to a different wallet account. Reconnect Privy and try again.");
    }

    return provider;
  };

  const signWithEmbeddedWallet = async (embeddedWallet, message, address) => {
    if (!embeddedWallet) throw new Error("No wallet available to sign.");
    const addr = String(address || embeddedWallet?.address || "").toLowerCase();
    if (!addr) throw new Error("Missing wallet address.");
    const provider = await embeddedWallet.getEthereumProvider?.();
    if (!provider?.request) throw new Error("Wallet provider not available.");
    return await provider.request({ method: "personal_sign", params: [String(message), addr] });
  };

  const ensureBackendAuthToken = async (force = false) => {
    const addr = String(wallet || "").toLowerCase();
    if (!addr) throw new Error("Authorization required.");
    if (!force && token) return token;
    if (_backendAuthInFlight.current) throw new Error("Authorization already in progress.");

    const embedded =
      privyWallets?.find((w) =>
        ["privy", "embedded"].includes(String(w?.walletClientType || "").toLowerCase()) ||
        String(w?.connectorType || "").toLowerCase() === "embedded"
      ) ||
      privyWallets?.[0];

    if (!embedded) throw new Error("Authorization required.");

    _backendAuthInFlight.current = true;
    try {
      const nonceRes = await api("/api/auth/nonce", {
        method: "POST",
        body: { address: addr },
      });
      const message = nonceRes?.message;
      const nonce = nonceRes?.nonce;
      if (!message || !nonce) throw new Error("Auth nonce failed.");

      const signature = await signWithEmbeddedWallet(embedded, message, addr);

      const verifyRes = await api("/api/auth/verify", {
        method: "POST",
        body: { address: addr, message, signature, nonce },
      });
      const backendToken = String(verifyRes?.token || "");
      if (!backendToken) throw new Error("Auth verify failed.");

      setToken(backendToken);
      return backendToken;
    } finally {
      _backendAuthInFlight.current = false;
    }
  };

  const _trySwitchChain = async (provider, chainId) => {
    if (!provider?.request || !chainId) return;
    const hexChainId = "0x" + Number(chainId).toString(16);
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexChainId }],
      });
    } catch {
      // Some embedded providers don't support chain switching; the tx will fail if on wrong chain.
    }
  };

  const _encodeUint256 = (bn) => {
    // bn can be a BigNumber (ethers v5 style) or bigint
    let hex = "";
    try {
      hex = typeof bn === "bigint" ? bn.toString(16) : String(bn?.toHexString?.() || "").replace(/^0x/, "");
    } catch {
      hex = "";
    }
    if (!hex) hex = "0";
    return hex.padStart(64, "0");
  };
  const _encodeAddress = (addr) => {
    const a = String(addr || "").trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(a)) throw new Error("Bad address for ABI encode.");
    return a.toLowerCase().replace(/^0x/, "").padStart(64, "0");
  };

  const _encodeBool = (b) => {
    return (b ? "1" : "0").padStart(64, "0");
  };

  const _hexToAddress = (hex) => {
    const h = String(hex || "0x").replace(/^0x/, "").padStart(64, "0");
    return "0x" + h.slice(24); // last 20 bytes
  };

  const _hexToBool = (hex) => {
    try { return BigInt(hex || "0x0") !== 0n; } catch { return false; }
  };

  const _isAddr = (a) => /^0x[a-fA-F0-9]{40}$/.test(String(a || "").trim());

  const sendNative = async () => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      if (!_isAddr(sendTo)) throw new Error("Recipient address invalid.");
      const amt = String(sendAmt || "").trim();
      if (!amt || Number(amt) <= 0) throw new Error("Amount invalid.");
      const chainKey = (activeGridChainKey || wsChainKey || balActiveChain || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      // Hard safety check: ensure we are on the expected chain before sending a tx.
      try {
        const currentHex = await provider.request({ method: "eth_chainId" });
        const wantHex = "0x" + Number(chainId).toString(16);
        if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
          throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${wantHex}).`);
        }
      } catch (e) {
        throw e;
      }

      const valueHex = Utils.hexValue(Utils.parseEther(amt));
      const txHash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet, to: String(sendTo).trim(), value: valueHex, data: "0x" }],
      });

      setTxMsg(`Sent. Tx: ${txHash}`);
      setSendAmt("");
      setSendTo("");
      // refresh wallet balances in background
      setTimeout(() => refreshBalances(), 200);
    } catch (e) {
      setTxMsg(String(e?.message || e || "Send failed"));
    } finally {
      setTxBusy(false);
    }
  };

  const depositToVault = async () => {
    try {
      setTxMsg("");
      setSecurityState("loading");
      setSecurityMsg("🟡 Checking token security...");

      if (!wallet) throw new Error("Wallet not connected.");
      const amt = String(depositAmt || "").trim();
      if (!amt || Number(amt) <= 0) throw new Error("Deposit amount invalid.");

      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr =
        (contracts?.chains?.[chainKey]?.vault || "").trim() ||
        (contracts?.chains?.[String(chainKey).toLowerCase()]?.vault || "").trim();
      if (!_isAddr(vaultAddr)) throw new Error("Vault address not available for this chain.");

      // Current UI deposits native only. We still run the new backend gate here so the
      // same flow is already wired for later token deposits. Native assets are bypassed backend-side.
      const nativeSymbol = String(chainKey || DEFAULT_CHAIN).toUpperCase();
      const pre = await securityPrecheckForDeposit({
        chainKey,
        symbol: nativeSymbol,
        isNative: true,
        token,
      });
      if (pre?.allowed === false) {
        setSecurityState("blocked");
        setSecurityMsg(`🔴 ${pre?.reason || "Deposit blocked by security check."}`);
        throw new Error(pre?.reason || "Deposit blocked by security check.");
      }

      setSecurityState("ok");
      setSecurityMsg("🟢 Token approved. Opening wallet signature...");
      // Let the user actually see the green state before Privy opens.
      await new Promise((r) => setTimeout(r, 3000));

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      // Hard safety check: ensure we are on the expected chain before sending a tx.
      const currentHex = await provider.request({ method: "eth_chainId" });
      const wantHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
        throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${wantHex}).`);
      }

      const valueHex = Utils.hexValue(Utils.parseEther(amt));

      // Try calling deposit() (WETH-style). If vault only has receive(), fallback to empty data.
      const trySend = async (data) => {
        return await provider.request({
          method: "eth_sendTransaction",
          params: [{ from: wallet, to: vaultAddr, value: valueHex, data }],
        });
      };

      let txHash = null;
      try {
        txHash = await trySend("0xd0e30db0"); // deposit()
      } catch (e) {
        txHash = await trySend("0x");
      }

      setSecurityState("ok");
      setSecurityMsg("🟢 Deposit submitted successfully.");
      setTxMsg(`Deposit submitted. Tx: ${txHash}`);
      setDepositAmt("");
      setTimeout(() => refreshBalances(), 1200);
    } catch (e) {
      const msg = String(e?.message || e || "Deposit failed");
      const low = msg.toLowerCase();

      if (e?.code === 4001 || low.includes("user rejected") || low.includes("rejected") || low.includes("denied") || low.includes("cancelled") || low.includes("canceled")) {
        setSecurityState("cancelled");
        setSecurityMsg("🟡 Transaction cancelled by user.");
      } else if (low.includes("execution reverted") || low.includes("estimate gas") || low.includes("call_exception") || low.includes("cycle_open") || low.includes("revert")) {
        setSecurityState("blocked");
        setSecurityMsg("🔴 Deposit rejected by contract.");
      } else if (!securityState) {
        setSecurityState("blocked");
        setSecurityMsg(`🔴 ${msg}`);
      } else if (securityState !== "blocked") {
        setSecurityState("blocked");
        setSecurityMsg(`🔴 ${msg}`);
      }

      setTxMsg(msg);
    } finally {
      setTxBusy(false);
    }
  };

  const withdrawFromVault = async () => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const amt = String(withdrawAmt || "").trim();
      if (!amt || Number(amt) <= 0) throw new Error("Withdraw amount invalid.");

      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr =
        (contracts?.chains?.[chainKey]?.vault || "").trim() ||
        (contracts?.chains?.[String(chainKey).toLowerCase()]?.vault || "").trim();
      if (!_isAddr(vaultAddr)) throw new Error("Vault address not available for this chain.");

      const wei = Utils.parseEther(amt);
      const data = "0x2e1a7d4d" + _encodeUint256(wei); // withdraw(uint256)

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      // Hard safety check: ensure we are on the expected chain before sending a tx.
      try {
        const currentHex = await provider.request({ method: "eth_chainId" });
        const wantHex = "0x" + Number(chainId).toString(16);
        if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
          throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${wantHex}).`);
        }
      } catch (e) {
        throw e;
      }

      const txHash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet, to: vaultAddr, value: "0x0", data }],
      });

      setTxMsg(`Withdraw submitted. Tx: ${txHash}`);
      setWithdrawAmt("");
      setTimeout(() => refreshBalances(), 1200);
    } catch (e) {
      setTxMsg(String(e?.message || e || "Withdraw failed"));
    } finally {
      setTxBusy(false);
    }
  };

const refreshProfitPayoutSettings = async () => {
    try {
      if (!wallet) return;
      const r = await api(`/api/vault/profit-payout-settings`, { method: "GET", token, wallet });
      const st = r?.settings || {};
      setProfitPayoutEnabled(!!st.enabled);
      setProfitPayoutPct(String(st.payoutPct ?? "50"));
      setProfitPayoutAsset(String(st.asset || "USDT").toUpperCase());
      setProfitPayoutSchedule(String(st.schedule || st.frequency || "EVERY_DAYS").toUpperCase());
      setProfitPayoutEveryDays(String(st.everyDays ?? st.intervalDays ?? "7"));
      setProfitPayoutMinUsd(String(st.minimumPayoutUsd ?? st.minPayoutUsd ?? "25"));
      setProfitPayoutPreview(r || null);
    } catch (e) {
      // keep UI stable; this panel is a settings layer and must not block Withdraw.
    }
  };

  const saveProfitPayoutSettings = async () => {
    try {
      if (!wallet) throw new Error("Wallet not connected.");
      setProfitPayoutBusy(true);
      const pct = Math.max(0, Math.min(100, Number(String(profitPayoutPct || "0").replace(",", ".")) || 0));
      const everyDays = Math.max(1, Math.min(365, Math.floor(Number(String(profitPayoutEveryDays || "7").replace(",", ".")) || 7)));
      const minimumPayoutUsd = Math.max(0, Math.min(1000000, Number(String(profitPayoutMinUsd || "0").replace(",", ".")) || 0));
      const schedule = ["IMMEDIATE", "EVERY_DAYS", "MANUAL_ONLY"].includes(String(profitPayoutSchedule || "").toUpperCase())
        ? String(profitPayoutSchedule).toUpperCase()
        : "EVERY_DAYS";
      const r = await api(`/api/vault/profit-payout-settings`, {
        method: "POST",
        token,
        wallet,
        body: {
          enabled: schedule === "MANUAL_ONLY" ? false : !!profitPayoutEnabled,
          schedule,
          frequency: schedule,
          everyDays,
          intervalDays: everyDays,
          minimumPayoutUsd,
          payoutPct: pct,
          asset: profitPayoutAsset || "USDT",
          destination: "CONNECTED_WALLET",
          source: "SECURED_PROFIT_ONLY",
        },
      });
      setProfitPayoutPreview(r || null);
      const scheduleLabel = schedule === "IMMEDIATE" ? "immediate" : schedule === "EVERY_DAYS" ? `every ${everyDays} day${everyDays === 1 ? "" : "s"}` : "manual only";
      setTxMsg(`Profit payout settings saved · ${scheduleLabel}${r?.estimatedNetToWalletUsd != null ? ` · preview net ≈ $${r.estimatedNetToWalletUsd}` : ""}.`);
    } catch (e) {
      setTxMsg(String(e?.message || e || "Profit payout settings failed"));
    } finally {
      setProfitPayoutBusy(false);
    }
  };

  useEffect(() => {
    if (!withdrawSendOpen) return;
    refreshProfitPayoutSettings();
  }, [withdrawSendOpen, wallet]);

  const _selectedWithdrawState = () => {
    const symbol = String(coreWithdrawAsset || "").toUpperCase();
    const tokenState = coreVaultOnchain?.tokens?.[symbol] || {};
    const account = tokenState?.account || {};
    const source = String(coreWithdrawSource || "BASE_CAPITAL").toUpperCase();
    const available = source === "BASE_CAPITAL"
      ? Number(account?.freeBase || 0)
      : source === "SECURED_NKR" ? Number(account?.securedNkrProfit || 0)
      : source === "SECURED_TRADER" ? Number(account?.securedTraderProfit || 0)
      : source === "SECURED_GRID" ? Number(account?.securedGridProfit || 0)
      : 0;
    return { symbol, tokenState, account, source, available: Math.max(0, available) };
  };

  const previewCoreVaultWithdraw = async () => {
    try {
      setTxMsg("");
      setCoreWithdrawQuote(null);
      if (!wallet) throw new Error("Wallet not connected.");
      const selected = _selectedWithdrawState();
      const amount = Number(String(coreWithdrawAmount || "").replace(",", "."));
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("Enter a valid withdraw amount.");
      if (!_isAddr(coreWithdrawAddress)) throw new Error("Enter a valid destination address.");
      const chainNativeNow = nativeSymbolForChain(coreVaultOnchain?.chain || balActiveChain || "");
      const isNativeAsset = !!selected.tokenState?.native || (!!chainNativeNow && selected.symbol === chainNativeNow);
      if (!selected.tokenState?.config?.withdrawEnabled && !(isNativeAsset && selected.available > 0)) {
        throw new Error(`${selected.symbol} withdrawals are not enabled in CoreVault.`);
      }
      if (amount > selected.available + 1e-12) throw new Error("Amount exceeds the available balance for the selected asset and source.");
      setCoreWithdrawQuoteBusy(true);
      setCoreWithdrawQuote({
        kind: selected.source === "BASE_CAPITAL" ? "DIRECT_BASE_WITHDRAW" : "DIRECT_PROFIT_WITHDRAW",
        asset: selected.symbol, amount, grossAmount: amount, netAmount: amount,
        source: selected.source, available: selected.available,
        costsIncluded: { gasUsd: 0, swapUsd: 0, slippageUsd: 0, routingBufferUsd: 0 },
        gasPaidSeparately: true, directVaultWithdrawal: true,
      });
    } catch (e) {
      setTxMsg(String(e?.message || e || "Withdraw preview failed"));
    } finally {
      setCoreWithdrawQuoteBusy(false);
    }
  };

  const confirmCoreVaultWithdraw = async () => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      if (!coreWithdrawQuote) throw new Error("Prepare the withdraw preview first.");
      const selected = _selectedWithdrawState();
      if (!_isAddr(coreWithdrawAddress)) throw new Error("Enter a valid destination address.");
      const rawAmount = String(coreWithdrawAmount || "").trim().replace(",", ".");
      if (!/^\d+(\.\d+)?$/.test(rawAmount) || Number(rawAmount) <= 0) throw new Error("Enter a valid withdraw amount.");
      const profitSystem = selected.source === "SECURED_NKR" ? "NKR" : selected.source === "SECURED_TRADER" ? "TRADER" : selected.source === "SECURED_GRID" ? "GRID" : null;
      setCoreWithdrawExecuteBusy(true);
      setTxMsg(`Preparing ${rawAmount} ${selected.symbol} withdrawal…`);
      const prepared = await api(`/api/nexus/core-vault/withdraw/prepare-transaction`, {
        method: "POST", token, wallet,
        body: {
          wallet, chain: balActiveChain || wsChainKey || DEFAULT_CHAIN, asset: selected.symbol, amount: rawAmount,
          recipient: coreWithdrawAddress, source: selected.source === "BASE_CAPITAL" ? "BASE_CAPITAL" : "SECURED_PROFIT",
          system: profitSystem,
        },
      });
      const tx = prepared?.transaction;
      if (!tx?.to || !tx?.data) throw new Error("Backend did not return a V5 withdrawal transaction.");
      const provider = await _getEmbeddedProvider();
      const chainId = Number(prepared?.chainId || 1);
      await _trySwitchChain(provider, chainId);
      const hash = await provider.request({ method: "eth_sendTransaction", params: [{ from: wallet, to: tx.to, value: tx.value || "0x0", data: tx.data }] });
      const transactionType = selected.source === "BASE_CAPITAL" ? (selected.tokenState?.native ? "NATIVE VAULT WITHDRAW" : "ERC20 VAULT WITHDRAW") : `${profitSystem} PROFIT WITHDRAW`;
      const txEntry = {
        type: transactionType,
        chain: prepared?.chain || "ETH",
        asset: selected.symbol,
        amount: rawAmount,
        from: wallet,
        to: String(tx.to || ""),
        recipient: coreWithdrawAddress,
      };
      // Store every directly user-signed Vault withdrawal immediately after a txHash exists.
      // Receipt confirmation continues in the background and must not block the wallet window.
      saveWalletTransaction({ ...txEntry, txHash: hash, status: "SUBMITTED" });
      setTxMsg(`Withdrawal signed and broadcast. Confirmation continues in Transactions. Tx: ${hash}`);
      setCoreWithdrawQuote(null);
      setCoreWithdrawAmount("");
      _trackWalletTxInBackground(provider, hash, txEntry, async () => {
        setTxMsg(`Withdrawal confirmed on-chain. Tx: ${hash}`);
        await Promise.allSettled([
          Promise.resolve().then(() => refreshCoreVaultOnchain?.()),
          Promise.resolve().then(() => refreshCoreVaultAccounting?.()),
          Promise.resolve().then(() => refreshBalances?.()),
        ]);
      });
    } catch (e) {
      setTxMsg(String(e?.message || e || "Withdrawal failed"));
    } finally {
      setCoreWithdrawExecuteBusy(false);
    }
  };

  // ---------
  // Vault on-chain reads (polBalance / inCycle / heldToken / heldTokenBal / operatorEnabled)
  // NOTE: This reads directly from the Vault contract using eth_call (no backend needed).
  // ---------
  const VAULT_SIG = {
    setOperator: "0x558a7297",        // setOperator(address,bool)
    startCycle:  "0x0a20e8c0",        // startCycle(address)
    endCycle:   "0x85588ce8",        // endCycle(address)
    isOperatorFor: "0xd95b6371",      // isOperatorFor(address,address)
    inCycle: "0x7870293e",            // inCycle(address)
    polBalance: "0x7754e652",         // polBalance(address)
    heldToken: "0x90ba1a44",          // heldToken(address)
    heldTokenBal: "0x4ad59fe9",       // heldTokenBal(address)
  };

  const _getVaultAddrForChain = (chainKey) => {
    return (
      (contracts?.chains?.[chainKey]?.vault || "").trim() ||
      (contracts?.chains?.[String(chainKey).toLowerCase()]?.vault || "").trim()
    );
  };

  const _getBotOperatorForChain = (chainKey) => {
    // backend can expose operator/executor address in /api/contracts
    return (
      (contracts?.chains?.[chainKey]?.operator || "").trim() ||
      (contracts?.chains?.[chainKey]?.executor || "").trim() ||
      (contracts?.chains?.[chainKey]?.bot || "").trim() ||
      (contracts?.chains?.[String(chainKey).toLowerCase()]?.operator || "").trim() ||
      (contracts?.chains?.[String(chainKey).toLowerCase()]?.executor || "").trim()
    );
  };

  const vaultStateFetchRef = useRef({ key: "", ts: 0, inflight: false });

  const refreshVaultState = async (preferredChainKey = "", opts = {}) => {
    try {
      if (!wallet) return;
      const force = opts === true || !!opts?.force;
      const forcedChain = String(preferredChainKey || "").toUpperCase().trim();
      const chainKey = (forcedChain || balActiveChain || wsChainKey || DEFAULT_CHAIN);

      // Protect Alchemy/RPC quota:
      // - Grid order execution still polls fast elsewhere (2.5s) and is NOT slowed here.
      // - Vault balance/operator state is expensive and does not need every render.
      // - When open orders run, keep vault state reasonably fresh (12s).
      // - When no open orders run, refresh it only about once per minute.
      const hasActiveGridOrdersForVault = Array.isArray(gridOrders) && gridOrders.some((o) => String(o?.status || "").toUpperCase() === "OPEN");
      const minAgeMs = force ? 0 : (hasActiveGridOrdersForVault ? 12000 : 60000);
      const nowMs = Date.now();
      const cacheKey = `${String(wallet || "").toLowerCase()}|${chainKey}`;
      if (!force && vaultStateFetchRef.current?.key === cacheKey && (nowMs - Number(vaultStateFetchRef.current?.ts || 0)) < minAgeMs) {
        return;
      }
      if (!force && vaultStateFetchRef.current?.inflight && vaultStateFetchRef.current?.key === cacheKey) {
        return;
      }
      vaultStateFetchRef.current = { key: cacheKey, ts: nowMs, inflight: true };

      // Primary path: backend RPC endpoint.
      // This avoids the embedded-wallet/provider race after F5.
      try {
        const qs = new URLSearchParams({ wallet, chain: chainKey, ...(force ? { refresh: "1" } : {}) }).toString();
        const r = await api(`/api/vault/state?${qs}`, { method: "GET", token, wallet });
        if (r && (r.status === "ok" || r.vault_balance !== undefined)) {
          const vaultBal = Number(r?.vault_balance ?? 0) || 0;
          const vaultWei = r?.vault_balance_wei != null ? hexToBigInt(r.vault_balance_wei) : BigInt(Math.round(vaultBal * 1e18));
          const heldTok = String(r?.heldToken || r?.held_token || "") || null;
          const heldBal = Number(r?.heldTokenBal ?? r?.held_token_bal ?? 0) || 0;
          const heldWei = r?.heldTokenBalWei != null ? hexToBigInt(r.heldTokenBalWei) : null;
          const operatorEnabled = !!(r?.operatorEnabled ?? r?.operator_enabled);
          const inCycle = !!(r?.inCycle ?? r?.in_cycle);

          setVaultState({
            polBalanceWei: vaultWei,
            polBalance: vaultBal,
            inCycle,
            heldToken: heldTok,
            heldTokenBalWei: heldWei,
            heldTokenBal: heldBal,
            operatorEnabled,
            balanceSource: r?.vault_balance_source || "",
            contractNativeBalance: Number(r?.vault_contract_native_balance ?? 0) || 0,
            walletAccountingBalance: Number(r?.wallet_accounting_balance ?? 0) || 0,
          });
          vaultStateFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
          return;
        }
      } catch (_) {
        // fallback below
      }

      // Legacy fallback: direct provider eth_call
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr = _getVaultAddrForChain(chainKey);
      if (!_isAddr(vaultAddr)) return;

      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      const botOp = _getBotOperatorForChain(chainKey);
      const hasBotOp = _isAddr(botOp);

      const call = async (data) => {
        return await provider.request({
          method: "eth_call",
          params: [{ to: vaultAddr, data }, "latest"],
        });
      };

      const polHex = await call(VAULT_SIG.polBalance + _encodeAddress(wallet));
      const inCycleHex = await call(VAULT_SIG.inCycle + _encodeAddress(wallet));
      const heldTokHex = await call(VAULT_SIG.heldToken + _encodeAddress(wallet));
      const heldBalHex = await call(VAULT_SIG.heldTokenBal + _encodeAddress(wallet));

      let operatorEnabled = false;
      if (hasBotOp) {
        const opHex = await call(
          VAULT_SIG.isOperatorFor + _encodeAddress(wallet) + _encodeAddress(botOp)
        );
        operatorEnabled = _hexToBool(opHex);
      }

      const polWei = hexToBigInt(polHex);
      const heldWei = hexToBigInt(heldBalHex);

      setVaultState({
        polBalanceWei: polWei,
        polBalance: Number(Utils.formatEther(polWei)),
        inCycle: _hexToBool(inCycleHex),
        heldToken: _hexToAddress(heldTokHex),
        heldTokenBalWei: heldWei,
        heldTokenBal: Number(Utils.formatUnits(heldWei, 18)),
        operatorEnabled,
      });
      vaultStateFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
    } catch (e) {
      // keep UI alive; vault state is best-effort
      try { vaultStateFetchRef.current = { ...(vaultStateFetchRef.current || {}), inflight: false }; } catch (_) {}
    }
  };

  const setVaultOperator = async (allowed) => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr = _getVaultAddrForChain(chainKey);
      if (!_isAddr(vaultAddr)) throw new Error("Vault address not available for this chain.");
      const botOp = _getBotOperatorForChain(chainKey);
      if (!_isAddr(botOp)) throw new Error("Backend bot operator address is missing in /api/contracts.");

      const data = VAULT_SIG.setOperator + _encodeAddress(botOp) + _encodeBool(!!allowed);

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      const currentHex = await provider.request({ method: "eth_chainId" });
      const wantHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
        throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${wantHex}).`);
      }

      const txHash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet, to: vaultAddr, value: "0x0", data }],
      });

      setTxMsg(`${allowed ? "Operator enabled" : "Operator disabled"}. Tx: ${txHash}`);
      setTimeout(() => refreshVaultState("", { force: true }), 1400);
    } catch (e) {
      setTxMsg(String(e?.message || e || "Operator tx failed"));
    } finally {
      setTxBusy(false);
    }
  };

  const startVaultCycle = async () => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr = _getVaultAddrForChain(chainKey);
      if (!_isAddr(vaultAddr)) throw new Error("Vault address not available for this chain.");

      const data = VAULT_SIG.startCycle + _encodeAddress(wallet);

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      const currentHex = await provider.request({ method: "eth_chainId" });
      const wantHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
        throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${wantHex}).`);
      }

      const txHash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet, to: vaultAddr, value: "0x0", data }],
      });

      setTxMsg(`Cycle start submitted. Tx: ${txHash}`);
      setTimeout(() => refreshVaultState("", { force: true }), 1400);
    } catch (e) {
      setTxMsg(String(e?.message || e || "Start cycle failed"));
    } finally {
      setTxBusy(false);
    }
  };

  const endVaultCycle = async () => {
    try {
      setTxMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey] || 137;
      const vaultAddr = _getVaultAddrForChain(chainKey);
      if (!_isAddr(vaultAddr)) throw new Error("Vault address not available for this chain.");

      const data = VAULT_SIG.endCycle + _encodeAddress(wallet);

      setTxBusy(true);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      const currentHex = await provider.request({ method: "eth_chainId" });
      const wantHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
        throw new Error(`Wrong network. Please switch your wallet to ${chainKey} (chainId ${chainId}).`);
      }

      const tx = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from: wallet, to: vaultAddr, data }],
      });

      setTxMsg(`Cycle end submitted. Tx: ${tx}`);
      // refresh vault state (inCycle should turn to NO after confirmation; refresh anyway)
      setTimeout(() => { try { refreshVaultState("", { force: true }); } catch {} }, 1200);
      setTimeout(() => { try { refreshVaultState("", { force: true }); } catch {} }, 4500);
      return tx;
    } catch (e) {
      setTxMsg(String(e?.message || e || "End cycle tx failed"));
      throw e;
    } finally {
      setTxBusy(false);
    }
  };

  // Alchemy balances (native per chain, optionally tokens later)
  const [balLoading, setBalLoading] = useState(false);
  const [balError, setBalError] = useState("");
  const [balByChain, setBalByChain] = useState({}); // { ETH: { native: "0.0" }, ... }

  // Dynamic wallet-chain list used by Wallet details and Withdraw & Send.
  // It is built from the Privy app-wallet balance object, so new EVM networks
  // (BASE, ARB, AVAX, etc.) appear automatically once balances are loaded.
  const walletChainKeys = useMemo(() => {
    const supported = new Set(Object.keys(CHAIN_ID || {}));
    const fromWallet = Object.keys(balByChain || {})
      .map((c) => normalizeWalletChainKey(c))
      .filter((c) => c && supported.has(c));
    const fallback = (Array.isArray(ENABLED_CHAINS) ? ENABLED_CHAINS : [])
      .map((c) => normalizeWalletChainKey(c))
      .filter((c) => c && supported.has(c));
    const seen = new Set();
    const merged = [...PRIMARY_EVM_CHAINS, ...fromWallet, ...fallback].filter((c) => !seen.has(c) && seen.add(c));
    return merged.length ? merged : [DEFAULT_CHAIN];
  }, [balByChain]);

  const walletChainOptions = useMemo(() => {
    return walletChainKeys.map((k) => ({ k, label: CHAIN_LABELS?.[k] || k }));
  }, [walletChainKeys]);

  const [showAllWalletChains, setShowAllWalletChains] = useState(() => {
    try { return localStorage.getItem("nexus_wallet_bal_all") !== "0"; } catch (_) { return true; }
  });

  

useEffect(() => {
    try { localStorage.setItem("nexus_wallet_bal_all", showAllWalletChains ? "1" : "0"); } catch (_) {}
  }, [showAllWalletChains]);
// keep vault state fresh
  useEffect(() => {
    if (!wallet) return;
    const chain = String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
    let forcedChain = chain;
    try {
      const savedCoin = String(
        localStorage.getItem(`${LS_GRID_COIN_PREFIX}:${chain}`) || ""
      ).toUpperCase().trim();
      if (["POL", "BNB", "ETH"].includes(savedCoin)) {
        forcedChain = savedCoin;
      }
    } catch (_) {}
    refreshVaultState(forcedChain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, wsChainKey, balActiveChain, contracts]);

  // Wallet USD valuation (CoinGecko). Includes native + stables + user-added tokens (when priced).
  const [gridBudgets, setGridBudgets] = useState({ totals: { locked_usd: 0, available_usd: 0 }, by_chain: {}, items: [], ts: null });
  const [gridBudgetsErr, setGridBudgetsErr] = useState("");

  const [coreVaultAccounting, setCoreVaultAccounting] = useState(null);
  // Wallet modal state follows the wallet network selector only.
  const [coreVaultOnchain, setCoreVaultOnchain] = useState(null);
  const [coreVaultOnchainLoading, setCoreVaultOnchainLoading] = useState(false);
  // Live Core Vault Capital is independent from the wallet selector. It keeps a
  // simultaneous on-chain snapshot for every supported EVM chain so Grid, NKR
  // and Trader can select/read any vault without changing the wallet panel.
  const LIVE_CORE_VAULT_CHAINS = ["ETH", "BNB", "POL", "BASE", "ARB"];
  const [coreVaultOnchainByChain, setCoreVaultOnchainByChain] = useState({});
  const [coreVaultAllChainsLoading, setCoreVaultAllChainsLoading] = useState(false);
  // Prevent slower/older RPC responses from overwriting a newer all-chain snapshot.
  const coreVaultAllChainsReadSeqRef = useRef(0);
  const coreVaultAllChainsInFlightRef = useRef(false);

  // Deposit selector: show every asset currently detected in the connected
  // Ethereum wallet. CoreVault admission remains strict: only the exact token
  // contract configured with depositEnabled can actually be deposited.
  const coreDepositWalletAssets = useMemo(() => {
    const chainKey = String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
    const nativeSymbol = chainKey === "BNB" ? "BNB" : chainKey === "POL" ? "POL" : "ETH";
    const walletState = balByChain?.[chainKey] || {};
    const vaultTokens = coreVaultOnchain?.tokens || {};
    const configured = Object.entries(vaultTokens).map(([vaultSymbol, state]) => ({
      vaultSymbol: String(vaultSymbol || "").toUpperCase(),
      state: state || {},
      address: String(state?.address || "").toLowerCase(),
    }));

    const matchVaultToken = (address, symbol) => {
      const addr = String(address || "").toLowerCase();
      const sym = String(symbol || "").toUpperCase();
      return configured.find((row) => (addr && row.address === addr) || (!addr && row.vaultSymbol === sym)) || null;
    };

    const rows = [];
    // Backend V5 on-chain state exposes the native asset inside tokens (ETH on Ethereum),
    // not as coreVaultOnchain.native. Resolve it by symbol first and then by address(0)
    // so the same logic also works later for BNB and POL.
    const nativeTokenState =
      vaultTokens?.[nativeSymbol] ||
      Object.values(vaultTokens).find((state) =>
        state?.native === true ||
        String(state?.address || "").toLowerCase() === ZERO_ADDRESS.toLowerCase()
      ) ||
      null;
    const nativeBalance = Number(walletState?.native || 0);
    if (Number.isFinite(nativeBalance) && nativeBalance > 0) {
      rows.push({
        key: `native:${nativeSymbol}`,
        chain: chainKey,
        symbol: nativeSymbol,
        name: chainKey === "BNB" ? "BNB Chain" : chainKey === "POL" ? "Polygon" : "Ethereum",
        address: ZERO_ADDRESS,
        decimals: Number(nativeTokenState?.decimals ?? 18),
        balance: String(walletState.native),
        isNative: true,
        vaultApproved: !!nativeTokenState?.config?.depositEnabled,
        tokenState: nativeTokenState,
        note: nativeTokenState?.config?.depositEnabled ? "V5 Native deposit enabled" : "Native deposit not configured on-chain",
      });
    }

    const stableSpecs = getStableWhitelistForChain(chainKey);
    for (const spec of stableSpecs) {
      const symbol = String(spec?.symbol || "").toUpperCase();
      const balance = Number(walletState?.stables?.[symbol] || 0);
      if (!Number.isFinite(balance) || balance <= 0) continue;
      const matched = matchVaultToken(spec?.address, symbol);
      rows.push({
        key: `erc20:${String(spec?.address || "").toLowerCase()}`,
        chain: chainKey,
        symbol,
        name: String(spec?.name || symbol),
        address: String(spec?.address || ""),
        decimals: Number(spec?.decimals ?? matched?.state?.decimals ?? 18),
        balance: String(walletState?.stables?.[symbol] || "0"),
        isNative: false,
        vaultApproved: !!matched?.state?.config?.depositEnabled,
        tokenState: matched?.state || null,
        note: matched?.state?.config?.depositEnabled ? "Vault approved" : "Owner approval required",
      });
    }

    for (const tokenRow of (walletState?.custom || [])) {
      const balance = Number(tokenRow?.balance || 0);
      if (!Number.isFinite(balance) || balance <= 0) continue;
      const address = String(tokenRow?.address || "");
      const symbol = String(tokenRow?.symbol || "TOKEN").toUpperCase();
      const matched = matchVaultToken(address, symbol);
      rows.push({
        key: `erc20:${address.toLowerCase()}`,
        chain: chainKey,
        symbol,
        name: String(tokenRow?.name || symbol),
        address,
        decimals: Number(tokenRow?.decimals ?? matched?.state?.decimals ?? 18),
        balance: String(tokenRow?.balance || "0"),
        isNative: false,
        vaultApproved: !!matched?.state?.config?.depositEnabled,
        tokenState: matched?.state || null,
        note: matched?.state?.config?.depositEnabled ? "Vault approved" : "Owner approval required",
      });
    }

    const seen = new Set();
    return rows
      .filter((row) => row.key && !seen.has(row.key) && seen.add(row.key))
      .sort((a, b) => Number(b.vaultApproved) - Number(a.vaultApproved) || a.symbol.localeCompare(b.symbol));
  }, [balByChain, coreVaultOnchain, balActiveChain, wsChainKey]);

  const selectedCoreDepositAsset = useMemo(() => {
    const requested = String(coreDepositAsset || "");
    return coreDepositWalletAssets.find((row) => row.key === requested)
      || coreDepositWalletAssets.find((row) => row.symbol === requested.toUpperCase())
      || null;
  }, [coreDepositAsset, coreDepositWalletAssets]);

  useEffect(() => {
    if (!coreDepositWalletAssets.length) return;

    // The old state used a symbol such as "USDC", while the select options use
    // unique keys such as "native:ETH" and "erc20:<address>". A symbol could
    // therefore resolve for display but still leave the controlled <select>
    // without a matching value. Normalize every resolved asset to its exact key
    // so the visible option, helper text and deposit handler always refer to the
    // same asset.
    if (selectedCoreDepositAsset?.key && coreDepositAsset !== selectedCoreDepositAsset.key) {
      setCoreDepositAsset(selectedCoreDepositAsset.key);
      return;
    }

    if (selectedCoreDepositAsset) return;
    const first = coreDepositWalletAssets.find((row) => row.vaultApproved) || coreDepositWalletAssets[0];
    if (first?.key) setCoreDepositAsset(first.key);
  }, [coreDepositAsset, coreDepositWalletAssets, selectedCoreDepositAsset]);

  const [privyAutomationStatus, setPrivyAutomationStatus] = useState("NOT_VERIFIED");
  const [privyAutomationError, setPrivyAutomationError] = useState("");
  const [privyAutomationBusy, setPrivyAutomationBusy] = useState(false);

  // ENGINE-133: one live Core Vault capital selector shared by Grid, NKR and Trading.
  // This is display/selection only. It never mixes Shadow capital with on-chain funds
  // and it does not reserve or move funds by itself.
  const [liveVaultChainByMode, setLiveVaultChainByMode] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nexus_live_vault_chain_by_mode") || "{}");
      return { normal: saved?.normal || DEFAULT_CHAIN || "ETH", rotation: saved?.rotation || DEFAULT_CHAIN || "ETH", trading: saved?.trading || DEFAULT_CHAIN || "ETH" };
    } catch {
      return { normal: DEFAULT_CHAIN || "ETH", rotation: DEFAULT_CHAIN || "ETH", trading: DEFAULT_CHAIN || "ETH" };
    }
  });
  useEffect(() => {
    try { localStorage.setItem("nexus_live_vault_chain_by_mode", JSON.stringify(liveVaultChainByMode || {})); } catch {}
  }, [liveVaultChainByMode]);
  const [liveVaultAssetByMode, setLiveVaultAssetByMode] = useState({ normal: "USDC", rotation: "USDC", trading: "USDC" });
  const refreshCoreVaultOnchain = useCallback(async (preferredChainKey = "") => {
    if (!wallet) { setCoreVaultOnchain(null); return; }
    setCoreVaultOnchainLoading(true);
    try {
      const chainKey = String(preferredChainKey || balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
      const res = await api(`/api/nexus/core-vault/onchain-state?chain=${encodeURIComponent(chainKey)}`, { wallet });
      setCoreVaultOnchain(res?.onchain || res || null);
    } catch (e) {
      setCoreVaultOnchain({ connected: false, status: "error", error: String(e?.message || e || "Core Vault read failed") });
    } finally {
      setCoreVaultOnchainLoading(false);
    }
  }, [wallet, api, balActiveChain, wsChainKey]);
  useEffect(() => { refreshCoreVaultOnchain(); }, [refreshCoreVaultOnchain]);
  useInterval(refreshCoreVaultOnchain, 15000, !!wallet);

  const refreshAllCoreVaultOnchain = useCallback(async ({ force = false } = {}) => {
    if (!wallet) { setCoreVaultOnchainByChain({}); return; }
    // The interval and chain-change effects can fire together. Never run two full
    // five-chain reads concurrently because a slower old response can otherwise
    // replace a newer snapshot and make balances appear to jump/disappear.
    if (coreVaultAllChainsInFlightRef.current && !force) return;
    const readSeq = ++coreVaultAllChainsReadSeqRef.current;
    coreVaultAllChainsInFlightRef.current = true;
    setCoreVaultAllChainsLoading(true);
    try {
      const rows = await Promise.all(LIVE_CORE_VAULT_CHAINS.map(async (chainKey) => {
        try {
          const res = await api(`/api/nexus/core-vault/onchain-state?chain=${encodeURIComponent(chainKey)}${force ? "&refresh=1" : ""}`, { wallet });
          const state = res?.onchain || res || null;
          return [chainKey, state && typeof state === "object" ? state : { connected: false, chain: chainKey, status: "empty" }, true];
        } catch (error) {
          return [chainKey, { connected: false, chain: chainKey, status: "error", error: String(error?.message || error || "Core Vault read failed") }, false];
        }
      }));
      if (readSeq !== coreVaultAllChainsReadSeqRef.current) return;
      setCoreVaultOnchainByChain((previous) => {
        const next = { ...(previous || {}) };
        for (const [chainKey, incoming, readOk] of rows) {
          const prior = previous?.[chainKey];
          // A temporary RPC/network failure must not erase the last confirmed
          // on-chain balance. Keep it visible, mark it stale, and retry next poll.
          if (!readOk && prior?.connected) {
            next[chainKey] = { ...prior, stale: true, staleError: incoming?.error || "Core Vault read failed", staleSince: prior?.staleSince || Date.now() };
          } else {
            next[chainKey] = { ...incoming, chain: String(incoming?.chain || chainKey).toUpperCase(), stale: false, readAt: Date.now() };
          }
        }
        return next;
      });
    } finally {
      if (readSeq === coreVaultAllChainsReadSeqRef.current) setCoreVaultAllChainsLoading(false);
      coreVaultAllChainsInFlightRef.current = false;
    }
  }, [wallet, api]);
  useEffect(() => { refreshAllCoreVaultOnchain(); }, [refreshAllCoreVaultOnchain]);
  useInterval(refreshAllCoreVaultOnchain, 15000, !!wallet);

  const _waitForTxReceipt = async (provider, txHash, timeoutMs = 180000) => {
    const started = Date.now();
    let lastRpcError = null;
    let attempts = 0;
    while (Date.now() - started < timeoutMs) {
      attempts += 1;
      try {
        const receipt = await provider.request({ method: "eth_getTransactionReceipt", params: [txHash] });
        if (receipt) {
          if (String(receipt.status || "").toLowerCase() === "0x0") throw new Error("Transaction failed on-chain.");
          return receipt;
        }
        lastRpcError = null;
      } catch (error) {
        // A temporary wallet/RPC timeout must not convert an already broadcast
        // transaction into a failed transaction. Keep polling while we have a hash.
        lastRpcError = error;
      }
      // Fast immediately after broadcast, then ease off to reduce RPC load.
      const delayMs = attempts <= 8 ? 600 : attempts <= 25 ? 1000 : 1500;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    const pending = new Error("Transaction is still pending on-chain.");
    pending.code = "TX_RECEIPT_PENDING";
    pending.txHash = txHash;
    pending.cause = lastRpcError || null;
    throw pending;
  };

  const _trackWalletTxInBackground = (provider, txHash, baseEntry, onConfirmed) => {
    if (!txHash) return;
    saveWalletTransaction({ ...baseEntry, txHash, status: "PENDING" });
    void (async () => {
      try {
        const receipt = await _waitForTxReceipt(provider, txHash, 15 * 60 * 1000);
        saveWalletTransaction({
          ...baseEntry,
          txHash,
          status: "CONFIRMED",
          blockNumber: receipt?.blockNumber || null,
          gasUsed: receipt?.gasUsed || null,
          note: "",
        });
        // UI refresh errors happen after the receipt is confirmed and must never
        // downgrade a successful on-chain transaction to FAILED.
        if (typeof onConfirmed === "function") {
          try {
            await onConfirmed(receipt);
          } catch (callbackError) {
            console.warn("Post-confirmation refresh failed:", callbackError);
          }
        }
      } catch (error) {
        saveWalletTransaction({
          ...baseEntry,
          txHash,
          status: error?.code === "TX_RECEIPT_PENDING" ? "PENDING" : "FAILED",
          note: error?.code === "TX_RECEIPT_PENDING" ? "Still pending on-chain." : String(error?.message || error || ""),
        });
      }
    })();
  };

  // BUILD277: Reconcile user-signed wallet history against the actual chain receipt.
  // A UI refresh/callback error must never leave a successful on-chain transaction as FAILED.
  useEffect(() => {
    if (!wallet || !walletTransactions.length) return;
    let cancelled = false;
    const candidates = walletTransactions
      .filter((row) => row?.txHash && ["SUBMITTED", "PENDING", "BROADCAST_UNKNOWN", "FAILED"].includes(String(row?.status || "").toUpperCase()))
      .slice(0, 40);
    if (!candidates.length) return;

    void (async () => {
      let provider;
      try { provider = await _getEmbeddedProvider(); } catch (_) { return; }
      for (const row of candidates) {
        if (cancelled) return;
        try {
          const receipt = await provider.request({ method: "eth_getTransactionReceipt", params: [row.txHash] });
          if (!receipt) continue;
          const ok = String(receipt.status || "").toLowerCase() !== "0x0";
          saveWalletTransaction({
            ...row,
            status: ok ? "CONFIRMED" : "FAILED",
            blockNumber: receipt?.blockNumber || row?.blockNumber || null,
            gasUsed: receipt?.gasUsed || row?.gasUsed || null,
            note: ok ? "" : (row?.note || "Transaction failed on-chain."),
          });
        } catch (error) {
          // Keep the current status on temporary RPC errors.
          console.warn("Wallet transaction reconciliation delayed:", error);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [wallet, walletTransactions, saveWalletTransaction]);

  const _trackCoreVaultTxInBackground = (provider, txHash, successLabel) => {
    if (!txHash) return;
    void (async () => {
      try {
        await _waitForTxReceipt(provider, txHash, 15 * 60 * 1000);
        setCoreDepositMsg(`${successLabel} confirmed. Tx: ${txHash}`);
        setCoreDepositAmount("");
      } catch (error) {
        if (error?.code === "TX_RECEIPT_PENDING") {
          setCoreDepositMsg(`${successLabel} was sent and is still pending. Tx: ${txHash}`);
        } else {
          setCoreDepositMsg(`${successLabel} failed on-chain. Tx: ${txHash}`);
        }
      } finally {
        await Promise.allSettled([
          refreshCoreVaultOnchain(),
          refreshCoreVaultAccounting(),
          refreshBalances(),
        ]);
        setCoreDepositBusy(false);
      }
    })();
  };

  // Explicit one-time Privy signer provisioning. This runs only when the user
  // starts a live system. A failed addSigners call is surfaced and live execution
  // remains blocked until Privy confirms that the configured quorum is attached.
  const ensurePrivyAutomationReady = async () => {
    if (privyAutomationStatus === "READY") return true;
    if (_privySignerSetupInFlight.current) {
      throw new Error("Privy automation setup is already running. Complete the open Privy window first.");
    }
    if (!ready || !authenticated) throw new Error("Privy wallet is not authenticated.");

    const embedded = (privyWallets || []).find((w) => {
      const clientType = String(w?.walletClientType || "").toLowerCase();
      const connectorType = String(w?.connectorType || "").toLowerCase();
      return clientType === "privy" || clientType === "embedded" || connectorType === "embedded";
    }) || (privyWallets || [])[0];

    const address = String(embedded?.address || wallet || "").trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(address)) {
      throw new Error("Privy embedded wallet address is invalid.");
    }

    // useWallets() exposes the wallet client used for signing, but depending on
    // the Privy SDK version it may not expose Privy's persistent wallet ID.
    // Resolve that ID from the authenticated user's linked wallet account by
    // matching the exact embedded-wallet address. Never use an unrelated first
    // linked account or an external wallet ID.
    const linkedAccounts = [
      ...(Array.isArray(privyUser?.linkedAccounts) ? privyUser.linkedAccounts : []),
      ...(Array.isArray(privyUser?.linked_accounts) ? privyUser.linked_accounts : []),
    ];
    const linkedWallet = linkedAccounts.find((account) => {
      const accountType = String(account?.type || account?.accountType || account?.account_type || "").toLowerCase();
      const accountAddress = String(account?.address || "").toLowerCase();
      return accountType === "wallet" && accountAddress === address.toLowerCase();
    });

    const walletId = String(
      linkedWallet?.id ||
      linkedWallet?.walletId ||
      linkedWallet?.wallet_id ||
      embedded?.id ||
      embedded?.walletId ||
      embedded?.wallet_id ||
      embedded?.linkedAccount?.id ||
      embedded?.linked_account?.id ||
      ""
    ).trim();

    if (!walletId) {
      throw new Error("Privy embedded wallet ID is not available yet. Reconnect the Privy wallet and try again.");
    }

    _privySignerSetupInFlight.current = true;
    setPrivyAutomationBusy(true);
    setPrivyAutomationError("");
    setPrivyAutomationStatus("CONFIG_LOADING");
    try {
      const backendToken = await ensureBackendAuthToken(false);
      const headers = {
        "Content-Type": "application/json",
        "X-Wallet-Address": address,
        Authorization: `Bearer ${backendToken}`,
      };

      const cfgRes = await fetch(`${API_BASE}/api/nexus/privy-trading/config`, {
        method: "GET", credentials: "include", cache: "no-store", headers,
      });
      const cfg = await cfgRes.json().catch(() => ({}));
      if (!cfgRes.ok) throw new Error(cfg?.error || `Privy config HTTP ${cfgRes.status}`);
      const signerId = String(cfg?.signerId || "").trim();
      const policyId = String(cfg?.policyId || "").trim();
      if (!signerId) throw new Error("Privy trading key quorum is not configured.");
      if (!policyId) throw new Error("Privy trading policy is not configured.");

      setPrivyAutomationStatus("USER_APPROVAL_REQUIRED");
      const policyStorageKey = `nexus_privy_signer_policy_v1:${address.toLowerCase()}:${signerId}`;
      let attachedPolicyId = "";
      try {
        attachedPolicyId = String(window.localStorage.getItem(policyStorageKey) || "").trim();
      } catch {}

      // Do not call Privy addSigners again when this browser already verified
      // the exact signer + policy pair. Privy answers duplicate attachments with
      // HTTP 400, which polluted the console and could interrupt the start flow.
      if (attachedPolicyId === policyId) {
        console.info("Privy trading signer already attached with the current policy; skipping duplicate attach and continuing with verification.");
      } else {
        try {
          await addSigners({
            address,
            signers: [{ signerId, policyIds: [policyId] }],
          });
          try { window.localStorage.setItem(policyStorageKey, policyId); } catch {}
        } catch (addSignerError) {
          const addSignerMessage = String(
            addSignerError?.message ||
            addSignerError?.error?.message ||
            addSignerError ||
            ""
          );
          const duplicateSigner = /duplicate\s+signer|already\s+(?:been\s+)?added|already\s+(?:exists|attached|registered)/i.test(addSignerMessage);
          if (!duplicateSigner) throw addSignerError;

          // The signer exists but the browser has no proof that the active policy
          // matches. Reattach once so the current policy becomes authoritative.
          if (typeof removeSigners !== "function") {
            throw new Error("Privy signer already exists, but this SDK cannot refresh its policy attachment.");
          }
          setPrivyAutomationStatus("POLICY_REATTACH_REQUIRED");
          await removeSigners({ address });
          setPrivyAutomationStatus("USER_APPROVAL_REQUIRED");
          await addSigners({
            address,
            signers: [{ signerId, policyIds: [policyId] }],
          });
          try { window.localStorage.setItem(policyStorageKey, policyId); } catch {}
          console.info("Privy trading signer reattached with the current policy.");
        }
      }

      setPrivyAutomationStatus("VERIFYING");
      const provisionRes = await fetch(`${API_BASE}/api/nexus/privy-trading/provision`, {
        method: "POST", credentials: "include", cache: "no-store", headers,
        body: JSON.stringify({ privyWalletId: walletId, walletAddress: address }),
      });
      const provision = await provisionRes.json().catch(() => ({}));
      if (!provisionRes.ok) {
        const findingText = Array.isArray(provision?.diagnostics?.findings)
          ? ` (${provision.diagnostics.findings.join(", ")})` : "";
        throw new Error(`${provision?.error || `Privy provision HTTP ${provisionRes.status}`}${findingText}`);
      }
      // Privy's authorization_key wallet filter is an ownership filter, not an
      // additional-signer lookup. After addSigners succeeds (or reports the signer
      // already exists), the backend validates wallet, quorum and key membership.
      // The first real Privy wallet RPC is the authoritative execution check.
      if (provision?.provisionAccepted !== true) {
        const findings = Array.isArray(provision?.diagnostics?.findings)
          ? provision.diagnostics.findings.join(", ") : "provision_not_accepted";
        throw new Error(`Privy provisioning was not accepted: ${findings}`);
      }

      setPrivyAutomationStatus("READY");
      return true;
    } catch (error) {
      const message = String(error?.message || error || "Privy automation setup failed");
      setPrivyAutomationStatus("ERROR");
      setPrivyAutomationError(message);
      throw new Error(`Privy automation setup failed: ${message}`);
    } finally {
      _privySignerSetupInFlight.current = false;
      setPrivyAutomationBusy(false);
    }
  };

  // ENGINE-175: create CoreVault sessions through the server-side Privy wallet API.
  // NKR, Trader and Grid must never trigger a browser approval popup.
  const createCoreVaultSystemSession = async ({ system, budgetUsd, durationHours = 24, maxSlippageBps = 100, maxLossBps = 1500, settlementAsset, chain, sessionConfig = null }) => {
    if (!wallet) throw new Error("Wallet not connected.");
    const budget = Number(String(budgetUsd ?? "").replace(",", "."));
    if (!Number.isFinite(budget) || budget <= 0) throw new Error("A positive CoreVault budget is required.");
    const systemU = String(system || "").toUpperCase();
    const modeKey = systemU === "NKR" ? "rotation" : systemU === "TRADER" ? "trading" : "normal";
    const selectedChain = String(chain || liveVaultChainByMode?.[modeKey] || activeGridChainKey || "ETH").toUpperCase();
    // NKR + Trader + Grid always settle in USDC/USDT (token payout removed, BUILD387).
    // Never fall back to native BNB/ETH/POL — that caused 409s and wrong Grid sessions.
    let selectedAsset = normalizeStablePayoutAsset(
      settlementAsset || liveVaultAssetByMode?.[modeKey] || "USDC",
      "USDC"
    );
    if (systemU === "NKR" || systemU === "TRADER" || systemU === "GRID") {
      selectedAsset = normalizeStablePayoutAsset(selectedAsset, "USDC");
    }
    const selectedVaultState = coreVaultOnchainByChain?.[selectedChain] || null;
    if (!selectedVaultState?.connected) {
      throw new Error(`${selectedChain} CoreVault V5 is not connected. Switch Live Core Vault Capital to ${selectedChain} and wait for LIVE CONNECTED.`);
    }

    await ensurePrivyAutomationReady();

    const backendToken = await ensureBackendAuthToken(false);
    const bodyPayload = {
      wallet,
      system: systemU,
      amountUsd: budget,
      budgetAmount: budget,
      durationHours: Math.max(1, Math.round(Number(durationHours) || 24)),
      maxSlippageBps: Math.max(1, Math.round(Number(maxSlippageBps) || 100)),
      maxLossBps: Math.max(1, Math.round(Number(maxLossBps) || 1500)),
      settlementAsset: selectedAsset,
      chain: selectedChain,
      ...(systemU === "NKR" ? {
        nkrCapitalMode: String(sessionConfig?.nkrCapitalMode || nkrCapitalMode || "DYNAMIC").toUpperCase(),
        nkrObservationWindow: String(sessionConfig?.nkrObservationWindow || nkrObservationWindow || "1h"),
        nkrProfitMode: String(sessionConfig?.nkrProfitMode || nkrProfitMode || "REINVEST").toUpperCase(),
        nkrPeriodValue: Math.max(1, Number(sessionConfig?.nkrPeriodValue ?? nkrPeriodDays ?? 1) || 1),
        nkrPeriodUnit: String(sessionConfig?.nkrPeriodUnit || nkrPeriodUnit || "days").toLowerCase(),
        nkrPeriodHours: Math.max(1, Number(sessionConfig?.nkrPeriodHours ?? normalizeNkrPeriodHours(sessionConfig?.nkrPeriodValue ?? nkrPeriodDays, sessionConfig?.nkrPeriodUnit ?? nkrPeriodUnit)) || 1),
        nkrPeriodDays: Math.max(1 / 24, Number(sessionConfig?.nkrPeriodHours ?? normalizeNkrPeriodHours(sessionConfig?.nkrPeriodValue ?? nkrPeriodDays, sessionConfig?.nkrPeriodUnit ?? nkrPeriodUnit)) / 24),
        maxActiveAssets: Math.max(0, Math.floor(Number(sessionConfig?.maxActiveAssets ?? rotationMaxActiveSessions ?? 0) || 0)),
        maxCapitalPerAssetPct: Math.max(0, Number(sessionConfig?.maxCapitalPerAssetPct ?? 80) || 80),
        allowedAssets: Array.isArray(sessionConfig?.allowedAssets) ? sessionConfig.allowedAssets : (Array.isArray(sessionConfig?.nkrAllowedAssets) ? sessionConfig.nkrAllowedAssets : selectedNkrAssets),
        nkrAllowedAssets: Array.isArray(sessionConfig?.nkrAllowedAssets) ? sessionConfig.nkrAllowedAssets : (Array.isArray(sessionConfig?.allowedAssets) ? sessionConfig.allowedAssets : selectedNkrAssets),
        allowed_assets: Array.isArray(sessionConfig?.allowed_assets) ? sessionConfig.allowed_assets : (Array.isArray(sessionConfig?.allowedAssets) ? sessionConfig.allowedAssets : selectedNkrAssets),
      } : {}),
    };
    const enrichCreateError = (error) => {
      const data = error?.data || {};
      const code = String(data?.error || error?.message || "session_create_failed");
      const avail = data?.availableAmount ?? data?.available_amount;
      const req = data?.requestedAmount ?? data?.requested_amount;
      if (/insufficient_free_vault_budget/i.test(code)) {
        return new Error(
          `Not enough free ${selectedAsset} on ${selectedChain}` +
          (avail != null ? ` (free ~${avail}, need ~${req ?? budget})` : "") +
          `. Check Live Core Vault Capital → ${selectedChain} → Free. If Free is 0 while Balance looks funded, the USDC contract may differ (native vs bridged) — refresh vault status.`
        );
      }
      if (/privy_live_execution_not_ready|privy_wallet_mapping/i.test(code)) {
        const blockers = Array.isArray(data?.blockers) ? data.blockers.join(", ") : "";
        return new Error(`Privy not ready for ${selectedChain}${blockers ? `: ${blockers}` : ""}.`);
      }
      if (/session_budget_limit_exceeded/i.test(code)) {
        const maxA = data?.maxAmount ?? data?.max_amount;
        const reqA = data?.requestedAmount ?? data?.requested_amount ?? budget;
        return new Error(
          `Vault max session budget on ${selectedChain} is ${maxA != null ? maxA : "?"} ${selectedAsset}` +
          ` (you requested ${reqA}). Lower "New session budget" and try again.`
        );
      }
      if (/settlement_asset_not_execution_ready/i.test(code)) {
        return new Error(`${selectedAsset} is not execution-ready on ${selectedChain} vault. Check token config / Owner Admin.`);
      }
      if (/core_vault_chain_not_supported/i.test(code)) {
        return new Error(`Chain ${selectedChain} is not supported for CoreVault sessions yet.`);
      }
      const details = Array.isArray(data?.blockers) && data.blockers.length ? `: ${data.blockers.join(", ")}` : "";
      const err = new Error(`${code}${details}`);
      err.status = error?.status;
      err.data = data;
      return err;
    };
    let result;
    try {
      result = await api("/api/nexus/core-vault/session/create-auto", {
        method: "POST",
        token: backendToken,
        wallet,
        body: bodyPayload,
      });
    } catch (error) {
      if (Number(error?.status || 0) === 401) {
        const refreshedToken = await ensureBackendAuthToken(true);
        try {
          result = await api("/api/nexus/core-vault/session/create-auto", {
            method: "POST",
            token: refreshedToken,
            wallet,
            body: bodyPayload,
          });
        } catch (retryErr) {
          throw enrichCreateError(retryErr);
        }
      } else {
        throw enrichCreateError(error);
      }
    }
    if (result?.status === "starting" && result?.jobId && selectedChain === "ETH") {
      const jobId = String(result.jobId);
      const tempId = `NKR-STARTING-ETH-${jobId}`;
      if (systemU === "NKR") {
        const now = Date.now();
        setRotationSessions((prev) => canonicalizeNkrSessions([
          ...(Array.isArray(prev) ? prev : []),
          {
            id: tempId, sessionId: tempId, chain: "ETH", chainId: 1,
            status: "STARTING", statusLabel: "STARTING", lifecycleState: "STARTING",
            active: true, visibleInActiveSessions: true, createdAt: now, updatedAt: now,
            budgetUsd: budget, settlementAsset: selectedAsset,
            nkrCapitalMode: bodyPayload.nkrCapitalMode, nkrObservationWindow: bodyPayload.nkrObservationWindow,
            nkrProfitMode: bodyPayload.nkrProfitMode, nkrPeriodDays: bodyPayload.nkrPeriodDays,
            nkrPeriodHours: bodyPayload.nkrPeriodHours, nkrPeriodUnit: bodyPayload.nkrPeriodUnit, nkrPeriodValue: bodyPayload.nkrPeriodValue,
            meta: { async_job_id: jobId, chain: "ETH", chain_id: 1, lifecycle_state: "STARTING", nkr_session: true },
          },
        ]));
        setNkrControlState("STARTING");
      }
      const pollEthJob = async () => {
        try {
          const job = await api(`/api/nexus/core-vault/session/create-status/${encodeURIComponent(jobId)}`, { method: "GET", token: backendToken, wallet });
          const js = String(job?.status || "").toLowerCase();
          const step = String(job?.step || "").toUpperCase();
          setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).map((s) => String(s?.id || s?.sessionId || "") === tempId
            ? { ...s, status: step === "CONFIRMATION_PENDING" ? "CONFIRMATION PENDING" : (js === "error" ? "ERROR" : "STARTING"), statusLabel: step, updatedAt: Date.now(), txHash: job?.txHash || s?.txHash || "", meta: { ...(s?.meta || {}), async_step: step, last_error: job?.error || "" } }
            : s));
          if (js === "success") {
            const live = await api(`/api/rotation-sessions?wallet=${encodeURIComponent(String(wallet || ""))}&wallet_address=${encodeURIComponent(String(wallet || ""))}`, { method: "GET", token: backendToken, wallet });
            setRotationSessions(canonicalizeNkrSessions(Array.isArray(live?.sessions) ? live.sessions : []));
            setActiveRotationSessionId(String(live?.activeRotationSessionId || ""));
            setNkrControlState(String(live?.controlState || "RUNNING").toUpperCase());
            setRotationBackendMsg(`ETH NKR session #${job?.sessionId || ""} confirmed and active.`);
            await Promise.allSettled([refreshCoreVaultOnchain(), refreshCoreVaultAccounting()]);
            return;
          }
          if (js === "error") {
            setRotationBackendMsg(`ETH session start failed: ${job?.error || "unknown error"}`);
            setNkrControlState("WAITING");
            return;
          }
          setTimeout(pollEthJob, 2500);
        } catch (_) {
          setTimeout(pollEthJob, 4000);
        }
      };
      setTimeout(pollEthJob, 800);
      return { pending: true, jobId, result, chain: selectedChain, settlementAsset: selectedAsset };
    }
    if (result?.status !== "ok" || !result?.txHash) {
      const details = Array.isArray(result?.blockers) && result.blockers.length ? `: ${result.blockers.join(", ")}` : "";
      throw new Error(`${result?.message || result?.error || "Automatic CoreVault session creation failed"}${details}`);
    }
    await Promise.allSettled([refreshCoreVaultOnchain(), refreshCoreVaultAccounting()]);
    return { hash: result.txHash, sessionId: result.sessionId, result, chain: selectedChain, settlementAsset: selectedAsset };
  };

  const depositToCoreVault = async () => {
    let keepBusyForPending = false;
    try {
      setCoreDepositMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const vault = String(coreVaultOnchain?.contractAddress || "").trim();
      if (!_isAddr(vault) || !coreVaultOnchain?.connected) throw new Error(`${String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase()} Core Vault is not connected.`);
      if (coreVaultOnchain?.paused) throw new Error("Core Vault is currently paused.");

      const selectedAsset = selectedCoreDepositAsset;
      if (!selectedAsset) throw new Error("Select an asset that is present in the connected wallet.");
      const rawAmount = String(coreDepositAmount || "").trim();
      if (!/^\d+(\.\d+)?$/.test(rawAmount) || Number(rawAmount) <= 0) throw new Error("Enter a valid deposit amount.");
      const walletBalance = Number(selectedAsset.balance || 0);
      if (Number.isFinite(walletBalance) && Number(rawAmount) > walletBalance + 1e-12) {
        throw new Error(`Amount exceeds the wallet balance of ${selectedAsset.balance} ${selectedAsset.symbol}.`);
      }

      if (selectedAsset.isNative) {
        if (!selectedAsset.vaultApproved || !selectedAsset?.tokenState?.config?.depositEnabled) {
          throw new Error(`${selectedAsset.symbol} Native Deposit is not enabled in the V5 Vault configuration.`);
        }
        setCoreDepositBusy(true);
        setCoreDepositMsg(`Preparing V5 Native ${selectedAsset.symbol} deposit…`);
        const backendToken = await ensureBackendAuthToken(false);
        const prepared = await api("/api/nexus/core-vault/native/prepare-transaction", {
          method: "POST",
          token: backendToken,
          wallet,
          body: { wallet, chain: String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase(), action: "deposit", amount: rawAmount },
        });
        if (prepared?.status !== "ok" || !prepared?.transaction) {
          throw new Error(prepared?.error || prepared?.message || "Native V5 deposit preparation failed.");
        }
        const provider = await _getEmbeddedProvider();
        await _trySwitchChain(provider, Number(prepared.chainId || 1));
        const currentHex = await provider.request({ method: "eth_chainId" });
        const expectedHex = "0x" + Number(prepared.chainId || 1).toString(16);
        if (String(currentHex).toLowerCase() !== expectedHex.toLowerCase()) {
          throw new Error(`Please switch your Privy wallet to ${String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase()}.`);
        }
        const tx = { ...prepared.transaction, from: wallet };
        setCoreDepositMsg(`Confirm ${rawAmount} ${selectedAsset.symbol} Native Deposit in Privy…`);
        const hash = await provider.request({ method: "eth_sendTransaction", params: [tx] });
        const txEntry = {
          type: "NATIVE VAULT DEPOSIT",
          chain: String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase(),
          asset: selectedAsset.symbol,
          amount: rawAmount,
          from: wallet,
          to: String(tx.to || vault),
        };
        saveWalletTransaction({ ...txEntry, txHash: hash, status: "SUBMITTED" });
        setCoreDepositMsg(`Native deposit signed and broadcast. Confirmation continues in Transactions. Tx: ${hash}`);
        setCoreDepositAmount("");
        _trackWalletTxInBackground(provider, hash, txEntry, () => {
          setCoreDepositMsg(`Native deposit confirmed on-chain. Tx: ${hash}`);
          refreshCoreVaultOnchain();
          refreshCoreVaultAccounting();
          refreshBalances();
        });
        return;
      }

      const symbol = String(selectedAsset.symbol || "TOKEN").toUpperCase();
      const tokenState = selectedAsset.tokenState || {};
      const tokenAddress = String(selectedAsset.address || tokenState?.address || "").trim();
      const decimals = Number(selectedAsset.decimals ?? tokenState?.decimals ?? 18);
      if (!_isAddr(tokenAddress)) throw new Error(`${symbol} contract is not available.`);
      if (!selectedAsset.vaultApproved || !tokenState?.config?.depositEnabled) {
        throw new Error(`${symbol} is visible in your wallet but is not yet approved for Core Vault deposits. The exact contract must pass GoPlus and be enabled by the Vault owner first.`);
      }

      const amountUnits = decimalStringToUnits(rawAmount, decimals);
      if (amountUnits <= 0n) throw new Error("Deposit amount is too small.");

      setCoreDepositBusy(true);
      setCoreDepositMsg(`Running GoPlus security check for ${symbol}…`);
      const security = await securityPrecheckForDeposit({
        chainKey: String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase(),
        tokenAddress,
        symbol,
        isNative: false,
        token: privyJwt || token,
      });
      if (!security || security.status === "error" || security.allowed !== true) {
        const why = security?.reason || security?.error || security?.blocked_by || "Token security check failed";
        throw new Error(`Deposit blocked before wallet signature: ${why}`);
      }
      setCoreDepositMsg(`GoPlus approved ${symbol}. Preparing wallet…`);
      const provider = await _getEmbeddedProvider();
      const activeChainKey = String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
      const activeChainId = Number(CHAIN_ID?.[activeChainKey] || 1);
      await _trySwitchChain(provider, activeChainId);
      const currentHex = await provider.request({ method: "eth_chainId" });
      const expectedHex = "0x" + activeChainId.toString(16);
      if (String(currentHex).toLowerCase() !== expectedHex.toLowerCase()) throw new Error(`Please switch your wallet to ${activeChainKey}.`);

      // Verify the exact configured Vault token again immediately before signing.
      // Symbols are display-only; contract identity is authoritative.
      const configuredAddress = String(tokenState?.address || "").toLowerCase();
      if (!configuredAddress || configuredAddress !== tokenAddress.toLowerCase() || !tokenState?.config?.depositEnabled) {
        throw new Error("Token contract does not match an enabled Core Vault deposit configuration.");
      }

      const amountWord = _encodeUint256(amountUnits);
      const allowanceData = "0xdd62ed3e" + _encodeAddress(wallet) + _encodeAddress(vault);
      const allowanceHex = await provider.request({ method: "eth_call", params: [{ to: tokenAddress, data: allowanceData }, "latest"] });
      let allowance = 0n;
      try { allowance = BigInt(allowanceHex || "0x0"); } catch { allowance = 0n; }

      const _extractTxHashFromError = (error) => {
        const text = String(error?.message || error?.details || error || "");
        const match = text.match(/0x[a-fA-F0-9]{64}/);
        return match ? match[0] : "";
      };

      const sendAndWait = async (to, data, label, transactionEntry) => {
        setCoreDepositMsg(label);
        let hash = "";
        try {
          hash = await provider.request({
            method: "eth_sendTransaction",
            params: [{ from: wallet, to, value: "0x0", data }],
          });
        } catch (error) {
          // Privy/Viem can time out after creating a transaction hash. Preserve that
          // user-signed action in Transactions instead of losing it completely.
          hash = _extractTxHashFromError(error);
          if (!hash) throw error;
          const timeoutEntry = {
            ...(transactionEntry || {}),
            from: wallet,
            to,
            txHash: hash,
            status: "BROADCAST_UNKNOWN",
            note: "Privy timed out before broadcast confirmation. Nexus is checking the network in the background; do not submit again until the status is resolved.",
          };
          saveWalletTransaction(timeoutEntry);
          keepBusyForPending = true;
          _trackWalletTxInBackground(provider, hash, timeoutEntry, () => {
            refreshCoreVaultOnchain();
            refreshCoreVaultAccounting();
            refreshBalances();
          });
          setCoreDepositMsg(`${label.replace(/…$/, "")} signed. Broadcast confirmation is being checked in Transactions. Tx: ${hash}`);
          return { hash, confirmed: false, pending: true, broadcastUnknown: true };
        }

        const baseEntry = {
          ...(transactionEntry || {}),
          from: wallet,
          to,
        };
        saveWalletTransaction({ ...baseEntry, txHash: hash, status: "SUBMITTED" });
        _trackWalletTxInBackground(provider, hash, baseEntry, () => {
          refreshCoreVaultOnchain();
          refreshCoreVaultAccounting();
          refreshBalances();
        });

        setCoreDepositMsg(`${label.replace(/…$/, "")} signed and broadcast. Confirmation continues in Transactions. Tx: ${hash}`);
        try {
          await _waitForTxReceipt(provider, hash, 90000);
          return { hash, confirmed: true };
        } catch (error) {
          if (error?.code !== "TX_RECEIPT_PENDING") throw error;
          keepBusyForPending = true;
          return { hash, confirmed: false, pending: true };
        }
      };

      if (allowance < amountUnits) {
        // USDT can require allowance reset to zero before increasing it.
        if (allowance > 0n) {
          const resetResult = await sendAndWait(
            tokenAddress,
            "0x095ea7b3" + _encodeAddress(vault) + _encodeUint256(0n),
            `Resetting ${symbol} approval…`,
            { type: "ERC20 VAULT APPROVAL RESET", chain: activeChainKey, asset: symbol, amount: "0", spender: vault }
          );
          if (!resetResult.confirmed) {
            setCoreDepositMsg(`Approval reset was sent and is still pending. Tx: ${resetResult.hash}`);
            return;
          }
        }
        // One reusable approval avoids a new approval signature on every later deposit.
        // The cap is 10,000 stablecoins (or the current amount when larger), not unlimited.
        const reusableCap = 10000n * (10n ** BigInt(decimals));
        const approvalUnits = amountUnits > reusableCap ? amountUnits : reusableCap;
        const approvalResult = await sendAndWait(
          tokenAddress,
          "0x095ea7b3" + _encodeAddress(vault) + _encodeUint256(approvalUnits),
          `One-time ${symbol} Vault approval…`,
          { type: "ERC20 VAULT APPROVAL", chain: activeChainKey, asset: symbol, amount: String(Number(approvalUnits) / (10 ** decimals)), spender: vault }
        );
        if (!approvalResult.confirmed) {
          setCoreDepositMsg(`Vault approval was sent and is still pending. Tx: ${approvalResult.hash}`);
          return;
        }
      }

      const depositResult = await sendAndWait(
        vault,
        "0x47e7ef24" + _encodeAddress(tokenAddress) + amountWord,
        `Depositing ${rawAmount} ${symbol} into Core Vault…`,
        { type: "ERC20 VAULT DEPOSIT", chain: activeChainKey, asset: symbol, amount: rawAmount, tokenAddress }
      );

      if (depositResult.confirmed) {
        setCoreDepositMsg(`Deposit confirmed. Tx: ${depositResult.hash}`);
        setCoreDepositAmount("");
      } else {
        setCoreDepositMsg(`Deposit was sent and is being confirmed in the background. Do not submit it again. Tx: ${depositResult.hash}`);
      }
      // Refresh immediately and again shortly afterwards. The on-chain balance is
      // authoritative even when the Privy dialog reports a local timeout.
      refreshCoreVaultOnchain();
      refreshCoreVaultAccounting();
      setTimeout(() => { refreshBalances(); refreshCoreVaultOnchain(); refreshCoreVaultAccounting(); }, 250);
      setTimeout(() => { refreshBalances(); refreshCoreVaultOnchain(); refreshCoreVaultAccounting(); }, 5000);
    } catch (e) {
      const msg = String(e?.message || e || "Deposit failed");
      const low = msg.toLowerCase();
      if (e?.code === 4001 || low.includes("rejected") || low.includes("denied") || low.includes("cancel")) {
        setCoreDepositMsg("Deposit cancelled by user.");
      } else {
        setCoreDepositMsg(msg);
      }
    } finally {
      if (!keepBusyForPending) setCoreDepositBusy(false);
    }
  };

  const refreshCoreVaultAccounting = useCallback(async () => {
    if (!wallet) { setCoreVaultAccounting(null); return; }
    try {
      const res = await api(`/api/nexus/core-vault/accounting`, { wallet });
      setCoreVaultAccounting(res?.accounting || res || null);
    } catch (_) { setCoreVaultAccounting(null); }
  }, [wallet, api]);
  useEffect(() => { refreshCoreVaultAccounting(); }, [refreshCoreVaultAccounting]);
  useEffect(() => {
    if (!withdrawSendOpen || !wallet) return;
    refreshCoreVaultOnchain();
    refreshCoreVaultAccounting();
  }, [withdrawSendOpen, wallet, refreshCoreVaultOnchain, refreshCoreVaultAccounting]);
  // Keep the Vault ledger synchronized with every Grid/NKR/Trader close, loss,
  // allocation change or reserve change. The shared backend endpoint is cheap
  // and wallet-bound; overlapping requests are prevented by useInterval.
  useInterval(refreshCoreVaultAccounting, 5000, !!wallet);

  const coreWithdrawAssetRows = useMemo(() => {
    // BUILD400: native withdraw for every EVM chain (live + future).
    // Prefer backend `row.native` / onchain.nativeSymbol; fall back to NATIVE_SYMBOL_BY_CHAIN.
    const tokens = coreVaultOnchain?.tokens || {};
    const chainKey = String(coreVaultOnchain?.chain || coreVaultOnchain?.chainKey || balActiveChain || "").toUpperCase();
    const chainNative = String(
      coreVaultOnchain?.nativeSymbol
      || coreVaultOnchain?.native
      || nativeSymbolForChain(chainKey)
      || ""
    ).toUpperCase();
    const rows = Object.values(tokens).filter((row) => {
      if (!row) return false;
      const sym = String(row.symbol || "").toUpperCase();
      if (!sym) return false;
      const configured = !!row?.config?.configured;
      const withdrawOk = !!row?.config?.withdrawEnabled;
      const free = Number(row?.account?.freeBase || 0);
      const isNative = !!row.native || (!!chainNative && sym === chainNative);
      if (isNative) {
        if (chainNative && sym !== chainNative) return false;
        return configured || withdrawOk || free > 0;
      }
      return configured && withdrawOk;
    }).map((row) => {
      const sym = String(row.symbol || "").toUpperCase();
      const isNative = !!row.native || (!!chainNative && sym === chainNative);
      return {
        symbol: sym,
        native: isNative,
        decimals: Number(row.decimals ?? (isNative ? 18 : 6)),
        account: row.account || {},
        withdrawEnabled: !!row?.config?.withdrawEnabled,
        configured: !!row?.config?.configured,
      };
    });
    if (chainNative && !rows.some((r) => r.symbol === chainNative)) {
      const tok = tokens[chainNative] || {};
      rows.unshift({
        symbol: chainNative,
        native: true,
        decimals: Number(tok?.decimals ?? 18),
        account: tok?.account || {},
        withdrawEnabled: !!tok?.config?.withdrawEnabled,
        configured: !!tok?.config?.configured,
      });
    }
    return rows;
  }, [coreVaultOnchain, balActiveChain]);

  useEffect(() => {
    if (!coreWithdrawAssetRows.length) return;
    if (!coreWithdrawAssetRows.some((row) => row.symbol === String(coreWithdrawAsset || "").toUpperCase())) {
      setCoreWithdrawAsset(coreWithdrawAssetRows[0].symbol);
      setCoreWithdrawQuote(null);
    }
  }, [coreWithdrawAssetRows, coreWithdrawAsset]);

  const coreWithdrawAvailable = useMemo(() => {
    const row = coreWithdrawAssetRows.find((item) => item.symbol === String(coreWithdrawAsset || "").toUpperCase());
    const a = row?.account || {};
    if (coreWithdrawSource === "BASE_CAPITAL") return Math.max(0, Number(a.freeBase || 0));
    if (coreWithdrawSource === "SECURED_NKR") return Math.max(0, Number(a.securedNkrProfit || 0));
    if (coreWithdrawSource === "SECURED_TRADER") return Math.max(0, Number(a.securedTraderProfit || 0));
    if (coreWithdrawSource === "SECURED_GRID") return Math.max(0, Number(a.securedGridProfit || 0));
    return 0;
  }, [coreWithdrawAssetRows, coreWithdrawAsset, coreWithdrawSource]);

  const coreVaultOverview = useMemo(() => {
    const a = coreVaultAccounting || {};

    // The verified contract is the source of truth for real user funds.
    // The backend accounting endpoint can briefly remain in SHADOW mode after a
    // deposit, which previously made Withdraw show $0 even though the contract
    // already held the user's USDC/USDT. Aggregate both enabled stablecoins here
    // and use the on-chain values whenever the Core Vault is connected.
    const onchainAccounts = ["USDC", "USDT"].map((symbol) => coreVaultOnchain?.tokens?.[symbol]?.account || {});
    const onchainBaseUsd = onchainAccounts.reduce((sum, account) => sum + Math.max(0, Number(account?.baseCapital || 0)), 0);
    const onchainSecuredUsd = onchainAccounts.reduce((sum, account) => sum + Math.max(0, Number(account?.totalSecuredProfit || 0)), 0);
    const onchainAllocatedUsd = onchainAccounts.reduce((sum, account) => sum + Math.max(0, Number(account?.totalAllocated || 0)), 0);
    const onchainStableUsd = onchainBaseUsd + onchainSecuredUsd + onchainAllocatedUsd;
    const hasLiveOnchainState = !!coreVaultOnchain?.connected;

    const stableBalanceUsd = hasLiveOnchainState
      ? onchainStableUsd
      : Math.max(0, Number(a.stableBalanceUsd || 0));
    const securedProfitUsd = hasLiveOnchainState
      ? onchainSecuredUsd
      : Math.max(0, Number(a.securedProfitUsd || 0));
    const protectedBaseUsd = hasLiveOnchainState
      ? onchainBaseUsd
      : Math.max(0, Number(a.baseCapitalUsd || 0));
    const allocatedUsd = hasLiveOnchainState
      ? onchainAllocatedUsd
      : Math.max(0, Number(a.allocatedUsd || 0));
    const reserveUsd = Math.max(0, Number(a.reserveUsd || 0));
    const profitBreakdown = {
      nkrUsd: Math.max(0, Number(a?.profitBreakdown?.nkrUsd || 0)),
      traderUsd: Math.max(0, Number(a?.profitBreakdown?.traderUsd || 0)),
      gridUsd: Math.max(0, Number(a?.profitBreakdown?.gridUsd || 0)),
      totalUsd: securedProfitUsd,
    };
    const realizedNetBreakdown = {
      nkrUsd: Number(a?.realizedNetBreakdown?.nkrUsd || 0),
      traderUsd: Number(a?.realizedNetBreakdown?.traderUsd || 0),
      gridUsd: Number(a?.realizedNetBreakdown?.gridUsd || 0),
      totalUsd: Number(a?.realizedNetBreakdown?.totalUsd || 0),
    };
    const allocationBreakdown = {
      nkrUsd: Math.max(0, Number(a?.allocationBreakdown?.nkrUsd || 0)),
      traderUsd: Math.max(0, Number(a?.allocationBreakdown?.traderUsd || 0)),
      gridUsd: Math.max(0, Number(a?.allocationBreakdown?.gridUsd || 0)),
      totalUsd: allocatedUsd,
    };
    const isShadow = !hasLiveOnchainState && String(a.mode || "SHADOW").toUpperCase() !== "LIVE";
    const availableBySource = isShadow ? { SECURED_PROFIT_ONLY: 0, BASE_CAPITAL: 0, ALL_STABLE: 0 } : {
      SECURED_PROFIT_ONLY: securedProfitUsd,
      BASE_CAPITAL: protectedBaseUsd,
      ALL_STABLE: stableBalanceUsd,
    };
    return {
      stableBalanceUsd, protectedBaseUsd, securedProfitUsd, allocatedUsd, reserveUsd,
      profitBreakdown, realizedNetBreakdown, allocationBreakdown,
      availableForWithdrawUsd: Math.max(0, Number(availableBySource[coreWithdrawSource] || 0)),
      mode: isShadow ? "SHADOW" : "LIVE",
      isShadow,
      note: hasLiveOnchainState ? `Live balances read directly from the verified ${String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase()} Core Vault.` : String(a.note || ""),
    };
  }, [coreVaultAccounting, coreVaultOnchain, coreWithdrawSource]);

  const [walletUsd, setWalletUsd] = useState({ total: null, byChain: {}, unpriced: 0, ts: null });
  const [walletPx, setWalletPx] = useState({ native: {}, tokenByChain: {}, ts: null });
  const [walletPanelTab, setWalletPanelTab] = useState("WALLET");
  const [walletAssetSecurityByKey, setWalletAssetSecurityByKey] = useState({});
  const walletAssetRows = useMemo(() => {
    const rows = [];
    for (const chain of walletChainKeys || []) {
      const data = balByChain?.[chain] || {};
      const nativeAmount = Number(data?.native);
      if (Number.isFinite(nativeAmount) && nativeAmount > 0) {
        const px = Number(walletPx?.native?.[chain]);
        rows.push({
          key: `${chain}:native`, chain, symbol: chain, name: walletChainDisplayName(chain),
          amount: nativeAmount, usd: Number.isFinite(px) ? nativeAmount * px : null,
          address: "native", kind: "native", decimals: 18, vaultStatus: "VERIFIED", vaultLabel: "Verified for Vault",
        });
      }
      for (const [symbol, rawAmount] of Object.entries(data?.stables || {})) {
        const amount = Number(rawAmount);
        if (!Number.isFinite(amount) || amount <= 0) continue;
        const spec = getStableWhitelistForChain(chain).find((x) => String(x?.symbol || "").toUpperCase() === String(symbol).toUpperCase());
        rows.push({
          key: `${chain}:${String(spec?.address || symbol).toLowerCase()}`, chain,
          symbol: String(symbol).toUpperCase(), name: "Approved stablecoin", amount, usd: amount,
          address: String(spec?.address || ""), kind: "stable", decimals: Number(spec?.decimals ?? 6), vaultStatus: "VERIFIED", vaultLabel: "Verified for Vault",
        });
      }
      for (const tokenRow of data?.custom || []) {
        const amount = Number(tokenRow?.balance);
        if (!Number.isFinite(amount) || amount <= 0) continue;
        const address = String(tokenRow?.address || "").toLowerCase();
        const _wnRow = wnativeSpecForChain(chain);
        const isWnative = _wnRow && String(_wnRow.address || "").toLowerCase() === address;
        let px = Number(walletPx?.tokenByChain?.[chain]?.[address]);
        // Wrapped native tracks native gas token price when no separate token price.
        if (!Number.isFinite(px) && isWnative) {
          px = Number(walletPx?.native?.[chain]);
        }
        const security = walletAssetSecurityByKey?.[`${chain}:${address}`];
        let vaultStatus = "PENDING";
        let vaultLabel = "Security check pending";
        if (security?.loading) { vaultStatus = "PENDING"; vaultLabel = "Security check pending"; }
        else if (security?.allowed === true) { vaultStatus = "VERIFIED"; vaultLabel = "Verified for Vault"; }
        else if (security?.checked) {
          vaultStatus = security?.blocked ? "BLOCKED" : "WALLET_ONLY";
          vaultLabel = security?.blocked ? "Blocked from Vault" : "Wallet only";
        }
        rows.push({
          key: `${chain}:${address}`, chain, symbol: String(tokenRow?.symbol || "TOKEN").toUpperCase(),
          name: isWnative ? String(_wnRow?.name || "Wrapped native") : String(tokenRow?.name || "ERC-20 token"), amount,
          usd: Number.isFinite(px) ? amount * px : null, address, kind: "custom",
          decimals: Number(tokenRow?.decimals ?? 18), vaultStatus, vaultLabel, security,
        });
      }
    }
    return rows.sort((a, b) => (Number(b.usd || 0) - Number(a.usd || 0)) || a.symbol.localeCompare(b.symbol));
  }, [walletChainKeys, balByChain, walletPx, walletAssetSecurityByKey]);

  useEffect(() => {
    if (!walletAssetRows.length) {
      if (walletSendAssetKey) setWalletSendAssetKey("");
      return;
    }
    if (!walletAssetRows.some((row) => row.key === walletSendAssetKey)) {
      setWalletSendAssetKey(walletAssetRows[0].key);
    }
    if (walletSwapFromKey && !walletAssetRows.some((row) => row.key === walletSwapFromKey)) {
      setWalletSwapFromKey(walletAssetRows[0]?.key || "");
    }
  }, [walletAssetRows, walletSendAssetKey, walletSwapFromKey]);


  const runPrivyWalletSwap = async () => {
    try {
      setWalletSwapMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      const fromRow = walletAssetRows.find((item) => item.key === walletSwapFromKey);
      if (!fromRow) throw new Error("Select an asset to swap from.");
      const rawAmount = String(walletSwapAmount || "").trim();
      if (!/^\d+(\.\d+)?$/.test(rawAmount) || Number(rawAmount) <= 0) throw new Error("Enter a valid amount.");
      if (Number(rawAmount) > Number(fromRow.amount || 0)) throw new Error("Amount exceeds wallet balance.");
      const toSym = String(walletSwapToSym || "USDC").toUpperCase();
      if (!["USDC", "USDT"].includes(toSym)) throw new Error("Swap destination must be USDC or USDT.");
      const chainKey = normalizeWalletChainKey(fromRow.chain || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey];
      if (!chainId) throw new Error(`Unsupported network: ${chainKey}`);

      setWalletSwapBusy(true);
      setWalletSwapMsg(`Preparing ${fromRow.symbol} → ${toSym} on ${walletChainDisplayName(chainKey)}…`);
      const tokenIn = (fromRow.kind === "native" || fromRow.address === "native")
        ? "native"
        : String(fromRow.address || "");
      const prepRes = await fetch(`${API_BASE}/api/wallet/swap/prepare`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(_authHeaders?.() || {}) },
        body: JSON.stringify({
          wallet,
          chain: chainKey,
          chainId,
          tokenIn,
          tokenOut: toSym,
          amount: Number(rawAmount),
          slippageBps: 100,
        }),
      });
      const prep = await prepRes.json().catch(() => ({}));
      if (!prepRes.ok || prep?.status !== "ok") throw new Error(prep?.error || `Prepare failed (${prepRes.status})`);
      const steps = Array.isArray(prep.steps) ? prep.steps : [];
      if (!steps.length) throw new Error("No swap steps returned.");

      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);
      const currentHex = await provider.request({ method: "eth_chainId" });
      const expectedHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== expectedHex.toLowerCase()) {
        throw new Error(`Please switch your Privy wallet to ${walletChainDisplayName(chainKey)}.`);
      }

      const hashes = [];
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        setWalletSwapMsg(`${step.label || step.type || "Step"} (${i + 1}/${steps.length})…`);
        const tx = {
          from: wallet,
          to: String(step.to || ""),
          data: String(step.data || "0x"),
          value: String(step.value || "0x0"),
        };
        const hash = await provider.request({ method: "eth_sendTransaction", params: [tx] });
        hashes.push(hash);
        try {
          saveWalletTransaction({
            type: `WALLET SWAP ${String(step.type || "").toUpperCase()}`,
            chain: chainKey,
            asset: fromRow.symbol,
            amount: rawAmount,
            from: wallet,
            to: step.to,
            txHash: hash,
            status: "SUBMITTED",
          });
        } catch (_) {}
      }
      setWalletSwapMsg(`Swap submitted (${hashes.length} tx). ${toSym} stays in your wallet. Refresh balances shortly.`);
      try { await refreshBalances?.(); } catch (_) {}
    } catch (e) {
      setWalletSwapMsg(String(e?.message || e || "Swap failed"));
    } finally {
      setWalletSwapBusy(false);
    }
  };

  const sendPrivyWalletAsset = async () => {
    try {
      setWalletSendMsg("");
      if (!wallet) throw new Error("Wallet not connected.");
      if (!_isAddr(walletSendTo)) throw new Error("Recipient address invalid.");
      const row = walletAssetRows.find((item) => item.key === walletSendAssetKey);
      if (!row) throw new Error("Select an asset.");
      const rawAmount = String(walletSendAmount || "").trim();
      if (!/^\d+(\.\d+)?$/.test(rawAmount) || Number(rawAmount) <= 0) throw new Error("Enter a valid amount.");
      if (Number(rawAmount) > Number(row.amount || 0)) throw new Error("Amount exceeds wallet balance.");

      const chainKey = normalizeWalletChainKey(row.chain || DEFAULT_CHAIN);
      const chainId = CHAIN_ID?.[chainKey];
      if (!chainId) throw new Error(`Unsupported network: ${chainKey}`);

      setWalletSendBusy(true);
      setWalletSendMsg(`Preparing ${row.symbol} transfer…`);
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);
      const currentHex = await provider.request({ method: "eth_chainId" });
      const expectedHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== expectedHex.toLowerCase()) {
        throw new Error(`Please switch your Privy wallet to ${walletChainDisplayName(chainKey)}.`);
      }

      let tx;
      if (row.kind === "native" || row.address === "native") {
        const value = decimalStringToUnits(rawAmount, 18);
        tx = { from: wallet, to: String(walletSendTo).trim(), value: "0x" + value.toString(16), data: "0x" };
      } else {
        if (!_isAddr(row.address)) throw new Error(`${row.symbol} token contract is unavailable.`);
        const units = decimalStringToUnits(rawAmount, Number(row.decimals ?? 18));
        tx = {
          from: wallet,
          to: row.address,
          value: "0x0",
          data: "0xa9059cbb" + _encodeAddress(walletSendTo) + _encodeUint256(units),
        };
      }

      setWalletSendMsg(`Confirm ${rawAmount} ${row.symbol} in Privy…`);
      const hash = await provider.request({ method: "eth_sendTransaction", params: [tx] });
      const txEntry = {
        type: "WALLET SEND",
        chain: chainKey,
        asset: row.symbol,
        amount: rawAmount,
        from: wallet,
        to: String(walletSendTo).trim(),
      };
      saveWalletTransaction({ ...txEntry, txHash: hash, status: "SUBMITTED" });
      setWalletSendMsg(`Signed and broadcast. Confirmation continues in Transactions. Tx: ${hash}`);
      setWalletSendAmount("");
      setWalletSendTo("");
      // Do not keep the Privy Wallet UI blocked while waiting for mining.
      _trackWalletTxInBackground(provider, hash, txEntry, () => {
        setWalletSendMsg(`Transfer confirmed on-chain. Tx: ${hash}`);
        refreshBalances();
      });
    } catch (e) {
      const msg = String(e?.message || e || "Transfer failed");
      const low = msg.toLowerCase();
      setWalletSendMsg(e?.code === 4001 || low.includes("rejected") || low.includes("denied") || low.includes("cancel") ? "Transfer cancelled by user." : msg);
    } finally {
      setWalletSendBusy(false);
    }
  };

  useEffect(() => {
    if (!wallet || !walletModalOpen) return;
    const candidates = walletAssetRows.filter((row) => row.kind === "custom" && row.address && !walletAssetSecurityByKey?.[row.key]);
    if (!candidates.length) return;
    let cancelled = false;
    const run = async () => {
      for (const row of candidates.slice(0, 30)) {
        if (cancelled) return;
        setWalletAssetSecurityByKey((prev) => ({ ...prev, [row.key]: { loading: true, checked: false } }));
        try {
          const result = await securityPrecheckForDeposit({ chainKey: row.chain, tokenAddress: row.address, symbol: row.symbol, isNative: false, token });
          if (cancelled) return;
          setWalletAssetSecurityByKey((prev) => ({
            ...prev,
            [row.key]: {
              loading: false, checked: true, allowed: result?.allowed === true,
              approved: result?.approved === true, blocked: result?.allowed !== true,
              reason: result?.reason || result?.blocked_by || "Not approved for Vault",
              checks: result?.checks || {},
            },
          }));
        } catch (error) {
          if (cancelled) return;
          setWalletAssetSecurityByKey((prev) => ({
            ...prev,
            [row.key]: { loading: false, checked: true, allowed: false, approved: false, blocked: true, reason: String(error?.message || "Security check failed") },
          }));
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [wallet, walletModalOpen, walletAssetRows, walletAssetSecurityByKey, token]);
  // BUILD456: click/tap outside the wallet panel closes it (does NOT disconnect).
  useEffect(() => {
    if (!walletModalOpen) return;
    const onPointerDown = (e) => {
      try {
        const panel = walletPanelRef.current;
        const trigger = walletTriggerRef.current;
        const target = e.target;
        if (panel && panel.contains(target)) return;
        if (trigger && trigger.contains(target)) return;
        // any element marked as wallet trigger (pill / value)
        if (target?.closest?.("[data-nexus-wallet-trigger]")) return;
        setWalletModalOpen(false);
      } catch (_) {}
    };
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [walletModalOpen]);


  const [walletUsdLoading, setWalletUsdLoading] = useState(false);
  const [walletProfitBaselineStore, setWalletProfitBaselineStore] = useLocalStorageState("nexus_wallet_profit_baseline_v1", {});

  const walletProfitBaseline = useMemo(() => {
    const key = String(wallet || "").toLowerCase();
    const entry = key ? walletProfitBaselineStore?.[key] : null;
    const amount = Number(entry?.amountUsd ?? entry?.amount ?? entry);
    return {
      amountUsd: Number.isFinite(amount) && amount > 0 ? amount : null,
      ts: Number(entry?.ts || 0) || null,
    };
  }, [wallet, walletProfitBaselineStore]);

  const walletProfit = useMemo(() => {
    const total = Number(walletUsd?.total);
    const base = Number(walletProfitBaseline?.amountUsd);
    if (!Number.isFinite(total) || !Number.isFinite(base) || base <= 0) {
      return { available: false, amount: null, pct: null };
    }
    const amount = total - base;
    const pct = base > 0 ? (amount / base) * 100 : null;
    return { available: true, amount, pct };
  }, [walletUsd, walletProfitBaseline]);

  const setWalletProfitBaselineNow = useCallback(() => {
    const key = String(wallet || "").toLowerCase();
    const total = Number(walletUsd?.total);
    if (!key || !Number.isFinite(total) || total <= 0) return;
    setWalletProfitBaselineStore((prev) => ({
      ...(prev || {}),
      [key]: { amountUsd: total, ts: Date.now() },
    }));
  }, [wallet, walletUsd, setWalletProfitBaselineStore]);

  const clearWalletProfitBaseline = useCallback(() => {
    const key = String(wallet || "").toLowerCase();
    if (!key) return;
    setWalletProfitBaselineStore((prev) => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  }, [wallet, setWalletProfitBaselineStore]);

  // ------------------------
  // Wallet tokens (User-added, unlimited, persisted)
  // ------------------------
  // We do NOT auto-scan all tokens on a chain (prevents spam/airdrop tokens).
  // Only tokens explicitly added by the user are fetched + shown.
  const [walletTokenStore, setWalletTokenStore] = useLocalStorageState("nexus_wallet_tokens_v1", {});
  const walletKey = String(wallet || "").toLowerCase();
  const walletTokensByChain = useMemo(() => {
    const empty = { ETH: [], POL: [], BNB: [] };
    if (!walletKey) return empty;
    const v = walletTokenStore?.[walletKey];
    return v && typeof v === "object" ? { ...empty, ...v } : empty;
  }, [walletKey, walletTokenStore]);

  const setWalletTokensForChain = (chain, nextList) => {
    const c = normalizeWalletChainKey(chain);
    if (!walletKey) return;
    setWalletTokenStore((prev) => {
      const empty = { ETH: [], POL: [], BNB: [] };
      const cur = (prev && prev[walletKey] && typeof prev[walletKey] === "object") ? { ...empty, ...prev[walletKey] } : empty;
      const next = { ...cur, [c]: Array.isArray(nextList) ? nextList : [] };
      return { ...(prev || {}), [walletKey]: next };
    });
  };

  const removeWalletToken = (chain, tokenAddress) => {
    const c = normalizeWalletChainKey(chain);
    const addr = String(tokenAddress || contract || "").toLowerCase();

    const cur = walletTokensByChain?.[c] || [];
    const next = cur.filter((t) => String(t?.address || "").toLowerCase() != addr);
    setWalletTokensForChain(c, next);
  };

  // Add-token modal state
  const [addTokenOpen, setAddTokenOpen] = useState(false);
  const [addTokenChain, setAddTokenChain] = useState(() => normalizeWalletChainKey(DEFAULT_CHAIN));
  const [addTokenQuery, setAddTokenQuery] = useState("");
  const [addTokenContract, setAddTokenContract] = useState("");
  const [addTokenBusy, setAddTokenBusy] = useState(false);
  const [addTokenErr, setAddTokenErr] = useState("");
  const [tokenListCache, setTokenListCache] = useState({ ETH: null, POL: null, BNB: null });

  const TOKEN_LIST_URL = {
    ETH: "https://tokens.uniswap.org",
    POL: "https://api-polygon-tokens.polygon.technology/tokenlists/popular.tokenlist.json",
    BNB: "https://tokens.pancakeswap.finance/pancakeswap-extended.json",
  };

  const loadTokenList = async (chain) => {
    const c = String(chain || "").toUpperCase();
    if (tokenListCache?.[c]) return;
    const url = TOKEN_LIST_URL[c];
    if (!url) return;
    try {
      const res = await fetch(url, { cache: "force-cache" });
      const json = await res.json();
      const tokens = Array.isArray(json?.tokens) ? json.tokens : [];
      const filtered = tokens
        .filter((t) => Number(t?.chainId) === CHAIN_ID[c])
        .map((t) => ({
          address: String(t?.address || "").toLowerCase(),
          symbol: String(t?.symbol || "").toUpperCase(),
          name: String(t?.name || ""),
          decimals: Number(t?.decimals ?? 18),
        }))
        .filter((t) => t.address && t.symbol && Number.isFinite(t.decimals));

      // Always include Nexus stable whitelist first (USDC/USDT), even if external lists fail/miss them.
      const merged = [];
      const seenTokens = new Set();
      for (const t of [...getStableWhitelistForChain(c), ...filtered]) {
        const addr = String(t?.address || "").toLowerCase();
        if (!addr || seenTokens.has(addr)) continue;
        seenTokens.add(addr);
        merged.push({
          address: addr,
          symbol: String(t?.symbol || "").toUpperCase(),
          name: String(t?.name || ""),
          decimals: Number(t?.decimals ?? 18),
        });
      }
      setTokenListCache((prev) => ({ ...(prev || {}), [c]: merged }));
    } catch {
      // If token list fetch fails, user can still add via contract address.
      setTokenListCache((prev) => ({ ...(prev || {}), [c]: getStableWhitelistForChain(c).map((t) => ({ ...t, address: String(t.address || "").toLowerCase() })) }));
    }
  };

  const openAddToken = (chain) => {
    const c = normalizeWalletChainKey(chain || balActiveChain || DEFAULT_CHAIN);
    setAddTokenChain(c);
    setAddTokenQuery("");
    setAddTokenContract("");
    setAddTokenErr("");
    setAddTokenOpen(true);
    loadTokenList(c);
  };

  const addWalletToken = async () => {
    const chain = normalizeWalletChainKey(addTokenChain || balActiveChain || DEFAULT_CHAIN);
    const list = walletTokensByChain?.[chain] || [];

    const safeAdd = (token) => {
      const addr = String(token?.address || "").toLowerCase();
      if (!addr) return;
      const exists = list.some((t) => String(t?.address || "").toLowerCase() === addr);
      if (exists) {
        setAddTokenErr("Token already added.");
        return;
      }
      const next = [...list, {
        address: addr,
        symbol: String(token?.symbol || "").toUpperCase() || "TOKEN",
        decimals: Number(token?.decimals ?? 18),
        name: String(token?.name || ""),
      }];
      setWalletTokensForChain(chain, next);
      setBalActiveChain(chain);
      setWsChainKey(chain);
      // Refresh balances after state commit (ensure new token list is included).
      // React state updates are async; calling refresh immediately can use stale walletTokens.
      requestAnimationFrame(() => requestAnimationFrame(() => refreshBalances()));
      setTimeout(() => refreshBalances(), 200);
      setAddTokenOpen(false);
    };

    setAddTokenBusy(true);
    setAddTokenErr("");
    try {
      const contract = String(addTokenContract || "").trim();
      if (contract && /^0x[a-fA-F0-9]{40}$/.test(contract)) {
        if (!alchemyClients?.[chain]) throw new Error("Network not ready.");
        const md = await alchemyClients[chain].core.getTokenMetadata(contract);
        const token = {
          address: String(contract).toLowerCase(),
          symbol: String(md?.symbol || "").toUpperCase(),
          name: String(md?.name || ""),
          decimals: Number(md?.decimals ?? 18),
        };
        if (!token.symbol) throw new Error("Invalid token contract.");
        safeAdd(token);
        return;
      }

      // If no contract, add from selected search result (first match by query+symbol)
      const q = String(addTokenQuery || "").trim().toLowerCase();
      if (!q) throw new Error("Search or paste a contract address.");
      const pool = tokenListCache?.[chain] || [];
      const match = pool.find((t) => (t.symbol || "").toLowerCase() == q || (t.address || "").toLowerCase() == q);
      if (!match) throw new Error("Select a token from results or paste contract address.");
      safeAdd(match);
    } catch (e) {
      setAddTokenErr(String(e?.message || e || "Failed to add token"));
    } finally {
      setAddTokenBusy(false);
    }
  };

  // Sync Privy auth + embedded wallet into our local UI state.
  // IMPORTANT:
  // Keep the initial Privy wallet-creation flow as light as possible:
  // - set wallet
  // - fetch Privy access token
  // - do NOT immediately trigger backend nonce/sign/verify here
  // Access / Redeem / Subscribe continue to work via wallet + refreshAccess().
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!ready) return;

      if (!authenticated) {
        _autoAuthStarted.current = false;
        setWallet("");
        setToken("");
        setPrivyJwt("");
        try { localStorage.removeItem("nexus_privy_jwt"); } catch {}
        return;
      }

      const embedded =
        privyWallets?.find((w) =>
          ["privy", "embedded"].includes(String(w?.walletClientType || "").toLowerCase()) ||
          String(w?.connectorType || "").toLowerCase() === "embedded"
        ) ||
        privyWallets?.[0];

      const addr = String(embedded?.address || "").toLowerCase();
      if (!cancelled && addr) {
        setWallet(addr);
      }

      try {
        const t = (await getAccessToken?.()) || "";
        if (!cancelled) {
          setPrivyJwt(t);
          try {
            if (t) localStorage.setItem("nexus_privy_jwt", t);
            else localStorage.removeItem("nexus_privy_jwt");
          } catch {}
        }
      } catch {
        if (!cancelled) setPrivyJwt("");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, authenticated, privyWallets?.length]);

  useEffect(() => {
    if (!wallet) {
      _autoAuthStarted.current = false;
      return;
    }

    if (token) return;
    if (!authenticated) return;
    if (_autoAuthStarted.current) return;

    const walletAtSchedule = String(wallet || "").toLowerCase();

    const timer = setTimeout(async () => {
      try {
        if (!walletAtSchedule) return;
        if (String(wallet || "").toLowerCase() !== walletAtSchedule) return;
        if (token) return;
        if (!authenticated) return;
        if (_autoAuthStarted.current) return;
        if (_backendAuthInFlight.current) return;

        _autoAuthStarted.current = true;
        await ensureBackendAuthToken();
      } catch (_) {
        // Keep silent in UI. If auto-auth fails, the normal on-demand flow still works.
        _autoAuthStarted.current = false;
      }
    }, 3000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, token, authenticated]);

  const connectWallet = async () => {
    // Keep login simple to avoid interfering with Privy's wallet-creation UI.
    try {
      if (!ready) return;
      if (authenticated) return;
      if (_loginInFlight.current) return;
      _loginInFlight.current = true;
      setErrorMsg("");
      await login();
    } catch (e) {
            setErrorMsg("");
    } finally {
      _loginInFlight.current = false;
    }
  };

  const disconnectWallet = async () => {
    try {
      try { resetWalletBoundUi({ clearAuth: true }); } catch {}
      await logout();
      setWalletModalOpen(false);
    } catch (e) {
      setErrorMsg(String(e?.message || e || "Logout failed"));
    } finally {
      try { resetWalletBoundUi({ clearAuth: true }); } catch {}
    }
  };

  const alchemyClients = useMemo(() => {
    if (!ALCHEMY_KEY) return null;
    // alchemy-sdk uses the API key directly; no manual RPC fetch needed.
    const clients = {
      ETH: new Alchemy({ apiKey: ALCHEMY_KEY, network: Network.ETH_MAINNET }),
      POL: new Alchemy({ apiKey: ALCHEMY_KEY, network: Network.MATIC_MAINNET }),
    };
    // Some older alchemy-sdk versions may not include BNB_MAINNET.
    if (Network?.BNB_MAINNET) {
      clients.BNB = new Alchemy({ apiKey: ALCHEMY_KEY, network: Network.BNB_MAINNET });
    }
    return clients;
  }, [ALCHEMY_KEY]);

  const fetchBackendNativeBalances = async (address, chains) => {
    try {
      const q = new URLSearchParams({
        wallet: String(address || ""),
        wallet_address: String(address || ""),
        chains: (Array.isArray(chains) ? chains : ENABLED_CHAINS).join(","),
      }).toString();
      const r = await api(`/api/wallet/native-balances?${q}`, { method: "GET", token, wallet: address });
      return r?.balances || {};
    } catch {
      return {};
    }
  };

  const fetchBackendDiscoveredTokens = async (address, chains) => {
    try {
      const r = await api("/api/wallet/discover-tokens", {
        method: "POST", token, wallet: address,
        body: { wallet: address, wallet_address: address, chains: Array.isArray(chains) ? chains : [] },
      });
      return r?.tokens_by_chain || {};
    } catch {
      return {};
    }
  };

  const fetchBackendTokenBalances = async (address, chain, specs) => {
    try {
      const tokenSpecs = Array.isArray(specs) ? specs.filter((t) => /^0x[a-fA-F0-9]{40}$/.test(String(t?.address || ""))) : [];
      if (!tokenSpecs.length) return {};
      const r = await api("/api/wallet/token-balances", {
        method: "POST",
        token,
        wallet: address,
        body: {
          wallet: String(address || ""),
          wallet_address: String(address || ""),
          chain: normalizeWalletChainKey(chain),
          tokens: tokenSpecs.map((t) => ({ address: t.address, symbol: t.symbol, decimals: t.decimals })),
        },
      });
      return r?.balances || {};
    } catch {
      return {};
    }
  };

  const fetchProviderErc20BalanceRaw = async (address, chain, tokenAddress) => {
    try {
      const c = normalizeWalletChainKey(chain);
      const provider = await _getEmbeddedProvider();
      const chainId = CHAIN_ID?.[c];
      if (!provider?.request || !chainId) return null;
      await _trySwitchChain(provider, chainId);
      const walletHex = String(address || "").toLowerCase().replace(/^0x/, "").padStart(64, "0");
      const to = String(tokenAddress || "");
      if (!/^0x[a-fA-F0-9]{40}$/.test(to)) return null;
      const data = "0x70a08231" + walletHex; // balanceOf(address)
      const rawHex = await provider.request({ method: "eth_call", params: [{ to, data }, "latest"] });
      if (!String(rawHex || "").startsWith("0x")) return null;
      return hexToBigInt(rawHex).toString();
    } catch {
      return null;
    }
  };

  /** BUILD449: chain-RPC balanceOf — no Privy switch, no JSON number wei. Returns decimal string of whole tokens. */
  const fetchWnativeBalanceHuman = async (address, chainKey, tokenAddress) => {
    try {
      const c = normalizeWalletChainKey(chainKey);
      const to = String(tokenAddress || "").toLowerCase();
      const owner = String(address || "").toLowerCase();
      if (!/^0x[a-f0-9]{40}$/.test(to) || !/^0x[a-f0-9]{40}$/.test(owner)) return null;
      const data = "0x70a08231" + owner.replace(/^0x/, "").padStart(64, "0");
      let rawHex = null;
      try {
        rawHex = await alchemyRpc(c, "eth_call", [{ to, data }, "latest"]);
      } catch (_) {
        rawHex = null;
      }
      if (!rawHex || !String(rawHex).startsWith("0x")) {
        // fallback provider
        rawHex = null;
        try {
          const provider = await _getEmbeddedProvider();
          const chainId = CHAIN_ID?.[c];
          if (provider?.request && chainId) {
            await _trySwitchChain(provider, chainId);
            rawHex = await provider.request({ method: "eth_call", params: [{ to, data }, "latest"] });
          }
        } catch (_) {}
      }
      if (!rawHex || !String(rawHex).startsWith("0x")) return null;
      const bi = hexToBigInt(rawHex);
      return stripTrailingZeros(formatNativeFromWei(bi, 18, 8));
    } catch {
      return null;
    }
  };

  const refreshBalances = async () => {
    if (!wallet) return;
    // Wallet balances are loaded through the backend RPC fallback.
    // This avoids frontend Alchemy limits and keeps API keys out of the browser path.
    setBalLoading(true);
    setBalError("");
    try {
      const address = wallet;
      const baseChains = ENABLED_CHAINS;
      const [backendNativeBalances, discoveredTokensByChain] = await Promise.all([
        fetchBackendNativeBalances(address, baseChains),
        fetchBackendDiscoveredTokens(address, baseChains),
      ]);

      // Read chains one after another. A Privy provider has one active chain at a time;
      // parallel wallet_switchEthereumChain calls can make ETH appear as BNB/POL.
      const results = [];
      for (const c of baseChains) {
          try {
            let nativeStr = "";

            // Preferred source: a chain-specific RPC endpoint. This never depends on the
            // currently selected Privy chain and therefore cannot copy ETH to BNB/POL.
            try {
              const rawBal = await alchemyRpc(c, "eth_getBalance", [address, "latest"]);
              const rpcNum = Number(Utils.formatEther(hexToBigInt(rawBal || "0x0")));
              if (Number.isFinite(rpcNum)) nativeStr = String(rpcNum);
            } catch (_) {}

            // Fallback: backend result for this exact chain. Zero is a valid balance and
            // must remain zero; never replace it with a balance from another active chain.
            if (nativeStr === "") {
              try {
                const backendNative = backendNativeBalances?.[c]?.native;
                if (backendNative !== null && backendNative !== undefined && Number.isFinite(Number(backendNative))) {
                  nativeStr = String(Number(backendNative));
                }
              } catch (_) {}
            }

            if (nativeStr === "") {
              throw new Error(c + " chain-specific balance unavailable");
            }

            const nativeNum = Number(nativeStr);
            const native = Number.isFinite(nativeNum)
              ? stripTrailingZeros(nativeNum.toFixed(6))
              : nativeStr;

// Phase 2: whitelisted tokens (per chain)
// Phase 2: tokens are fetched ONLY from (a) stable whitelist + (b) user-added tokens.
const stableSpecs = getStableWhitelistForChain(c);
const customSpecs = [
  ...(walletTokensByChain?.[c] || []),
  ...(discoveredTokensByChain?.[c] || []),
];
// BUILD445: always query wrapped native (WPOL/WETH/WBNB/…) so balance > 0 shows in Assets.
// Zero balances stay out of the UI (walletAssetRows filters amount > 0).
const _wn = wnativeSpecForChain(c);
const wnativeSpecs = _wn && _wn.address ? [{ address: _wn.address, symbol: _wn.symbol, decimals: 18, name: _wn.name }] : [];

// De-dupe by contract address. Automatic discovery is display-only; Vault admission
// is still decided separately by exact-contract Owner approval + GoPlus security.
const allSpecs = [];
const seen = new Map(); // address -> index in allSpecs
const wnAddr = String(_wn?.address || "").toLowerCase();
for (const t of [...stableSpecs, ...customSpecs, ...wnativeSpecs]) {
  const addr = String(t?.address || "").toLowerCase();
  if (!addr) continue;
  const isWn = !!(wnAddr && addr === wnAddr);
  const entry = {
    address: addr,
    symbol: isWn ? String(_wn.symbol || "WPOL").toUpperCase() : String(t?.symbol || "").toUpperCase(),
    decimals: isWn ? 18 : Number(t?.decimals ?? 18),
    name: isWn ? String(_wn.name || "Wrapped native") : String(t?.name || ""),
  };
  if (seen.has(addr)) {
    // BUILD447: if discovery came first with wrong decimals, overwrite with WNative truth
    if (isWn) {
      allSpecs[seen.get(addr)] = entry;
    }
    continue;
  }
  seen.set(addr, allSpecs.length);
  allSpecs.push(entry);
}
if (wnAddr && !seen.has(wnAddr)) {
  seen.set(wnAddr, allSpecs.length);
  allSpecs.push({
    address: wnAddr,
    symbol: String(_wn.symbol || "WPOL").toUpperCase(),
    decimals: 18,
    name: String(_wn.name || "Wrapped native"),
  });
}

const stables = {};
const custom = [];

if (allSpecs.length) {
  const respBalances = await fetchBackendTokenBalances(address, c, allSpecs);
  const stableAddrSet = new Set(stableSpecs.map((t) => String(t?.address || "").toLowerCase()));

  for (const t of allSpecs) {
    let rawBalance = respBalances?.[t.address]?.balance_raw;
    const isWnTok = !!(wnAddr && String(t.address || "").toLowerCase() === wnAddr);

    // BUILD448: Wei balances > 2^53 cannot be JSON numbers — 80e18 becomes ~8e18 and shows as 8.
    // Prefer hex/string paths; for WNative always read via provider eth_call (hex string).
    const toWeiBi = (raw) => {
      if (raw == null || raw === "") return 0n;
      if (typeof raw === "bigint") return raw;
      if (typeof raw === "number") {
        // unsafe — reject float path for large balances
        if (!Number.isFinite(raw) || raw < 0) return 0n;
        if (raw > Number.MAX_SAFE_INTEGER) return 0n;
        return BigInt(Math.floor(raw));
      }
      const s = String(raw).trim();
      if (!s) return 0n;
      if (s.startsWith("0x") || s.startsWith("0X")) return BigInt(s);
      if (/e/i.test(s)) return 0n; // scientific notation from JSON number stringification
      if (!/^[0-9]+$/.test(s)) return 0n;
      return BigInt(s);
    };

    try {
      let backendBi = toWeiBi(rawBalance);
      // Always refresh WNative from provider hex — never trust JSON number wei
      if (isWnTok || backendBi <= 0n) {
        const providerRaw = await fetchProviderErc20BalanceRaw(address, c, t.address);
        if (providerRaw != null) {
          const providerBi = toWeiBi(providerRaw);
          if (isWnTok || providerBi > backendBi) {
            rawBalance = providerRaw;
            backendBi = providerBi;
          }
        }
      }
    } catch (_) {}

    const bi = toWeiBi(rawBalance);
    const dec = isWnTok ? 18 : Number(t.decimals ?? 18);
    const val = stripTrailingZeros(formatNativeFromWei(bi, dec, 6));
    if (stableAddrSet.has(t.address)) {
      // For stables, show compact (2 decimals) unless tiny.
      const val2 = stripTrailingZeros(formatNativeFromWei(bi, Number(t.decimals ?? 6), 2));
      stables[t.symbol] = val2;
    } else {
      let bal = val;
      let decOut = isWnTok ? 18 : Number(t.decimals ?? 18);
      if (isWnTok) {
        const human = await fetchWnativeBalanceHuman(address, c, t.address);
        if (human != null && human !== "" && Number(human) >= 0) {
          bal = human;
          decOut = 18;
        }
      }
      custom.push({ address: t.address, symbol: t.symbol, balance: bal, decimals: decOut, name: t.name });
    }
  }
}

results.push([c, { native, stables, custom }]);
          } catch (e) {
            results.push([c, { native: "—", error: String(e?.message || e || "chain-specific RPC balance unavailable") }]);
          }
      }

      const out = {};
      for (const [c, v] of results) out[c] = v;
      setBalByChain(out);
      const chainErrors = Object.entries(out)
        .filter(([, v]) => v?.error)
        .map(([c, v]) => `${c}: ${v.error}`);
      setBalError(chainErrors.length ? chainErrors.join(" · ") : "");

      // Compute wallet total value in USD (best-effort; unpriced tokens are excluded from total).
      (async () => {
        try {
          setWalletUsdLoading(true);

          const chains = ENABLED_CHAINS.filter((c) => out?.[c]);
          const nativePx = await fetchNativeUsdPrices(chains);

          // Token prices (user-added tokens only; stables assumed $1)
          const tokenPxByChain = {};
          for (const c of chains) {
            const custom = out?.[c]?.custom || [];
            const addrs = custom.map((t) => t?.address).filter(Boolean);
            tokenPxByChain[c] = addrs.length ? await fetchTokenUsdPrices(c, addrs) : {};
          }

          // Save latest price maps for UI (wallet token price display).
          setWalletPx({ native: nativePx || {}, tokenByChain: tokenPxByChain || {}, ts: Date.now() });

const byChain = {};
          let total = 0;
          let unpriced = 0;

          for (const c of chains) {
            const row = out?.[c] || {};
            // native
            const nativeBal = Number(row?.native);
            const nPx = nativePx?.[c];
            const nativeVal = (Number.isFinite(nativeBal) && Number.isFinite(nPx)) ? nativeBal * nPx : 0;

            // stables (assume $1)
            const stablesMap = row?.stables || {};
            let stableVal = 0;
            for (const k of Object.keys(stablesMap || {})) {
              const b = Number(stablesMap?.[k]);
              if (Number.isFinite(b)) stableVal += b * 1.0;
            }

            // custom priced tokens
            let customVal = 0;
            for (const t of (row?.custom || [])) {
              const addr = String(t?.address || "").toLowerCase();
              const b = Number(t?.balance);
              const px = tokenPxByChain?.[c]?.[addr];
              if (Number.isFinite(b) && Number.isFinite(px)) customVal += b * px;
              else if (Number.isFinite(b) && b > 0) unpriced += 1;
            }

            const chainTotal = nativeVal + stableVal + customVal;
            byChain[c] = {
              total: chainTotal,
              native: nativeVal,
              stables: stableVal,
              custom: customVal,
            };
            total += chainTotal;
          }

          setWalletUsd({ total, byChain, unpriced, ts: Date.now() });
        } catch {
          // keep silent; balances still show
          setWalletUsd((prev) => ({ ...(prev || {}), total: prev?.total ?? null, ts: Date.now() }));

      // Grid budget locks (USD-based) so wallet can show "Available vs In bots"
      try {
        setGridBudgetsErr("");

        // Prefer per-chain budgets if backend supports it.
        // Fallback to legacy totals-only endpoint.
        let bdata = null;
        try {
          const budByChain = await api("/api/grid/budgets_by_chain", { method: "GET", token });
          bdata = budByChain?.data || budByChain;
        } catch (_) {
          const bud = await api("/api/grid/budgets", { method: "GET", token });
          bdata = bud?.data || bud;
        }

        const totals = (bdata && bdata.totals) ? bdata.totals : { locked_usd: 0, available_usd: 0 };
        const by_chain = (bdata && bdata.by_chain) ? bdata.by_chain : {};
        const items = (bdata && bdata.items) ? bdata.items : [];

        setGridBudgets({ totals, by_chain, items, ts: Date.now() });
      } catch (e) {
        // Don't break wallet balances if budgets fail
        setGridBudgetsErr(String(e?.message || e || "Failed to load grid budgets"));
        setGridBudgets((prev) => ({ ...(prev || {}), totals: prev?.totals ?? { locked_usd: 0, available_usd: 0 }, by_chain: prev?.by_chain ?? {}, items: prev?.items ?? [], ts: Date.now() }));
      }

        } finally {
          setWalletUsdLoading(false);
        }
      })();
    } catch (e) {
      setBalError(String(e?.message || e || "Failed to load balances"));
    } finally {
      setBalLoading(false);
    }
  };

  // Auto-refresh balances when the token list changes (add/remove),
  // so the user doesn't need to click Refresh manually.
  const walletTokensSig = useMemo(() => JSON.stringify(walletTokensByChain), [walletTokensByChain]);
  const didInitTokenAutoRefresh = useRef(false);
  useEffect(() => {
    if (!wallet) return;
    if (!didInitTokenAutoRefresh.current) {
      didInitTokenAutoRefresh.current = true;
      return;
    }
    const t = setTimeout(() => {
      refreshBalances();
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, walletTokensSig]);

  // Auto-load balances when the wallet panel is opened.
  // Auto-load balances once after wallet connect (so Grid coin list is populated without opening the wallet panel).
  const didAutoLoadBalances = useRef(false);
  useEffect(() => {
    if (!wallet) return;
    if (didAutoLoadBalances.current) return;
    didAutoLoadBalances.current = true;
    refreshBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);

  // Refresh balances when active chain changes (keeps UI + Grid coin list in sync).
  useEffect(() => {
    if (!wallet) return;
    refreshBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balActiveChain]);

  // Auto-load balances when the wallet panel is opened.
  useEffect(() => {
    if (!walletModalOpen) return;
    if (!wallet) return;
    refreshBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletModalOpen, wallet]);

  // watchlist
  const [watchItems, setWatchItems] = useState(() => cloneNexusDefaultWatchItems());
  const [watchRows, setWatchRows] = useState([]);
  const [watchDragKey, setWatchDragKey] = useState("");
  const [watchDropKey, setWatchDropKey] = useState("");
  const [watchSortMode, setWatchSortMode] = useState("manual"); // manual | winner | loser
  const [ratingModal, setRatingModal] = useState({ open: false, row: null, symbol: "", systemRating: "", systemScore: 0 });
  const [ratingStatus, setRatingStatus] = useState(null);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [ratingErr, setRatingErr] = useState("");
  const [ratingSummaryBySymbol, setRatingSummaryBySymbol] = useState({});
  const [userRatingBySymbol, setUserRatingBySymbol] = useState({});
  const [onchainBySymbol, setOnchainBySymbol] = useState({});
  const [marketConditionBySymbol, setMarketConditionBySymbol] = useState({});
  const [activeWhaleNews, setActiveWhaleNews] = useState(null);

  const [watchSyncedWallet, setWatchSyncedWallet] = useState("");
  const [appStateSyncedWallet, setAppStateSyncedWallet] = useState("");
  const watchSyncBusyRef = useRef(false);
  const watchApplyingServerRef = useRef(false);
  const watchDirtyRef = useRef(false);
  const watchLastServerTsRef = useRef((() => {
    try { return Number(localStorage.getItem("nexus_watch_server_updated_ts") || 0) || 0; } catch { return 0; }
  })());
  const storeWatchServerTs = useCallback((ts) => {
    const n = Number(ts || 0);
    if (!Number.isFinite(n) || n <= 0) return;
    watchLastServerTsRef.current = Math.max(Number(watchLastServerTsRef.current || 0), n);
    try { localStorage.setItem("nexus_watch_server_updated_ts", String(watchLastServerTsRef.current)); } catch {}
  }, []);
  const markWatchDirty = useCallback(() => {
    if (!watchApplyingServerRef.current) watchDirtyRef.current = true;
  }, []);
  const appStateHydratedRef = useRef(false);
  const rotationBackendHydratedRef = useRef(false);
  const rotationBackendSyncBusyRef = useRef(false);
  const rotationBackendApplyingRef = useRef(false);
  const prevWalletRef = useRef(String(wallet || "").toLowerCase());
  const appStateSyncBusyRef = useRef(false);
  const appStateApplyingServerRef = useRef(false);
  const appStateLastServerTsRef = useRef((() => {
    try { return Number(localStorage.getItem("nexus_app_state_server_updated_ts") || 0) || 0; } catch { return 0; }
  })());
  const storeAppStateServerTs = useCallback((ts) => {
    const n = Number(ts || 0);
    if (!Number.isFinite(n) || n <= 0) return;
    appStateLastServerTsRef.current = Math.max(Number(appStateLastServerTsRef.current || 0), n);
    try { localStorage.setItem("nexus_app_state_server_updated_ts", String(appStateLastServerTsRef.current)); } catch {}
  }, []);
  const normalizeWatchItems = useCallback((items) => {
    const arr = Array.isArray(items) ? items : [];
    const out = [];
    const seen = new Set();

    for (const it of arr) {
      if (!it || typeof it !== "object") continue;
      const mode = String(it?.mode || "market").toLowerCase();

      if (mode === "dex") {
        const contract = String(it?.contract || it?.tokenAddress || "").trim().toLowerCase();
        if (!contract) continue;
        const item = {
          ...it,
          mode: "dex",
          contract,
          tokenAddress: contract,
          symbol: String(it?.symbol || contract.slice(0, 10)).trim().toUpperCase(),
          chain: String(it?.chain || "pol").trim(),
        };
        const key = _watchKeyFromItem(item);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      } else {
        const symbol = String(it?.symbol || "").trim().toUpperCase();
        const cgId = String(it?.coingecko_id || it?.id || "").trim().toLowerCase();
        if (!symbol || !cgId) continue;
        const item = {
          ...it,
          mode: "market",
          symbol,
          coingecko_id: cgId,
          id: cgId,
          name: String(it?.name || symbol).trim(),
        };
        const key = _watchKeyFromItem(item);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      }
    }
    return out;
  }, []);

  const saveWatchlistToServer = useCallback(async (itemsArg) => {
    const wa = resolveWalletAddress(wallet);
    if (!wa) return null;
    try {
      const normalized = normalizeWatchItems(itemsArg);

      const clean = normalized.map((it) => {
        const mode = String(it?.mode || "market").toLowerCase();

        if (mode === "dex") {
          const contract = String(it?.contract || it?.tokenAddress || "").trim().toLowerCase();
          return {
            mode: "dex",
            contract,
            tokenAddress: contract,
            symbol: String(it?.symbol || "").trim().toUpperCase(),
            chain: String(it?.chain || "pol").trim(),
            name: String(it?.name || it?.symbol || "").trim(),
          };
        }

        const cgId = String(it?.coingecko_id || it?.id || "").trim().toLowerCase();
        return {
          mode: "market",
          id: cgId,
          coingecko_id: cgId,
          symbol: String(it?.symbol || "").trim().toUpperCase(),
          name: String(it?.name || it?.symbol || "").trim(),
        };
      });

      const res = await api(`/api/watchlist?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, {
        method: "POST",
        token,
        wallet: wa,
        body: { wallet: wa, wallet_address: wa, items: clean },
      });
      storeWatchServerTs(res?.updated_ts);
      watchDirtyRef.current = false;
      return res;
    } catch (e) {
      console.warn("watchlist save failed", e);
      return null;
    }
  }, [wallet, token, normalizeWatchItems, storeWatchServerTs]);

  const syncWatchlistFromServer = useCallback(async () => {
    const wa = resolveWalletAddress(wallet);
    if (!wa) {
      // Public/guest Nexus view: always start with a useful market instead of an empty page.
      // Nothing is written to a wallet/backend until a real Privy wallet exists.
      const guestItems = cloneNexusDefaultWatchItems();
      watchApplyingServerRef.current = true;
      setWatchItems(guestItems);
      watchApplyingServerRef.current = false;
      fetchWatchSnapshot(guestItems, { force: true, user: false, allowGuest: true });
      return;
    }
    if (watchSyncBusyRef.current) return;
    watchSyncBusyRef.current = true;

    try {
      const r = await api(`/api/watchlist?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, {
        method: "GET",
        token,
        wallet: wa,
      });

      const serverItems = normalizeWatchItems(r?.items || []);
      const localItems = normalizeWatchItems(watchItems || []);
      const serverUpdatedTs = Number(r?.updated_ts || 0) || 0;
      const lastSeenServerTs = Number(watchLastServerTsRef.current || 0) || 0;

      const sig = (arr) => JSON.stringify((arr || []).map((x) => _watchKeyFromItem(x)).filter(Boolean));
      const serverSig = sig(serverItems);
      const localSig = sig(localItems);
      const neverSynced = String(watchSyncedWallet || "").toLowerCase() !== String(wa || "").toLowerCase();

      // Backend is the cross-device source of truth. Empty server watchlist is authoritative too.
      // LocalStorage must never re-upload old device items during hydration.
      const serverIsNewerOrDifferent = true;

      if (serverIsNewerOrDifferent && !watchDirtyRef.current) {
        // Server wins for normal hydration/sync. This is what makes desktop -> mobile and mobile -> desktop reliable.
        // Clear local tombstones for items that exist on the server, otherwise a coin re-added on another device can stay hidden.
        try { serverItems.forEach((it) => _clearTombstone(_watchKeyFromItem(it))); } catch {}
        if (serverSig !== localSig) {
          watchApplyingServerRef.current = true;
          setWatchItems(serverItems);
          watchApplyingServerRef.current = false;
        }
        storeWatchServerTs(serverUpdatedTs);
      }

      setWatchSyncedWallet(wa);
      fetchWatchSnapshot(serverItems, { force: true, user: false });
      setCompareForceNonce((n) => n + 1);
    } catch (e) {
      console.warn("watchlist sync failed", e);
      fetchWatchSnapshot();
    } finally {
      watchSyncBusyRef.current = false;
      watchApplyingServerRef.current = false;
    }
  }, [wallet, token, watchItems, normalizeWatchItems, setWatchItems, watchSyncedWallet, setWatchSyncedWallet, saveWatchlistToServer, storeWatchServerTs]);

  // Persist local watchlist edits explicitly in add/remove/reorder handlers.
  // Do NOT autosave every watchItems change: server hydration also changes watchItems,
  // and autosaving that can overwrite newer changes from the other device.
  const watchItemsPersistKey = useMemo(() => {
    try {
      return JSON.stringify(normalizeWatchItems(watchItems || []).map(_watchKeyFromItem).filter(Boolean));
    } catch {
      return "[]";
    }
  }, [watchItems, normalizeWatchItems]);

  useEffect(() => {
    // Kept intentionally as a no-op dependency anchor for future explicit sync diagnostics.
  }, [watchItemsPersistKey]);

  const watchSortValue = useCallback((row) => {
    const raw = row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? row?.change_24h;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, []);

  // Watchlist display rows must be derived from watchItems (MASTER), not only from
  // the async snapshot rows. This keeps desktop/mobile, Compare and AI in sync even
  // when the snapshot request is delayed or returns only partial rows on mobile.
  const watchDisplayRows = useMemo(() => {
    const items = normalizeWatchItems(watchItems || []);
    const rows = Array.isArray(watchRows) ? watchRows : [];
    const rowMap = new Map(rows.map((r) => [_watchKeyFromRow(r), r]));

    return items.map((it) => {
      const mode = String(it?.mode || "market").toLowerCase();
      const sym = String(it?.symbol || "").toUpperCase();
      const contract = String(it?.contract || it?.tokenAddress || "").toLowerCase();
      const cgId = String(it?.coingecko_id || it?.id || "").toLowerCase();
      const key = _watchKeyFromItem(it);
      const row = rowMap.get(key) || {};

      const base = {
        ...it,
        mode,
        symbol: sym,
        name: it?.name || row?.name || sym,
        source: "pending",
        price: null,
        change24h: null,
        chg_24h: null,
        volume24h: null,
        vol: null,
        marketCap: null,
        market_cap: null,
      };

      if (mode === "dex") {
        base.contract = contract;
        base.tokenAddress = contract;
        base.chain = String(it?.chain || row?.chain || "pol").trim();
      } else {
        base.id = cgId;
        base.coingecko_id = cgId;
      }

      // Snapshot/enriched row wins for market data, but identity stays anchored to watchItems.
      return {
        ...base,
        ...row,
        mode,
        symbol: String(row?.symbol || sym).toUpperCase(),
        name: row?.name || base.name,
        contract: mode === "dex" ? String(row?.contract || row?.tokenAddress || contract).toLowerCase() : row?.contract,
        tokenAddress: mode === "dex" ? String(row?.tokenAddress || row?.contract || contract).toLowerCase() : row?.tokenAddress,
        chain: mode === "dex" ? String(row?.chain || base.chain || "pol").trim() : row?.chain,
        id: mode !== "dex" ? String(row?.id || row?.coingecko_id || cgId).toLowerCase() : row?.id,
        coingecko_id: mode !== "dex" ? String(row?.coingecko_id || row?.id || cgId).toLowerCase() : row?.coingecko_id,
      };
    });
  }, [watchItems, watchRows, normalizeWatchItems]);

  const sortedWatchRows = useMemo(() => {
    const rows = Array.isArray(watchDisplayRows) ? [...watchDisplayRows] : [];
    const mode = String(watchSortMode || "manual").toLowerCase();
    if (mode !== "winner" && mode !== "loser") return rows;

    return rows
      .map((row, originalIndex) => ({ row, originalIndex, value: watchSortValue(row) }))
      .sort((a, b) => {
        const av = a.value;
        const bv = b.value;
        if (av == null && bv == null) return a.originalIndex - b.originalIndex;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (mode === "winner") return (bv - av) || (a.originalIndex - b.originalIndex);
        return (av - bv) || (a.originalIndex - b.originalIndex);
      })
      .map((x) => x.row);
  }, [watchDisplayRows, watchSortMode, watchSortValue]);

  const toggleWatchSort = useCallback((mode) => {
    setWatchSortMode((prev) => (String(prev || "manual") === mode ? "manual" : mode));
  }, [setWatchSortMode]);

  useEffect(() => {
    const wa = resolveWalletAddress(wallet);
    if (!wa) return;
    const symbols = Array.from(new Set((sortedWatchRows || []).map((r) => String(r?.symbol || "").toUpperCase()).filter(Boolean)));
    if (!symbols.length) return;
    let cancelled = false;
    Promise.all(symbols.map(async (sym) => {
      try {
        const r = await api(`/api/ratings/coin?symbol=${encodeURIComponent(sym)}&wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, {
          method: "GET",
          token,
          wallet: wa,
        });
        return [sym, r || null];
      } catch {
        return [sym, null];
      }
    })).then((entries) => {
      if (cancelled) return;
      setRatingSummaryBySymbol((prev) => {
        const next = { ...(prev || {}) };
        for (const [sym, status] of entries) {
          if (status?.summary) next[sym] = status.summary;
        }
        return next;
      });
      setUserRatingBySymbol((prev) => {
        const next = { ...(prev || {}) };
        for (const [sym, status] of entries) {
          const own = String(status?.user_rating_today || status?.last_user_rating || "").toUpperCase();
          if (own) next[sym] = own;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [wallet, token, sortedWatchRows]);

  const onchainWatchSymbolsKey = useMemo(() => {
    return Array.from(new Set((sortedWatchRows || []).map((r) => String(r?.symbol || "").toUpperCase()).filter(Boolean)))
      .sort()
      .join(",");
  }, [sortedWatchRows]);

  const marketConditionFetchRef = useRef({ key: "", ts: 0, inflight: false });

  useEffect(() => {
    const symbols = onchainWatchSymbolsKey ? onchainWatchSymbolsKey.split(",").filter(Boolean) : [];
    if (!symbols.length) return;

    const nowMs = Date.now();
    const cacheKey = onchainWatchSymbolsKey;
    const minAgeMs = 60 * 60 * 1000; // 60 minutes: market-condition uses 20d daily data; protect CoinGecko credits
    if (marketConditionFetchRef.current?.key === cacheKey && (nowMs - Number(marketConditionFetchRef.current?.ts || 0)) < minAgeMs) {
      return;
    }
    if (marketConditionFetchRef.current?.inflight && marketConditionFetchRef.current?.key === cacheKey) {
      return;
    }

    let cancelled = false;
    const t = setTimeout(() => {
      marketConditionFetchRef.current = { key: cacheKey, ts: nowMs, inflight: true };
      (async () => {
        const entries = [];
        // Fetch sequentially instead of firing a parallel burst. Backend cache still
        // keeps Strategist data fresh, but this avoids request storms on refresh.
        for (const sym of symbols) {
          try {
            const r = await api(`/api/market-condition?symbol=${encodeURIComponent(sym)}`, { method: "GET", token, wallet });
            entries.push([sym, r || null]);
          } catch {
            entries.push([sym, null]);
          }
          if (cancelled) break;
          await sleep(120);
        }
        return entries;
      })().then((entries) => {
        if (cancelled) return;
        setMarketConditionBySymbol((prev) => {
          const next = { ...(prev || {}) };
          for (const [sym, data] of entries) {
            if (data?.status === "ok" || data?.state) next[sym] = data;
          }
          return next;
        });
        marketConditionFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
      }).catch(() => {
        marketConditionFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [onchainWatchSymbolsKey, token, wallet]);

  const onchainFetchRef = useRef({ key: "", ts: 0, inflight: false });

  useEffect(() => {
    const symbols = onchainWatchSymbolsKey ? onchainWatchSymbolsKey.split(",").filter(Boolean) : [];
    if (!symbols.length) return;

    // Protect Moralis quota:
    // Do not refetch on every price/watchlist render. Fetch only when the visible symbol set
    // changes or when the cache is old. AI Insight still uses the latest cached signals.
    const nowMs = Date.now();
    const cacheKey = `${String(wallet || "").toLowerCase()}|${onchainWatchSymbolsKey}`;
    const minAgeMs = 60 * 60 * 1000; // 60 minutes: market-condition uses 20d daily data; protect CoinGecko credits
    if (onchainFetchRef.current?.key === cacheKey && (nowMs - Number(onchainFetchRef.current?.ts || 0)) < minAgeMs) {
      return;
    }
    if (onchainFetchRef.current?.inflight && onchainFetchRef.current?.key === cacheKey) {
      return;
    }

    let cancelled = false;
    const t = setTimeout(() => {
      onchainFetchRef.current = { key: cacheKey, ts: nowMs, inflight: true };
      api(`/api/onchain/signals?symbols=${encodeURIComponent(symbols.join(","))}`, {
        method: "GET",
        token,
        wallet,
      })
        .then((res) => {
          if (cancelled) return;
          const signals = res?.signals || {};
          setOnchainBySymbol((prev) => ({ ...(prev || {}), ...(signals || {}) }));
          onchainFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
        })
        .catch(() => {
          onchainFetchRef.current = { key: cacheKey, ts: Date.now(), inflight: false };
        });
    }, 800);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [wallet, token, onchainWatchSymbolsKey]);

  const openRatingModal = useCallback(async (row) => {
    const sym = String(row?.symbol || "").toUpperCase();
    if (!sym) return;
    const summary = ratingSummaryBySymbol?.[sym];
    const onchain = onchainBySymbol?.[sym];
    const sysScore = Math.max(0, Math.min(100, watchFinalScore(row, summary) + watchOnchainScoreDelta(onchain)));
    const sysRating = ratingFromScore(sysScore);
    setRatingModal({ open: true, row, symbol: sym, systemRating: sysRating, systemScore: sysScore });
    setRatingStatus(null);
    setRatingErr("");
    const wa = resolveWalletAddress(wallet);
    if (!wa) {
      setRatingErr("Connect wallet first.");
      return;
    }
    try {
      const r = await api(`/api/ratings/coin?symbol=${encodeURIComponent(sym)}&wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, {
        method: "GET",
        token,
        wallet: wa,
      });
      setRatingStatus(r || null);
    } catch (e) {
      setRatingErr(e?.message || "Rating status failed");
    }
  }, [wallet, token, ratingSummaryBySymbol, onchainBySymbol]);

  const closeRatingModal = useCallback(() => {
    setRatingModal({ open: false, row: null, symbol: "", systemRating: "", systemScore: 0 });
    setRatingStatus(null);
    setRatingErr("");
    setRatingBusy(false);
  }, []);

  const submitUserRating = useCallback(async (rating) => {
    const sym = String(ratingModal?.symbol || "").toUpperCase();
    const wa = resolveWalletAddress(wallet);
    if (!sym || !wa) return;
    setRatingBusy(true);
    setRatingErr("");
    try {
      const r = await api(`/api/ratings/vote?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, {
        method: "POST",
        token,
        wallet: wa,
        body: { wallet: wa, wallet_address: wa, symbol: sym, rating },
      });
      setRatingStatus((prev) => ({ ...(prev || {}), ...(r || {}), already_voted_today: true, user_rating_today: rating, last_user_rating: rating }));
      setUserRatingBySymbol((prev) => ({ ...(prev || {}), [sym]: rating }));
      if (r?.summary) setRatingSummaryBySymbol((prev) => ({ ...(prev || {}), [sym]: r.summary }));
    } catch (e) {
      const msg = e?.data?.error || e?.message || "Rating failed";
      setRatingErr(msg === "already rated today" ? "You already rated this coin today." : msg);
      if (e?.data?.user_rating_today) {
        setRatingStatus((prev) => ({ ...(prev || {}), ...(e.data || {}), can_vote: false, already_voted_today: true }));
      }
    } finally {
      setRatingBusy(false);
    }
  }, [ratingModal, wallet, token]);

  const openRatingLink = useCallback(() => {
    const link = String(ratingStatus?.link || "").trim();
    if (!link) return;
    try { window.open(link, "_blank", "noopener,noreferrer"); } catch {}
  }, [ratingStatus]);

  const openRatingExplorer = useCallback(() => {
    const link = String(ratingStatus?.coin_info?.explorer || "").trim();
    if (!link) return;
    try { window.open(link, "_blank", "noopener,noreferrer"); } catch {}
  }, [ratingStatus]);


  // Compare selections are wallet-bound. Hydrate the exact wallet key before any write,
  // otherwise the transient "guest" render during Privy startup can overwrite a saved wallet selection.
  const compareWalletKey = String(resolveWalletAddress(wallet) || wallet || "").toLowerCase();
  const compareStorageKey = useMemo(
    () => compareWalletKey ? `nexus_compare_symbols_v3_wallet_${compareWalletKey}` : "nexus_compare_symbols_v3_guest",
    [compareWalletKey]
  );
  const compareHydratedKeyRef = useRef("");
  const compareSkipNextPersistRef = useRef(false);
  const [compareSet, setCompareSet] = useState(() => [...NEXUS_DEFAULT_COMPARE_SYMBOLS]);

  useEffect(() => {
    let next = [];
    try {
      const raw = localStorage.getItem(compareStorageKey);
      if (raw == null && !compareWalletKey) {
        // Guest view gets the five public defaults checked from the first render.
        next = [...NEXUS_DEFAULT_COMPARE_SYMBOLS];
      } else {
        const arr = JSON.parse(raw || "[]");
        next = Array.isArray(arr)
          ? arr.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20)
          : [];
      }
    } catch {
      if (!compareWalletKey) next = [...NEXUS_DEFAULT_COMPARE_SYMBOLS];
    }

    // Critical deploy/remount guard:
    // React runs this hydration effect and the persistence effect from the same render.
    // Without this guard, the persistence effect still sees the initial [] and overwrites
    // the saved wallet selection before setCompareSet(next) is rendered.
    compareHydratedKeyRef.current = compareStorageKey;
    compareSkipNextPersistRef.current = true;
    setCompareSet(next); // empty is authoritative for this exact wallet key
  }, [compareStorageKey, compareWalletKey]);

  useEffect(() => {
    if (compareHydratedKeyRef.current !== compareStorageKey) return;
    if (compareSkipNextPersistRef.current) {
      compareSkipNextPersistRef.current = false;
      return;
    }
    try {
      localStorage.setItem(
        compareStorageKey,
        JSON.stringify((compareSet || []).map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20))
      );
    } catch {}
  }, [compareSet, compareStorageKey]);
  const compareSymbols = useMemo(() => {
    const uniq = [];
    for (const s of compareSet || []) {
      const sym = String(s || "").toUpperCase().trim();
      if (sym && !uniq.includes(sym)) uniq.push(sym);
    }
    return uniq.slice(0, 20);
  }, [compareSet]);

  // -------- Fixed color slots (10), stable per coin --------
  // Rule: coin keeps its color while selected; when removed, its slot becomes free for the next new coin.
  const colorSlotsRef = useRef(new Map()); // sym -> slot index (0..9)
  const freeSlotsRef = useRef(new Set(PALETTE20.map((_, i) => i)));

  const ensureColorSlot = (sym) => {
    const S = String(sym || "").toUpperCase();
    if (!S) return 0;
    const m = colorSlotsRef.current;
    if (m.has(S)) return m.get(S);
    const free = freeSlotsRef.current;
    const next = free.values().next().value;
    const idx = next !== undefined ? next : (m.size % PALETTE20.length);
    free.delete(idx);
    m.set(S, idx);
    return idx;
  };

  const releaseColorSlot = (sym) => {
    const S = String(sym || "").toUpperCase();
    if (!S) return;
    const m = colorSlotsRef.current;
    const idx = m.get(S);
    if (idx === undefined) return;
    m.delete(S);
    freeSlotsRef.current.add(idx);
  };

  // Keep mapping in sync with current compareSymbols.
  useEffect(() => {
    const current = new Set(compareSymbols);
    for (const sym of Array.from(colorSlotsRef.current.keys())) {
      if (!current.has(sym)) releaseColorSlot(sym);
    }
    compareSymbols.forEach((sym) => ensureColorSlot(sym));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareSymbols.join("|")]);

  const colorForSym = (sym) => PALETTE20[ensureColorSlot(sym) % PALETTE20.length];
  const lineClassForSym = (sym) => `line${(ensureColorSlot(sym) % PALETTE20.length) + 1}`;

  // compare/chart
  const [timeframe, setTimeframe] = useState("90D");
  const compareFetchRange = useMemo(() => _compareFetchRange(timeframe), [timeframe]);
  const PAIR_EXPLAIN_TF = "30D";

  // ------------------------
  // 2Y (from CoinGecko)
  // ------------------------
  // 2Y is available from the backend (CoinGecko). Always allow selecting 2Y.
  // We may still load data in the background, but we don't block selection.
  // timeframe (e.g. 90D) until 2Y is ready.
  const MIN_2Y_POINTS = 0; // 0 = no gating
  const [pendingTf, setPendingTf] = useState(null); // e.g. '2Y' while loading
  const pendingTfRef = useRef(null);
  useEffect(() => { pendingTfRef.current = pendingTf; }, [pendingTf]);

  const has2YData = useMemo(() => {
    if (!compareSymbols.length) return false;
    const cached2y = _cmpGetCached(compareSymbols, "2Y");
    if (!cached2y) return false;
    // Require all selected symbols to have enough 2Y points.
    return compareSymbols.every((s) => {
      const S = String(s || "").toUpperCase();
      const arr = cached2y?.[S];
      return Array.isArray(arr) && arr.length >= MIN_2Y_POINTS;
    });
  }, [compareSymbols.join("|"), timeframe]);

  // Safety: if localStorage remembers timeframe=2Y but we don't have data yet,
  // fall back to 90D (prevents blank/confusing chart states).
  useEffect(() => {
    if (String(timeframe || "").toUpperCase() === "2Y" && !has2YData) {
      setTimeframe("90D");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, has2YData]);

  // Access (NFT / Code) - UI only (stored locally). Later we can wire this to backend verification.
  // Access (NFT / Code) — backend driven (status + redeem)
  const [access, setAccess] = useState(null); // { active, until, source, tier, note }
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessTab, setAccessTab] = useState("redeem"); // 'redeem' | 'subscribe'
  const [billingMenuOpen, setBillingMenuOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportCategory, setSupportCategory] = useState("General");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportBusy, setSupportBusy] = useState(false);
  const [supportMsg, setSupportMsg] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [nexusBackendState, setNexusBackendState] = useState(null);
  const [shadowExecutorState, setShadowExecutorState] = useState(null);
  const [shadowExecutorBusy, setShadowExecutorBusy] = useState(false);
  const [shadowExecutorMsg, setShadowExecutorMsg] = useState("");
  const [shadowDrawerOpen, setShadowDrawerOpen] = useState(true);

  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");

  // Subscribe (USDC/USDT on ETH)
  // Core plan: $25 / 30 days
  const SUB_PRICE_USD = 25;
  const SUB_PLAN = "pro";
  const STRATEGIST_WEEKLY_PRICE_USD = 20;
  const STRATEGIST_MONTHLY_PRICE_USD = 50;
  const [subPlan, setSubPlan] = useState("core"); // core | strategist_weekly | strategist_monthly
  const [subChain, setSubChain] = useState("ETH"); // ETH | BNB | POL
  const [subToken, setSubToken] = useState("USDT"); // Core/monthly: USDC|USDT; weekly: NKR

  const selectedSubPlan = subPlan === "strategist_weekly" || subPlan === "strategist_monthly" ? subPlan : SUB_PLAN;
  const selectedSubPriceUsd =
    subPlan === "strategist_weekly" ? STRATEGIST_WEEKLY_PRICE_USD :
    subPlan === "strategist_monthly" ? STRATEGIST_MONTHLY_PRICE_USD :
    SUB_PRICE_USD;
  const selectedSubLabel =
    subPlan === "strategist_weekly" ? "Strategist Weekly" :
    subPlan === "strategist_monthly" ? "Strategist Monthly" :
    "Nexus Core";
  const selectedSubPaymentAssets = ["USDT", "USDC"];
  const [subBusy, setSubBusy] = useState(false);
  const [subMsg, setSubMsg] = useState("");
  const [nkrPresale, setNkrPresale] = useState(null);
  const [presaleBusy, setPresaleBusy] = useState(false);
  const [presaleMsg, setPresaleMsg] = useState("");
  const [presalePanelOpen, setPresalePanelOpen] = useState(false);
  const [nkrSpot, setNkrSpot] = useState(null);
  const [nkrTokenInfoOpen, setNkrTokenInfoOpen] = useState(false);
  const [accessHelpOpen, setAccessHelpOpen] = useState(false);
  const [presaleUsd, setPresaleUsd] = useState(20);
  const [presaleQuote, setPresaleQuote] = useState(null);
  const [billingPackBusy, setBillingPackBusy] = useState(false);
  const [billingPackMsg, setBillingPackMsg] = useState("");


  const [autoRenewBusy, setAutoRenewBusy] = useState(false);
  const refreshNkrPresale = useCallback(async (force = false) => {
    try {
      const q = force ? "?force=1" : "";
      const r = await api(`/api/nkr/presale/status${q}`, { method: "GET", token });
      const p = r?.presale || r || null;
      setNkrPresale(p);
      return p;
    } catch (e) {
      setNkrPresale(null);
      return null;
    }
  }, [api, token]);

  // Close Buy NKR panel on outside click or Escape
  useEffect(() => {
    if (!presalePanelOpen) return undefined;
    const onDown = (e) => {
      try {
        const root = document.getElementById("nkr-presale-panel-root");
        if (root && root.contains(e.target)) return;
        const btn = document.getElementById("nkr-presale-buy-btn");
        if (btn && btn.contains(e.target)) return;
        setPresalePanelOpen(false);
      } catch (_) {
        setPresalePanelOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPresalePanelOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [presalePanelOpen]);

  // Always load presale status so header "Buy NKR" is visible when active
  useEffect(() => {
    refreshNkrPresale(false);
    const id = setInterval(() => {
      try { refreshNkrPresale(false); } catch (_) {}
    }, 60000);
    return () => clearInterval(id);
  }, [wallet, refreshNkrPresale]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(
          "https://api.dexscreener.com/latest/dex/pairs/ethereum/0x099d9a4626137572fbc0fa49cd73d78c554558f5",
          { cache: "no-store" }
        );
        const j = await r.json();
        const pair = j?.pair || (Array.isArray(j?.pairs) ? j.pairs[0] : null);
        if (!alive || !pair) return;
        const px = Number(pair.priceUsd || pair.priceNative || 0);
        const ch = Number(pair?.priceChange?.h24 ?? pair?.priceChange?.h6 ?? pair?.priceChange?.h1 ?? 0);
        setNkrSpot({
          price: Number.isFinite(px) ? px : 0,
          change24h: Number.isFinite(ch) ? ch : 0,
          liq: Number(pair?.liquidity?.usd || 0),
          vol: Number(pair?.volume?.h24 || 0),
          mcap: Number(pair?.fdv || pair?.marketCap || 0),
        });
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const [autoRenewMsg, setAutoRenewMsg] = useState("");

  const accessWalletKey = String(resolveWalletAddress(wallet) || wallet || "").toLowerCase();
  const accessCacheKey = accessWalletKey ? `nexus_access_status_v2_${accessWalletKey}` : "";
  const accessRefreshBusyRef = useRef(false);
  const accessRefreshQueuedRef = useRef(false);
  const accessRequestSeqRef = useRef(0);
  const accessRef = useRef(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessLastError, setAccessLastError] = useState("");

  // Restore the last verified access result immediately while the live request is loading.
  useEffect(() => {
    if (!accessCacheKey) {
      setAccess(null);
      return;
    }
    try {
      const cached = JSON.parse(localStorage.getItem(accessCacheKey) || "null");
      if (cached && typeof cached === "object") {
        setAccess(cached);
      } else {
        setAccess(null);
      }
    } catch {
      setAccess(null);
    }
  }, [accessCacheKey]);

  const refreshAccess = useCallback(async ({ retries = 2, force = false } = {}) => {
    const addr = String(resolveWalletAddress(wallet) || wallet || "").trim();
    if (!addr) return false;

    if (accessRefreshBusyRef.current && !force) {
      // Never silently discard a refresh request. Run it immediately after the current request.
      accessRefreshQueuedRef.current = true;
      return false;
    }

    const requestSeq = ++accessRequestSeqRef.current;
    accessRefreshBusyRef.current = true;
    accessRefreshQueuedRef.current = false;
    if (force || !accessRef.current) setAccessLoading(true);
    setAccessLastError("");

    try {
      let lastErr = null;
      for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
          const res = await api(`/api/access/status?addr=${encodeURIComponent(addr)}&_ts=${Date.now()}`, { wallet: addr });
          // Ignore a late answer belonging to an older wallet/request.
          if (requestSeq !== accessRequestSeqRef.current) return false;
          const active = (res && res.active !== undefined) ? !!res.active : !!(res && res.plan && res.plan !== "free");
          const normalized = res ? { ...res, active, verified_at: Date.now() } : { active: false, verified_at: Date.now() };
          setAccess(normalized);
          setAccessLastError("");
          try { localStorage.setItem(`nexus_access_status_v2_${addr.toLowerCase()}`, JSON.stringify(normalized)); } catch {}
          return true;
        } catch (e) {
          lastErr = e;
          if (attempt < retries) {
            // Fast startup retry sequence: 250 ms, 600 ms, 1.2 s...
            const retryDelay = attempt === 0 ? 250 : attempt === 1 ? 600 : 1200;
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }
      console.warn("access/status failed after retries", lastErr);
      setAccessLastError(String(lastErr?.message || "Access status temporarily unavailable."));
      return false; // keep last verified/cached status; never flicker to OFF because of a timeout
    } finally {
      if (requestSeq === accessRequestSeqRef.current) {
        accessRefreshBusyRef.current = false;
        setAccessLoading(false);
        if (accessRefreshQueuedRef.current) {
          accessRefreshQueuedRef.current = false;
          setTimeout(() => refreshAccess({ retries: 1 }), 0);
        }
      }
    }
  }, [wallet, api]);

  useEffect(() => {
    if (!accessWalletKey) return;

    // Immediate request plus a short startup safety burst. This covers Privy/token hydration
    // and sleeping backend instances without requiring manual page refreshes.
    refreshAccess({ retries: 2 });
    const burst1 = window.setTimeout(() => refreshAccess({ retries: 1 }), 1200);
    const burst2 = window.setTimeout(() => refreshAccess({ retries: 1 }), 3500);
    const burst3 = window.setTimeout(() => refreshAccess({ retries: 1 }), 8000);

    const onFocus = () => refreshAccess({ retries: 1 });
    const onOnline = () => refreshAccess({ retries: 2 });
    const onVisible = () => { if (document.visibilityState === "visible") refreshAccess({ retries: 1 }); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => refreshAccess({ retries: 1 }), 45000);
    return () => {
      window.clearTimeout(burst1);
      window.clearTimeout(burst2);
      window.clearTimeout(burst3);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [accessWalletKey, refreshAccess]);

  // Pro access: subscription or redeem code
  accessRef.current = access;
  const isPro = !!(access?.active);
  const strategistActive = !!(access?.strategist_active || access?.strategist_access?.active);
  const canUseStrategist = !!(access?.can_use_strategist || strategistActive || access?.is_demo || access?.mode === "DEMO" || access?.mode === "EXPIRED" || (access && !access?.active && !access?.is_live));
  const demoAiUsedToday = Number(access?.ai_used_today ?? 0);
  const demoAiDailyLimit = access?.ai_daily_limit ?? 3;
  const demoAiMonthDaysUsed = Number(access?.ai_month_days_used ?? 0);
  const demoAiMonthDaysLimit = access?.ai_month_days_limit ?? 5;
  const accessExpiresTs = Number(access?.expires_at || 0);
  const accessDaysLeft = accessExpiresTs > 0
    ? Math.ceil((accessExpiresTs - Math.floor(Date.now() / 1000)) / 86400)
    : null;
  const showAccessReminder = !!(isPro && accessDaysLeft !== null && accessDaysLeft <= 3);
  const showAccessExpiredNotice = !!(!isPro && accessExpiresTs > 0);

  const hasCoreAccess = !!(
    access?.core_active ||
    access?.core_access?.active ||
    access?.full_access ||
    (access?.active && !String(access?.plan || access?.tier || "").toLowerCase().includes("strategist"))
  );
  const shadowVisibilityKey = wallet ? `nexus_shadow_visible_v2_${String(wallet).toLowerCase()}` : "";
  // Demo users may see Shadow immediately. Once Core access/subscription is active,
  // Shadow starts OFF and is shown only after the user explicitly enables it.
  const [shadowUiEnabled, setShadowUiEnabled] = useState(false);

  useEffect(() => {
    if (!hasCoreAccess) {
      setShadowUiEnabled(true);
      setShadowDrawerOpen(true);
      return;
    }
    try {
      const saved = shadowVisibilityKey ? localStorage.getItem(shadowVisibilityKey) : null;
      const enabled = saved == null ? false : saved === "true";
      setShadowUiEnabled(enabled);
      setShadowDrawerOpen(enabled);
    } catch {
      setShadowUiEnabled(false);
      setShadowDrawerOpen(false);
    }
  }, [hasCoreAccess, shadowVisibilityKey]);

  const setShadowVisibilityForWallet = useCallback((enabled) => {
    const next = !!enabled;
    setShadowUiEnabled(next);
    setShadowDrawerOpen(next);
    try { if (shadowVisibilityKey) localStorage.setItem(shadowVisibilityKey, String(next)); } catch {}
  }, [shadowVisibilityKey]);

  const requirePro = useCallback((actionLabel = "This action") => {
    if (isPro) return true;
    // Open Access modal directly on Subscribe tab with a friendly message
    setAccessTab("subscribe");
    setAccessModalOpen(true);
    setSubMsg(`🔒 ${actionLabel} requires an active Nexus Core subscription ($25/30 days).`);
    return false;
  }, [isPro]);

  const requireStrategistAccess = useCallback((actionLabel = "Nexus Strategist") => {
    // BUILD464: Demo / no-Core wallets may use free Strategist quota (backend 3/7d).
    // canUseStrategist is true for is_demo; if access not loaded yet, allow attempt (backend decides).
    if (canUseStrategist || access?.is_demo || access?.mode === "DEMO" || access?.mode === "EXPIRED" || (!access?.active && access != null)) {
      return true;
    }
    if (access == null && wallet) {
      // Access still loading — do not hard-block free trial
      return true;
    }
    setAccessTab("subscribe");
    setSubPlan("strategist_monthly");
    setAccessModalOpen(true);
    setSubMsg(`🔒 ${actionLabel}: free trial is 3 uses / 7 days. Then Weekly $20/7d or Monthly $50/30d.`);
    return false;
  }, [canUseStrategist, access, wallet]);

  const submitSupportTicket = useCallback(async () => {
    const msg = String(supportMessage || "").trim();
    const email = String(supportEmail || "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOk) {
      setSupportMsg("Please enter a valid email address.");
      return;
    }
    if (msg.length < 10) {
      setSupportMsg("Please describe the issue with at least 10 characters.");
      return;
    }
    setSupportBusy(true);
    setSupportMsg("");
    try {
      const res = await api("/api/support/ticket", {
        method: "POST",
        token,
        wallet,
        body: {
          wallet,
          wallet_address: wallet,
          email,
          category: supportCategory,
          subject: supportSubject || supportCategory,
          message: msg,
          meta: {
            app_version: APP_VERSION,
            access_mode: access?.mode || "DEMO",
            core_active: !!isPro,
            strategist_active: !!strategistActive,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          },
        },
      });
      setSupportMsg(`Support ticket created: ${res?.ticket_id || "received"}`);
      setSupportSubject("");
      setSupportMessage("");
      setSupportEmail("");
    } catch (e) {
      setSupportMsg(e?.message || "Support ticket failed.");
    } finally {
      setSupportBusy(false);
    }
  }, [supportMessage, supportEmail, supportCategory, supportSubject, token, wallet, api, access?.mode, isPro, strategistActive]);

  const refreshNexusBackendState = useCallback(async () => {
    if (!wallet) {
      setNexusBackendState(null);
      setShadowExecutorState(null);
      return null;
    }
    try {
      const data = await api(`/api/nexus/trading/state`, { wallet });
      setNexusBackendState(data || null);
      if (data?.shadow_executor) setShadowExecutorState(data.shadow_executor);
      return data || null;
    } catch {
      return null;
    }
  }, [api, wallet]);

  const refreshShadowExecutorState = useCallback(async () => {
    if (!wallet) {
      setShadowExecutorState(null);
      return null;
    }
    try {
      const data = await api(`/api/nexus/shadow/executor`, { wallet });
      setShadowExecutorState(data || null);
      return data || null;
    } catch {
      return null;
    }
  }, [api, wallet]);

  useEffect(() => {
    refreshNexusBackendState();
    refreshShadowExecutorState();
  }, [refreshNexusBackendState, refreshShadowExecutorState]);


  const redeemNow = useCallback(async () => {
    const code = (redeemCode || "").trim();
    if (!code) {
      setRedeemMsg("Bitte Code eingeben.");
      return;
    }
    if (!wallet) {
      setRedeemMsg("Wallet nicht verbunden.");
      return;
    }
    setRedeemBusy(true);
    setRedeemMsg("");
    try {
      const res = await api("/api/access/redeem", {
        method: "POST",
        body: { addr: wallet, code },
      });
      if (res?.ok === false) throw new Error(res?.error || "Redeem failed");
      setRedeemMsg(res?.message || "Aktiviert.");
      setRedeemCode("");
      setAccessModalOpen(false);
      await refreshAccess();
    } catch (e) {
      setRedeemMsg(e?.message || "Fehler beim Aktivieren.");
    } finally {
      setRedeemBusy(false);
    }
  }, [redeemCode, wallet, api, refreshAccess]);

  // Alias for UI handler (legacy name)
  const redeemAccess = redeemNow;

  // Auto-Renew settings (safe mode: stores preference only; payment trigger comes later via Privy)
  const fmtAccessDate = useCallback((ts) => {
    const n = Number(ts);
    if (!Number.isFinite(n) || n <= 0) return "—";
    try {
      return new Date(n * 1000).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch {
      return "—";
    }
  }, []);

  const getPrimaryPrivyWallet = useCallback(() => {
    return (
      privyWallets?.find((w) =>
        ["privy", "embedded"].includes(String(w?.walletClientType || "").toLowerCase()) ||
        String(w?.connectorType || "").toLowerCase() === "embedded"
      ) ||
      privyWallets?.[0] ||
      null
    );
  }, [privyWallets]);

  const ensureAutoRenewConsent = useCallback(async () => {
    // IMPORTANT:
    // Privy useDelegatedActions / delegateWallet is NOT supported in this normal web/browser flow.
    // Calling it here causes the UI to hang at "..." with:
    //   "useDelegatedActions is only supported for on-device execution"
    //
    // For the live website we therefore store Auto Renew as a backend preference only.
    // The real recurring charge must be executed later by the server-side Privy worker.
    if (!wallet) throw new Error("Wallet not connected.");
    if (!["USDC", "USDT"].includes(String(subToken || "").toUpperCase())) {
      throw new Error("Auto Renew supports USDC or USDT only.");
    }

    const res = await api("/api/access/auto-renew/set", {
      method: "POST",
      body: {
        wallet,
        enabled: true,
        token: subToken,
        chain: subChain,
        mode: "web_preference_only",
      },
    });

    if (res?.status === "error" || res?.ok === false) {
      throw new Error(res?.error || "Auto Renew update failed.");
    }

    return res;
  }, [wallet, subToken, subChain, api]);

  const setAutoRenewPreference = useCallback(async (enabled) => {
    if (!wallet) {
      setAutoRenewMsg("Wallet not connected.");
      return;
    }
    if (!["USDC", "USDT"].includes(String(subToken || "").toUpperCase())) {
      setAutoRenewMsg("Auto Renew supports USDC or USDT only.");
      return;
    }

    setAutoRenewBusy(true);
    setAutoRenewMsg("");
    try {
      const res = await api("/api/access/auto-renew/set", {
        method: "POST",
        body: {
          wallet,
          enabled: !!enabled,
          token: subToken,
          chain: subChain,
          mode: "web_preference_only",
        },
      });

      if (res?.status === "error" || res?.ok === false) {
        throw new Error(res?.error || "Auto Renew update failed.");
      }

      setAutoRenewMsg(
        enabled
          ? "Auto Renew ON. Web mode saved. Server-side Privy worker is required for real recurring payment."
          : "Auto Renew disabled."
      );

      await refreshAccess();
    } catch (e) {
      setAutoRenewMsg(e?.message || "Auto Renew update failed.");
    } finally {
      setAutoRenewBusy(false);
    }
  }, [wallet, subToken, subChain, api, refreshAccess]);

  // NFTs disabled in Phase 1 (UI + backend)

  const subscribePay = useCallback(async () => {
    if (!wallet) {
      setSubMsg("Wallet not connected.");
      return;
    }

    setSubBusy(true);
    setSubMsg("");
    try {
      const chainKey = normalizeWalletChainKey(subChain || "POL");
      const chainId = CHAIN_ID_BY_KEY[chainKey];
      if (!chainId) throw new Error("Unsupported chain.");

      const payToken = String(subToken || "").toUpperCase();
      const cfg = await fetchSubscribeConfig();
      const allowed = Array.isArray(cfg?.plans?.[subPlan]?.payment_assets) ? cfg.plans[subPlan].payment_assets : selectedSubPaymentAssets;
      if (!allowed.includes(payToken)) throw new Error(`${selectedSubLabel} must be paid with ${allowed.join(" or ")}.`);
      const recipient = payToken === "NKR" ? String(cfg?.nkr?.recipient || "").trim() : String(cfg?.treasury || "").trim();
      if (!/^0x[a-fA-F0-9]{40}$/.test(recipient)) throw new Error(`${payToken} payment recipient is not configured.`);

      // Use the Privy embedded wallet provider for payment.
      // Do NOT use window.ethereum here: with Privy embedded wallets it can throw
      // "The requested account and/or method has not been authorized by the user."
      const provider = await _getEmbeddedProvider();
      await _trySwitchChain(provider, chainId);

      // Hard safety check: payment must be sent on the selected subscription chain.
      const currentHex = await provider.request({ method: "eth_chainId" });
      const wantHex = "0x" + Number(chainId).toString(16);
      if (String(currentHex).toLowerCase() !== String(wantHex).toLowerCase()) {
        throw new Error(`Wrong network. Please switch your Privy wallet to ${chainKey}.`);
      }

      // Pay with stablecoin (USDC/USDT) on the selected chain.
      // Token address + decimals come from backend config first, then local safe fallback.
      let spec;
      let paymentAmount;
      if (payToken === "NKR") {
        const nkr = cfg?.nkr || {};
        if (Number(nkr.chain_id || 0) !== Number(chainId)) throw new Error(`NKR payment is available only on ${nkr.chain || "the configured network"}.`);
        if (!nkr.configured) throw new Error("NKR payment is not configured yet.");
        spec = { address: nkr.address, decimals: Number(nkr.decimals || 18) };
        paymentAmount = Number(selectedSubPriceUsd) / Number(nkr.price_usd || 0);
      } else {
        spec = getSubscribeTokenSpec(cfg, chainKey, payToken);
        paymentAmount = Number(selectedSubPriceUsd ?? cfg?.price_usd ?? SUB_PRICE_USD ?? 25);
      }
      if (!/^0x[a-fA-F0-9]{40}$/.test(String(spec.address || ""))) throw new Error(`${payToken} address is not configured for ${chainKey}.`);
      if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) throw new Error("Payment quote is unavailable.");
      const amountUnits = decimalStringToUnits(String(paymentAmount), spec.decimals || 6);
      const data = _erc20TransferData(recipient, amountUnits);

      const txHash = await provider.request({
        method: "eth_sendTransaction",
        params: [
          {
            from: wallet,
            to: spec.address,
            data,
            value: "0x0",
          },
        ],
      });

      saveWalletTransaction({
        type: "SUBSCRIPTION PAYMENT",
        chain: chainKey,
        asset: payToken,
        amount: String(paymentAmount),
        from: wallet,
        to: recipient,
        txHash,
        status: "SUBMITTED",
      });
      _trackWalletTxInBackground(provider, txHash, {
        type: "SUBSCRIPTION PAYMENT",
        chain: chainKey,
        asset: payToken,
        amount: String(paymentAmount),
        from: wallet,
        to: recipient,
      });

      const res = await api("/api/access/subscribe/verify", {
        method: "POST",
        token,
        wallet,
        body: { wallet, wallet_address: wallet, chain_id: chainId, tx_hash: txHash, plan: selectedSubPlan, token_type: "erc20", token: payToken, email: billingEmail },
      });

      setSubMsg(res?.already_verified ? "Payment already verified. Access updated." : `${selectedSubLabel} payment verified. Access activated.`);

      setAccessModalOpen(false);
      await refreshAccess();
    } catch (e) {
      setSubMsg(e?.message || "Payment failed.");
      console.error("Payment failed:", e);
    } finally {
      setSubBusy(false);
    }
  }, [wallet, subChain, subToken, subPlan, selectedSubPlan, selectedSubPriceUsd, selectedSubLabel, selectedSubPaymentAssets, billingEmail, token, api, refreshAccess, _getEmbeddedProvider, _trySwitchChain]);


  const encodeBuyUsdData = useCallback((usdE18) => {
    // buyUsd(uint256) selector 0x8cd6d717 + uint256 arg (approx — use eth helper if available)
    // selector = first 4 bytes of keccak256("buyUsd(uint256)")
    const sel = "8cd6d717";
    const hex = BigInt(usdE18).toString(16).padStart(64, "0");
    return "0x" + sel + hex;
  }, []);

  const encodeBuyBillingPackData = useCallback(() => {
    // buyBillingPack() — selector keccak256("buyBillingPack()")[:4]
    return "0x7c3a0001"; // placeholder replaced below after computing
  }, []);

  // Real selectors computed once (inline known values from Solidity)
  // buyUsd(uint256) = 0x98d5fdca is WRONG; we compute via text:
  // Using fixed known: will set properly after keccak in runtime if ethers available.
  const PRESALE_V3 = "0xA9307CE69E4BcC6E2e8b98eFB4a132C2496Dbc73";

  const buyNkrPresaleUsd = useCallback(async (usdAmount) => {
    if (!wallet) {
      setPresaleMsg("Wallet not connected.");
      return;
    }
    const usd = Math.max(5, Math.min(50000, Number(usdAmount) || 20));
    setPresaleBusy(true);
    setPresaleMsg("");
    try {
      const st = await refreshNkrPresale(true);
      if (!st?.presale_active) {
        const err = String(st?.error || "");
        throw new Error(err ? `Presale status: ${err.slice(0, 120)}` : "Presale is not active (API). Check RPC_URL_1 + NEXUS_NKR_PRESALE_ADDRESS.");
      }
      const to = String(st.presale_address || PRESALE_V3).trim();
      const qr = await api(`/api/nkr/presale/quote?usd=${usd}`, { method: "GET", token });
      const q = qr?.quote;
      if (!q?.eth_required_wei) throw new Error("Quote failed.");
      let valueWei = BigInt(String(q.eth_required_wei));
      valueWei = valueWei + valueWei / 50n; // +2% buffer
      const valueHex = "0x" + valueWei.toString(16);
      const usdE18 = BigInt(Math.round(usd * 1e6)) * 10n ** 12n; // usd * 1e18
      // buyUsd(uint256) selector
      // buyUsd(uint256) selector 0x97fcb94d
      const data = "0x97fcb94d" + usdE18.toString(16).padStart(64, "0");
      const provider = await _getEmbeddedProvider();
      if (!provider?.request) throw new Error("Wallet provider unavailable.");
      try { await _trySwitchChain(provider, 1); } catch (_) {}
      const from = resolveWalletAddress(wallet) || wallet;
      const hash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from, to, value: valueHex, data }],
      });
      setPresaleMsg(`Buy submitted ${String(hash).slice(0, 12)}… → ~${Number(q.nkr_total || 0).toLocaleString()} NKR (no bonus).`);
      setTimeout(() => { try { refreshNkrPresale(true); } catch (_) {} }, 8000);
    } catch (e) {
      setPresaleMsg(String(e?.message || e || "Buy failed"));
    } finally {
      setPresaleBusy(false);
    }
  }, [wallet, refreshNkrPresale, api, token, _getEmbeddedProvider, _trySwitchChain]);

  const buyNkrBillingPack = useCallback(async () => {
    if (!wallet) {
      setBillingPackMsg("Wallet not connected.");
      return;
    }
    setBillingPackBusy(true);
    setBillingPackMsg("");
    try {
      const st = await refreshNkrPresale(true);
      if (!st?.presale_active) throw new Error("Presale is not active.");
      const to = String(st.presale_address || PRESALE_V3).trim();
      const bp = st.billing_pack;
      if (!bp?.eth_required_wei) throw new Error("Billing pack quote missing.");
      let valueWei = BigInt(String(bp.eth_required_wei));
      valueWei = valueWei + valueWei / 50n;
      const valueHex = "0x" + valueWei.toString(16);
      // buyBillingPack() selector 0x05944739
      const data = "0x05944739";
      const provider = await _getEmbeddedProvider();
      if (!provider?.request) throw new Error("Wallet provider unavailable.");
      try { await _trySwitchChain(provider, 1); } catch (_) {}
      const from = resolveWalletAddress(wallet) || wallet;
      const hash = await provider.request({
        method: "eth_sendTransaction",
        params: [{ from, to, value: valueHex, data }],
      });
      setBillingPackMsg(`Billing pack ${String(hash).slice(0, 12)}… → ${Number(bp.nkr_total || 44000).toLocaleString()} NKR (+10%).`);
      setTimeout(() => { try { refreshNkrPresale(true); } catch (_) {} }, 8000);
    } catch (e) {
      setBillingPackMsg(String(e?.message || e || "Billing pack failed"));
    } finally {
      setBillingPackBusy(false);
    }
  }, [wallet, refreshNkrPresale, _getEmbeddedProvider, _trySwitchChain]);

  // legacy alias
  const buyNkrPresalePackage = buyNkrBillingPack;




  // Best-pair explain (click -> modal)
  const [selectedPair, setSelectedPair] = useState(null); // e.g. { pair:"BTC/ETH", score, corr }
  const [aiExplainLoading, setAiExplainLoading] = useState(false);
  const [aiExplainText, setAiExplainText] = useState("");
  const [aiExplainData, setAiExplainData] = useState(null);
  const [pairExplainSeries, setPairExplainSeries] = useState({}); // {SYM: [values...]}
  const [pairExplainLoading, setPairExplainLoading] = useState(false);

  function _pairSyms(p) {
    const s = (p || "").split("/");
    return [String(s[0] || "").trim().toUpperCase(), String(s[1] || "").trim().toUpperCase()];
  }

  function _retPctFromChart(sym) {
    try {
      const lines = chartRaw?.lines || {};
      const norm = normalizeToIndex(lines);
      const arr = norm[sym] || [];
      if (!arr.length) return null;
      const first = Number(arr[0]);
      const last = Number(arr[arr.length - 1]);
      if (!Number.isFinite(first) || !Number.isFinite(last) || !first) return null;
      return ((last - first) / first) * 100.0;
    } catch {
      return null;
    }
  }

  function _pctColorStyle(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return {};
  return { color: n >= 0 ? "#39d98a" : "#ff5c5c" };
}

function _fmtPctLocal(x) {
    if (x === null || x === undefined || !Number.isFinite(Number(x))) return "—";
    const n = Number(x);
    const sign = n > 0 ? "+" : "";
    return sign + n.toFixed(2) + "%";
  }

  function _toMs(ts) {
    // accept seconds or ms
    const n = Number(ts);
    if (!Number.isFinite(n)) return null;
    return n < 1e11 ? n * 1000 : n;
  }

  function _fmtDay(ts) {
    const ms = _toMs(ts);
    if (!ms) return "—";
    const d = new Date(ms);
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getUTCFullYear()}`;
  }

  function _fmtDayFromIndex(i, total) {
    const now = new Date();
    const baseUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()); // start of today (UTC)
    const daysAgo = (Math.max(0, (total - 1) - i));
    return _fmtDay(baseUtcMs - (daysAgo * 86400000));
  }

  function _dailyPctSeries(arr) {
    // arr: [{t, v}, ...] or [number,...]
    if (!Array.isArray(arr) || arr.length < 2) return [];
    const out = [];
    for (let i = 1; i < arr.length; i++) {
      const p0 = arr[i - 1];
      const p1 = arr[i];
      const v0 = (p0 && typeof p0 === "object") ? Number(p0.v) : Number(p0);
      const v1 = (p1 && typeof p1 === "object") ? Number(p1.v) : Number(p1);
      const t1 = (p1 && typeof p1 === "object") ? p1.t : i;
      if (!Number.isFinite(v0) || !Number.isFinite(v1) || !v0) continue;
      out.push({ t: t1, pct: ((v1 - v0) / v0) * 100.0 });
    }
    return out;
  }

  function _buildDailyRows(seriesMap, a, b, maxDays) {
    const aArr = seriesMap?.[a] || [];
    const bArr = seriesMap?.[b] || [];
    const aD = _dailyPctSeries(aArr);
    const bD = _dailyPctSeries(bArr);
    const n = Math.min(aD.length, bD.length);
    if (!n) return [];
    const rows = [];
    for (let i = 0; i < n; i++) {
      const ap = aD[i]?.pct;
      const bp = bD[i]?.pct;
      const t = aD[i]?.t ?? bD[i]?.t;
      rows.push({
        t,
        a: Number.isFinite(ap) ? ap : null,
        b: Number.isFinite(bp) ? bp : null,
        d: Number.isFinite(ap) && Number.isFinite(bp) ? (ap - bp) : null,
      });
    }
    // keep last maxDays rows
    const keep = Math.max(1, Number(maxDays) || 30);
    const kept = rows.slice(Math.max(0, rows.length - keep));

    // Force dates to be "today back N days" in UTC to avoid weird/old timestamps and timezone confusion.
    // This assumes the daily series is ordered oldest -> newest (which our backend series typically is).
    const now = new Date();
    const baseUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()); // start of today (UTC)
    const k = kept.length;
    for (let i = 0; i < k; i++) {
      const daysAgo = (k - 1 - i);
      kept[i].t = baseUtcMs - (daysAgo * 86400000);
    }
    return kept;
  }

  function _calcSimpleRsi(arr, period = 14) {
    const pts = Array.isArray(arr) ? arr : [];
    if (pts.length < period + 1) return null;
    let gain = 0;
    let loss = 0;
    for (let i = pts.length - period; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const v0 = (prev && typeof prev === "object") ? Number(prev.v) : Number(prev);
      const v1 = (curr && typeof curr === "object") ? Number(curr.v) : Number(curr);
      if (!Number.isFinite(v0) || !Number.isFinite(v1)) continue;
      const diff = v1 - v0;
      if (diff > 0) gain += diff;
      else loss += Math.abs(diff);
    }
    const avgGain = gain / period;
    const avgLoss = loss / period;
    if (!Number.isFinite(avgGain) || !Number.isFinite(avgLoss)) return null;
    if (avgLoss === 0) return 100;
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  }

  function _rsiBadgeState(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return { label: "n/a", tone: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.10)", color: "rgba(232,242,240,0.92)" };
    if (n > 70) return { label: "overbought", tone: "rgba(255,92,92,0.16)", border: "rgba(255,92,92,0.32)", color: "#ff8d8d" };
    if (n < 30) return { label: "oversold", tone: "rgba(57,217,138,0.16)", border: "rgba(57,217,138,0.32)", color: "#58e7a0" };
    return { label: "neutral", tone: "rgba(255,184,0,0.14)", border: "rgba(255,184,0,0.28)", color: "#ffd36a" };
  }

  function _seriesStatsFallback(arr) {
    const pts = Array.isArray(arr) ? arr : [];
    const vals = pts
      .map((p) => (p && typeof p === "object") ? Number(p.v) : Number(p))
      .filter((v) => Number.isFinite(v));
    if (vals.length < 2) return { volPct: null, maxDDPct: null };
    const rets = [];
    for (let i = 1; i < vals.length; i++) {
      const a = vals[i - 1];
      const b = vals[i];
      if (Number.isFinite(a) && Number.isFinite(b) && a !== 0) rets.push((b / a) - 1);
    }
    const mean = rets.length ? rets.reduce((s, x) => s + x, 0) / rets.length : 0;
    const variance = rets.length ? rets.reduce((s, x) => s + ((x - mean) ** 2), 0) / rets.length : 0;
    const volPct = rets.length ? Math.sqrt(variance) * 100 : null;
    let peak = vals[0];
    let maxDD = 0;
    for (const v of vals) {
      if (v > peak) peak = v;
      const dd = peak ? ((v / peak) - 1) * 100 : 0;
      if (dd < maxDD) maxDD = dd;
    }
    return { volPct, maxDDPct: maxDD };
  }

  function _pairQualityMeta(score) {
    const n = Number(score);
    if (!Number.isFinite(n)) {
      return {
        label: "Unclear",
        color: "rgba(255,255,255,0.72)",
        bg: "rgba(255,255,255,0.05)",
        border: "rgba(255,255,255,0.14)",
      };
    }
    if (n >= 82) {
      return {
        label: "Strong",
        color: "#00ff88",
        bg: "rgba(0,255,136,0.08)",
        border: "rgba(0,255,136,0.55)",
      };
    }
    if (n >= 68) {
      return {
        label: "Good",
        color: "#ffd36a",
        bg: "rgba(255,211,106,0.08)",
        border: "rgba(255,211,106,0.45)",
      };
    }
    if (n >= 52) {
      return {
        label: "Neutral",
        color: "#ffaa00",
        bg: "rgba(255,170,0,0.08)",
        border: "rgba(255,170,0,0.42)",
      };
    }
    return {
      label: "Risky",
      color: "#ff6b6b",
      bg: "rgba(255,107,107,0.08)",
      border: "rgba(255,107,107,0.45)",
    };
  }


  function openPairExplain(p) {
    setSelectedPair(p);
    setAiExplainText("");
    setAiExplainData(null);
    setAiExplainLoading(false);

    // No automatic chart sync here:
    // clicking a pair should only open the pair modal.

    // preload cached series for this pair + timeframe (refreshes automatically on new UTC day)
    const cached = _readPairExplainCache(p?.pair, PAIR_EXPLAIN_TF);
    if (cached) setPairExplainSeries(cached);
  }

  const inflightPairExplain = useRef(false);
  const pairExplainMemCache = useRef({}); // key -> { day: 'YYYY-MM-DD', series: {...} }

  function _utcDayKey() {
    const d = new Date();
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }

  function _pairExplainCacheKey(pairStr, tf) {
    const p = String(pairStr || "").toUpperCase().replace(/\s+/g, "");
    const t = String(tf || "").toUpperCase();
    return `pairExplain:${t}:${p}`;
  }

  function _readPairExplainCache(pairStr, tf) {
    const key = _pairExplainCacheKey(pairStr, tf);
    const day = _utcDayKey();

    // memory first
    const mem = pairExplainMemCache.current?.[key];
    if (mem?.day === day && mem?.series) return mem.series;

    // localStorage next
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (obj?.day !== day) return null;
      if (obj?.series) {
        pairExplainMemCache.current[key] = { day, series: obj.series };
        return obj.series;
      }
    } catch {}
    return null;
  }

  
function _buildDailyPctFromPrice(points, maxDays = 30) {
  // points: [{t, v}] where v=price. returns [{t, v}] where v=% change day-over-day
  if (!Array.isArray(points) || points.length < 2) return [];
  const dayLast = new Map(); // dayKey -> {t, v}
  for (const p of points) {
    const t = Number(p?.t);
    const v = Number(p?.v);
    if (!Number.isFinite(t) || !Number.isFinite(v) || t <= 0) continue;
    const d = new Date(t);
    const dayKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
    // keep the last point of the day
    dayLast.set(dayKey, { t, v });
  }
  const days = Array.from(dayLast.keys()).sort();
  if (days.length < 2) return [];
  const out = [];
  let prev = dayLast.get(days[0])?.v;
  for (let i = 1; i < days.length; i++) {
    const cur = dayLast.get(days[i]);
    if (!cur) continue;
    const pct = (prev && prev !== 0) ? ((cur.v - prev) / prev) * 100 : NaN;
    if (Number.isFinite(pct)) {
      // put timestamp at midday UTC for stable labels
      const [Y,M,D] = days[i].split("-").map(Number);
      const tMid = Date.UTC(Y, M-1, D, 12, 0, 0);
      out.push({ t: tMid, v: pct });
    }
    prev = cur.v;
  }
  return out.slice(-maxDays);
}

function _writePairExplainCache(pairStr, tf, series) {
    const key = _pairExplainCacheKey(pairStr, tf);
    const day = _utcDayKey();
    try {
      pairExplainMemCache.current[key] = { day, series };
      localStorage.setItem(key, JSON.stringify({ day, series }));
    } catch {}
  }

  async function ensurePairExplainSeries(pairStr) {
    if (!pairStr) return;
    const [a, b] = _pairSyms(pairStr);
    if (!a || !b) return;

    // Use cached daily series (refreshes automatically on new UTC day)
    const cached = _readPairExplainCache(pairStr, PAIR_EXPLAIN_TF);
    if (cached?.[a]?.length && cached?.[b]?.length) {
      setPairExplainSeries(cached);
      return;
    }

    // If we already have both, skip.
    if (pairExplainSeries?.[a]?.length && pairExplainSeries?.[b]?.length) return;
    if (inflightPairExplain.current) return;

    inflightPairExplain.current = true;
    setPairExplainLoading(true);
    try {
      const qs = new URLSearchParams({ symbols: `${a},${b}`, range: PAIR_EXPLAIN_TF }).toString();
      const r = await api(`/api/compare?${qs}`, { method: "GET" });

// Prefer backend-provided daily series for "Daily moves (last 30 days)".
// If backend doesn't send daily (or it's empty), derive it from the price series.
const hasDaily = !!(r?.daily && Object.keys(r.daily || {}).length);
const raw = hasDaily ? (r.daily || {}) : (r?.series || {});

// Normalize: backend may return points as [[ts_ms, price], ...]. UI expects {t, v}.
const series = {};
for (const [sym, pts] of Object.entries(raw || {})) {
  if (!Array.isArray(pts)) { series[sym] = []; continue; }
  if (pts.length && Array.isArray(pts[0])) {
    series[sym] = pts
      .map((p) => ({ t: Number(p?.[0] ?? 0), v: Number(p?.[1] ?? 0) }))
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t > 0);
  } else {
    series[sym] = pts
      .map((p) => ({ t: Number(p?.t ?? p?.time ?? p?.x ?? 0), v: Number(p?.v ?? p?.p ?? p?.y ?? 0) }))
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t > 0);
  }
}

if (!hasDaily) {
  // raw was price series -> convert to daily % moves
  for (const sym of Object.keys(series)) {
    series[sym] = _buildDailyPctFromPrice(series[sym], 30);
  }
}

setPairExplainSeries(series);
_writePairExplainCache(pairStr, PAIR_EXPLAIN_TF, series);
    } catch (e) {
      // keep silent; UI will show —
    } finally {
      inflightPairExplain.current = false;
      setPairExplainLoading(false);
    }
  }

  function _retPctFromSeries(seriesMap, sym) {
    try {
      const arr = seriesMap?.[sym] || [];
      if (!arr.length) return null;

      const firstPt = arr[0];
      const lastPt = arr[arr.length - 1];

      // backend series format can be:
// - [{t, v}, ...]
// - [[ts_ms, price], ...]
// - [number, ...] (rare fallback)
      const first = Array.isArray(firstPt) ? Number(firstPt?.[1]) : ((firstPt && typeof firstPt === "object") ? Number(firstPt.v) : Number(firstPt));
      const last  = Array.isArray(lastPt)  ? Number(lastPt?.[1])  : ((lastPt && typeof lastPt === "object") ? Number(lastPt.v) : Number(lastPt));

      if (!Number.isFinite(first) || !Number.isFinite(last) || !first) return null;
      return ((last - first) / first) * 100.0;
    } catch {
      return null;
    }
  }

  function _retPctForExplain(sym) {
    // Prefer dedicated pair series (works even if coin not selected in Compare chart)
    const x = _retPctFromSeries(pairExplainSeries, sym);
    if (Number.isFinite(x)) return x;
    // fallback to current chart
    return _retPctFromChart(sym);
  }

  useEffect(() => {
    if (!selectedPair?.pair) return;
    ensurePairExplainSeries(selectedPair.pair);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPair?.pair, timeframe]);

  async function runAiExplain() {
    // BUILD464: Free users get AI Insight without Core; backend enforces 3/7d quota.
    // Core/Live still allowed. Only block when clearly no wallet session.
    if (!wallet) {
      setAiExplainText("Connect your wallet to use AI Insight.");
      return;
    }
    if (!selectedPair) {
      setAiExplainText("Select a pair first, then run AI Insight.");
      return;
    }
    setAiExplainLoading(true);
    try {
      const [a, b] = _pairSyms(selectedPair.pair);
      const corr = Number(selectedPair?.corr);
      const isExtremeInsight = normalizedAiInsightMode === "extreme";

      const windowDefs = ["7D", "30D", "90D", "1Y"];
      const pairWindows = {};
      for (const tf of windowDefs) {
        const sliced = sliceCompareSeries(pairExplainSeries || {}, tf);
        const stats = _seriesStatsFromSeriesMap(sliced, [a, b]);
        pairWindows[tf] = stats || {};
      }

      const getRet = (sym, tf) => {
        const st = pairWindows?.[tf]?.[sym];
        return Number.isFinite(Number(st?.changePct)) ? Number(st.changePct) : null;
      };

      const ra = getRet(a, "30D");
      const rb = getRet(b, "30D");
      const spread = (Number.isFinite(ra) && Number.isFinite(rb)) ? (ra - rb) : null;

      const adaptiveWeights = sanitizeCompareWeights(activeCompareWeights || DEFAULT_COMPARE_WEIGHTS);
      const wCorr = Number(adaptiveWeights.corr || 0);
      const wMomentum = Number(adaptiveWeights.momentum || 0);
      const wOpportunity = Number(adaptiveWeights.opportunity || 0);
      const wStability = Number(adaptiveWeights.stability || 0);
      const wSentiment = Number(adaptiveWeights.sentiment || 0);

      // Adaptive Threshold System:
      // Custom Weighting now changes the actual AI Insight thresholds, not only the prompt.
      // Higher Opportunity/Momentum makes the system earlier and more scout-oriented.
      // Higher Stability/Correlation makes it stricter and confirmation-oriented.
      const adaptiveAggression =
        (isExtremeInsight ? 0.18 : 0) +
        ((wMomentum - DEFAULT_COMPARE_WEIGHTS.momentum) / 100) * 0.22 +
        ((wOpportunity - DEFAULT_COMPARE_WEIGHTS.opportunity) / 100) * 0.26 -
        ((wStability - DEFAULT_COMPARE_WEIGHTS.stability) / 100) * 0.18 -
        ((wCorr - DEFAULT_COMPARE_WEIGHTS.corr) / 100) * 0.12;

      const thresholdShift = Math.max(-0.18, Math.min(0.24, adaptiveAggression));
      const adaptiveCorrMin = Math.max(0.42, Math.min(0.86, (isExtremeInsight ? 0.62 : 0.80) - thresholdShift));
      const adaptiveAvoidCorr = Math.max(0.18, Math.min(0.52, (isExtremeInsight ? 0.30 : 0.45) - thresholdShift * 0.65));
      const adaptiveSpreadMin = Math.max(0.25, Math.min(1.25, (isExtremeInsight ? 0.45 : 0.75) - thresholdShift * 1.35));
      const adaptiveWaitSpread = Math.max(0.20, Math.min(1.00, (isExtremeInsight ? 0.35 : 0.75) - thresholdShift * 1.15));
      const adaptiveWideSpread = Math.max(2.5, Math.min(6.5, 4 - thresholdShift * 5));
      const adaptiveMidSpread = Math.max(1.25, Math.min(3.5, 2 - thresholdShift * 3));

      const adaptiveProfileLabel =
        thresholdShift >= 0.12 ? "Aggressive / early scout" :
        thresholdShift <= -0.10 ? "Strict / confirmation-first" :
        "Balanced adaptive";

      const winner = Number.isFinite(ra) && Number.isFinite(rb) ? (ra >= rb ? a : b) : null;
      const loser = Number.isFinite(ra) && Number.isFinite(rb) ? (ra >= rb ? b : a) : null;

      const classify = (v) => {
        if (!Number.isFinite(v)) return "n/a";
        if (v >= 8) return "bullish";
        if (v <= -8) return "bearish";
        return "neutral";
      };

      const avgRet = (tf) => {
        const aa = getRet(a, tf);
        const bb = getRet(b, tf);
        if (Number.isFinite(aa) && Number.isFinite(bb)) return (aa + bb) / 2;
        if (Number.isFinite(aa)) return aa;
        if (Number.isFinite(bb)) return bb;
        return null;
      };

      const trendStructure = windowDefs
        .map((tf) => `${tf} ${classify(avgRet(tf))}`)
        .join(" · ");

      const shortBias = classify(avgRet("30D"));
      const longBias = classify(avgRet("1Y"));

      let momentumShift = "Momentum is mixed across timeframes.";
      if (shortBias === "bearish" && longBias === "bullish") momentumShift = "Short-term weakness inside a stronger long-term structure.";
      else if (shortBias === "bullish" && longBias === "bearish") momentumShift = "Short-term recovery attempt against a weaker long-term backdrop.";
      else if (shortBias === longBias && shortBias !== "n/a") momentumShift = `Short-term and long-term momentum are aligned ${shortBias}.`;

      let setup = "Neutral";
      let confidence = isExtremeInsight ? 5.9 : 5.4;
      let confidenceLabel = "MEDIUM";
      let risk = isExtremeInsight ? "Medium-High" : "Medium";
      let gridMode = isExtremeInsight ? "Extreme Scout" : "Standard";
      let gridRange = isExtremeInsight ? "4–8%" : "2–4%";
      let why = [];
      let verdictText = isExtremeInsight
        ? "Extreme mode treats this as an early-signal scan: weaker confirmation is allowed, but invalidation is faster."
        : "This pair is interesting, but the signal is not strong enough for a clear structure yet.";

      if (Number.isFinite(corr)) {
        if (corr >= Math.max(0.88, adaptiveCorrMin + 0.18)) confidence += isExtremeInsight ? 1.5 : 1.8;
        else if (corr >= adaptiveCorrMin) confidence += isExtremeInsight ? 1.1 : 1.2;
        else if (corr >= Math.max(0.52, adaptiveCorrMin - 0.15)) confidence += isExtremeInsight ? 0.9 : 0.6;
        else if (corr >= adaptiveAvoidCorr && isExtremeInsight) confidence += 0.3;
        else if (corr < adaptiveAvoidCorr) confidence -= isExtremeInsight ? 0.7 : 1.4;
      }

      if (Number.isFinite(spread)) {
        const absSpread = Math.abs(spread);
        if (absSpread >= adaptiveWideSpread) confidence += isExtremeInsight ? 2.1 : 1.6;
        else if (absSpread >= adaptiveMidSpread) confidence += isExtremeInsight ? 1.5 : 1.0;
        else if (absSpread >= adaptiveSpreadMin) confidence += isExtremeInsight ? 0.9 : 0.5;
        else confidence -= isExtremeInsight ? 0.1 : 0.4;
      }

      // Weight bias changes confidence after raw metric scoring.
      confidence += thresholdShift * 2.2;
      if (wStability >= 40 && Number.isFinite(corr) && corr < 0.65) confidence -= 0.8;
      if (wOpportunity >= 40 && Number.isFinite(spread) && Math.abs(spread) >= adaptiveSpreadMin) confidence += 0.5;
      if (wMomentum >= 40 && shortBias !== "n/a" && shortBias !== "neutral") confidence += 0.35;

      if (Number.isFinite(corr) && corr >= adaptiveCorrMin && Number.isFinite(spread) && Math.abs(spread) >= adaptiveSpreadMin && winner && loser) {
        setup = isExtremeInsight ? "EXTREME REVERSAL SCOUT" : "MEAN REVERSION";
        verdictText = isExtremeInsight
          ? `${winner} is leading ${loser} over 30D. Extreme mode flags this earlier as a high-sensitivity reversal/rotation scout, not as a confirmed clean setup.`
          : `${winner} outperformed ${loser} over 30D. With relatively high correlation, the pair shows a mean-reversion style structure.`;
        why.push(`${isExtremeInsight ? "Usable" : "High"} correlation (${corr >= 0 ? "+" : ""}${corr.toFixed(2)}) keeps the pair relationship relevant.`);
        why.push(`The performance spread of ${_fmtPctLocal(spread)} creates a visible imbalance between both sides.`);
        why.push(`${winner} is currently the stronger side, while ${loser} is the weaker side in this window.`);
        if (isExtremeInsight) {
          why.push("Extreme mode accepts earlier imbalance, but the setup can invalidate faster if follow-through fails.");
        }
        if (Math.abs(spread) >= adaptiveWideSpread) {
          gridMode = isExtremeInsight ? "Extreme Wide" : "Wide";
          gridRange = isExtremeInsight ? "6–10%" : "4–6%";
          risk = isExtremeInsight ? "High" : "Medium-High";
        } else if (Math.abs(spread) >= adaptiveMidSpread) {
          gridMode = isExtremeInsight ? "Extreme Scout" : "Standard";
          gridRange = isExtremeInsight ? "4–8%" : "3–5%";
          risk = isExtremeInsight ? "Medium-High" : "Medium";
        } else if (isExtremeInsight) {
          gridMode = "Extreme Scout";
          gridRange = "3–6%";
          risk = "Medium-High";
        }
      } else if (Number.isFinite(corr) && corr < adaptiveAvoidCorr) {
        setup = "AVOID";
        confidence -= 1.2;
        confidenceLabel = "LOW";
        risk = "High";
        verdictText = "This pair is not moving together reliably enough for a clean paired structure.";
        why.push(`Low correlation (${Number.isFinite(corr) ? ((corr >= 0 ? "+" : "") + corr.toFixed(2)) : "—"}) weakens the paired logic.`);
        why.push("Pairs with weak correlation can drift apart instead of reverting.");
        why.push("This increases the chance of persistent divergence.");
        gridMode = "Wait";
        gridRange = "No setup";
      } else if (Number.isFinite(spread) && Math.abs(spread) < adaptiveWaitSpread) {
        setup = isExtremeInsight ? "EARLY WATCH" : "WAIT";
        confidence -= isExtremeInsight ? 0.2 : 0.6;
        confidenceLabel = "LOW-MED";
        risk = isExtremeInsight ? "Medium" : "Low-Medium";
        verdictText = isExtremeInsight
          ? "Extreme mode keeps this on early watch, but the spread is still too small for a strong edge."
          : "The pair is correlated enough, but the spread is still small and the structure remains less expressive.";
        why.push("The current performance gap is still narrow.");
        why.push("Without enough spread, the relative structure can feel random and weak.");
        why.push(isExtremeInsight ? "Extreme mode can monitor it earlier, but confirmation is still thin." : "A clearer imbalance usually creates a stronger read.");
        gridMode = isExtremeInsight ? "Extreme Watch" : "Wait";
        gridRange = isExtremeInsight ? "2–4%" : "Below 2%";
      } else if (winner && loser) {
        setup = isExtremeInsight ? "EXTREME TREND SCOUT" : "TREND BIAS";
        confidence += isExtremeInsight ? 0.8 : 0.2;
        confidenceLabel = "MEDIUM";
        risk = isExtremeInsight ? "High" : "Medium-High";
        verdictText = isExtremeInsight
          ? `${winner} is stronger than ${loser}. Extreme mode treats this as an early trend/rotation scout with higher false-signal risk.`
          : `${winner} is stronger than ${loser}, but the pair does not yet qualify as a clean high-confidence paired structure.`;
        why.push("There is a leader and a laggard, but the data is not perfectly aligned for a strong reversion structure.");
        why.push(isExtremeInsight ? "Extreme mode prioritizes early movement, so false continuation risk is higher." : "Mixed conditions increase the chance of continuation instead of catch-up.");
        why.push(isExtremeInsight ? "This is a scout read, not a confirmed structure." : "The current profile looks more mixed than clean.");
      }

      confidence = Math.max(1, Math.min(10, Math.round(confidence * 10) / 10));
      if (confidence >= 8) confidenceLabel = "HIGH";
      else if (confidence <= 4.9) confidenceLabel = "LOW";

      const insightSummary =
        longBias === "bullish" && shortBias === "bearish"
          ? "Short-term pressure is visible, but the broader structure still looks stronger."
          : longBias === "bearish" && shortBias === "bullish"
            ? "There is a rebound attempt, but the broader structure remains weaker."
            : "The pair structure is currently mixed and should be monitored across multiple windows.";

      const localFallbackText = [
        `Trend Structure: ${trendStructure}`,
        `Momentum Shift: ${momentumShift}`,
        `Risk View: ${risk}`,
        `Interpretation: ${verdictText}`,
        `Insight Summary: ${insightSummary}`,
      ].join("\n")


      const pairStats = pairWindows?.["30D"] || {};
      const aiSignalContext = buildAiSignalContext({
        syms: [a, b].filter(Boolean),
        watchRows,
        ratingSummaryBySymbol,
        onchainBySymbol,
        marketConditionBySymbol,
        bestPairsToShow,
        compareWeights: activeCompareWeights,
        aiMode: normalizedAiInsightMode,
        nexusTradingContext: {
          prepared_setup: tradingPreparedSetup || null,
          learning_count: Array.isArray(tradingLearningSetups) ? tradingLearningSetups.length : 0,
          latest_learning_event: Array.isArray(tradingLearningSetups) && tradingLearningSetups[0]?.learningEvent ? tradingLearningSetups[0].learningEvent : null,
          configured_budget_usd: tradingBudgetUsd || "",
          risk_mode: tradingRiskMode || "",
        },
      });
      const aiSignalText = formatAiSignalContextForPrompt(aiSignalContext);
      const payload = {
        kind: "explain",
        symbols: [a, b].filter(Boolean),
        profile: "balanced",
        timeframe: String(timeframe || "90D").toUpperCase(),
        index_mode: !!indexMode,
        series_stats: pairStats,
        insight_windows: pairWindows,
        ai_signal_context: aiSignalContext,
        ai_mode: normalizedAiInsightMode,
        compare_weights: activeCompareWeights,
        question:
          `Analyze the current pair structure for ${a} vs ${b}. ` +
          `This is AI Insight Level 2: explain not only what the data says, but how the structure may behave. ` +
          `AI Insight mode is ${normalizedAiInsightMode}. Extreme mode means higher sensitivity to early momentum, rebound, spread and hidden-opportunity structures, while still explaining invalidation clearly. ` +
          `Use the active Compare weights as interpretation priorities: correlation ${activeCompareWeights.corr}%, momentum ${activeCompareWeights.momentum}%, opportunity ${activeCompareWeights.opportunity}%, stability ${activeCompareWeights.stability}%, community sentiment ${activeCompareWeights.sentiment}%. ` +
          `Use pair statistics, multi-timeframe context, rating, community votes, on-chain context, all_compare_pairs hidden-opportunity scan, and wallet-fit to describe structure, behavior, strategy fit, and risk posture. ` +
          `IMPORTANT: mention rating, market condition (OE/RVOL), and on-chain confirmation explicitly when available. If on-chain is neutral or missing, say it is neutral/no strong signal instead of ignoring it. ` +
          `Include a compact behavior read, for example range-bound, mean-reversion style, trend-bias, unstable/choppy, or low-conviction. ` +
          `Include strategy fit without giving advice: grid-fit, wait/no-clean-setup, rotation-style, or continuation-risk. ` +
          `Do not tell the user what to do and do not give buy/sell instructions. ` +
          `Current pair correlation: ${Number.isFinite(corr) ? corr.toFixed(2) : "n/a"}. ` +
          `30D spread: ${Number.isFinite(spread) ? spread.toFixed(2) + "%" : "n/a"}. ` +
          `Short bias: ${shortBias}. Long bias: ${longBias}. ` +
          `Local setup classification: ${setup}, confidence ${confidence}/10, risk ${risk}, grid fit ${gridMode} (${gridRange}). ` +
          (aiSignalText ? `Signal context: ${aiSignalText}` : ""),
      };

      let backendText = "";
      if (!token) throw new Error("Please reconnect your wallet to authorize AI.");
      try {
        const r = await api("/api/ai/insight", { method: "POST", token, body: payload });
        console.log("AI RESPONSE:", r);
        const backendEngineV2 = r?.ai_engine_v2 || r?.engine_v2 || null;
        if (backendEngineV2 && typeof backendEngineV2 === "object") {
          payload.__backendEngineV2 = backendEngineV2;
        }
        if (r?.context_used && typeof r.context_used === "object") {
          payload.__contextUsed = r.context_used;
        }
        if (r?.market_memory_snapshot_id) {
          payload.__marketMemorySnapshotId = r.market_memory_snapshot_id;
        }
        backendText =
          r?.answer ??
          r?.output ??
          r?.text ??
          r?.message ??
          (typeof r === "string" ? r : "");
      } catch {
        backendText = "";
      }

      const engineV2 = payload.__backendEngineV2 || null;
      const backendContextUsed = payload.__contextUsed || null;
      const finalTextRaw = buildCompactAiInsight({
        backendText,
        trendStructure,
        momentumShift,
        insightSummary,
      });

      const signalCoins = Array.isArray(aiSignalContext?.coins) ? aiSignalContext.coins : [];
      const signalLine = signalCoins.length
        ? signalCoins.map((c) => {
            const rating = c?.rating || "n/a";
            const votes = Number(c?.user_rating_votes || 0);
            const delta = Number(c?.onchain_delta || 0);
            const ocSummary = String(c?.onchain?.summary || "").trim();
            const ocShort = ocSummary
              ? ocSummary
              : (delta ? `on-chain delta ${delta > 0 ? "+" : ""}${delta}` : "on-chain neutral/no strong signal");
            return `${c.symbol}: Rating ${rating}, Votes ${votes}, ${ocShort}`;
          }).join(" | ")
        : "";

      const absSpreadForLevel2 = Math.abs(Number(spread));
      const behaviorRead =
        Number.isFinite(corr) && corr >= adaptiveCorrMin && Number.isFinite(absSpreadForLevel2) && absSpreadForLevel2 >= adaptiveMidSpread
          ? (thresholdShift >= 0.12 ? "early mean-reversion / opportunity-scout behavior" : "mean-reversion style behavior with visible imbalance")
          : Number.isFinite(corr) && corr < adaptiveAvoidCorr
            ? "unstable/choppy behavior because the pair relationship is weak"
            : Number.isFinite(absSpreadForLevel2) && absSpreadForLevel2 < adaptiveWaitSpread
              ? "low-conviction/range-bound behavior because the spread is still narrow"
              : (thresholdShift <= -0.10 ? "confirmation-watch behavior with stricter stability requirements" : "mixed behavior with limited confirmation");
      const strategyFitLine = `Behavior: ${behaviorRead}. Adaptive profile: ${adaptiveProfileLabel}. Strategy fit: ${gridMode || "Standard"} / ${gridRange || "n/a"} with ${risk || "medium"} risk.`;

      let finalText = finalTextRaw;
      if (isExtremeInsight && !/extreme mode|high-sensitivity|early-signal|false-signal/i.test(finalText)) {
        finalText = `Extreme mode: high-sensitivity early-signal read. It reacts sooner than Standard mode, but false-signal and fast invalidation risk are higher.\n\n${finalText}`;
      }
      if (signalLine && !/rating|on-chain|onchain|community|vote/i.test(finalText)) {
        finalText = `${finalText}\n\nSignal context: ${signalLine}`;
      }
      if (!/behavior|strategy fit|grid-fit|range-bound|mean-reversion|unstable|choppy|conviction/i.test(finalText)) {
        finalText = `${finalText}\n\nLevel 2: ${strategyFitLine}`;
      }
      if (isExtremeInsight && !/Extreme fit:/i.test(finalText)) {
        finalText = `${finalText}\n\nExtreme fit: earlier detection, wider scout range, higher risk, faster invalidation if momentum or spread confirmation fades.`;
      }
      if (customCompareWeightsOn && !/Adaptive weighting:/i.test(finalText)) {
        finalText = `${finalText}\n\nAdaptive weighting: ${adaptiveProfileLabel}. Momentum ${wMomentum}%, Opportunity ${wOpportunity}%, Stability ${wStability}%, Correlation ${wCorr}% changed the active thresholds for this read.`;
      }

      setAiExplainText(finalText);
      setAiExplainData({
        setup: setup,
        confidence: confidence,
        confidenceLabel: confidenceLabel,
        risk: risk,
        effectiveMode: isExtremeInsight ? "Extreme" : "Standard",
        exitRisk: engineV2?.exit_risk || null,
        preExitWarning: Boolean(engineV2?.pre_exit_warning),
        contradictions: Array.isArray(engineV2?.contradictions) ? engineV2.contradictions : [],
        gridMode,
        gridRange,
        why,
        verdictText: finalText,
        winner,
        loser,
        corr,
        trendStructure,
        momentumShift,
        insightSummary,
        windows: pairWindows,
        aiSignalContext,
        engineV2,
        aiMode: normalizedAiInsightMode,
        compareWeights: activeCompareWeights,
        adaptiveProfile: adaptiveProfileLabel,
        adaptiveThresholds: {
          corrMin: adaptiveCorrMin,
          avoidCorr: adaptiveAvoidCorr,
          spreadMin: adaptiveSpreadMin,
          waitSpread: adaptiveWaitSpread,
          thresholdShift,
          customOn: !!customCompareWeightsOn,
        },
        strategistMemory: {
          snapshotId: payload.__marketMemorySnapshotId || null,
          used: Boolean(backendContextUsed?.has_strategist_memory_v2),
          bias: backendContextUsed?.strategist_memory_bias || "",
        },
        aiInsightBridge: {
          used: Boolean(backendContextUsed?.has_ai_insight_bridge),
          bias: backendContextUsed?.ai_insight_bridge_bias || "",
        },
        pairAlerts: Array.isArray(engineV2?.pair_alerts) ? engineV2.pair_alerts : [],
      });
    } catch (e) {
      setAiExplainText("");
      setAiExplainData(null);
    } finally {
      setAiExplainLoading(false);
    }
  }

  const [compareLoading, setCompareLoading] = useState(false);
  const [compareSeries, setCompareSeries] = useState(() => {
    // Prefer per-(timeframe+symbols) cache so reloads feel instant
    const cached = _cmpGetCached(compareSymbols, _compareFetchRange(timeframe));
    if (cached) return cached;

    // Fallback: legacy single-bucket cache
    try {
      const raw = localStorage.getItem(LS_COMPARE_SERIES_CACHE);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const lastGoodCompareRef = useRef(null);
  const compareAbortRef = useRef(null);
  const lastCompareFetchRef = useRef(0);
  const compareRetryRef = useRef({ key: "", n: 0, t: null });
  const compareFailRetryRef = useRef({ key: "", n: 0, t: null });

  // seed "last good" on first load
  useEffect(() => {
    if (!lastGoodCompareRef.current && compareSeries && Object.keys(compareSeries).length) {
      lastGoodCompareRef.current = compareSeries;
    }
  }, []);

  // Keep compareSeries in sync with compareSymbols so removing coins never leaves "ghost" lines
  // (compare fetch is throttled; this prunes immediately even if fetchCompare returns early)
  const prevCompareSymbolsRef = useRef(compareSymbols);

  useEffect(() => {
    // prune local series immediately
    setCompareSeries((prev) => {
      const next = {};
      for (const s of compareSymbols || []) {
        if (prev && prev[s]) next[s] = prev[s];
      }
      lastGoodCompareRef.current = next;
      try { localStorage.setItem(LS_COMPARE_SERIES_CACHE, JSON.stringify(next)); } catch {}
      return next;
    });

    // If symbols decreased (removal), bypass throttle once to refresh from backend
    const prevSyms = prevCompareSymbolsRef.current || [];
    const removed = prevSyms.length > (compareSymbols || []).length;
    prevCompareSymbolsRef.current = compareSymbols;

    if (removed) fetchCompare({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareSymbols.join("|")]);

  const [indexMode, setIndexMode] = useState(true);
  const [viewMode, setViewMode] = useState("overlay"); // overlay | grid
  const [comparePage, setComparePage] = useState("all"); // first10 | next10 | all
  const [highlightedSyms, setHighlightedSyms] = useState([]);
  const [showTop10Pairs, setShowTop10Pairs] = useState(true);
  const [bestPairsSortMode, setBestPairsSortMode] = useLocalStorageState("nexus_best_pairs_sort_mode", "score"); // score | spread
  const [movementPanelOpen, setMovementPanelOpen] = useState(false);
  const [customCompareWeightsOn, setCustomCompareWeightsOn] = useLocalStorageState("nexus_compare_custom_weights_on", false);
  const [compareWeightsExpanded, setCompareWeightsExpanded] = useLocalStorageState("nexus_compare_weights_expanded", false);
  const [compareWeights, setCompareWeights] = useLocalStorageState("nexus_compare_custom_weights_v1", DEFAULT_COMPARE_WEIGHTS);
  const [aiInsightMode, setAiInsightMode] = useLocalStorageState("nexus_ai_insight_mode_v1", "standard");
  const normalizedAiInsightMode = String(aiInsightMode || "standard").toLowerCase() === "extreme" ? "extreme" : "standard";
  const safeCompareWeights = useMemo(() => sanitizeCompareWeights(compareWeights), [compareWeights]);
  const activeCompareWeights = useMemo(() => customCompareWeightsOn ? safeCompareWeights : DEFAULT_COMPARE_WEIGHTS, [customCompareWeightsOn, safeCompareWeights]);
  const compareWeightsTotal = useMemo(() => compareWeightTotal(safeCompareWeights), [safeCompareWeights]);
  const compareWeightsRemaining = Math.max(0, 100 - compareWeightsTotal);
  const updateCompareWeight = useCallback((key, rawValue) => {
    const k = String(key || "");
    if (!COMPARE_WEIGHT_KEYS.includes(k)) return;
    const requested = Math.max(0, Math.min(100, Math.round(Number(rawValue) || 0)));
    setCompareWeights((prev) => {
      const base = sanitizeCompareWeights(prev);
      const otherTotal = COMPARE_WEIGHT_KEYS
        .filter((x) => x !== k)
        .reduce((sum, x) => sum + Number(base[x] || 0), 0);
      const maxAllowed = Math.max(0, 100 - otherTotal);
      return { ...base, [k]: Math.min(requested, maxAllowed) };
    });
  }, [setCompareWeights]);
  const resetCompareWeights = useCallback(() => setCompareWeights(DEFAULT_COMPARE_WEIGHTS), [setCompareWeights]);
  const [gridModalSym, setGridModalSym] = useState(null);

  useEffect(() => {
    setHighlightedSyms((prev) => {
      const allowed = new Set((compareSymbols || []).map((s) => String(s || "").toUpperCase()));
      return (Array.isArray(prev) ? prev : [])
        .map((s) => String(s || "").toUpperCase())
        .filter((s) => allowed.has(s));
    });
  }, [compareSymbols.join("|")]);

  useEffect(() => {
    const sym = String(gridModalSym || "").toUpperCase().trim();
    if (!sym) return;
    if (!(compareSymbols || []).includes(sym)) setGridModalSym(null);
  }, [gridModalSym, compareSymbols.join("|")]);

  const compareSeriesView = useMemo(() => sliceCompareSeries(compareSeries, timeframe), [compareSeries, timeframe]);

  const visibleCompareSymbols = useMemo(() => {
    if (comparePage === "first10") return compareSymbols.slice(0, 10);
    if (comparePage === "next10") return compareSymbols.slice(10, 20);
    return compareSymbols;
  }, [compareSymbols, comparePage]);

  const visibleHighlightedSyms = useMemo(() => {
    const visibleSet = new Set((visibleCompareSymbols || []).map((s) => String(s || "").toUpperCase()));
    return (highlightedSyms || []).filter((s) => visibleSet.has(String(s || "").toUpperCase()));
  }, [highlightedSyms, visibleCompareSymbols]);

  const visibleCompareSeriesView = useMemo(() => {
    const out = {};
    for (const sym of visibleCompareSymbols || []) {
      out[sym] = compareSeriesView?.[sym] || [];
    }
    return out;
  }, [visibleCompareSymbols, compareSeriesView]);

  // Chart uses the *view* timeframe (default 90D), but analytics like "best pairs" still use full data (1Y/2Y)
  const chartRaw = useMemo(() => buildUnifiedChart(visibleCompareSeriesView), [visibleCompareSeriesView]);
  const chartRawFull = useMemo(() => buildUnifiedChart(compareSeries), [compareSeries]);
  const gridModalChart = useMemo(() => {
    const sym = String(gridModalSym || "").toUpperCase().trim();
    if (!sym) return { x: [], lines: {}, order: [] };
    return buildUnifiedChart({ [sym]: compareSeriesView?.[sym] || [] });
  }, [gridModalSym, compareSeriesView]);
  const gridModalRow = useMemo(() => {
    const sym = String(gridModalSym || "").toUpperCase().trim();
    return sym ? (watchRows || []).find((r) => String(r?.symbol || "").toUpperCase() === sym) || null : null;
  }, [gridModalSym, watchRows]);
    const bestPairsAll = useMemo(() => computeBestPairsFromSeries(compareSeries, 1000, activeCompareWeights, ratingSummaryBySymbol), [compareSeries, activeCompareWeights, ratingSummaryBySymbol]);
  const bestPairsSorted = useMemo(() => {
    const rows = Array.isArray(bestPairsAll) ? [...bestPairsAll] : [];
    if (String(bestPairsSortMode || "score") !== "spread") return rows;

    return rows.sort((a, b) => {
      const av = Number.isFinite(Number(a?.spreadPct)) ? Number(a.spreadPct) : -Infinity;
      const bv = Number.isFinite(Number(b?.spreadPct)) ? Number(b.spreadPct) : -Infinity;
      if (bv !== av) return bv - av;
      return (Number(b?.score || 0) - Number(a?.score || 0));
    });
  }, [bestPairsAll, bestPairsSortMode]);
  const bestPairsToShow = useMemo(() => (showTop10Pairs ? bestPairsSorted.slice(0, 10) : bestPairsSorted), [showTop10Pairs, bestPairsSorted]);
  const bestPairUiAlerts = useMemo(
    () => buildLocalPairAlertsForUi(bestPairsAll, activeCompareWeights, normalizedAiInsightMode),
    [bestPairsAll, activeCompareWeights, normalizedAiInsightMode]
  );
  const bestPairTopAlert = bestPairUiAlerts?.[0] || null;
  const bestPairAlertTone = bestPairUiAlerts.some((a) => a?.strength === "high")
    ? "high"
    : bestPairUiAlerts.some((a) => a?.strength === "medium")
      ? "medium"
      : bestPairUiAlerts.length
        ? "low"
        : "none";
  const bestPairAlertPairSet = useMemo(() => {
    const set = new Set();
    for (const a of bestPairUiAlerts || []) {
      const key = String(a?.pair || "").toUpperCase().trim();
      if (key) set.add(key);
    }
    return set;
  }, [bestPairUiAlerts]);
  const bestPairAlertToneByPair = useMemo(() => {
    const out = {};
    for (const a of bestPairUiAlerts || []) {
      const key = String(a?.pair || "").toUpperCase().trim();
      if (!key) continue;
      const tone = a?.strength === "high" ? "high" : a?.strength === "medium" ? "medium" : "low";
      if (!out[key] || tone === "high" || (tone === "medium" && out[key] !== "high")) out[key] = tone;
    }
    return out;
  }, [bestPairUiAlerts]);
  const bestPairAlertScoreByPair = useMemo(() => {
    const out = {};
    for (const a of bestPairUiAlerts || []) {
      const key = String(a?.pair || "").toUpperCase().trim();
      if (!key) continue;
      const score = Number(a?.movement_chance_score ?? a?.movementChanceScore ?? a?.score);
      if (!Number.isFinite(score)) continue;
      if (!Number.isFinite(Number(out[key])) || score > Number(out[key])) out[key] = score;
    }
    return out;
  }, [bestPairUiAlerts]);
  const selectedPairLocalAlerts = useMemo(() => {
    const pairKey = String(selectedPair?.pair || "").toUpperCase();
    if (!pairKey) return [];
    return (bestPairUiAlerts || []).filter((a) => String(a?.pair || "").toUpperCase() === pairKey);
  }, [bestPairUiAlerts, selectedPair?.pair]);
  const selectedPairMainAlert = selectedPairLocalAlerts?.[0] || null;
  const selectedPairAlertTone = selectedPairLocalAlerts.some((a) => a?.strength === "high")
    ? "high"
    : selectedPairLocalAlerts.some((a) => a?.strength === "medium")
      ? "medium"
      : selectedPairLocalAlerts.length
        ? "low"
        : "none";
  const toggleBestPairsSort = useCallback((mode) => {
    setBestPairsSortMode((prev) => (String(prev || "score") === mode ? "score" : mode));
  }, [setBestPairsSortMode]);

  // grid (manual)
  // Grid UI works with symbols; backend grid endpoints are keyed by item_id.
  const [gridChain, setGridChain] = useState(() => String(DEFAULT_CHAIN || "POL").toUpperCase());
  const [gridItem, setGridItem] = useState(() => String(DEFAULT_CHAIN || "POL").toUpperCase());
  const [rotationSelectedPick, setRotationSelectedPick] = useState(null);
  const [rotationShowAllRecommendations, setRotationShowAllRecommendations] = useState(false);
  const [rotationNetworkScope, setRotationNetworkScope] = useState("ALL");
  // BUILD462: optional NKR allowed assets (like Trader). Empty = Strategist picks best on session chain.
  const [nkrAllowedAssets, setNkrAllowedAssets] = useState("");

  const [rotationMode, setRotationMode] = useState("RECOMMENDATION");
  const [nkrCapitalMode, setNkrCapitalMode] = useState("DYNAMIC");
  // BUILD384: synchronous source of truth for the next session create request.
  // React state can still contain the previous mode in the same click cycle after
  // Aggressive consent; this ref prevents a consented AGGRESSIVE start becoming DYNAMIC.
  const nkrCapitalModeRef = useRef("DYNAMIC");
  // BUILD407: keep the exact selected NKR mode locked while an async CoreVault create is pending.
  // Delayed app-state hydration must never overwrite AGGRESSIVE with an older DYNAMIC draft.
  const nkrStartPendingRef = useRef(false);
  // BUILD442/443: cache exact start settings by on-chain session id + last pending start
  // (ETH async create returns before sessionId exists — must not lose AGGRESSIVE/budget).
  const nkrStartSettingsCacheRef = useRef({});
  const nkrPendingStartConfigRef = useRef(null);
  const nkrPeriodEditingRef = useRef(false);
  const tradingRuntimeEditingRef = useRef(false);
  const nkrModeEditingRef = useRef(false);

  const nkrStartModeRef = useRef("");
  useEffect(() => { nkrCapitalModeRef.current = String(nkrCapitalMode || "DYNAMIC").toUpperCase(); }, [nkrCapitalMode]);
  const [nkrObservationWindow, setNkrObservationWindow] = useState("1h");
  const [nkrSessionInfoOpen, setNkrSessionInfoOpen] = useState(null);
  const [nkrProfitMode, setNkrProfitMode] = useState("REINVEST");
  // BUILD406: NKR period can be entered in hours or days. The numeric draft stays
  // separate from the unit so 2 hours is never rounded up to 1 day.
  const [nkrPeriodDays, setNkrPeriodDays] = useState("10");
  const [nkrPeriodUnit, setNkrPeriodUnit] = useState("days");
  const normalizeNkrPeriodHours = useCallback((value = nkrPeriodDays, unit = nkrPeriodUnit) => {
    const n = Number(String(value ?? "").replace(",", "."));
    const safe = Number.isFinite(n) && n > 0 ? n : 1;
    const u = String(unit || "days").toLowerCase();
    const hours = u.startsWith("day") || u === "tage" ? safe * 24 : safe;
    return Math.max(1, Math.round(hours * 100) / 100);
  }, [nkrPeriodDays, nkrPeriodUnit]);
  const [nkrControlState, setNkrControlState] = useState("WAITING");
  // Soft clock so Strategist "Crypto Moves" rotates every ~10s without a full reload
  const [strategistMovesTick, setStrategistMovesTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStrategistMovesTick((n) => n + 1), 10000);
    return () => clearInterval(id);
  }, []);

  // BUILD434: CoinGecko Basic-compatible trending discovery.
  // The API key stays server-side; this UI only reads Nexus' backend proxy.
  const [coingeckoTrending, setCoingeckoTrending] = useState([]);
  const [coingeckoTrendingLoaded, setCoingeckoTrendingLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const loadTrending = async () => {
      try {
        const res = await api("/api/coingecko/trending");
        const rows = Array.isArray(res?.coins) ? res.coins : [];
        if (!cancelled) setCoingeckoTrending(rows);
      } catch (_) {
        if (!cancelled) setCoingeckoTrending([]);
      } finally {
        if (!cancelled) setCoingeckoTrendingLoaded(true);
      }
    };
    loadTrending();
    const id = setInterval(loadTrending, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  // User-facing Strategist status (English). Regular users have no System Info access.
  const [nkrStrategistStatus, setNkrStrategistStatus] = useState(null);
  const [rotationBudgetRelease, setRotationBudgetRelease] = useState("");
  // ENGINE-101: separate draft input for adding capital to an active NKR run.
  // Never bind this field to rotationBudgetRelease, which is the authoritative current total budget.
  const [rotationCapitalTopup, setRotationCapitalTopup] = useState("");
  const [rotationRiskLimit, setRotationRiskLimit] = useState("");
  const [rotationMinNetAdvantage, setRotationMinNetAdvantage] = useState("0.5");
  const [rotationMaxSlippage, setRotationMaxSlippage] = useState("1");
  const [rotationRuntimeHours, setRotationRuntimeHours] = useState("24");
  const [rotationMaxActiveSessions, setRotationMaxActiveSessions] = useState("0");
  // Draft input only: server hydration must not overwrite this while the user is typing.
  const [nkrMaxAssetsEditing, setNkrMaxAssetsEditing] = useState(false);
  const nkrMaxAssetsEditingRef = useRef(false);
  const [rotationAllowDexSpread, setRotationAllowDexSpread] = useState(true);
  const [rotationAllowCexDexSpread, setRotationAllowCexDexSpread] = useState(false);
  const [rotationRouters, setRotationRouters] = useState({ QuickSwap: true, Uniswap: true, PancakeSwap: true, "1inch": true, "0x": false, SushiSwap: false });
  const [rotationSwapModalOpen, setRotationSwapModalOpen] = useState(false);
  const [rotationSwapFromAsset, setRotationSwapFromAsset] = useState("AUTO");
  const [rotationSwapAmount, setRotationSwapAmount] = useState("");
  const [fundingPrompt, setFundingPrompt] = useState(null);
  const [rotationBudgetReleased, setRotationBudgetReleased] = useState(false);
  const [rotationBackendLoading, setRotationBackendLoading] = useState(false);


  const [rotationBackendMsg, setRotationBackendMsg] = useState("");
  const [rotationShadowBusy, setRotationShadowBusy] = useState(false);
  const [rotationShadowSnapshot, setRotationShadowSnapshot] = useState(null);
  const [rotationShadowEvents, setRotationShadowEvents] = useState([]);
  const [rotationShadowEventsOpen, setRotationShadowEventsOpen] = useState(false);
  const [rotationShadowEventsShowAll, setRotationShadowEventsShowAll] = useState(false);
  const [rotationShadowEventsFilter, setRotationShadowEventsFilter] = useState("ALL");
  const [nkrAggressiveConsentOpen, setNkrAggressiveConsentOpen] = useState(false);
  const [nkrAggressivePendingValue, setNkrAggressivePendingValue] = useState("");
  const [nkrAggressiveAcceptedForDraft, setNkrAggressiveAcceptedForDraft] = useState(false);
  const rotationAutoShadowRef = useRef(0);
  // Wallet-bound delete tombstones: prevents a just-deleted Rotation session from
  // being resurrected by a delayed GET/POST sync or by the auto Shadow loop.
  const rotationDeletedSessionIdsRef = useRef(new Set());

  const isRotationSessionRunnable = useCallback((sess, now = Date.now()) => {
    if (!sess || typeof sess !== "object") return false;
    const st = String(sess?.status || "APPROVED").toUpperCase();
    // FINALIZED / STOPPING must never count as runnable (restart after on-chain finalize).
    if (["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "RELEASED", "REBALANCED_OUT", "WAITING_REALLOCATION", "WATCH_POOL", "FINALIZED", "STOPPING", "FINALIZING", "DELETED", "ARCHIVED", "COMPLETE", "COMPLETED", "CANCELLED"].includes(st)) return false;
    const explicitHours = Number(sess?.nkrPeriodHours || sess?.runtimeHours || sess?.meta?.nkr_period_hours || sess?.meta?.runtime_hours || 0);
    const fallbackDays = Number(sess?.periodDays || sess?.nkrPeriodDays || sess?.meta?.nkr_period_days || 0);
    const periodHours = explicitHours > 0 ? explicitHours : (fallbackDays > 0 ? fallbackDays * 24 : normalizeNkrPeriodHours());
    const start = Number(sess?.campaignStartedAt || sess?.meta?.campaign_started_at || sess?.startedAt || sess?.createdAt || 0);
    const periodExpiry = start > 0 ? start + periodHours * 60 * 60 * 1000 : 0;
    const exp = Number(sess?.campaignExpiresAt || sess?.meta?.campaign_expires_at || periodExpiry || sess?.expiresAt || sess?.expires_at || sess?.meta?.expires_at || 0);
    return !exp || exp > now;
  }, [nkrPeriodDays, nkrPeriodUnit, normalizeNkrPeriodHours]);

  // Multi-session support: each later budget approval becomes an independent user-bounded session.
  // Existing sessions are preserved; new Trading/NKR sessions get their own session_id.
  const [tradingSessions, setTradingSessions] = useState([]);
  const [gridEventHistory, setGridEventHistory] = useState([]);
  const [nkrEventHistory, setNkrEventHistory] = useState([]);
  const [traderEventHistory, setTraderEventHistory] = useState([]);
  const [activeTradingSessionId, setActiveTradingSessionId] = useState("");
  const [rotationSessions, setRotationSessions] = useState([]);

  // BUILD443: while an ETH NKR start is pending, stamp frozen config onto matching sessions once.
  const nkrPendingStampSigRef = useRef("");
  useEffect(() => {
    const pending = nkrPendingStartConfigRef.current;
    if (!pending || !pending.chain) return;
    const age = Date.now() - Number(pending.frozenAt || 0);
    if (age > 15 * 60 * 1000) { nkrPendingStartConfigRef.current = null; nkrPendingStampSigRef.current = ""; return; }
    const list = Array.isArray(rotationSessions) ? rotationSessions : [];
    if (!list.length) return;
    const wantMode = String(pending.nkrCapitalMode || "").toUpperCase();
    const need = list.some((s) => {
      const ch = String(s?.chain || s?.meta?.chain || "").toUpperCase();
      if (ch && ch !== String(pending.chain).toUpperCase()) return false;
      const mode = String(s?.nkrCapitalMode || s?.capitalMode || s?.meta?.nkr_capital_mode || "").toUpperCase();
      const budget = Number(s?.budgetUsd || s?.sessionBudgetUsd || s?.reservedUsd || 0);
      return mode !== wantMode || budget <= 0;
    });
    if (!need) {
      nkrStartPendingRef.current = false;
      return;
    }
    const sig = list.map((s) => `${s?.id}:${s?.nkrCapitalMode}:${s?.budgetUsd}`).join("|") + `|${wantMode}|${pending.budgetUsd}`;
    if (sig === nkrPendingStampSigRef.current) return;
    nkrPendingStampSigRef.current = sig;
    const next = list.map((s) => {
      const ch = String(s?.chain || s?.meta?.chain || s?.meta?.chain_key || "").toUpperCase();
      if (ch && ch !== String(pending.chain).toUpperCase()) return s;
      const oid = String(s?.onchainSessionId || s?.coreVaultSessionId || s?.meta?.onchain_session_id || "");
      if (oid) nkrStartSettingsCacheRef.current[`OID:${oid}`] = { ...pending };
      const hasBudget = Number(s?.budgetUsd || s?.sessionBudgetUsd || s?.reservedUsd || 0) > 0;
      return {
        ...s,
        nkrCapitalMode: wantMode || s?.nkrCapitalMode,
        capitalMode: wantMode || s?.capitalMode,
        nkrObservationWindow: s?.nkrObservationWindow || pending.nkrObservationWindow,
        nkrProfitMode: s?.nkrProfitMode || pending.nkrProfitMode,
        nkrPeriodHours: s?.nkrPeriodHours || pending.nkrPeriodHours,
        nkrPeriodDays: s?.nkrPeriodDays || pending.nkrPeriodDays,
        nkrPeriodUnit: s?.nkrPeriodUnit || pending.nkrPeriodUnit,
        budgetUsd: hasBudget ? s.budgetUsd : pending.budgetUsd,
        sessionBudgetUsd: Number(s?.sessionBudgetUsd || 0) > 0 ? s.sessionBudgetUsd : pending.sessionBudgetUsd,
        reservedUsd: Number(s?.reservedUsd || 0) > 0 ? s.reservedUsd : pending.reservedUsd,
        workingCapitalUsd: Number(s?.workingCapitalUsd || 0) > 0 ? s.workingCapitalUsd : pending.workingCapitalUsd,
        meta: {
          ...(s?.meta && typeof s.meta === "object" ? s.meta : {}),
          nkr_capital_mode: wantMode,
          capital_mode: wantMode,
          nkr_observation_window: pending.nkrObservationWindow,
          nkr_profit_mode: pending.nkrProfitMode,
          nkr_period_hours: pending.nkrPeriodHours,
          nkr_period_days: pending.nkrPeriodDays,
          session_budget_usd: pending.budgetUsd,
        },
      };
    });
    setRotationSessions(next);
    nkrStartPendingRef.current = false;
  }, [rotationSessions]);
  const finalizedNkrStorageKey = useMemo(() => `nexus_nkr_finalized_onchain_v2_${String(resolveWalletAddress(wallet) || wallet || "guest").toLowerCase()}`, [wallet]);
  const readFinalizedNkrIds = useCallback(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(finalizedNkrStorageKey) || "[]");
      return new Set((Array.isArray(arr) ? arr : []).map((x) => String(x)));
    } catch { return new Set(); }
  }, [finalizedNkrStorageKey]);
  useEffect(() => {
    const sessions = Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [];
    if (!sessions.length) return;
    const chainFromPreviewRow = (row) => {
      const id = Number(row?.chainId || row?.chain_id || 0);
      if (id === 1) return "ETH";
      if (id === 56) return "BNB";
      if (id === 137) return "POL";
      const k = String(row?.chain || row?.chainKey || "").toUpperCase();
      if (k === "ETHEREUM") return "ETH";
      if (k === "BSC") return "BNB";
      if (k === "POLYGON" || k === "MATIC") return "POL";
      return k || "ETH";
    };
    const finalized = readFinalizedNkrIds();
    for (const row of sessions) {
      const st = String(row?.statusLabel || row?.status || "").toUpperCase();
      const openCount = Number(row?.openAssetCount ?? row?.open_asset_count ?? 0) || 0;
      const sid = String(row?.sessionId ?? row?.session_id ?? "");
      if (!sid) continue;
      if (["FINALIZED", "COMPLETE", "COMPLETED", "CLOSED"].includes(st) && openCount <= 0) {
        // Chain-scoped key so ETH #1 FINALIZED never hides BNB #1 / POL #1
        finalized.add(`${chainFromPreviewRow(row)}:${sid}`);
      }
    }
    finalized.delete("");
    try { localStorage.setItem(finalizedNkrStorageKey, JSON.stringify(Array.from(finalized))); } catch {}
    if (finalized.size) {
      const rowFinalKey = (row) => {
        const sid = String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.meta?.onchain_session_id ?? row?.meta?.core_vault_session_id ?? "");
        if (!sid) return "";
        const ch = String(row?.chain || row?.chainKey || row?.meta?.chain || row?.chainId || row?.meta?.chain_id || "").toUpperCase();
        const chN = ch === "ETHEREUM" || ch === "1" ? "ETH" : ch === "BSC" || ch === "56" ? "BNB" : ch === "POLYGON" || ch === "MATIC" || ch === "137" ? "POL" : ch;
        return `${chN || "UNK"}:${sid}`;
      };
      setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).filter((row) => {
        const key = rowFinalKey(row);
        return !key || !finalized.has(key);
      }));
      setActiveRotationSessionId((prev) => {
        const row = (Array.isArray(rotationSessions) ? rotationSessions : []).find((x) => String(x?.id || x?.session_id || "") === String(prev || ""));
        const key = row ? rowFinalKey(row) : "";
        return key && finalized.has(key) ? "" : prev;
      });
    }
  }, [coreVaultSessionPreview, finalizedNkrStorageKey, readFinalizedNkrIds]);
  const [nkrExitUiState, setNkrExitUiState] = useState({});
  const [activeRotationSessionId, setActiveRotationSessionId] = useState("");
  const [gridUiHydrated, setGridUiHydrated] = useState(false);
  // Derived identifiers for backend grid endpoints (stable across refreshes)
  const uiChainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
  const activeGridChainKey = useMemo(() => {
    return String(gridChain || DEFAULT_CHAIN || "POL").toUpperCase().trim();
  }, [gridChain]);

  // Trading has its own chain selection. Keep it independent from the wallet
  // and from Grid, but always provide a defined fallback for session hydration.
  const activeTraderChainKey = useMemo(() => {
    return String(liveVaultChainByMode?.trading || activeGridChainKey || DEFAULT_CHAIN || "ETH")
      .toUpperCase()
      .trim();
  }, [liveVaultChainByMode?.trading, activeGridChainKey]);

  // NKR has its own chain selection as well. Keep it independent from the
  // wallet and Grid, and always define it before NKR session hydration runs.
  const activeNkrChainKey = useMemo(() => {
    return String(liveVaultChainByMode?.rotation || activeGridChainKey || DEFAULT_CHAIN || "ETH")
      .toUpperCase()
      .trim();
  }, [liveVaultChainByMode?.rotation, activeGridChainKey]);

  // Keep this directly after activeGridChainKey. Several Trading/Rotation/Grid
  // preflight builders use the native chain symbol as a safe fallback, so it
  // must be initialized before those callbacks are created.
  const activeGridChainSymbol = useMemo(() => {
    return String(activeGridChainKey || DEFAULT_CHAIN).toUpperCase();
  }, [activeGridChainKey]);

  const gridItemId = useMemo(() => {
    const sym = String(gridItem || "").toUpperCase().trim();
    if (!sym) return "";
    return `${activeGridChainKey}:${sym}`;
  }, [activeGridChainKey, gridItem]);

  // Nexus backend bridge: load Vault router allowlist + fee policy for the active Grid/Rotation chain.
  // These calls are background checks only; they do not execute trades or touch the Vault.
  useEffect(() => {
    let cancelled = false;

    const chain = String(activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase().trim();

    async function loadNexusBackendBridge() {
      try {
        const routers = await api(`/api/nexus/routers?chain=${encodeURIComponent(chain)}`, { token, wallet });
        if (!cancelled) console.log("NEXUS ROUTERS", routers);
      } catch (e) {
        if (!cancelled) console.warn("NEXUS ROUTERS FAILED", e);
      }

      try {
        const feePolicy = await api(`/api/nexus/fee-policy?chain=${encodeURIComponent(chain)}`, { token, wallet });
        if (!cancelled) console.log("NEXUS FEE POLICY", feePolicy);
      } catch (e) {
        if (!cancelled) console.warn("NEXUS FEE POLICY FAILED", e);
      }
    }

    loadNexusBackendBridge();

    return () => {
      cancelled = true;
    };
  }, [activeGridChainKey, token, wallet]);

  // If the selected grid coin is a native coin, re-read the Vault on that chain after refresh.
  // Important: do NOT mutate wallet chain state here. We only force the vault read itself.
  useEffect(() => {
    const sym = String(gridItem || "").toUpperCase().trim();
    const chain = String(activeGridChainKey || "").toUpperCase().trim();
    if (!wallet || !chain) return;

    // Grid must read the same V5 CoreVault state as NKR and Trader. The legacy
    // /api/vault/state read is retained only as a fallback for older chains.
    const t0 = setTimeout(() => { try { refreshCoreVaultOnchain(chain); } catch (_) {} }, 80);
    const t1 = setTimeout(() => { try { refreshVaultState(chain, { force: true }); } catch (_) {} }, 250);
    const t2 = setTimeout(() => { try { refreshVaultState(chain); } catch (_) {} }, 1200);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
  }, [wallet, gridItem, activeGridChainKey, refreshCoreVaultOnchain]);

const [gridNativeUsd, setGridNativeUsd] = useState({});

// Ensure native coin USD price is available for POL/BNB/ETH even if they are not in the watchlist.
// This fixes Grid "Price: --" when the watch snapshot doesn't include the native symbol.
useEffect(() => {
  const sym = String(gridItem || "").toUpperCase();
  if (!["ETH", "BNB", "POL"].includes(sym)) return;

  (async () => {
    try {
      const px = await fetchNativeUsdPrices([sym]);
      if (px && px[sym] != null) {
        setGridNativeUsd((prev) => ({ ...(prev || {}), [sym]: px[sym] }));
      }
    } catch {
      // ignore
    }
  })();
}, [gridItem]);
  // Grid source: Privy app-wallet holdings across ALL supported chains.
  // Network selector is independent from the currently active Privy/wallet network.
  // First choose the Grid network, then the Coin dropdown only shows assets on that network.
  const gridWalletCoinsByChain = useMemo(() => {
    const map = {};
    const chains = Array.isArray(ENABLED_CHAINS) && ENABLED_CHAINS.length
      ? ENABLED_CHAINS
      : ["POL", "BNB", "ETH"];

    for (const rawChain of chains) {
      const chain = String(rawChain || "").toUpperCase().trim();
      if (!chain) continue;
      const row = balByChain?.[chain] || {};
      const out = [];

      // native coin per supported chain (ETH / POL / BNB)
      out.push(chain);

      // stables present on this chain (USDC/USDT etc.)
      const stablesMap = row?.stables || {};
      for (const k of Object.keys(stablesMap)) out.push(String(k).toUpperCase());

      // user-added tokens on this chain
      const custom = row?.custom || [];
      for (const t of custom) {
        const sym = String(t?.symbol || "").toUpperCase().trim();
        if (sym) out.push(sym);
      }

      const seen = new Set();
      map[chain] = out
        .map((x) => String(x || "").toUpperCase().trim())
        .filter((x) => x && !seen.has(x) && seen.add(x));
    }
    return map;
  }, [balByChain]);

  const gridWalletChains = useMemo(() => {
    const chains = Object.keys(gridWalletCoinsByChain || {}).filter((ck) => (gridWalletCoinsByChain?.[ck] || []).length > 0);
    return chains.length ? chains : [String(DEFAULT_CHAIN || "POL").toUpperCase()];
  }, [gridWalletCoinsByChain]);

  const gridWalletCoins = useMemo(() => {
    const ck = String(activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
    const out = [...(gridWalletCoinsByChain?.[ck] || [])];
    const vaultChain = String(coreVaultOnchain?.chain || coreVaultOnchain?.chainKey || "").toUpperCase();
    if (vaultChain === ck && coreVaultOnchain?.connected) {
      for (const [symbol, state] of Object.entries(coreVaultOnchain?.tokens || {})) {
        const account = state?.account || {};
        const total = Number(account?.baseCapital || 0) + Number(account?.totalSecuredProfit || 0);
        const positionAmount = Number(state?.position?.amount || state?.positionAmount || 0);
        if (total > 0 || positionAmount > 0) out.push(String(symbol || "").toUpperCase());
      }
    }
    return Array.from(new Set(out.map((x) => String(x || "").toUpperCase()).filter(Boolean)));
  }, [gridWalletCoinsByChain, activeGridChainKey, coreVaultOnchain]);

  const handleRotationPickToGrid = useCallback((pick) => {
    const rawSym = String(pick?.sym || pick || "").toUpperCase().trim();
    if (!rawSym) return;

    const coinsByChain = gridWalletCoinsByChain || {};
    const hasCoinOnChain = (chain, coin) => {
      const list = coinsByChain?.[String(chain || "").toUpperCase()] || [];
      return list.map((x) => String(x || "").toUpperCase()).includes(String(coin || "").toUpperCase());
    };

    const chooseFirstAvailable = (options) => {
      for (const opt of options) {
        if (hasCoinOnChain(opt.chain, opt.coin)) return opt;
      }
      return null;
    };

    let resolved = null;
    let note = "";

    if (rawSym === "BTC") {
      resolved = chooseFirstAvailable([
        { chain: "ETH", coin: "WBTC" },
        { chain: "BNB", coin: "BTCB" },
        { chain: "BNB", coin: "WBTC" },
        { chain: "POL", coin: "WBTC" },
      ]);
      note = resolved ? `${rawSym} selected for NKR as ${resolved.coin} on ${resolved.chain}.` : "BTC needs WBTC/BTCB in the Vault wallet first.";
    } else if (rawSym === "SOL") {
      resolved = chooseFirstAvailable([
        { chain: "ETH", coin: "WSOL" },
        { chain: "BNB", coin: "WSOL" },
        { chain: "POL", coin: "WSOL" },
      ]);
      note = resolved ? `${rawSym} selected for NKR as ${resolved.coin} on ${resolved.chain}.` : "SOL needs WSOL in the Vault wallet first.";
    } else {
      for (const chain of Object.keys(coinsByChain || {}).map((x) => String(x || "").toUpperCase())) {
        if (hasCoinOnChain(chain, rawSym)) {
          resolved = { chain, coin: rawSym };
          break;
        }
      }
      note = resolved ? `${rawSym} selected for NKR on ${resolved.chain}.` : `${rawSym} is not available in the Vault wallet assets yet.`;
    }

    setRotationSelectedPick({
      source: rawSym,
      chain: resolved?.chain || "",
      coin: resolved?.coin || "",
      ok: Boolean(resolved),
      note,
      score: Number(pick?.score ?? pick?.strategistScore),
      rank: pick?.rank || "",
      rating: pick?.rating || "",
      change24h: Number(pick?.ch),
      whaleText: pick?.whaleText || "Neutral",
      riskText: pick?.riskText || "Normal",
      ts: Date.now(),
    });
    setRotationBudgetReleased(false);
  }, [gridWalletCoinsByChain]);

  const [manualPayoutAsset, setManualPayoutAsset] = useState("USDC");
  const [strategistRotationCandidates, setStrategistRotationCandidates] = useState([]);

  const releaseRotationBudget = useCallback(async () => {
    const startChainCheck = String(liveVaultChainByMode?.rotation || activeGridChainKey || "ETH").toUpperCase();
    const nowPre = Date.now();
    const alreadyOnChain = (Array.isArray(rotationSessions) ? rotationSessions : []).some((s) => {
      if (!isRotationSessionRunnable(s, nowPre)) return false;
      const ch = String(s?.chain || s?.meta?.chain || s?.meta?.chain_key || "").toUpperCase();
      return ch === startChainCheck || (!ch && startChainCheck === "ETH");
    });
    if (alreadyOnChain) {
      setRotationBackendMsg(`NKR is already running on ${startChainCheck}. Switch the vault chain to start a parallel session.`);
      return;
    }
    // Parallel start: form may show Add Capital (topup). Accept that amount as the new session budget.
    const amount = Number(String(rotationBudgetRelease || rotationCapitalTopup || "").replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setRotationBackendMsg(`Enter a budget amount to start NKR on ${startChainCheck}.`);
      return;
    }
    // BUILD443: freeze mode/period/budget at click time. Never re-read the draft after
    // create returns — form may already be reset to DYNAMIC while ETH job is still pending.
    const startedNkrCapitalMode = String(nkrCapitalModeRef.current || nkrCapitalMode || "DYNAMIC").toUpperCase();
    const startedObservation = String(nkrObservationWindow || "1h");
    const startedProfitMode = String(nkrProfitMode || "REINVEST").toUpperCase();
    const startedPeriodUnit = String(nkrPeriodUnit || "days").toLowerCase();
    const startedPeriodValue = nkrPeriodDays;
    const startedPeriodHours = normalizeNkrPeriodHours();
    const startedPeriodDays = startedPeriodHours / 24;
    const startedMaxAssets = Number(rotationMaxActiveSessions) || 0;
    const startedPayout = String(liveVaultAssetByMode?.rotation || "USDC").toUpperCase();
    // Aggressive always requires a wallet-bound warning acceptance for this draft.
    if (startedNkrCapitalMode === "AGGRESSIVE" && !nkrAggressiveAcceptedForDraft) {
      setNkrAggressivePendingValue("AGGRESSIVE");
      setNkrAggressiveConsentOpen(true);
      setRotationBackendMsg("Confirm the Aggressive risk warning before starting this NKR session.");
      return;
    }
    const frozenStartConfig = {
      nkrCapitalMode: startedNkrCapitalMode,
      capitalMode: startedNkrCapitalMode,
      nkrObservationWindow: startedObservation,
      nkrProfitMode: startedProfitMode,
      nkrPeriodHours: startedPeriodHours,
      nkrPeriodDays: startedPeriodDays,
      nkrPeriodUnit: startedPeriodUnit,
      nkrPeriodValue: startedPeriodValue,
      maxActiveAssets: startedMaxAssets,
      allowedAssets: selectedNkrAssets,
      nkrAllowedAssets: selectedNkrAssets,
      budgetUsd: Number(amount) || 0,
      sessionBudgetUsd: Number(amount) || 0,
      reservedUsd: Number(amount) || 0,
      workingCapitalUsd: Number(amount) || 0,
      payoutAsset: startedPayout,
      chain: startChainCheck,
      frozenAt: Date.now(),
    };
    nkrPendingStartConfigRef.current = frozenStartConfig;
    nkrStartPendingRef.current = true;
    let coreVaultSession = null;
    try {
      setRotationBackendLoading(true);
      setRotationBackendMsg(`Reserving NKR budget on ${startChainCheck} · mode ${startedNkrCapitalMode}...`);
      coreVaultSession = await createCoreVaultSystemSession({
        system: "NKR",
        budgetUsd: amount,
        durationHours: startedPeriodHours,
        maxSlippageBps: Math.round((Number(rotationMaxSlippage) || 1) * 100),
        maxLossBps: Math.round((Number(rotationRiskLimit) || 15) * 100),
        chain: startChainCheck,
        settlementAsset: startedPayout,
        sessionConfig: {
          nkrCapitalMode: startedNkrCapitalMode,
          nkrObservationWindow: startedObservation,
          nkrProfitMode: startedProfitMode,
          nkrPeriodValue: startedPeriodValue,
          nkrPeriodUnit: startedPeriodUnit,
          nkrPeriodHours: startedPeriodHours,
          nkrPeriodDays: startedPeriodDays,
          maxActiveAssets: startedMaxAssets,
          maxCapitalPerAssetPct: 80,
          allowedAssets: selectedNkrAssets,
          nkrAllowedAssets: selectedNkrAssets,
          allowed_assets: selectedNkrAssets,
        },
      });
      if (coreVaultSession?.pending) {
        // ETH async path: keep frozen config until session id appears (poll merges it).
        const jobId = String(coreVaultSession?.jobId || coreVaultSession?.result?.jobId || "");
        if (jobId) nkrStartSettingsCacheRef.current[`JOB:${jobId}`] = frozenStartConfig;
        nkrStartSettingsCacheRef.current[`CHAIN:${startChainCheck}`] = frozenStartConfig;
        setRotationBudgetReleased(true);
        setRotationBackendMsg(`ETH NKR session starting (${startedNkrCapitalMode}) · waiting for on-chain confirmation. Do not change mode until the session appears.`);
        setRotationCapitalTopup("");
        setRotationBackendLoading(false);
        // Do NOT reset form mode yet — wait until session is visible with stamped mode.
        return;
      }
      setRotationBackendMsg(`NKR started on ${startChainCheck} · ${startedNkrCapitalMode}. Capital reserved · scanning ${startChainCheck}-tradable assets.`);
      setRotationCapitalTopup("");
    } catch (e) {
      const rawMessage = String(e?.message || e || "NKR CoreVault session failed");
      const routeMissing = rawMessage.includes("verified_trade_route_required") || rawMessage.toLowerCase().includes("enabled trade route") || rawMessage.toLowerCase().includes("verified corevault trade route");
      const noFree = /insufficient_free_vault_budget|insufficient free/i.test(rawMessage);
      const privyBlocked = /privy_live_execution_not_ready|privy_not_ready|privy_wallet_mapping/i.test(rawMessage);
      const notConnected = /CoreVault V5 is not connected/i.test(rawMessage);
      let userMessage = toUserFacingMessage(rawMessage, `Could not start NKR on ${startChainCheck}.`);
      if (/privy_insufficient_native_gas/i.test(rawMessage)) {
        userMessage = `Could not start NKR on ${startChainCheck}: not enough native token for gas. Add a small amount of BNB and try again.`;
      } else if (/privy_policy_denied/i.test(rawMessage)) {
        userMessage = `Could not start NKR on ${startChainCheck}: wallet policy blocked the vault transaction.`;
      } else if (/privy_gas_allowance|gas required exceeds allowance/i.test(rawMessage)) {
        userMessage = `Could not start NKR on ${startChainCheck}: gas estimate rejected by the network. Check BNB balance and retry.`;
      } else if (/privy_broadcast_failure|transaction_broadcast_failure/i.test(rawMessage)) {
        userMessage = `Could not start NKR on ${startChainCheck}: broadcast failed. Check BNB for gas and retry.`;
      } else if (userMessage === rawMessage || /corevault|0x|sessionof|privy_rpc_/i.test(userMessage)) {
        // Keep a short technical tail so we can diagnose without opening DevTools
        const tail = rawMessage.replace(/\s+/g, " ").slice(0, 120);
        userMessage = `Could not start NKR on ${startChainCheck}: ${tail}`;
      }
      if (routeMissing) userMessage = `Live NKR on ${startChainCheck} needs an enabled verified TRADE route on that chain (Owner Admin).`;
      else if (/session_budget_limit_exceeded/i.test(rawMessage)) {
        const maxA = e?.data?.maxAmount;
        userMessage = maxA != null
          ? `Vault max session on ${startChainCheck} is $${maxA}. Lower the budget and try again.`
          : `Session budget exceeds vault limit on ${startChainCheck}. Lower the amount and try again.`;
      }
      else if (noFree) userMessage = `Not enough free USDC on ${startChainCheck} vault. Deposit USDC on ${startChainCheck}, then start again.`;
      else if (privyBlocked) userMessage = `Privy live execution is not ready for ${startChainCheck}. Check policy / wallet mapping for that chain.`;
      else if (notConnected) userMessage = `${startChainCheck} CoreVault is not connected. Switch chain in Live Core Vault Capital and wait for LIVE CONNECTED.`;
      setRotationBackendMsg(userMessage);
      setErrorMsg(userMessage);
      setRotationBackendLoading(false);
      return;
    } finally {
      setRotationBackendLoading(false);
    }

    const runtimeHours = normalizeNkrPeriodHours();
    const periodDays = runtimeHours / 24;
    const now = Date.now();

    // Pick the next NKR target from the same pipeline that the Strategist uses.
    // Important: do not create duplicate active sessions for the same target while other
    // Strategist candidates are available. The visible session target must match the
    // candidate that will be sent into Shadow/Vault-preview.
    // Key by chain:symbol so the same display asset can run on ETH and BNB in parallel.
    const activeTargetSet = new Set((Array.isArray(rotationSessions) ? rotationSessions : [])
      .filter((s) => {
        const st = String(s?.status || "").toUpperCase();
        const exp = Number(s?.expiresAt || s?.expires_at || 0);
        return !["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "FINALIZED"].includes(st) && (!exp || exp > now);
      })
      .map((s) => {
        const sym = String(s?.sourceSymbol || s?.symbol || s?.targetAsset || s?.meta?.source_symbol || "").toUpperCase();
        if (!sym) return "";
        const ch = String(s?.chain || s?.meta?.chain || s?.meta?.chain_key || "").toUpperCase() || "ETH";
        return `${ch}:${sym}`;
      })
      .filter(Boolean));

    const normalizeCandidateSym = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
    const candidatePool = [];
    const pushCandidate = (sym, meta = {}) => {
      const raw = normalizeCandidateSym(sym);
      if (!raw) return;
      if (["USDC", "USDT", "USD", "EUR"].includes(raw)) return;
      if (candidatePool.some((c) => c.rawSymbol === raw)) return;
      candidatePool.push({ rawSymbol: raw, ...meta });
    };

    if (rotationSelectedPick?.source || rotationSelectedPick?.coin) {
      pushCandidate(rotationSelectedPick?.source || rotationSelectedPick?.coin, {
        chain: rotationSelectedPick?.chain,
        coin: rotationSelectedPick?.coin,
        score: rotationSelectedPick?.score,
        rank: rotationSelectedPick?.rank,
        source: "selected_rotation_target",
        ok: rotationSelectedPick?.ok,
      });
    }
    for (const cand of Array.isArray(strategistRotationCandidates) ? strategistRotationCandidates : []) {
      if (String(cand?.rank || "").toUpperCase() === "AVOID") continue;
      pushCandidate(cand?.sym || cand?.symbol, {
        chain: cand?.chain,
        coin: cand?.coin,
        score: cand?.score ?? cand?.strategistScore,
        rank: cand?.rank,
        source: "strategist_candidate",
        strategistScore: cand?.strategistScore,
      });
    }
    // NKR starts independently from the optional recommendation list.
    // A recommendation may provide the first candidate, but when none exists the
    // session starts in SCANNING mode and keeps its capital in USDC/USDT until
    // the NKR engine finds an allowed, executable watchlist asset.
    const startChain = String(liveVaultChainByMode?.rotation || activeGridChainKey || DEFAULT_CHAIN || "ETH").toUpperCase();
    const pickedCandidate = candidatePool.find((c) => {
      const ch = String(c.chain || startChain || "ETH").toUpperCase();
      return !activeTargetSet.has(`${ch}:${c.rawSymbol}`) && !activeTargetSet.has(c.rawSymbol);
    }) || {
      rawSymbol: "",
      coin: "",
      chain: startChain,
      source: "autonomous_watchlist_scan",
      rank: "SCANNING",
      ok: true,
    };

    // Session chain is always the vault chain the user selected for this Start — not a foreign candidate chain.
    const candidateChain = startChain;
    const candidateSymbol = String(pickedCandidate.coin || pickedCandidate.rawSymbol || "").toUpperCase();
    const sourceSymbol = String(pickedCandidate.rawSymbol || "").toUpperCase();
    const sessionId = makeNexusSessionId("NKR");
    const expiresAt = now + periodDays * 24 * 60 * 60 * 1000;
    // NKR session state is backend-owned. The browser stores no session snapshot.
    setRotationBudgetReleased(true);
    const startStamp = { ...(nkrPendingStartConfigRef.current || {}), nkrCapitalMode: startedNkrCapitalMode, capitalMode: startedNkrCapitalMode, budgetUsd: Number(amount) || 0, sessionBudgetUsd: Number(amount) || 0, reservedUsd: Number(amount) || 0, workingCapitalUsd: Number(amount) || 0, nkrPeriodHours: Number(runtimeHours) || startedPeriodHours, nkrPeriodDays: Number(periodDays) || startedPeriodDays, nkrPeriodUnit: startedPeriodUnit, nkrPeriodValue: startedPeriodValue, nkrObservationWindow: startedObservation, nkrProfitMode: startedProfitMode, maxActiveAssets: startedMaxAssets, payoutAsset: startedPayout };
    try {
      const liveState = await api(`/api/rotation-sessions?wallet=${encodeURIComponent(String(wallet || ""))}&wallet_address=${encodeURIComponent(String(wallet || ""))}`, { method: "GET", token, wallet });
      const backendSessions = Array.isArray(liveState?.sessions) ? liveState.sessions : [];
      // Stamp cache for every live session that still lacks mode/budget.
      const stamped = backendSessions.map((s) => {
        const oid = String(s?.onchainSessionId || s?.coreVaultSessionId || s?.meta?.onchain_session_id || "");
        if (oid) nkrStartSettingsCacheRef.current[`OID:${oid}`] = { ...startStamp, ...(nkrStartSettingsCacheRef.current[`OID:${oid}`] || {}) };
        const hasMode = !!(s?.nkrCapitalMode || s?.capitalMode || s?.meta?.nkr_capital_mode);
        const hasBudget = Number(s?.budgetUsd || s?.sessionBudgetUsd || s?.reservedUsd || 0) > 0;
        if (hasMode && hasBudget) return s;
        const cached = (oid && nkrStartSettingsCacheRef.current[`OID:${oid}`]) || startStamp;
        return {
          ...s,
          nkrCapitalMode: s?.nkrCapitalMode || cached.nkrCapitalMode,
          capitalMode: s?.capitalMode || cached.capitalMode,
          nkrObservationWindow: s?.nkrObservationWindow || cached.nkrObservationWindow,
          nkrProfitMode: s?.nkrProfitMode || cached.nkrProfitMode,
          nkrPeriodHours: s?.nkrPeriodHours || cached.nkrPeriodHours,
          nkrPeriodDays: s?.nkrPeriodDays || cached.nkrPeriodDays,
          nkrPeriodUnit: s?.nkrPeriodUnit || cached.nkrPeriodUnit,
          nkrPeriodValue: s?.nkrPeriodValue || cached.nkrPeriodValue,
          maxActiveAssets: s?.maxActiveAssets ?? cached.maxActiveAssets,
          budgetUsd: Number(s?.budgetUsd || 0) > 0 ? s.budgetUsd : cached.budgetUsd,
          sessionBudgetUsd: Number(s?.sessionBudgetUsd || 0) > 0 ? s.sessionBudgetUsd : cached.sessionBudgetUsd,
          reservedUsd: Number(s?.reservedUsd || 0) > 0 ? s.reservedUsd : cached.reservedUsd,
          workingCapitalUsd: Number(s?.workingCapitalUsd || 0) > 0 ? s.workingCapitalUsd : cached.workingCapitalUsd,
          meta: {
            ...(s?.meta && typeof s.meta === "object" ? s.meta : {}),
            nkr_capital_mode: s?.meta?.nkr_capital_mode || cached.nkrCapitalMode,
            capital_mode: s?.meta?.capital_mode || cached.capitalMode,
            nkr_observation_window: s?.meta?.nkr_observation_window || cached.nkrObservationWindow,
            nkr_profit_mode: s?.meta?.nkr_profit_mode || cached.nkrProfitMode,
            nkr_period_hours: s?.meta?.nkr_period_hours || cached.nkrPeriodHours,
            nkr_period_days: s?.meta?.nkr_period_days || cached.nkrPeriodDays,
            nkr_period_unit: s?.meta?.nkr_period_unit || cached.nkrPeriodUnit,
            session_budget_usd: Number(s?.meta?.session_budget_usd || 0) > 0 ? s.meta.session_budget_usd : cached.budgetUsd,
          },
        };
      });
      // Also cache by the create response id immediately.
      const createOid = String(coreVaultSession?.sessionId || coreVaultSession?.result?.sessionId || "");
      if (createOid) nkrStartSettingsCacheRef.current[`OID:${createOid}`] = startStamp;
      setRotationSessions(stamped);
      setActiveRotationSessionId(String(liveState?.activeRotationSessionId || ""));
      // Prefer backend controlState. Never force RUNNING just because terminal FINALIZED rows still exist.
      const liveOk = backendSessions.some((s) => !["STOPPED","FINALIZED","CLOSED","EXPIRED","CANCELLED","RELEASED","DELETED","ARCHIVED","COMPLETE","COMPLETED","STOPPING","FINALIZING"].includes(String(s?.status || "").toUpperCase()));
      const ctrl = String(liveState?.controlState || liveState?.nkrControlState || "").toUpperCase();
      setNkrControlState(ctrl || (liveOk ? "RUNNING" : "WAITING"));
    } catch (syncError) {
      console.warn("NKR backend refresh after start failed", syncError);
      setRotationSessions([]);
      setActiveRotationSessionId("");
      setNkrControlState("UNKNOWN");
    }
    const onchainSid = coreVaultSession?.sessionId || coreVaultSession?.result?.sessionId || "";
    setRotationBackendMsg(
      `NKR session on ${startChainCheck} approved ✓${onchainSid ? ` #${onchainSid}` : ` ${sessionId}`}. Period ${periodDays} days · chain-locked scan.`
    );
    // After a successful start: lock Aggressive on the running session (backend stamps
    // mode at ensure_local while UI was still AGGRESSIVE during create/sync above).
    // Reset the form to DYNAMIC so a second Aggressive start always requires the warning again.
    setNkrAggressivePendingValue("");
    setNkrAggressiveConsentOpen(false);
    setNkrAggressiveAcceptedForDraft(false);
    if (String(startedNkrCapitalMode || "").toUpperCase() === "AGGRESSIVE") {
      // Permanent wallet audit: this start is bound to the on-chain session id.
      try {
        if (wallet) {
          await api(`/api/nexus/nkr/aggressive-ack`, {
            method: "POST",
            token,
            wallet,
            body: {
              performance_mode: "AGGRESSIVE",
              accepted: true,
              warning_version: AGGRESSIVE_WARNING_VERSION,
              frontend_build: FRONTEND_BUILD_ID,
              scope: "session_start",
              session_id: String(onchainSid || sessionId || ""),
              budget_usd: Number(amount) || 0,
              chain: startChainCheck,
            },
          });
        }
      } catch (ackErr) {
        console.warn("NKR Aggressive session-start audit failed", ackErr);
      }
    }
    // BUILD439: running session mode is locked in backend start-config.
    // Draft setup always returns to DYNAMIC so the next start requires an explicit choice
    // (and Aggressive re-confirm). Session cards must read session.nkrCapitalMode only.
    nkrCapitalModeRef.current = "DYNAMIC";
    setNkrCapitalMode("DYNAMIC");
    setNkrAggressiveAcceptedForDraft(false);
    try {
      if (wallet) {
        await api('/api/nkr/state', {
          method: 'POST', token, wallet,
          body: {
            nkrCapitalMode: "DYNAMIC",
            nkrObservationWindow,
            nkrProfitMode,
            nkrPeriodValue: nkrPeriodDays,
            nkrPeriodUnit,
            nkrPeriodHours: runtimeHours,
            nkrPeriodDays: periodDays,
            maxActiveAssets: rotationMaxActiveSessions,
          },
        });
      }
    } catch (persistModeErr) {
      console.warn('NKR mode draft persistence failed', persistModeErr);
    }
  }, [rotationBudgetRelease, rotationCapitalTopup, rotationMaxActiveSessions, rotationRuntimeHours, rotationSessions, makeNexusSessionId, setRotationSessions, setActiveRotationSessionId, activeGridChainKey, liveVaultChainByMode, liveVaultAssetByMode, rotationSelectedPick, strategistRotationCandidates, watchRows, gridItem, rotationMode, nkrCapitalMode, nkrObservationWindow, nkrProfitMode, nkrPeriodDays, nkrPeriodUnit, normalizeNkrPeriodHours, rotationNetworkScope, rotationRiskLimit, rotationMinNetAdvantage, rotationMaxSlippage, manualPayoutAsset, wallet, setNkrAggressiveAcceptedForDraft, setNkrAggressivePendingValue, setNkrAggressiveConsentOpen, setNkrCapitalMode, nkrAggressiveAcceptedForDraft, createCoreVaultSystemSession, api, token, isRotationSessionRunnable]);

  const startRotationSafeMode = useCallback(async () => {
    // SAFE MODE only: preview + backend safety check. No swap, no Vault transaction.
    setRotationBackendLoading(true);
    setRotationBackendMsg("");
    try {
      const chain = String(rotationSelectedPick?.chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
      const native = chain === "BNB" ? "BNB" : chain === "ETH" ? "ETH" : "POL";
      const symbol = String(rotationSelectedPick?.coin || rotationSelectedPick?.source || gridItem || native).toUpperCase();
      const activeRotationSession = (Array.isArray(rotationSessions) ? rotationSessions : []).find((s) => String(s?.id || "") === String(activeRotationSessionId || "")) || (Array.isArray(rotationSessions) ? rotationSessions : [])[0] || null;
      let amountUsd = Number(String(rotationBudgetRelease || "").replace(",", "."));
      if (!Number.isFinite(amountUsd) || amountUsd <= 0) amountUsd = Number(activeRotationSession?.budgetUsd || 0);
      if (!Number.isFinite(amountUsd) || amountUsd <= 0) amountUsd = 1; // tiny test amount for preview/check
      const slippageSource = activeRotationSession?.maxSlippagePct ?? activeRotationSession?.meta?.max_slippage_pct ?? rotationMaxSlippage ?? "1";
      const riskLimitSource = activeRotationSession?.riskLimitPct ?? activeRotationSession?.meta?.risk_limit_pct ?? rotationRiskLimit;
      const minNetSource = activeRotationSession?.minNetAdvantagePct ?? activeRotationSession?.meta?.min_net_advantage_pct ?? rotationMinNetAdvantage;
      const slippagePct = Number(String(slippageSource || "1").replace(",", "."));

      const body = {
        wallet,
        chain,
        symbol,
        side: "BUY",
        amountUsd,
        amount_usd: amountUsd,
        tokenIn: native,
        token_in: native,
        tokenOut: symbol,
        token_out: symbol,
        slippageBps: Number.isFinite(slippagePct) ? Math.round(slippagePct * 100) : 100,
        slippage_bps: Number.isFinite(slippagePct) ? Math.round(slippagePct * 100) : 100,
        riskLimitPct: riskLimitSource,
        minNetAdvantagePct: minNetSource,
        safeMode: true,
      };

      const preview = await api("/api/nexus/order-preview", { method: "POST", token, wallet, body });
      console.log("NEXUS ORDER PREVIEW", preview);

      const exec = await api("/api/nexus/execute-plan", {
        method: "POST",
        token,
        wallet,
        body: { wallet, chain, preview, requireVaultBalance: true, safeMode: true },
      });
      console.log("NEXUS EXECUTION CHECK", exec);

      setRotationBackendMsg("Safety check sent ✓");
    } catch (e) {
      console.error("NEXUS VAULT CHECK FAILED", e);
      setRotationBackendMsg(`Safety check failed: ${e?.message || e}`);
    } finally {
      setRotationBackendLoading(false);
    }
  }, [rotationSelectedPick, activeGridChainKey, gridItem, rotationBudgetRelease, rotationMaxSlippage, rotationRiskLimit, rotationMinNetAdvantage, rotationSessions, activeRotationSessionId, token, wallet]);



  const resetRotationBudgetRelease = useCallback(() => {
    setRotationBudgetReleased(false);
  }, []);

  const toggleRotationRouter = useCallback((router) => {
    setRotationRouters((prev) => ({ ...(prev || {}), [router]: !prev?.[router] }));
    setRotationBudgetReleased(false);
  }, []);

  // Keep selected Grid network valid, independent from active Privy network.
  useEffect(() => {
    if (!gridUiHydrated) return;
    const cur = String(gridChain || "").toUpperCase();
    if (cur && gridWalletChains.includes(cur)) return;
    if (gridWalletChains.length) setGridChain(gridWalletChains[0]);
  }, [gridUiHydrated, gridWalletChains, gridChain]);

  // Keep selected grid coin valid for the selected Grid network.
  useEffect(() => {
    if (!gridUiHydrated) return;
    if (!gridWalletCoins.length) return;
    const cur = String(gridItem || "").toUpperCase();
    if (!cur || !gridWalletCoins.includes(cur)) {
      setGridItem(gridWalletCoins[0]);
    }
  }, [gridUiHydrated, gridWalletCoins, gridItem]);

  // Backend-first: selected Grid chain/item are synchronized via /api/app-state.
  // LocalStorage is not used as an authority here; fallback selection only happens when the server has no value.
  useEffect(() => {
    if (!gridUiHydrated) return;
    const cur = String(gridItem || "").toUpperCase();
    if (cur && gridWalletCoins.includes(cur)) return;
    if (gridWalletCoins.length) setGridItem(gridWalletCoins[0]);
  }, [gridUiHydrated, gridWalletCoins, gridItem]);

  const [gridAutoPath, setGridAutoPath] = useState(true); // V2 -> V3 fallback (EVM)
  const [gridMode, setGridMode] = useState("normal");

  const [tradingBudgetUsd, setTradingBudgetUsd] = useState("");
  const [tradingBudgetSplitInput, setTradingBudgetSplitInput] = useState("");
  const [tradingExecutionQueue, setTradingExecutionQueue] = useState([]);
  const [tradingRuntimeHours, setTradingRuntimeHours] = useState("24");
  const [tradingRuntimeUnit, setTradingRuntimeUnit] = useState("hours");
  const [tradingRuntimeNowMs, setTradingRuntimeNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setTradingRuntimeNowMs(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const [tradingHoldHours, setTradingHoldHours] = useState("1");
  const [tradingAllowedAssets, setTradingAllowedAssets] = useState("");
  const normalizeTradingCsv = useCallback((value) => {
    return String(value || "")
      .split(",")
      .map((x) => String(x || "").trim().toUpperCase())
      .filter(Boolean);
  }, []);
  const traderAssetOptions = useMemo(() => {
    // BUILD395: Allowed assets = Watchlist only (no hardcoded extra tokens).
    // Market-mode items have no chain → eligible on every live vault chain.
    // DEX items only on their contract chain.
    const chain = String(activeGridChainKey || "ETH").toUpperCase();
    const out = new Set();
    const src = Array.isArray(watchItems) && watchItems.length
      ? watchItems
      : (Array.isArray(watchRows) ? watchRows : []);
    for (const row of src) {
      const sym = String(row?.symbol || row?.sym || row?.asset || "").trim().toUpperCase();
      if (!sym) continue;
      const mode = String(row?.mode || "market").toLowerCase();
      const rowChain = String(row?.chain || row?.chain_key || row?.network || "").trim().toUpperCase();
      if (mode === "dex") {
        const rc = rowChain === "ETHEREUM" || rowChain === "1" ? "ETH"
          : rowChain === "BSC" || rowChain === "56" ? "BNB"
          : rowChain === "POLYGON" || rowChain === "MATIC" || rowChain === "137" ? "POL"
          : rowChain;
        if (rc && rc !== chain) continue;
      }
      out.add(sym);
    }
    return Array.from(out).filter(Boolean).sort();
  }, [activeGridChainKey, watchItems, watchRows]);
  const selectedTraderAssets = useMemo(() => normalizeTradingCsv(tradingAllowedAssets), [tradingAllowedAssets]);
  const toggleTraderAsset = useCallback((symbol) => {
    const sym = String(symbol || "").toUpperCase();
    const next = new Set(normalizeTradingCsv(tradingAllowedAssets));
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    setTradingAllowedAssets(Array.from(next).join(","));
  }, [tradingAllowedAssets]);

  const selectedNkrAssets = useMemo(() => normalizeTradingCsv(nkrAllowedAssets), [nkrAllowedAssets, normalizeTradingCsv]);
  const toggleNkrAsset = useCallback((symbol) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    const next = new Set(normalizeTradingCsv(nkrAllowedAssets));
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    setNkrAllowedAssets(Array.from(next).join(","));
    setRotationBudgetReleased(false);
  }, [nkrAllowedAssets, normalizeTradingCsv]);
  const nkrAssetOptions = useMemo(() => {
    const chain = String(liveVaultChainByMode?.rotation || activeGridChainKey || "ETH").toUpperCase();
    const out = new Set();
    const src = Array.isArray(watchItems) && watchItems.length
      ? watchItems
      : (Array.isArray(watchRows) ? watchRows : []);
    for (const row of src) {
      const sym = String(row?.symbol || row?.sym || row?.asset || "").trim().toUpperCase();
      if (!sym) continue;
      const mode = String(row?.mode || "market").toLowerCase();
      const rowChain = String(row?.chain || row?.chain_key || row?.network || "").trim().toUpperCase();
      if (mode === "dex") {
        const rc = rowChain === "ETHEREUM" || rowChain === "1" ? "ETH"
          : rowChain === "BSC" || rowChain === "56" ? "BNB"
          : rowChain === "POLYGON" || rowChain === "MATIC" || rowChain === "137" ? "POL"
          : rowChain;
        if (rc && rc !== chain) continue;
      }
      out.add(sym);
    }
    if (chain === "ETH") out.add("ETH");
    if (chain === "BNB") out.add("BNB");
    if (chain === "POL") out.add("POL");
    return Array.from(out).filter(Boolean).sort();
  }, [liveVaultChainByMode?.rotation, activeGridChainKey, watchItems, watchRows]);

  // Trader chain is derived exclusively from Live Core Vault Capital.
  // There is no second Allowed Chains selector or local chain authority.
  const tradingAllowedChains = String(activeGridChainKey || "ETH").toUpperCase();
  const setTradingAllowedChains = () => {};
  const [tradingRiskMode, setTradingRiskMode] = useState("BALANCED");
  const [tradingCautionDrawdownPct, setTradingCautionDrawdownPct] = useState("0");
  const [tradingHardStopPct, setTradingHardStopPct] = useState("12");
  const [tradingProfitLockPct, setTradingProfitLockPct] = useState("20");
  const [tradingReuseProfitPct, setTradingReuseProfitPct] = useState("0");
  const [tradingMaxCombinedSlots, setTradingMaxCombinedSlots] = useState("2");
  const [tradingMaxSlippagePct, setTradingMaxSlippagePct] = useState("1.2");
  const [tradingMaxTrades, setTradingMaxTrades] = useState("6");
  const [tradingConfidenceMin, setTradingConfidenceMin] = useState("MEDIUM");
  const [tradingStyle, setTradingStyle] = useState("TACTICAL");
  const [tradingStartBusy, setTradingStartBusy] = useState(false);
  const [tradingStopBusy, setTradingStopBusy] = useState(false);
  const [aggressiveRiskConsentOpen, setAggressiveRiskConsentOpen] = useState(false);
  const [aggressiveRiskPendingValue, setAggressiveRiskPendingValue] = useState("");
  const [aggressiveRiskAcceptedForDraft, setAggressiveRiskAcceptedForDraft] = useState(false);
  const aggressiveRiskAcceptedRef = useRef(false);
  // BUILD428: session settings survive queue/on-chain hydration (style/risk/stops).
  const traderSessionSettingsCacheRef = useRef({});

  const [tradingPreparedSetup, setTradingPreparedSetup] = useState(null);
  const [tradingLearningSetups, setTradingLearningSetups] = useState([]);
  const [tradingRiskExpanded, setTradingRiskExpanded] = useState(false);
  const [tradingSetupExpanded, setTradingSetupExpanded] = useState(false);
  const [tradingSessionSlotsExpanded, setTradingSessionSlotsExpanded] = useState(false);
  const [tradingQueueSlotsExpanded, setTradingQueueSlotsExpanded] = useState(false);
  const [expandedTradingSessionSlots, setExpandedTradingSessionSlots] = useState({});
  const [expandedTradingSessionDetails, setExpandedTradingSessionDetails] = useState({});
  const [rotationRecommendationsExpanded, setRotationRecommendationsExpanded] = useState(false);
  const [tradingSessionStatus, setTradingSessionStatus] = useState("PREPARED");
  const [tradingSessionUpdatedTs, setTradingSessionUpdatedTs] = useState(0);

  const tradingHoldStateHydratedRef = useRef(false);
  const tradingRiskRequestRef = useRef({ key: "", ts: 0, inFlight: false });

  const tradingSessionLabel = String(tradingSessionStatus || "PREPARED").toUpperCase();

  const getTradingSlotSessionId = useCallback((slot = {}) => {
    return String(slot?.session_id || slot?.sessionId || slot?.trade_session_id || "").trim();
  }, []);

  const normalizeTradingSessionBaseId = useCallback((sessionId = "") => {
    const raw = String(sessionId || "").trim();
    return raw.includes("::") ? raw.split("::", 1)[0].trim() : raw;
  }, []);

  const tradingSessionIdMatches = useCallback((a = "", b = "") => {
    const aa = String(a || "").trim();
    const bb = String(b || "").trim();
    if (!aa || !bb) return false;
    if (aa === bb) return true;
    return normalizeTradingSessionBaseId(aa) === normalizeTradingSessionBaseId(bb);
  }, [normalizeTradingSessionBaseId]);

  const normalizeTradingRuntimeHours = useCallback((value = tradingRuntimeHours, unit = tradingRuntimeUnit) => {
    const n = Number(String(value ?? "").replace(",", "."));
    const safe = Number.isFinite(n) && n > 0 ? n : 24;
    const u = String(unit || "hours").toLowerCase();
    const hours = u.startsWith("day") || u === "tage" || u === "days" ? safe * 24 : safe;
    return Math.max(1, Math.min(24 * 30, Math.round(hours * 100) / 100));
  }, [tradingRuntimeHours, tradingRuntimeUnit]);

  const normalizeTradingReuseProfitPct = useCallback((value = tradingReuseProfitPct) => {
    const n = Number(String(value ?? "0").replace(",", "."));
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n * 100) / 100));
  }, [tradingReuseProfitPct]);

  const normalizeTradingMaxCombinedSlots = useCallback((value = tradingMaxCombinedSlots) => {
    const n = Number(String(value ?? "2").replace(",", "."));
    if (!Number.isFinite(n)) return 2;
    return Math.max(0, Math.min(3, Math.round(n)));
  }, [tradingMaxCombinedSlots]);

  const getTradingSlotAmountUsd = useCallback((slot = {}) => {
    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
    const candidates = [
      slot.amountUsd,
      slot.amount_usd,
      slot.reserved_capital_usd,
      slot.reservedCapitalUsd,
      slot.budgetUsd,
      meta.amountUsd,
      meta.amount_usd,
      meta.reserved_capital_usd,
      meta.paper_position_usd,
    ];
    for (const value of candidates) {
      const n = Number(String(value ?? "").replace(",", "."));
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  }, []);

  const getTradingSlotCollectedProfitUsd = useCallback((slot = {}) => {
    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
    const candidates = [
      slot.paper_collected_profit_usd,
      slot.collected_profit_usd,
      slot.realized_profit_usd,
      slot.paper_realized_total_usd,
      meta.paper_collected_profit_usd,
      meta.collected_profit_usd,
      meta.realized_profit_usd,
      meta.paper_realized_total_usd,
    ];
    const nums = candidates
      .map((value) => Number(String(value ?? "").replace(",", ".")))
      .filter((n) => Number.isFinite(n));
    if (!nums.length) return 0;
    const nonZero = nums.filter((n) => Math.abs(n) > 0.0001);
    if (!nonZero.length) return 0;
    // Avoid stale top-level 0 hiding the real value inside meta.
    return nonZero.reduce((best, n) => Math.abs(n) > Math.abs(best) ? n : best, nonZero[0]);
  }, []);

  const getTradingSessionProfitUsd = useCallback((sess = {}) => {
    // Backend slot meta is the most reliable source after Shadow recycle:
    // each slot keeps its protected/collected profit separately while the
    // current cycle PnL resets to zero.
    const q = Array.isArray(sess.queue) ? sess.queue : [];
    const queueProfit = Number(q.reduce((sum, slot) => sum + getTradingSlotCollectedProfitUsd(slot), 0).toFixed(4));

    const explicit = [
      sess.collectedProfitUsd,
      sess.collected_profit_usd,
      sess.paperCollectedProfitUsd,
      sess.paper_collected_profit_usd,
      sess.realizedProfitUsd,
      sess.realized_profit_usd,
    ];
    let explicitProfit = 0;
    for (const value of explicit) {
      const n = Number(String(value ?? "").replace(",", "."));
      if (Number.isFinite(n)) {
        explicitProfit = n;
        break;
      }
    }

    // Avoid a stale explicit 0 hiding real collected slot profit.
    if (Math.abs(queueProfit) > 0.0001) return queueProfit;
    return explicitProfit;
  }, [getTradingSlotCollectedProfitUsd]);

  const getTradingSessionBudgetUsd = useCallback((sess = {}) => {
    const explicit = [sess.budgetUsd, sess.approvedBudgetUsd, sess.reservedCapitalUsd, sess.allocatedUsd, sess.amountUsd];
    for (const value of explicit) {
      const n = Number(String(value ?? "").replace(",", "."));
      if (Number.isFinite(n) && n > 0) return n;
    }
    const q = Array.isArray(sess.queue) ? sess.queue : [];
    return q.reduce((sum, slot) => sum + getTradingSlotAmountUsd(slot), 0);
  }, [getTradingSlotAmountUsd]);

  const normalizeTradingAssetToken = useCallback((value = "") => {
    const raw = String(value || "").trim().toUpperCase();
    if (!raw || raw === "ASSET") return "";
    // Some old persisted sessions contain polluted labels like "BNB,POL" or "ETH,POL".
    // For display/control a Trading session must show exactly one primary chain/asset.
    const parts = raw.split(/[\s,|/+]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
    const allowed = ["ETH", "BNB", "POL"];
    return parts.find((x) => allowed.includes(x)) || parts[0] || "";
  }, []);

  const getTradingSlotDisplayAsset = useCallback((slot = {}, fallback = "") => {
    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
    // IMPORTANT: For Trading/Shadow sessions the visible asset is the slot symbol/asset.
    // The chain field can be polluted by older backend/UI state or payout-chain defaults
    // (example: BNB slot with chain=POL). Therefore symbol/asset wins over chain.
    const candidates = [
      slot?.symbol,
      slot?.asset,
      slot?.baseAsset,
      slot?.base_asset,
      meta.symbol,
      meta.asset,
      meta.baseAsset,
      meta.base_asset,
      fallback,
      slot?.chain,
      slot?.chain_key,
      slot?.network,
      meta.chain,
      meta.chain_key,
      meta.network,
    ];
    for (const candidate of candidates) {
      const token = normalizeTradingAssetToken(candidate);
      if (token) return token;
    }
    return "";
  }, [normalizeTradingAssetToken]);

  const getTradingSessionPrimaryAsset = useCallback((sess = {}) => {
    const q = Array.isArray(sess.queue) ? sess.queue : [];
    const counts = new Map();
    const addCount = (value, weight = 1) => {
      const token = normalizeTradingAssetToken(value);
      if (!token) return;
      counts.set(token, (counts.get(token) || 0) + weight);
    };

    // Prefer actual slot symbol/asset. Do NOT let old comma labels or polluted chain fields win.
    q.forEach((slot) => {
      const asset = getTradingSlotDisplayAsset(slot);
      addCount(asset, 10);
    });

    if (counts.size) {
      return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
    }

    const candidates = [
      sess.asset,
      sess.symbol,
      sess.primaryAsset,
      sess.primary_asset,
      ...(Array.isArray(sess.assets) ? sess.assets : []),
      sess.chain,
      sess.chain_key,
      sess.network,
      ...(Array.isArray(sess.chains) ? sess.chains : []),
    ];
    for (const candidate of candidates) {
      const token = normalizeTradingAssetToken(candidate);
      if (token) return token;
    }
    return "ASSET";
  }, [normalizeTradingAssetToken, getTradingSlotDisplayAsset]);

  const getTradingSessionAssets = useCallback((sess = {}) => {
    const primary = getTradingSessionPrimaryAsset(sess);
    return primary && primary !== "ASSET" ? [primary] : [];
  }, [getTradingSessionPrimaryAsset]);

  const getTradingSessionSlotCount = useCallback((sess = {}) => {
    const q = Array.isArray(sess.queue) ? sess.queue : [];
    if (q.length) return q.length;
    const explicit = Number(sess.slots || sess.slotCount || sess.meta?.slotCount || 0);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    try {
      const sid = String(sess?.id || sess?.session_id || "");
      const oid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || 0) || 0;
      const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || "").toUpperCase();
      const cached = traderSessionSettingsCacheRef.current[sid]
        || (oid ? traderSessionSettingsCacheRef.current[`OID:${oid}`] : null)
        || (oid && chain ? traderSessionSettingsCacheRef.current[`${chain}:${oid}`] : null)
        || {};
      const fromCache = Number(cached.slotCount || (Array.isArray(cached.budgetSplits) ? cached.budgetSplits.length : 0) || 0);
      if (fromCache > 0) return fromCache;
    } catch (_) {}
    return 0;
  }, []);

  const getTradingSessionTradeCount = useCallback((sess = {}, slotsOverride = null) => {
    const slots = Array.isArray(slotsOverride) ? slotsOverride : (Array.isArray(sess?.queue) ? sess.queue : []);
    const explicit = Number(
      sess?.tradesExecuted ??
      sess?.trades_executed ??
      sess?.tradeCount ??
      sess?.trade_count ??
      sess?.completedTrades ??
      sess?.completed_trades ??
      0
    );

    const slotCycles = slots.reduce((sum, slot) => {
      const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
      const n = Number(
        slot?.shadow_cycles ??
        meta?.shadow_cycles ??
        slot?.trade_count ??
        meta?.trade_count ??
        slot?.trades_executed ??
        meta?.trades_executed ??
        0
      );
      return sum + (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
    }, 0);

    return Math.max(0, Math.floor(Math.max(Number.isFinite(explicit) ? explicit : 0, slotCycles)));
  }, []);


  const dedupeTradingQueue = useCallback((items = []) => {
    const rows = Array.isArray(items) ? items.filter((x) => x && typeof x === "object") : [];
    const order = [];
    const byKey = new Map();
    const slotNoOf = (value, fallback = "") => {
      const raw = String(value ?? fallback ?? "").trim();
      const m = raw.match(/\d+/);
      return m ? m[0] : raw;
    };

    rows.forEach((slot, idx) => {
      const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
      const sid = String(getTradingSlotSessionId(slot) || meta.session_id || "NO_SESSION").trim() || "NO_SESSION";
      const chain = String(slot?.chain || slot?.chain_key || slot?.network || meta.chain || activeGridChainKey || "NO_CHAIN").toUpperCase().trim();
      const slotNo = slotNoOf(slot?.slot || slot?.slot_id || slot?.slotId || meta.slot, idx + 1);
      const symbol = String(slot?.symbol || slot?.asset || meta.asset || "").toUpperCase().trim();
      const id = String(slot?.id || slot?.queue_id || "").trim();
      const st = String(slot?.status || slot?.state || "WAIT").toUpperCase();
      if (["STOPPED", "CLOSED", "CANCELLED", "EXPIRED", "RELEASED"].includes(st)) return;

      const key = sid !== "NO_SESSION" && slotNo
        ? `session:${sid}|chain:${chain}|slot:${slotNo}`
        : id
          ? `id:${id}`
          : `fallback:${chain}|${symbol}|slot:${slotNo}`;

      const normalized = {
        ...slot,
        status: st,
        state: st,
        chain,
        chain_key: chain,
        session_id: sid !== "NO_SESSION" ? sid : (slot.session_id || ""),
        sessionId: sid !== "NO_SESSION" ? sid : (slot.sessionId || ""),
        slot: slotNo,
        slot_id: slotNo,
        symbol: symbol || slot.symbol || slot.asset || "",
        asset: symbol || slot.asset || slot.symbol || "",
        amountUsd: getTradingSlotAmountUsd(slot),
        reserved_capital_usd: getTradingSlotAmountUsd(slot),
      };

      if (!byKey.has(key)) order.push(key);
      byKey.set(key, normalized);
    });

    return order.map((key) => byKey.get(key)).filter(Boolean);
  }, [getTradingSlotSessionId, activeGridChainKey, getTradingSlotAmountUsd, getTradingSlotDisplayAsset]);

  const buildTradingSessionsFromQueue = useCallback((queue = []) => {
    const rows = Array.isArray(queue) ? queue.filter((x) => x && typeof x === "object") : [];
    const bySession = new Map();
    const slotNoOf = (value, fallback = "") => {
      const raw = String(value ?? fallback ?? "").trim();
      const m = raw.match(/\d+/);
      return m ? m[0] : raw;
    };

    rows.forEach((slot, idx) => {
      const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
      const rawSid = String(getTradingSlotSessionId(slot) || meta.session_id || meta.trade_session_id || "").trim();
      const asset = getTradingSlotDisplayAsset(slot, activeGridChainKey || DEFAULT_CHAIN || "");
      const chain = asset || getTradingSlotDisplayAsset({ chain: slot?.chain || slot?.chain_key || meta.chain }, activeGridChainKey || DEFAULT_CHAIN || "");
      const baseSid = rawSid || `LEGACY-${asset || chain || "ASSET"}`;
      // A historical backend session id may contain slots from multiple assets. Split it here.
      const sid = `${baseSid}::${asset || chain || "ASSET"}`;
      const st = String(slot?.status || slot?.state || "WAIT").toUpperCase();
      if (["STOPPED", "CLOSED", "CANCELLED", "EXPIRED", "RELEASED"].includes(st)) return;
      const amount = getTradingSlotAmountUsd(slot);
      const slotNo = slotNoOf(slot?.slot || slot?.slot_id || meta.slot, idx + 1);
      const existing = bySession.get(sid) || {
        id: sid,
        baseSessionId: baseSid,
        status: "ACTIVE",
        asset: asset || chain || "ASSET",
        symbol: asset || chain || "ASSET",
        chains: asset ? [asset] : (chain ? [chain] : []),
        queue: [],
        approvedBudgetUsd: 0,
        collectedProfitUsd: 0,
        approvedAt: Number(slot?.created_ts || 0) ? Number(slot.created_ts) * 1000 : Date.now(),
        updatedAt: Number(slot?.updated_ts || 0) ? Number(slot.updated_ts) * 1000 : Date.now(),
        restoredFromBackend: true,
        style: String(slot?.style || slot?.trading_style || slot?.strategy || meta.style || meta.trading_style || meta.strategy || "").toUpperCase() || undefined,
        trading_style: String(slot?.trading_style || slot?.style || slot?.strategy || meta.trading_style || meta.style || meta.strategy || "").toUpperCase() || undefined,
        strategy: String(slot?.strategy || slot?.style || slot?.trading_style || meta.strategy || meta.style || meta.trading_style || "").toUpperCase() || undefined,
        riskMode: String(slot?.riskMode || slot?.risk_mode || slot?.trading_risk_mode || meta.riskMode || meta.risk_mode || meta.trading_risk_mode || "").toUpperCase() || undefined,
        risk_mode: String(slot?.risk_mode || slot?.riskMode || slot?.trading_risk_mode || meta.risk_mode || meta.riskMode || meta.trading_risk_mode || "").toUpperCase() || undefined,
        trading_risk_mode: String(slot?.trading_risk_mode || slot?.risk_mode || slot?.riskMode || meta.trading_risk_mode || meta.risk_mode || meta.riskMode || "").toUpperCase() || undefined,
        maxTrades: Number(slot?.maxTrades ?? slot?.max_trades ?? meta.maxTrades ?? meta.max_trades ?? 0) || undefined,
        max_trades: Number(slot?.max_trades ?? slot?.maxTrades ?? meta.max_trades ?? meta.maxTrades ?? 0) || undefined,
        hardStopPct: Number(slot?.hardStopPct ?? slot?.hard_stop_pct ?? meta.hardStopPct ?? meta.hard_stop_pct ?? 0) || undefined,
        hard_stop_pct: Number(slot?.hard_stop_pct ?? slot?.hardStopPct ?? meta.hard_stop_pct ?? meta.hardStopPct ?? 0) || undefined,
        profitLockPct: Number(slot?.profitLockPct ?? slot?.profit_lock_pct ?? meta.profitLockPct ?? meta.profit_lock_pct ?? 0) || undefined,
        profit_lock_pct: Number(slot?.profit_lock_pct ?? slot?.profitLockPct ?? meta.profit_lock_pct ?? meta.profitLockPct ?? 0) || undefined,
        maxSlippagePct: Number(slot?.maxSlippagePct ?? slot?.max_slippage_pct ?? meta.maxSlippagePct ?? meta.max_slippage_pct ?? 0) || undefined,
        max_slippage_pct: Number(slot?.max_slippage_pct ?? slot?.maxSlippagePct ?? meta.max_slippage_pct ?? meta.maxSlippagePct ?? 0) || undefined,
        cautionDrawdownPct: Number(slot?.cautionDrawdownPct ?? slot?.caution_drawdown_pct ?? meta.cautionDrawdownPct ?? meta.caution_drawdown_pct ?? 0) || undefined,
        caution_drawdown_pct: Number(slot?.caution_drawdown_pct ?? slot?.cautionDrawdownPct ?? meta.caution_drawdown_pct ?? meta.cautionDrawdownPct ?? 0) || undefined,
        payoutAsset: String(slot?.payoutAsset || slot?.payout_asset || meta.payoutAsset || meta.payout_asset || "").toUpperCase() || undefined,
        payout_asset: String(slot?.payout_asset || slot?.payoutAsset || meta.payout_asset || meta.payoutAsset || "").toUpperCase() || undefined,
        runtimeHours: Number(slot?.runtime_hours ?? slot?.runtimeHours ?? meta.runtime_hours ?? meta.runtimeHours ?? 0) || undefined,
        runtime_hours: Number(slot?.runtime_hours ?? slot?.runtimeHours ?? meta.runtime_hours ?? meta.runtimeHours ?? 0) || undefined,
        reuseProfitPct: Number(slot?.reuseProfitPct ?? slot?.reuse_profit_pct ?? slot?.profitReusePct ?? slot?.profit_reuse_pct ?? meta.reuseProfitPct ?? meta.reuse_profit_pct ?? meta.profitReusePct ?? meta.profit_reuse_pct ?? 0) || 0,
        reuse_profit_pct: Number(slot?.reuse_profit_pct ?? slot?.reuseProfitPct ?? slot?.profit_reuse_pct ?? slot?.profitReusePct ?? meta.reuse_profit_pct ?? meta.reuseProfitPct ?? meta.profit_reuse_pct ?? meta.profitReusePct ?? 0) || 0,
        profitReusePct: Number(slot?.profitReusePct ?? slot?.profit_reuse_pct ?? slot?.reuseProfitPct ?? slot?.reuse_profit_pct ?? meta.profitReusePct ?? meta.profit_reuse_pct ?? meta.reuseProfitPct ?? meta.reuse_profit_pct ?? 0) || 0,
        profit_reuse_pct: Number(slot?.profit_reuse_pct ?? slot?.profitReusePct ?? slot?.reuse_profit_pct ?? slot?.reuseProfitPct ?? meta.profit_reuse_pct ?? meta.profitReusePct ?? meta.reuse_profit_pct ?? meta.reuseProfitPct ?? 0) || 0,
        maxCombinedSlots: Number(slot?.maxCombinedSlots ?? slot?.max_combined_slots ?? slot?.slotDonorCap ?? slot?.slot_donor_cap ?? meta.maxCombinedSlots ?? meta.max_combined_slots ?? meta.slotDonorCap ?? meta.slot_donor_cap ?? 0) || 0,
        max_combined_slots: Number(slot?.max_combined_slots ?? slot?.maxCombinedSlots ?? slot?.slot_donor_cap ?? slot?.slotDonorCap ?? meta.max_combined_slots ?? meta.maxCombinedSlots ?? meta.slot_donor_cap ?? meta.slotDonorCap ?? 0) || 0,
        slotDonorCap: Number(slot?.slotDonorCap ?? slot?.slot_donor_cap ?? slot?.maxCombinedSlots ?? slot?.max_combined_slots ?? meta.slotDonorCap ?? meta.slot_donor_cap ?? meta.maxCombinedSlots ?? meta.max_combined_slots ?? 0) || 0,
        slot_donor_cap: Number(slot?.slot_donor_cap ?? slot?.slotDonorCap ?? slot?.max_combined_slots ?? slot?.maxCombinedSlots ?? meta.slot_donor_cap ?? meta.slotDonorCap ?? meta.max_combined_slots ?? meta.maxCombinedSlots ?? 0) || 0,
        expiresAt: (Number(slot?.expires_ts ?? slot?.session_expires_ts ?? meta.expires_ts ?? meta.session_expires_ts ?? 0) ? Number(slot?.expires_ts ?? slot?.session_expires_ts ?? meta.expires_ts ?? meta.session_expires_ts) * 1000 : undefined),
        expires_ts: Number(slot?.expires_ts ?? slot?.session_expires_ts ?? meta.expires_ts ?? meta.session_expires_ts ?? 0) || undefined,
      };
      if (asset && !existing.chains.includes(asset)) existing.chains.push(asset);
      const slotReusePct = Number(slot?.reuseProfitPct ?? slot?.reuse_profit_pct ?? slot?.profitReusePct ?? slot?.profit_reuse_pct ?? meta.reuseProfitPct ?? meta.reuse_profit_pct ?? meta.profitReusePct ?? meta.profit_reuse_pct ?? NaN);
      if (Number.isFinite(slotReusePct)) {
        existing.reuseProfitPct = slotReusePct;
        existing.reuse_profit_pct = slotReusePct;
        existing.profitReusePct = slotReusePct;
        existing.profit_reuse_pct = slotReusePct;
      }
      const slotMaxCombined = Number(slot?.maxCombinedSlots ?? slot?.max_combined_slots ?? slot?.slotDonorCap ?? slot?.slot_donor_cap ?? meta.maxCombinedSlots ?? meta.max_combined_slots ?? meta.slotDonorCap ?? meta.slot_donor_cap ?? NaN);
      if (Number.isFinite(slotMaxCombined)) {
        existing.maxCombinedSlots = slotMaxCombined;
        existing.max_combined_slots = slotMaxCombined;
        existing.slotDonorCap = slotMaxCombined;
        existing.slot_donor_cap = slotMaxCombined;
      }
      const slotRuntimeHours = Number(slot?.runtimeHours ?? slot?.runtime_hours ?? meta.runtimeHours ?? meta.runtime_hours ?? NaN);
      if (Number.isFinite(slotRuntimeHours) && slotRuntimeHours > 0) {
        existing.runtimeHours = slotRuntimeHours;
        existing.runtime_hours = slotRuntimeHours;
      }
      const slotStyle = String(slot?.style || slot?.trading_style || slot?.strategy || meta.style || meta.trading_style || meta.strategy || "").toUpperCase();
      if (slotStyle) { existing.style = slotStyle; existing.trading_style = slotStyle; existing.strategy = slotStyle; }
      const slotRiskMode = String(slot?.riskMode || slot?.risk_mode || slot?.trading_risk_mode || meta.riskMode || meta.risk_mode || meta.trading_risk_mode || "").toUpperCase();
      if (slotRiskMode) { existing.riskMode = slotRiskMode; existing.risk_mode = slotRiskMode; existing.trading_risk_mode = slotRiskMode; }
      const slotMaxTrades = Number(slot?.maxTrades ?? slot?.max_trades ?? meta.maxTrades ?? meta.max_trades ?? NaN);
      if (Number.isFinite(slotMaxTrades) && slotMaxTrades > 0) { existing.maxTrades = slotMaxTrades; existing.max_trades = slotMaxTrades; }
      const slotHardStop = Number(slot?.hardStopPct ?? slot?.hard_stop_pct ?? meta.hardStopPct ?? meta.hard_stop_pct ?? NaN);
      if (Number.isFinite(slotHardStop) && slotHardStop > 0) { existing.hardStopPct = slotHardStop; existing.hard_stop_pct = slotHardStop; }
      const slotProfitLock = Number(slot?.profitLockPct ?? slot?.profit_lock_pct ?? meta.profitLockPct ?? meta.profit_lock_pct ?? NaN);
      if (Number.isFinite(slotProfitLock) && slotProfitLock > 0) { existing.profitLockPct = slotProfitLock; existing.profit_lock_pct = slotProfitLock; }
      const slotSlippage = Number(slot?.maxSlippagePct ?? slot?.max_slippage_pct ?? meta.maxSlippagePct ?? meta.max_slippage_pct ?? NaN);
      if (Number.isFinite(slotSlippage) && slotSlippage > 0) { existing.maxSlippagePct = slotSlippage; existing.max_slippage_pct = slotSlippage; }
      const slotCaution = Number(slot?.cautionDrawdownPct ?? slot?.caution_drawdown_pct ?? meta.cautionDrawdownPct ?? meta.caution_drawdown_pct ?? NaN);
      if (Number.isFinite(slotCaution) && slotCaution > 0) { existing.cautionDrawdownPct = slotCaution; existing.caution_drawdown_pct = slotCaution; }
      const slotPayout = String(slot?.payoutAsset || slot?.payout_asset || meta.payoutAsset || meta.payout_asset || "").toUpperCase();
      if (slotPayout) { existing.payoutAsset = slotPayout; existing.payout_asset = slotPayout; }
      const slotExpiresTs = Number(slot?.expires_ts ?? slot?.session_expires_ts ?? meta.expires_ts ?? meta.session_expires_ts ?? NaN);
      if (Number.isFinite(slotExpiresTs) && slotExpiresTs > 0) {
        existing.expires_ts = slotExpiresTs;
        existing.expiresAt = slotExpiresTs * 1000;
      }
      existing.approvedBudgetUsd = Number((Number(existing.approvedBudgetUsd || 0) + amount).toFixed(2));
      existing.collectedProfitUsd = Number((Number(existing.collectedProfitUsd || 0) + getTradingSlotCollectedProfitUsd(slot)).toFixed(4));
      existing.updatedAt = Math.max(Number(existing.updatedAt || 0), Number(slot?.updated_ts || 0) ? Number(slot.updated_ts) * 1000 : Date.now());
      existing.queue.push({
        ...slot,
        session_id: rawSid || baseSid,
        sessionId: rawSid || baseSid,
        trade_session_id: rawSid || baseSid,
        slot: slotNo,
        slot_id: slotNo,
        style: slot?.style || slot?.trading_style || slot?.strategy || meta.style || meta.trading_style || meta.strategy || existing.style,
        trading_style: slot?.trading_style || slot?.style || slot?.strategy || meta.trading_style || meta.style || meta.strategy || existing.trading_style,
        strategy: slot?.strategy || slot?.style || slot?.trading_style || meta.strategy || meta.style || meta.trading_style || existing.strategy,
        riskMode: slot?.riskMode || slot?.risk_mode || slot?.trading_risk_mode || meta.riskMode || meta.risk_mode || meta.trading_risk_mode || existing.riskMode,
        risk_mode: slot?.risk_mode || slot?.riskMode || slot?.trading_risk_mode || meta.risk_mode || meta.riskMode || meta.trading_risk_mode || existing.risk_mode,
        trading_risk_mode: slot?.trading_risk_mode || slot?.risk_mode || slot?.riskMode || meta.trading_risk_mode || meta.risk_mode || meta.riskMode || existing.trading_risk_mode,
        maxTrades: slot?.maxTrades ?? slot?.max_trades ?? meta.maxTrades ?? meta.max_trades ?? existing.maxTrades,
        max_trades: slot?.max_trades ?? slot?.maxTrades ?? meta.max_trades ?? meta.maxTrades ?? existing.max_trades,
        hardStopPct: slot?.hardStopPct ?? slot?.hard_stop_pct ?? meta.hardStopPct ?? meta.hard_stop_pct ?? existing.hardStopPct,
        hard_stop_pct: slot?.hard_stop_pct ?? slot?.hardStopPct ?? meta.hard_stop_pct ?? meta.hardStopPct ?? existing.hard_stop_pct,
        profitLockPct: slot?.profitLockPct ?? slot?.profit_lock_pct ?? meta.profitLockPct ?? meta.profit_lock_pct ?? existing.profitLockPct,
        profit_lock_pct: slot?.profit_lock_pct ?? slot?.profitLockPct ?? meta.profit_lock_pct ?? meta.profitLockPct ?? existing.profit_lock_pct,
        maxSlippagePct: slot?.maxSlippagePct ?? slot?.max_slippage_pct ?? meta.maxSlippagePct ?? meta.max_slippage_pct ?? existing.maxSlippagePct,
        max_slippage_pct: slot?.max_slippage_pct ?? slot?.maxSlippagePct ?? meta.max_slippage_pct ?? meta.maxSlippagePct ?? existing.max_slippage_pct,
        cautionDrawdownPct: slot?.cautionDrawdownPct ?? slot?.caution_drawdown_pct ?? meta.cautionDrawdownPct ?? meta.caution_drawdown_pct ?? existing.cautionDrawdownPct,
        caution_drawdown_pct: slot?.caution_drawdown_pct ?? slot?.cautionDrawdownPct ?? meta.caution_drawdown_pct ?? meta.cautionDrawdownPct ?? existing.caution_drawdown_pct,
        payoutAsset: slot?.payoutAsset || slot?.payout_asset || meta.payoutAsset || meta.payout_asset || existing.payoutAsset,
        payout_asset: slot?.payout_asset || slot?.payoutAsset || meta.payout_asset || meta.payoutAsset || existing.payout_asset,
        status: st,
        state: st,
        symbol: asset || slot?.symbol || slot?.asset || "",
        asset: asset || slot?.asset || slot?.symbol || "",
      });
      bySession.set(sid, existing);
    });

    return Array.from(bySession.values()).sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
  }, [getTradingSlotSessionId, activeGridChainKey, getTradingSlotAmountUsd, getTradingSlotDisplayAsset, getTradingSlotCollectedProfitUsd]);

  // One-time/local cleanup for queues that were polluted by an older Shadow preview merge.
  // This removes duplicated slot cards from localStorage without touching stopped sessions.
  useEffect(() => {
    setTradingExecutionQueue((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const next = dedupeTradingQueue(existing);
      if (next.length === existing.length) return prev;
      return next;
    });
  }, [dedupeTradingQueue, setTradingExecutionQueue]);

  // Backend-first Trading hydration: backend queue/hold/risk state is authoritative across devices.
  // Important: do not lose the session dropdown on refresh. The backend queue is the
  // persisted truth, while tradingSessions is UI metadata reconstructed from that queue.
  useEffect(() => {
    const execQueue = nexusBackendState?.execution?.queue;
    if (Array.isArray(execQueue)) {
      const normalizedQueue = dedupeTradingQueue(execQueue);
      setTradingExecutionQueue(normalizedQueue);

      const restoredSessions = buildTradingSessionsFromQueue(normalizedQueue);
      const restoredOpenSessions = restoredSessions.filter((sess) => {
        const st = String(sess?.status || "").toUpperCase();
        return !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "FINALIZED", "COMPLETED", "COMPLETE", "ARCHIVED"].includes(st);
      });
      if (restoredOpenSessions.length) {
        setTradingSessions((prev) => {
          const prevList = Array.isArray(prev) ? prev : [];
          return restoredOpenSessions.map((sess) => {
            const sid = String(sess?.id || sess?.session_id || "");
            const oid = String(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || "");
            const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || "").toUpperCase();
            const match = prevList.find((p) => {
              const ps = String(p?.id || p?.session_id || "");
              const po = String(p?.onchainSessionId || p?.onchain_session_id || p?.coreVaultSessionId || "");
              const pc = String(p?.chain || p?.chainKey || (Array.isArray(p?.chains) ? p.chains[0] : "") || "").toUpperCase();
              if (oid && po && oid === po) return true;
              if (sid && ps && sid === ps) return true;
              if (chain && pc && chain === pc && !po) return true;
              return false;
            }) || {};
            const style = sess?.style || sess?.trading_style || match?.style || match?.trading_style || match?.strategy;
            const risk = sess?.riskMode || sess?.risk_mode || match?.riskMode || match?.risk_mode;
            return {
              ...match,
              ...sess,
              style: style || undefined,
              trading_style: style || undefined,
              strategy: style || undefined,
              riskMode: risk || undefined,
              risk_mode: risk || undefined,
              trading_risk_mode: risk || undefined,
              maxTrades: sess?.maxTrades ?? sess?.max_trades ?? match?.maxTrades ?? match?.max_trades,
              max_trades: sess?.max_trades ?? sess?.maxTrades ?? match?.max_trades ?? match?.maxTrades,
              hardStopPct: sess?.hardStopPct ?? sess?.hard_stop_pct ?? match?.hardStopPct ?? match?.hard_stop_pct,
              hard_stop_pct: sess?.hard_stop_pct ?? sess?.hardStopPct ?? match?.hard_stop_pct ?? match?.hardStopPct,
              profitLockPct: sess?.profitLockPct ?? sess?.profit_lock_pct ?? match?.profitLockPct ?? match?.profit_lock_pct,
              profit_lock_pct: sess?.profit_lock_pct ?? sess?.profitLockPct ?? match?.profit_lock_pct ?? match?.profitLockPct,
              maxSlippagePct: sess?.maxSlippagePct ?? sess?.max_slippage_pct ?? match?.maxSlippagePct ?? match?.max_slippage_pct,
              max_slippage_pct: sess?.max_slippage_pct ?? sess?.maxSlippagePct ?? match?.max_slippage_pct ?? match?.maxSlippagePct,
              reuseProfitPct: sess?.reuseProfitPct ?? sess?.reuse_profit_pct ?? match?.reuseProfitPct ?? match?.reuse_profit_pct ?? 0,
              meta: { ...(match?.meta || {}), ...(sess?.meta || {}), style, trading_style: style, risk_mode: risk },
            };
          });
        });
        setActiveTradingSessionId((prev) => {
          const current = String(prev || "").trim();
          if (current && restoredOpenSessions.some((sess) => String(sess.id) === current)) return current;
          return String(restoredOpenSessions[0]?.id || "");
        });
      } else {
        // BUILD417: an empty authoritative execution queue must be allowed to clear stale
        // local session cards. A live CoreVault session without slots is supplied separately
        // by openTradingSessions from the on-chain inventory below.
        setTradingSessions((prev) => {
          const rows = Array.isArray(prev) ? prev : [];
          const keep = rows.filter((sess) => {
            const st = String(sess?.status || "").toUpperCase();
            return ["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "FINALIZED", "COMPLETED", "COMPLETE", "ARCHIVED"].includes(st);
          });
          return keep.length === rows.length ? prev : keep;
        });
        setActiveTradingSessionId("");
      }
    }
    const holdStatus = String(nexusBackendState?.hold_state?.status || "").toUpperCase();
    if (holdStatus) setTradingSessionStatus(holdStatus);
  }, [nexusBackendState, dedupeTradingQueue, buildTradingSessionsFromQueue, setTradingExecutionQueue, setTradingSessionStatus]);

  const openTradingSessions = useMemo(() => {
    const terminal = new Set(["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "ARCHIVED", "FINALIZED", "COMPLETED", "COMPLETE", "STOPPING", "CLOSING", "FINALIZING", "EXITING"]);
    const chainName = (row = {}) => String(
      row?.chain || row?.chainKey || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(row?.chainId)] || "")
    ).toUpperCase();
    const onchainId = (row = {}) => {
      const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
      return String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.sessionId ?? meta?.onchain_session_id ?? meta?.onchainSessionId ?? meta?.coreVaultSessionId ?? "").trim();
    };
    const authRows = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
      .filter((row) => String(row?.engine || "").toUpperCase() === "TRADER")
      .filter((row) => {
        const st = String(row?.statusLabel || row?.status || "").toUpperCase();
        const raw = Number(row?.statusId ?? row?.rawStatusId ?? row?.onchainStatusId ?? 0);
        // BUILD431: hide card as soon as on-chain is CLOSING (3) or FINALIZED (4).
        if (raw === 3 || raw === 4) return false;
        if (["CLOSING", "STOPPING", "FINALIZING", "EXITING", "FINALIZED", "COMPLETED", "COMPLETE"].includes(st)) return false;
        return !terminal.has(st);
      });
    const authByKey = new Map();
    authRows.forEach((row) => {
      const oid = onchainId(row);
      const chain = chainName(row);
      if (oid && chain) authByKey.set(`${chain}:${oid}`, row);
    });

    const localSessions = (Array.isArray(tradingSessions) ? tradingSessions : [])
      .filter((s) => !terminal.has(String(s?.status || "").toUpperCase()));
    const localByKey = new Map();
    localSessions.forEach((sess) => {
      const oid = onchainId(sess);
      const chain = chainName(sess);
      if (oid && chain) localByKey.set(`${chain}:${oid}`, sess);
    });

    const merged = [];
    // CoreVault inventory is authoritative for live on-chain Trader sessions. Merge local
    // queue/slot metadata only when it belongs to that exact chain + CoreVault session id.
    authByKey.forEach((row, key) => {
      const local = localByKey.get(key) || {};
      const oid = onchainId(row);
      const chain = chainName(row) || String(activeTraderChainKey || activeGridChainKey || "ETH").toUpperCase();
      const settlementAsset = String(row?.settlementAsset || row?.asset || local?.settlementAsset || local?.asset || (chain === "BNB" ? "BNB" : chain === "POL" ? "POL" : "ETH")).toUpperCase();
      // Prefer exact onchain key match; else same-chain local session (settings often live there).
      const localFallback = localSessions.find((s) => chainName(s) === chain && !terminal.has(String(s?.status || "").toUpperCase())) || {};
      const src = (local && Object.keys(local).length) ? local : localFallback;
      try {
        const localSid = String(src?.id || src?.session_id || local?.id || "");
        const cached = (localSid && traderSessionSettingsCacheRef.current[localSid])
          || traderSessionSettingsCacheRef.current[`${chain}:${oid}`]
          || traderSessionSettingsCacheRef.current[`OID:${oid}`];
        if (cached) {
          traderSessionSettingsCacheRef.current[`${chain}:${oid}`] = { ...cached, onchainSessionId: oid, chain };
          traderSessionSettingsCacheRef.current[`OID:${oid}`] = { ...cached, onchainSessionId: oid, chain };
          if (localSid) traderSessionSettingsCacheRef.current[localSid] = { ...cached, onchainSessionId: oid, chain };
        } else if (src?.style || src?.trading_style) {
          const payload = {
            style: src.style || src.trading_style,
            trading_style: src.trading_style || src.style,
            strategy: src.strategy || src.style,
            riskMode: src.riskMode || src.risk_mode,
            risk_mode: src.risk_mode || src.riskMode,
            maxTrades: src.maxTrades ?? src.max_trades,
            hardStopPct: src.hardStopPct ?? src.hard_stop_pct,
            profitLockPct: src.profitLockPct ?? src.profit_lock_pct,
            maxSlippagePct: src.maxSlippagePct ?? src.max_slippage_pct,
            reuseProfitPct: src.reuseProfitPct ?? src.reuse_profit_pct,
            chain, onchainSessionId: oid,
          };
          traderSessionSettingsCacheRef.current[`${chain}:${oid}`] = payload;
          traderSessionSettingsCacheRef.current[`OID:${oid}`] = payload;
        }
      } catch (_) {}
      merged.push({
        ...src,
        id: String(src?.id || local?.id || local?.session_id || `TRADER-LIVE-${chain}-${oid}`),
        session_id: String(src?.session_id || src?.id || local?.session_id || local?.id || `TRADER-LIVE-${chain}-${oid}`),
        onchainSessionId: oid,
        coreVaultSessionId: oid,
        chain,
        chains: [chain],
        chainId: Number(row?.chainId || src?.chainId || local?.chainId || 0),
        settlementAsset,
        budgetUsd: Number(row?.budget || row?.budgetAmount || src?.budgetUsd || src?.approvedBudgetUsd || local?.budgetUsd || 0),
        approvedBudgetUsd: Number(src?.approvedBudgetUsd || src?.budgetUsd || row?.budget || local?.approvedBudgetUsd || 0),
        status: String(row?.statusLabel || row?.status || src?.status || local?.status || "ACTIVE").toUpperCase(),
        style: src?.style || src?.trading_style || src?.strategy || local?.style || undefined,
        trading_style: src?.trading_style || src?.style || src?.strategy || local?.trading_style || undefined,
        strategy: src?.strategy || src?.style || src?.trading_style || local?.strategy || undefined,
        riskMode: src?.riskMode || src?.risk_mode || local?.riskMode || undefined,
        risk_mode: src?.risk_mode || src?.riskMode || local?.risk_mode || undefined,
        trading_risk_mode: src?.trading_risk_mode || src?.risk_mode || local?.trading_risk_mode || undefined,
        maxTrades: src?.maxTrades ?? src?.max_trades ?? local?.maxTrades,
        max_trades: src?.max_trades ?? src?.maxTrades ?? local?.max_trades,
        hardStopPct: src?.hardStopPct ?? src?.hard_stop_pct ?? local?.hardStopPct,
        hard_stop_pct: src?.hard_stop_pct ?? src?.hardStopPct ?? local?.hard_stop_pct,
        profitLockPct: src?.profitLockPct ?? src?.profit_lock_pct ?? local?.profitLockPct,
        profit_lock_pct: src?.profit_lock_pct ?? src?.profitLockPct ?? local?.profit_lock_pct,
        maxSlippagePct: src?.maxSlippagePct ?? src?.max_slippage_pct ?? local?.maxSlippagePct,
        max_slippage_pct: src?.max_slippage_pct ?? src?.maxSlippagePct ?? local?.max_slippage_pct,
        reuseProfitPct: src?.reuseProfitPct ?? src?.reuse_profit_pct ?? local?.reuseProfitPct ?? 0,
        reuse_profit_pct: src?.reuse_profit_pct ?? src?.reuseProfitPct ?? local?.reuse_profit_pct ?? 0,
        meta: { ...(src?.meta || local?.meta || {}), ...(row?.meta || {}), style: src?.style || src?.trading_style, trading_style: src?.trading_style || src?.style, risk_mode: src?.risk_mode || src?.riskMode },
        createdAt: Number(src?.createdAt || local?.createdAt || row?.createdAt || Date.now()),
        updatedAt: Math.max(Number(src?.updatedAt || 0), Number(local?.updatedAt || 0), Date.now()),
        source: "corevault_onchain",
        runtimeBacked: !!(src && Object.keys(src).length),
        exactOnchainControl: true,
        queue: (Array.isArray(src?.queue) && src.queue.length)
          ? src.queue
          : (Array.isArray(local?.queue) && local.queue.length)
            ? local.queue
            : (() => {
                const c = traderSessionSettingsCacheRef.current[`${chain}:${oid}`]
                  || traderSessionSettingsCacheRef.current[`OID:${oid}`]
                  || {};
                const splits = Array.isArray(c.budgetSplits) ? c.budgetSplits : (Array.isArray(c.slotAmounts) ? c.slotAmounts : []);
                if (!splits.length) return [];
                return splits.map((amount, idx) => ({
                  id: `cached-slot-${chain}-${oid}-${idx + 1}`,
                  slot: idx + 1,
                  slot_id: idx + 1,
                  amountUsd: Number(amount) || 0,
                  reserved_capital_usd: Number(amount) || 0,
                  status: "WAIT",
                  state: "WAIT",
                  chain,
                  session_id: String(src?.id || local?.id || `TRADER-LIVE-${chain}-${oid}`),
                  style: c.style,
                  trading_style: c.trading_style || c.style,
                  meta: { ...(c || {}), source: "settings_cache_slots" },
                }));
              })(),
        slots: Number(src?.slots || src?.slotCount || local?.slots || 0) || undefined,
        slotCount: Number(src?.slotCount || src?.slots || local?.slotCount || 0) || undefined,
      });
    });

    // Before the first on-chain inventory refresh, keep local STARTING/CONFIRMING sessions
    // visible. Once a CoreVault id is known, a local card survives only if the authoritative
    // inventory still contains that exact chain + id.
    if (!coreVaultSessionPreview) {
      localSessions.forEach((sess) => merged.push(sess));
    } else {
      localSessions.forEach((sess) => {
        const oid = onchainId(sess);
        if (oid) return; // handled only through authoritative inventory above
        const st = String(sess?.status || "").toUpperCase();
        if (["STARTING", "CREATE_SUBMITTING", "CONFIRMING", "RECOVERING", "RECOVERING_RECEIPT", "START_REQUESTED"].includes(st)) merged.push(sess);
      });
    }

    const byIdentity = new Map();
    merged.forEach((sess, idx) => {
      const oid = onchainId(sess);
      const chain = chainName(sess);
      const key = oid && chain ? `${chain}:${oid}` : String(sess?.id || sess?.session_id || `LOCAL-${idx}`);
      const prev = byIdentity.get(key);
      if (!prev || Number(sess?.updatedAt || 0) >= Number(prev?.updatedAt || 0)) byIdentity.set(key, sess);
    });
    return Array.from(byIdentity.values()).sort((a, b) => Number(b?.updatedAt || 0) - Number(a?.updatedAt || 0));
  }, [tradingSessions, coreVaultSessionPreview, activeTraderChainKey, activeGridChainKey]);

  // BUILD417: backend-first ghost-card reconciliation. Some recovery endpoints stop
  // returning a session after FINALIZED. In that case waiting for a FINALIZED row
  // leaves an ACTIVE card forever. Verify known CoreVault ids per chain; if the
  // authoritative inventory no longer contains that id, retire only that exact card.
  useEffect(() => {
    const live = Array.isArray(openTradingSessions) ? openTradingSessions : [];
    const candidates = live.map((sess) => {
      const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
      const oid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || meta?.onchain_session_id || meta?.coreVaultSessionId || 0) || 0;
      const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || meta?.chain || "").toUpperCase();
      const chainId = Number({ ETH: 1, BNB: 56, POL: 137 }[chain] || 0);
      return { sess, oid, chain, chainId };
    }).filter((x) => x.oid > 0 && x.chainId > 0);
    if (!candidates.length) return;

    let cancelled = false;
    const run = async () => {
      const byChain = new Map();
      candidates.forEach((x) => { if (!byChain.has(x.chainId)) byChain.set(x.chainId, []); byChain.get(x.chainId).push(x); });
      const retired = [];
      for (const [chainId, rows] of byChain.entries()) {
        try {
          const state = await api(`/api/nexus/live-reservation/recover?engine=TRADER&chainId=${chainId}&_ts=${Date.now()}`, { method: "GET", token, wallet });
          if (cancelled || !Array.isArray(state?.sessions)) continue;
          for (const item of rows) {
            const found = state.sessions.find((row) => Number(row?.sessionId || 0) === item.oid);
            const foundStatus = String(found?.statusLabel || found?.status || "").toUpperCase();
            const foundStatusId = Number(found?.statusId ?? found?.rawStatusId ?? 0);
            const terminal = foundStatusId === 4 || ["FINALIZED", "COMPLETED", "COMPLETE", "CLOSED", "CANCELLED", "RELEASED"].includes(foundStatus);
            if (!found || terminal) retired.push(item);
          }
        } catch {}
      }
      if (cancelled || !retired.length) return;
      const retireKeys = new Set(retired.map((x) => `${x.chain}:${x.oid}`));
      const retiredLocalIds = new Set();
      setTradingSessions((prev) => (Array.isArray(prev) ? prev : []).map((sess) => {
        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
        const oid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || meta?.onchain_session_id || meta?.coreVaultSessionId || 0) || 0;
        const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || meta?.chain || "").toUpperCase();
        if (!retireKeys.has(`${chain}:${oid}`)) return sess;
        const localId = String(sess?.id || sess?.session_id || sess?.sessionId || "").trim();
        if (localId) retiredLocalIds.add(localId);
        const now = Date.now();
        return { ...sess, status: "FINALIZED", statusLabel: "FINALIZED", lifecycleState: "FINALIZED", active: false, queue: [], stoppedAt: Number(sess?.stoppedAt || now), closedAt: Number(sess?.closedAt || now), updatedAt: now };
      }));
      if (retiredLocalIds.size) {
        setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => {
          const sid = String(getTradingSlotSessionId(slot) || "").trim();
          return !Array.from(retiredLocalIds).some((rid) => tradingSessionIdMatches(sid, rid));
        }));
        setActiveTradingSessionId((prev) => retiredLocalIds.has(String(prev || "").trim()) ? "" : prev);
        setTradingSessionStatus("PREPARED");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [openTradingSessions, api, token, wallet, setTradingSessions, setTradingExecutionQueue, setActiveTradingSessionId, setTradingSessionStatus, getTradingSlotSessionId, tradingSessionIdMatches]);

  const stoppedTradingSessions = useMemo(() => {
    const sessions = Array.isArray(tradingSessions) ? tradingSessions : [];
    return sessions.filter((sess) => {
      const st = String(sess?.status || "").toUpperCase();
      return ["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "FINALIZED", "COMPLETED", "COMPLETE", "ARCHIVED"].includes(st);
    });
  }, [tradingSessions]);

  // BUILD417: CoreVault terminal state is authoritative for Trader card visibility.
  // Finalized on-chain Trader sessions retire matching local/UI sessions even when
  // wallet app-state still contains an older ACTIVE snapshot. Match by chain + id.
  useEffect(() => {
    const previewRows = Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [];
    if (!previewRows.length) return;
    const chainOf = (row = {}) => String(
      row?.chain || row?.chainKey || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(row?.chainId)] || "")
    ).toUpperCase();
    const terminalTraderKeys = new Set(
      previewRows
        .filter((row) => String(row?.engine || "").toUpperCase() === "TRADER")
        .filter((row) => {
          const label = String(row?.statusLabel || row?.status || "").toUpperCase();
          const rawId = Number(row?.statusId ?? row?.rawStatusId ?? row?.onchainStatusId ?? 0);
          return rawId === 4 || ["FINALIZED", "COMPLETED", "COMPLETE", "CLOSED", "CANCELLED", "RELEASED"].includes(label);
        })
        .map((row) => `${chainOf(row)}:${String(row?.sessionId ?? row?.onchainSessionId ?? row?.coreVaultSessionId ?? "").trim()}`)
        .filter((key) => !key.endsWith(":"))
    );
    if (!terminalTraderKeys.size) return;

    setTradingSessions((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      let changed = false;
      const next = rows.map((sess) => {
        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
        const oid = String(
          sess?.onchainSessionId ?? sess?.onchain_session_id ?? sess?.coreVaultSessionId ??
          meta?.onchain_session_id ?? meta?.onchainSessionId ?? meta?.coreVaultSessionId ?? ""
        ).trim();
        const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || meta?.chain || "").toUpperCase();
        const key = `${chain}:${oid}`;
        if (!oid || !terminalTraderKeys.has(key)) return sess;
        const st = String(sess?.status || "").toUpperCase();
        if (["STOPPED", "FINALIZED", "CLOSED", "COMPLETED", "COMPLETE", "RELEASED", "ARCHIVED"].includes(st) && sess?.active === false) return sess;
        changed = true;
        const now = Date.now();
        return {
          ...sess, status: "FINALIZED", statusLabel: "FINALIZED", lifecycleState: "FINALIZED", active: false, queue: [],
          stoppedAt: Number(sess?.stoppedAt || now), closedAt: Number(sess?.closedAt || now), updatedAt: now,
        };
      });
      return changed ? next : prev;
    });
  }, [coreVaultSessionPreview, setTradingSessions]);

  const selectedTradingSession = useMemo(() => {
    const sessions = Array.isArray(openTradingSessions) ? openTradingSessions : [];
    if (!sessions.length) return null;
    const activeId = String(activeTradingSessionId || "").trim();
    const active = sessions.find((sess) => String(sess?.id || "") === activeId);
    if (active) return active;

    // Prefer a session that belongs to the currently selected Trading/Grid chain.
    // Without this, switching POL/BNB/ETH can show the wrong session or an empty panel.
    const chain = String(activeGridChainKey || DEFAULT_CHAIN || "").toUpperCase().trim();
    const sameChain = chain
      ? sessions.find((sess) => (Array.isArray(sess?.chains) ? sess.chains : [])
          .map((x) => String(x || "").toUpperCase().trim())
          .includes(chain))
      : null;
    return sameChain || sessions[0] || null;
  }, [openTradingSessions, activeTradingSessionId, activeGridChainKey]);

  const selectedTradingSessionId = String(selectedTradingSession?.id || activeTradingSessionId || "").trim();

  const normalizeSessionTimeMs = useCallback((value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n < 1_000_000_000_000 ? n * 1000 : n;
  }, []);

  const formatTradingDuration = useCallback((ms) => {
    const totalMin = Math.max(0, Math.floor(Number(ms || 0) / 60000));
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const minutes = totalMin % 60;
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, []);

  const getTradingSessionTiming = useCallback((sess) => {
    const meta = sess?.meta || {};
    const startedMs = normalizeSessionTimeMs(
      sess?.sessionStartedAt ?? sess?.startedAt ?? sess?.createdAt ?? sess?.approvedAt ??
      sess?.session_started_ts ?? sess?.started_ts ?? sess?.created_ts ??
      meta.sessionStartedAt ?? meta.startedAt ?? meta.createdAt ?? meta.approvedAt ??
      meta.session_started_ts ?? meta.started_ts ?? meta.created_ts
    );
    const expiresMs = normalizeSessionTimeMs(
      sess?.expiresAt ?? sess?.expires_at ?? sess?.sessionExpiresAt ?? sess?.session_expires_ts ?? sess?.expires_ts ??
      meta.expiresAt ?? meta.expires_at ?? meta.sessionExpiresAt ?? meta.session_expires_ts ?? meta.expires_ts
    );
    const now = Number(tradingRuntimeNowMs || Date.now());
    const elapsedMs = startedMs > 0 ? Math.max(0, now - startedMs) : 0;
    const remainingMs = expiresMs > 0 ? Math.max(0, expiresMs - now) : 0;
    return {
      startedMs,
      expiresMs,
      elapsedMs,
      remainingMs,
      elapsedLabel: startedMs > 0 ? formatTradingDuration(elapsedMs) : "—",
      remainingLabel: expiresMs > 0 ? formatTradingDuration(remainingMs) : "—",
      isExpired: expiresMs > 0 && now >= expiresMs,
    };
  }, [normalizeSessionTimeMs, formatTradingDuration, tradingRuntimeNowMs]);

  const getTradingVaultRuntimeLabel = useCallback((sessions = []) => {
    const activeSessions = (Array.isArray(sessions) ? sessions : []).filter((sess) => {
      const st = String(sess?.status || "").toUpperCase();
      return !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "ARCHIVED"].includes(st);
    });
    const startedTimes = activeSessions
      .map((sess) => getTradingSessionTiming(sess).startedMs)
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!startedTimes.length) return "—";
    return formatTradingDuration(Math.max(0, Number(tradingRuntimeNowMs || Date.now()) - Math.min(...startedTimes)));
  }, [getTradingSessionTiming, formatTradingDuration, tradingRuntimeNowMs]);

  const getTradingStrategistRuntimeSnapshot = useCallback((sess = {}, slotsOverride = null, runOverride = null) => {
    const slots = Array.isArray(slotsOverride) ? slotsOverride : (Array.isArray(sess?.queue) ? sess.queue : []);
    const run = runOverride || shadowExecutorState?.last_run || shadowExecutorState?.run || null;
    const summary = run?.summary || shadowExecutorState?.summary || {};
    const runtime = summary?.runtime || shadowExecutorState?.runtime || {};
    const strategist = runtime?.strategist || summary?.strategist || shadowExecutorState?.strategist || {};
    const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};

    const timing = getTradingSessionTiming(sess);
    const runtimeHours = Number(sess?.runtimeHours ?? sess?.runtime_hours ?? meta.runtimeHours ?? meta.runtime_hours ?? runtime?.runtime_hours ?? normalizeTradingRuntimeHours()) || normalizeTradingRuntimeHours();
    const snapshotStyle = String(sess?.style || sess?.trading_style || sess?.strategy || meta.style || meta.trading_style || meta.strategy || tradingStyle || "TACTICAL").toUpperCase();
    const isAggressiveSnapshot = snapshotStyle === "AGGRESSIVE";
    const maxTrades = isAggressiveSnapshot ? 0 : Math.max(1, Math.floor(Number(sess?.maxTrades ?? sess?.max_trades ?? meta.maxTrades ?? meta.max_trades ?? runtime?.max_trades ?? tradingMaxTrades ?? 10) || 10));
    const hardLimit = isAggressiveSnapshot ? 0 : Math.max(maxTrades, Math.floor(Number(strategist?.hard_limit ?? strategist?.hard_trade_limit ?? runtime?.hard_trade_limit ?? (maxTrades + 5)) || (maxTrades + 5)));
    const tradesUsed = getTradingSessionTradeCount(sess, slots);

    const elapsedRatio = runtimeHours > 0
      ? Math.max(0, Math.min(1, Number(timing?.elapsedMs || 0) / (runtimeHours * 3600 * 1000)))
      : 0;
    const activeSlotsForPacing = slots.filter((slot) => String(slot?.status || slot?.state || "WAIT").toUpperCase() === "ACTIVE").length;
    const readySlotsForPacing = slots.filter((slot) => String(slot?.status || slot?.state || "WAIT").toUpperCase() === "READY").length;
    const rawAllowedByTimeFallback = isAggressiveSnapshot ? activeSlotsForPacing + readySlotsForPacing : Math.floor(elapsedRatio * maxTrades + 0.0001);
    const allowedByTimeFallback = isAggressiveSnapshot ? activeSlotsForPacing + readySlotsForPacing : Math.max(
      (elapsedRatio > 0 && readySlotsForPacing > 0) ? 1 : 0,
      Math.min(hardLimit, rawAllowedByTimeFallback)
    );
    const allowedByTime = isAggressiveSnapshot ? (activeSlotsForPacing + readySlotsForPacing) : Math.max(0, Math.floor(Number(
      strategist?.allowed_by_time
      ?? strategist?.allowed_trades_now
      ?? strategist?.paced_soft_allowed
      ?? runtime?.allowed_by_time
      ?? runtime?.allowed_trades_now
      ?? runtime?.paced_soft_allowed
      ?? allowedByTimeFallback
    ) || 0));

    const activeSlots = activeSlotsForPacing;
    const readySlots = readySlotsForPacing;
    const waitSlots = slots.filter((slot) => String(slot?.status || slot?.state || "WAIT").toUpperCase() === "WAIT").length;
    const maxCombined = Math.max(1, Math.floor(Number(sess?.maxCombinedSlots ?? sess?.max_combined_slots ?? sess?.slotDonorCap ?? sess?.slot_donor_cap ?? meta.maxCombinedSlots ?? meta.max_combined_slots ?? meta.slotDonorCap ?? meta.slot_donor_cap ?? runtime?.max_combined_slots ?? normalizeTradingMaxCombinedSlots()) || 1));

    const frontendMarketScore = Number(shadowMarketContext?.market_score ?? shadowMarketContext?.marketScore ?? 50);
    const frontendBreadth = Number(shadowMarketContext?.breadth_pct ?? shadowMarketContext?.breadthPct ?? 50);
    const frontendMcap = Number(shadowMarketContext?.mcap_change_pct ?? shadowMarketContext?.mcapChangePct ?? 0);
    const frontendLiquidity = Number(shadowMarketContext?.vol_cap ?? shadowMarketContext?.volCap ?? 0);
    const frontendRegime = (frontendMarketScore >= 75 && frontendBreadth >= 60 && frontendMcap >= 1.5 && frontendLiquidity >= 3.5)
      ? "STRONG_GREEN"
      : (frontendMarketScore >= 60 && frontendBreadth >= 45 && frontendMcap >= 0.3)
        ? "GREEN"
        : ((frontendMarketScore < 30 && frontendBreadth <= 35) || frontendMcap <= -1.8 || frontendBreadth <= 20)
          ? "RED"
          : "NEUTRAL";
    // ENGINE-095: use backend smoothed/stable regime first. Do not recompute RED/GREEN
    // from local frontend context if the backend already provides a stable regime.
    const regimeRaw = String(
      strategist?.stable_market_regime ?? strategist?.market_regime ?? runtime?.stable_market_regime ?? runtime?.market_regime ?? summary?.stable_market_regime ?? summary?.market_regime ?? frontendRegime ?? "NEUTRAL"
    ).toUpperCase();
    const regime = regimeRaw.includes("STRONG") ? "STRONG_GREEN" : regimeRaw.includes("GREEN") ? "GREEN" : regimeRaw.includes("RED") || regimeRaw.includes("RISK_OFF") ? "RED" : "NEUTRAL";
    const regimeTone = regime === "RED"
      ? { label: "RED / ruhiger", color: "#ff8a8a", bg: "rgba(255,107,107,.08)", border: "rgba(255,107,107,.28)" }
      : regime === "GREEN"
        ? { label: "GREEN / aktiver", color: "#7cf7a2", bg: "rgba(34,197,94,.08)", border: "rgba(34,197,94,.26)" }
        : regime === "STRONG_GREEN"
          ? { label: "STRONG GREEN / aggressiv achtsam", color: "#54f0a4", bg: "rgba(0,255,136,.10)", border: "rgba(0,255,136,.34)" }
          : { label: "NEUTRAL / normal", color: "#ffd166", bg: "rgba(255,209,102,.08)", border: "rgba(255,209,102,.24)" };

    const netEdge = Number(strategist?.net_edge ?? strategist?.net_expected_profit_pct ?? runtime?.net_edge ?? NaN);
    const minEdge = Number(strategist?.min_edge_required ?? strategist?.min_edge ?? runtime?.min_edge_required ?? NaN);
    const decisionReason = String(strategist?.reason || runtime?.decision_reason || summary?.message || "Strategist waits unless market edge is positive after estimated costs, risk and trade pace.");

    const combineRows = slots
      .filter((slot) => {
        const m = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
        return Number(slot?.combined_slots ?? slot?.combinedSlots ?? m.combined_slots ?? m.combinedSlots ?? 0) > 1 || String(slot?.combine_reason || m.combine_reason || "").trim();
      })
      .map((slot) => {
        const m = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
        return {
          slot: slot?.slot || slot?.slot_id || m.slot || "Slot",
          symbol: slot?.symbol || slot?.asset || m.asset || "ASSET",
          count: Number(slot?.combined_slots ?? slot?.combinedSlots ?? m.combined_slots ?? m.combinedSlots ?? 1) || 1,
          capital: Number(slot?.reserved_capital_usd ?? slot?.amountUsd ?? slot?.amount_usd ?? m.paper_position_usd ?? 0) || 0,
          reason: String(slot?.combine_reason || m.combine_reason || slot?.reason || m.reason || "Highest net edge within Max Combined Slots.")
        };
      });

    return {
      regime,
      regimeTone,
      runtimeHours,
      maxTrades,
      maxTradesLabel: isAggressiveSnapshot ? "No Limit" : String(maxTrades),
      hardLimit,
      hardLimitLabel: isAggressiveSnapshot ? "No Limit" : String(hardLimit),
      pacingMode: isAggressiveSnapshot ? "DISABLED" : "ACTIVE",
      tradesUsed,
      allowedByTime,
      allowedByTimeLabel: isAggressiveSnapshot ? "Unlimited" : String(allowedByTime),
      activeSlots,
      readySlots,
      waitSlots,
      maxCombined,
      netEdge,
      minEdge,
      decisionReason,
      combineRows,
      elapsedPct: Math.round(elapsedRatio * 100),
    };
  }, [shadowExecutorState, getTradingSessionTiming, getTradingSessionTradeCount, normalizeTradingRuntimeHours, normalizeTradingMaxCombinedSlots, tradingMaxTrades]);

  const getTradingSlotDecisionReason = useCallback((slot = {}) => {
    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
    const state = String(slot?.status || slot?.state || "WAIT").toUpperCase();
    const reason = String(
      slot?.decision_reason || slot?.reason || slot?.condition || slot?.shadow_transition?.reason ||
      meta.decision_reason || meta.reason || meta.pause_reason || meta.shadow_reason || ""
    ).trim();
    if (reason) return reason;
    if (state === "WAIT") return "Waiting: net edge is not strong enough after costs, risk and trade pace.";
    const style = String(slot?.style || slot?.trading_style || slot?.strategy || meta.style || meta.trading_style || meta.strategy || "").toUpperCase();
    if (state === "READY" && style === "AGGRESSIVE") return "Aggressive Performance: ready for legacy execution; core safety remains active and trade pacing is disabled.";
    if (state === "READY") return "Ready: setup passed basic checks; Strategist waits for pace/edge confirmation.";
    if (state === "ACTIVE") return "Active: Strategist allocated capital within slot and session limits.";
    if (state === "PROTECT") return "Protect: risk increased; no new add-ons until quality improves.";
    if (state === "SIMULATED_EXIT") return "Exited: paper cycle closed by Strategist decision (Shadow only).";
    return "Strategist state is synced from backend.";
  }, []);



  const updateTradingSessionMeta = useCallback((sessionId, patch = {}) => {
    const sid = String(sessionId || "").trim();
    if (!sid) return;
    setTradingSessions((prev) => (Array.isArray(prev) ? prev : []).map((sess) => {
      if (String(sess?.id || "") !== sid) return sess;
      return { ...sess, ...(patch || {}), updatedAt: Date.now() };
    }));
  }, [setTradingSessions]);

  const selectTradingSession = useCallback((sessionId) => {
    const sid = String(sessionId || "").trim();
    if (!sid) return;
    setActiveTradingSessionId(sid);
  }, [setActiveTradingSessionId]);
  const clampTradingHoldHours = useCallback((value) => {
    const n = Number(String(value ?? "").replace(",", "."));
    if (!Number.isFinite(n)) return 1;
    return Math.max(1, Math.min(12, n));
  }, []);


  const parseTradingBudgetSplits = useCallback((value, totalBudget = tradingBudgetUsd) => {
    const total = Number(String(totalBudget || "").replace(",", "."));
    const raw = String(value || "").trim();
    const nums = raw
      ? raw.split(/[,+;\s]+/)
          .map((x) => Number(String(x || "").replace(",", ".")))
          .filter((n) => Number.isFinite(n) && n > 0)
      : [];

    if (nums.length) return nums.slice(0, 12);

    if (!(total > 0)) return [];
    if (total <= 100) return [Number(total.toFixed(2))];
    if (total <= 250) return [Number((total * 0.5).toFixed(2)), Number((total * 0.25).toFixed(2)), Number((total * 0.25).toFixed(2))];

    const first = Math.min(100, Math.round(total * 0.34));
    const remaining = Math.max(0, total - first);
    const slot = Math.max(25, Math.round(remaining / 4));
    const out = [first];
    let used = first;
    while (used + slot < total && out.length < 6) {
      out.push(slot);
      used += slot;
    }
    const last = Number((total - used).toFixed(2));
    if (last > 0) out.push(last);
    return out;
  }, [tradingBudgetUsd]);

  const buildTradingQueue = useCallback((setup = tradingPreparedSetup, splitsArg = null) => {
    const budgetTotal = Number(String(tradingBudgetUsd || "").replace(",", "."));
    const splitsRaw = Array.isArray(splitsArg) ? splitsArg : parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd);
    const splits = (Array.isArray(splitsRaw) ? splitsRaw : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);

    // If the user clears the trading budget or all slot values, the prepared slot cards
    // must disappear immediately instead of showing the last queue from localStorage.
    if (!(budgetTotal > 0) || !splits.length) return [];

    const base = setup && typeof setup === "object" ? setup : {};
    const confidence = String(base.confidence || tradingConfidenceMin || "MEDIUM").toUpperCase();
    const suitability = String(base.suitability || "MEDIUM").toUpperCase();
    const riskMode = String(base.riskMode || tradingRiskMode || "BALANCED").toUpperCase();
    const style = String(base.style || tradingStyle || "TACTICAL").toUpperCase();

    // Trading must stay usable even when the user did not manually type an asset yet.
    // Fallback order: prepared Strategist setup -> allowed assets -> selected Grid asset -> active chain symbol.
    // This prevents the Prepared/Budget flow from locking itself with an empty queue.
    const symbol = String(
      base.symbol ||
      base.sym ||
      (String(tradingAllowedAssets || "").split(",")[0] || "") ||
      gridItem ||
      activeGridChainSymbol ||
      ""
    ).trim().toUpperCase();

    const priorityBase =
      confidence === "HIGH" ? 80 :
      confidence === "LOW" ? 35 :
      55;

    return splits.map((amount, idx) => {
      let status = "WAIT";
      if (style === "AGGRESSIVE") {
        // Aggressive Performance is a user-confirmed legacy/high-frequency mode.
        // Prepare every slot as READY; backend core safety can still block invalid slots.
        status = "READY";
      } else if (suitability === "LOW" || confidence === "LOW") status = idx === 0 ? "BLOCKED" : "WAIT";
      else if (idx === 0 && ["HIGH", "MEDIUM-HIGH", "MEDIUM"].includes(confidence)) status = "READY";
      else if (idx === 1 && confidence === "HIGH" && riskMode !== "DEFENSIVE") status = "READY";

      const condition =
        status === "READY"
          ? (style === "AGGRESSIVE"
              ? "Aggressive Performance: ready for legacy execution; core safety remains active."
              : "Ready after user approval; execution still requires manual/session control.")
          : status === "BLOCKED"
            ? "Blocked until confidence, liquidity or risk improves."
            : idx === 1
              ? "Wait for confirmation that momentum and liquidity remain stable."
              : "Wait for follow-up confirmation or a cleaner pullback/edge.";

      return {
        id: `slot_${idx + 1}_${Date.now()}`,
        slot: idx + 1,
        amountUsd: Number(Number(amount).toFixed(2)),
        symbol,
        chain: activeGridChainKey,
        chain_key: activeGridChainKey,
        status,
        priority: Math.max(0, Math.min(100, priorityBase - idx * 8)),
        condition,
        confidence,
        suitability,
        riskMode,
        style,
      };
    });
  }, [
    parseTradingBudgetSplits,
    tradingBudgetSplitInput,
    tradingBudgetUsd,
    tradingPreparedSetup,
    tradingConfidenceMin,
    tradingRiskMode,
    tradingStyle,
    tradingAllowedAssets,
    gridItem,
    activeGridChainSymbol,
    activeGridChainKey,
  ]);

  const tradingQueueSummary = useMemo(() => {
    const queue = Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue : [];
    const active = queue.filter((s) => ["ACTIVE", "PROTECT"].includes(String(s.status || "").toUpperCase()));
    const ready = queue.filter((s) => s.status === "READY");
    const blocked = queue.filter((s) => s.status === "BLOCKED");
    const wait = queue.filter((s) => s.status === "WAIT");
    const hold = queue.filter((s) => ["HOLD", "OBSERVE"].includes(String(s.status || "").toUpperCase()));
    const releaseRequired = queue.filter((s) => String(s.status || "").toUpperCase() === "RELEASE_REQUIRED");
    const total = queue.reduce((sum, s) => sum + (Number(s.amountUsd) || 0), 0);
    return { queue, active, ready, blocked, wait, hold, releaseRequired, total };
  }, [tradingExecutionQueue]);

  const tradingVisibleQueueSummary = useMemo(() => {
    const allQueue = Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue : [];
    const sid = String(selectedTradingSessionId || "").trim();
    let queue = [];
    // Composite UI sessions (session_id::ASSET) must use the reconstructed session queue.
    // Filtering only by raw session_id would merge ETH/BNB/POL again.
    if (selectedTradingSession && Array.isArray(selectedTradingSession.queue)) {
      queue = selectedTradingSession.queue;
    } else if (sid) {
      const baseSid = sid.includes("::") ? sid.split("::")[0] : sid;
      queue = allQueue.filter((slot) => String(getTradingSlotSessionId(slot) || "") === baseSid);
    } else {
      queue = allQueue;
    }

    const active = queue.filter((slot) => ["ACTIVE", "PROTECT"].includes(String(slot.status || "").toUpperCase()));
    const ready = queue.filter((slot) => String(slot.status || "").toUpperCase() === "READY");
    const blocked = queue.filter((slot) => String(slot.status || "").toUpperCase() === "BLOCKED");
    const wait = queue.filter((slot) => String(slot.status || "").toUpperCase() === "WAIT");
    const hold = queue.filter((slot) => ["HOLD", "OBSERVE"].includes(String(slot.status || "").toUpperCase()));
    const releaseRequired = queue.filter((slot) => String(slot.status || "").toUpperCase() === "RELEASE_REQUIRED");
    const total = queue.reduce((sum, slot) => sum + (Number(slot.amountUsd) || 0), 0);
    return { queue, active, ready, blocked, wait, hold, releaseRequired, total };
  }, [tradingExecutionQueue, selectedTradingSessionId, selectedTradingSession, getTradingSlotSessionId]);

  const selectedTradingSessionLabel = useMemo(() => {
    const raw = String(selectedTradingSession?.status || "").toUpperCase();
    if (raw) return raw;
    const q = tradingVisibleQueueSummary.queue || [];
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "PROTECT")) return "PROTECT";
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "ACTIVE")) return "ACTIVE";
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "HOLD")) return "HOLD";
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "OBSERVE")) return "OBSERVE";
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "WAIT")) return "WAIT";
    if (q.some((slot) => String(slot.status || "").toUpperCase() === "BLOCKED")) return "WAIT";
    return tradingSessionLabel;
  }, [selectedTradingSession, tradingVisibleQueueSummary.queue, tradingSessionLabel]);

  const refreshTradingQueue = useCallback((setup = tradingPreparedSetup) => {
    const next = buildTradingQueue(setup);
    // Multi-session safety: do not overwrite existing runtime queues from
    // older approved sessions while the user edits the next-budget form.
    setTradingExecutionQueue((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const hasApprovedSessions = Array.isArray(openTradingSessions) && openTradingSessions.length > 0;
      return hasApprovedSessions ? existing : next;
    });
    return next;
  }, [buildTradingQueue, setTradingExecutionQueue, tradingPreparedSetup, openTradingSessions]);

  useEffect(() => {
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    if (["HOLD", "OBSERVE", "RELEASE_REQUIRED"].includes(tradingSessionLabel)) return;
    const budget = Number(String(tradingBudgetUsd || "").replace(",", "."));
    const splits = parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd);
    const hasPositiveSlots = Array.isArray(splits) && splits.some((n) => Number(n) > 0);
    const hasSetup = tradingPreparedSetup && typeof tradingPreparedSetup === "object";
    const hasAssets = String(tradingAllowedAssets || "").trim();

    const hasApprovedSessions = Array.isArray(openTradingSessions) && openTradingSessions.length > 0;

    if (!(budget > 0) || !hasPositiveSlots || (!hasSetup && !hasAssets)) {
      if (!hasApprovedSessions) setTradingExecutionQueue([]);
      return;
    }

    // In multi-session mode, editing the form prepares the next budget only.
    // It must not replace or erase queues from already approved sessions.
    if (hasApprovedSessions) return;

    const t = setTimeout(() => {
      refreshTradingQueue(hasSetup ? tradingPreparedSetup : null);
    }, 200);
    return () => clearTimeout(t);
  }, [
    gridMode,
    tradingSessionLabel,
    tradingBudgetUsd,
    tradingBudgetSplitInput,
    tradingAllowedAssets,
    tradingAllowedChains,
    tradingStyle,
    tradingRiskMode,
    tradingConfidenceMin,
    tradingStyle,
    tradingPreparedSetup,
    refreshTradingQueue,
    parseTradingBudgetSplits,
    setTradingExecutionQueue,
    tradingSessions,
  ]);

  const tradingPreflight = useMemo(() => {
    const budget = Number(String(tradingBudgetUsd || "").replace(",", "."));
    const runtime = Number(String(tradingRuntimeHours || "").replace(",", "."));
    const holdHours = Number(String(tradingHoldHours || "").replace(",", "."));
    const slippage = Number(String(tradingMaxSlippagePct || "").replace(",", "."));
    const maxTrades = Number(String(tradingMaxTrades || "").replace(",", "."));
    const cautionDd = Number(String(tradingCautionDrawdownPct || "").replace(",", "."));
    const hardStop = Number(String(tradingHardStopPct || "").replace(",", "."));
    const profitLock = Number(String(tradingProfitLockPct || "").replace(",", "."));
    // User-entered values stay primary. If fields are still empty, fall back to the
    // currently prepared/selected context so Redeem-Code users are not stuck in PREPARED.
    const rawAssets = normalizeTradingCsv(tradingAllowedAssets);
    const fallbackAsset = String(
      tradingPreparedSetup?.symbol ||
      tradingPreparedSetup?.sym ||
      gridItem ||
      activeGridChainSymbol ||
      ""
    ).trim().toUpperCase();
    const assets = rawAssets.length ? rawAssets : normalizeTradingCsv(fallbackAsset);

    const chains = [String(activeGridChainKey || "ETH").trim().toUpperCase()].filter(Boolean);
    const budgetSplits = parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd);
    const splitTotal = budgetSplits.reduce((sum, n) => sum + (Number(n) || 0), 0);
    const knownChains = new Set([...(gridWalletChains || []), "POL", "BNB", "ETH"].map((x) => String(x || "").toUpperCase()));
    const unsupportedChains = chains.filter((ck) => !knownChains.has(ck));
    const issues = [];

    if (!(budget > 0)) issues.push("budget");
    if (budgetSplits.length && budget > 0 && Math.abs(splitTotal - budget) > Math.max(1, budget * 0.03)) issues.push("split total");
    if (!assets.length) issues.push("asset");
    if (unsupportedChains.length) issues.push(`unsupported chain ${unsupportedChains[0]}`);
    if (!(runtime >= 1 && runtime <= 168)) issues.push("runtime 1-168h");
    if (!(holdHours >= 1 && holdHours <= 12)) issues.push("hold 1-12h");
    if (!Number.isFinite(slippage) || slippage <= 0 || slippage > 5) issues.push("slippage <=5%");
    const aggressivePerformance = String(tradingStyle || "").toUpperCase() === "AGGRESSIVE";
    if (!aggressivePerformance && (!Number.isFinite(maxTrades) || maxTrades < 1 || maxTrades > 50)) issues.push("max trades");
    if (!Number.isFinite(cautionDd) || cautionDd < 0 || cautionDd > 25) issues.push("caution DD");
    if (!Number.isFinite(hardStop) || hardStop < 1 || hardStop > 50) issues.push("hard stop");
    if (!Number.isFinite(profitLock) || profitLock < 0 || profitLock > 80) issues.push("profit lock");
    if (!aggressivePerformance && !["DEFENSIVE", "BALANCED", "DYNAMIC"].includes(String(tradingRiskMode || "").toUpperCase())) issues.push("risk mode");

    return {
      ok: issues.length === 0,
      issues,
      budget,
      runtime,
      holdHours,
      assets,
      chains,
      slippage,
      maxTrades,
      budgetSplits,
      splitTotal,
      cautionDd,
      hardStop,
      profitLock,
      title: issues.length ? `Preflight needs: ${issues.join(", ")}` : "Preflight ready. Nexus Trading can be armed.",
    };
  }, [
    tradingBudgetUsd,
    tradingRuntimeHours,
    tradingHoldHours,
    tradingAllowedAssets,
    tradingAllowedChains,
    tradingStyle,
    tradingRiskMode,
    tradingCautionDrawdownPct,
    tradingHardStopPct,
    tradingProfitLockPct,
    tradingMaxSlippagePct,
    tradingMaxTrades,
    tradingBudgetSplitInput,
    parseTradingBudgetSplits,
    gridWalletChains,
    normalizeTradingCsv,
    tradingPreparedSetup,
    gridItem,
    activeGridChainSymbol,
    activeGridChainKey,
  ]);

  const tradingCanApprove = !!tradingPreflight.ok;
  // One-click Trader flow: the same Start Trading action creates/reserves the
  // CoreVault V5 budget first and starts the backend worker after confirmation.
  const tradingCanStart = !tradingStartBusy && !!wallet && (
    !!tradingPreflight.ok ||
    !!String(selectedTradingSessionId || activeTradingSessionId || "").trim()
  );

  // Controls must be based on the selected Trading session first, not only on
  // temporary Shadow slot states. A Shadow run can promote slots or keep cards
  // in WAIT while the approved session itself is still open/active. If controls
  // follow only slot labels, Pause/Stop can disappear although capital/session
  // control must remain available.
  const selectedTradingSessionStatus = String(selectedTradingSession?.status || "").toUpperCase();
  const selectedTradingSessionIsOpen = !!selectedTradingSession && !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED"].includes(selectedTradingSessionStatus);
  const selectedSessionHasRuntimeQueue = Array.isArray(tradingVisibleQueueSummary?.queue) && tradingVisibleQueueSummary.queue.length > 0;
  const selectedControlStatus = selectedTradingSessionStatus || selectedTradingSessionLabel;

  const tradingCanPause =
    ["ACTIVE", "PROTECT", "READY"].includes(selectedControlStatus) ||
    (selectedTradingSessionIsOpen && selectedSessionHasRuntimeQueue && !["PAUSED", "HOLD", "OBSERVE", "RELEASE_REQUIRED", "STOPPED", "PREPARED"].includes(selectedControlStatus));
  const tradingCanResume = selectedControlStatus === "PAUSED";
  const tradingCanStop =
    ["ARMED", "ACTIVE", "PROTECT", "PAUSED", "WAIT", "READY"].includes(selectedControlStatus) ||
    (selectedTradingSessionIsOpen && selectedSessionHasRuntimeQueue);
  const tradingCanReleaseCapital = ["HOLD", "OBSERVE", "RELEASE_REQUIRED", "STOPPED"].includes(selectedControlStatus);


  /** BUILD461: Live CoreVault sessions must never show Shadow paper cycles (SIMULATED_EXIT). */
  const isTradingSessionLive = useCallback((sess = null) => {
    const s = sess && typeof sess === "object" ? sess : {};
    const meta = s.meta && typeof s.meta === "object" ? s.meta : {};
    const oid = Number(
      s.onchainSessionId ?? s.onchain_session_id ?? s.coreVaultSessionId ??
      meta.onchain_session_id ?? meta.onchainSessionId ?? meta.coreVaultSessionId ?? 0
    ) || 0;
    const mode = String(s.executionMode || s.execution_mode || meta.execution_mode || meta.executionMode || "").toLowerCase();
    if (oid > 0) return true;
    if (mode === "live") return true;
    if (s.liveVaultReady === true || meta.live_vault_ready === true) return true;
    return false;
  }, []);

  const sanitizeLiveTradingSlot = useCallback((slot = {}) => {
    const st = String(slot?.status || slot?.state || "").toUpperCase();
    if (st !== "SIMULATED_EXIT" && st !== "SHADOW_PAPER_EXIT") return slot;
    const meta = { ...(slot?.meta && typeof slot.meta === "object" ? slot.meta : {}) };
    return {
      ...slot,
      status: "WAIT",
      state: "WAIT",
      decision_reason: "Waiting for live entry — paper/shadow exit is not used in Live mode.",
      reason: "Waiting for live entry — paper/shadow exit is not used in Live mode.",
      paper_gross_pnl_usd: 0,
      paper_net_pnl_usd: 0,
      paper_pnl_usd: 0,
      paper_estimated_costs_usd: 0,
      paper_cycle_realized_usd: 0,
      meta: {
        ...meta,
        decision_reason: "Waiting for live entry — paper/shadow exit is not used in Live mode.",
        live_sanitized_from: st,
        paper_gross_pnl_usd: 0,
        paper_net_pnl_usd: 0,
        paper_pnl_usd: 0,
      },
    };
  }, []);

  const applyShadowQueuePreview = useCallback((shadowQueue = [], shadowRun = null) => {
    if (isTradingSessionLive(selectedTradingSession)) {
      return false;
    }
    const previewRows = Array.isArray(shadowQueue) ? shadowQueue.filter((x) => x && typeof x === "object") : [];
    if (!previewRows.length) return false;

    const now = Date.now();
    const runConfig = shadowRun?.config && typeof shadowRun.config === "object" ? shadowRun.config : {};
    const sid = String(runConfig.session_id || shadowRun?.summary?.runtime?.session_id || selectedTradingSessionId || activeTradingSessionId || "").trim();
    const visibleRows = Array.isArray(tradingVisibleQueueSummary?.queue) ? tradingVisibleQueueSummary.queue : [];
    const chain = String(
      runConfig.chain ||
      previewRows.find((r) => String(r?.chain || r?.chain_key || r?.network || "").trim())?.chain ||
      visibleRows.find((r) => String(r?.chain || r?.chain_key || r?.network || "").trim())?.chain ||
      activeGridChainKey ||
      DEFAULT_CHAIN ||
      ""
    ).toUpperCase().trim();
    const runId = String(shadowRun?.run_id || shadowRun?.id || "").trim();
    const slotNoOf = (value, fallback = "") => {
      const raw = String(value ?? fallback ?? "").trim();
      const m = raw.match(/\d+/);
      return m ? m[0] : raw;
    };

    const bySlot = new Map();
    previewRows.forEach((row, idx) => {
      const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
      const rowSession = String(getTradingSlotSessionId(row) || row?.session_id || row?.sessionId || meta.session_id || sid || "").trim();
      const rowChain = String(row?.chain || row?.chain_key || row?.network || meta.chain || chain || "").toUpperCase().trim();
      if (sid && rowSession && rowSession !== sid) return;
      if (chain && rowChain && rowChain !== chain) return;
      const slotNo = slotNoOf(row?.slot || row?.slot_id || row?.slotId || meta.slot, idx + 1);
      if (!slotNo) return;
      const transition = row.shadow_transition && typeof row.shadow_transition === "object" ? row.shadow_transition : {};
      const st = String(row.status || row.state || "WAIT").toUpperCase();
      if (["STOPPED", "CLOSED", "CANCELLED", "EXPIRED", "RELEASED"].includes(st)) return;
      const symbol = String(row.symbol || row.asset || meta.asset || "").toUpperCase();
      const amountUsd = Number(row.amountUsd ?? row.reserved_capital_usd ?? row.amount_usd ?? meta.amountUsd ?? 0);
      const paperCollected = getTradingSlotCollectedProfitUsd(row);
      const nextMeta = {
        ...meta,
        paper_collected_profit_usd: Math.abs(paperCollected) > 0.0001 ? paperCollected : meta.paper_collected_profit_usd,
        collected_profit_usd: Math.abs(paperCollected) > 0.0001 ? paperCollected : meta.collected_profit_usd,
        realized_profit_usd: Math.abs(paperCollected) > 0.0001 ? paperCollected : meta.realized_profit_usd,
        paper_realized_total_usd: Math.abs(paperCollected) > 0.0001 ? paperCollected : meta.paper_realized_total_usd,
      };
      const normalized = {
        ...row,
        meta: nextMeta,
        id: row.id || row.queue_id || `${rowSession || sid || "SESSION"}-${rowChain || chain || "CHAIN"}-${slotNo}-${symbol || "ASSET"}`,
        queue_id: row.queue_id || row.id || `${rowSession || sid || "SESSION"}-${rowChain || chain || "CHAIN"}-${slotNo}-${symbol || "ASSET"}`,
        status: st,
        state: st,
        chain: rowChain || chain,
        chain_key: rowChain || chain,
        session_id: rowSession || sid,
        sessionId: rowSession || sid,
        trade_session_id: rowSession || sid,
        slot: slotNo,
        slot_id: slotNo,
        symbol: symbol || row.symbol || row.asset || "",
        asset: symbol || row.asset || row.symbol || "",
        amountUsd: Number.isFinite(amountUsd) && amountUsd > 0 ? amountUsd : Number(row.amountUsd || row.reserved_capital_usd || 0),
        reserved_capital_usd: Number.isFinite(amountUsd) && amountUsd > 0 ? amountUsd : Number(row.reserved_capital_usd || row.amountUsd || 0),
        priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 0,
        confidence: Number.isFinite(Number(row.confidence)) ? Number(row.confidence) : row.confidence_score,
        confidence_score: Number.isFinite(Number(row.confidence_score)) ? Number(row.confidence_score) : row.confidence,
        risk_score: Number.isFinite(Number(row.risk_score)) ? Number(row.risk_score) : 0,
        condition: String(transition.reason || row.condition || row.reason || "Strategist updated this slot."),
        shadowTransition: transition,
        paper_collected_profit_usd: paperCollected,
        collected_profit_usd: paperCollected,
        realized_profit_usd: paperCollected,
        paper_realized_total_usd: paperCollected,
        shadowLastRunId: runId,
        shadowUpdatedAt: now,
      };
      bySlot.set(String(slotNo), normalized);
    });

    const replacement = Array.from(bySlot.values()).sort((a, b) => Number(a.slot || 0) - Number(b.slot || 0));
    if (!replacement.length) return false;

    setTradingExecutionQueue((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const keep = existing.filter((slot) => {
        const slotSession = String(getTradingSlotSessionId(slot) || "").trim();
        const slotChain = String(slot?.chain || slot?.chain_key || slot?.network || chain || "").toUpperCase().trim();
        if (sid && slotSession === sid) return false;
        if (!sid && chain && slotChain === chain) return false;
        return true;
      });
      return dedupeTradingQueue([...keep, ...replacement]);
    });

    const hasActive = replacement.some((x) => String(x.status || x.state || "").toUpperCase() === "ACTIVE");
    const hasReady = replacement.some((x) => String(x.status || x.state || "").toUpperCase() === "READY");
    const hasPaused = replacement.some((x) => String(x.status || x.state || "").toUpperCase() === "PAUSED");
    const hasProtect = replacement.some((x) => String(x.status || x.state || "").toUpperCase() === "PROTECT");
    const nextSessionStatus = hasPaused ? "PAUSED" : hasProtect ? "PROTECT" : hasActive ? "ACTIVE" : hasReady ? "ACTIVE" : "WAIT";

    setTradingSessionStatus(nextSessionStatus);
    setTradingSessionUpdatedTs(now);
    const replacementCollectedProfitUsd = Number(replacement.reduce((sum, slot) => sum + getTradingSlotCollectedProfitUsd(slot), 0).toFixed(4));
    updateTradingSessionMeta(sid, {
      status: nextSessionStatus,
      collectedProfitUsd: replacementCollectedProfitUsd,
      collected_profit_usd: replacementCollectedProfitUsd,
      paperCollectedProfitUsd: replacementCollectedProfitUsd,
      paper_collected_profit_usd: replacementCollectedProfitUsd,
      shadowLastRunId: runId,
      shadowAppliedAt: now,
      shadowAppliedSlots: replacement.length,
      queue: replacement,
    });
    setTradingPreparedSetup((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const previousSession = base.session && typeof base.session === "object" ? base.session : {};
      return {
        ...base,
        session: {
          ...previousSession,
          status: nextSessionStatus,
          shadowLastRunId: runId,
          shadowAppliedAt: now,
          shadowAppliedSlots: replacement.length,
          executionQueue: replacement,
          userAction: {
            ...(previousSession.userAction || {}),
            shadowPreviewApplied: true,
            sessionId: sid,
          },
          updatedTs: now,
        },
      };
    });

    return true;
  }, [selectedTradingSessionId, activeTradingSessionId, activeGridChainKey, tradingVisibleQueueSummary, getTradingSlotSessionId, getTradingSlotCollectedProfitUsd, dedupeTradingQueue, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, setTradingPreparedSetup]);

  const runShadowExecutorValidation = useCallback(async () => {
    if (!wallet) {
      setShadowExecutorMsg("Wallet not connected.");
      return;
    }
    setShadowExecutorBusy(true);
    setShadowExecutorMsg("");
    try {
      const currentQueue = Array.isArray(tradingVisibleQueueSummary?.queue) ? tradingVisibleQueueSummary.queue : [];
      const body = {
        action: "validate",
        source: "frontend_trading_panel_test_only",
        config: {
          session_id: selectedTradingSessionId || "",
          base_session_id: normalizeTradingSessionBaseId(selectedTradingSessionId || ""),
          chain: activeGridChainKey || gridChain || "",
          runtime_hours: normalizeTradingRuntimeHours(),
          runtime_unit: tradingRuntimeUnit,
          max_trades: sessionMaxTrades,
          maxTrades: sessionMaxTrades,
          style: sessionStyle,
          trading_style: sessionStyle,
          risk_mode: sessionRiskMode,
          riskMode: sessionRiskMode,
          max_slippage_pct: sessionMaxSlippagePct,
          maxSlippagePct: sessionMaxSlippagePct,
          hard_stop_pct: sessionHardStopPct,
          profit_lock_pct: sessionProfitLockPct,
          caution_drawdown_pct: sessionCautionDrawdownPct,
          reuse_profit_pct: normalizeTradingReuseProfitPct(),
          profit_reuse_pct: normalizeTradingReuseProfitPct(),
          max_combined_slots: normalizeTradingMaxCombinedSlots(),
          maxCombinedSlots: normalizeTradingMaxCombinedSlots(),
          market_context: shadowMarketContext,
          marketContext: shadowMarketContext,
          global_market: shadowMarketContext,
          globalMarket: shadowMarketContext,
          // Important: Test is only a read-only validation. It must never rewrite
          // visible slots or clear the live-like Shadow queue.
          persist_state: false,
        },
      };
      if (currentQueue.length) body.queue = currentQueue;
      const res = await api(`/api/nexus/shadow/executor`, { method: "POST", wallet, body });
      const shadowRun = res?.run || null;
      const shadowQueue = Array.isArray(shadowRun?.queue) ? shadowRun.queue : [];
      const summary = shadowRun?.summary || {};
      setShadowExecutorState({ ...(shadowExecutorState || {}), ...(res || {}), last_run: shadowRun, run: shadowRun });
      setShadowExecutorMsg(`Shadow test only: ${summary?.virtual_fills ?? 0} virtual fill(s), ${summary?.virtual_waits ?? 0} waiting. No slot state was changed.`);
    } catch (e) {
      setShadowExecutorMsg(e?.message || "Shadow validation failed.");
    } finally {
      setShadowExecutorBusy(false);
    }
  }, [api, wallet, tradingVisibleQueueSummary, selectedTradingSessionId, activeGridChainKey, gridChain, tradingRuntimeHours, tradingRuntimeUnit, normalizeTradingRuntimeHours, normalizeTradingSessionBaseId, tradingMaxTrades, tradingRiskMode, tradingStyle, tradingMaxSlippagePct, tradingHardStopPct, tradingProfitLockPct, tradingCautionDrawdownPct, normalizeTradingReuseProfitPct, normalizeTradingMaxCombinedSlots, shadowExecutorState]);

  const runShadowRuntimeAction = useCallback(async (action = "tick") => {
    if (!wallet) {
      setShadowExecutorMsg("Wallet not connected.");
      return;
    }
    setShadowExecutorBusy(true);
    setShadowExecutorMsg("");
    try {
      const currentQueue = Array.isArray(tradingVisibleQueueSummary?.queue) ? tradingVisibleQueueSummary.queue : [];
      const sessionMeta = selectedTradingSession && typeof selectedTradingSession === "object" ? selectedTradingSession : {};
      const sessionExpiresTs = Number(sessionMeta.expires_ts || sessionMeta.session_expires_ts || (sessionMeta.expiresAt ? Math.floor(Number(sessionMeta.expiresAt) / 1000) : 0)) || 0;
      const sessionRuntimeHours = Number(sessionMeta.runtime_hours || sessionMeta.runtimeHours || normalizeTradingRuntimeHours()) || normalizeTradingRuntimeHours();
      const sessionReuseProfitPct = Number(sessionMeta.reuseProfitPct ?? sessionMeta.reuse_profit_pct ?? sessionMeta.profitReusePct ?? sessionMeta.profit_reuse_pct ?? normalizeTradingReuseProfitPct()) || 0;
      const sessionMaxCombinedSlots = Number(sessionMeta.maxCombinedSlots ?? sessionMeta.max_combined_slots ?? sessionMeta.slotDonorCap ?? sessionMeta.slot_donor_cap ?? normalizeTradingMaxCombinedSlots()) || 0;
      const sessionStyle = String(sessionMeta.style || sessionMeta.trading_style || sessionMeta.strategy || tradingStyle || "TACTICAL").toUpperCase();
      const isAggressiveSession = sessionStyle === "AGGRESSIVE";
      const sessionRiskMode = isAggressiveSession ? "LEGACY_PROTECTION" : String(sessionMeta.riskMode || sessionMeta.risk_mode || sessionMeta.trading_risk_mode || tradingRiskMode || "BALANCED").toUpperCase();
      const sessionMaxTrades = isAggressiveSession ? 0 : (Number(sessionMeta.maxTrades ?? sessionMeta.max_trades ?? tradingMaxTrades ?? 0) || 0);
      const sessionMaxSlippagePct = Number(sessionMeta.maxSlippagePct ?? sessionMeta.max_slippage_pct ?? tradingMaxSlippagePct ?? 0) || 0;
      const sessionHardStopPct = Number(sessionMeta.hardStopPct ?? sessionMeta.hard_stop_pct ?? tradingHardStopPct ?? 0) || 0;
      const sessionProfitLockPct = Number(sessionMeta.profitLockPct ?? sessionMeta.profit_lock_pct ?? tradingProfitLockPct ?? 0) || 0;
      const sessionCautionDrawdownPct = Number(sessionMeta.cautionDrawdownPct ?? sessionMeta.caution_drawdown_pct ?? tradingCautionDrawdownPct ?? 0) || 0;
      const body = {
        action,
        source: "frontend_shadow_runtime",
        config: {
          action,
          session_id: selectedTradingSessionId || "",
          base_session_id: normalizeTradingSessionBaseId(selectedTradingSessionId || ""),
          chain: activeGridChainKey || gridChain || "",
          runtime_hours: sessionRuntimeHours,
          runtime_unit: tradingRuntimeUnit,
          expires_ts: sessionExpiresTs || undefined,
          session_expires_ts: sessionExpiresTs || undefined,
          max_trades: sessionMaxTrades,
          maxTrades: sessionMaxTrades,
          style: sessionStyle,
          trading_style: sessionStyle,
          risk_mode: sessionRiskMode,
          riskMode: sessionRiskMode,
          max_slippage_pct: sessionMaxSlippagePct,
          maxSlippagePct: sessionMaxSlippagePct,
          hard_stop_pct: sessionHardStopPct,
          profit_lock_pct: sessionProfitLockPct,
          caution_drawdown_pct: sessionCautionDrawdownPct,
          reuse_profit_pct: sessionReuseProfitPct,
          profit_reuse_pct: sessionReuseProfitPct,
          max_combined_slots: sessionMaxCombinedSlots,
          maxCombinedSlots: sessionMaxCombinedSlots,
          market_context: shadowMarketContext,
          marketContext: shadowMarketContext,
          global_market: shadowMarketContext,
          globalMarket: shadowMarketContext,
          persist_state: true,
          run_all_sessions: true,
          runAllSessions: true,
          multi_session_runtime: true,
          multiSessionRuntime: true,
        },
      };
      // Runtime actions are backend-first. Do NOT send the visible frontend queue
      // for start/tick/resume/pause/stop, because it may be stale or locally mapped
      // and can overwrite the authoritative backend queue. The backend may use a
      // body queue only as last-resort seed when its persisted queue is empty.
      if (!['start', 'tick', 'resume', 'pause', 'stop'].includes(String(action || '').toLowerCase()) && currentQueue.length) {
        body.queue = currentQueue;
      }
      const res = await api(`/api/nexus/shadow/executor`, { method: "POST", wallet, body });
      const shadowRun = res?.run || null;
      const shadowQueue = Array.isArray(shadowRun?.queue) ? shadowRun.queue : [];
      const runtimeStatus = String(res?.runtime_status || shadowRun?.summary?.runtime_status || shadowRun?.summary?.runtime?.status || action || "updated").toUpperCase();

      if (String(action || "").toLowerCase() === "stop") {
        const sid = String(selectedTradingSessionId || activeTradingSessionId || "").trim();
        setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => !tradingSessionIdMatches(getTradingSlotSessionId(slot), sid)));
        updateTradingSessionMeta(sid, { status: "STOPPED", stoppedAt: Date.now(), active: false, queue: [] });
        const remainingOpen = (Array.isArray(openTradingSessions) ? openTradingSessions : []).filter((sess) => !tradingSessionIdMatches(sess?.id, sid));
        setActiveTradingSessionId(String(remainingOpen?.[0]?.id || ""));
        setTradingSessionStatus("PREPARED");
        setTradingSessionUpdatedTs(Date.now());
      } else {
        applyShadowQueuePreview(shadowQueue, shadowRun);
      }

      setShadowExecutorState({ ...(shadowExecutorState || {}), ...(res || {}), last_run: shadowRun, run: shadowRun });
      const strategist = shadowRun?.summary?.runtime?.strategist || res?.runtime?.strategist || res?.strategist || {};
      const extra = strategist?.driver ? ` Strategist: ${strategist.active_slots ?? 0} active, ${strategist.ready_slots ?? 0} ready, ${strategist.simulated_exits ?? 0} exited.` : "";
      setShadowExecutorMsg(`Shadow runtime ${runtimeStatus}.${extra} Paper-only; no Vault transaction triggered.`);
      await refreshNexusBackendState();
    } catch (e) {
      setShadowExecutorMsg(e?.message || `Shadow ${action} failed.`);
    } finally {
      setShadowExecutorBusy(false);
    }
  }, [api, wallet, tradingVisibleQueueSummary, selectedTradingSession, selectedTradingSessionId, activeGridChainKey, gridChain, tradingRuntimeHours, tradingRuntimeUnit, normalizeTradingRuntimeHours, normalizeTradingSessionBaseId, tradingSessionIdMatches, tradingMaxTrades, tradingRiskMode, tradingMaxSlippagePct, normalizeTradingReuseProfitPct, normalizeTradingMaxCombinedSlots, shadowExecutorState, applyShadowQueuePreview, refreshNexusBackendState, openTradingSessions, setActiveTradingSessionId, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, getTradingSlotSessionId]);

  useEffect(() => {
    const run = shadowExecutorState?.last_run || shadowExecutorState?.run || null;
    const status = String(shadowExecutorState?.runtime_status || run?.summary?.runtime_status || run?.summary?.runtime?.status || "").toLowerCase();
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    if (status !== "running") return;
    if (!wallet || !selectedTradingSessionId) return;
    if (isTradingSessionLive(selectedTradingSession)) return;
    const id = setInterval(() => {
      if (document?.hidden) return;
      if (isTradingSessionLive(selectedTradingSession)) return;
      runShadowRuntimeAction("tick");
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [gridMode, wallet, selectedTradingSessionId, selectedTradingSession, isTradingSessionLive, shadowExecutorState?.runtime_status, shadowExecutorState?.last_run, shadowExecutorState?.run, runShadowRuntimeAction]);

  useEffect(() => {
    if (!isTradingSessionLive(selectedTradingSession)) return;
    setTradingExecutionQueue((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      let changed = false;
      const next = rows.map((slot) => {
        const st = String(slot?.status || slot?.state || "").toUpperCase();
        if (st !== "SIMULATED_EXIT" && st !== "SHADOW_PAPER_EXIT") return slot;
        changed = true;
        return sanitizeLiveTradingSlot(slot);
      });
      return changed ? next : prev;
    });
  }, [selectedTradingSession, selectedTradingSessionId, isTradingSessionLive, sanitizeLiveTradingSlot]);




  // Runtime heartbeat for multi-session Trading.
  // A user-approved session must not stay forever in WAIT/BLOCKED without a fresh
  // decision. This is still off-chain/session-control logic; it does not trigger Vault
  // execution. It only keeps the selected/open sessions moving through READY/ACTIVE/WAIT
  // so Shadow and later Live Execution have a current runtime state to evaluate.
  const updateTradingPreparedSession = useCallback((patch = {}) => {
    setTradingPreparedSetup((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const previousSession = base.session && typeof base.session === "object" ? base.session : {};
      const nextSession = { ...previousSession, ...patch, updatedTs: Date.now() };
      const next = { ...base, session: nextSession };

      if (next.learningEvent && typeof next.learningEvent === "object") {
        next.learningEvent = {
          ...next.learningEvent,
          userAction: {
            ...(next.learningEvent.userAction || {}),
            ...(patch.userAction || {}),
          },
          session: nextSession,
        };
      }
      return next;
    });
  }, [setTradingPreparedSetup]);

  const applyTradingRuntimeHeartbeat = useCallback((reason = "auto") => {
    // Backend-first rule: the frontend must never promote READY/WAIT slots to ACTIVE.
    // This local heartbeat created false ACTIVE slots in the UI while the backend still
    // reported virtual_fills=0 / closed_trades=0. Runtime decisions belong only to the
    // Shadow/Strategist backend tick.
    return;
  }, []);

  useEffect(() => {
    // Backend-first Shadow polling. This keeps the displayed state aligned with the
    // persisted backend runtime and lets GET /api/nexus/shadow/executor safely tick
    // already-running sessions when due. It does not change slots locally.
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    if (!wallet) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try { await refreshShadowExecutorState(); } catch {}
      try { await refreshNexusBackendState(); } catch {}
    };
    poll();
    const id = setInterval(poll, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [gridMode, wallet, refreshShadowExecutorState, refreshNexusBackendState, aggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen, setAggressiveRiskAcceptedForDraft, setTradingStyle]);

  const tradingGlobalRiskState = useMemo(() => {
    const fromBackend = nexusBackendState?.risk_state || nexusBackendState?.global_risk_state || null;
    const fromSession = tradingPreparedSetup?.session?.backendRiskState || tradingPreparedSetup?.session?.backendRiskDecision?.risk_state || null;
    const rs = fromBackend && typeof fromBackend === "object" ? fromBackend : fromSession && typeof fromSession === "object" ? fromSession : null;
    if (!rs) return null;
    const status = String(rs.global_status || rs.status || "ACTIVE_OK").toUpperCase();
    const cooldownUntil = Number(rs.cooldown_until_ts || 0) > 0 ? Number(rs.cooldown_until_ts) * 1000 : 0;
    const invalidations = Array.isArray(rs.invalidations) ? rs.invalidations : [];
    return { ...rs, status, cooldownUntil, invalidations };
  }, [nexusBackendState, tradingPreparedSetup]);

  const tradingGlobalRiskLabel = useMemo(() => {
    const rs = tradingGlobalRiskState;
    if (!rs) return "Risk sync: ready";
    const score = Number.isFinite(Number(rs.risk_score)) ? ` · Risk ${Math.round(Number(rs.risk_score))}/100` : "";
    const cooldown = rs.cooldownUntil > Date.now() ? ` · cooldown until ${new Date(rs.cooldownUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "";
    const status = rs.status === "ACTIVE_OK" ? "Risk sync: clean" : rs.status === "PROTECT" ? "Risk sync: PROTECT" : rs.status === "COOLDOWN" ? "Risk sync: COOLDOWN" : `Risk sync: ${rs.status}`;
    return `${status}${score}${cooldown}`;
  }, [tradingGlobalRiskState]);

  const handleTradingApproveBudget = useCallback(async () => {
    if (!tradingCanApprove) {
      setErrorMsg(tradingPreflight.title || "Complete Nexus Trading setup before starting.");
      return null;
    }
    if (String(tradingStyle || "").toUpperCase() === "AGGRESSIVE" && !(aggressiveRiskAcceptedRef.current || aggressiveRiskAcceptedForDraft)) {
      setAggressiveRiskPendingValue("AGGRESSIVE");
      setAggressiveRiskConsentOpen(true);
      setErrorMsg("Please accept the Aggressive Performance risk warning before starting this Trading session.");
      return null;
    }
    const traderStartChain = String(liveVaultChainByMode?.trading || activeGridChainKey || "ETH").toUpperCase();
    let coreVaultCreate = null;
    try {
      setErrorMsg(`Reserving Trading budget on ${traderStartChain} in CoreVault...`);
      coreVaultCreate = await createCoreVaultSystemSession({
        system: "TRADER",
        budgetUsd: Number(String(tradingBudgetUsd || "0").replace(",", ".")),
        durationHours: normalizeTradingRuntimeHours(),
        maxSlippageBps: Math.round((Number(tradingMaxSlippagePct) || 1) * 100),
        maxLossBps: Math.round((Number(tradingHardStopPct) || 15) * 100),
        chain: traderStartChain,
        settlementAsset: String(liveVaultAssetByMode?.trading || "USDC").toUpperCase(),
      });
      setErrorMsg("");
    } catch (e) {
      setErrorMsg(`Trading CoreVault (${traderStartChain}): ${e?.message || e}`);
      return null;
    }
    const now = Date.now();
    const sessionId = makeNexusSessionId("TRD");
    const coreVaultSessionId = Number(coreVaultCreate?.sessionId || coreVaultCreate?.result?.sessionId || 0) || 0;
    const coreVaultChainId = Number({ ETH: 1, BNB: 56, POL: 137 }[traderStartChain] || 0);
    const runtimeHoursNum = normalizeTradingRuntimeHours();
    const reuseProfitPctNum = normalizeTradingReuseProfitPct();
    const maxCombinedSlotsNum = normalizeTradingMaxCombinedSlots();
    const sessionStyle = String(tradingStyle || "TACTICAL").toUpperCase();
    const isAggressiveSession = sessionStyle === "AGGRESSIVE";
    const sessionRiskMode = isAggressiveSession ? "LEGACY_PROTECTION" : String(tradingRiskMode || "BALANCED").toUpperCase();
    const sessionMaxTrades = isAggressiveSession ? 0 : (Number(tradingMaxTrades || 0) || 0);
    const sessionHardStopPct = Number(String(tradingHardStopPct || "").replace(",", ".")) || 0;
    const sessionProfitLockPct = Number(String(tradingProfitLockPct || "").replace(",", ".")) || 0;
    const sessionMaxSlippagePct = Number(String(tradingMaxSlippagePct || "").replace(",", ".")) || 0;
    const sessionCautionDrawdownPct = Number(String(tradingCautionDrawdownPct || "").replace(",", ".")) || 0;
    const sessionStartedAt = now;
    const sessionExpiresAt = now + Math.round(runtimeHoursNum * 3600 * 1000);
    const sessionExpiresTs = Math.floor(sessionExpiresAt / 1000);
    setActiveTradingSessionId(sessionId);
    // BUILD428: cache settings under local session id and on-chain id when known.
    const budgetSplitsForCache = (() => {
      try { return parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd); }
      catch (_) { return []; }
    })();
    try {
      const settingsPayload = {
        style: sessionStyle,
        trading_style: sessionStyle,
        strategy: sessionStyle,
        riskMode: sessionRiskMode,
        risk_mode: sessionRiskMode,
        trading_risk_mode: sessionRiskMode,
        maxTrades: sessionMaxTrades,
        max_trades: sessionMaxTrades,
        hardStopPct: sessionHardStopPct,
        hard_stop_pct: sessionHardStopPct,
        profitLockPct: sessionProfitLockPct,
        profit_lock_pct: sessionProfitLockPct,
        maxSlippagePct: sessionMaxSlippagePct,
        max_slippage_pct: sessionMaxSlippagePct,
        cautionDrawdownPct: sessionCautionDrawdownPct,
        caution_drawdown_pct: sessionCautionDrawdownPct,
        reuseProfitPct: reuseProfitPctNum,
        reuse_profit_pct: reuseProfitPctNum,
        maxCombinedSlots: maxCombinedSlotsNum,
        runtimeHours: runtimeHoursNum,
        payoutAsset: String(manualPayoutAsset || "USDC").toUpperCase(),
        chain: traderStartChain,
        onchainSessionId: coreVaultSessionId || 0,
        budgetUsd: Number(String(tradingBudgetUsd || "0").replace(",", ".")) || 0,
        budgetSplits: Array.isArray(budgetSplitsForCache) ? budgetSplitsForCache : [],
        slotCount: Array.isArray(budgetSplitsForCache) ? budgetSplitsForCache.length : 0,
        slotAmounts: Array.isArray(budgetSplitsForCache) ? budgetSplitsForCache : [],
      };
      traderSessionSettingsCacheRef.current[sessionId] = settingsPayload;
      if (coreVaultSessionId) {
        traderSessionSettingsCacheRef.current[`${traderStartChain}:${coreVaultSessionId}`] = settingsPayload;
        traderSessionSettingsCacheRef.current[`OID:${coreVaultSessionId}`] = settingsPayload;
      }
    } catch (_) {}
    const queue = buildTradingQueue();

    const maxInitialActiveSlots = Math.max(1, Number(maxCombinedSlotsNum || 1) || 1);
    let activatedCount = 0;
    const activeQueue = (Array.isArray(queue) ? queue : []).map((slot, idx) => {
      const slotMeta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
      let next = {
        ...slot,
        session_id: sessionId,
        sessionId,
        trade_session_id: sessionId,
        chain: traderStartChain,
        chainKey: traderStartChain,
        chainId: coreVaultChainId,
        onchainSessionId: coreVaultSessionId || undefined,
        onchain_session_id: coreVaultSessionId || undefined,
        coreVaultSessionId: coreVaultSessionId || undefined,
        runtime_hours: runtimeHoursNum,
        reuse_profit_pct: reuseProfitPctNum,
        profit_reuse_pct: reuseProfitPctNum,
        max_combined_slots: maxCombinedSlotsNum,
        maxCombinedSlots: maxCombinedSlotsNum,
        slot_donor_cap: maxCombinedSlotsNum,
        style: sessionStyle,
        trading_style: sessionStyle,
        strategy: sessionStyle,
        riskMode: sessionRiskMode,
        risk_mode: sessionRiskMode,
        trading_risk_mode: sessionRiskMode,
        maxTrades: sessionMaxTrades,
        max_trades: sessionMaxTrades,
        hardStopPct: sessionHardStopPct,
        hard_stop_pct: sessionHardStopPct,
        profitLockPct: sessionProfitLockPct,
        profit_lock_pct: sessionProfitLockPct,
        maxSlippagePct: sessionMaxSlippagePct,
        max_slippage_pct: sessionMaxSlippagePct,
        cautionDrawdownPct: sessionCautionDrawdownPct,
        caution_drawdown_pct: sessionCautionDrawdownPct,
        session_started_ts: Math.floor(sessionStartedAt / 1000),
        session_expires_ts: sessionExpiresTs,
        expires_ts: sessionExpiresTs,
        expiresAt: sessionExpiresAt,
        meta: {
          ...slotMeta,
          session_id: sessionId,
          trade_session_id: sessionId,
          chain_id: coreVaultChainId,
          onchain_session_id: coreVaultSessionId || undefined,
          coreVaultSessionId: coreVaultSessionId || undefined,
          runtime_hours: runtimeHoursNum,
          reuse_profit_pct: reuseProfitPctNum,
          profit_reuse_pct: reuseProfitPctNum,
          max_combined_slots: maxCombinedSlotsNum,
          maxCombinedSlots: maxCombinedSlotsNum,
          slot_donor_cap: maxCombinedSlotsNum,
          style: sessionStyle,
          trading_style: sessionStyle,
          strategy: sessionStyle,
          riskMode: sessionRiskMode,
          risk_mode: sessionRiskMode,
          trading_risk_mode: sessionRiskMode,
          maxTrades: sessionMaxTrades,
          max_trades: sessionMaxTrades,
          hardStopPct: sessionHardStopPct,
          hard_stop_pct: sessionHardStopPct,
          profitLockPct: sessionProfitLockPct,
          profit_lock_pct: sessionProfitLockPct,
          maxSlippagePct: sessionMaxSlippagePct,
          max_slippage_pct: sessionMaxSlippagePct,
          cautionDrawdownPct: sessionCautionDrawdownPct,
          caution_drawdown_pct: sessionCautionDrawdownPct,
          session_started_ts: Math.floor(sessionStartedAt / 1000),
          session_expires_ts: sessionExpiresTs,
          expires_ts: sessionExpiresTs,
        },
      };
      const slotState = String(slot.status || "WAIT").toUpperCase();
      const slotPriority = Number(slot.priority || slot.strategist_score || slot.shadow_quality || 0);
      const canInitialActivate =
        activatedCount < maxInitialActiveSlots &&
        !["HOLD", "OBSERVE", "RELEASE_REQUIRED", "PROTECT"].includes(slotState) &&
        (slotState === "READY" || slotPriority >= 50 || (idx < maxInitialActiveSlots && slotPriority >= 35));
      if (canInitialActivate) {
        // Frontend only prepares READY candidates. The backend Strategist decides
        // whether a slot actually becomes ACTIVE after market-regime, net-edge,
        // cost, risk, pacing and Max Combined Slot checks.
        activatedCount += 1;
        next = { ...next, previousStatus: slot.status, status: "READY", preparedAt: now, lastRuntimeRecheckAt: now };
      }
      return next;
    });

    // Do not replace older trades. A later 300$ approval becomes a separate session.
    setTradingExecutionQueue((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      return dedupeTradingQueue([...existing, ...activeQueue]);
    });
    setTradingSessions((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const assets = Array.isArray(tradingPreflight.assets) && tradingPreflight.assets.length ? tradingPreflight.assets : [activeQueue?.[0]?.symbol].filter(Boolean);
      const chains = Array.isArray(tradingPreflight.chains) && tradingPreflight.chains.length ? tradingPreflight.chains : [activeGridChainKey].filter(Boolean);
      return [
        {
          id: sessionId,
          type: "TRADING",
          budgetUsd: Number(String(tradingBudgetUsd || "").replace(",", ".")) || 0,
          assets,
          chains,
          chain: traderStartChain,
          chainKey: traderStartChain,
          chainId: coreVaultChainId,
          onchainSessionId: coreVaultSessionId || undefined,
          onchain_session_id: coreVaultSessionId || undefined,
          coreVaultSessionId: coreVaultSessionId || undefined,
          meta: { chain: traderStartChain, chain_key: traderStartChain, chain_id: coreVaultChainId, onchain_session_id: coreVaultSessionId || undefined, coreVaultSessionId: coreVaultSessionId || undefined, slotCount: activeQueue.length, budgetSplits: Array.isArray(budgetSplitsForCache) ? budgetSplitsForCache : [], style: sessionStyle, risk_mode: sessionRiskMode },
          status: activeQueue.some((s) => String(s.status || "").toUpperCase() === "READY") ? "READY" : "WAIT",
          slots: activeQueue.length,
          slotCount: activeQueue.length,
          queue: activeQueue,
          budgetSplits: Array.isArray(budgetSplitsForCache) ? budgetSplitsForCache : activeQueue.map((s) => Number(s?.amountUsd || 0)),
          runtimeHours: runtimeHoursNum,
          runtime_hours: runtimeHoursNum,
          reuseProfitPct: reuseProfitPctNum,
          reuse_profit_pct: reuseProfitPctNum,
          profitReusePct: reuseProfitPctNum,
          profit_reuse_pct: reuseProfitPctNum,
          maxCombinedSlots: maxCombinedSlotsNum,
          max_combined_slots: maxCombinedSlotsNum,
          slotDonorCap: maxCombinedSlotsNum,
          slot_donor_cap: maxCombinedSlotsNum,
          style: sessionStyle,
          trading_style: sessionStyle,
          strategy: sessionStyle,
          riskMode: sessionRiskMode,
          risk_mode: sessionRiskMode,
          trading_risk_mode: sessionRiskMode,
          maxTrades: sessionMaxTrades,
          max_trades: sessionMaxTrades,
          hardStopPct: sessionHardStopPct,
          hard_stop_pct: sessionHardStopPct,
          profitLockPct: sessionProfitLockPct,
          profit_lock_pct: sessionProfitLockPct,
          maxSlippagePct: sessionMaxSlippagePct,
          max_slippage_pct: sessionMaxSlippagePct,
          cautionDrawdownPct: sessionCautionDrawdownPct,
          caution_drawdown_pct: sessionCautionDrawdownPct,
          payoutAsset: manualPayoutAsset || "USDC",
          payout_asset: manualPayoutAsset || "USDC",
          expiresAt: sessionExpiresAt,
          expires_ts: sessionExpiresTs,
          createdAt: now,
          updatedAt: now,
        },
        ...existing,
      ].slice(0, 20);
    });
    setTradingSessionStatus("ARMED");
    setTradingSessionUpdatedTs(now);
    updateTradingPreparedSession({
      status: "ARMED",
      sessionId,
      chain: traderStartChain,
      chainId: coreVaultChainId,
      onchainSessionId: coreVaultSessionId || undefined,
      onchain_session_id: coreVaultSessionId || undefined,
      coreVaultSessionId: coreVaultSessionId || undefined,
      approvedBudgetUsd: tradingBudgetUsd,
      approvedAt: now,
      startedAt: now,
      runtimeHours: runtimeHoursNum,
      runtime_hours: runtimeHoursNum,
      reuseProfitPct: reuseProfitPctNum,
      reuse_profit_pct: reuseProfitPctNum,
      profitReusePct: reuseProfitPctNum,
      profit_reuse_pct: reuseProfitPctNum,
      maxCombinedSlots: maxCombinedSlotsNum,
      max_combined_slots: maxCombinedSlotsNum,
      slotDonorCap: maxCombinedSlotsNum,
      slot_donor_cap: maxCombinedSlotsNum,
      style: sessionStyle,
      trading_style: sessionStyle,
      strategy: sessionStyle,
      riskMode: sessionRiskMode,
      risk_mode: sessionRiskMode,
      trading_risk_mode: sessionRiskMode,
      maxTrades: sessionMaxTrades,
      max_trades: sessionMaxTrades,
      hardStopPct: sessionHardStopPct,
      hard_stop_pct: sessionHardStopPct,
      profitLockPct: sessionProfitLockPct,
      profit_lock_pct: sessionProfitLockPct,
      maxSlippagePct: sessionMaxSlippagePct,
      max_slippage_pct: sessionMaxSlippagePct,
      cautionDrawdownPct: sessionCautionDrawdownPct,
      caution_drawdown_pct: sessionCautionDrawdownPct,
      payoutAsset: manualPayoutAsset || "USDC",
      payout_asset: manualPayoutAsset || "USDC",
      expiresAt: sessionExpiresAt,
      expires_ts: sessionExpiresTs,
      holdHours: clampTradingHoldHours(tradingHoldHours),
      observeMaxHours: 12,
      preflight: tradingPreflight,
      executionQueue: activeQueue,
      userAction: { approvedBudget: true, armed: true, started: false, preflightOk: true, multiSession: true },
      note: "Trading budget approved in CoreVault V5. Press Start Trading to activate the Strategist/Trader worker inside the approved limits.",
    });

    // Backend-first: immediately create/update the authoritative queue rows.
    // Without this, Shadow can simulate locally but the next backend hydration may
    // restore old WAIT rows or an empty queue on another device.
    if (wallet && Array.isArray(activeQueue) && activeQueue.length) {
      await Promise.allSettled(activeQueue.map((slot, idx) => {
        const slotId = String(slot?.slot_id || slot?.slot || idx + 1);
        const symbol = String(slot?.symbol || slot?.asset || "").toUpperCase();
        const chain = String(slot?.chain || slot?.chain_key || activeGridChainKey || "").toUpperCase();
        const state = String(slot?.status || slot?.state || "WAIT").toUpperCase();
        return api(`/api/nexus/trading/queue`, {
          method: "POST",
          wallet,
          body: {
            id: slot?.id || slot?.queue_id || `${sessionId}-${chain}-${slotId}-${symbol || idx + 1}`,
            queue_id: slot?.id || slot?.queue_id || `${sessionId}-${chain}-${slotId}-${symbol || idx + 1}`,
            slot_id: slotId,
            slot: slotId,
            asset: symbol,
            symbol,
            chain,
            chain_key: chain,
            action: slot?.action || "OBSERVE",
            state,
            priority: Number(slot?.priority || 0),
            confidence: Number(slot?.confidence || slot?.confidence_score || 0),
            risk_score: Number(slot?.risk_score || 0),
            reserved_capital_usd: Number(slot?.reserved_capital_usd || slot?.amountUsd || 0),
            session_id: sessionId,
            trade_session_id: sessionId,
            onchain_session_id: coreVaultSessionId || undefined,
            coreVaultSessionId: coreVaultSessionId || undefined,
            chain_id: coreVaultChainId,
            runtime_hours: runtimeHoursNum,
            reuse_profit_pct: reuseProfitPctNum,
            profit_reuse_pct: reuseProfitPctNum,
            max_combined_slots: maxCombinedSlotsNum,
            maxCombinedSlots: maxCombinedSlotsNum,
            slot_donor_cap: maxCombinedSlotsNum,
            style: sessionStyle,
            trading_style: sessionStyle,
            strategy: sessionStyle,
            riskMode: sessionRiskMode,
            risk_mode: sessionRiskMode,
            trading_risk_mode: sessionRiskMode,
            maxTrades: sessionMaxTrades,
            max_trades: sessionMaxTrades,
            hardStopPct: sessionHardStopPct,
            hard_stop_pct: sessionHardStopPct,
            profitLockPct: sessionProfitLockPct,
            profit_lock_pct: sessionProfitLockPct,
            maxSlippagePct: sessionMaxSlippagePct,
            max_slippage_pct: sessionMaxSlippagePct,
            cautionDrawdownPct: sessionCautionDrawdownPct,
            caution_drawdown_pct: sessionCautionDrawdownPct,
            session_started_ts: Math.floor(sessionStartedAt / 1000),
            session_expires_ts: sessionExpiresTs,
            expires_ts: sessionExpiresTs,
            meta: { ...(slot?.meta || {}), session_id: sessionId, trade_session_id: sessionId, chain_id: coreVaultChainId, onchain_session_id: coreVaultSessionId || undefined, coreVaultSessionId: coreVaultSessionId || undefined, runtime_hours: runtimeHoursNum, reuse_profit_pct: reuseProfitPctNum, profit_reuse_pct: reuseProfitPctNum, max_combined_slots: maxCombinedSlotsNum, maxCombinedSlots: maxCombinedSlotsNum, slot_donor_cap: maxCombinedSlotsNum, style: sessionStyle, trading_style: sessionStyle, strategy: sessionStyle, riskMode: sessionRiskMode, risk_mode: sessionRiskMode, trading_risk_mode: sessionRiskMode, maxTrades: sessionMaxTrades, max_trades: sessionMaxTrades, hardStopPct: sessionHardStopPct, hard_stop_pct: sessionHardStopPct, profitLockPct: sessionProfitLockPct, profit_lock_pct: sessionProfitLockPct, maxSlippagePct: sessionMaxSlippagePct, max_slippage_pct: sessionMaxSlippagePct, cautionDrawdownPct: sessionCautionDrawdownPct, caution_drawdown_pct: sessionCautionDrawdownPct, payoutAsset: manualPayoutAsset || "USDC", payout_asset: manualPayoutAsset || "USDC", session_started_ts: Math.floor(sessionStartedAt / 1000), session_expires_ts: sessionExpiresTs, expires_ts: sessionExpiresTs, source: "frontend_budget_approval" },
            signals: slot?.signals || {},
            reason: slot?.condition || slot?.reason || "Created from approved Trading session.",
          },
        });
      }));
      await refreshNexusBackendState();
    }

    if (wallet && isAggressiveSession) {
      try {
        await api(`/api/nexus/trading/aggressive-ack`, {
          method: "POST",
          wallet,
          body: {
            performance_mode: "AGGRESSIVE",
            accepted: true,
            warning_version: AGGRESSIVE_WARNING_VERSION,
            frontend_build: FRONTEND_BUILD_ID,
            scope: "session",
            session_id: sessionId,
            budget_usd: Number(String(tradingBudgetUsd || "0").replace(",", ".")) || 0,
          },
        });
      } catch (e) {
        console.warn("Aggressive session audit failed", e);
      }
    }

    // BUILD426: after a session is created, always reset Performance to DYNAMIC.
    // Aggressive must be chosen and confirmed again for every new session — never sticky.
    setTradingStyle("DYNAMIC");
    aggressiveRiskAcceptedRef.current = false;
    setAggressiveRiskAcceptedForDraft(false);
    setAggressiveRiskPendingValue("");
    setAggressiveRiskConsentOpen(false);
    setTradingBudgetUsd("");
    setTradingBudgetSplitInput("");
    setErrorMsg(`Trading budget confirmed: ${fmtUsd(Number(String(tradingBudgetUsd || "0").replace(",", ".")) || 0)} · ${sessionId}. Starting Trader...`);
    return sessionId;
  }, [tradingCanApprove, tradingBudgetUsd, tradingHoldHours, tradingPreflight, buildTradingQueue, clampTradingHoldHours, activeGridChainKey, makeNexusSessionId, normalizeTradingRuntimeHours, normalizeTradingReuseProfitPct, normalizeTradingMaxCombinedSlots, tradingStyle, aggressiveRiskAcceptedForDraft, tradingRiskMode, tradingMaxTrades, tradingHardStopPct, tradingProfitLockPct, tradingMaxSlippagePct, tradingCautionDrawdownPct, manualPayoutAsset, dedupeTradingQueue, setTradingExecutionQueue, setTradingSessions, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingPreparedSession, setActiveTradingSessionId, setErrorMsg, setTradingBudgetUsd, setTradingBudgetSplitInput, wallet, api, refreshNexusBackendState, createCoreVaultSystemSession, liveVaultChainByMode, liveVaultAssetByMode]);

  const handleTradingStartSession = useCallback(async () => {
    if (tradingStartBusy) return;
    if (!wallet) { setErrorMsg("Connect the wallet before starting Trading."); return; }
    setTradingStartBusy(true);

    const normalizeTraderChain = (value) => {
      const k = String(value || "").trim().toUpperCase();
      if (k === "ETHEREUM" || k === "1") return "ETH";
      if (k === "BSC" || k === "56") return "BNB";
      if (k === "POLYGON" || k === "MATIC" || k === "137") return "POL";
      return k || "ETH";
    };
    const selectedTraderChain = normalizeTraderChain(liveVaultChainByMode?.trading || activeGridChainKey || "ETH");
    const openTraderSessions = (Array.isArray(tradingSessions) ? tradingSessions : []).filter((s) => {
      const st = String(s?.status || "").toUpperCase();
      return s && !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "FINALIZED"].includes(st);
    });
    const sessionOnSelectedChain = openTraderSessions.find((s) => {
      const ch = normalizeTraderChain(s?.chain || s?.chainKey || s?.meta?.chain || s?.meta?.chain_key || "");
      return ch === selectedTraderChain;
    });

    // Multi-chain: if selected chain already has a Trader session, start/resume that one.
    // If not, always create a NEW session for this chain — even when ETH/BNB already runs elsewhere.
    let sid = sessionOnSelectedChain
      ? String(sessionOnSelectedChain?.id || sessionOnSelectedChain?.session_id || "").trim()
      : "";
    if (!sid) {
      if (!tradingPreflight.ok) {
        setErrorMsg(tradingPreflight.title || "Complete the Trading setup before starting.");
        setTradingStartBusy(false);
        return;
      }
      sid = String((await handleTradingApproveBudget()) || "").trim();
      if (!sid) { setTradingStartBusy(false); return; }
    }

    try {
      await api("/api/nexus/trading/control", {
        method: "POST",
        wallet,
        body: {
          action: "start",
          session_id: sid,
          chain: selectedTraderChain,
          allowed_assets: selectedTraderAssets,
          settlement_asset: String(manualPayoutAsset || "USDC").toUpperCase(),
        },
      });
      const now = Date.now();
      setTradingSessionStatus("ACTIVE");
      setTradingSessionUpdatedTs(now);
      updateTradingPreparedSession({ status: "ACTIVE", startedAt: now, userAction: { started: true }, chain: selectedTraderChain });
      await refreshNexusBackendState();
      // BUILD426: draft Performance always back to DYNAMIC after a successful start.
      setTradingStyle("DYNAMIC");
      aggressiveRiskAcceptedRef.current = false;
      setAggressiveRiskAcceptedForDraft(false);
      setAggressiveRiskPendingValue("");
      setAggressiveRiskConsentOpen(false);
      setErrorMsg(`Trading started on ${selectedTraderChain}: ${sid}`);
    } catch (e) {
      setErrorMsg(`Start Trading failed: ${e?.message || e}`);
    } finally {
      setTradingStartBusy(false);
    }
  }, [tradingStartBusy, wallet, tradingSessions, liveVaultChainByMode, tradingPreflight, handleTradingApproveBudget, activeGridChainKey, selectedTraderAssets, manualPayoutAsset, api, refreshNexusBackendState, setErrorMsg, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingPreparedSession, setTradingStyle, setAggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen]);

  const resolveTraderOnchainControl = useCallback((sessionOverride = null) => {
    const sess = sessionOverride && typeof sessionOverride === "object" && !sessionOverride?.nativeEvent ? sessionOverride : (selectedTradingSession || {});
    const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
    const sid = String(sess?.id || selectedTradingSessionId || activeTradingSessionId || "").trim();
    const localOnchainSid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || meta?.onchain_session_id || meta?.coreVaultSessionId || 0) || 0;
    const selectedLiveChain = String(liveVaultChainByMode?.trading || activeGridChainKey || DEFAULT_CHAIN || "ETH").toUpperCase();
    const activeOnchainTraderRows = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
      .filter((row) => String(row?.engine || "").toUpperCase() === "TRADER")
      .filter((row) => !["FINALIZED", "COMPLETED", "CANCELLED"].includes(String(row?.statusLabel || "").toUpperCase()));
    const localChain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || "").toUpperCase();
    const authoritativeRow =
      (localOnchainSid > 0 ? activeOnchainTraderRows.find((row) => Number(row?.sessionId || 0) === localOnchainSid) : null) ||
      activeOnchainTraderRows.find((row) => String(row?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(row?.chainId)] || "")).toUpperCase() === (localChain || selectedLiveChain)) ||
      (activeOnchainTraderRows.length === 1 ? activeOnchainTraderRows[0] : null);
    const chain = String(authoritativeRow?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(authoritativeRow?.chainId)] || "") || localChain || selectedLiveChain).toUpperCase();
    const onchainSid = Number(authoritativeRow?.sessionId || localOnchainSid || 0) || 0;
    return { sess, sid, chain, onchainSid };
  }, [selectedTradingSession, selectedTradingSessionId, activeTradingSessionId, liveVaultChainByMode, activeGridChainKey, coreVaultSessionPreview]);

  const handleTradingPauseSession = useCallback(async (sessionOverride = null) => {
    try { if (typeof refreshOnchainSessionsSilent === 'function') await refreshOnchainSessionsSilent(); } catch {}
    const { sess, sid, chain, onchainSid } = resolveTraderOnchainControl(sessionOverride);
    if (!sid) { setErrorMsg("Trader Pause: no local session id."); return; }
    if (!onchainSid) { setErrorMsg("Trader Pause: on-chain session id missing. Open System Info → Refresh on-chain sessions, then try again."); return; }
    try {
      setErrorMsg(`Pausing Trader ${chain} #${onchainSid} on-chain...`);
      await api("/api/nexus/trading/control", { method:"POST", token, wallet, body:{ action:"pause", session_id:sid, sessionId:sid, chain, onchainSessionId:onchainSid, onchain_session_id:onchainSid } });
      const now = Date.now();
      setTradingSessionStatus("PAUSED");
      setTradingSessionUpdatedTs(now);
      updateTradingSessionMeta(sid, { status:"PAUSED", pausedAt:now, onchainSessionId:onchainSid, chain });
      setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).map((slot) => tradingSessionIdMatches(getTradingSlotSessionId(slot), sid) ? { ...slot, status:"WAIT", pausedAt:now } : slot));
      await refreshNexusBackendState().catch(() => null);
      setErrorMsg(`Trader ${chain} #${onchainSid} paused on-chain.`);
    } catch (e) { setErrorMsg(`Trader Pause failed: ${e?.message || e}`); }
  }, [resolveTraderOnchainControl, api, token, wallet, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, setTradingExecutionQueue, tradingSessionIdMatches, getTradingSlotSessionId, refreshNexusBackendState, setErrorMsg]);

  const handleTradingResumeSession = useCallback(async (sessionOverride = null) => {
    const { sid, chain, onchainSid } = resolveTraderOnchainControl(sessionOverride);
    if (!sid) { setErrorMsg("Trader Resume: no local session id."); return; }
    if (!onchainSid) { setErrorMsg("Trader Resume: on-chain session id missing. Refresh on-chain sessions first."); return; }
    try {
      setErrorMsg(`Resuming Trader ${chain} #${onchainSid} on-chain...`);
      await api("/api/nexus/trading/control", { method:"POST", token, wallet, body:{ action:"resume", session_id:sid, sessionId:sid, chain, onchainSessionId:onchainSid, onchain_session_id:onchainSid } });
      const now = Date.now();
      setTradingSessionStatus("ACTIVE");
      setTradingSessionUpdatedTs(now);
      updateTradingSessionMeta(sid, { status:"ACTIVE", resumedAt:now, onchainSessionId:onchainSid, chain });
      await refreshNexusBackendState().catch(() => null);
      setErrorMsg(`Trader ${chain} #${onchainSid} resumed on-chain.`);
    } catch (e) { setErrorMsg(`Trader Resume failed: ${e?.message || e}`); }
  }, [resolveTraderOnchainControl, api, token, wallet, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, refreshNexusBackendState, setErrorMsg]);

  const handleTradingStopSession = useCallback(async (sessionIdOverride = "", sessionOverride = null) => {
    if (tradingStopBusy) return;
    // BUILD428: busy IMMEDIATELY so the red Stop button fades before any network work.
    setTradingStopBusy(true);
    setTradingSessionStatus("STOPPING");
    try { if (typeof refreshOnchainSessionsSilent === 'function') await refreshOnchainSessionsSilent(); } catch {}
    // BUILD414: React passes the click SyntheticEvent as the first argument when a
    // handler is bound as onClick={handleTradingStopSession}. Never interpret that
    // event object as a session id ("[object Object]"). Only explicit string/number
    // overrides are valid; otherwise resolve the selected/active Trader session.
    const hasExplicitSessionId = typeof sessionIdOverride === "string" || typeof sessionIdOverride === "number";
    const requestedSid = hasExplicitSessionId ? String(sessionIdOverride || "").trim() : "";
    const explicitSessionOverride = sessionOverride && typeof sessionOverride === "object" && !sessionOverride?.nativeEvent ? sessionOverride : null;
    if (!requestedSid && !tradingCanStop) {
      setTradingStopBusy(false);
      setErrorMsg("Trader Stop is not available for the current session state. Select the ACTIVE session card first.");
      return;
    }
    const now = Date.now();
    const overrideSession = explicitSessionOverride;
    const resolvedSession = overrideSession || (requestedSid
      ? (Array.isArray(openTradingSessions) ? openTradingSessions : []).find((row) => tradingSessionIdMatches(row?.id || row?.session_id || row?.sessionId, requestedSid))
      : null) || selectedTradingSession || {};
    const sess = resolvedSession;
    const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
    const sid = String(requestedSid || selectedTradingSessionId || activeTradingSessionId || sess?.id || "").trim();
    if (!sid) {
      setTradingStopBusy(false);
      setErrorMsg("Trader stop could not resolve the selected session.");
      return;
    }
    // BUILD416: the authoritative Trader chain/session comes from the live CoreVault inventory,
    // never from stale local slot/session metadata. A Polygon CoreVault session may legitimately
    // trade an ETH asset, so sess.chain can describe the slot asset context and must not redirect
    // Stop & Finalize to Ethereum.
    const localOnchainSid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || meta?.onchain_session_id || meta?.coreVaultSessionId || 0) || 0;
    const activeOnchainTraderRows = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
      .filter((row) => String(row?.engine || "").toUpperCase() === "TRADER")
      .filter((row) => !["FINALIZED", "COMPLETED", "CANCELLED"].includes(String(row?.statusLabel || "").toUpperCase()));
    const selectedLiveChain = String(
      sess?.chain || sess?.chainKey || liveVaultChainByMode?.trading || activeGridChainKey || DEFAULT_CHAIN || "ETH"
    ).toUpperCase();
    // Prefer POL/BNB/ETH match from the visible "On-chain Trader session #N" list.
    const authoritativeRow =
      (localOnchainSid > 0 ? activeOnchainTraderRows.find((row) => Number(row?.sessionId || 0) === localOnchainSid) : null) ||
      activeOnchainTraderRows.find((row) => String(row?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(row?.chainId)] || "")).toUpperCase() === selectedLiveChain) ||
      (activeOnchainTraderRows.length === 1 ? activeOnchainTraderRows[0] : null) ||
      (activeOnchainTraderRows.length ? activeOnchainTraderRows[0] : null);
    const chain = String(authoritativeRow?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(authoritativeRow?.chainId)] || "") || selectedLiveChain).toUpperCase();
    const onchainSid = Number(authoritativeRow?.sessionId || localOnchainSid || 0) || 0;
    if (!onchainSid) {
      setErrorMsg("Trader Stop: no on-chain session id. Open System Info → Refresh on-chain sessions, then Stop again.");
      setTradingStopBusy(false);
      return;
    }

    setTradingStopBusy(true);
    setTradingSessionStatus("STOPPING");
    setTradingSessionUpdatedTs(now);
    // BUILD431: hide session card immediately (optimistic). On-chain Start Closing + Finalize continue in background.
    setTradingSessions((prev) => (Array.isArray(prev) ? prev : []).map((row) => {
      const rid = String(row?.id || row?.session_id || "");
      const oid = Number(row?.onchainSessionId || row?.onchain_session_id || row?.coreVaultSessionId || 0) || 0;
      const same = tradingSessionIdMatches(rid, sid) || (onchainSid > 0 && oid === onchainSid);
      if (!same) return row;
      return { ...row, status: "CLOSING", statusLabel: "CLOSING", active: false, stoppingAt: now, closedAt: now, onchainSessionId: onchainSid || row?.onchainSessionId, queue: [] };
    }));
    setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => {
      if (tradingSessionIdMatches(getTradingSlotSessionId(slot), sid)) return false;
      const slotOid = Number(slot?.onchainSessionId || slot?.onchain_session_id || slot?.meta?.onchain_session_id || 0) || 0;
      if (onchainSid > 0 && slotOid === onchainSid) return false;
      return true;
    }));
    setActiveTradingSessionId((prev) => (tradingSessionIdMatches(String(prev || ""), sid) ? "" : prev));
    updateTradingSessionMeta(sid, { status: "CLOSING", stoppingAt: now, active: false, chain, onchainSessionId: onchainSid || undefined, onchain_session_id: onchainSid || undefined });
    updateTradingPreparedSession({ status: "CLOSING", stoppingAt: now, chain, onchainSessionId: onchainSid || undefined, userAction: { stopped: true, sessionId: sid } });
    setErrorMsg(`Trader ${chain} #${onchainSid}: closing on-chain… card removed.`);

    try {
      // BUILD418: keep the runtime queue until CoreVault confirms FINALIZED.
      // BUILD423: use the same exact on-chain stop path as System Info recovery.
      // /api/nexus/trading/control often only touched the local queue; orphan stop always finalizes.
      const chainIdNum = Number({ ETH: 1, BNB: 56, POL: 137 }[chain] || 0);
      const stopResult = await api("/api/live/stop-orphan-session", {
        method: "POST",
        token,
        wallet,
        body: {
          engine: "TRADER",
          chain,
          chainId: chainIdNum,
          sessionId: onchainSid,
          onchainSessionId: onchainSid,
        },
      });
      // Also mark local queue stopped so Strategist does not keep ticking.
      try {
        await api("/api/nexus/trading/control", {
          method: "POST",
          token,
          wallet,
          body: {
            action: "stop",
            session_id: sid,
            sessionId: sid,
            chain,
            onchainSessionId: onchainSid,
            onchain_session_id: onchainSid,
          },
        });
      } catch (_) { /* local queue best-effort */ }
      const resolvedOnchainSid = Number(onchainSid || stopResult?.coreVaultFinalize?.[0]?.sessionId || 0) || 0;
      setErrorMsg(`Trader stop accepted on ${chain}${resolvedOnchainSid ? ` · CoreVault #${resolvedOnchainSid}` : ""}. Closing and finalizing on-chain...`);

      const finishStopAfterFinalized = () => {
        const doneAt = Date.now();
        setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => !tradingSessionIdMatches(getTradingSlotSessionId(slot), sid)));
        api("/api/nexus/trading/hold-state", { method:"POST", body:{ action:"stop", queue:[], reason:"onchain_finalized", session_id:sid, base_session_id:normalizeTradingSessionBaseId(sid), chain } }).catch(() => null);
        setTradingSessionStatus("PREPARED");
        setTradingSessionUpdatedTs(doneAt);
        updateTradingSessionMeta(sid, { status: "STOPPED", stoppedAt: doneAt, closedAt: doneAt, active: false, onchainSessionId: resolvedOnchainSid || undefined });
        const remainingOpen = (Array.isArray(openTradingSessions) ? openTradingSessions : []).filter((sessRow) => !tradingSessionIdMatches(sessRow?.id, sid));
        setActiveTradingSessionId(String(remainingOpen?.[0]?.id || ""));
        updateTradingPreparedSession({
          status: "PREPARED", sessionId: sid, stoppedAt: doneAt, closedAt: doneAt, executionQueue: [],
          userAction: { stopped: true, closedSession: true, sessionId: sid },
          outcome: { status: "session_finalized_onchain" },
          note: "Trading session finalized on-chain and removed from active sessions.",
        });
        setErrorMsg(`Trader session${resolvedOnchainSid ? ` #${resolvedOnchainSid}` : ""} finalized on-chain.`);
      };

      // Poll authoritative CoreVault state. The yellow orphan recovery control is now
      // only a fallback; a normal Protect / Stop completes the same lifecycle itself.
      const chainId = Number({ ETH: 1, BNB: 56, POL: 137 }[chain] || 0);
      const pollFinalized = async (attempt = 0) => {
        try {
          if (chainId > 0 && resolvedOnchainSid > 0) {
            const state = await api(`/api/nexus/trading/session-status?chain=${encodeURIComponent(chain)}&sessionId=${encodeURIComponent(resolvedOnchainSid)}&_ts=${Date.now()}`, { method:"GET", token, wallet });
            const statusId = Number(state?.statusId || 0);
            const statusLabel = String(state?.statusLabel || "").toUpperCase();
            if (statusId === 4 || statusLabel === "FINALIZED") {
              finishStopAfterFinalized();
              await refreshNexusBackendState().catch(() => null);
              return;
            }
            if ([1,2,3].includes(statusId)) {
              const liveStatus = statusId === 3 ? "STOPPING" : (statusId === 2 ? "PAUSED" : "ACTIVE");
              setTradingSessionStatus(liveStatus);
              updateTradingSessionMeta(sid, { status:liveStatus, onchainSessionId:resolvedOnchainSid, chain, active:true });
            }
          }
        } catch (pollErr) {
          if (attempt === 0) setErrorMsg("Trader stop submitted; waiting for confirmed on-chain finalization...");
        }
        if (attempt < 60) window.setTimeout(() => pollFinalized(attempt + 1), attempt < 10 ? 1500 : 3000);
        else setErrorMsg(`Trader ${chain} #${resolvedOnchainSid || ""} is still not confirmed FINALIZED. Session card kept for recovery/control.`);
      };
      window.setTimeout(() => pollFinalized(0), 900);
      return stopResult;
    } catch (e) {
      const em = String(e?.message || e || "unknown");
      setErrorMsg(`Trader Stop failed: ${em}`);
      setTradingSessionStatus("ACTIVE");
      setTradingSessionUpdatedTs(Date.now());
      updateTradingSessionMeta(sid, { status: "ACTIVE", stoppingAt: 0, active: true });
      setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).map((slot) => {
        if (!tradingSessionIdMatches(getTradingSlotSessionId(slot), sid)) return slot;
        return String(slot?.status || "").toUpperCase() === "STOPPING" ? { ...slot, status: "READY" } : slot;
      }));
      setErrorMsg(`Trader Stop & Finalize failed: ${e?.message || e}`);
      return null;
    } finally {
      setTradingStopBusy(false);
    }
  }, [tradingCanStop, tradingStopBusy, selectedTradingSession, selectedTradingSessionId, activeTradingSessionId, liveVaultChainByMode, activeGridChainKey, getTradingSlotSessionId, tradingSessionIdMatches, normalizeTradingSessionBaseId, openTradingSessions, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, updateTradingPreparedSession, setActiveTradingSessionId, token, wallet, api, refreshNexusBackendState, setErrorMsg, coreVaultSessionPreview]);

  const handleTradingReleaseCapital = useCallback(() => {
    if (!tradingCanReleaseCapital) return;
    const now = Date.now();
    const sid = String(selectedTradingSessionId || activeTradingSessionId || "").trim();
    setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => String(getTradingSlotSessionId(slot) || "") !== sid));
    setTradingSessionStatus("PREPARED");
    setTradingSessionUpdatedTs(now);
    updateTradingSessionMeta(sid, { status: "RELEASED", releasedAt: now });
    updateTradingPreparedSession({
      status: "PREPARED",
      sessionId: sid,
      releasedAt: now,
      executionQueue: [],
      userAction: { releasedCapital: true, sessionId: sid },
      note: "Capital released by user for the selected session. Other Trading sessions remain untouched.",
    });
    api("/api/nexus/trading/hold-state", { method: "POST", body: { action: "release", session_id: sid } }).catch(() => {});
  }, [tradingCanReleaseCapital, selectedTradingSessionId, activeTradingSessionId, getTradingSlotSessionId, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingSessionMeta, updateTradingPreparedSession]);



  const applyTradingRiskDecision = useCallback((riskResult = {}) => {
    const decisions = Array.isArray(riskResult?.decisions) ? riskResult.decisions : [];
    if (!decisions.length) return;

    const now = Date.now();
    const decisionBySlot = new Map();
    decisions.forEach((d) => {
      const key = String(d?.slot ?? "");
      if (key) decisionBySlot.set(key, d);
    });

    let shouldEnterHold = false;
    let nextSession = tradingSessionLabel;
    let holdReason = "Risk decision engine requested capital protection.";
    const holdHours = clampTradingHoldHours(tradingHoldHours);
    const holdUntil = now + holdHours * 60 * 60 * 1000;
    const observeUntil = now + 12 * 60 * 60 * 1000;

    const nextQueue = (Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue : []).map((slot) => {
      const d = decisionBySlot.get(String(slot?.slot ?? ""));
      if (!d) return slot;

      const action = String(d.action || "KEEP_ACTIVE").toUpperCase();
      const reasons = Array.isArray(d.reasons) ? d.reasons.filter(Boolean).join(" ") : String(d.reason || "");
      const riskScore = Number(d.risk_score || 0);
      const confirmations = Number(d.confirmations || 0);
      const decisionMeta = {
        riskDecision: d.decision || action,
        riskScore,
        riskConfirmations: confirmations,
        riskReasons: Array.isArray(d.reasons) ? d.reasons : [],
        riskCheckedAt: now,
      };

      if (action === "FORCE_EXIT" || action === "EXIT") {
        shouldEnterHold = true;
        nextSession = "HOLD";
        holdReason = reasons || (action === "FORCE_EXIT" ? "Hard risk block triggered force exit." : "Confirmed risk cluster triggered exit.");
        return {
          ...slot,
          ...decisionMeta,
          status: "HOLD",
          exitRequestedAt: now,
          holdStartedAt: now,
          holdUntil,
          observeUntil,
          condition: action === "FORCE_EXIT" ? "Force exit risk detected. Capital is protected in HOLD." : "Exit risk confirmed. Capital is protected in HOLD.",
          observeReason: holdReason,
        };
      }

      if (action === "REDUCE") {
        nextSession = nextSession === "ACTIVE" ? "PROTECT" : nextSession;
        return {
          ...slot,
          ...decisionMeta,
          status: "PROTECT",
          reduceRecommendedAt: now,
          condition: "Reduce / protect recommended. Risk cluster is elevated but not force-exit critical.",
          observeReason: reasons || "The Strategist detected elevated risk and recommends reducing exposure before conditions worsen.",
        };
      }

      if (action === "PROTECT") {
        nextSession = nextSession === "ACTIVE" ? "PROTECT" : nextSession;
        return {
          ...slot,
          ...decisionMeta,
          status: "PROTECT",
          protectStartedAt: slot.protectStartedAt || now,
          condition: "Protect mode. No new add-ons; Strategist watches for confirmation or recovery.",
          observeReason: reasons || "The Strategist detected early risk signals. This is not an exit yet, but the trade is under protection.",
        };
      }

      if (action === "CLEAR_PROTECT") {
        return {
          ...slot,
          ...decisionMeta,
          status: "ACTIVE",
          protectClearedAt: now,
          condition: "Risk normalized. Slot returned to ACTIVE monitoring.",
          observeReason: reasons || "Risk cluster cleared and market conditions normalized enough for active monitoring.",
        };
      }

      return {
        ...slot,
        ...decisionMeta,
        condition: slot.condition || "Active; Strategist continues monitoring for risk changes.",
      };
    });

    setTradingExecutionQueue(nextQueue);
    if (nextSession !== tradingSessionLabel) {
      setTradingSessionStatus(nextSession);
      setTradingSessionUpdatedTs(now);
    }
    updateTradingPreparedSession({
      status: nextSession,
      executionQueue: nextQueue,
      backendRiskDecision: riskResult,
      backendRiskState: riskResult?.risk_state || riskResult?.global_risk_state || null,
      riskCheckedAt: now,
    });

    if (shouldEnterHold) {
      api("/api/nexus/trading/hold-state", {
        method: "POST",
        body: { action: "exit", hold_hours: holdHours, queue: nextQueue, reason: holdReason },
      }).catch(() => {});
    }
  }, [tradingExecutionQueue, tradingSessionLabel, tradingHoldHours, clampTradingHoldHours, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingPreparedSession]);

  useEffect(() => {
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    const selectedStyle = String(
      selectedTradingSession?.style || selectedTradingSession?.trading_style || selectedTradingSession?.strategy ||
      selectedTradingSession?.meta?.style || selectedTradingSession?.meta?.trading_style || selectedTradingSession?.meta?.strategy ||
      tradingStyle || ""
    ).toUpperCase();
    if (selectedStyle === "AGGRESSIVE") return;
    if (!["ACTIVE", "PROTECT"].includes(tradingSessionLabel)) return;
    if (!Array.isArray(tradingExecutionQueue) || !tradingExecutionQueue.length) return;

    let cancelled = false;
    const checkRisk = () => {
      const monitoredQueue = (Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue : [])
        .filter((slot) => ["ACTIVE", "PROTECT", "READY"].includes(String(slot?.status || "").toUpperCase()));
      if (!monitoredQueue.length) return;

      // Prevent request storms: queue/risk updates can re-render the component and
      // would otherwise immediately re-trigger this effect. Keep one risk decision
      // request per stable queue/config snapshot unless the 60s interval fires.
      const riskKey = JSON.stringify({
        wallet: String(wallet || "").toLowerCase(),
        session: String(selectedTradingSessionId || activeTradingSessionId || ""),
        queue: monitoredQueue.map((slot) => ({
          slot: slot?.slot || slot?.slot_id || slot?.id || "",
          symbol: slot?.symbol || slot?.asset || "",
          status: String(slot?.status || "").toUpperCase(),
          confidence: Number(slot?.confidence ?? slot?.confidence_score ?? 0),
          risk_score: Number(slot?.risk_score ?? slot?.riskScore ?? 0),
          priority: Number(slot?.priority ?? 0),
        })),
        risk_mode: tradingRiskMode,
        caution_drawdown_pct: tradingCautionDrawdownPct,
        hard_stop_pct: tradingHardStopPct,
        max_slippage_pct: tradingMaxSlippagePct,
      });
      const nowMs = Date.now();
      const last = tradingRiskRequestRef.current || { key: "", ts: 0, inFlight: false };
      if (last.inFlight) return;
      if (last.key === riskKey && nowMs - Number(last.ts || 0) < 55000) return;
      tradingRiskRequestRef.current = { key: riskKey, ts: nowMs, inFlight: true };

      api("/api/nexus/trading/risk-decision", {
        method: "POST",
        wallet,
        body: {
          wallet,
          wallet_address: wallet,
          queue: monitoredQueue,
          config: {
            risk_mode: tradingRiskMode,
            caution_drawdown_pct: tradingCautionDrawdownPct,
            hard_stop_pct: tradingHardStopPct,
            max_slippage_pct: tradingMaxSlippagePct,
          },
        },
      })
        .then((res) => {
          if (cancelled) return;
          if (res?.status === "ok") {
            applyTradingRiskDecision(res);
            if (typeof refreshNexusBackendState === "function") refreshNexusBackendState();
          }
        })
        .catch(() => {})
        .finally(() => {
          const cur = tradingRiskRequestRef.current || {};
          tradingRiskRequestRef.current = { ...cur, inFlight: false };
        });
    };

    const first = setTimeout(checkRisk, 5000);
    const id = setInterval(checkRisk, 120 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(id);
    };
  }, [gridMode, tradingSessionLabel, tradingExecutionQueue, selectedTradingSession, tradingStyle, tradingRiskMode, tradingCautionDrawdownPct, tradingHardStopPct, tradingMaxSlippagePct, wallet, selectedTradingSessionId, activeTradingSessionId, applyTradingRiskDecision, refreshNexusBackendState]);

  useEffect(() => {
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    if (!["HOLD", "OBSERVE"].includes(tradingSessionLabel)) return;

    const tick = () => {
      const now = Date.now();
      let nextSession = tradingSessionLabel;
      let changed = false;
      let releaseNeeded = false;

      const nextQueue = (Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue : []).map((slot) => {
        const st = String(slot?.status || "").toUpperCase();
        if (!["HOLD", "OBSERVE"].includes(st)) return slot;
        const holdUntil = Number(slot.holdUntil || 0);
        const observeUntil = Number(slot.observeUntil || 0);

        if (observeUntil > 0 && now >= observeUntil) {
          changed = true;
          releaseNeeded = true;
          return {
            ...slot,
            status: "RELEASE_REQUIRED",
            releaseRequiredAt: now,
            condition: "Max 12h observation reached. Capital stays protected and requires user release before new allocation.",
            observeReason: "The maximum observation window has been reached. Nexus will not reallocate automatically; user release is required before capital can be used again.",
          };
        }

        if (st === "HOLD" && holdUntil > 0 && now >= holdUntil) {
          changed = true;
          nextSession = "OBSERVE";
          return {
            ...slot,
            status: "OBSERVE",
            observeStartedAt: now,
            condition: "Minimum HOLD completed. Strategist keeps observing; no trade unless market quality becomes clean.",
            observeReason: "Minimum HOLD is complete, but this is not trade approval. The Strategist still needs clean market structure, liquidity confirmation, acceptable RVOL and controlled risk before any new allocation.",
          };
        }

        return slot;
      });

      if (releaseNeeded) nextSession = "RELEASE_REQUIRED";
      if (changed) {
        setTradingExecutionQueue(nextQueue);
        setTradingSessionStatus(nextSession);
        setTradingSessionUpdatedTs(now);
        updateTradingPreparedSession({
          status: nextSession,
          executionQueue: nextQueue,
          updatedByHoldObserve: true,
          releaseRequired: releaseNeeded,
        });
      }
    };

    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [gridMode, tradingSessionLabel, tradingExecutionQueue, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingPreparedSession]);

  useEffect(() => {
    if (String(gridMode || "").toLowerCase() !== "trading") return;
    if (tradingHoldStateHydratedRef.current) return;
    tradingHoldStateHydratedRef.current = true;

    api("/api/nexus/trading/hold-state")
      .then((res) => {
        const holdState = res?.hold_state;
        if (!holdState || typeof holdState !== "object") return;

        const status = String(holdState.status || "").toUpperCase();
        if (!["HOLD", "OBSERVE", "RELEASE_REQUIRED"].includes(status)) return;

        const holdUntilMs = Number(holdState.hold_until_ts || 0) > 0 ? Number(holdState.hold_until_ts) * 1000 : 0;
        const observeUntilMs = Number(holdState.observe_until_ts || 0) > 0 ? Number(holdState.observe_until_ts) * 1000 : 0;
        const reason = String(holdState.reason || "Capital is protected while the Strategist keeps observing market quality.");
        const queue = Array.isArray(holdState.queue) ? holdState.queue : [];
        const nextQueue = queue.map((slot) => {
          const slotStatus = String(slot?.status || "").toUpperCase();
          const shouldForceRelease = status === "RELEASE_REQUIRED" && ["HOLD", "OBSERVE", "RELEASE_REQUIRED"].includes(slotStatus);
          const shouldSyncObserve = status === "OBSERVE" && slotStatus === "HOLD";
          return {
            ...slot,
            status: shouldForceRelease ? "RELEASE_REQUIRED" : shouldSyncObserve ? "OBSERVE" : (slot.status || status),
            holdUntil: Number(slot?.holdUntil || 0) || holdUntilMs || undefined,
            observeUntil: Number(slot?.observeUntil || 0) || observeUntilMs || undefined,
            observeReason: slot?.observeReason || reason,
            condition: slot?.condition || reason,
          };
        });

        setTradingSessionStatus(status);
        setTradingSessionUpdatedTs(Date.now());
        if (nextQueue.length) {
          const sid = String(selectedTradingSessionId || activeTradingSessionId || "").trim();
          const chain = String(activeGridChainKey || DEFAULT_CHAIN || "").toUpperCase().trim();
          const normalizedHoldQueue = nextQueue.map((slot, idx) => ({
            ...slot,
            session_id: getTradingSlotSessionId(slot) || sid || slot.session_id,
            sessionId: getTradingSlotSessionId(slot) || sid || slot.sessionId,
            trade_session_id: getTradingSlotSessionId(slot) || sid || slot.trade_session_id,
            chain: String(slot?.chain || slot?.chain_key || chain || "").toUpperCase(),
            chain_key: String(slot?.chain || slot?.chain_key || chain || "").toUpperCase(),
            slot: slot?.slot || slot?.slot_id || idx + 1,
          }));
          setTradingExecutionQueue((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            const kept = existing.filter((slot) => {
              const slotSession = String(getTradingSlotSessionId(slot) || "").trim();
              const slotChain = String(slot?.chain || slot?.chain_key || "").toUpperCase().trim();
              if (sid && slotSession === sid && (!chain || slotChain === chain)) return false;
              return true;
            });
            return dedupeTradingQueue([...kept, ...normalizedHoldQueue]);
          });
        }
        updateTradingPreparedSession({
          status,
          executionQueue: nextQueue,
          hydratedFromHoldState: true,
          releaseRequired: status === "RELEASE_REQUIRED",
        });
      })
      .catch(() => {});
  }, [gridMode, selectedTradingSessionId, activeTradingSessionId, activeGridChainKey, getTradingSlotSessionId, dedupeTradingQueue, setTradingExecutionQueue, setTradingSessionStatus, setTradingSessionUpdatedTs, updateTradingPreparedSession]);

  const applyTradingRiskPreset = useCallback((mode, confidence = tradingConfidenceMin) => {
    const risk = String(mode || "BALANCED").toUpperCase();
    const conf = String(confidence || "MEDIUM").toUpperCase();

    const presets = {
      DEFENSIVE: { caution: 2, hard: 8, profit: 12, slip: 0.7, trades: 3 },
      BALANCED: { caution: 3, hard: 12, profit: 20, slip: 1.2, trades: 6 },
      DYNAMIC: { caution: 5, hard: 18, profit: 28, slip: 1.8, trades: 10 },
    };

    const confidenceAdjust = {
      LOW: { caution: 1, hard: 2, profit: 4, slip: 0.4, trades: 2 },
      MEDIUM: { caution: 0, hard: 0, profit: 0, slip: 0, trades: 0 },
      HIGH: { caution: -1, hard: -2, profit: -4, slip: -0.3, trades: -2 },
    };

    const base = presets[risk] || presets.BALANCED;
    const adj = confidenceAdjust[conf] || confidenceAdjust.MEDIUM;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    setTradingRiskMode(risk);
    setTradingConfidenceMin(conf);
    setTradingCautionDrawdownPct(String(clamp(base.caution + adj.caution, 1, 8)));
    setTradingHardStopPct(String(clamp(base.hard + adj.hard, 5, 25)));
    setTradingProfitLockPct(String(clamp(base.profit + adj.profit, 8, 40)));
    setTradingMaxSlippagePct(String(clamp(Number((base.slip + adj.slip).toFixed(1)), 0.3, 3)));
    setTradingMaxTrades(String(clamp(base.trades + adj.trades, 1, 15)));
    setTradingMaxCombinedSlots(String(risk === "DYNAMIC" ? 3 : risk === "DEFENSIVE" ? 1 : 2));
  }, [
    tradingConfidenceMin,
    setTradingRiskMode,
    setTradingConfidenceMin,
    setTradingCautionDrawdownPct,
    setTradingHardStopPct,
    setTradingProfitLockPct,
    setTradingMaxSlippagePct,
    setTradingMaxTrades,
    setTradingMaxCombinedSlots,
  ]);

  const resetNkrAggressiveDraftSelection = useCallback(() => {
    // Nexus NKR Aggressive consent is budget/draft specific, never persistent.
    // A new budget starts from Dynamic again so Aggressive cannot be reused silently.
    setNkrCapitalMode("DYNAMIC");
    setNkrAggressiveAcceptedForDraft(false);
    setNkrAggressivePendingValue("");
    setNkrAggressiveConsentOpen(false);
  }, [setNkrCapitalMode, setNkrAggressiveAcceptedForDraft, setNkrAggressivePendingValue, setNkrAggressiveConsentOpen]);

  const handleNkrBudgetInputChange = useCallback((value) => {
    // Only reset Aggressive when there is no live NKR run. Changing "Add Capital" while
    // Aggressive is active must not silently drop the consented mode.
    const hasLiveNkr = Array.isArray(rotationSessions) && rotationSessions.some((s) => !["STOPPED", "EXPIRED", "CLOSED", "DELETED", "ARCHIVED", "REBALANCED_OUT", "FINALIZED", "COMPLETE", "COMPLETED", "CANCELLED", "RELEASED"].includes(String(s?.status || "").toUpperCase()));
    if (!hasLiveNkr) {
      resetNkrAggressiveDraftSelection();
    }
    setRotationBudgetRelease(value);
    setRotationBudgetReleased(false);
  }, [resetNkrAggressiveDraftSelection, setRotationBudgetRelease, setRotationBudgetReleased, rotationSessions]);

  const applyRunningNkrMode = useCallback(async (nextMode) => {
    // The selector is a draft for the next session only. Running sessions keep
    // the immutable mode stored at their own start.
    const next = String(nextMode || "DYNAMIC").toUpperCase();
    nkrCapitalModeRef.current = next;
    setNkrCapitalMode(next);
    setRotationBudgetReleased(false);
    // BUILD437: save only the wallet setup draft. Do NOT send sessions here; running
    // sessions keep the immutable start snapshot stored by the backend.
    try {
      if (wallet) {
        await api('/api/nkr/state', { method: 'POST', token, wallet, body: { nkrCapitalMode: next } });
      }
    } catch (persistModeErr) {
      console.warn('NKR mode draft persistence failed', persistModeErr);
    }
    return true;
  }, [setNkrCapitalMode, setRotationBudgetReleased, wallet, token, api]);

  const handleNkrCapitalModeChange = useCallback(async (value) => {
    const next = String(value || "DYNAMIC").toUpperCase();
    if (next === "AGGRESSIVE") {
      setNkrAggressiveAcceptedForDraft(false);
      setNkrAggressivePendingValue(next);
      setNkrAggressiveConsentOpen(true);
      return;
    }
    setNkrAggressiveAcceptedForDraft(false);
    setRotationBudgetReleased(false);
    // This changes only the next-session draft. Running sessions keep their immutable start snapshot.
    await applyRunningNkrMode(next);
  }, [applyRunningNkrMode, setRotationBudgetReleased, setNkrAggressiveAcceptedForDraft, setNkrAggressivePendingValue, setNkrAggressiveConsentOpen]);

  const confirmNkrAggressiveConsent = useCallback(async () => {
    const next = String(nkrAggressivePendingValue || "AGGRESSIVE").toUpperCase();
    setRotationBudgetReleased(false);
    setNkrAggressiveAcceptedForDraft(true);
    setNkrAggressivePendingValue("");
    setNkrAggressiveConsentOpen(false);
    // Aggressive becomes the active Strategist mode for the running session (not draft-only).
    await applyRunningNkrMode(next);
    try {
      if (wallet) {
        await api(`/api/nexus/nkr/aggressive-ack`, {
          method: "POST",
          token,
          wallet,
          body: {
            performance_mode: "AGGRESSIVE",
            accepted: true,
            warning_version: AGGRESSIVE_WARNING_VERSION,
            frontend_build: FRONTEND_BUILD_ID,
            scope: "draft_consent",
            budget_usd: Number(String(rotationBudgetRelease || "0").replace(",", ".")) || 0,
          },
        });
      }
    } catch (e) {
      console.warn("NKR Aggressive warning audit failed", e);
    }
  }, [nkrAggressivePendingValue, applyRunningNkrMode, setRotationBudgetReleased, setNkrAggressiveAcceptedForDraft, setNkrAggressivePendingValue, setNkrAggressiveConsentOpen, wallet, token, api, rotationBudgetRelease]);

  const cancelNkrAggressiveConsent = useCallback(() => {
    setNkrAggressiveAcceptedForDraft(false);
    setNkrAggressivePendingValue("");
    setNkrAggressiveConsentOpen(false);
  }, [setNkrAggressiveAcceptedForDraft, setNkrAggressivePendingValue, setNkrAggressiveConsentOpen]);

  const resetAggressiveDraftSelection = useCallback(() => {
    // Aggressive consent is session/draft specific, never persistent.
    // Any new budget input must start from Tactical again so the risk warning
    // cannot be bypassed by a previous session.
    setTradingStyle("TACTICAL");
    aggressiveRiskAcceptedRef.current = false;
    setAggressiveRiskAcceptedForDraft(false);
    setAggressiveRiskPendingValue("");
    setAggressiveRiskConsentOpen(false);
  }, [setTradingStyle, setAggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen]);

  const handleTradingBudgetInputChange = useCallback((value) => {
    // Performance selection is sticky. Editing budget must never silently change
    // AGGRESSIVE back to TACTICAL; only an explicit performance change / Clear resets it.
    setTradingBudgetUsd(value);
  }, [setTradingBudgetUsd]);

  const handleTradingBudgetSplitInputChange = useCallback((value) => {
    setTradingBudgetSplitInput(value);
  }, [setTradingBudgetSplitInput]);

  const handleTradingRiskModeChange = useCallback((value) => {
    applyTradingRiskPreset(value, tradingConfidenceMin);
  }, [applyTradingRiskPreset, tradingConfidenceMin]);

  const handleTradingConfidenceChange = useCallback((value) => {
    applyTradingRiskPreset(tradingRiskMode, value);
  }, [applyTradingRiskPreset, tradingRiskMode]);

  const handleTradingPerformanceChange = useCallback((value) => {
    const next = String(value || "TACTICAL").toUpperCase();
    if (next === "AGGRESSIVE") {
      aggressiveRiskAcceptedRef.current = false;
      setAggressiveRiskAcceptedForDraft(false);
      setAggressiveRiskPendingValue(next);
      setAggressiveRiskConsentOpen(true);
      return;
    }
    aggressiveRiskAcceptedRef.current = false;
    setAggressiveRiskAcceptedForDraft(false);
    setTradingStyle(next);
  }, [setTradingStyle, setAggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen]);

  const confirmAggressiveRiskConsent = useCallback(async () => {
    const next = String(aggressiveRiskPendingValue || "AGGRESSIVE").toUpperCase();
    // Sticky: confirmed style stays selected until the user changes it.
    setTradingStyle(next);
    setTradingRiskExpanded(false);
    aggressiveRiskAcceptedRef.current = true;
    setAggressiveRiskAcceptedForDraft(true);
    setAggressiveRiskPendingValue("");
    setAggressiveRiskConsentOpen(false);

    // Backend audit trail: wallet-bound proof that the user accepted
    // the Aggressive warning for this draft and any following session start.
    try {
      if (wallet) {
        await api(`/api/nexus/trading/aggressive-ack`, {
          method: "POST",
          wallet,
          body: {
            performance_mode: "AGGRESSIVE",
            accepted: true,
            warning_version: AGGRESSIVE_WARNING_VERSION,
            frontend_build: FRONTEND_BUILD_ID,
            scope: "active_or_draft",
            budget_usd: Number(String(tradingBudgetUsd || "0").replace(",", ".")) || 0,
          },
        });
      }
    } catch (e) {
      console.warn("Aggressive warning audit failed", e);
    }
  }, [aggressiveRiskPendingValue, setTradingStyle, setTradingRiskExpanded, setAggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen, wallet, api, tradingBudgetUsd]);

  const cancelAggressiveRiskConsent = useCallback(() => {
    aggressiveRiskAcceptedRef.current = false;
    setAggressiveRiskAcceptedForDraft(false);
    setAggressiveRiskPendingValue("");
    setAggressiveRiskConsentOpen(false);
  }, [setAggressiveRiskAcceptedForDraft, setAggressiveRiskPendingValue, setAggressiveRiskConsentOpen]);

  const isTradingAggressivePerformance = String(tradingStyle || "").toUpperCase() === "AGGRESSIVE";

  useEffect(() => {
    if (isTradingAggressivePerformance && tradingRiskExpanded) {
      setTradingRiskExpanded(false);
    }
  }, [isTradingAggressivePerformance, tradingRiskExpanded, setTradingRiskExpanded]);





  // const uiChainKey defined above (useMemo) 

  const isEthChain = String(uiChainKey || "").toUpperCase().includes("ETH");

  const [gridInvestQty, setGridInvestQty] = useState("");
  const [gridMeta, setGridMeta] = useState({ tick: null, price: null });
  const loadEngineHistory = useCallback(async (engine) => {
    const upper = String(engine || "").toUpperCase();
    if (!resolveWalletAddress(wallet) || !["GRID","NKR","TRADER"].includes(upper)) return;
    try {
      const r = await api(`/api/nexus/engine-history?engine=${upper}&limit=2000`, { wallet, token });
      const rows = Array.isArray(r?.events) ? r.events : [];
      if (upper === "GRID") setGridEventHistory(rows);
      else if (upper === "NKR") setNkrEventHistory(rows);
      else setTraderEventHistory(rows);
    } catch {}
  }, [wallet, token]);

  useEffect(() => { loadEngineHistory("GRID"); loadEngineHistory("NKR"); loadEngineHistory("TRADER"); }, [loadEngineHistory]);

  const [gridOrders, setGridOrders] = useState([]);
  const [gridOrdersOpen, setGridOrdersOpen] = useState(false);

  useEffect(() => {
    const wa = resolveWalletAddress(wallet); if (!wa || !Array.isArray(gridOrders)) return;
    const events = gridOrders.map((o, idx) => {
      const ts = Number(o?.updated_ts || o?.updatedTs || o?.filled_ts || o?.created_ts || o?.createdTs || Date.now());
      const status = String(o?.status || o?.state || "ORDER").toUpperCase();
      return {
        eventKey: `GRID:${o?.id || o?.order_id || idx}:${status}:${ts}`,
        eventType: status, status, ts,
        chain: o?.chain || o?.network || activeGridChainKey || gridChain || "",
        asset: o?.symbol || o?.asset || gridItem || "",
        side: o?.side || o?.action || "",
        priceUsd: Number(o?.fill_price || o?.filled_price || o?.price || 0),
        quantity: Number(o?.filled_qty || o?.quantity || o?.qty || 0),
        budgetUsd: Number(o?.amountUsd || o?.amount_usd || o?.reserved || o?.notional || 0),
        grossUsd: Number(o?.grossUsd || o?.gross_profit_usd || 0), costsUsd: Number(o?.costsUsd || o?.fees_usd || o?.feeUsd || 0),
        netUsd: Number(o?.netUsd || o?.profitUsd || o?.pnlUsd || o?.realized_pnl || 0),
        reason: o?.reason || o?.message || "", txHash: o?.txHash || o?.tx_hash || "",
      };
    });
    const t=setTimeout(async () => { try { await api('/api/nexus/engine-history/sync', { method:'POST', wallet, token, body:{ engine:'GRID', events } }); await loadEngineHistory('GRID'); } catch {} }, 900);
    return () => clearTimeout(t);
  }, [gridOrders, wallet, token, activeGridChainKey, gridChain, gridItem, loadEngineHistory]);

  useEffect(() => {
    const wa = resolveWalletAddress(wallet); if (!wa || !Array.isArray(tradingSessions)) return;
    const events=[];
    tradingSessions.forEach((s, si) => {
      const slots=Array.isArray(s?.slots) ? s.slots : [s];
      slots.forEach((slot, idx) => {
        const meta=slot?.meta && typeof slot.meta === 'object' ? slot.meta : {};
        const ts=Number(slot?.updated_ts || slot?.updatedTs || slot?.closed_at || slot?.opened_at || s?.updated_ts || s?.created_ts || Date.now());
        const status=String(slot?.status || slot?.state || s?.status || 'SESSION').toUpperCase();
        events.push({
          eventKey:`TRADER:${s?.id || s?.session_id || si}:${slot?.id || slot?.slot_id || idx}:${status}:${ts}`,
          eventType:status,status,ts,
          chain:slot?.chain || s?.chain || meta?.chain || '', asset:slot?.symbol || slot?.asset || s?.symbol || s?.asset || meta?.symbol || '',
          side:slot?.side || slot?.action || meta?.side || '', priceUsd:Number(slot?.exit_price_usd || slot?.entry_price_usd || slot?.paper_mark_price || meta?.paper_mark_price || 0),
          quantity:Number(slot?.quantity || slot?.qty || meta?.quantity || 0), budgetUsd:Number(slot?.amountUsd || slot?.reserved_capital_usd || s?.budgetUsd || s?.budget || 0),
          grossUsd:Number(slot?.grossUsd || slot?.gross_profit_usd || meta?.grossUsd || 0), costsUsd:Number(slot?.costsUsd || slot?.fees_usd || meta?.costsUsd || 0),
          netUsd:Number(slot?.netUsd || slot?.profitUsd || slot?.pnlUsd || meta?.netUsd || 0), reason:slot?.reason || meta?.reason || s?.reason || '', txHash:slot?.txHash || meta?.txHash || '',
        });
      });
    });
    const t=setTimeout(async () => { try { await api('/api/nexus/engine-history/sync', { method:'POST', wallet, token, body:{ engine:'TRADER', events } }); await loadEngineHistory('TRADER'); } catch {} }, 1100);
    return () => clearTimeout(t);
  }, [tradingSessions, wallet, token, loadEngineHistory]);

  useEffect(() => {
    const wa = resolveWalletAddress(wallet); if (!wa || !Array.isArray(rotationSessions)) return;
    const events=[];
    rotationSessions.forEach((s, si) => {
      const slots=Array.isArray(s?.slots) ? s.slots : [s];
      slots.forEach((slot, idx) => {
        const meta=slot?.meta && typeof slot.meta === 'object' ? slot.meta : (s?.meta && typeof s.meta === 'object' ? s.meta : {});
        const ts=Number(slot?.updated_ts || slot?.updatedTs || slot?.closed_at || slot?.opened_at || s?.updated_ts || s?.created_ts || Date.now());
        const status=String(slot?.status || slot?.state || s?.status || 'SESSION').toUpperCase();
        events.push({
          eventKey:`NKR:${s?.id || s?.session_id || si}:${slot?.id || slot?.slot_id || idx}:${status}:${ts}`,
          eventType:status,status,ts,
          chain:slot?.chain || s?.chain || meta?.chain || '',
          asset:slot?.symbol || slot?.asset || s?.symbol || s?.asset || meta?.symbol || '',
          side:slot?.side || slot?.action || meta?.side || '',
          priceUsd:Number(slot?.exit_price_usd || slot?.entry_price_usd || slot?.paper_mark_price || meta?.paper_mark_price || 0),
          quantity:Number(slot?.quantity || slot?.qty || meta?.quantity || 0),
          budgetUsd:Number(slot?.amountUsd || slot?.reserved_capital_usd || s?.budgetUsd || s?.budget || 0),
          grossUsd:Number(slot?.grossUsd || slot?.gross_profit_usd || meta?.grossUsd || 0),
          costsUsd:Number(slot?.costsUsd || slot?.fees_usd || meta?.costsUsd || 0),
          netUsd:Number(slot?.netUsd || slot?.profitUsd || slot?.pnlUsd || meta?.netUsd || 0),
          reason:slot?.reason || meta?.reason || s?.reason || '',
          txHash:slot?.txHash || slot?.tx_hash || meta?.txHash || '',
        });
      });
    });
    const t=setTimeout(async () => { try { await api('/api/nexus/engine-history/sync', { method:'POST', wallet, token, body:{ engine:'NKR', events } }); await loadEngineHistory('NKR'); } catch {} }, 1000);
    return () => clearTimeout(t);
  }, [rotationSessions, wallet, token, loadEngineHistory]);
  const [gridSetupOpen, setGridSetupOpen] = useState(false);
  const [gridVaultStats, setGridVaultStats] = useState({ vault: 0, reserved: 0, free: 0 });
  // Helper: extract order id from different backend schemas
  const idOf = (o) => o?.order_id ?? o?.orderId ?? o?.id ?? o?._id ?? o?.uuid ?? null;

  const patchGridOrderStatusLocal = useCallback((orderId, nextStatus) => {
    const oid = String(orderId || "");
    if (!oid) return;
    const status = String(nextStatus || "").toUpperCase();
    setGridOrders((prev) => (Array.isArray(prev) ? prev : []).map((o) => {
      const currentId = String(idOf(o) || "");
      if (currentId !== oid) return o;
      return {
        ...o,
        status,
        state: status,
        updated_ts: Math.floor(Date.now() / 1000),
      };
    }));
  }, []);

  // Normalize orders coming from backend/polling so the UI can't show duplicates.
  // (Some backend revisions may return the same order twice during eventual consistency.)

  useEffect(() => {
    if (gridOrders.length === 0) {
      setGridOrdersOpen(false);
      return;
    }
    if (gridOrders.length <= 3 && !isCompactMobile) {
      setGridOrdersOpen(true);
    }
  }, [gridOrders.length, isCompactMobile]);

  const normalizeGridOrders = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return [];
      const seen = new Set();
      const out = [];
      for (const o of arr) {
        const id = idOf(o);
        const key = id != null ? String(id) : JSON.stringify(o);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(o);
      }
      return out;
    },
    [idOf]
  );

  const [manualSide, setManualSide] = useState("SELL");
  const [manualMaxLossPct, setManualMaxLossPct] = useState("15");
  const [manualPrice, setManualPrice] = useState("");
  const [manualBuyMode, setManualBuyMode] = useState("QTY"); // USD | QTY (only used for BUY)
  const [manualUsd, setManualUsd] = useState("");
  const [manualSlippagePct, setManualSlippagePct] = useState(5); // %
  const [manualDeadlineMin, setManualDeadlineMin] = useState(20); // minutes
  const GRID_PRICE_PRESETS = useMemo(
    () => ({
      FAST: [0.25, 0.5, 1],
      STANDARD: [0.5, 1, 2],
      WIDE: [1, 2, 3],
      VERY_WIDE: [5, 10, 15],
    }),
    []
  );
  const [manualPricePreset, setManualPricePreset] = useState("STANDARD");
  const [aiGridAssistState, setAiGridAssistState] = useState({ active: false, preset: "", slippage: "", note: "" });
  const [aiGridManualOverride, setAiGridManualOverride] = useState(false);
  const aiGridAssistKeyRef = useRef("");
  const gridQuickSteps = useMemo(() => {
    const arr = GRID_PRICE_PRESETS?.[manualPricePreset] || GRID_PRICE_PRESETS?.STANDARD || [0.5, 1, 2];
    return Array.isArray(arr) ? arr : [0.5, 1, 2];
  }, [GRID_PRICE_PRESETS, manualPricePreset]);
  const [manualQty, setManualQty] = useState("");
  // BUILD387: payout only USDC or USDT (native/token payout removed).
  const currentPayoutAssets = useMemo(() => [...NEXUS_STABLE_PAYOUT_ASSETS], []);
  const visiblePayoutAssets = useMemo(() => currentPayoutAssets, [currentPayoutAssets]);
  const extraPayoutAssets = useMemo(() => [], []);
  const [payoutMenuOpen, setPayoutMenuOpen] = useState(false);
  const payoutMenuRef = useRef(null);
  useEffect(() => {
    const cur = normalizeStablePayoutAsset(manualPayoutAsset, "USDC");
    if (cur !== String(manualPayoutAsset || "").toUpperCase()) {
      setManualPayoutAsset(cur);
    }
  }, [manualPayoutAsset]);
  useEffect(() => {
    if (!payoutMenuOpen) return;
    const onDown = (e) => {
      if (payoutMenuRef.current && !payoutMenuRef.current.contains(e.target)) {
        setPayoutMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [payoutMenuOpen]);


  const renderPayoutAssetSelector = (label = "Payout asset", options = {}) => {
    const allowExtraAssets = options?.allowExtraAssets !== false;
    return (
    <div className="formRow">
      <label>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {visiblePayoutAssets.map((asset) => {
          const active = String(manualPayoutAsset || "").toUpperCase() === String(asset).toUpperCase();
          return (
            <button
              key={asset}
              type="button"
              onClick={() => setManualPayoutAsset(String(asset).toUpperCase())}
              style={{
                ...compactGridChipStyle,
                minWidth: 66,
                background: active ? "linear-gradient(90deg, #22c55e, #16a34a)" : "rgba(34,197,94,.16)",
                color: active ? "#071512" : "#d9fff0",
                border: active ? "1px solid rgba(34,197,94,.55)" : "1px solid rgba(34,197,94,.32)",
                boxShadow: active ? "0 0 12px rgba(34,197,94,.28)" : "none",
                fontWeight: active ? 800 : 700,
              }}
              title={`Set payout asset to ${asset}`}
            >
              {asset}
            </button>
          );
        })}
        {allowExtraAssets && extraPayoutAssets.length > 0 && (
          <div ref={payoutMenuRef} style={{ position: "relative", minWidth: 220 }}>
            <button
              type="button"
              onClick={() => setPayoutMenuOpen((v) => !v)}
              style={{
                width: "100%",
                height: isCompactMobile ? 32 : 36,
                padding: "0 12px",
                borderRadius: 10,
                background: "rgba(34,197,94,.16)",
                color: "#ffffff",
                border: "1px solid rgba(34,197,94,.38)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                fontWeight: 800,
                boxShadow: payoutMenuOpen ? "0 0 12px rgba(34,197,94,.22)" : "none",
              }}
            >
              <span>{extraPayoutAssets.includes(String(manualPayoutAsset || "").toUpperCase()) ? String(manualPayoutAsset || "").toUpperCase() : "More assets"}</span>
              <span style={{ fontSize: 12 }}>{payoutMenuOpen ? "▲" : "▼"}</span>
            </button>
            {payoutMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "linear-gradient(180deg, rgba(74,222,128,.98), rgba(34,197,94,.98))",
                  border: "1px solid rgba(34,197,94,.55)",
                  boxShadow: "0 16px 34px rgba(0,0,0,.35)",
                }}
              >
                {extraPayoutAssets.map((asset) => {
                  const active = String(manualPayoutAsset || "").toUpperCase() === String(asset).toUpperCase();
                  return (
                    <button
                      key={asset}
                      type="button"
                      onClick={() => {
                        setManualPayoutAsset(String(asset).toUpperCase());
                        setPayoutMenuOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        background: active ? "linear-gradient(90deg, #86efac, #4ade80)" : "rgba(255,255,255,.10)",
                        color: "#071512",
                        border: "none",
                        borderTop: "1px solid rgba(7,21,18,.10)",
                        fontWeight: active ? 800 : 700,
                        cursor: "pointer",
                      }}
                    >
                      {asset}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
  };
  const [gridOrderChainOpen, setGridOrderChainOpen] = useState({});

  // AI
  const [aiSelected, setAiSelected] = useState([]);
  const [compareForceNonce, setCompareForceNonce] = useState(0);
  const syncAppStateFromServer = useCallback(async () => {
    const wa = resolveWalletAddress(wallet);
    if (!wa) {
      appStateHydratedRef.current = true;
      return;
    }
    if (appStateSyncBusyRef.current) return;
    appStateSyncBusyRef.current = true;
    try {
      const r = await api(`/api/app-state?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}`, { method: "GET", token, wallet: wa });
      const state = r?.state || {};
      const serverUpdatedTs = Number(r?.updated_ts || 0) || 0;
      const lastSeenServerTs = Number(appStateLastServerTsRef.current || 0) || 0;
      let serverCompare = Array.isArray(state?.compare) ? state.compare.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20) : [];
      // A fresh/restarted backend can temporarily have no saved app-state row. In that
      // case it must not erase the user's visible Compare selection. Recover from the
      // current watchlist only when the server truly has no persisted state.
      if (!serverCompare.length) {
        let persistedCompare = [];
        try {
          const raw = localStorage.getItem(compareStorageKey);
          const parsed = JSON.parse(raw || "[]");
          persistedCompare = Array.isArray(parsed) ? parsed.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20) : [];
        } catch {}
        if (persistedCompare.length) {
          serverCompare = persistedCompare;
        } else if (Number(r?.updated_ts || 0) === 0) {
          const recovered = (Array.isArray(watchItems) ? watchItems : [])
            .map((x) => String((x && typeof x === "object" ? (x.symbol || x.ticker || x.code) : x) || "").toUpperCase().trim())
            .filter(Boolean)
            .slice(0, 20);
          if (recovered.length) serverCompare = recovered;
        }
      }
      const serverTf = String(state?.timeframe || "90D").toUpperCase();
      const serverIndex = state?.indexMode == null ? true : !!state.indexMode;
      const serverAi = Array.isArray(state?.aiSelected) ? state.aiSelected.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 6) : [];
      const serverUi = state?.ui && typeof state.ui === "object" ? state.ui : {};
      const serverTradingSessions = Array.isArray(serverUi.tradingSessions) ? serverUi.tradingSessions.filter((x) => x && typeof x === "object") : [];
      const serverActiveTradingSessionId = String(serverUi.activeTradingSessionId || "").trim();
      // Rotation runtime sessions are loaded from /api/rotation-sessions, not from ui_state_json.
      // ui_state_json only keeps display/settings values; the lifecycle itself is Trading-style backend state.
      const serverRotationSessions = [];
      const neverSynced = String(appStateSyncedWallet || "").toLowerCase() !== String(wa || "").toLowerCase();
      const localCompare = Array.isArray(compareSet) ? compareSet.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20) : [];
      const localAi = Array.isArray(aiSelected) ? aiSelected.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 6) : [];
      const hasLocal = localCompare.length || localAi.length || String(timeframe || "90D").toUpperCase() !== "90D" || !!indexMode === false;
      const hasServer = serverCompare.length || serverAi.length || serverTf !== "90D" || serverIndex !== true;
      const sig = (cmp, tf, idx, ai) => JSON.stringify({ cmp: cmp || [], tf: String(tf || "90D").toUpperCase(), idx: !!idx, ai: ai || [] });
      const serverSig = sig(serverCompare, serverTf, serverIndex, serverAi);
      const localSig = sig(localCompare, timeframe, indexMode, localAi);

      // Compare selections must survive refreshes and temporary/partial backend state.
      // An empty server compare array is NOT allowed to erase a non-empty wallet-local selection.
      // This was the cause of the disappearing checkboxes: the 10s app-state refresh applied [],
      // then the local persistence effect saved that empty array over the wallet key as well.
      const effectiveServerCompare = serverCompare.length ? serverCompare : localCompare;
      const effectiveServerSig = sig(effectiveServerCompare, serverTf, serverIndex, serverAi);
      const serverIsAuthoritative = serverUpdatedTs > 0 || hasServer || !hasLocal;
      if (serverIsAuthoritative) {
        appStateApplyingServerRef.current = true;
        if (effectiveServerSig !== localSig) {
          setCompareSet(effectiveServerCompare);
          setTimeframe(serverTf || "90D");
          setIndexMode(!!serverIndex);
          setAiSelected(serverAi);
          try { localStorage.removeItem(LS_COMPARE_SERIES_CACHE); localStorage.removeItem(LS_COMPARE_STORE); } catch {}
          setCompareForceNonce((n) => n + 1);
        }
        if (serverUi.watchSortMode != null) setWatchSortMode(String(serverUi.watchSortMode || "manual"));
        if (serverUi.gridMode != null) setGridMode(String(serverUi.gridMode || "normal"));
        if (serverUi.gridChain != null) setGridChain(String(serverUi.gridChain || DEFAULT_CHAIN || "POL").toUpperCase());
        if (serverUi.gridItem != null) setGridItem(String(serverUi.gridItem || "").toUpperCase());
        if (neverSynced && !tradingRuntimeEditingRef.current) {
          if (serverUi.tradingRuntimeHours != null) setTradingRuntimeHours(String(serverUi.tradingRuntimeHours));
          if (serverUi.tradingRuntimeUnit != null) setTradingRuntimeUnit(String(serverUi.tradingRuntimeUnit || "hours"));
        }
        if (serverUi.tradingHoldHours != null) setTradingHoldHours(String(serverUi.tradingHoldHours));
        if (serverUi.tradingAllowedAssets != null) setTradingAllowedAssets(String(serverUi.tradingAllowedAssets));
        if (serverUi.tradingAllowedChains != null) setTradingAllowedChains(String(serverUi.tradingAllowedChains));
        if (serverUi.tradingRiskMode != null) setTradingRiskMode(String(serverUi.tradingRiskMode));
        if (serverUi.tradingCautionDrawdownPct != null) setTradingCautionDrawdownPct(String(serverUi.tradingCautionDrawdownPct));
        if (serverUi.tradingHardStopPct != null) setTradingHardStopPct(String(serverUi.tradingHardStopPct));
        if (serverUi.tradingProfitLockPct != null) setTradingProfitLockPct(String(serverUi.tradingProfitLockPct));
        if (serverUi.tradingReuseProfitPct != null) setTradingReuseProfitPct(String(serverUi.tradingReuseProfitPct));
        if (serverUi.tradingMaxCombinedSlots != null) setTradingMaxCombinedSlots(String(serverUi.tradingMaxCombinedSlots));
        if (serverUi.tradingMaxSlippagePct != null) setTradingMaxSlippagePct(String(serverUi.tradingMaxSlippagePct));
        if (serverUi.tradingMaxTrades != null) setTradingMaxTrades(String(serverUi.tradingMaxTrades));
        if (serverUi.tradingConfidenceMin != null) setTradingConfidenceMin(String(serverUi.tradingConfidenceMin));
        if (serverUi.tradingStyle != null) setTradingStyle(String(serverUi.tradingStyle));
        if (serverUi.tradingBudgetUsd != null) setTradingBudgetUsd(String(serverUi.tradingBudgetUsd));
        if (serverUi.tradingBudgetSplitInput != null) setTradingBudgetSplitInput(String(serverUi.tradingBudgetSplitInput));
        const rotationSettingsSource = serverUi;
        if (rotationSettingsSource.rotationRuntimeHours != null) setRotationRuntimeHours(String(rotationSettingsSource.rotationRuntimeHours));
        if (!nkrMaxAssetsEditingRef.current && rotationSettingsSource.rotationMaxActiveSessions != null) setRotationMaxActiveSessions(String(rotationSettingsSource.rotationMaxActiveSessions));
        if (rotationSettingsSource.rotationRiskLimit != null) setRotationRiskLimit(String(rotationSettingsSource.rotationRiskLimit));
        if (rotationSettingsSource.rotationMaxSlippage != null) setRotationMaxSlippage(String(rotationSettingsSource.rotationMaxSlippage));
        if (rotationSettingsSource.rotationMinNetAdvantage != null) setRotationMinNetAdvantage(String(rotationSettingsSource.rotationMinNetAdvantage));
        if (rotationSettingsSource.rotationMode != null) setRotationMode(String(rotationSettingsSource.rotationMode));
        if (rotationSettingsSource.nkrCapitalMode != null && !nkrStartPendingRef.current && !nkrModeEditingRef.current) setNkrCapitalMode(String(rotationSettingsSource.nkrCapitalMode));
        if (rotationSettingsSource.nkrObservationWindow != null) setNkrObservationWindow(String(rotationSettingsSource.nkrObservationWindow));
        if (rotationSettingsSource.nkrProfitMode != null) setNkrProfitMode(String(rotationSettingsSource.nkrProfitMode));
        // BUILD440: period is local UI draft. Restore only on first wallet hydrate.
        // Continuous app-state polls used to re-apply nkrPeriodDays as hours/24 (e.g. 0.08)
        // and flip the unit to hours a few seconds later.
        if (neverSynced && !nkrPeriodEditingRef.current && (rotationSettingsSource.nkrPeriodUnit != null || rotationSettingsSource.nkrPeriodValue != null || rotationSettingsSource.nkrPeriodHours != null || rotationSettingsSource.nkrPeriodDays != null)) {
          const explicitUnit = String(rotationSettingsSource.nkrPeriodUnit || "").toLowerCase().trim();
          const restoredHours = Number(rotationSettingsSource.nkrPeriodHours);
          const restoredDays = Number(rotationSettingsSource.nkrPeriodDays);
          const restoredValue = Number(rotationSettingsSource.nkrPeriodValue);
          // Prefer explicit unit. Never invent unit from hours<24 when unit is present.
          let restoredUnit = explicitUnit === "hours" || explicitUnit === "days" ? explicitUnit : "";
          if (!restoredUnit) {
            // Legacy rows without unit: values < 1 that match hours/24 are day-fractions → show hours.
            if (Number.isFinite(restoredValue) && restoredValue > 0 && restoredValue < 1 && Number.isFinite(restoredHours) && restoredHours >= 1) {
              restoredUnit = "hours";
            } else if (Number.isFinite(restoredHours) && restoredHours > 0 && restoredHours < 24 && !(Number.isFinite(restoredDays) && restoredDays >= 1)) {
              restoredUnit = "hours";
            } else {
              restoredUnit = "days";
            }
          }
          let draftValue;
          if (restoredUnit === "hours") {
            // Show hours number the user typed — never a day fraction.
            if (Number.isFinite(restoredValue) && restoredValue >= 1) draftValue = restoredValue;
            else if (Number.isFinite(restoredHours) && restoredHours > 0) draftValue = restoredHours;
            else if (Number.isFinite(restoredDays) && restoredDays > 0) draftValue = restoredDays * 24;
            else draftValue = 24;
          } else {
            // Days unit: if stored value is a day-fraction (<1) but hours exist, convert to whole/half days.
            if (Number.isFinite(restoredValue) && restoredValue >= 1) draftValue = restoredValue;
            else if (Number.isFinite(restoredDays) && restoredDays >= 1) draftValue = restoredDays;
            else if (Number.isFinite(restoredHours) && restoredHours > 0) draftValue = restoredHours / 24;
            else if (Number.isFinite(restoredValue) && restoredValue > 0) draftValue = restoredValue; // rare fraction
            else draftValue = 1;
            // Avoid showing 0.08 days — round to 2 decimals only when not near-integer.
            if (draftValue < 1) {
              // Prefer switching display to hours for sub-day periods.
              restoredUnit = "hours";
              draftValue = Number.isFinite(restoredHours) && restoredHours > 0 ? restoredHours : Math.max(1, Math.round(draftValue * 24));
            }
          }
          const rounded = Math.abs(draftValue - Math.round(draftValue)) < 1e-9
            ? Math.round(draftValue)
            : Math.round(draftValue * 100) / 100;
          setNkrPeriodUnit(restoredUnit);
          setNkrPeriodDays(String(rounded));
        }
        if (rotationSettingsSource.rotationBudgetRelease != null) setRotationBudgetRelease(String(rotationSettingsSource.rotationBudgetRelease));
        if (rotationSettingsSource.rotationShadowSnapshot && typeof rotationSettingsSource.rotationShadowSnapshot === "object") setRotationShadowSnapshot(rotationSettingsSource.rotationShadowSnapshot);
        if (Array.isArray(rotationSettingsSource.rotationShadowEvents)) setRotationShadowEvents(rotationSettingsSource.rotationShadowEvents);
        if (rotationSettingsSource.rotationNetworkScope != null) setRotationNetworkScope(String(rotationSettingsSource.rotationNetworkScope));
        // BUILD417: ui_state_json is settings/display persistence only, never Trader lifecycle truth.
        // Active Trader sessions are reconstructed from the authoritative execution queue and
        // CoreVault on-chain inventory. Rehydrating serverUi.tradingSessions here could resurrect
        // a session that was already finalized on-chain.
        void serverTradingSessions;
        void serverActiveTradingSessionId;
        // NKR sessions are hydrated separately from /api/rotation-sessions.
      }
      if (serverUpdatedTs) storeAppStateServerTs(serverUpdatedTs);
      setAppStateSyncedWallet(wa);
    } catch (e) {
      console.warn("app-state sync failed", e);
    } finally {
      appStateHydratedRef.current = true;
      appStateSyncBusyRef.current = false;
      setTimeout(() => { appStateApplyingServerRef.current = false; }, 0);
    }
  }, [wallet, token, compareSet, timeframe, indexMode, aiSelected, appStateSyncedWallet, setAppStateSyncedWallet, storeAppStateServerTs, compareStorageKey]);

  useEffect(() => {
    syncAppStateFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);

  useEffect(() => {
    const wa = resolveWalletAddress(wallet);
    if (!wa || !appStateHydratedRef.current) return;
    if (appStateApplyingServerRef.current) return;
    const payload = {
      wallet: wa,
      wallet_address: wa,
      compare: Array.isArray(compareSet) ? compareSet.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 20) : [],
      timeframe,
      indexMode,
      aiSelected: Array.isArray(aiSelected) ? aiSelected.map((x) => String(x || "").toUpperCase()).filter(Boolean).slice(0, 6) : [],
      ui: {
        watchSortMode,
        gridMode,
        gridChain: String(activeGridChainKey || gridChain || DEFAULT_CHAIN || "POL").toUpperCase(),
        gridItem: String(gridItem || "").toUpperCase(),
        tradingRuntimeHours,
        tradingRuntimeUnit,
        tradingHoldHours,
        tradingAllowedAssets,
        tradingAllowedChains,
        tradingRiskMode,
        tradingCautionDrawdownPct,
        tradingHardStopPct,
        tradingProfitLockPct,
        tradingReuseProfitPct,
        tradingMaxCombinedSlots,
        tradingMaxSlippagePct,
        tradingMaxTrades,
        tradingConfidenceMin,
        tradingStyle,
        tradingBudgetUsd,
        tradingBudgetSplitInput,
        tradingSessions: (Array.isArray(tradingSessions) ? tradingSessions : []).slice(0, 30),
        activeTradingSessionId,
        rotationRuntimeHours,
        rotationMaxActiveSessions,
        rotationRiskLimit,
        rotationMaxSlippage,
        rotationMinNetAdvantage,
        rotationMode,
        nkrCapitalMode,
        nkrObservationWindow,
        nkrProfitMode,
        // BUILD440: nkrPeriodValue = what the user sees in the input; unit is authoritative.
        nkrPeriodValue: nkrPeriodDays,
        nkrPeriodUnit,
        nkrPeriodHours: normalizeNkrPeriodHours(),
        // True day-count for analytics only — never feed this back into the input as a fraction.
        nkrPeriodDays: (String(nkrPeriodUnit || "").toLowerCase() === "days"
          ? (Number(String(nkrPeriodDays || "").replace(",", ".")) || 1)
          : (normalizeNkrPeriodHours() / 24)),
        rotationBudgetRelease,
        rotationShadowSnapshot,
        rotationShadowEvents: (Array.isArray(rotationShadowEvents) ? rotationShadowEvents : []),
        rotationNetworkScope,
      },
    };
    const t = setTimeout(async () => {
      if (appStateSyncBusyRef.current || appStateApplyingServerRef.current) return;
      appStateSyncBusyRef.current = true;
      try {
        const saved = await api("/api/app-state", { method: "POST", token, wallet: wa, body: payload });
        storeAppStateServerTs(saved?.updated_ts);
        setAppStateSyncedWallet(wa);
      } catch (e) {
        console.warn("app-state save failed", e);
      } finally {
        appStateSyncBusyRef.current = false;
      }
    }, 300);
    return () => clearTimeout(t);
  }, [wallet, token, compareSet, timeframe, indexMode, aiSelected, watchSortMode, gridMode, activeGridChainKey, gridChain, gridItem, tradingRuntimeHours, tradingRuntimeUnit, tradingHoldHours, tradingAllowedAssets, tradingAllowedChains, tradingRiskMode, tradingCautionDrawdownPct, tradingHardStopPct, tradingProfitLockPct, tradingReuseProfitPct, tradingMaxCombinedSlots, tradingMaxSlippagePct, tradingMaxTrades, tradingConfidenceMin, tradingStyle, tradingBudgetUsd, tradingBudgetSplitInput, tradingSessions, activeTradingSessionId, rotationRuntimeHours, rotationMaxActiveSessions, rotationRiskLimit, rotationMaxSlippage, rotationMinNetAdvantage, rotationMode, nkrCapitalMode, nkrObservationWindow, nkrProfitMode, nkrPeriodDays, nkrPeriodUnit, normalizeNkrPeriodHours, rotationBudgetRelease, rotationShadowSnapshot, rotationShadowEvents, rotationNetworkScope, setAppStateSyncedWallet, storeAppStateServerTs]);

  // Rotation runtime persistence: backend-first, wallet-bound, Trading-style.
  // The Rotation lifecycle is stored through /api/rotation-sessions. /api/app-state only keeps settings/display state.
  const canonicalizeNkrSessions = useCallback((rows = []) => {
    const terminal = new Set(["STOPPED", "FINALIZED", "CLOSED", "COMPLETE", "COMPLETED", "EXPIRED", "DELETED", "ARCHIVED", "RELEASED"]);
    const getOnchainId = (s = {}) => {
      const meta = s?.meta && typeof s.meta === "object" ? s.meta : {};
      const raw = s?.onchainSessionId || s?.onchain_session_id || meta?.onchain_session_id || meta?.onchainSessionId || "";
      if (/^\d+$/.test(String(raw || "")) && Number(raw) > 0) return String(raw);
      const sid = String(s?.id || s?.session_id || "");
      const m = sid.match(/^NKR-LIVE-(\d+)$/i);
      return m ? m[1] : "";
    };
    const richness = (s = {}) => {
      const meta = s?.meta && typeof s.meta === "object" ? s.meta : {};
      const sym = String(s?.positionAsset || s?.targetAsset || s?.asset || s?.symbol || meta?.nkr_active_asset || "").toUpperCase();
      return [
        getOnchainId(s) ? 1 : 0,
        sym && !["ASSET", "WAITING"].includes(sym) ? 1 : 0,
        Number(s?.positionQty || meta?.nkr_position_qty || 0) > 0 ? 1 : 0,
        String(s?.lastTxHash || meta?.nkr_live_buy_tx_hash || "") ? 1 : 0,
        Number(s?.updatedAt || s?.updated_at || 0) || 0,
      ];
    };
    const isRicher = (a, b) => {
      const aa = richness(a), bb = richness(b);
      for (let i = 0; i < aa.length; i += 1) {
        if (aa[i] !== bb[i]) return aa[i] > bb[i];
      }
      return false;
    };
    const normalizeAuthoritativeStatus = (row = {}) => {
      const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
      const rawId = Number(row?.onchainStatusId ?? row?.rawStatusId ?? row?.statusId ?? meta?.raw_status_id ?? meta?.status_id ?? 0);
      const byId = rawId === 1 ? "ACTIVE" : rawId === 2 ? "PAUSED" : rawId === 3 ? "CLOSING" : rawId === 4 ? "FINALIZED" : "";
      const explicit = String(row?.onchainStatus || row?.statusLabel || row?.status || row?.lifecycleState || meta?.session_status || meta?.lifecycle_state || "").toUpperCase();
      const status = byId || explicit || "ACTIVE";
      return {
        ...row, status, statusLabel: status, onchainStatus: status, lifecycleState: status,
        rawStatusId: rawId || row?.rawStatusId, statusId: rawId || row?.statusId, onchainStatusId: rawId || row?.onchainStatusId,
        meta: { ...meta, lifecycle_state: status, session_status: status, ...(rawId ? { raw_status_id: rawId, status_id: rawId } : {}) },
      };
    };
    const input = Array.isArray(rows) ? rows.filter((x) => x && typeof x === "object").map(normalizeAuthoritativeStatus) : [];
    const hasLive = input.some((s) => getOnchainId(s));
    const map = new Map();
    for (const s of input) {
      const status = String(s?.status || "").toUpperCase();
      const oid = getOnchainId(s);
      if (hasLive && !oid && !terminal.has(status)) continue;
      const sid = String(s?.id || s?.session_id || "").trim();
      const chainKey = normalizeNkrChainKey(s?.chain || s?.chainKey || s?.meta?.chain || s?.chainId || s?.meta?.chain_id || "");
      const key = oid ? `ONCHAIN:${chainKey}:${oid}` : `LOCAL:${sid}`;
      const current = map.get(key);
      if (!current || isRicher(s, current)) map.set(key, s);
    }
    return Array.from(map.values());
  }, []);

  const syncRotationSessionsFromServer = useCallback(async () => {
    const wa = resolveWalletAddress(wallet);
    if (!wa) {
      rotationBackendHydratedRef.current = true;
      return;
    }
    if (rotationBackendSyncBusyRef.current) return;
    rotationBackendSyncBusyRef.current = true;
    rotationBackendApplyingRef.current = true;
    try {
      // Cache-bust so multi-device clients never keep a stale "open session" snapshot.
      const r = await api(`/api/rotation-sessions?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}&_ts=${Date.now()}`, { method: "GET", token, wallet: wa });
      const deletedIds = rotationDeletedSessionIdsRef.current || new Set();
      const backendRows = Array.isArray(r?.sessions)
        ? r.sessions.filter((x) => x && typeof x === "object" && !deletedIds.has(String(x?.id || x?.session_id || "")))
        : [];
      // Backend is the only multi-device truth.
      // Never re-hydrate closed sessions from a stale CoreVault preview on another device.
      // Preview fallback is only allowed when the backend itself still reports live rows
      // (presentation lag), never when the backend list is empty / WAITING.
      const backendCtrl = String(r?.controlState || r?.nkrControlState || "").toUpperCase();
      const backendHasLive = backendRows.some((x) => !["STOPPED", "FINALIZED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "DELETED", "ARCHIVED", "COMPLETE", "COMPLETED"].includes(String(x?.status || "").toUpperCase()));
      let onchainFallbackRows = [];
      if (backendHasLive) {
        const backendOnchainIds = new Set(
          backendRows.map((x) => String(x?.onchainSessionId ?? x?.meta?.onchain_session_id ?? x?.coreVaultSessionId ?? "")).filter(Boolean)
        );
        onchainFallbackRows = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
          .filter((sess) => String(sess?.engine || "").toUpperCase() === "NKR")
          .filter((sess) => !["FINALIZED", "COMPLETED", "CANCELLED", "CLOSED"].includes(String(sess?.statusLabel || sess?.status || "").toUpperCase()))
          .filter((sess) => backendOnchainIds.has(String(sess?.sessionId || sess?.onchainSessionId || "")))
          .filter((sess) => {
            const oid = String(sess?.sessionId || sess?.onchainSessionId || "");
            const ch = normalizeNkrChainKey(sess?.chain || sess?.chainKey || sess?.chainId || "");
            return !backendRows.some((row) => nkrSessionControlKey(row) === nkrSessionControlKey(ch, oid));
          })
          .map((sess) => {
            const oid = String(sess?.sessionId || sess?.onchainSessionId || "");
            const budget = Number(sess?.budget || sess?.budgetAmount || 0);
            const chain = String(sess?.chain || sess?.chainKey || activeNkrChainKey || "ETH").toUpperCase();
            const asset = String(sess?.settlementAsset || sess?.asset || (chain === "BNB" ? "BNB" : chain === "POL" ? "POL" : "ETH")).toUpperCase();
            return {
              id: `NKR-LIVE-${chain}-${oid}`, session_id: `NKR-LIVE-${chain}-${oid}`, onchainSessionId: oid, coreVaultSessionId: oid,
              type: "NKR", engineType: "NKR", status: String(sess?.statusLabel || sess?.status || "ACTIVE").toUpperCase(),
              lifecycleState: String(sess?.statusLabel || sess?.status || "ACTIVE").toUpperCase(), positionState: "WAITING",
              active: true, visibleInActiveSessions: true, chain, chainId: Number(sess?.chainId || 0),
              settlementAsset: asset, asset, budgetUsd: budget, budgetAmount: budget, reservedUsd: budget,
              maxActiveAssets: Number(rotationMaxActiveSessions || 0), createdAt: Number(sess?.createdAt || Date.now()), updatedAt: Date.now(),
              meta: { nkr_session: true, onchain_session_id: oid, chain, chain_id: Number(sess?.chainId || 0), settlement_asset: asset }
            };
          });
      }
      const sessions = canonicalizeNkrSessions(backendRows.length ? [...backendRows, ...onchainFallbackRows] : []);
      const activeIdRaw = String(r?.activeRotationSessionId || "").trim();
      const activeId = deletedIds.has(activeIdRaw) ? "" : activeIdRaw;
      // Empty backend list clears every device. Do not keep a ghost card from local/mobile state.
      setRotationSessions((prev) => {
        const previous = Array.isArray(prev) ? prev : [];
        const prevByKey = new Map(previous.map((row) => [nkrSessionControlKey(row), row]));
        return sessions.map((row) => {
          const key = nkrSessionControlKey(row);
          const old = prevByKey.get(key);
          const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
          const rawId = Number(row?.onchainStatusId ?? row?.rawStatusId ?? row?.statusId ?? meta?.raw_status_id ?? meta?.status_id ?? 0);
          const incoming = String(row?.onchainStatus || row?.statusLabel || row?.status || "").toUpperCase();
          const oldStatus = String(old?.onchainStatus || old?.statusLabel || old?.status || "").toUpperCase();
          // A server row without an authoritative status id may be a transient registry
          // projection. Never use it to flip a confirmed PAUSED card back to ACTIVE.
          if (oldStatus === "PAUSED" && incoming === "ACTIVE" && rawId !== 1) return old;
          return row;
        });
      });
      setActiveRotationSessionId((prev) => {
        if (!sessions.length) return "";
        const prevExists = sessions.some((s) => String(s?.id || s?.session_id || "") === String(prev || ""));
        if (prevExists) return prev;
        const backendExists = activeId && sessions.some((s) => String(s?.id || s?.session_id || "") === activeId);
        return backendExists ? activeId : String(sessions[0]?.id || sessions[0]?.session_id || "");
      });
      if (!sessions.length && (backendCtrl === "WAITING" || backendCtrl === "STOPPED" || backendCtrl === "FINALIZED" || !backendCtrl)) {
        // Ensure control does not stay RUNNING/PAUSED on a second device after close.
      }
      // controlState from backend is authoritative. Terminal-only lists must become WAITING
      // so Start NKR is available after FINALIZED (handover restart requirement).
      // STOPPING beats PAUSED (Pause→Stop must not fall back to PAUSED).
      const stoppingOk = sessions.some((s) => ["STOPPING", "FINALIZING", "CLOSING", "EXITING"].includes(String(s?.status || "").toUpperCase()));
      const pausedOk = sessions.some((s) => String(s?.status || "").toUpperCase() === "PAUSED");
      const liveOk = sessions.some((s) => !["STOPPED","FINALIZED","CLOSED","EXPIRED","CANCELLED","RELEASED","DELETED","ARCHIVED","COMPLETE","COMPLETED","STOPPING","FINALIZING"].includes(String(s?.status || "").toUpperCase()));
      const ctrl = String(r?.controlState || r?.nkrControlState || r?.summary?.runtime || "").toUpperCase();
      if (stoppingOk || ctrl === "STOPPING") {
        setNkrControlState("STOPPING");
      } else if (ctrl && !["FINALIZED","CLOSED","COMPLETE","COMPLETED","EXPIRED"].includes(ctrl)) {
        setNkrControlState(ctrl);
      } else if (pausedOk) {
        setNkrControlState("PAUSED");
      } else {
        setNkrControlState(liveOk ? "RUNNING" : "WAITING");
      }
      // Strategist snapshot for the public NKR panel (English only).
      if (r?.strategist && typeof r.strategist === "object") {
        setNkrStrategistStatus(stripGateObjects(r.strategist));
      }
    } catch (e) {
      console.warn("rotation session sync failed", e);
    } finally {
      rotationBackendHydratedRef.current = true;
      rotationBackendSyncBusyRef.current = false;
      setTimeout(() => { rotationBackendApplyingRef.current = false; }, 0);
    }
  }, [wallet, token, canonicalizeNkrSessions, coreVaultSessionPreview, activeNkrChainKey, rotationMaxActiveSessions]);

  useEffect(() => {
    rotationBackendHydratedRef.current = false;
    syncRotationSessionsFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);

  useEffect(() => {
    if (!resolveWalletAddress(wallet)) return undefined;
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      // Rotation sessions are the authoritative lifecycle source. Do not launch
      // three overlapping backend refresh trees every eight seconds. App state
      // and full backend state already have their own dedicated refresh paths.
      syncRotationSessionsFromServer();
    };
    const timer = setInterval(refresh, 20000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const onPageShow = () => refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    // Immediate sync on mount / wallet change so mobile does not keep a stale card.
    refresh();
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [wallet, syncRotationSessionsFromServer, syncAppStateFromServer]);

  // NKR session rows are read-only in the browser. No frontend POST persistence.


  const resetWalletBoundUi = useCallback(({ clearAuth = false } = {}) => {
    try {
      if (watchRetryRef.current?.t) {
        clearTimeout(watchRetryRef.current.t);
      }
    } catch {}
    watchRetryRef.current = { key: "", n: 0, t: null };
    watchSyncBusyRef.current = false;
    appStateSyncBusyRef.current = false;
    appStateHydratedRef.current = false;
    rotationBackendSyncBusyRef.current = false;
    rotationBackendHydratedRef.current = false;
    rotationBackendApplyingRef.current = false;
    inflightWatch.current = false;
    watchRefreshQueued.current = false;

    const resetToGuest = !!clearAuth;
    setWatchItems(resetToGuest ? cloneNexusDefaultWatchItems() : []);
    setWatchRows([]);
    // Clear only in-memory UI state. Never persist this transient reset over the
    // wallet-bound compare selection during auth/wallet handover or a new deployment.
    compareHydratedKeyRef.current = "";
    compareSkipNextPersistRef.current = true;
    setCompareSet(resetToGuest ? [...NEXUS_DEFAULT_COMPARE_SYMBOLS] : []);
    setTimeframe("90D");
    setIndexMode(true);
    setAiSelected([]);
    setCompareSeries({});
    lastGoodCompareRef.current = {};
    setWatchSyncedWallet("");
    setAppStateSyncedWallet("");
    try { setGridOrders([]); } catch {}
    try { setGridMeta({ tick: null, price: null }); } catch {}
    try { setGridVaultStats({ vault: 0, reserved: 0, free: 0 }); } catch {}
    try { setGridUiHydrated(false); } catch {}
    try { setRotationSessions([]); } catch {}
    try { setActiveRotationSessionId(""); } catch {}
    try { setRotationShadowSnapshot(null); } catch {}
    try { setRotationShadowEvents([]); } catch {}
    try { setNkrControlState("WAITING"); } catch {}
    if (clearAuth) {
      try { setWallet(""); } catch {}
      try { setToken(""); } catch {}
      try { setPrivyJwt(""); } catch {}
    }

    const keys = [
      "nexus_watch_items",
      "nexus_watch_synced_wallet",
      "nexus_app_state_synced_wallet",
      "nexus_compare_set",
      "nexus_timeframe",
      "nexus_ai_selected",
      LS_WATCH_ROWS_CACHE,
      LS_COMPARE_SERIES_CACHE,
      LS_COMPARE_STORE,
      LS_WATCH_REMOVED,
    ];
    if (clearAuth) {
      keys.push("nexus_wallet", "nexus_token");
    }
    for (const key of keys) {
      try { localStorage.removeItem(key); } catch {}
    }

    // Reset is local-clean only. If wallet/auth remains connected, immediately rehydrate
    // wallet-bound backend state so NKR budget/sessions/control are not lost.
    if (!clearAuth) {
      setTimeout(() => {
        try { syncAppStateFromServer(); } catch {}
        try { syncRotationSessionsFromServer(); } catch {}
      }, 100);
    }
  }, [setWatchItems, setWatchRows, setCompareSet, setTimeframe, setIndexMode, setAiSelected, setCompareSeries, setWatchSyncedWallet, setAppStateSyncedWallet, setGridOrders, setGridMeta, setGridVaultStats, setGridUiHydrated, setRotationSessions, setActiveRotationSessionId, setRotationShadowSnapshot, setRotationShadowEvents, setNkrControlState, setWallet, setToken, setPrivyJwt, syncAppStateFromServer, syncRotationSessionsFromServer]);

  useEffect(() => {
    const prev = prevWalletRef.current || "";
    const curr = String(wallet || "").toLowerCase();
    if (prev && prev !== curr) {
      resetWalletBoundUi({ clearAuth: !curr });
    }
    prevWalletRef.current = curr;
  }, [wallet, resetWalletBoundUi]);

  const [aiKind, setAiKind] = useState("research");
  const aiProfile = "balanced";
const [aiQuestion, setAiQuestion] = useState("");
  
  const [aiFollowUp, setAiFollowUp] = useState(false);
  const [aiHistory, setAiHistory] = useState([]); // [{role:"user"|"assistant", content:string}]
const [aiLoading, setAiLoading] = useState(false);
  const [aiOutput, setAiOutput] = useState("");
  const [strategistMarketIntel, setStrategistMarketIntel] = useState(null);
  const [strategistMarketIntelOpen, setStrategistMarketIntelOpen] = useState(false);
  const [globalStrategistReport, setGlobalStrategistReport] = useState(null);
  const [globalStrategistLoading, setGlobalStrategistLoading] = useState(false);
  const [strategistBridge, setStrategistBridge] = useState(null);
  const [strategistAppliedOpen, setStrategistAppliedOpen] = useState(false);

  const buildRotationShadowAssets = useCallback(() => {
    const preferredChain = String(rotationSelectedPick?.chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
    const bySymbol = new Map();

    const addAsset = (row = {}, extra = {}) => {
      const symbol = String(row?.symbol || row?.sym || row?.coin || row?.source || extra?.symbol || "").toUpperCase().trim();
      if (!symbol) return;
      const chain = String(extra?.chain || row?.chain || row?.network || preferredChain).toUpperCase();
      const key = `${chain}:${symbol}`;
      if (bySymbol.has(key)) return;
      bySymbol.set(key, {
        symbol,
        chain,
        token: row?.contract || row?.tokenAddress || row?.address || "",
        price: Number(row?.price ?? row?.priceUsd ?? row?.usd ?? 0) || 0,
        change24h: Number(row?.change24h ?? row?.ch ?? row?.change_24h ?? 0) || 0,
        volume24h: Number(row?.volume24h ?? row?.total_volume ?? row?.volume_24h ?? 0) || 0,
        marketCap: Number(row?.marketCap ?? row?.market_cap ?? row?.mcap ?? 0) || 0,
        scoreHint: Number(row?.scoreHint ?? row?.strategistScore ?? row?.score ?? extra?.score ?? extra?.strategistScore ?? 0) || 0,
        strategistScore: Number(row?.strategistScore ?? extra?.strategistScore ?? row?.score ?? extra?.score ?? 0) || 0,
        rank: row?.rank || extra?.rank || "",
        source: extra?.source || "watchlist_live",
      });
    };

    if (rotationSelectedPick?.source || rotationSelectedPick?.coin) {
      const sym = String(rotationSelectedPick?.coin || rotationSelectedPick?.source || "").toUpperCase();
      const row = (watchRows || []).find((r) => String(r?.symbol || "").toUpperCase() === String(rotationSelectedPick?.source || sym).toUpperCase()) || {};
      addAsset({ ...row, symbol: sym }, { chain: rotationSelectedPick?.chain || preferredChain, source: "selected_rotation_target" });
    }

    // Strategist candidates must be added before the broad watchlist fallback.
    // Otherwise a long watchlist can fill the asset map before candidates like CC reach Shadow.
    for (const cand of Array.isArray(strategistRotationCandidates) ? strategistRotationCandidates : []) {
      const sym = String(cand?.sym || cand?.symbol || "").toUpperCase();
      if (!sym || String(cand?.rank || "").toUpperCase() === "AVOID") continue;
      const row = (watchRows || []).find((r) => String(r?.symbol || "").toUpperCase() === sym) || {};
      addAsset(
        { ...row, ...cand, symbol: sym, change24h: cand?.ch ?? row?.change24h },
        { chain: cand?.chain || preferredChain, source: "strategist_recommendation", score: cand?.score, strategistScore: cand?.strategistScore, rank: cand?.rank }
      );
      if (bySymbol.size >= 8) break;
    }

    for (const sess of Array.isArray(rotationSessions) ? rotationSessions : []) {
      const sym = String(sess?.sourceSymbol || sess?.symbol || sess?.targetAsset || "").toUpperCase();
      if (!sym) continue;
      addAsset({ symbol: sym, score: sess?.score, strategistScore: sess?.meta?.strategist_score }, { chain: sess?.chain || preferredChain, source: "active_rotation_session", score: sess?.score, strategistScore: sess?.meta?.strategist_score });
    }

    for (const row of Array.isArray(watchRows) ? watchRows : []) {
      addAsset(row, { source: "watchlist_live" });
      if (bySymbol.size >= 16) break;
    }

    return Array.from(bySymbol.values()).filter((a) => a.symbol).slice(0, 16);
  }, [rotationSelectedPick, activeGridChainKey, watchRows, strategistRotationCandidates, rotationSessions]);


  const applyNkrBackendControl = useCallback(async (action, opts = {}) => {
    const actionU = String(action || "").toUpperCase();
    const now = Date.now();
    const localStatus = actionU === "PAUSE" ? "PAUSED" : actionU === "RESUME" ? "RUNNING" : actionU === "STOP" || actionU === "STOP_EXIT" || actionU === "PANIC_STOP" ? "STOPPING" : actionU === "DELETE" ? "WAITING" : actionU;
    const targetSid = String(opts.sessionId || "").trim();
    const targetChain = normalizeNkrChainKey(opts.chain || "");
    const targetControlKey = nkrSessionControlKey(targetChain, targetSid);
    if (["PAUSE", "RESUME", "STOP", "STOP_EXIT", "PANIC_STOP"].includes(actionU) && (!/^\d+$/.test(targetSid) || !targetChain)) {
      setRotationBackendMsg(`NKR ${actionU}: missing exact on-chain session id for this card.`);
      return;
    }
    // Immediate per-card state for PAUSE/RESUME. Do not wait for System Info or a
    // full page reset before changing the button and status label. Raw on-chain
    // status fields are patched too because several card selectors prefer them.
    if (["PAUSE", "RESUME"].includes(actionU)) {
      const paused = actionU === "PAUSE";
      setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).map((row) => {
        if (!row || typeof row !== "object") return row;
        const meta0 = row?.meta && typeof row.meta === "object" ? row.meta : {};
        const sid = String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? meta0?.onchain_session_id ?? meta0?.core_vault_session_id ?? "");
        const schain = normalizeNkrChainKey(row?.chain || row?.chainKey || meta0?.chain || meta0?.chain_key || row?.chainId || row?.chain_id || meta0?.chain_id || "");
        if (!opts.allSessions && (sid !== targetSid || schain !== targetChain)) return row;
        const nextStatus = paused ? "PAUSED" : "ACTIVE";
        const nextRaw = paused ? 2 : 1;
        return {
          ...row, status: nextStatus, statusLabel: nextStatus, onchainStatus: nextStatus,
          lifecycleState: nextStatus, rawStatusId: nextRaw, statusId: nextRaw, onchainStatusId: nextRaw,
          updatedAt: now,
          meta: { ...meta0, lifecycle_state: nextStatus, session_status: nextStatus, raw_status_id: nextRaw, status_id: nextRaw, nkr_user_control: paused ? "PAUSED_BY_USER" : "RESUMED_BY_USER" },
        };
      }));
    }

    // Optimistic UI: Pause → Stop must show EXITING/STOPPING immediately, never flicker back to PAUSED.
    if (["STOP", "STOP_EXIT", "PANIC_STOP"].includes(actionU)) {
      if (opts.allSessions) setNkrControlState("STOPPING");
      setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).map((s) => {
        if (!s || typeof s !== "object") return s;
        const meta0 = s?.meta && typeof s.meta === "object" ? s.meta : {};
        const sid = String(s?.onchainSessionId ?? s?.onchain_session_id ?? s?.coreVaultSessionId ?? meta0?.onchain_session_id ?? meta0?.core_vault_session_id ?? "");
        const schain = normalizeNkrChainKey(s?.chain || s?.chainKey || meta0?.chain || meta0?.chain_key || s?.chainId || s?.chain_id || meta0?.chain_id || "");
        if (!opts.allSessions && (sid !== targetSid || schain !== targetChain)) return s;
        const meta = { ...meta0, lifecycle_state: "STOPPING", position_state: "STOPPING", stop_message: "Stop & Exit in progress" };
        return { ...s, status: "STOPPING", lifecycleState: "STOPPING", positionState: "STOPPING", updatedAt: now, meta };
      }));
      if (targetSid) setNkrExitUiState((p) => ({ ...p, [targetControlKey]: "PENDING" }));
    }
    try {
      // Per-card control requires sessionId+chain. Explicit allSessions allows bulk.
      if (!opts.sessionId && !opts.allSessions && ["PAUSE", "RESUME", "STOP", "STOP_EXIT"].includes(actionU)) {
        setRotationBackendMsg("NKR control needs a session id. Use the buttons on the session card.");
        return;
      }
      const resp = await api("/api/nkr/control", {
        method: "POST",
        token,
        wallet,
        body: {
          action: actionU,
          sessionId: opts.sessionId || "",
          chain: opts.chain || "",
          requireSessionId: !opts.allSessions,
        },
      });
      if (Array.isArray(resp?.sessions)) {
        // Never let a server PAUSED row overwrite an in-flight STOPPING exit.
        const merged = canonicalizeNkrSessions(resp.sessions).map((s) => {
          let st = String(s?.status || "").toUpperCase();
          const ctrl = String(resp?.controlState || localStatus || "").toUpperCase();
          const meta0 = s?.meta && typeof s.meta === "object" ? s.meta : {};
          const sid = String(s?.onchainSessionId ?? s?.onchain_session_id ?? s?.coreVaultSessionId ?? meta0?.onchain_session_id ?? meta0?.core_vault_session_id ?? "");
          const schain = normalizeNkrChainKey(s?.chain || s?.chainKey || meta0?.chain || meta0?.chain_key || s?.chainId || s?.chain_id || meta0?.chain_id || "");
          if (["PAUSE", "RESUME"].includes(actionU) && (opts.allSessions || (sid === targetSid && schain === targetChain))) {
            const paused = actionU === "PAUSE";
            const nextStatus = paused ? "PAUSED" : "ACTIVE";
            const nextRaw = paused ? 2 : 1;
            return { ...s, status: nextStatus, statusLabel: nextStatus, onchainStatus: nextStatus, lifecycleState: nextStatus, rawStatusId: nextRaw, statusId: nextRaw, onchainStatusId: nextRaw, meta: { ...meta0, lifecycle_state: nextStatus, raw_status_id: nextRaw, status_id: nextRaw } };
          }
          if (["STOP", "STOP_EXIT", "PANIC_STOP"].includes(actionU) && st === "PAUSED") {
            return { ...s, status: "STOPPING", lifecycleState: "STOPPING", positionState: "STOPPING" };
          }
          if (ctrl === "STOPPING" && st === "PAUSED") {
            return { ...s, status: "STOPPING", lifecycleState: "STOPPING", positionState: "STOPPING" };
          }
          return s;
        });
        setRotationSessions(merged);
      }
      const respCtrl = String(resp?.controlState || "").toUpperCase();
      if (["STOP", "STOP_EXIT", "PANIC_STOP"].includes(actionU) && opts.allSessions) {
        setNkrControlState("STOPPING");
      } else if (respCtrl) {
        setNkrControlState(respCtrl);
      } else {
        setNkrControlState(localStatus);
      }
      if (["STOP", "STOP_EXIT", "PANIC_STOP"].includes(actionU)) {
        // Poll until FINALIZED, but keep STOPPING label (never re-show PAUSED).
        const pollOnce = async () => {
          try {
            const live = await api(`/api/rotation-sessions?wallet=${encodeURIComponent(String(wallet || ""))}&wallet_address=${encodeURIComponent(String(wallet || ""))}`, { method: "GET", token, wallet });
            const rows = Array.isArray(live?.sessions) ? canonicalizeNkrSessions(live.sessions) : [];
            const ctrl = String(live?.controlState || "").toUpperCase();
            const patched = rows.map((s) => {
              const st = String(s?.status || "").toUpperCase();
              if (st === "PAUSED" && (ctrl === "STOPPING" || ["STOPPING", "FINALIZING", "CLOSING"].includes(st))) {
                return { ...s, status: "STOPPING", lifecycleState: "STOPPING" };
              }
              if (st === "PAUSED" && ctrl === "STOPPING") return { ...s, status: "STOPPING", lifecycleState: "STOPPING" };
              return s;
            });
            setRotationSessions(patched);
            setNkrExitUiState((prev) => {
              const next = { ...(prev || {}) };
              for (const row of patched) {
                const key = nkrSessionControlKey(row);
                const st = String(row?.status || row?.statusLabel || "").toUpperCase();
                const err = String(row?.lastError || row?.last_error || row?.meta?.last_error || "").trim();
                if (["FINALIZED", "STOPPED", "CLOSED", "COMPLETE", "COMPLETED", "RELEASED"].includes(st)) delete next[key];
                else if (["EXIT_FAILED", "FAILED", "ERROR"].includes(st) || err) next[key] = "FAILED";
                else if (["STOPPING", "CLOSING", "FINALIZING", "EXITING", "EXIT_PENDING"].includes(st)) next[key] = "PENDING";
              }
              return next;
            });
            if (ctrl === "STOPPING" || patched.some((s) => ["STOPPING", "CLOSING", "FINALIZING"].includes(String(s?.status || "").toUpperCase()))) {
              setNkrControlState("STOPPING");
            } else if (ctrl && !["PAUSED"].includes(ctrl)) {
              setNkrControlState(ctrl);
            }
            if (live?.strategist && typeof live.strategist === "object") setNkrStrategistStatus(live.strategist);
          } catch (_) {}
        };
        // CLOSING can legitimately take longer than 15 seconds on public RPCs.
        // Keep the card synchronized long enough to observe EXIT -> FINALIZED instead
        // of leaving a permanent stale PENDING label in the browser.
        [2500, 8000, 15000, 30000, 60000, 120000].forEach((delay) => setTimeout(pollOnce, delay));
      }
      if (["PAUSE", "RESUME"].includes(actionU)) {
        const refreshPauseState = async () => {
          try { await syncRotationSessionsFromServer(); } catch (_) {}
        };
        [1200, 3500].forEach((delay) => setTimeout(refreshPauseState, delay));
      }
      setRotationShadowEvents((prev) => [{
        id: `NKR-CTRL-${now}`,
        ts: now,
        status: actionU,
        action: actionU,
        eventKind: "MONITOR",
        onChain: false,
        mode: "shadow",
        text: actionU === "PAUSE" ? "NKR paused by user and stored in backend." : actionU === "RESUME" ? "NKR resumed by explicit user action." : actionU === "STOP" || actionU === "STOP_EXIT" || actionU === "PANIC_STOP" ? "Stop & Exit in progress — session stays visible until finalized. Not paused." : "NKR deleted forever from backend.",
      }, ...(Array.isArray(prev) ? prev : [])]);
      setRotationBackendMsg(resp?.message || (actionU === "DELETE" ? "NKR deleted forever." : `NKR ${actionU.toLowerCase()} confirmed on-chain.`));
      // The card already changed optimistically. Do not immediately overwrite it with
      // a stale registry read; the scheduled refreshes below confirm the on-chain state.
      return true;
    } catch (e) {
      console.error("NKR BACKEND CONTROL FAILED", e);
      const msg = String(e?.message || e || "");
      const friendly = /aborted|AbortError/i.test(msg)
        ? "NKR control timed out or was interrupted. Please try again on the same session card."
        : toUserFacingMessage(msg, "NKR control failed. Please try again.");
      setRotationBackendMsg(friendly.startsWith("NKR") || friendly.length < 12 ? (friendly.startsWith("NKR") ? friendly : `NKR: ${friendly}`) : `NKR: ${friendly}`);
      // Do not force global control to a single-session optimistic state after failure.
      try {
        const open = (Array.isArray(rotationSessions) ? rotationSessions : []).filter((s) => {
          const st = String(s?.status || "").toUpperCase();
          return !["STOPPED", "CLOSED", "FINALIZED", "EXPIRED", "DELETED"].includes(st);
        });
        const allPaused = open.length > 0 && open.every((s) => String(s?.status || "").toUpperCase() === "PAUSED");
        setNkrControlState(allPaused ? "PAUSED" : open.length ? "RUNNING" : "WAITING");
      } catch {
        setNkrControlState(localStatus);
      }
      return false;
    }
  }, [token, wallet, setRotationSessions, setNkrControlState, canonicalizeNkrSessions, setNkrExitUiState, setNkrStrategistStatus, syncRotationSessionsFromServer]);

  const runRotationShadowSimulation = useCallback(async ({ silent = false } = {}) => {
    if (rotationShadowBusy) return;
    const control = String(nkrControlState || "WAITING").toUpperCase();
    if (["PAUSED", "STOPPED"].includes(control)) {
      if (!silent) setRotationBackendMsg(control === "PAUSED" ? "NKR is paused. Resume NKR before running Shadow." : "NKR is stopped. Approve/run a new NKR budget before continuing.");
      return;
    }
    setRotationShadowBusy(true);
    if (!silent) setRotationBackendMsg("NKR Shadow is reading live market context...");

    try {
      const deletedIds = rotationDeletedSessionIdsRef.current || new Set();
      let sessions = (Array.isArray(rotationSessions) ? rotationSessions : []).filter((s) => !deletedIds.has(String(s?.id || s?.session_id || "")));
      const nowStart = Date.now();
      const typedBudgetStart = Number(String(rotationBudgetRelease || "").replace(",", "."));

      // ENGINE-072: NKR executor/profit logic belongs in the backend, not in UI-only state.
      // If sessions already exist, let /api/nkr/executor-tick update events, net profit and
      // collected profit wallet-bound in SQLite. UI keeps only display/polling responsibility.
      if (sessions.length) {
        try {
          // Enrich market rows with systemScore / volume / on-chain delta so the
          // Strategist can use them. Missing fields stay absent → zero impact.
          const strategistMarketRows = (Array.isArray(watchRows) ? watchRows : []).map((row) => {
            if (!row || typeof row !== "object") return row;
            const next = { ...row };
            const sys = Number(typeof watchSystemScore === "function" ? watchSystemScore(row) : (row.systemScore || row.score));
            if (Number.isFinite(sys) && sys > 0) {
              next.systemScore = sys;
              if (!(Number(next.score) > 0)) next.score = sys;
            }
            const vol = Number(row.volume24h ?? row.total_volume ?? row.volume_24h ?? row.quoteVolume ?? row.volume);
            if (Number.isFinite(vol) && vol > 0) next.volume24h = vol;
            const rvol = Number(row.rvol ?? row.relativeVolume ?? row.relative_volume);
            if (Number.isFinite(rvol) && rvol > 0) next.rvol = rvol;
            const ocDelta = Number(row.onchain_delta ?? row.score_delta ?? row?.onchain?.score_delta);
            if (Number.isFinite(ocDelta)) next.strategist_onchain_delta = Math.max(-5, Math.min(5, ocDelta));
            return next;
          });
          const tick = await api(`/api/nkr/executor-tick`, {
            method: "POST",
            token,
            wallet,
            body: {
              sessions,
              marketRows: strategistMarketRows,
              // BUILD437: running session strategy is immutable. The current UI controls
              // are only the draft for a future start and must not be sent as live rules.
              settings: {},
            },
          });
          if (Array.isArray(tick?.sessions)) {
            setRotationSessions(tick.sessions);
            setNkrControlState("RUNNING");
            const msg = Array.isArray(tick?.summary?.messages) && tick.summary.messages.length
              ? tick.summary.messages.join(" · ")
              : "NKR backend executor tick completed.";
            setRotationShadowSnapshot((prev) => ({ ...(prev || {}), status: "ok", backendExecutor: true, summary: tick?.summary || {}, ts: Date.now() }));
            setRotationShadowEvents((prev) => [{
              id: `NKR-BACKEND-TICK-${Date.now()}`,
              ts: Date.now(),
              status: "TICK",
              action: "MONITOR",
              eventKind: "MONITOR",
              onChain: false,
              mode: "shadow",
              text: msg,
            }, ...(Array.isArray(prev) ? prev : [])]);
            if (!silent) setRotationBackendMsg(msg);
            return;
          }
        } catch (e) {
          console.warn("NKR backend executor tick unavailable; preserving live session state", e);
          const hasLiveOnchainNkr = (Array.isArray(sessions) ? sessions : []).some((sess) => {
            const meta = sess?.meta && typeof sess.meta === 'object' ? sess.meta : {};
            const sid = sess?.onchainSessionId ?? sess?.onchain_session_id ?? sess?.coreVaultSessionId ?? meta?.onchain_session_id ?? meta?.core_vault_session_id;
            return String(sid || '').trim() !== '' || String(sess?.executionMode || meta?.execution_mode || '').toLowerCase() === 'live';
          });
          if (hasLiveOnchainNkr) {
            if (!silent) setRotationBackendMsg(toUserFacingMessage(e?.message || e, "Live update briefly unavailable — running session settings preserved."));
            return;
          }
          if (!silent) setRotationBackendMsg(toUserFacingMessage(e?.message || e, "Live update briefly unavailable — using preview."));
        }
      }
      if (!sessions.length && !silent && Number.isFinite(typedBudgetStart) && typedBudgetStart > 0) {
        const periodDays = normalizeNkrPeriodHours() / 24;
        const activeLimitRaw = Number(String(rotationMaxActiveSessions || "3").replace(",", "."));
        const activeLimit = Math.max(1, Number.isFinite(activeLimitRaw) ? Math.floor(activeLimitRaw) : 3);
        const modeU = String(nkrCapitalMode || "DYNAMIC").toUpperCase();
        const reservePct = modeU === "AGGRESSIVE" ? 10 : modeU === "DEFENSIVE" ? 35 : modeU === "TACTICAL" ? 25 : 20;
        const maxPerAssetPct = modeU === "AGGRESSIVE" ? 40 : modeU === "DEFENSIVE" ? 25 : 35;
        const investableUsd = Math.max(0, typedBudgetStart * (1 - reservePct / 100));
        const perAssetCapUsd = Math.max(0, typedBudgetStart * (maxPerAssetPct / 100));
        const baseAssetNew = String(manualPayoutAsset || "USDC").toUpperCase();
        const normalizeCandidateSym = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
        const candidateMap = new Map();
        const pushCandidate = (sym, meta = {}) => {
          const raw = normalizeCandidateSym(sym);
          if (!raw || ["USDC", "USDT", "USD", "EUR"].includes(raw)) return;
          const prev = candidateMap.get(raw) || {};
          const score = Number(meta.score ?? meta.strategistScore ?? prev.score ?? 50);
          candidateMap.set(raw, { ...prev, ...meta, rawSymbol: raw, score: Number.isFinite(score) ? score : 50 });
        };
        if (rotationSelectedPick?.source || rotationSelectedPick?.coin) {
          pushCandidate(rotationSelectedPick?.source || rotationSelectedPick?.coin, {
            chain: rotationSelectedPick?.chain,
            coin: rotationSelectedPick?.coin,
            score: rotationSelectedPick?.score,
            rank: rotationSelectedPick?.rank,
            source: "selected_nkr_target",
            ok: rotationSelectedPick?.ok,
          });
        }
        for (const cand of Array.isArray(strategistRotationCandidates) ? strategistRotationCandidates : []) {
          if (String(cand?.rank || "").toUpperCase() === "AVOID") continue;
          pushCandidate(cand?.sym || cand?.symbol, {
            chain: cand?.chain,
            coin: cand?.coin,
            score: cand?.score ?? cand?.strategistScore,
            rank: cand?.rank,
            source: "strategist_candidate",
            strategistScore: cand?.strategistScore,
          });
        }
        for (const row of Array.isArray(watchRows) ? watchRows : []) {
          const sym = normalizeCandidateSym(row?.symbol || row?.sym || row?.asset);
          if (!sym) continue;
          const ch = Number(row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? 0);
          const score = typeof watchSystemScore === "function" ? watchSystemScore(row) : (Number.isFinite(ch) ? Math.max(35, Math.min(90, 55 + ch * 3)) : 55);
          pushCandidate(sym, { score, source: "watchlist", change24h: ch, chain: row?.chain || rotationNetworkScope || activeGridChainKey || "ALL" });
        }
        const rankedCandidates = Array.from(candidateMap.values())
          .filter((c) => c.ok !== false)
          .map((c) => ({ ...c, score: Number.isFinite(Number(c.score)) ? Number(c.score) : 50 }))
          .sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
        const bestCandidateScore = Number(rankedCandidates[0]?.score || 0);
        const minScoreByMode = { AGGRESSIVE: 58, DYNAMIC: 62, TACTICAL: 65, DEFENSIVE: 70 };
        const minScore = Number(minScoreByMode[modeU] ?? minScoreByMode.DYNAMIC);
        const dynamicQualityCut = Math.max(minScore, bestCandidateScore - 14);
        const qualityCandidates = rankedCandidates.filter((c) => Number(c.score || 0) >= dynamicQualityCut);
        const supportCandidates = rankedCandidates.filter((c) => Number(c.score || 0) >= minScore && Number(c.score || 0) < dynamicQualityCut);
        const desiredCount = Math.max(
          1,
          Math.min(
            activeLimit,
            qualityCandidates.length + Math.min(2, supportCandidates.length)
          )
        );
        const selectedCandidates = [...qualityCandidates, ...supportCandidates].slice(0, desiredCount);
        if (!selectedCandidates.length) {
          setRotationBackendMsg("NKR waiting: no asset clears Smart Allocation quality. Max sessions is only a limit, not a target.");
          setNkrControlState("WAITING");
          setRotationShadowBusy(false);
          return;
        }
        const convictionPower = modeU === "AGGRESSIVE" ? 1.45 : modeU === "DEFENSIVE" ? 1.9 : 1.65;
        const weights = selectedCandidates.map((c) => Math.pow(Math.max(1, Number(c.score || 0) - minScore + 6), convictionPower));
        const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;
        const minUsefulAllocationUsd = Math.min(500, Math.max(100, typedBudgetStart * 0.025));
        let allocatedSoFar = 0;
        const rawAllocations = selectedCandidates.map((c, idx) => {
          const rawAmount = investableUsd * (weights[idx] / totalWeight);
          const capped = Math.min(perAssetCapUsd || rawAmount, rawAmount);
          const amount = capped >= minUsefulAllocationUsd || idx < Math.min(2, selectedCandidates.length) ? capped : 0;
          allocatedSoFar += amount;
          return { candidate: c, amount };
        }).filter((r) => Number(r.amount || 0) > 0);
        let leftover = Math.max(0, investableUsd - allocatedSoFar);
        // Redistribute leftover only to already selected high-conviction sessions.
        for (const row of rawAllocations) {
          if (leftover <= 0.01) break;
          const room = Math.max(0, perAssetCapUsd - row.amount);
          const add = Math.min(room, leftover);
          row.amount += add;
          leftover -= add;
        }
        const newSessions = rawAllocations
          .filter((r) => Number(r.amount) > 0)
          .map((r, idx) => {
            const c = r.candidate || {};
            const sid = makeNexusSessionId("NKR");
            const candidateSymbol = String(c.coin || c.rawSymbol || "ASSET").toUpperCase();
            const sourceSymbol = String(c.rawSymbol || candidateSymbol).toUpperCase();
            const candidateChain = String(c.chain || rotationNetworkScope || activeGridChainKey || "ALL").toUpperCase();
            const amount = Number(Number(r.amount || 0).toFixed(4));
            return {
              id: sid,
              session_id: sid,
              type: "NKR",
              status: "ACTIVE",
              lifecycleState: "ACTIVE",
              positionState: "WAITING",
              executionMode: "shadow",
              budgetUsd: amount,
              workingCapitalUsd: amount,
              sessionCapitalUsd: amount,
              reservedUsd: amount,
              totalNkrBudgetUsd: Number(typedBudgetStart.toFixed(4)),
              nkrCashReserveUsd: Number(Math.max(0, typedBudgetStart - rawAllocations.reduce((s, x) => s + Number(x.amount || 0), 0)).toFixed(4)),
              nkrAllocationPct: typedBudgetStart > 0 ? Number(((amount / typedBudgetStart) * 100).toFixed(2)) : 0,
              baseAsset: baseAssetNew,
              payoutAsset: baseAssetNew,
              nkrCapitalMode,
              nkrObservationWindow,
              nkrProfitMode,
              nkrPeriodDays: normalizeNkrPeriodHours() / 24,
              nkrPeriodHours: normalizeNkrPeriodHours(),
              nkrPeriodUnit,
              nkrPeriodValue: nkrPeriodDays,
              runtimeHours: normalizeNkrPeriodHours(),
              chain: candidateChain,
              symbol: candidateSymbol,
              sourceSymbol,
              targetAsset: candidateSymbol,
              confidence: Number(c.score || 0),
              score: Number(c.score || 0),
              createdAt: nowStart,
              updatedAt: nowStart,
              expiresAt: nowStart + periodDays * 24 * 60 * 60 * 1000,
              meta: {
                nkr_session: true,
                wallet_bound: true,
                execution_mode: "shadow",
                lifecycle_state: "ACTIVE",
                position_state: "WAITING",
                base_asset: baseAssetNew,
                nkr_capital_mode: nkrCapitalMode,
                nkr_observation_window: nkrObservationWindow,
                nkr_profit_mode: nkrProfitMode,
                nkr_period_days: normalizeNkrPeriodHours() / 24,
                nkr_period_hours: normalizeNkrPeriodHours(),
                nkr_period_unit: nkrPeriodUnit,
                nkr_period_value: nkrPeriodDays,
                runtime_hours: normalizeNkrPeriodHours(),
                nkr_portfolio_allocation: true,
                nkr_allocation_rank: idx + 1,
                nkr_allocation_pct: typedBudgetStart > 0 ? Number(((amount / typedBudgetStart) * 100).toFixed(2)) : 0,
                total_nkr_budget_usd: Number(typedBudgetStart.toFixed(4)),
                cash_reserve_pct: reservePct,
                max_per_asset_pct: maxPerAssetPct,
                reserved_usd: amount,
                source_symbol: sourceSymbol,
                selected_symbol: candidateSymbol,
                candidate_source: c.source || "watchlist",
                nkr_watch_pool: "NKR_WATCH_POOL_PROMOTION_V1",
                nkr_promotion_policy: "weak_assets_stay_scanned_promote_when_strong",
              },
            };
          });
        sessions = newSessions;
        setRotationSessions(newSessions);
        setActiveRotationSessionId(newSessions[0]?.id ? String(newSessions[0].id) : "");
        setNkrControlState("RUNNING");
        setRotationBackendMsg(`NKR portfolio started: ${newSessions.length} sessions, ${fmtUsd(newSessions.reduce((s, x) => s + Number(x.budgetUsd || 0), 0))} allocated, ${fmtUsd(Math.max(0, typedBudgetStart - newSessions.reduce((s, x) => s + Number(x.budgetUsd || 0), 0)))} stable reserve.`);
      }
      const firstActive = sessions.find((s) => String(s?.id || "") === String(activeRotationSessionId || "") && isRotationSessionRunnable(s, nowStart))
        || sessions.find((s) => isRotationSessionRunnable(s, nowStart))
        || null;
      if (sessions.length && !firstActive) {
        setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).map((sess) => {
          const pHours = Number(sess?.nkrPeriodHours || sess?.runtimeHours || sess?.meta?.nkr_period_hours || sess?.meta?.runtime_hours || 0) || (Number(sess?.periodDays || sess?.nkrPeriodDays || sess?.meta?.nkr_period_days || 0) * 24) || normalizeNkrPeriodHours();
          const pStart = Number(sess?.campaignStartedAt || sess?.meta?.campaign_started_at || sess?.startedAt || sess?.createdAt || 0);
          const exp = Number(sess?.campaignExpiresAt || sess?.meta?.campaign_expires_at || (pStart > 0 ? pStart + pHours * 60 * 60 * 1000 : 0) || sess?.expiresAt || sess?.expires_at || sess?.meta?.expires_at || 0);
          const st = String(sess?.status || "APPROVED").toUpperCase();
          if (exp && exp <= nowStart && !["STOPPED", "PAUSED", "EXPIRED", "CLOSED"].includes(st)) {
            return { ...sess, status: "EXPIRED", expiredAt: nowStart, updatedAt: nowStart };
          }
          return sess;
        }));
        setRotationBackendMsg("NKR Shadow waiting: no active NKR session inside the selected period.");
        return;
      }
      const typedBudget = Number(String(rotationBudgetRelease || "").replace(",", "."));
      const baseBudgetUsd = Number(firstActive?.budgetUsd || 0) > 0
        ? Number(firstActive.budgetUsd)
        : Number.isFinite(typedBudget) && typedBudget > 0
          ? typedBudget
          : 100;
      const workingCapitalUsd = Number(firstActive?.workingCapitalUsd ?? firstActive?.sessionCapitalUsd ?? firstActive?.budgetUsd ?? 0) > 0
        ? Number(firstActive?.workingCapitalUsd ?? firstActive?.sessionCapitalUsd ?? firstActive?.budgetUsd)
        : baseBudgetUsd;
      const budgetUsd = workingCapitalUsd;
      const baseAsset = String(firstActive?.baseAsset || firstActive?.payoutAsset || firstActive?.meta?.base_asset || manualPayoutAsset || "USDC").toUpperCase();
      // LIVE-VAULT SAFETY: once a Rotation session is created, its target is locked.
      // Strategist may fill another free slot with a new target, but it must not rewrite
      // an existing session from ETH to BNB/XLM/etc. during the runtime window.
      const lockedSessionTarget = String(
        firstActive?.targetAsset ||
        firstActive?.sourceSymbol ||
        firstActive?.symbol ||
        firstActive?.meta?.target_asset ||
        firstActive?.meta?.source_symbol ||
        ""
      ).toUpperCase().trim();
      const chain = String(firstActive?.chain || rotationSelectedPick?.chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
      const slippageSource = firstActive?.maxSlippagePct ?? firstActive?.meta?.max_slippage_pct ?? rotationMaxSlippage ?? "1";
      const riskLimitSource = firstActive?.riskLimitPct ?? firstActive?.meta?.risk_limit_pct ?? rotationRiskLimit;
      const minNetSource = firstActive?.minNetAdvantagePct ?? firstActive?.meta?.min_net_advantage_pct ?? rotationMinNetAdvantage;
      const slippagePct = Number(String(slippageSource || "1").replace(",", "."));
      const assets = buildRotationShadowAssets();

      if (!assets.length) {
        setRotationBackendMsg("NKR Shadow needs Watchlist/market rows first. Add assets or wait for market data refresh.");
        setNkrControlState("WAITING");
        setRotationShadowSnapshot({ status: "waiting", assets: [], plan: [], previews: [], ts: Date.now() });
        return;
      }

      const body = {
        wallet,
        chain,
        baseAsset,
        base_asset: baseAsset,
        budgetUsd,
        budget_usd: budgetUsd,
        assets,
        slippageBps: Number.isFinite(slippagePct) ? Math.round(slippagePct * 100) : 100,
        slippage_bps: Number.isFinite(slippagePct) ? Math.round(slippagePct * 100) : 100,
        minNetAdvantagePct: minNetSource,
        riskLimitPct: riskLimitSource,
        safeMode: true,
        shadowOnly: true,
      };

      const preview = await api("/api/nexus/rotation-preview", { method: "POST", token, wallet, body });
      const plan = Array.isArray(preview?.plan) ? preview.plan : [];
      const previews = Array.isArray(preview?.previews) ? preview.previews : [];
      const candidateRows = plan.filter((row) => ["INCREASE", "HOLD"].includes(String(row?.action || "").toUpperCase()));

      // ENGINE-061: Watch Pool + Promotion + dynamic max sessions (max is a ceiling, not a target).
      // Max sessions is only a ceiling. Coins that are too weak get no capital,
      // but they stay in the scan pool and may be promoted immediately when they become stronger.
      const modeForPromotion = String(nkrCapitalMode || "DYNAMIC").toUpperCase();
      const promotionMinScore = modeForPromotion === "DEFENSIVE" ? 70 : modeForPromotion === "TACTICAL" ? 65 : modeForPromotion === "AGGRESSIVE" ? 58 : 62;
      const activeLimitRawPromo = Number(String(rotationMaxActiveSessions || "3").replace(",", "."));
      const maxNkrSessions = Math.max(1, Number.isFinite(activeLimitRawPromo) ? Math.floor(activeLimitRawPromo) : 3);
      const activeSymbols = new Set((Array.isArray(sessions) ? sessions : [])
        .filter((s) => isRotationSessionRunnable(s, nowStart))
        .map((s) => String(s?.targetAsset || s?.sourceSymbol || s?.symbol || s?.meta?.source_symbol || "").toUpperCase())
        .filter(Boolean));
      const watchPoolRows = (Array.isArray(plan) ? plan : [])
        .map((row) => ({
          ...row,
          symbol: String(row?.symbol || "").toUpperCase(),
          score: Number(row?.score || 0) || 0,
          state: activeSymbols.has(String(row?.symbol || "").toUpperCase()) ? "ACTIVE" : "WATCH_POOL",
        }))
        .filter((row) => row.symbol)
        .sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
      const nkrMarketBySymbol = new Map();
      for (const a of Array.isArray(assets) ? assets : []) {
        const sym = String(a?.symbol || "").toUpperCase();
        if (!sym) continue;
        nkrMarketBySymbol.set(sym, {
          symbol: sym,
          price: Number(a?.price ?? a?.usd ?? a?.current_price ?? 0) || 0,
          change24h: Number(a?.change24h ?? a?.chg_24h ?? a?.usd_24h_change ?? 0) || 0,
          score: Number(a?.score ?? a?.strategistScore ?? 0) || 0,
          source: "assets",
        });
      }
      for (const row of Array.isArray(watchRows) ? watchRows : []) {
        const sym = String(row?.symbol || row?.sym || row?.asset || "").toUpperCase();
        if (!sym) continue;
        const prev = nkrMarketBySymbol.get(sym) || {};
        nkrMarketBySymbol.set(sym, {
          ...prev,
          symbol: sym,
          price: Number(row?.price ?? row?.usd ?? row?.current_price ?? prev.price ?? 0) || 0,
          change24h: Number(row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? prev.change24h ?? 0) || 0,
          score: Number(prev.score || (typeof watchSystemScore === "function" ? watchSystemScore(row) : 0) || 0),
          source: "watchlist",
        });
      }
      for (const row of Array.isArray(watchPoolRows) ? watchPoolRows : []) {
        const sym = String(row?.symbol || "").toUpperCase();
        if (!sym) continue;
        const prev = nkrMarketBySymbol.get(sym) || {};
        nkrMarketBySymbol.set(sym, {
          ...prev,
          symbol: sym,
          score: Math.max(Number(prev.score || 0), Number(row?.score || 0) || 0),
          action: String(row?.action || prev.action || "").toUpperCase(),
        });
      }

      // ENGINE-084: aggressive replacement unblocked. Active NKR sessions are not permanent.
      // In Aggressive mode, open weak sessions may be replaced after a shorter age window
      // when stronger green assets are available. 10 means max sessions, not fixed holdings.
      const runnableForRebalance = (Array.isArray(sessions) ? sessions : []).filter((s) => isRotationSessionRunnable(s, nowStart));
      const greenRebalanceTargets = Array.from(nkrMarketBySymbol.values())
        .filter((m) => String(m?.symbol || "").toUpperCase() && Number(m?.change24h || 0) >= 0 && Number(m?.score || 0) >= promotionMinScore)
        .sort((a, b) => ((Number(b.change24h || 0) * 18) + Number(b.score || 0)) - ((Number(a.change24h || 0) * 18) + Number(a.score || 0)));
      const bestRebalanceTarget = greenRebalanceTargets[0] || null;
      const weakForRebalance = runnableForRebalance.filter((sess) => {
        const sym = String(sess?.targetAsset || sess?.sourceSymbol || sess?.symbol || sess?.meta?.source_symbol || "").toUpperCase();
        const m = nkrMarketBySymbol.get(sym) || {};
        const ch = Number(m?.change24h || 0) || 0;
        const score = Number(sess?.score || sess?.confidence || m?.score || 0) || 0;
        const events = Array.isArray(sess?.rotationEvents) ? sess.rotationEvents.length : 0;
        const ageMin = (nowStart - Number(sess?.createdAt || sess?.startedAt || nowStart)) / 60000;
        const lastRebalanceAge = nowStart - Number(sess?.meta?.nkr_last_rebalance_ts || 0);
        const isAggressiveNkr = modeForPromotion === "AGGRESSIVE";
        if (!sym || !bestRebalanceTarget?.symbol || sym === String(bestRebalanceTarget.symbol).toUpperCase()) return false;
        if (lastRebalanceAge < 5 * 60 * 1000) return false;
        const bestScore = Number(bestRebalanceTarget.score || 0);
        const bestChange = Number(bestRebalanceTarget.change24h || 0);
        if (isAggressiveNkr) {
          // OpenRotation/events are NOT a brake in Aggressive. Hard Safety still applies in backend.
          return ageMin >= 60 && bestChange >= 0 && ch < 0.15 && score <= bestScore - 5;
        }
        const hasOpenProgress = !!sess?.openRotation || events > 0;
        return !hasOpenProgress && ageMin >= 20 && ch < -0.25 && score < bestScore - 8;
      });
      if (bestRebalanceTarget && weakForRebalance.length) {
        const targetSym = String(bestRebalanceTarget.symbol || "").toUpperCase();
        setRotationSessions((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const weakIds = new Set(weakForRebalance.map((s) => String(s?.id || s?.session_id || "")));
          const releasedUsd = weakForRebalance.reduce((sum, w) => sum + (Number(w?.budgetUsd || w?.workingCapitalUsd || 0) || 0), 0);
          const typedTotalBudgetForRebalance = Number(String(rotationBudgetRelease || "").replace(",", "."));
          const totalBudgetForRebalance = Number.isFinite(typedTotalBudgetForRebalance) && typedTotalBudgetForRebalance > 0
            ? typedTotalBudgetForRebalance
            : arr.reduce((sum, x) => sum + (Number(x?.budgetUsd || x?.workingCapitalUsd || 0) || 0), 0);
          const allocationPctFromBudget = (usd) => totalBudgetForRebalance > 0 ? Number(((Number(usd || 0) / totalBudgetForRebalance) * 100).toFixed(2)) : 0;
          const existingTarget = arr.find((s) =>
            String(s?.targetAsset || s?.sourceSymbol || s?.symbol || "").toUpperCase() === targetSym &&
            !weakIds.has(String(s?.id || s?.session_id || "")) &&
            !["STOPPED", "CLOSED", "EXPIRED", "REBALANCED_OUT", "RELEASED"].includes(String(s?.status || "").toUpperCase())
          );

          // If the stronger target already exists, stop/remove weak cards completely and add their capital to the target.
          // If the stronger target does not exist yet, convert the first weak card into the stronger target and remove the rest.
          const primaryWeakId = String(weakForRebalance[0]?.id || weakForRebalance[0]?.session_id || "");
          let convertedPrimary = false;

          return arr.flatMap((sess) => {
            const id = String(sess?.id || sess?.session_id || "");
            const sym = String(sess?.targetAsset || sess?.sourceSymbol || sess?.symbol || sess?.meta?.source_symbol || "").toUpperCase();

            if (weakIds.has(id)) {
              if (existingTarget) {
                // Full stop: card disappears from Active NKR Sessions. The global event log keeps the audit trail.
                return [];
              }

              if (!convertedPrimary && id === primaryWeakId) {
                convertedPrimary = true;
                const nextBudget = Math.max(Number(sess?.budgetUsd || sess?.workingCapitalUsd || 0) || 0, releasedUsd);
                return [{
                  ...sess,
                  status: "ACTIVE",
                  lifecycleState: "ACTIVE",
                  positionState: "READY_DISPATCHED",
                  symbol: targetSym,
                  sourceSymbol: targetSym,
                  targetAsset: targetSym,
                  score: Number(bestRebalanceTarget.score || sess?.score || 0),
                  confidence: Number(bestRebalanceTarget.score || sess?.confidence || 0),
                  budgetUsd: Number(nextBudget.toFixed(4)),
                  workingCapitalUsd: Number(nextBudget.toFixed(4)),
                  sessionCapitalUsd: Number(nextBudget.toFixed(4)),
                  reservedUsd: Number(nextBudget.toFixed(4)),
                  nkrAllocationPct: allocationPctFromBudget(nextBudget),
                  updatedAt: nowStart,
                  rebalancedFrom: sym,
                  rotationEvents: [{
                    id: `NKR-REBALANCE-STOP-IN-${nowStart}`,
                    ts: nowStart,
                    mode: "shadow",
                    status: "REBALANCED_IN",
                    flow: `${sym} → ${targetSym}`,
                    fromAsset: sym,
                    targetAsset: targetSym,
                    reason: "aggressive_weak_session_replaced_by_stronger_green_asset",
                    netUsd: 0,
                  }, ...(Array.isArray(sess?.rotationEvents) ? sess.rotationEvents : [])],
                  lastRotationEvent: {
                    id: `NKR-REBALANCE-STOP-IN-${nowStart}`,
                    ts: nowStart,
                    status: "REBALANCED_IN",
                    flow: `${sym} → ${targetSym}`,
                    netUsd: 0,
                  },
                  meta: {
                    ...(sess?.meta || {}),
                    selected_symbol: targetSym,
                    source_symbol: targetSym,
                    position_state: "READY_DISPATCHED",
                    nkr_rebalance_mode: "STOP_WEAK_SESSION_AND_REDIRECT_CAPITAL",
                    nkr_rebalance_reason: `aggressive_weak_session_replaced_by_stronger_green_${targetSym}`,
                    nkr_previous_symbol: sym,
                    nkr_last_rebalance_ts: nowStart,
                    nkr_allocation_pct: allocationPctFromBudget(nextBudget),
                    nkr_total_budget_usd: totalBudgetForRebalance,
                  },
                }];
              }

              return [];
            }

            if (existingTarget && id === String(existingTarget?.id || existingTarget?.session_id || "")) {
              const nextBudget = Number(sess?.budgetUsd || sess?.workingCapitalUsd || 0) + releasedUsd;
              return [{
                ...sess,
                budgetUsd: Number(nextBudget.toFixed(4)),
                workingCapitalUsd: Number(nextBudget.toFixed(4)),
                sessionCapitalUsd: Number(nextBudget.toFixed(4)),
                reservedUsd: Number(nextBudget.toFixed(4)),
                nkrAllocationPct: allocationPctFromBudget(nextBudget),
                updatedAt: nowStart,
                rotationEvents: [{
                  id: `NKR-REBALANCE-STOP-IN-${nowStart}`,
                  ts: nowStart,
                  mode: "shadow",
                  status: "REBALANCE_INCREASE",
                  flow: `${String(sess?.baseAsset || baseAsset)} → ${targetSym}`,
                  targetAsset: targetSym,
                  reason: "capital_redirected_from_stopped_weak_sessions",
                  netUsd: 0,
                }, ...(Array.isArray(sess?.rotationEvents) ? sess.rotationEvents : [])],
                lastRotationEvent: {
                  id: `NKR-REBALANCE-STOP-IN-${nowStart}`,
                  ts: nowStart,
                  status: "REBALANCE_INCREASE",
                  flow: `stopped weak sessions → ${targetSym}`,
                  netUsd: 0,
                },
                meta: {
                  ...(sess?.meta || {}),
                  nkr_rebalance_in_usd: Number(releasedUsd.toFixed(4)),
                  nkr_rebalance_mode: "STOP_WEAK_SESSION_AND_REDIRECT_CAPITAL",
                  nkr_last_rebalance_ts: nowStart,
                  nkr_allocation_pct: allocationPctFromBudget(nextBudget),
                  nkr_total_budget_usd: totalBudgetForRebalance,
                },
              }];
            }

            return [sess];
          });
        });
        setRotationShadowEvents((prev) => [{
          id: `NKR-REBALANCE-STOP-${nowStart}`,
          ts: nowStart,
          text: `NKR REBALANCE: stopped ${weakForRebalance.map((s) => String(s?.targetAsset || s?.symbol || "ASSET").toUpperCase()).join(", ")} and redirected ${fmtUsd(weakForRebalance.reduce((sum, w) => sum + (Number(w?.budgetUsd || w?.workingCapitalUsd || 0) || 0), 0))} toward ${targetSym}.`,
        }, ...(Array.isArray(prev) ? prev : [])]);
      }

      const promotionCandidate = watchPoolRows.find((row) =>
        row.state !== "ACTIVE" &&
        row.score >= promotionMinScore &&
        ["INCREASE", "HOLD", "WATCH"].includes(String(row?.action || "WATCH").toUpperCase())
      ) || null;
      if (promotionCandidate && !silent) {
        const runnableSessions = (Array.isArray(sessions) ? sessions : []).filter((s) => isRotationSessionRunnable(s, nowStart));
        const totalNkrBudget = Number(String(rotationBudgetRelease || "").replace(",", "."));
        const modeReservePct = modeForPromotion === "AGGRESSIVE" ? 10 : modeForPromotion === "DEFENSIVE" ? 35 : modeForPromotion === "TACTICAL" ? 25 : 20;
        const modeMaxPerAssetPct = modeForPromotion === "AGGRESSIVE" ? 40 : modeForPromotion === "DEFENSIVE" ? 25 : 35;
        const totalBudgetForPromotion = Number.isFinite(totalNkrBudget) && totalNkrBudget > 0 ? totalNkrBudget : runnableSessions.reduce((s, x) => s + Number(x?.budgetUsd || 0), 0);
        const investableForPromotion = Math.max(0, totalBudgetForPromotion * (1 - modeReservePct / 100));
        const allocatedForPromotion = runnableSessions.reduce((s, x) => s + Number(x?.budgetUsd || x?.workingCapitalUsd || 0), 0);
        const freeForPromotion = Math.max(0, investableForPromotion - allocatedForPromotion);
        const weakestActive = runnableSessions
          .slice()
          .sort((a, b) => Number(a?.score || a?.confidence || 0) - Number(b?.score || b?.confidence || 0))[0] || null;
        const weakestScore = Number(weakestActive?.score || weakestActive?.confidence || 0) || 0;
        // ENGINE-085: no automatic free-slot fill. Max sessions is a ceiling, not a target.
        const canAddFreeSlot = false;
        const canReplaceWeakest = weakestActive && promotionCandidate.score >= Math.max(promotionMinScore, weakestScore + 5);
        if (canAddFreeSlot || canReplaceWeakest) {
          const promoteUsd = canAddFreeSlot
            ? Math.min(freeForPromotion, Math.max(100, totalBudgetForPromotion * 0.08), totalBudgetForPromotion * (modeMaxPerAssetPct / 100))
            : Number(weakestActive?.budgetUsd || weakestActive?.workingCapitalUsd || Math.max(100, totalBudgetForPromotion * 0.08));
          const sid = canAddFreeSlot ? makeNexusSessionId("NKR") : String(weakestActive?.id || weakestActive?.session_id || makeNexusSessionId("NKR"));
          const promotedSession = {
            ...(canReplaceWeakest ? weakestActive : {}),
            id: sid,
            session_id: sid,
            type: "NKR",
            status: "ACTIVE",
            lifecycleState: "ACTIVE",
            positionState: "READY_DISPATCHED",
            executionMode: "shadow",
            budgetUsd: Number(Number(promoteUsd || 0).toFixed(4)),
            workingCapitalUsd: Number(Number(promoteUsd || 0).toFixed(4)),
            sessionCapitalUsd: Number(Number(promoteUsd || 0).toFixed(4)),
            reservedUsd: Number(Number(promoteUsd || 0).toFixed(4)),
            totalNkrBudgetUsd: Number(Number(totalBudgetForPromotion || 0).toFixed(4)),
            nkrCashReserveUsd: Number(Math.max(0, totalBudgetForPromotion - investableForPromotion).toFixed(4)),
            nkrAllocationPct: totalBudgetForPromotion > 0 ? Number(((promoteUsd / totalBudgetForPromotion) * 100).toFixed(2)) : 0,
            baseAsset,
            payoutAsset: baseAsset,
            nkrCapitalMode,
            nkrObservationWindow,
            nkrProfitMode,
            nkrPeriodDays: normalizeNkrPeriodHours() / 24,
            nkrPeriodHours: normalizeNkrPeriodHours(),
            nkrPeriodUnit,
            nkrPeriodValue: nkrPeriodDays,
            runtimeHours: normalizeNkrPeriodHours(),
            chain: String(promotionCandidate?.chain || chain || activeGridChainKey || "ALL").toUpperCase(),
            symbol: promotionCandidate.symbol,
            sourceSymbol: promotionCandidate.symbol,
            targetAsset: promotionCandidate.symbol,
            confidence: promotionCandidate.score,
            score: promotionCandidate.score,
            createdAt: canAddFreeSlot ? nowStart : (weakestActive?.createdAt || nowStart),
            updatedAt: nowStart,
            promotedAt: nowStart,
            meta: {
              ...((canReplaceWeakest && weakestActive?.meta) || {}),
              nkr_session: true,
              wallet_bound: true,
              execution_mode: "shadow",
              lifecycle_state: "ACTIVE",
              position_state: "READY_DISPATCHED",
              base_asset: baseAsset,
              selected_symbol: promotionCandidate.symbol,
              source_symbol: promotionCandidate.symbol,
              nkr_watch_pool: "NKR_WATCH_POOL_PROMOTION_V1",
              nkr_promotion_reason: "candidate_replaced_weaker_active_session_no_autofill",
              replaced_symbol: canReplaceWeakest ? String(weakestActive?.targetAsset || weakestActive?.symbol || "").toUpperCase() : "",
              replaced_score: canReplaceWeakest ? weakestScore : null,
              promotion_score: promotionCandidate.score,
              nkr_max_sessions_policy: "maximum_not_target",
              live_vault_ready: false,
              reserved_usd: Number(Number(promoteUsd || 0).toFixed(4)),
            },
          };
          setRotationSessions((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            if (canReplaceWeakest) {
              return arr.map((s) => String(s?.id || s?.session_id || "") === sid ? promotedSession : s);
            }
            return [promotedSession, ...arr].slice(0, maxNkrSessions);
          });
          setRotationShadowEvents((prev) => [{
            id: `NKR-PROMOTE-${nowStart}`,
            ts: nowStart,
            text: `WATCH_POOL → READY_DISPATCHED: ${promotionCandidate.symbol} score ${promotionCandidate.score}/100 replaced weaker ${String(weakestActive?.targetAsset || weakestActive?.symbol || "session").toUpperCase()} (${weakestScore}/100). No free-slot autofill.`,
          }, ...(Array.isArray(prev) ? prev : [])]);
        }
      }
      const lockedPlanRow = lockedSessionTarget
        ? (plan.find((row) => String(row?.symbol || "").toUpperCase() === lockedSessionTarget)
            || candidateRows.find((row) => String(row?.symbol || "").toUpperCase() === lockedSessionTarget)
            || null)
        : null;
      const best = lockedPlanRow || candidateRows[0] || plan[0] || null;
      const bestSymbol = String(lockedSessionTarget || best?.symbol || firstActive?.sourceSymbol || firstActive?.symbol || rotationSelectedPick?.coin || rotationSelectedPick?.source || gridItem || "ASSET").toUpperCase();
      const bestChain = String(firstActive?.chain || best?.chain || chain).toUpperCase();
      const bestScore = Number(best?.score || firstActive?.score || firstActive?.confidence || rotationSelectedPick?.score || 0) || 0;
      const bestWeight = Number(best?.target_weight_pct || 0) || 0;
      const targetUsd = Number(best?.target_usd || budgetUsd) || budgetUsd;
      const assetRow = assets.find((a) => String(a.symbol).toUpperCase() === bestSymbol) || assets[0] || {};
      const change24h = Number(assetRow?.change24h || 0) || 0;

      // NKR is a dispatcher/capital manager. Use the same soft Shadow cost style as Trader:
      // costs are a realism drag capped near 0.05% of notional, not a hard pre-executor brake.
      const rawEdgePct = Math.max(-3, Math.min(8, ((bestScore - 50) * 0.055) + (change24h * 0.08)));
      const shadowGasCapUsd = bestChain === "ETH" ? 0.20 : bestChain === "BNB" ? 0.03 : 0.01;
      const dexFeeBps = 3;
      const slippageBps = Math.min(5, Math.max(0, (Number.isFinite(slippagePct) ? slippagePct : 1) * 100 * 0.02));
      const uncappedCostsUsd = shadowGasCapUsd + targetUsd * ((dexFeeBps + slippageBps) / 10000);
      const costsUsd = Math.min(uncappedCostsUsd, Math.max(0.01, targetUsd * 0.0005));
      const grossUsd = targetUsd * (rawEdgePct / 100);
      const netUsd = grossUsd - costsUsd;
      const minNetAdv = Number(String(minNetSource || "0.5").replace(",", "."));
      const netPct = targetUsd > 0 ? (netUsd / targetUsd) * 100 : 0;
      // Keep entry gates aligned with backend Strategist (Aggressive 51 / Dynamic 62 / Tactical 65 / Defensive 70).
      const dispatchMinScore = nkrCapitalMode === "DEFENSIVE" ? 70 : nkrCapitalMode === "TACTICAL" ? 65 : nkrCapitalMode === "AGGRESSIVE" ? 51 : 62;
      const isDispatcherApproved = bestScore >= dispatchMinScore && rawEdgePct > -0.25;
      const isExecutorNetPositive = netUsd >= 0 || netPct >= (Number.isFinite(minNetAdv) ? Math.min(minNetAdv, 0.2) : 0.2);
      const isExecutableShadowEdge = isDispatcherApproved && isExecutorNetPositive;
      const nkrModeKey = String(nkrCapitalMode || "DYNAMIC").toUpperCase();
      const profitLockPctByMode = { DYNAMIC: 2.0, TACTICAL: 1.7, AGGRESSIVE: 2.8, DEFENSIVE: 1.2 };
      const profitLockPct = Number(profitLockPctByMode[nkrModeKey] ?? profitLockPctByMode.DYNAMIC);
      const profitLockUsd = Math.max(50, targetUsd * (profitLockPct / 100));
      const smallProfitUsd = Math.max(20, targetUsd * 0.006);
      // NKR must not sell just because it sees a tiny +$3, +$10 or +$20 edge.
      // Positive but small edges are kept running so the winner can mature.
      const closesPosition = isExecutableShadowEdge && netPct >= profitLockPct && netUsd >= profitLockUsd;
      const holdsWinner = isExecutableShadowEdge && netUsd > smallProfitUsd && !closesPosition;
      const action = closesPosition ? "SIMULATED_ROTATION_CLOSED" : holdsWinner ? "HOLD_WINNER_MAXIMIZE" : isExecutableShadowEdge ? "EXECUTOR_ACTIVE" : isDispatcherApproved ? "READY_DISPATCHED" : "WAIT_SCORE";
      const now = Date.now();
      const completedEvent = closesPosition ? {
        id: `ROT-EVT-${now}`,
        ts: now,
        mode: "shadow",
        status: "CLOSED",
        baseAsset,
        fromAsset: baseAsset,
        targetAsset: bestSymbol,
        backToAsset: baseAsset,
        chain: bestChain,
        buyUsd: Number(targetUsd.toFixed(4)),
        sellUsd: Number((targetUsd + netUsd).toFixed(4)),
        grossUsd: Number(grossUsd.toFixed(4)),
        costsUsd: Number(costsUsd.toFixed(4)),
        netUsd: Number(netUsd.toFixed(4)),
        netPct: Number(netPct.toFixed(4)),
        confidence: bestScore,
        flow: `${baseAsset} → ${bestSymbol} → ${baseAsset}`,
        liveVaultTx: null,
      } : null;
      const sessionEvent = completedEvent || (isDispatcherApproved ? {
        id: `ROT-EXEC-${now}`,
        ts: now,
        mode: "shadow",
        status: isExecutableShadowEdge ? "POSITION_OPEN" : "DISPATCHED",
        executorState: isExecutableShadowEdge ? "EXECUTOR_ACTIVE" : "READY_DISPATCHED",
        baseAsset,
        fromAsset: baseAsset,
        targetAsset: bestSymbol,
        backToAsset: baseAsset,
        chain: bestChain,
        buyUsd: Number(targetUsd.toFixed(4)),
        sellUsd: null,
        grossUsd: Number(grossUsd.toFixed(4)),
        costsUsd: Number(costsUsd.toFixed(4)),
        netUsd: Number(netUsd.toFixed(4)),
        netPct: Number(netPct.toFixed(4)),
        confidence: bestScore,
        flow: `${baseAsset} → ${bestSymbol} → ${baseAsset}`,
        reason: isExecutableShadowEdge ? "nkr_dispatcher_executor_shadow_position_tracking" : "nkr_dispatched_waiting_executor_entry_check",
        liveVaultTx: null,
      } : null);

      setNkrControlState("RUNNING");
      setRotationShadowSnapshot({
        status: "ok",
        chain,
        budgetUsd,
        baseAsset,
        assets,
        plan,
        previews,
        watchPool: watchPoolRows,
        promotionCandidate,
        rebalanceTarget: bestRebalanceTarget,
        weakRebalanceSessions: weakForRebalance.map((s) => String(s?.targetAsset || s?.symbol || "").toUpperCase()).filter(Boolean),
        watchPoolMode: "NKR_WATCH_POOL_PROMOTION_V1",
        best,
        bestSymbol,
        bestChain,
        bestScore,
        bestWeight,
        grossUsd,
        costsUsd,
        netUsd,
        netPct,
        profitLockPct,
        profitLockUsd,
        smallProfitUsd,
        closesPosition,
        holdsWinner,
        action,
        ts: now,
      });

      setRotationShadowEvents((prev) => [{
        id: `ROT-SHADOW-${now}`,
        ts: now,
        text: `${baseAsset} → ${bestSymbol} → ${baseAsset}: ${action} · score ${bestScore || "—"}/100 · gross ${fmtUsd(grossUsd)} · costs ${fmtUsd(costsUsd)} · net ${fmtUsd(netUsd)} · lock ${profitLockPct.toFixed(2)}%/${fmtUsd(profitLockUsd)}`,
      }, ...(Array.isArray(prev) ? prev : [])]);

      if (sessions.length && firstActive?.id) {
        setRotationSessions((prev) => (Array.isArray(prev) ? prev : []).map((sess) => {
          // Critical: only the actually runnable active session may be updated by the Shadow loop.
          // A STOPPED/closed session must never be revived back into WAITING by a later Shadow tick.
          if (deletedIds.has(String(sess?.id || sess?.session_id || ""))) return sess;
          if (String(sess?.id || "") !== String(firstActive?.id || "")) return sess;
          const currentStatus = String(sess?.status || "").toUpperCase();
          if (["STOPPED", "CLOSED", "CANCELLED", "EXPIRED", "RELEASED", "ARCHIVED", "REBALANCED_OUT", "WAITING_REALLOCATION", "WATCH_POOL"].includes(currentStatus)) return sess;
          const prevEvents = Array.isArray(sess?.rotationEvents) ? sess.rotationEvents : [];
          const nextEvents = sessionEvent ? [sessionEvent, ...prevEvents] : prevEvents;
          const prevTotalEvents = Number(sess?.totalEventCount ?? sess?.eventCount ?? sess?.meta?.nkr_total_event_count ?? prevEvents.length) || prevEvents.length;
          const nextTotalEvents = prevTotalEvents + (sessionEvent ? 1 : 0);
          // ENGINE-082: frontend may display shadow activity, but it must NEVER book collected profit.
          // Collected Profit is backend-owned only, otherwise the same close can be counted once in UI
          // and once again by /api/nkr/executor-tick.
          const prevCollected = Number(sess?.collectedProfitUsd ?? sess?.meta?.collectedProfitUsd ?? 0) || 0;
          const countedProfitIds = new Set(sess?.countedProfitTradeIds || sess?.meta?.nkr_counted_profit_trade_ids || []);
          const addCollected = 0;
          if (sessionEvent && completedEvent) {
            sessionEvent.addedToCollectedProfit = false;
            sessionEvent.alreadyCounted = true;
            sessionEvent.reason = `${sessionEvent.reason || "frontend_shadow"}_display_only_backend_books_profit`;
          }
          const nextCollected = prevCollected;
          const prevGross = Number(sess?.grossProfitUsd ?? 0) || 0;
          const prevCosts = Number(sess?.costsUsd ?? 0) || 0;
          const prevNet = Number(sess?.netProfitUsd ?? sess?.rotationProfitUsd ?? sess?.profitUsd ?? 0) || 0;
          return {
            ...sess,
            status: closesPosition || holdsWinner || isDispatcherApproved ? "ACTIVE" : "WAITING",
            lifecycleState: closesPosition || holdsWinner || isDispatcherApproved ? "ACTIVE" : "WAITING",
            positionState: closesPosition ? "CLOSED" : isExecutableShadowEdge ? "OPEN" : isDispatcherApproved ? "READY_DISPATCHED" : "WAITING",
            executionMode: "shadow",
            symbol: bestSymbol,
            sourceSymbol: bestSymbol,
            targetAsset: bestSymbol,
            chain: bestChain,
            baseAsset,
            payoutAsset: baseAsset,
            confidence: bestScore,
            score: bestScore,
            workingCapitalUsd: Number((Number(sess?.budgetUsd || baseBudgetUsd) || baseBudgetUsd).toFixed(4)),
            sessionCapitalUsd: Number((Number(sess?.budgetUsd || baseBudgetUsd) || baseBudgetUsd).toFixed(4)),
            reservedUsd: Number((Number(sess?.budgetUsd || baseBudgetUsd) || baseBudgetUsd).toFixed(4)),
            collectedProfitUsd: Number(nextCollected.toFixed(4)),
            profitUsd: Number(nextCollected.toFixed(4)),
            rotationProfitUsd: Number(nextCollected.toFixed(4)),
            grossProfitUsd: Number((completedEvent ? (prevGross + grossUsd) : (sessionEvent ? grossUsd : prevGross)).toFixed(4)),
            costsUsd: Number((completedEvent ? (prevCosts + costsUsd) : (sessionEvent ? costsUsd : prevCosts)).toFixed(4)),
            netProfitUsd: Number((completedEvent ? (prevNet + netUsd) : (sessionEvent ? netUsd : prevNet)).toFixed(4)),
            lastRotationEvent: sessionEvent || sess?.lastRotationEvent || null,
            rotationEvents: nextEvents,
            totalEventCount: nextTotalEvents,
            eventCount: nextTotalEvents,
            rotationEventHistoryCount: nextEvents.length,
            openRotation: closesPosition ? null : (isExecutableShadowEdge ? {
              openedAt: sess?.openRotation?.openedAt || now,
              baseAsset,
              targetAsset: bestSymbol,
              chain: bestChain,
              entryUsd: Number(targetUsd.toFixed(4)),
              currentNetUsd: Number(netUsd.toFixed(4)),
              currentNetPct: Number(netPct.toFixed(4)),
              state: "POSITION_TRACKING",
            } : (sess?.openRotation || null)),
            riskLimitPct: riskLimitSource,
            minNetAdvantagePct: minNetSource,
            maxSlippagePct: slippageSource,
            updatedAt: now,
            countedProfitTradeIds: Array.from(countedProfitIds),
            meta: {
              ...(sess?.meta || {}),
              nkr_counted_profit_trade_ids: Array.from(countedProfitIds),
              rotation_shadow: true,
              rotation_action: action,
              capital_flow: "BASE_TO_TARGET_TO_BASE",
              base_asset: baseAsset,
              collectedProfitUsd: Number(nextCollected.toFixed(4)),
              target_weight_pct: bestWeight,
              target_usd: targetUsd,
              raw_edge_pct: Number(rawEdgePct.toFixed(4)),
              net_edge_pct: Number(netPct.toFixed(4)),
              nkr_dispatcher_approved: Boolean(isDispatcherApproved),
              nkr_executor_net_positive: Boolean(isExecutorNetPositive),
              nkr_cost_model: "aligned_shadow_trader_cost_cap_0_05pct",
              nkr_max_sessions_policy: "maximum_not_target",
              profit_lock_pct: Number(profitLockPct.toFixed(4)),
              profit_lock_usd: Number(profitLockUsd.toFixed(4)),
              small_profit_no_close_usd: Number(smallProfitUsd.toFixed(4)),
              nkr_profit_runner: "NKR_PROFIT_RUNNER_EXIT_GUARD_V1",
              market_change_24h: change24h,
              backend_plan_ts: preview?.ts || null,
              live_vault_ready: false,
              execution_mode: "shadow",
              lifecycle_state: closesPosition || holdsWinner || isDispatcherApproved ? "ACTIVE" : "WAITING",
              position_state: closesPosition ? "CLOSED" : isExecutableShadowEdge ? "OPEN" : isDispatcherApproved ? "READY_DISPATCHED" : "WAITING",
              reserved_usd: Number((Number(sess?.budgetUsd || baseBudgetUsd) || baseBudgetUsd).toFixed(4)),
            },
          };
        }));
      }

      if (!silent) {
        setRotationBackendMsg(`${closesPosition ? "NKR profit locked" : holdsWinner ? "NKR winner running" : isExecutableShadowEdge ? "NKR executor active" : isDispatcherApproved ? "NKR dispatched to executor" : "NKR waiting"}: ${baseAsset} → ${bestSymbol} → ${baseAsset} · net ${fmtUsd(netUsd)} (${netPct.toFixed(2)}%) · lock ${profitLockPct.toFixed(2)}%/${fmtUsd(profitLockUsd)}. Paper-only; no Vault swap triggered.`);
      }
    } catch (e) {
      console.error("ROTATION SHADOW SIM FAILED", e);
      setRotationBackendMsg(`NKR Shadow failed: ${e?.message || e}`);
      setRotationShadowSnapshot((prev) => ({ ...(prev || {}), status: "error", error: e?.message || String(e), ts: Date.now() }));
    } finally {
      setRotationShadowBusy(false);
    }
  }, [rotationShadowBusy, rotationSessions, activeRotationSessionId, rotationBudgetRelease, rotationSelectedPick, activeGridChainKey, gridItem, rotationMaxSlippage, buildRotationShadowAssets, wallet, token, rotationMinNetAdvantage, rotationRiskLimit, isRotationSessionRunnable, manualPayoutAsset, nkrControlState, nkrCapitalMode, nkrObservationWindow, nkrProfitMode, nkrPeriodDays, nkrPeriodUnit, normalizeNkrPeriodHours, rotationNetworkScope, makeNexusSessionId]);

  useInterval(() => {
    const now = Date.now();
    const sessions = Array.isArray(rotationSessions) ? rotationSessions : [];
    const runnable = sessions.filter((sess) => isRotationSessionRunnable(sess, now));
    const hasRunnable = runnable.length > 0;
    const hasLiveRuntime = runnable.some((sess) => String(sess?.executionMode || sess?.meta?.execution_mode || "").toLowerCase() === "live" || sess?.liveVaultReady === true || sess?.meta?.live_vault_ready === true);
    // Live NKR is ticked by the backend worker and must continue after the browser closes.
    if (hasLiveRuntime || !hasRunnable || rotationShadowBusy || ["PAUSED", "STOPPED", "STOPPING"].includes(String(nkrControlState || "").toUpperCase())) return;
    if (now - Number(rotationAutoShadowRef.current || 0) < 90_000) return;
    rotationAutoShadowRef.current = now;
    runRotationShadowSimulation({ silent: true });
  }, 30_000, Boolean(Array.isArray(rotationSessions) && rotationSessions.length && !["PAUSED", "STOPPED"].includes(String(nkrControlState || "").toUpperCase())));


  // watch snapshot polling
  const inflightWatch = useRef(false);
  const watchRefreshQueued = useRef(false);
  const watchRetryRef = useRef({ key: "", n: 0, t: null });

  const fillMissingWatchMarketData = useCallback(async (rowsArg, itemsArg) => {
    const rows = Array.isArray(rowsArg) ? rowsArg : [];
    const items = Array.isArray(itemsArg) ? itemsArg : [];
    // Mobile can have price/volume cached while Market Cap is still missing.
    // So we hydrate rows when either price OR Market Cap is incomplete.
    const marketMissing = rows.filter((r) => {
      const mode = String(r?.mode || "market").toLowerCase();
      const p = Number(r?.price);
      const mcRaw = r?.marketCap ?? r?.market_cap ?? r?.mcap ?? r?.marketcap;
      const mc = Number(mcRaw);
      return mode === "market" && ((!Number.isFinite(p) || p <= 0) || !Number.isFinite(mc) || mc <= 0);
    });
    if (!marketMissing.length) return rows;

    // Build symbol <-> CoinGecko id hints from both rows and watch items.
    const itemBySym = new Map(
      items
        .map((it) => [String(it?.symbol || "").toUpperCase(), it])
        .filter(([sym]) => !!sym)
    );

    const marketById = {};
    const ids = Array.from(new Set(
      marketMissing
        .map((r) => {
          const sym = String(r?.symbol || "").toUpperCase();
          const item = itemBySym.get(sym);
          return String(r?.coingecko_id || r?.id || item?.coingecko_id || item?.id || "").trim().toLowerCase();
        })
        .filter(Boolean)
    ));

    // Strongest fallback first: exact CoinGecko ids.
    if (ids.length) {
      try {
        const qs = new URLSearchParams({
          ids: ids.join(","),
          vs_currencies: "usd",
          include_24hr_change: "true",
          include_24hr_vol: "true",
          include_market_cap: "true",
        }).toString();
        const cg = await api(`/api/coingecko/simple_price?${qs}`, { method: "GET", token, wallet });
        for (const [id, data] of Object.entries(cg || {})) {
          const p = Number(data?.usd);
          if (!Number.isFinite(p) || p <= 0) continue;
          marketById[String(id || "").toLowerCase()] = {
            price: p,
            change24h: Number(data?.usd_24h_change),
            volume24h: Number(data?.usd_24h_vol),
            marketCap: Number(data?.usd_market_cap),
            market_cap: Number(data?.usd_market_cap),
            source: "coingecko-id",
          };
        }
      } catch (e) {
        console.warn("coingecko id fallback failed", e);
      }
    }

    const nativeSyms = Array.from(new Set(
      marketMissing.map((r) => String(r?.symbol || "").toUpperCase()).filter((s) => ["ETH","BNB","POL","MATIC"].includes(s))
    ));
    const nativeMap = {};
    if (nativeSyms.length) {
      try {
        const px = await fetchNativeUsdPrices(nativeSyms.map((s) => (s === "MATIC" ? "POL" : s)));
        for (const [k, v] of Object.entries(px || {})) {
          nativeMap[k === "POL" ? "POL" : k] = Number(v);
        }
        if (nativeMap.POL && !nativeMap.MATIC) nativeMap.MATIC = nativeMap.POL;
      } catch {}
    }

    const otherSyms = Array.from(new Set(
      marketMissing
        .map((r) => String(r?.symbol || "").toUpperCase())
        .filter(Boolean)
        .filter((s) => !nativeMap[s])
    ));
    const otherMap = {};
    if (otherSyms.length) {
      try {
        const priceResp = await api(`/api/prices?symbols=${encodeURIComponent(otherSyms.join(","))}`, { method: "GET", token, wallet });
        const priceMap = priceResp?.prices || {};
        for (const [sym, data] of Object.entries(priceMap || {})) {
          const p = Number(data?.price);
          if (Number.isFinite(p) && p > 0) {
            otherMap[String(sym || "").toUpperCase()] = {
              price: p,
              change24h: Number(data?.change24h ?? data?.chg_24h),
              volume24h: Number(data?.volume24h ?? data?.vol),
              source: "price-fallback",
            };
          }
        }
      } catch (e) {
        console.warn("supplemental market prices failed", e);
      }
    }

    let changed = false;
    const next = rows.map((row) => {
      const sym = String(row?.symbol || "").toUpperCase();
      const cur = Number(row?.price);
      const curMc = Number(row?.marketCap ?? row?.market_cap ?? row?.mcap ?? row?.marketcap);
      const needsPrice = !Number.isFinite(cur) || cur <= 0;
      const needsMcap = !Number.isFinite(curMc) || curMc <= 0;
      if (!needsPrice && !needsMcap) return row;

      const item = itemBySym.get(sym);
      const cgId = String(row?.coingecko_id || row?.id || item?.coingecko_id || item?.id || "").trim().toLowerCase();
      const exact = (cgId && marketById[cgId]) ? marketById[cgId] : null;
      const fallback = exact || (nativeMap[sym] ? { price: nativeMap[sym], source: "native-fallback" } : null) || otherMap[sym];

      const p = Number(fallback?.price);
      const nextPrice = Number.isFinite(p) && p > 0 ? p : cur;
      const fbMc = Number(fallback?.marketCap ?? fallback?.market_cap ?? fallback?.usd_market_cap);
      const nextMcap = Number.isFinite(fbMc) && fbMc > 0 ? fbMc : (Number.isFinite(curMc) && curMc > 0 ? curMc : null);
      if ((!Number.isFinite(nextPrice) || nextPrice <= 0) && nextMcap == null) return row;

      changed = true;
      const ch24 = Number.isFinite(Number(fallback?.change24h)) ? Number(fallback.change24h) : row?.change24h ?? row?.chg_24h ?? null;
      const v24 = Number.isFinite(Number(fallback?.volume24h)) ? Number(fallback.volume24h) : row?.volume24h ?? row?.vol ?? null;

      return {
        ...row,
        coingecko_id: row?.coingecko_id || row?.id || cgId || undefined,
        id: row?.id || row?.coingecko_id || cgId || undefined,
        price: nextPrice,
        change24h: ch24,
        chg_24h: ch24,
        volume24h: v24,
        vol: v24,
        marketCap: nextMcap,
        market_cap: nextMcap,
        source: row?.source === "error" || row?.source === "pending" || !row?.source ? (fallback?.source || "price-fallback") : row.source,
      };
    });

    if (!changed) return rows;
    setWatchRows(next);
    return next;
  }, [token, wallet, setWatchRows]);

  const fetchWatchSnapshot = async (itemsOverride = null, opts = {}) => {
    if (!wallet && !opts?.allowGuest) {
      setWatchRows([]);
      return;
    }
    if (inflightWatch.current) {
      // If a refresh is requested while a snapshot is already in-flight, queue exactly one refresh.
      watchRefreshQueued.current = true;
      return;
    }
    inflightWatch.current = true;
    try {
      const wa = resolveWalletAddress(wallet);
      const snapPath = wa ? `/api/watchlist/snapshot?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}` : "/api/watchlist/snapshot";
      const r = await api(snapPath, { method: "POST", token, wallet: wa || wallet, body: { wallet: wa || wallet, wallet_address: wa || wallet, items: (itemsOverride ?? watchItems) } });
      const nextRowsRaw = (r?.results || r?.rows || []);
      // Merge-only rule: watchItems is the source of truth. Never resurrect removed items.
      const allowedKeys = new Set(((itemsOverride ?? watchItems) || []).map(_watchKeyFromItem));
      const tomb = _loadTombstones();
      const nextRows = (Array.isArray(nextRowsRaw) ? nextRowsRaw : []).filter((row) => {
        const k = _watchKeyFromRow(row);
        if (tomb && tomb[k] != null) return false;
        // Also drop anything not in current watchItems (never replace by snapshot).
        return allowedKeys.has(k);
      });
      let mergedRows = [];
      setWatchRows((prev0) => {
        const prev = Array.isArray(prev0) ? prev0 : [];
        const prevMap = new Map(prev.map((r) => [_watchKeyFromRow(r), r]));
        const nextMap = new Map((nextRows || []).map((r) => [_watchKeyFromRow(r), r]));
        const itemsSrc = (itemsOverride ?? watchItems) || [];
        const merged = [];

        // Ensure one row per watch item (source of truth), preserving any existing placeholder rows
        for (const it of itemsSrc) {
          const k = _watchKeyFromItem(it);
          const row = nextMap.get(k) || prevMap.get(k) || {
            symbol: String(it?.symbol || "").toUpperCase(),
            mode: String(it?.mode || "market"),
            coingecko_id: String(it?.coingecko_id || it?.id || ""),
            id: String(it?.coingecko_id || it?.id || ""),
            name: it?.name || String(it?.symbol || "").toUpperCase(),
            price: null,
            chg_24h: null,
            vol: null,
            source: "pending",
          };
          merged.push(row);
          nextMap.delete(k);
        }

        // Add any remaining rows returned by backend that are still allowed (should be none, but keep safe)
        for (const [k, row] of nextMap.entries()) {
          if (allowedKeys.has(k)) merged.push(row);
        }

        mergedRows = merged;
        return merged;
      });
      try {
        await fillMissingWatchMarketData(mergedRows, (itemsOverride ?? watchItems) || []);
      } catch {}
      if ((r?.results || r?.rows || []).length) {
        const symUp = String(gridItem || "").toUpperCase();
        const list = (r?.results || r?.rows || []);
        const exists = (list || []).some((x) => String(x.symbol || "").toUpperCase() === symUp);
        if (!exists && (list || []).length) setGridItem(String(list[0].symbol || "BTC").toUpperCase());
      }

      // If backend is warming up (new coin), rows may temporarily show source="error"/missing price.
      // Retry a couple times quickly so user doesn't need a full page refresh.
      const _items = (itemsOverride ?? watchItems) || [];
      const _key = Array.isArray(_items)
        ? _items
            .map((w) => String(w?.symbol || "").toUpperCase())
            .filter(Boolean)
            .sort()
            .join("|")
        : "";
      const _hasErrors = Array.isArray(nextRows) && nextRows.some((x) => {
        const src = String(x?.source || "").toLowerCase();
        const p = x?.price;
        return src === "error" || p == null || p === "—" || (typeof p === "string" && !p.trim());
      });

      if (!_hasErrors) {
        // reset retry state when good data arrives
        if (watchRetryRef.current.t) { try { clearTimeout(watchRetryRef.current.t); } catch {} }
        watchRetryRef.current = { key: _key, n: 0, t: null };
      } else if (_key) {
        if (watchRetryRef.current.key !== _key) {
          // new selection -> reset counter
          if (watchRetryRef.current.t) { try { clearTimeout(watchRetryRef.current.t); } catch {} }
          watchRetryRef.current = { key: _key, n: 0, t: null };
        }
        if (watchRetryRef.current.n < 5) {
          const delay = 1200 * (watchRetryRef.current.n + 1);
          const nnext = watchRetryRef.current.n + 1;
          if (watchRetryRef.current.t) { try { clearTimeout(watchRetryRef.current.t); } catch {} }
          watchRetryRef.current.t = setTimeout(() => {
            watchRetryRef.current.n = nnext;
            fetchWatchSnapshot(itemsOverride, { ...opts, force: true });
          }, delay);
        }
      }
    } catch (e) {
      setErrorMsg(`Watchlist: ${e.message}`);
    } finally {
      inflightWatch.current = false;
      if (watchRefreshQueued.current) {
        // Run exactly one queued refresh after the current one completes.
        watchRefreshQueued.current = false;
        // Force refresh so we don't get blocked by any internal guards.
        fetchWatchSnapshot(itemsOverride, { ...opts, force: true });
      }
    }
  };

  useEffect(() => {
    syncWatchlistFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);
  useInterval(syncWatchlistFromServer, 15000, !!wallet);
  useInterval(syncAppStateFromServer, 15000, !!wallet);

  useEffect(() => {
    const onFocusSync = () => {
      syncWatchlistFromServer();
      syncAppStateFromServer();
      setCompareForceNonce((n) => n + 1);
    };
    window.addEventListener("focus", onFocusSync);
    document.addEventListener("visibilitychange", onFocusSync);
    return () => {
      window.removeEventListener("focus", onFocusSync);
      document.removeEventListener("visibilitychange", onFocusSync);
    };
  }, [syncWatchlistFromServer, syncAppStateFromServer]);

  // 🔁 Refetch snapshot immediately when watchlist changes (so newly added coins get data without full page refresh)
  const watchlistKey = useMemo(() => {
  const arr = Array.isArray(watchItems) ? watchItems : [];
  // Use full unique key (mode + id/contract + symbol) so adding tokens or same-symbol variants triggers refresh immediately.
  return arr
    .map((w) => {
      const sym = String(w?.symbol || "").toUpperCase().trim();
      const mode = String(w?.mode || "market").toLowerCase();
      const id = String(w?.coingecko_id || w?.id || "").toLowerCase();
      const addr = String(w?.contract || w?.tokenAddress || "").toLowerCase();
      return `${mode}|${sym}|${id || addr}`;
    })
    .filter(Boolean)
    .join("|");
}, [watchItems]);

  useEffect(() => {
    if (!watchItems || !watchItems.length) return;
    fetchWatchSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistKey]);

  // Normal market/watchlist refresh:
  // Keep this slower than Grid execution. It updates prices, 24h data and mini charts.
  // fetchWatchSnapshot already has an in-flight guard, so it will not stack requests.
  useInterval(
    () => fetchWatchSnapshot(null, { force: true, user: false }),
    30000,
    !!wallet && Array.isArray(watchItems) && watchItems.length > 0
  );

  // compare fetch (batched /api/compare)
  const inflightCompare = useRef(false);
  const fetchCompare = async (opts = {}) => {
    if (!compareSymbols.length) {
      // Clear chart immediately when nothing is selected (prevents stale cache lines)
      setCompareSeries({});
      lastGoodCompareRef.current = {};
      try { localStorage.setItem(LS_COMPARE_SERIES_CACHE, JSON.stringify({})); } catch {}
      if (compareRetryRef.current.t) { try { clearTimeout(compareRetryRef.current.t); } catch {} }
      compareRetryRef.current = { key: "", n: 0, t: null };
      return;
    }

    // simple throttle so UI changes don't spam the backend
    const now = Date.now();
    if (!opts.force && now - lastCompareFetchRef.current < 800) return;
    lastCompareFetchRef.current = now;

    // Abort previous in-flight compare request
    if (compareAbortRef.current) {
      try { compareAbortRef.current.abort(); } catch {}
    }
    const ac = new AbortController();
    compareAbortRef.current = ac;

    // Show cached series immediately (SWR) so chart renders without waiting for backend
    // NOTE: fetchRange can be overridden (used for preloading 2Y while keeping UI timeframe at 90D).
    const fetchRange = (opts && opts.fetchRangeOverride) ? String(opts.fetchRangeOverride).toUpperCase() : _compareFetchRange(timeframe);
    const cached = (opts.force || opts.noCache) ? null : _cmpGetCached(compareSymbols, fetchRange);
    if (cached && Object.keys(cached || {}).length) {
      setCompareSeries(cached);
      lastGoodCompareRef.current = cached;
    }

    setCompareLoading(true);
    try {
      const syms = compareSymbols.slice(0, 20).join(",");
      const wa = resolveWalletAddress(wallet);
      const qs = new URLSearchParams({ symbols: syms, range: fetchRange });
      if (wa) qs.set("wallet", wa);
      if (opts.force || opts.noCache) qs.set("_", String(Date.now()));
      const url = `${API_BASE}/api/compare?${qs.toString()}`;
      const r = await fetch(url, { method: "GET", credentials: "include", cache: "no-store", headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }, signal: ac.signal });

      let data = null;
      try { data = await r.json(); } catch { data = null; }

      if (!r.ok) {
        const msg = (data && (data.error || data.message)) ? (data.error || data.message) : `HTTP ${r.status}`;
        throw new Error(msg);
      }

      // reset HTTP-failure retry state on any successful HTTP response
      {
        const cmpKeyHTTP = compareSymbols.join("|");
        if (compareFailRetryRef.current.t) { try { clearTimeout(compareFailRetryRef.current.t); } catch {} }
        compareFailRetryRef.current = { key: cmpKeyHTTP, n: 0, t: null };
      }

      if (data && data.series) {
        const normalized = normalizeBackendSeries(data.series);
        // Ensure all currently selected symbols exist as keys (even if empty)
        for (const s of compareSymbols) {
          const S = String(s || "").toUpperCase();
          if (S && !Object.prototype.hasOwnProperty.call(normalized, S)) normalized[S] = [];
        }

        // If some selected coins are still warming up (empty series), retry a couple times.
        const cmpKey = compareSymbols.join("|");
        const missing = compareSymbols.filter((s) => {
          const S = String(s || "").toUpperCase();
          return !S || !Array.isArray(normalized[S]) || normalized[S].length === 0;
        });

        if (!missing.length) {
          if (compareRetryRef.current.t) { try { clearTimeout(compareRetryRef.current.t); } catch {} }
          compareRetryRef.current = { key: cmpKey, n: 0, t: null };
        } else {
          if (compareRetryRef.current.key !== cmpKey) {
            if (compareRetryRef.current.t) { try { clearTimeout(compareRetryRef.current.t); } catch {} }
            compareRetryRef.current = { key: cmpKey, n: 0, t: null };
          }
          if (compareRetryRef.current.n < 2) {
            const delay = 1400 * (compareRetryRef.current.n + 1);
            const nnext = compareRetryRef.current.n + 1;
            if (compareRetryRef.current.t) { try { clearTimeout(compareRetryRef.current.t); } catch {} }
            compareRetryRef.current.t = setTimeout(() => {
              compareRetryRef.current.n = nnext;
              fetchCompare({ force: true });
            }, delay);
          }
        }

        const hasAny = Object.values(normalized).some((arr) => Array.isArray(arr) && arr.length);
        if (hasAny) {
          setCompareSeries(normalized);
          lastGoodCompareRef.current = normalized;
          try { localStorage.setItem(LS_COMPARE_SERIES_CACHE, JSON.stringify(normalized)); } catch {}
          _cmpPutCached(compareSymbols, fetchRange, normalized);

          // If we were preloading 2Y, only switch UI to 2Y once data is actually sufficient.
          if (String(fetchRange).toUpperCase() === "2Y" && pendingTfRef.current === "2Y") {
            const ok2y = compareSymbols.every((s) => {
              const S = String(s || "").toUpperCase();
              const arr = normalized?.[S];
              return Array.isArray(arr) && arr.length >= MIN_2Y_POINTS;
            });
            setPendingTf(null);
            if (ok2y) {
              setTimeframe("2Y");
            } else {
              // Keep current timeframe (e.g. 90D) and inform user.
              setErrorMsg("2Y Daten sind noch nicht verfügbar (zu wenig Historie gesammelt)." );
            }
          }
        } else if (lastGoodCompareRef.current) {
          setCompareSeries(lastGoodCompareRef.current);
        }
      } else if (lastGoodCompareRef.current) {
        // keep last-good series if backend returns empty
        setCompareSeries(lastGoodCompareRef.current);
      }
    } catch (e) {
      // If request was aborted, ignore silently
      if (e && (e.name === "AbortError" || String(e).includes("AbortError"))) return;

      // keep last good data (no UI errors)
      if (lastGoodCompareRef.current) {
        setCompareSeries(lastGoodCompareRef.current);
      }

      // Auto-retry on transient backend/network failures so users don't need to refresh.
      // This is separate from the "missing series" warm-up retry above.
      try {
        const cmpKeyHTTP = compareSymbols.join("|");
        if (compareFailRetryRef.current.key !== cmpKeyHTTP) {
          if (compareFailRetryRef.current.t) { try { clearTimeout(compareFailRetryRef.current.t); } catch {} }
          compareFailRetryRef.current = { key: cmpKeyHTTP, n: 0, t: null };
        }
        const maxRetries = 3;
        const online = (typeof navigator === "undefined") ? true : (navigator.onLine !== false);
        if (online && compareFailRetryRef.current.n < maxRetries) {
          const nnext = compareFailRetryRef.current.n + 1;
          // backoff: 1.2s, 2.4s, 4.8s
          const delay = 1200 * Math.pow(2, compareFailRetryRef.current.n);
          if (compareFailRetryRef.current.t) { try { clearTimeout(compareFailRetryRef.current.t); } catch {} }
          compareFailRetryRef.current.t = setTimeout(() => {
            compareFailRetryRef.current.n = nnext;
            fetchCompare({ force: true });
          }, delay);
        }
      } catch {}

      // If we were preloading 2Y, clear pending state on failure.
      try {
        if (String(fetchRange).toUpperCase() === "2Y" && pendingTfRef.current === "2Y") {
          setPendingTf(null);
        }
      } catch {}

      // still log for debugging
      // eslint-disable-next-line no-console
      console.warn("compare fetch failed:", e);
    } finally {
      setCompareLoading(false);
    }
  };

  useEffect(() => {
    fetchCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareFetchRange, compareSymbols.join("|")]);

  useEffect(() => {
    if (!compareForceNonce) return;
    fetchCompare({ force: true, noCache: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareForceNonce]);

  // Compare/history refresh: keep charts reasonably fresh without hammering backend/CoinGecko.
  useInterval(() => fetchCompare({ force: true, noCache: true }), 90000, compareSymbols.length > 0);

  // policy (UI-only for now)

  // grid
  
// Hydrate grid chain/item from backend once per wallet.
// Backend is the source of truth across devices, so do not trust local defaults on refresh.
useEffect(() => {
  let cancelled = false;
  if (!walletAddress) {
    setGridUiHydrated(false);
    return;
  }

  (async () => {
    try {
      const r = await api("/api/grid/ui/state", {
        method: "GET",
        token,
        wallet: walletAddress,
      });
      if (cancelled) return;

      const srvChain = String(r?.active_chain ?? r?.activeChain ?? "").toUpperCase().trim();
      const srvItem = String(r?.active_item ?? r?.activeItem ?? "").trim();
      const srvSym = srvItem ? String(srvItem.split(":").pop() || "").toUpperCase().trim() : "";

      if (srvChain && ENABLED_CHAINS.includes(srvChain)) {
        setBalActiveChain((prev) => (String(prev || "").toUpperCase() === srvChain ? prev : srvChain));
        setWsChainKey((prev) => (String(prev || "").toUpperCase() === srvChain ? prev : srvChain));
      }
      if (srvSym) {
        setGridItem((prev) => (String(prev || "").toUpperCase() === srvSym ? prev : srvSym));
      }
    } catch (_) {
      // keep local fallback if backend UI state is unavailable
    } finally {
      if (!cancelled) setGridUiHydrated(true);
    }
  })();

  return () => { cancelled = true; };
}, [walletAddress, token]);

// After hydration and on every chain/item switch, ask backend for the authoritative grid context.
// This fixes the stale-chain issue where the UI only became correct after toggling BNB -> POL manually.
useEffect(() => {
  let cancelled = false;
  if (!gridUiHydrated || !walletAddress || !gridItemId) return;

  (async () => {
    try {
      const params = new URLSearchParams({
        chain: activeGridChainKey,
        item: gridItemId,
        wallet: walletAddress,
        addr: walletAddress,
      });
      const r = await api(`/api/grid/init?${params.toString()}`, {
        method: "GET",
        token,
        wallet: walletAddress,
      });
      if (cancelled) return;
      if (r?.status && String(r.status).toLowerCase() === "error") return;

      const srvChain = String(r?.active_chain ?? r?.loaded_chain ?? activeGridChainKey).toUpperCase().trim();
      const srvItemId = String(r?.active_item ?? r?.loaded_item_id ?? gridItemId).trim();
      const srvSym = srvItemId ? String(srvItemId.split(":").pop() || "").toUpperCase().trim() : "";

      if (srvChain && ENABLED_CHAINS.includes(srvChain)) {
        setBalActiveChain((prev) => (String(prev || "").toUpperCase() === srvChain ? prev : srvChain));
        setWsChainKey((prev) => (String(prev || "").toUpperCase() === srvChain ? prev : srvChain));
      }
      if (srvSym && srvSym !== String(gridItem || "").toUpperCase().trim()) {
        setGridItem(srvSym);
      }

      const initOrdersRaw = getGridOrdersFromResponse(r);
      if (Array.isArray(initOrdersRaw)) {
        const initOrders = normalizeGridOrders(initOrdersRaw);
        setGridOrders(initOrders);
      }

      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
      applyGridMetaResponse(r, srvItemId || gridItemId);

      // /api/grid/init returns backend vault_state with backend field names
      // (vault_balance, vault_balance_wei). The UI reads the historical frontend
      // fields (polBalance, polBalanceWei), so normalize here. Otherwise a real
      // on-chain vault balance can show as 0 in Grid Trader until a separate
      // /api/vault/state refresh happens.
      if (r?.vault_state) {
        const vs = r.vault_state || {};
        const rawBal = vs.vault_balance ?? vs.polBalance ?? vs.nativeBalance ?? null;
        const rawWei = vs.vault_balance_wei ?? vs.polBalanceWei ?? vs.nativeBalanceWei ?? null;
        const rawHeldBal = vs.heldTokenBal ?? vs.held_token_bal ?? null;
        const rawHeldWei = vs.heldTokenBalWei ?? vs.held_token_bal_wei ?? null;
        setVaultState((prev) => ({
          ...(prev || {}),
          ...(vs || {}),
          polBalance: rawBal != null ? (Number(rawBal) || 0) : (Number(prev?.polBalance) || 0),
          polBalanceWei: rawWei != null ? hexToBigInt(rawWei) : (prev?.polBalanceWei ?? null),
          heldToken: vs.heldToken ?? vs.held_token ?? prev?.heldToken ?? null,
          heldTokenBal: rawHeldBal != null ? (Number(rawHeldBal) || 0) : (Number(prev?.heldTokenBal) || 0),
          heldTokenBalWei: rawHeldWei != null ? hexToBigInt(rawHeldWei) : (prev?.heldTokenBalWei ?? null),
          inCycle: !!(vs.inCycle ?? vs.in_cycle ?? prev?.inCycle),
          operatorEnabled: !!(vs.operatorEnabled ?? vs.operator_enabled ?? prev?.operatorEnabled),
        }));
      }

      setTimeout(() => { try { refreshVaultState(srvChain || activeGridChainKey || "", { force: true }); } catch (_) {} }, 300);
    } catch (e) {
      setErrorMsg((prev) => prev || `Grid init: ${e?.message || e}`);
    }
  })();

  return () => { cancelled = true; };
}, [gridUiHydrated, walletAddress, token, activeGridChainKey, gridItemId]);

const [gridBusy, setGridBusy] = useState({
  start: false,
  stop: false,
  add: false,
  stopOrderId: null,
  deleteOrderId: null,
});

const isGridReady = useMemo(() => {
  return !!walletAddress && !!gridItemId && gridUiHydrated;
}, [walletAddress, gridItemId, gridUiHydrated]);

const mergeGridMetaStable = useCallback((prev, incoming) => {
  const out = { ...(prev || {}), ...(incoming || {}) };
  const prevTick = Number(prev?.tick || 0);
  const nextTick = Number(incoming?.tick || 0);

  if (Number.isFinite(prevTick) && prevTick > 0) {
    if (!Number.isFinite(nextTick) || nextTick <= 0) {
      out.tick = prevTick;
    } else {
      // Never let stale/alternate streams move the tick backwards.
      if (nextTick < prevTick) out.tick = prevTick;
      // Forward jumps are allowed so the visible tick can continue advancing.
    }
  } else if (Number.isFinite(nextTick) && nextTick > 500) {
    // When no stable tick exists yet, ignore obviously wrong large counter values.
    out.tick = prev?.tick ?? null;
  }

  const prevPrice = Number(prev?.price || 0);
  const nextPrice = Number(incoming?.price || 0);
  if (Number.isFinite(prevPrice) && prevPrice > 0 && (!Number.isFinite(nextPrice) || nextPrice <= 0)) {
    out.price = prevPrice;
  }
  return out;
}, []);

const applyGridMetaResponse = useCallback((r, fallbackItemId = gridItemId) => {
  setGridMeta((prev) => {
    const incoming = getGridMetaFromResponse(r, { ...prev, gridItemId: fallbackItemId });
    return mergeGridMetaStable(prev, incoming);
  });
}, [gridItemId, mergeGridMetaStable]);

useEffect(() => {
  if (!gridItemId) return;
  // BUILD435: visible Grid Orders are wallet-wide and must survive setup chain/item changes.
  // Only reset setup-specific vault stats here.
  setGridVaultStats({ vault: 0, reserved: 0, free: 0 });
}, [gridItemId]);

const gridOrdersFetchGuardRef = useRef({ key: "", ts: 0, inflight: false });

const fetchGridOrders = useCallback(async (opts = {}) => {
  // Hard request-storm guard. This function is called from several places
  // (initial load, manual refresh, intervals, grid actions). Keep the guard here
  // so every caller is protected, even if React re-renders quickly.
  const force = !!(opts && opts.force);
  const nowMs = Date.now();
  const requestKey = `walletwide|${String(walletAddress || "")}`;
  const guard = gridOrdersFetchGuardRef.current || { key: "", ts: 0, inflight: false };
  const openNow = (Array.isArray(gridOrders) ? gridOrders : []).some((o) => String(o?.status || "").toUpperCase() === "OPEN");
  const minGapMs = openNow ? 15000 : 60000;
  if (guard.inflight) return;
  if (!force && guard.key === requestKey && nowMs - Number(guard.ts || 0) < minGapMs) return;

  // Only fetch when wallet + backend grid context are ready.
  // Do not require the backend auth token here: api() can fall back to API key + wallet header,
  // and requiring token caused empty grid state after refresh on some devices until auth finished.
  if (!gridUiHydrated || !walletAddress) return;

  gridOrdersFetchGuardRef.current = { key: requestKey, ts: nowMs, inflight: true };
  try {
    // Be permissive with query param naming across backend revisions.
    // Some deployments use `addr`, others `wallet`.
    // BUILD435: fetch all Grid orders for this wallet across every chain.
    // Chain/item selection below is setup context only and must not hide running sessions.
    const params = new URLSearchParams({
      addr: walletAddress,
      wallet: walletAddress,
    });

    const r = await api(`/api/grid/orders?${params.toString()}`, {
      method: "GET",
      token,
    wallet: walletAddress,
    });

    // If auth isn't ready yet, NEVER overwrite current UI state
    if (r?.unauthenticated || r?.data?.unauthenticated) {
      return;
    }

    setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));

    const nextOrdersRaw = r?.orders ?? r?.data?.orders;

    // Do not clear orders if backend didn't return an orders array (transient HTML/errors/etc.)
    if (!Array.isArray(nextOrdersRaw)) return;

    const nextOrders = normalizeGridOrders(nextOrdersRaw);
    setGridOrders(nextOrders);

	try {
      const sym = String(gridItem || "").toUpperCase().trim();
      if (["POL", "BNB", "ETH"].includes(sym)) {
        setTimeout(() => { try { refreshVaultState(sym); } catch (_) {} }, 500);
      }
    } catch (_) {}

    // Wallet-wide order responses are presentation-only; do not overwrite selected setup metadata.
    if (r?.item || r?.active_item || r?.data?.item || r?.data?.active_item) {
      applyGridMetaResponse(r, gridItemId);
    }
  } catch (e) {
    // Browser/timeout aborts are transient. Keep current grid state and do not show a red error box.
    const msg = String(e?.message || e || "");
    if (
      e?.name === "AbortError" ||
      msg.toLowerCase().includes("abort") ||
      msg.toLowerCase().includes("signal is aborted")
    ) {
      return;
    }
    // Keep existing orders on real backend errors; surface message.
    setErrorMsg(`Grid orders: ${msg || "temporary backend error"}`);
  } finally {
    const cur = gridOrdersFetchGuardRef.current || {};
    gridOrdersFetchGuardRef.current = { ...cur, inflight: false };
  }
}, [gridUiHydrated, gridItemId, walletAddress, token, normalizeGridOrders, gridItem, refreshVaultState, gridOrders]);

// Auto-load orders as soon as wallet/auth becomes ready (e.g. after refresh)
useEffect(() => {
  if (!isGridReady) return;
  fetchGridOrders();
}, [isGridReady, fetchGridOrders]);

const kickGridRefresh = useCallback(() => {
  try { fetchGridOrders({ force: true }); } catch (_) {}
}, [fetchGridOrders]);

const hasOpenGridOrders = useMemo(
  () => (gridOrders || []).some((o) => String(o?.status || "").toUpperCase() === "OPEN"),
  [gridOrders]
);

const gridUiActive = ["grid", "trading"].includes(String(gridMode || "").toLowerCase());
const gridPollingAllowed =
  gridUiActive &&
  !!isGridReady &&
  !!gridItemId &&
  !!walletAddress &&
  !gridBusy.start &&
  !gridBusy.stop &&
  !gridBusy.add &&
  !gridBusy.stopOrderId &&
  !gridBusy.deleteOrderId &&
  !(typeof document !== "undefined" && document.hidden);

// Grid order-state refresh:
// - active/open orders: fast enough for trader UI
// - no open orders: slower background refresh
useInterval(
  () => {
    fetchGridOrders();
  },
  hasOpenGridOrders ? 45000 : 180000,
  gridPollingAllowed
);

// Execute polling: this is the real trigger that makes BUY/SELL fire.
// It only runs while at least one order is OPEN.
useInterval(
  async () => {
    if (!gridItemId || !walletAddress) return;
    try {
      const r = await setGridExecute(gridItemId);
      applyGridMetaResponse(r, gridItemId);
      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));

      // BUILD436: execute response is item-scoped. Never replace the wallet-wide order list
      // with it; immediately refresh the authoritative wallet-wide SQLite order view instead.
      try { fetchGridOrders({ force: true }); } catch (_) {}

      try {
        const sym = String(gridItem || "").toUpperCase().trim();
        if (["POL", "BNB", "ETH"].includes(sym)) {
          setTimeout(() => { try { refreshVaultState(sym); } catch (_) {} }, 500);
        }
      } catch (_) {}
    } catch (_) {
      // silent: polling should never spam the UI
    }
  },
  45000,
  gridPollingAllowed && hasOpenGridOrders
);

  async function gridStart() {
    console.log("[GRID] Start clicked");
    setErrorMsg("");

if (gridBusy.start) return;
if (!isGridReady) {
  setErrorMsg("Grid not ready yet (connect wallet + select coin).");
  return;
}
setGridBusy((s) => ({ ...s, start: true }));

    // NOTE: Do not silently block the button. Always hit backend so Network shows a request.
    // Backend will validate subscription / vault prerequisites and return a clear error if needed.

    // Safety: Grid runs autonomously via backend operator + Vault funds.
    // Require: Vault has budget deposited and operator is enabled (so backend can trade without further user signatures).
    const chainKeyPre = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
    const want = Number(gridInvestQty) || 0;
    const have = Number(vaultState?.polBalance || 0);

    // Friendly UI guidance instead of crashing:
    // If prerequisites are missing, open the Vault modal and pre-fill the required deposit amount.
    if (!vaultState?.operatorEnabled) {
      setErrorMsg("Core Vault setup required: configure the Core Vault and Executor authorization for this EVM network.");
      setWsChainKey(chainKeyPre);
      setWithdrawSendOpen(true);
      // (no hard return)
    }
    if (!want || want <= 0) {
      setErrorMsg("Set a Budget (Qty) amount > 0.");
      // (no hard return)
    }
    if (have <= 0) {
      setErrorMsg("Vault has 0 balance. Please deposit funds into the Vault first.");
      setWsChainKey(chainKeyPre);
      setDepositAmt(String(want));
      setWithdrawSendOpen(true);
      // (no hard return)
    }
    if (want > have + 1e-12) {
      const needed = Math.max(0, want - have);
      setErrorMsg(`Not enough Vault funds. Deposit +${needed.toFixed(6)} native into the Vault to start.`);
      setWsChainKey(chainKeyPre);
      setDepositAmt(String(needed));
      setWithdrawSendOpen(true);
      // (no hard return)
    }

    try {
      const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const curPriceNum = Number(gridMeta?.price ?? 0) || 0;
      const investQty = Number(gridInvestQty) || 0;
      const investUsd = (investQty > 0 && curPriceNum > 0) ? (investQty * curPriceNum) : investQty;
      const itemId =
        gridItemId ||
        gridMeta?.gridItemId ||
        gridMeta?.itemId ||
        gridMeta?.id ||
        `${chainKey}:${String(gridItem || "").toUpperCase()}`; // fallback
      const body = {
        item: gridItemId,
        // Include wallet so backend's anon-grid mode (GRID_ALLOW_ANON=1) can authorize.
        addr: walletAddress || undefined,
        order_mode: "MANUAL",
        // Vault budget is in native units (POL/BNB/ETH). Backend may ignore unknown keys; keep invest_usd for backwards compatibility; invest_qty is the new canonical key.
        invest_native: investQty,
        invest_qty: investQty,
        invest_usd: investUsd,
        budget_qty: investQty,
        budget_usd: investUsd,
        chain: chainKey,
        auto_path: !!gridAutoPath,
      };
      // BUILD387: Grid CoreVault session settles to USDC/USDT only (not the traded token).
      const gridPayout = normalizeStablePayoutAsset(manualPayoutAsset || "USDC", "USDC");
      await createCoreVaultSystemSession({
        system: "GRID",
        budgetUsd: Math.max(investUsd, investQty, 1),
        durationHours: 24,
        maxSlippageBps: Math.round((Number(manualSlippagePct) || 1) * 100),
        maxLossBps: Math.max(1, Math.round((Number(manualMaxLossPct) || 15) * 100)),
        chain: String(chainKey || "ETH").toUpperCase(),
        settlementAsset: gridPayout,
      });
      const r = await api("/api/grid/cycle/start", { method: "POST", token, body: { ...body, core_vault_session_reserved: true } });
      applyGridMetaResponse(r, itemId);
      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
      const startOrdersRaw = getGridOrdersFromResponse(r);
      if (Array.isArray(startOrdersRaw)) {
        const startOrders = normalizeGridOrders(startOrdersRaw);
        setGridOrders(startOrders);
      }
      setGridBusy((s) => ({ ...s, stop: false }));
      kickGridRefresh();
      setGridBusy((s) => ({ ...s, start: false }));
} catch (e) {
      setErrorMsg(`Grid start: ${e.message}`);
      setGridBusy((s) => ({ ...s, start: false }));
    }
  }

async function setGridExecute(itemIdArg = "") {
  const itemId = String(
    itemIdArg ||
    gridItemId ||
    gridMeta?.gridItemId ||
    gridMeta?.itemId ||
    gridMeta?.id ||
    `${(balActiveChain || wsChainKey || DEFAULT_CHAIN)}:${String(gridItem || "").toUpperCase()}`
  ).trim();
  if (!itemId) throw new Error("Missing grid item for execute.");

  const frontendPriceCandidates = [
    shownGridPrice,
    gridLiveFallback,
    gridNativeUsd?.[String(gridItem || "").toUpperCase()],
    gridMeta?.price,
  ];
  const frontendPrice = frontendPriceCandidates
    .map((v) => Number(v))
    .find((v) => Number.isFinite(v) && v > 0);

  return await api("/api/grid/execute", {
    method: "POST",
    token,
    wallet: walletAddress,
    body: {
      item: itemId,
      addr: walletAddress || undefined,
      wallet: walletAddress || undefined,
      price: frontendPrice,
      current_price: frontendPrice,
      market_price: frontendPrice,
    },
  });
}

  async function gridStop() {
    setErrorMsg("");

if (gridBusy.stop) return;
if (!isGridReady) {
  setErrorMsg("Grid not ready yet (connect wallet + select coin).");
  return;
}
setGridBusy((s) => ({ ...s, stop: true }));

    try {
	  const chainKey = (balActiveChain || wsChainKey || DEFAULT_CHAIN);
      const itemId =
        gridItemId ||
        gridMeta?.gridItemId ||
        gridMeta?.itemId ||
        gridMeta?.id ||
        `${chainKey}:${String(gridItem || "").toUpperCase()}`;
      const r = await api("/api/grid/stop", { method: "POST", token, wallet: walletAddress, body: { item: gridItemId, addr: walletAddress || undefined }, });
      applyGridMetaResponse(r, itemId);
      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
      const stopOrdersRaw = getGridOrdersFromResponse(r);
      if (Array.isArray(stopOrdersRaw)) {
        const stopOrders = normalizeGridOrders(stopOrdersRaw);
        setGridOrders(stopOrders);
      }
      setGridBusy((s) => ({ ...s, stop: false }));
      kickGridRefresh();
    } catch (e) {
      setErrorMsg(`Grid stop: ${e.message}`);
      setGridBusy((s) => ({ ...s, stop: false }));
    }
  }


  const activeGridNativeUsd = useMemo(() => {
    const px = Number(walletPx?.native?.[activeGridChainSymbol]);
    return Number.isFinite(px) && px > 0 ? px : null;
  }, [walletPx, activeGridChainSymbol]);

  const getNexusOrderPriceUsd = useCallback((symbol) => {
    const sym = String(symbol || "").toUpperCase().trim();
    if (!sym) return 0;
    // Stables are always $1 — never inherit grid/NKR ticker price.
    if (["USDC", "USDT", "USD", "DAI"].includes(sym) || sym.includes("USDC") || sym.includes("USDT")) return 1;
    try {
      const rows = Array.isArray(watchRows) ? watchRows : [];
      const row = rows.find((r) => String(r?.symbol || r?.coin || r?.id || "").toUpperCase() === sym);
      if (row) {
        const candidates = [
          row?.price,
          row?.priceUsd,
          row?.usd,
          row?.current_price,
          row?.currentPrice,
          row?.market_data?.current_price?.usd,
        ];
        for (const v of candidates) {
          const n = Number(v);
          if (Number.isFinite(n) && n > 0) return n;
        }
      }
    } catch (_) {}
    // Only use active native USD when the symbol is that chain's native coin.
    const nativeSym = String(activeGridChainKey || "").toUpperCase() === "BNB" ? "BNB"
      : String(activeGridChainKey || "").toUpperCase() === "POL" ? "POL"
      : "ETH";
    if (sym === nativeSym || (sym === "MATIC" && nativeSym === "POL") || (sym === "WETH" && nativeSym === "ETH") || (sym === "WBNB" && nativeSym === "BNB")) {
      const activePx = Number(activeGridNativeUsd || 0);
      if (Number.isFinite(activePx) && activePx > 0) return activePx;
    }
    return 0;
  }, [watchRows, activeGridNativeUsd, activeGridChainKey]);

  const resolveFundingBeforeOrder = useCallback(async ({ source, chain, symbol, side = "BUY", priceUsd, qty, budgetUsd, meta = {}, fundingApproved = false, fundingSourceAsset = "", pendingKind = "CORE" }) => {
    if (fundingApproved) return true;
    try {
      const src = String(source || "GRID").toUpperCase();
      const ch = String(chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
      const sym = String(symbol || "").toUpperCase();
      const px = Number(priceUsd || 0);
      const q = Number(qty || 0);
      const usd = Number(budgetUsd || (px * q) || 0);
      if (!walletAddress || !token || !sym || !(usd > 0) || !(px > 0) || !(q > 0)) return true;

      const report = await api("/api/nexus/funding/resolve", {
        method: "POST",
        token,
        wallet: walletAddress,
        body: {
          wallet: walletAddress,
          wallet_address: walletAddress,
          item: `${ch}:${sym}`,
          item_id: `${ch}:${sym}`,
          chain: ch,
          symbol: sym,
          side: String(side || "BUY").toUpperCase(),
          price: px,
          qty: q,
          amountUsd: usd,
          budgetUsd: usd,
          nativePriceUsd: Number(activeGridNativeUsd || 0),
          source: src,
          ...meta,
        },
      });

      if (report?.funding_required) {
        setFundingPrompt({
          source: src,
          chain: ch,
          symbol: sym,
          side: String(side || "BUY").toUpperCase(),
          budgetUsd: usd,
          priceUsd: px,
          qty: q,
          message: report?.message || `Not enough ${ch} available.`,
          shortageUsd: Number(report?.shortageUsd || 0),
          shortageNative: Number(report?.shortageNative || 0),
          suggestions: Array.isArray(report?.suggestions) ? report.suggestions : [],
          pending: { kind: pendingKind, source: src, chain: ch, symbol: sym, side, budgetUsd: usd, priceUsd: px, qty: q, meta },
          ts: Date.now(),
        });
        return false;
      }
      return true;
    } catch (e) {
      if (e?.status === 409 && e?.data?.funding) {
        const report = e.data.funding;
        const src = String(source || "GRID").toUpperCase();
        const ch = String(chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
        const sym = String(symbol || "").toUpperCase();
        setFundingPrompt({
          source: src,
          chain: ch,
          symbol: sym,
          side: String(side || "BUY").toUpperCase(),
          budgetUsd: Number(budgetUsd || 0),
          priceUsd: Number(priceUsd || 0),
          qty: Number(qty || 0),
          message: report?.message || `Not enough ${ch} available.`,
          shortageUsd: Number(report?.shortageUsd || 0),
          shortageNative: Number(report?.shortageNative || 0),
          suggestions: Array.isArray(report?.suggestions) ? report.suggestions : [],
          pending: { kind: pendingKind, source: src, chain: ch, symbol: sym, side, budgetUsd, priceUsd, qty, meta },
          ts: Date.now(),
        });
        return false;
      }
      // Funding resolver is a safety helper. If it is unavailable, do not hard-freeze the UI;
      // the backend add endpoint still performs its own funding check.
      return true;
    }
  }, [api, token, walletAddress, activeGridChainKey, activeGridNativeUsd]);

  const addCoreOrderFromModule = useCallback(async ({ source, chain, symbol, side = "BUY", budgetUsd, priceUsd, meta = {}, fundingApproved = false, fundingSourceAsset = "" }) => {
    setErrorMsg("");
    if (!token) return setErrorMsg("");
    if (!requirePro("Placing a new order")) return;
    if (gridBusy.add) return;
    const src = String(source || "MANUAL").toUpperCase();
    const ch = String(chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase().trim();
    const sym = String(symbol || "").toUpperCase().trim();
    if (!sym) return setErrorMsg(`${src}: select an asset first.`);
    const usd = Number(String(budgetUsd ?? "").replace(",", "."));
    if (!Number.isFinite(usd) || usd <= 0) return setErrorMsg(`${src}: set a budget > 0.`);
    const px = Number(priceUsd || getNexusOrderPriceUsd(sym));
    if (!Number.isFinite(px) || px <= 0) return setErrorMsg(`${src}: price unavailable for ${sym}.`);
    const qty = usd / px;
    if (!Number.isFinite(qty) || qty <= 0) return setErrorMsg(`${src}: invalid quantity.`);

    const fundingOk = await resolveFundingBeforeOrder({
      source: src, chain: ch, symbol: sym, side, priceUsd: px, qty, budgetUsd: usd, meta, fundingApproved, fundingSourceAsset, pendingKind: "CORE"
    });
    if (!fundingOk) return;

    setGridBusy((st) => ({ ...st, add: true }));
    try {
      const itemId = `${ch}:${sym}`;
      const slippagePct = src === "ROTATION"
        ? Number(String(rotationMaxSlippage || "1").replace(",", "."))
        : src === "TRADING"
          ? Number(String(tradingMaxSlippagePct || "1").replace(",", "."))
          : Number(manualSlippagePct || 5);
      const deadlineMin = Number(manualDeadlineMin || 20);
      const clientOrderId = `${src.toLowerCase()}_${ch}_${sym}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const body = {
        item: itemId,
        item_id: itemId,
        addr: walletAddress || undefined,
        wallet: walletAddress || undefined,
        chain: ch,
        side: String(side || "BUY").toUpperCase(),
        price: px,
        qty,
        source: src,
        origin_module: src === "ROTATION" ? "nexus_rotation" : src === "TRADING" ? "nexus_trading" : "nexus_grid",
        client_order_id: clientOrderId,
        payout_asset: normalizeStablePayoutAsset(manualPayoutAsset || "USDC", "USDC"),
        payoutAsset: normalizeStablePayoutAsset(manualPayoutAsset || "USDC", "USDC"),
        settlement_mode: "sell_existing_asset_on_rule",
        vault_exit_policy: "EXIT_TO_USDC_OR_USDT_ONLY",
        max_loss_pct: Math.max(0, Math.min(100, Number(manualMaxLossPct) || 0)),
        original_asset: String(gridItem || "").toUpperCase(),
        slippage_bps: Math.round(Math.max(0.1, Math.min(20, Number.isFinite(slippagePct) ? slippagePct : 1)) * 100),
        deadline_sec: Math.floor(Math.max(5, Math.min(120, Number.isFinite(deadlineMin) ? deadlineMin : 20)) * 60),
        nativePriceUsd: Number(activeGridNativeUsd || 0),
        funding_approved: !!fundingApproved,
        funding_source_asset: String(fundingSourceAsset || "").toUpperCase(),
        ...meta,
      };

      const r = await api("/api/grid/manual/add", { method: "POST", token, wallet: walletAddress, body });
      const savedOrder = r?.order || null;
      setGridChain(ch);
      setGridItem(sym);
      if (savedOrder) {
        const normalized = normalizeGridOrders([{ ...savedOrder, item: itemId, item_id: itemId, chain: ch }]);
        setGridOrders((prev) => {
          const keep = (prev || []).filter((o) => String(o?.id || o?.order_id || "") !== String(savedOrder.id || savedOrder.order_id || ""));
          return [...keep, ...normalized];
        });
      }
      applyGridMetaResponse(r, itemId);
      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
      setTimeout(() => { try { fetchGridOrders({ force: true }); } catch (_) {} }, 800);
      setErrorMsg(`${src} order added: ${String(side || "BUY").toUpperCase()} ${sym} with ${fmtUsd(usd)} budget.`);
    } catch (e) {
      setErrorMsg(`${src} add order: ${e?.message || e}`);
    } finally {
      setGridBusy((st) => ({ ...st, add: false }));
    }
  }, [
    token,
    walletAddress,
    gridBusy.add,
    activeGridChainKey,
    activeGridNativeUsd,
    getNexusOrderPriceUsd,
    resolveFundingBeforeOrder,
    rotationMaxSlippage,
    tradingMaxSlippagePct,
    manualSlippagePct,
    manualDeadlineMin,
    manualPayoutAsset,
    requirePro,
    api,
    normalizeGridOrders,
    applyGridMetaResponse,
    getGridVaultStatsFromResponse,
    fetchGridOrders,
    setGridChain,
    setGridItem,
    setGridOrders,
    setGridVaultStats,
    setErrorMsg,
  ]);

  const continueWithFundingSuggestion = useCallback(async (suggestion) => {
    const p = fundingPrompt?.pending;
    if (!p) return;
    const asset = String(suggestion?.asset || fundingPrompt?.suggestions?.[0]?.asset || "").toUpperCase();
    setFundingPrompt(null);
    if (p.kind === "MANUAL") {
      await addManualOrder({ fundingApproved: true, fundingSourceAsset: asset });
      return;
    }
    await addCoreOrderFromModule({
      source: p.source,
      chain: p.chain,
      symbol: p.symbol,
      side: p.side,
      budgetUsd: p.budgetUsd,
      priceUsd: p.priceUsd,
      meta: p.meta || {},
      fundingApproved: true,
      fundingSourceAsset: asset,
    });
  }, [fundingPrompt, addCoreOrderFromModule]);

  const renderFundingPrompt = useCallback((moduleName = "GRID") => {
    const fp = fundingPrompt;
    if (!fp) return null;
    const src = String(fp.source || "").toUpperCase();
    const mod = String(moduleName || "").toUpperCase();
    if (src && mod && src !== mod) return null;
    const suggestions = Array.isArray(fp.suggestions) ? fp.suggestions : [];
    return (
      <div
        style={{
          padding: "9px 10px",
          borderRadius: 12,
          border: "1px solid rgba(245,193,108,.34)",
          background: "rgba(245,193,108,.08)",
          display: "grid",
          gap: 8,
          marginTop: 8,
        }}
      >
        <div style={{ color: "#f5c16c", fontWeight: 900, fontSize: 13 }}>
          Funding needed · {fp.symbol} / {fp.chain}
        </div>
        <div className="muted tiny">
          {fp.message || "Not enough direct asset available."} {fp.shortageUsd > 0 ? `Shortage about ${fmtUsd(fp.shortageUsd)}.` : ""}
        </div>
        {suggestions.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {suggestions.map((sug, i) => (
              <button key={`${sug.asset || "asset"}-${i}`} type="button" className="miniBtn" onClick={() => continueWithFundingSuggestion(sug)}>
                Use {String(sug.asset || "asset").toUpperCase()}
              </button>
            ))}
            <button type="button" className="miniBtn" onClick={() => setFundingPrompt(null)}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="miniBtn" onClick={() => setFundingPrompt(null)}>Close</button>
          </div>
        )}
      </div>
    );
  }, [fundingPrompt, continueWithFundingSuggestion]);

  const getRotationPreflight = useCallback(() => {
    const activeRotationSession = (Array.isArray(rotationSessions) ? rotationSessions : []).find((s) => String(s?.id || "") === String(activeRotationSessionId || "")) || (Array.isArray(rotationSessions) ? rotationSessions : [])[0] || null;
    const typedAmount = Number(String(rotationBudgetRelease || "").replace(",", "."));
    const amount = Number.isFinite(typedAmount) && typedAmount > 0 ? typedAmount : Number(activeRotationSession?.budgetUsd || 0);
    const fallbackChain = String(activeRotationSession?.chain || activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
    const native = fallbackChain === "BNB" ? "BNB" : fallbackChain === "ETH" ? "ETH" : "POL";

    const pick = rotationSelectedPick && typeof rotationSelectedPick === "object" ? rotationSelectedPick : {};
    const candidate = Array.isArray(strategistRotationCandidates)
      ? strategistRotationCandidates.find((c) => String(c?.rank || "").toUpperCase() !== "AVOID")
      : null;

    const sourceSymbol = String(
      pick.coin ||
      pick.source ||
      candidate?.sym ||
      candidate?.symbol ||
      ""
    ).toUpperCase().trim();

    const coinsByChain = gridWalletCoinsByChain || {};
    const hasCoinOnChain = (chain, coin) => {
      const list = coinsByChain?.[String(chain || "").toUpperCase()] || [];
      return list.map((x) => String(x || "").toUpperCase()).includes(String(coin || "").toUpperCase());
    };
    const chooseFirstAvailable = (options) => {
      for (const opt of options) {
        if (hasCoinOnChain(opt.chain, opt.coin)) return opt;
      }
      return null;
    };

    let resolvedChain = String(pick.chain || activeRotationSession?.chain || "").toUpperCase();
    let resolvedSymbol = String(pick.coin || activeRotationSession?.symbol || "").toUpperCase();

    if (!resolvedSymbol && sourceSymbol === "BTC") {
      const resolved = chooseFirstAvailable([
        { chain: "ETH", coin: "WBTC" },
        { chain: "BNB", coin: "BTCB" },
        { chain: "BNB", coin: "WBTC" },
        { chain: "POL", coin: "WBTC" },
      ]);
      resolvedChain = resolved?.chain || resolvedChain;
      resolvedSymbol = resolved?.coin || "WBTC";
    } else if (!resolvedSymbol && sourceSymbol === "SOL") {
      const resolved = chooseFirstAvailable([
        { chain: "ETH", coin: "WSOL" },
        { chain: "BNB", coin: "WSOL" },
        { chain: "POL", coin: "WSOL" },
      ]);
      resolvedChain = resolved?.chain || resolvedChain;
      resolvedSymbol = resolved?.coin || "WSOL";
    }

    if (!resolvedSymbol) resolvedSymbol = sourceSymbol;
    if (!resolvedChain) {
      for (const chain of Object.keys(coinsByChain || {}).map((x) => String(x || "").toUpperCase())) {
        if (hasCoinOnChain(chain, resolvedSymbol)) {
          resolvedChain = chain;
          break;
        }
      }
    }
    // Recommendations are optional. The session can start without a current target;
    // NKR will scan the allowed watchlist and keep capital stable until a route qualifies.
    if (!resolvedChain) resolvedChain = String(
      rotationNetworkScope !== "ALL" ? rotationNetworkScope : (activeRotationSession?.chain || activeGridChainKey || DEFAULT_CHAIN || "ETH")
    ).toUpperCase();

    const amountOk = Number.isFinite(amount) && amount > 0;
    const symbolOk = Boolean(resolvedSymbol);
    const hasRealTarget = Boolean(pick?.ok || candidate?.sym || candidate?.symbol || pick?.coin || pick?.source);
    return {
      ok: amountOk,
      amount,
      amountOk,
      chain: resolvedChain,
      symbol: resolvedSymbol,
      source: pick.source || candidate?.sym || candidate?.symbol || "",
      selected: Boolean(pick?.ok || candidate?.sym || candidate?.symbol),
      score: pick.score ?? candidate?.score,
      message: !amountOk
        ? "Enter a NKR budget first."
        : hasRealTarget
          ? `Ready: ${resolvedSymbol}${resolvedChain ? ` on ${resolvedChain}` : ""}.`
          : "NKR ready. Assets will be selected automatically from the allowed watchlist; capital remains in USDC/USDT until a valid opportunity is found.",
    };
  }, [rotationBudgetRelease, activeGridChainKey, rotationSelectedPick, strategistRotationCandidates, gridWalletCoinsByChain, rotationSessions, activeRotationSessionId, rotationNetworkScope]);

  const addRotationOrder = useCallback(async () => {
    const pick = rotationSelectedPick || {};
    const preflight = getRotationPreflight();
    if (!preflight.ok) {
      setRotationBackendMsg(preflight.message || "NKR preflight is not ready.");
      return;
    }
    const chain = preflight.chain;
    const symbol = preflight.symbol;
    await addCoreOrderFromModule({
      source: "ROTATION",
      chain,
      symbol,
      side: "BUY",
      budgetUsd: preflight.amount,
      priceUsd: getNexusOrderPriceUsd(symbol),
      meta: {
        strategy_id: `rotation_${chain}_${symbol}`,
        rotation_score: pick.score ?? preflight.score,
        rotation_mode: rotationMode,
        risk_limit_pct: rotationRiskLimit,
        min_net_advantage_pct: rotationMinNetAdvantage,
        session_id: activeRotationSessionId || makeNexusSessionId("ROT"),
        rotation_session_id: activeRotationSessionId || "",
        preflight_fallback_used: false,
        preflight_source: preflight.source,
      },
    });
  }, [rotationSelectedPick, rotationBudgetRelease, getRotationPreflight, getNexusOrderPriceUsd, rotationMode, rotationRiskLimit, rotationMinNetAdvantage, activeRotationSessionId, makeNexusSessionId, addCoreOrderFromModule]);

  const addTradingOrder = useCallback(async () => {
    const chains = String(tradingAllowedChains || activeGridChainKey || DEFAULT_CHAIN || "POL").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
    const assets = String(tradingAllowedAssets || gridItem || chains[0] || "POL").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
    const chain = chains[0] || String(activeGridChainKey || DEFAULT_CHAIN || "POL").toUpperCase();
    const symbol = assets[0] || chain;
    await addCoreOrderFromModule({
      source: "TRADING",
      chain,
      symbol,
      side: "BUY",
      budgetUsd: tradingBudgetUsd,
      priceUsd: getNexusOrderPriceUsd(symbol),
      meta: {
        strategy_id: `trading_${chain}_${symbol}_${String(tradingStyle || "TACTICAL").toLowerCase()}`,
        session_id: activeTradingSessionId || makeNexusSessionId("TRDORDER"),
        trade_session_id: activeTradingSessionId || "",
        trading_style: tradingStyle,
        trading_runtime_hours: normalizeTradingRuntimeHours(),
        trading_runtime_unit: tradingRuntimeUnit,
        trading_hold_hours: clampTradingHoldHours(tradingHoldHours),
        trading_observe_max_hours: 12,
        trading_risk_mode: tradingRiskMode,
        caution_drawdown_pct: tradingCautionDrawdownPct,
        hard_stop_pct: tradingHardStopPct,
        profit_lock_pct: tradingProfitLockPct,
        max_trades: tradingMaxTrades,
        budget_splits: parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd),
        execution_queue: tradingExecutionQueue,
      },
    });
  }, [tradingAllowedChains, tradingAllowedAssets, activeGridChainKey, gridItem, tradingBudgetUsd, tradingBudgetSplitInput, tradingExecutionQueue, parseTradingBudgetSplits, getNexusOrderPriceUsd, tradingStyle, tradingRuntimeHours, tradingRuntimeUnit, normalizeTradingRuntimeHours, tradingHoldHours, clampTradingHoldHours, tradingRiskMode, tradingCautionDrawdownPct, tradingHardStopPct, tradingProfitLockPct, tradingMaxTrades, activeTradingSessionId, makeNexusSessionId, addCoreOrderFromModule]);

  async function addManualOrder(opts = {}) {
    setErrorMsg("");
    if (!token) return setErrorMsg("");
    if (!requirePro("Placing a new order")) return;
    if (!gridItemId) return setErrorMsg('Select coin first.');
    if (gridBusy.add) return;
    if (!isGridReady) {
      setErrorMsg("Grid not ready yet (connect wallet + select coin).");
      return;
    }
    setGridBusy((s) => ({ ...s, add: true }));
try {
      const price = Number(manualPrice);
      if (!Number.isFinite(price) || price <= 0) throw new Error("Invalid price.");

      const slp = Math.min(20, Math.max(0.1, Number(manualSlippagePct) || 5));
      const dlm = Math.min(120, Math.max(5, Number(manualDeadlineMin) || 20));
      const deadlineSec = Math.floor(dlm * 60);
      const gridPayout = normalizeStablePayoutAsset(manualPayoutAsset || "USDC", "USDC");
      const chainKey = String(activeGridChainKey || DEFAULT_CHAIN).toUpperCase();
      // BUILD387: ensure GRID CoreVault session exists with USDC/USDT settlement before the order.
      // Order alone is not enough for on-chain sell — session must be bound correctly from the start.
      let gridSessionId = "";
      try {
        const qtyEst = Number(manualQty || 0);
        const budgetUsd = Math.max(1, (Number.isFinite(price) && Number.isFinite(qtyEst) && qtyEst > 0) ? price * qtyEst : qtyEst || 1);
        const created = await createCoreVaultSystemSession({
          system: "GRID",
          budgetUsd,
          durationHours: 24,
          maxSlippageBps: Math.round(slp * 100),
          maxLossBps: Math.max(1, Math.round((Number(manualMaxLossPct) || 15) * 100)),
          chain: chainKey,
          settlementAsset: gridPayout,
        });
        gridSessionId = String(created?.sessionId || created?.onchainSessionId || created?.onchain_session_id || "");
      } catch (sessErr) {
        // If a GRID session already exists, backend may reject duplicate — still allow order with warning.
        const msg = String(sessErr?.message || sessErr || "");
        if (!/already|exists|active session|limit/i.test(msg)) {
          throw new Error(`Grid Vault session required: ${msg}`);
        }
      }
      const body = {
        item: gridItemId,
        addr: walletAddress || undefined,
        wallet: walletAddress || undefined,
        side: "SELL",
        price,
        chain: chainKey,
        payout_asset: gridPayout,
        payoutAsset: gridPayout,
        settlement_mode: "swap_on_fill_hold_until_withdraw",
        vault_exit_policy: "EXIT_TO_USDC_OR_USDT_ONLY",
        max_loss_pct: Math.max(0, Math.min(100, Number(manualMaxLossPct) || 0)),
        original_asset: String(gridItem || "").toUpperCase(),
        session_id: gridSessionId || undefined,
        core_vault_session_id: gridSessionId || undefined,
        slippage_bps: Math.round(slp * 100),
        deadline_sec: deadlineSec,
        source: "GRID",
        origin_module: "nexus_grid",
        client_order_id: `grid_${String(activeGridChainKey || DEFAULT_CHAIN).toLowerCase()}_${String(gridItem || "asset").toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      };

      
// Qty-only: all coins/tokens are entered as quantity (also USDC/USDT)
const qty = manualQty === "" ? undefined : Number(manualQty);
if (qty === undefined || !Number.isFinite(qty) || qty <= 0) throw new Error("Invalid Qty amount.");
body.qty = qty;
body.nativePriceUsd = Number(activeGridNativeUsd || 0);
body.funding_approved = !!opts.fundingApproved;
body.funding_source_asset = String(opts.fundingSourceAsset || "").toUpperCase();

// Grid never funds a purchase. It can only reserve an already-held token quantity.
if (qty > Number(manualVaultAvailableQty || 0)) {
  throw new Error(`Not enough ${String(gridItem || "token").toUpperCase()} available in the Vault.`);
}

	// Canonical endpoint is /api/grid/manual/add.
	// If the backend is on an older deployed revision, fall back to known aliases.
	      const endpoints = ["/api/grid/manual/add", "/api/grid/add", "/api/grid/order/add", "/api/add"]; 
	      let r = null;
	      let lastErr = null;
	      for (const ep of endpoints) {
	        try {
	          r = await api(ep, { method: "POST", token, body, wallet: walletAddress });
	          lastErr = null;
	          break;
	        } catch (err) {
	          lastErr = err;
	          const m = String(err?.message || "");
	          if (!m.includes("404") && !m.toLowerCase().includes("not found")) throw err;
	        }
	      }
	      if (lastErr) throw lastErr;
      
      applyGridMetaResponse(r, gridItemId);
      setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));

      // BUILD436: add response is item-scoped; keep all other chain sessions visible.
      // Always reload the wallet-wide list after the server commits the order.
      kickGridRefresh();
      setGridBusy((s) => ({ ...s, add: false }));
} catch (e) {
      setErrorMsg(`Grid start: ${e.message}`);
    
      setGridBusy((s) => ({ ...s, add: false }));}
  }
  
  
  async function stopGridOrder(orderId) {
    setErrorMsg("");
    if (!token) return setErrorMsg("");

    const _oid = String(orderId);
    if (gridBusy.stopOrderId === _oid) return;
    const orderObj = (Array.isArray(gridOrders) ? gridOrders : []).find((x) => String(idOf(x)) === _oid) || {};
    const orderItemRaw = String(orderObj?.item || orderObj?.item_id || "").trim();
    const orderChainRaw = String(orderObj?.chain || orderObj?.chainKey || orderObj?.network || (orderItemRaw.includes(":") ? orderItemRaw.split(":", 1)[0] : "")).toUpperCase();
    const chainKey = orderChainRaw || String(activeGridChainKey || balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
    const orderSymbol = String(orderObj?.symbol || orderObj?.asset || (orderItemRaw.includes(":") ? orderItemRaw.split(":").slice(1).join(":") : orderItemRaw) || gridItem || "").toUpperCase();
    const targetGridItemId = orderItemRaw || `${chainKey}:${orderSymbol}`;
    if (!targetGridItemId || !chainKey) {
      setErrorMsg("Grid order context missing.");
      return;
    }
    setGridBusy((s) => ({ ...s, stopOrderId: _oid }));
    patchGridOrderStatusLocal(_oid, "PAUSED");

    const sessId = String(orderObj?.session_id || orderObj?.core_vault_session_id || orderObj?.meta?.session_id || "").trim();

    // BUILD388: Pause = order PAUSED + on-chain setSessionPaused (like NKR/Trader).
    const addrPayload = walletAddress || undefined;
    const baseBody = {
      item: targetGridItemId, item_id: targetGridItemId, chain: chainKey,
      addr: addrPayload, wallet: addrPayload,
      session_id: sessId || undefined, core_vault_session_id: sessId || undefined,
    };
    const attempts = [
      { url: "/api/grid/order/pause", method: "POST", body: { ...baseBody, order_id: orderId, id: orderId, orderId } },
      { url: "/api/grid/order/stop", method: "POST", body: { ...baseBody, order_id: orderId, id: orderId, orderId } },
    ];

    let lastErr = null;
    for (const a of attempts) {
      try {
        const r = await api(a.url, { method: a.method, token, wallet: walletAddress, body: a.body });
        // BUILD436: response is scoped to this order's Grid session. Keep wallet-wide orders intact.
        if (targetGridItemId === String(gridItemId || "")) {
          setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
        }
        patchGridOrderStatusLocal(_oid, "PAUSED");
        if (r?.onchain_error) setErrorMsg(`Pause on-chain: ${r.onchain_error}`);
        setTimeout(() => { try { fetchGridOrders({ force: true }); } catch (_) {} }, 350);
        setGridBusy((s) => ({ ...s, stopOrderId: null }));
        return;
      } catch (e) {
        lastErr = e;
      }
    }

    patchGridOrderStatusLocal(_oid, "OPEN");
    setGridBusy((s) => ({ ...s, stopOrderId: null }));
    setErrorMsg(`Pause order: ${lastErr?.message || "failed"}`);
  }
  async function resumeGridOrder(orderId) {
    setErrorMsg("");
    if (!token) return setErrorMsg("");

    const _oid = String(orderId);
    if (gridBusy.stopOrderId === _oid) return;
    const orderObj = (Array.isArray(gridOrders) ? gridOrders : []).find((x) => String(idOf(x)) === _oid) || {};
    const orderItemRaw = String(orderObj?.item || orderObj?.item_id || "").trim();
    const orderChainRaw = String(orderObj?.chain || orderObj?.chainKey || orderObj?.network || (orderItemRaw.includes(":") ? orderItemRaw.split(":", 1)[0] : "")).toUpperCase();
    const chainKey = orderChainRaw || String(activeGridChainKey || balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
    const orderSymbol = String(orderObj?.symbol || orderObj?.asset || (orderItemRaw.includes(":") ? orderItemRaw.split(":").slice(1).join(":") : orderItemRaw) || gridItem || "").toUpperCase();
    const targetGridItemId = orderItemRaw || `${chainKey}:${orderSymbol}`;
    if (!targetGridItemId || !chainKey) {
      setErrorMsg("Grid order context missing.");
      return;
    }
    setGridBusy((s) => ({ ...s, stopOrderId: _oid }));
    patchGridOrderStatusLocal(_oid, "OPEN");

    const sessId = String(orderObj?.session_id || orderObj?.core_vault_session_id || orderObj?.meta?.session_id || "").trim();
    const addrPayload = walletAddress || undefined;
    const baseBody = {
      item: targetGridItemId, item_id: targetGridItemId, chain: chainKey,
      addr: addrPayload, wallet: addrPayload,
      session_id: sessId || undefined, core_vault_session_id: sessId || undefined,
    };
    const attempts = [
      { url: "/api/grid/order/resume", method: "POST", body: { ...baseBody, order_id: orderId, id: orderId, orderId } },
      { url: "/api/grid/order/start", method: "POST", body: { ...baseBody, order_id: orderId, id: orderId, orderId } },
      { url: "/api/grid/order/restart", method: "POST", body: { ...baseBody, order_id: orderId, id: orderId, orderId } },
    ];

    let lastErr = null;
    for (const a of attempts) {
      try {
        const r = await api(a.url, { method: a.method, token, wallet: walletAddress, body: a.body });
        // BUILD436: response is scoped to this order's Grid session. Keep wallet-wide orders intact.
        if (targetGridItemId === String(gridItemId || "")) {
          setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
        }
        patchGridOrderStatusLocal(_oid, "OPEN");
        if (r?.onchain_error) setErrorMsg(`Resume on-chain: ${r.onchain_error}`);
        setTimeout(() => { try { fetchGridOrders({ force: true }); } catch (_) {} }, 350);
        setGridBusy((s) => ({ ...s, stopOrderId: null }));
        return;
      } catch (e) {
        lastErr = e;
      }
    }

    patchGridOrderStatusLocal(_oid, "PAUSED");
    setGridBusy((s) => ({ ...s, stopOrderId: null }));
    setErrorMsg(`Resume order: ${lastErr?.message || "failed"}`);
  }

  async function deleteGridOrder(orderId) {
    // BUILD388: UI label is Stop — ends order + finalizes on-chain GRID session (like NKR/Trader Stop).
    setErrorMsg("");
    if (!token) return setErrorMsg("");

    const _oid = String(orderId);
    if (gridBusy.deleteOrderId === _oid) return;
    const orderObj = (Array.isArray(gridOrders) ? gridOrders : []).find((x) => String(idOf(x)) === _oid) || {};
    const orderItemRaw = String(orderObj?.item || orderObj?.item_id || "").trim();
    const orderChainRaw = String(orderObj?.chain || orderObj?.chainKey || orderObj?.network || (orderItemRaw.includes(":") ? orderItemRaw.split(":", 1)[0] : "")).toUpperCase();
    const chainKey = orderChainRaw || String(activeGridChainKey || balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase();
    const orderSymbol = String(orderObj?.symbol || orderObj?.asset || (orderItemRaw.includes(":") ? orderItemRaw.split(":").slice(1).join(":") : orderItemRaw) || gridItem || "").toUpperCase();
    const targetGridItemId = orderItemRaw || `${chainKey}:${orderSymbol}`;
    if (!targetGridItemId || !chainKey) {
      setErrorMsg("Grid order context missing.");
      return;
    }
    setGridBusy((s) => ({ ...s, deleteOrderId: _oid }));

    const sessId = String(orderObj?.session_id || orderObj?.core_vault_session_id || orderObj?.meta?.session_id || "").trim();

    const addrPayload = walletAddress || undefined;
    const body = {
      item: targetGridItemId, item_id: targetGridItemId, chain: chainKey,
      addr: addrPayload, wallet: addrPayload,
      order_id: orderId, id: orderId, orderId,
      session_id: sessId || undefined, core_vault_session_id: sessId || undefined,
    };
    const attempts = [
      { url: "/api/grid/order/delete", method: "POST", body },
      { url: "/api/grid/order/remove", method: "POST", body },
    ];

    let lastErr = null;
    for (const a of attempts) {
      try {
        const r = await api(a.url, { method: a.method, token, wallet: walletAddress, body: a.body });
        // BUILD436: remove only this order locally; the wallet-wide refresh reconciles final truth.
        setGridOrders((prev) => (Array.isArray(prev) ? prev : []).filter((o) => String(idOf(o)) !== _oid));
        if (targetGridItemId === String(gridItemId || "")) {
          setGridVaultStats((prev) => getGridVaultStatsFromResponse(r, prev));
        }
        kickGridRefresh();
        if (r?.onchain_error) setErrorMsg(`Stop finalize: ${r.onchain_error}`);
        else if (Array.isArray(r?.onchain_finalize) && r.onchain_finalize.some((x) => x && x.ok === false)) {
          setErrorMsg("Order stopped; session finalize may need System Info recovery.");
        }
        setGridBusy((s) => ({ ...s, deleteOrderId: null }));
        return;
      } catch (e) {
        lastErr = e;
      }
    }

    setGridBusy((s) => ({ ...s, deleteOrderId: null }));
    setErrorMsg(`Stop order: ${lastErr?.message || "failed"}`);
  }
// Duplicate slow fallback disabled: the main grid order-state refresh above already
// handles the no-open-orders case at a slower cadence.
useInterval(fetchGridOrders, 30000, false);

  const gridLiveFallback = useMemo(() => {
  const tgt = String(gridItem || "").toUpperCase();
  const rows = watchRows || [];

  const findPrice = (sym) => {
    const r = rows.find(
      (x) => String(x.symbol || "").toUpperCase() === String(sym || "").toUpperCase()
    );
    return r?.price != null ? r.price : null;
  };

  // 1) Direct match
  let p = findPrice(tgt);
  if (p != null) return p;

  // 2) Alias mapping for wrapped/native differences
  if (tgt === "POL") {
    p = findPrice("MATIC");
    if (p != null) return p;
    p = findPrice("WMATIC");
    if (p != null) return p;
  }

  if (tgt === "BNB") {
    p = findPrice("WBNB");
    if (p != null) return p;
  }

  if (tgt === "ETH") {
    p = findPrice("WETH");
    if (p != null) return p;
  }

  return null;
}, [watchRows, gridItem]);
  const shownGridPrice = gridLiveFallback ?? gridNativeUsd[String(gridItem || '').toUpperCase()] ?? gridMeta.price ?? null;

  const setManualPriceFromMarket = () => {
    if (!shownGridPrice || !Number.isFinite(Number(shownGridPrice))) return;
    setManualPrice(String(shownGridPrice));
  };

  const nudgeManualPricePct = (pct) => {
    const px = Number(shownGridPrice);
    if (!Number.isFinite(px) || px <= 0) return;
    const p = Number(pct);
    if (!Number.isFinite(p)) return;
    const next = px * (1 + p / 100);
    // Keep enough decimals for small coins; manual input can be adjusted by user.
    setManualPrice(next.toFixed(12));
  };



  const getLivePriceForSymbol = useCallback((sym) => {
    const S = String(sym || "").toUpperCase().trim();
    if (!S) return null;

    const rows = Array.isArray(watchRows) ? watchRows : [];
    const row = rows.find((x) => String(x?.symbol || "").toUpperCase().trim() === S);
    const pxRow = Number(row?.price);
    if (Number.isFinite(pxRow) && pxRow > 0) return pxRow;

    const pxNative = Number(gridNativeUsd?.[S]);
    if (Number.isFinite(pxNative) && pxNative > 0) return pxNative;

    if (S === String(gridItem || "").toUpperCase().trim()) {
      const pxShown = Number(shownGridPrice);
      if (Number.isFinite(pxShown) && pxShown > 0) return pxShown;
    }

    return null;
  }, [watchRows, gridNativeUsd, gridItem, shownGridPrice]);

  const parseSuggestedGridPct = useCallback((raw) => {
    const s = String(raw || "").replace(/–/g, "-");
    const nums = (s.match(/-?\d+(?:\.\d+)?/g) || []).map(Number).filter(Number.isFinite);
    if (!nums.length) return 2;
    if (nums.length === 1) return Math.abs(nums[0]);
    return Math.abs((nums[0] + nums[1]) / 2);
  }, []);

  const deriveAiGridAssist = useCallback((data) => {
    const d = data || {};
    const modeText = String(d.mode || d.gridMode || d.grid_mode || d.setup || d.verdict || "").toUpperCase();
    const riskText = String(d.risk || d.riskView || d.risk_view || "").toUpperCase();
    const gridText = String(d.gridRange || d.range || d.suggestedGrid || "").toUpperCase();
    const behaviorText = String(d.behavior || d.behaviorRead || d.strategy || d.action || "").toUpperCase();
    const pct = parseSuggestedGridPct(gridText || "2-4%");

    let preset = "STANDARD";
    if (modeText.includes("VERY") || gridText.includes("8") || pct >= 5) {
      preset = "VERY_WIDE";
    } else if (modeText.includes("WIDE") || pct >= 2.5 || behaviorText.includes("VOLATILITY")) {
      preset = "WIDE";
    } else if (modeText.includes("FAST") || pct <= 1) {
      preset = "FAST";
    }

    let slippage = 5;
    if (preset === "VERY_WIDE" || riskText.includes("HIGH")) slippage = 8;
    else if (preset === "WIDE" || riskText.includes("MEDIUM")) slippage = 6;
    else if (preset === "FAST") slippage = 3;

    const note = `AI Assist: ${preset.replace("_", " ")} / ${slippage}% slippage`;
    return { preset, slippage, note };
  }, [parseSuggestedGridPct]);

  const openGridPanel = useCallback(() => {
    setActivePanel("vault");
    setSelectedPair(null);

    setTimeout(() => {
      try {
        setActivePanel("vault");
        const el = document.querySelector(".section-grid");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } catch (_) {}
    }, 80);
  }, [setActivePanel, setSelectedPair]);

  const applyAiSuggestionToGrid = useCallback((sym, side) => {
    const S = String(sym || "").toUpperCase().trim();
    const SIDE = String(side || "BUY").toUpperCase() === "SELL" ? "SELL" : "BUY";
    if (!S) return;

    const nativeChain = ["POL", "BNB", "ETH"].includes(S) ? S : "";
    const availableNow = (gridWalletCoins || []).map((x) => String(x || "").toUpperCase()).includes(S);

    // Open the Grid even if the asset cannot be applied, so the user sees the context.
    openGridPanel();

    // Native assets require switching the Grid chain first.
    if (nativeChain) {
      setGridChain(nativeChain);
      setBalActiveChain(nativeChain);
      setWsChainKey(nativeChain);
    } else if (!availableNow) {
      setErrorMsg(`${S} is not available in your Grid wallet assets on the current chain. Add the token to Wallet/Grid first, then apply again.`);
      return;
    }

    setGridItem(S);
    setManualSide(SIDE);
    setManualBuyMode("QTY");

    // Level 4 Light: AI may prefill soft grid parameters, but it never creates orders.
    const aiAssist = deriveAiGridAssist(aiExplainData);
    setManualPricePreset(aiAssist.preset);
    setManualSlippagePct(String(aiAssist.slippage));
    setAiGridManualOverride(false);
    setAiGridAssistState({ active: true, preset: aiAssist.preset, slippage: aiAssist.slippage, note: aiAssist.note });

    const pct = parseSuggestedGridPct(aiExplainData?.gridRange || aiExplainData?.range || "2-4%");
    const px = getLivePriceForSymbol(S);
    if (Number.isFinite(px) && px > 0) {
      const target = SIDE === "BUY" ? px * (1 - pct / 100) : px * (1 + pct / 100);
      setManualPrice(target.toFixed(12).replace(/\.?0+$/, ""));
    } else {
      // Price may arrive after the chain/coin switch; keep empty rather than writing a fake value.
      setManualPrice("");
    }

    setErrorMsg(`Applied ${SIDE} ${S} to Grid. Enter Qty and press Add Order when you are ready.`);
  }, [
    aiExplainData,
    getLivePriceForSymbol,
    parseSuggestedGridPct,
    gridWalletCoins,
    openGridPanel,
    setGridChain,
    setBalActiveChain,
    setWsChainKey,
    setGridItem,
    setManualSide,
    setManualBuyMode,
    setManualPrice,
    setManualPricePreset,
    setManualSlippagePct,
    deriveAiGridAssist,
    setErrorMsg,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadStrategistIntel = async () => {
      try {
        const symbols = Array.from(new Set([
          ...(Array.isArray(rotationSessions) ? rotationSessions.map((s) => String(s?.symbol || s?.sourceSymbol || s?.targetAsset || "").toUpperCase()) : []),
          ...(Array.isArray(watchRows) ? watchRows.slice(0, 30).map((r) => String(r?.symbol || r?.sym || r?.asset || "").toUpperCase()) : []),
        ].filter(Boolean)));
        if (!symbols.length) return;
        const res = await api(`/api/nexus/binance-public/intelligence?symbols=${encodeURIComponent(symbols.join(","))}&tier=active`, { token, wallet });
        if (!cancelled) setStrategistMarketIntel(res || null);
        try {
          const globalRes = await api(`/api/nexus/global-strategist?symbols=${encodeURIComponent(symbols.slice(0, 18).join(","))}`, { token, wallet });
          if (!cancelled) setGlobalStrategistReport(globalRes || null);
        } catch {}
      } catch (e) {
        if (!cancelled) setStrategistMarketIntel({ status: "error", error: String(e?.message || e) });
      }
    };
    loadStrategistIntel();
    const timer = setInterval(loadStrategistIntel, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [token, wallet, rotationSessions, watchRows]);

  const extractStrategistSymbol = useCallback((body) => {
    const text = String(body || "");
    const known = Array.from(new Set([
      ...(compareSymbols || []),
      ...(watchRows || []).map((r) => r?.symbol || r?.sym),
      ...(gridWalletCoins || []),
      gridItem,
      "BTC",
      "ETH",
      "SOL",
      "BNB",
      "POL",
    ].map((x) => String(x || "").toUpperCase().trim()).filter(Boolean)));

    const upper = text.toUpperCase();
    const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\$&");
    const fromKnown = known.find((sym) => sym && new RegExp(`\\b${escapeRegExp(sym)}\\b`).test(upper));
    if (fromKnown) return fromKnown;

    const generic = upper.match(/\b[A-Z0-9]{2,10}\b/g) || [];
    const blocked = new Set(["NEXUS", "GRID", "ROTATION", "MARKET", "READ", "RISK", "RVOL", "EMA", "BTCUSD", "USDC", "USDT"]);
    return generic.find((sym) => !blocked.has(sym)) || "";
  }, [compareSymbols, watchRows, gridWalletCoins, gridItem]);

  const strategistAssetRoutes = useMemo(() => {
    const routes = [];
    const enabled = Array.isArray(ENABLED_CHAINS) && ENABLED_CHAINS.length ? ENABLED_CHAINS : ["POL", "BNB", "ETH"];
    const enabledSet = new Set(enabled.map((x) => String(x || "").toUpperCase()));

    const addRoute = ({ symbol, chain, contract = "", source = "vault" }) => {
      const sym = String(symbol || "").toUpperCase().trim();
      const ck = String(chain || "").toUpperCase().trim();
      if (!sym || !ck || !enabledSet.has(ck)) return;
      routes.push({
        symbol: sym,
        chain: ck,
        contract: String(contract || "").trim(),
        source,
        isNative: sym === ck || (sym === "MATIC" && ck === "POL"),
      });
    };

    for (const chain of enabledSet) {
      addRoute({ symbol: chain, chain, contract: "native", source: "native" });
    }

    const coinsByChain = gridWalletCoinsByChain || {};
    for (const chain of Object.keys(coinsByChain || {})) {
      for (const symbol of coinsByChain[chain] || []) {
        addRoute({ symbol, chain, source: "vault_asset" });
      }
    }

    for (const chain of Object.keys(balByChain || {})) {
      const row = balByChain?.[chain] || {};
      for (const t of row?.custom || []) {
        addRoute({
          symbol: t?.symbol,
          chain,
          contract: t?.address || t?.contract || t?.tokenAddress || "",
          source: "wallet_token",
        });
      }
      for (const stable of Object.keys(row?.stables || {})) {
        addRoute({ symbol: stable, chain, source: "stable" });
      }
    }

    for (const item of watchItems || []) {
      const symbol = item?.symbol || item?.sym;
      const chain = item?.chain || item?.chainKey || item?.network;
      const contract = item?.contract || item?.tokenAddress || item?.address || "";
      if (contract && chain) addRoute({ symbol, chain, contract, source: "watchlist_contract" });
    }

    const seen = new Set();
    return routes.filter((r) => {
      const key = `${r.symbol}|${r.chain}|${String(r.contract || "").toLowerCase()}|${r.source}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [gridWalletCoinsByChain, balByChain, watchItems]);

  const strategistExecutionGuard = useCallback((sym, target = "grid", targetChainOverride = "") => {
    const raw = String(sym || "").toUpperCase().trim();
    const targetMode = String(target || "grid").toLowerCase();
    const currentChain = String(targetChainOverride || activeGridChainKey || gridChain || DEFAULT_CHAIN || "POL").toUpperCase();

    if (!raw) {
      return { ok: false, normalized: "", warning: "No asset symbol found for Nexus execution." };
    }

    const routesFor = (symbol) => (strategistAssetRoutes || []).filter((r) => String(r?.symbol || "").toUpperCase() === String(symbol || "").toUpperCase());
    const currentChainRoute = (routes) => routes.find((r) => String(r.chain || "").toUpperCase() === currentChain);
    const routeLabel = (routes) => routes.map((r) => `${r.symbol} on ${r.chain}${r.contract && r.contract !== "native" ? " (contract)" : ""}`).join(", ");

    const nativeMap = {
      ETH: { chain: "ETH", coin: "ETH" },
      BNB: { chain: "BNB", coin: "BNB" },
      POL: { chain: "POL", coin: "POL" },
      MATIC: { chain: "POL", coin: "POL" },
    };

    if (nativeMap[raw]) {
      const n = nativeMap[raw];
      if (targetMode === "grid" && n.chain !== currentChain) {
        return {
          ok: false,
          normalized: n.coin,
          chain: n.chain,
          warning: `${raw} is native on ${n.chain}, but Grid is currently on ${currentChain}. Switch the Grid network first, then apply again.`,
        };
      }
      return { ok: true, normalized: n.coin, chain: n.chain, warning: "" };
    }

    const wrappedSymbols = {
      BTC: ["WBTC", "BTCB"],
      SOL: ["WSOL"],
    };

    if (wrappedSymbols[raw]) {
      const wrappedRoutes = wrappedSymbols[raw].flatMap((s) => routesFor(s));
      if (!wrappedRoutes.length) {
        const needed = raw === "BTC" ? "WBTC / BTCB" : "WSOL";
        return {
          ok: false,
          normalized: needed,
          warning: `${raw} native is not executable directly in the current EVM Vault setup. Please add/fund ${needed} first, then apply again.`,
        };
      }

      const route = targetMode === "grid" ? (currentChainRoute(wrappedRoutes) || wrappedRoutes[0]) : wrappedRoutes[0];
      if (targetMode === "grid" && route.chain !== currentChain) {
        return {
          ok: false,
          normalized: route.symbol,
          chain: route.chain,
          warning: `${raw} is available as ${route.symbol} on ${route.chain}, but Grid is currently on ${currentChain}. Switch the Grid network first, then apply again.`,
        };
      }

      return {
        ok: true,
        normalized: targetMode === "rotation" ? raw : route.symbol,
        chain: route.chain,
        route,
        warning: "",
      };
    }

    const routes = routesFor(raw);
    if (!routes.length) {
      return {
        ok: false,
        normalized: raw,
        warning: `${raw} can be used on supported EVM networks, but it is not currently available in your wallet/Vault. Please fund or add ${raw} on POL, BNB, or ETH first, then apply again.`,
      };
    }

    const route = targetMode === "grid" ? (currentChainRoute(routes) || routes[0]) : routes[0];
    if (targetMode === "grid" && route.chain !== currentChain) {
      return {
        ok: false,
        normalized: raw,
        chain: route.chain,
        route,
        warning: `${raw} exists on ${route.chain} (${routeLabel(routes)}), but Grid is currently on ${currentChain}. Switch the Grid network first, then apply again.`,
      };
    }

    return {
      ok: true,
      normalized: raw,
      chain: route.chain,
      route,
      warning: "",
    };
  }, [activeGridChainKey, gridChain, strategistAssetRoutes]);

  const deriveStrategistRiskPreset = useCallback((body) => {
    const text = String(body || "").toLowerCase();
    if (text.includes("high risk") || text.includes("elevated") || text.includes("weak liquidity") || text.includes("overextended")) {
      return { confidence: "Medium", riskLimit: "3", rotationSlippage: "1.5", minAdvantage: "0.8", gridSlippage: "6", preset: "WIDE" };
    }
    if (text.includes("low risk") || text.includes("stable") || text.includes("range") || text.includes("compression")) {
      return { confidence: "Medium-High", riskLimit: "2", rotationSlippage: "1", minAdvantage: "0.5", gridSlippage: "4", preset: "STANDARD" };
    }
    return { confidence: "Medium", riskLimit: "2.5", rotationSlippage: "1.2", minAdvantage: "0.6", gridSlippage: "5", preset: "STANDARD" };
  }, []);


  const deriveStrategistTraderSetup = useCallback((body, preparedSym = "", preset = {}) => {
    const raw = String(body || "");
    const cleanAsset = (value) => String(value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9:_,-]/g, "")
      .replace(/,+/g, ",")
      .replace(/^,|,$/g, "");

    const cleanChains = (value) => {
      const text = String(value || "").toUpperCase();
      const found = [];
      if (/\b(POL|POLYGON|MATIC)\b/.test(text)) found.push("POL");
      if (/\b(BNB|BSC|BINANCE)\b/.test(text)) found.push("BNB");
      if (/\b(ETH|ETHEREUM)\b/.test(text)) found.push("ETH");
      return Array.from(new Set(found)).join(",") || "POL,BNB,ETH";
    };

    const pickLineValue = (keys) => {
      const lines = raw.split(/\r?\n/);
      for (const line of lines) {
        const s = String(line || "").trim();
        for (const key of keys) {
          const rx = new RegExp(`${key}\\s*[:=\\-]\\s*(.+)$`, "i");
          const m = s.match(rx);
          if (m && m[1]) return m[1].trim().replace(/^[-•*]\s*/, "");
        }
      }
      return "";
    };

    const numberFrom = (value, fallback, { min = 0, max = 999, midpoint = false } = {}) => {
      const s = String(value || "");
      const range = s.match(/(-?\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(-?\d+(?:\.\d+)?)/i);
      let n = fallback;
      if (range && midpoint) n = (Number(range[1]) + Number(range[2])) / 2;
      else {
        const single = s.match(/(-?\d+(?:\.\d+)?)/);
        if (single) n = Number(single[1]);
      }
      if (!Number.isFinite(Number(n))) n = fallback;
      n = Math.max(min, Math.min(max, Number(n)));
      return `${Number.isInteger(n) ? n : Number(n.toFixed(2))}`;
    };

    const valueNumber = (keys, fallback, opts = {}) => numberFrom(pickLineValue(keys), fallback, opts);

    // Hidden Nexus Trading suitability engine.
    // This stays internal: it only shapes the prepared Trading/Risk values and does not add UI noise.
    const lowered = raw.toLowerCase();
    let suitabilityScore = 0;
    const addIf = (rx, delta) => { if (rx.test(lowered)) suitabilityScore += delta; };
    addIf(/\b(fake move|fake breakout|bull trap|bear trap|trap risk|weak breakout|failed breakout)\b/i, -3);
    addIf(/\b(weak liquidity|thin liquidity|low liquidity|illiquid|unstable liquidity)\b/i, -2);
    addIf(/\b(overextended|exhaustion|exhausted|blow[- ]?off|parabolic)\b/i, -2);
    addIf(/\b(low rvol|weak rvol|volume does not confirm|no volume confirmation|weak volume)\b/i, -2);
    addIf(/\b(high volatility|volatility spike|unstable momentum|choppy|uncertain|uncertainty)\b/i, -1);
    addIf(/\b(strong rvol|healthy rvol|volume[- ]?backed|volume confirmation|confirmed breakout)\b/i, 2);
    addIf(/\b(strong continuation|trend continuation|momentum continuation|stable trend|higher lows|clean structure)\b/i, 2);
    addIf(/\b(good liquidity|deep liquidity|stable liquidity)\b/i, 1);

    let suitability = "MEDIUM";
    const suitabilityRaw = pickLineValue(["Trading Suitability", "Nexus Trading Suitability", "Suitability"]);
    if (/\b(low|not recommended|avoid|poor)\b/i.test(suitabilityRaw)) suitability = "LOW";
    else if (/\b(high|strong|suitable)\b/i.test(suitabilityRaw)) suitability = "HIGH";
    else if (suitabilityScore <= -3) suitability = "LOW";
    else if (suitabilityScore >= 3) suitability = "HIGH";

    let riskMode = "BALANCED";
    const riskRaw = pickLineValue(["Recommended Risk Mode", "Risk Mode", "Risk"]);
    if (/\b(defensive|cautious|low risk|weak|fake|unstable|avoid)\b/i.test(riskRaw || raw)) riskMode = "DEFENSIVE";
    if (/\b(dynamic|aggressive|momentum|breakout|strong continuation|high confidence)\b/i.test(riskRaw || raw)) riskMode = "DYNAMIC";
    if (/\b(balanced|medium|normal)\b/i.test(riskRaw)) riskMode = "BALANCED";
    if (suitability === "LOW") riskMode = "DEFENSIVE";

    const styleRaw = pickLineValue(["Tactical Style", "Trading Style", "Style"]);
    let style = "TACTICAL";
    if (/\b(momentum|breakout|continuation)\b/i.test(styleRaw || raw)) style = "MOMENTUM";
    if (/\b(accumulation|accumulate)\b/i.test(styleRaw || raw)) style = "ACCUMULATION";
    if (/\b(range|grid|cycle)\b/i.test(styleRaw || raw)) style = "RANGE";
    if (/\b(rotation|relative strength)\b/i.test(styleRaw || raw)) style = "ROTATION";
    if (/\b(defensive|caution|protect)\b/i.test(styleRaw || raw)) style = "TACTICAL";

    const runtimeRaw = pickLineValue(["Runtime", "Runtime Suggestion", "Trading Runtime"]);
    let runtimeHours = numberFrom(runtimeRaw, riskMode === "DEFENSIVE" ? 6 : riskMode === "DYNAMIC" ? 24 : 12, { min: 1, max: 72, midpoint: true });
    if (/\bday|days\b/i.test(runtimeRaw)) runtimeHours = numberFrom(runtimeRaw, 1, { min: 1, max: 3, midpoint: true });
    if (/\bday|days\b/i.test(runtimeRaw)) runtimeHours = `${Math.min(72, Math.max(1, Number(runtimeHours) * 24))}`;

    let maxTrades = valueNumber(["Max Trades", "Max Trades Suggestion", "Trades"], riskMode === "DEFENSIVE" ? 2 : riskMode === "DYNAMIC" ? 8 : 4, { min: 1, max: 20, midpoint: true });
    let maxSlippagePct = valueNumber(["Max Slippage", "Max Slippage Suggestion", "Slippage"], riskMode === "DEFENSIVE" ? 0.7 : riskMode === "DYNAMIC" ? 1.5 : 1.0, { min: 0.1, max: 10, midpoint: true });

    const allowedAssetsRaw = pickLineValue(["Allowed Assets", "Assets"]);
    const allowedChainsRaw = pickLineValue(["Allowed Chains", "Chains"]);
    const assets = cleanAsset(allowedAssetsRaw) || cleanAsset(preparedSym) || "";
    const chains = cleanChains(allowedChainsRaw || raw);

    const confidenceRaw = pickLineValue(["AI Confidence", "Confidence"]);
    let confidence = "MEDIUM";
    if (/\b(high|medium-high|strong)\b/i.test(confidenceRaw || String(preset?.confidence || ""))) confidence = "HIGH";
    if (/\b(low|weak)\b/i.test(confidenceRaw || "")) confidence = "LOW";

    let cautionDrawdownPct = valueNumber(["Caution Drawdown", "Caution DD", "Drawdown"], riskMode === "DEFENSIVE" ? 2 : riskMode === "DYNAMIC" ? 4 : 3, { min: 1, max: 20, midpoint: true });
    let hardStopPct = valueNumber(["Hard Stop", "Stop"], riskMode === "DEFENSIVE" ? 8 : riskMode === "DYNAMIC" ? 15 : 12, { min: 2, max: 40, midpoint: true });
    let profitLockPct = valueNumber(["Profit Lock", "Lock"], riskMode === "DEFENSIVE" ? 10 : riskMode === "DYNAMIC" ? 25 : 20, { min: 1, max: 80, midpoint: true });

    // Background guardrails: keep UI clean, but make weak setups automatically safer.
    if (suitability === "LOW") {
      riskMode = "DEFENSIVE";
      runtimeHours = `${Math.min(Number(runtimeHours) || 6, 6)}`;
      maxTrades = `${Math.min(Number(maxTrades) || 2, 2)}`;
      maxSlippagePct = `${Math.min(Number(maxSlippagePct) || 0.7, 0.7)}`;
      cautionDrawdownPct = `${Math.min(Number(cautionDrawdownPct) || 2, 2)}`;
      hardStopPct = `${Math.min(Number(hardStopPct) || 8, 8)}`;
      profitLockPct = `${Math.min(Number(profitLockPct) || 10, 10)}`;
    } else if (suitability === "MEDIUM" && riskMode === "DYNAMIC") {
      riskMode = "BALANCED";
      maxTrades = `${Math.min(Number(maxTrades) || 4, 6)}`;
      maxSlippagePct = `${Math.min(Number(maxSlippagePct) || 1.0, 1.2)}`;
    }

    return {
      suitability,
      suitabilityScore,
      riskMode,
      style,
      runtimeHours,
      maxTrades,
      maxSlippagePct,
      cautionDrawdownPct,
      hardStopPct,
      profitLockPct,
      allowedAssets: assets,
      allowedChains: chains,
      confidence,
      schemaVersion: "strategist_trader_setup_v1",
      reason: pickLineValue(["Tactical Reason", "Reason"]) || "Strategist-derived controlled trading preparation.",
      invalidation: pickLineValue(["Invalidation", "Invalidation Conditions"]) || "Review market structure before approval.",
    };
  }, []);

  const applyStrategistToGrid = useCallback((body) => {
    const sym = extractStrategistSymbol(body);
    if (!sym) {
      setGridMode("normal");
      openGridPanel();
      setErrorMsg("No coin symbol found in the Nexus Grid card. Add a coin symbol to the task or card output first.");
      return;
    }

    // Always open Nexus Grid and prefill non-executing strategy fields.
    // Guard only blocks execution readiness, not visible preparation.
    setGridMode("normal");
    openGridPanel();

    const intendedGridChain = ["ETH", "BNB", "POL"].includes(sym)
      ? sym
      : String(activeGridChainKey || gridChain || DEFAULT_CHAIN || "POL").toUpperCase();

    const preset = deriveStrategistRiskPreset(body);
    setManualPricePreset(preset.preset);
    setManualSlippagePct(String(preset.gridSlippage));
    setAiGridAssistState({
      active: true,
      preset: preset.preset,
      slippage: preset.gridSlippage,
      note: `Strategist prepared ${preset.preset.replace("_", " ")} grid / ${preset.gridSlippage}% slippage`,
    });

    const guard = strategistExecutionGuard(sym, "grid", intendedGridChain);
    const preparedSym = guard.normalized && guard.ok ? guard.normalized : sym;

    if (!guard.ok) {
      setStrategistBridge({
        type: "blocked",
        sym: preparedSym,
        label: "Execution Guard",
        confidence: "Blocked",
        note: `${guard.warning} Grid strategy fields were still prepared; switch/add the correct EVM asset before adding any order.`,
        ts: Date.now(),
      });
      setErrorMsg(guard.warning);
      return;
    }

    setGridChain(intendedGridChain);

    setStrategistBridge({
      type: "grid",
      sym: preparedSym,
      label: "Nexus Grid",
      confidence: preset.confidence,
      note: `Prepared by Nexus Strategist. Preset ${preset.preset.replace("_", " ")} · slippage ${preset.gridSlippage}%. Review price, amount and risk before adding any order.`,
      ts: Date.now(),
    });
    applyAiSuggestionToGrid(preparedSym, "BUY");
    setErrorMsg(`Prepared ${preparedSym} in Nexus Grid. Review price, amount and risk before adding any order.`);
  }, [extractStrategistSymbol, strategistExecutionGuard, deriveStrategistRiskPreset, activeGridChainKey, gridChain, setGridMode, setGridChain, openGridPanel, setManualPricePreset, setManualSlippagePct, setAiGridAssistState, applyAiSuggestionToGrid, setErrorMsg]);

  const extractStrategistRotationCandidates = useCallback((body) => {
    const raw = String(body || "");
    const out = [];
    const seen = new Set();

    const parseNum = (value) => {
      const n = Number(String(value ?? "").replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    };

    const classifyCandidate = (spreadPct, line = "") => {
      const s = Number(spreadPct);
      const txt = String(line || "").toLowerCase();
      const saysHigh = /(confidence:\s*high|saubere rotation|clean rotation|best choice|beste wahl|strong edge)/i.test(txt);
      const saysMedium = /(confidence:\s*medium|secondary|zweite wahl|good secondary|schwache edge|weak edge)/i.test(txt);
      const saysAvoid = /(avoid|meiden|zu kleiner|too small|fake|spike|low confidence|confidence:\s*low|nicht sauber|not confirmed)/i.test(txt);

      if (saysAvoid || (Number.isFinite(s) && s < 0.5)) return "AVOID";
      if (saysHigh || (Number.isFinite(s) && s >= 2.0)) return "BEST";
      if (saysMedium || (Number.isFinite(s) && s >= 0.8)) return "SECONDARY";
      return "SECONDARY";
    };

    const rankWeight = (rank) => {
      if (rank === "BEST") return 300;
      if (rank === "SECONDARY") return 200;
      if (rank === "AVOID") return 50;
      return 100;
    };

    const add = (sym, meta = {}) => {
      const s = String(sym || "").toUpperCase().replace(/[^A-Z0-9]/g, "").trim();
      if (!s || s.length < 2 || s.length > 12) return;
      if (["USD", "USDT", "USDC", "EUR", "BTCB", "WBTC", "PRICE", "SPREAD", "EXCHANGE", "BUY", "SELL"].includes(s)) return;

      const spreadPct = parseNum(meta.spreadPct);
      const sourceLine = meta.sourceLine || "";
      const rank = meta.rank || classifyCandidate(spreadPct, sourceLine);
      const score = rankWeight(rank) + (Number.isFinite(spreadPct) ? Math.min(80, Math.max(0, spreadPct * 4)) : 0);

      const item = { sym: s, ...meta, spreadPct, rank, strategistScore: score };
      if (seen.has(s)) {
        const i = out.findIndex((x) => x.sym === s);
        if (i >= 0 && Number(out[i].strategistScore || 0) < score) out[i] = item;
        return;
      }
      seen.add(s);
      out.push(item);
    };

    // Prefer bullet/list lines like "- LINK: cheaper buy..." or "- ETH: günstig..."
    raw.split(/\r?\n/).forEach((line) => {
      const s = String(line || "").trim();
      const m = s.match(/^[\-•*]\s*\*\*?([A-Z0-9]{2,12})\*\*?\s*:/i)
        || s.match(/^[\-•*]\s*([A-Z0-9]{2,12})\s*:/i);
      if (m) {
        const spread = s.match(/(?:Netto-Edge|Net edge|Differenz|difference|Spread|Premium)\s*(?:ca\.?|about|~)?\s*([+-]?\d+(?:[.,]\d+)?)\s*%/i);
        let rank = "";
        if (/(best choice|beste wahl|saubere rotation|clean rotation|confidence:\s*high)/i.test(s)) rank = "BEST";
        else if (/(avoid|meiden|zu kleiner|too small|fake|spike|confidence:\s*low)/i.test(s)) rank = "AVOID";
        else if (/(secondary|zweite wahl|weak edge|schwache edge|confidence:\s*medium)/i.test(s)) rank = "SECONDARY";
        add(m[1], { sourceLine: s, spreadPct: spread ? spread[1] : undefined, rank });
      }
    });

    // Fallback: extract bold symbols.
    if (!out.length) {
      const rx = /\*\*([A-Z0-9]{2,12})\*\*/g;
      let m;
      while ((m = rx.exec(raw))) add(m[1]);
    }

    // Last fallback: old single-symbol extractor.
    if (!out.length) {
      const one = extractStrategistSymbol(raw);
      if (one) add(one, { rank: "SECONDARY" });
    }

    return out
      .sort((a, b) => Number(b.strategistScore || 0) - Number(a.strategistScore || 0))
      .slice(0, 8);
  }, [extractStrategistSymbol]);


  const applyStrategistToRotation = useCallback((body) => {
    const candidates = extractStrategistRotationCandidates(body);
    const sym = candidates[0]?.sym || "";
    if (!sym) {
      setGridMode("rotation");
      openGridPanel();
      setStrategistRotationCandidates([]);
      setErrorMsg("No coin symbol found in the NKR card. Add a coin symbol to the task or card output first.");
      return;
    }

    setStrategistRotationCandidates(candidates);

    // Always open NKR and always prefill the non-executing strategy fields.
    // Guard only blocks execution readiness, not visible preparation.
    setGridMode("rotation");
    openGridPanel();

    const preset = deriveStrategistRiskPreset(body);
    const guard = strategistExecutionGuard(sym, "rotation");
    const preparedSym = guard.normalized && guard.ok ? guard.normalized : sym;

    const normalizeRotationScope = (raw) => {
      const s = String(raw || "").toUpperCase().trim();
      if (!s) return "";
      if (/\b(ETH|ETHEREUM)\b/.test(s)) return "ETH";
      if (/\b(BNB|BSC|BINANCE)\b/.test(s)) return "BNB";
      if (/\b(POL|POLYGON|MATIC)\b/.test(s)) return "POL";
      return ["ETH", "BNB", "POL"].includes(s) ? s : "";
    };

    const pickLineValue = (keys) => {
      const lines = String(body || "").split(/\r?\n/);
      for (const line of lines) {
        const s = String(line || "").trim();
        for (const key of keys) {
          const rx = new RegExp(`${key}\\s*[:=\\-]\\s*(.+)$`, "i");
          const m = s.match(rx);
          if (m && m[1]) return m[1].trim().replace(/^[-•*]\s*/, "");
        }
      }
      return "";
    };

    const scopeFromOutput = normalizeRotationScope(pickLineValue(["Allowed Chains", "Network Scope", "Chains"]));
    const targetScope = normalizeRotationScope(guard.chain) || scopeFromOutput || normalizeRotationScope(preparedSym) || "ALL";
    const nextNetworkScope = targetScope === "ALL" ? "ALL" : targetScope;
    const nextMode = "RECOMMENDATION";
    const nextRiskLimit = preset.riskLimit;
    const nextMinAdvantage = preset.minAdvantage;
    const nextMaxSlippage = preset.rotationSlippage;

    const buildAppliedSetting = (label, value, previous, suffix = "") => ({
      label,
      value: `${value ?? ""}${suffix}`,
      previous: previous === undefined || previous === null || previous === "" ? "empty" : `${previous}${suffix}`,
      changed: String(value ?? "") !== String(previous ?? ""),
      source: "Strategist",
    });

    const appliedSettings = [
      buildAppliedSetting("Network Scope", nextNetworkScope, rotationNetworkScope),
      buildAppliedSetting("Mode", nextMode, rotationMode),
      buildAppliedSetting("Selected Asset", preparedSym || "—", rotationSelectedPick?.source || rotationSelectedPick?.coin || ""),
      buildAppliedSetting("Strategist Rank", candidates[0]?.rank || "SECONDARY", rotationSelectedPick?.rank || ""),
      buildAppliedSetting("Risk Limit", nextRiskLimit, rotationRiskLimit, "%"),
      buildAppliedSetting("Min Advantage", nextMinAdvantage, rotationMinNetAdvantage, "%"),
      buildAppliedSetting("Max Slippage", nextMaxSlippage, rotationMaxSlippage, "%"),
    ];

    setRotationNetworkScope(nextNetworkScope);
    setRotationMode(nextMode);
    setRotationRiskLimit(nextRiskLimit);
    setRotationMinNetAdvantage(nextMinAdvantage);
    setRotationMaxSlippage(nextMaxSlippage);
    setRotationBudgetRelease("");
    setRotationBudgetReleased(false);
    setStrategistAppliedOpen(false);

    // Mark/select the recommendation so the user sees what the Strategist tried to use.
    // If it is not executable, handleRotationPickToGrid will still show the selected recommendation / not available note.
    handleRotationPickToGrid({ sym: preparedSym });

    if (!guard.ok) {
      setStrategistBridge({
        type: "blocked",
        sym: preparedSym,
        label: "Execution Guard",
        confidence: "Blocked",
        note: `${guard.warning} Strategy fields were still prepared; add/select the correct EVM asset before budget release.`,
        appliedSettings,
        ts: Date.now(),
      });
      setErrorMsg(guard.warning);
      return;
    }

    setStrategistBridge({
      type: "rotation",
      sym: preparedSym,
      label: "Nexus NKR",
      confidence: preset.confidence,
      note: candidates.length > 1
        ? `Prepared ${preparedSym} first. ${candidates.length} Strategist candidates were detected: ${candidates.map((c) => c.sym).join(", ")}. Candidates are ranked by Strategist quality. Select another candidate in the Strategist candidates list if needed.`
        : "",
      candidates,
      appliedSettings,
      ts: Date.now(),
    });
    setErrorMsg(
      candidates.length > 1
        ? `Prepared ${preparedSym} first. ${candidates.length} Strategist candidates available: ${candidates.map((c) => c.sym).join(", ")}.`
        : `Prepared ${preparedSym} in NKR. Enter budget, review selection, then continue manually.`
    );
  }, [
    extractStrategistSymbol,
    extractStrategistRotationCandidates,
    strategistExecutionGuard,
    deriveStrategistRiskPreset,
    setGridMode,
    setRotationNetworkScope,
    setRotationMode,
    setRotationRiskLimit,
    setRotationMinNetAdvantage,
    setRotationMaxSlippage,
    setRotationBudgetRelease,
    setRotationBudgetReleased,
    openGridPanel,
    handleRotationPickToGrid,
    setErrorMsg,
    rotationNetworkScope,
    rotationMode,
    rotationSelectedPick,
    rotationRiskLimit,
    rotationMinNetAdvantage,
    rotationMaxSlippage,
  ]);

  const applyStrategistToTrading = useCallback((body) => {
    const sym = extractStrategistSymbol(body);

    setGridMode("trading");
    openGridPanel();

    const preset = deriveStrategistRiskPreset(body);
    const guard = sym ? strategistExecutionGuard(sym, "rotation") : { ok: false, normalized: "", warning: "No asset symbol found for Nexus Trading." };
    const preparedSym = (guard.ok && guard.normalized) ? guard.normalized : (sym || "");
    const traderSetup = deriveStrategistTraderSetup(body, preparedSym, preset);

    const setup = {
      id: `trading_${Date.now()}`,
      ts: Date.now(),
      source: "Nexus Strategist",
      schemaVersion: traderSetup.schemaVersion,
      symbol: preparedSym,
      requestedSymbol: sym || "",
      executable: !!guard.ok,
      guardWarning: guard.ok ? "" : guard.warning,
      suitability: traderSetup.suitability,
      confidence: traderSetup.confidence || preset.confidence,
      style: traderSetup.style || "TACTICAL",
      budgetUsd: tradingBudgetUsd || "",
      budgetSplits: parseTradingBudgetSplits(tradingBudgetSplitInput, tradingBudgetUsd),
      runtimeHours: traderSetup.runtimeHours || "24",
      allowedAssets: traderSetup.allowedAssets || preparedSym || "",
      allowedChains: traderSetup.allowedChains || "POL,BNB,ETH",
      riskMode: traderSetup.riskMode || "BALANCED",
      cautionDrawdownPct: traderSetup.cautionDrawdownPct || "3",
      hardStopPct: traderSetup.hardStopPct || "12",
      profitLockPct: traderSetup.profitLockPct || "20",
      maxSlippagePct: traderSetup.maxSlippagePct || "1.2",
      maxTrades: traderSetup.maxTrades || "6",
      confidenceMin: traderSetup.confidence || "MEDIUM",
      tacticalReason: traderSetup.reason || "",
      invalidation: traderSetup.invalidation || "",
      notes: String(body || "").slice(0, 900),
      executionQueue: [],
      session: {
        status: "PREPARED",
        approvedBudgetUsd: "",
        approvedAt: null,
        startedAt: null,
        pausedAt: null,
        stoppedAt: null,
        updatedTs: Date.now(),
      },
    };

    const buildAppliedSetting = (label, value, previous, suffix = "") => ({
      label,
      value: `${value ?? ""}${suffix}`,
      previous: previous === undefined || previous === null || previous === "" ? "empty" : `${previous}${suffix}`,
      changed: String(value ?? "") !== String(previous ?? ""),
      source: "Strategist",
    });

    setup.appliedSettings = [
      buildAppliedSetting("Risk Mode", setup.riskMode, tradingRiskMode),
      buildAppliedSetting("Budget Slots", setup.budgetSplits?.length ? setup.budgetSplits.join(" / ") : "auto", tradingBudgetSplitInput || "auto"),
      buildAppliedSetting("Runtime", setup.runtimeHours, tradingRuntimeHours, "h"),
      buildAppliedSetting("Style", setup.style, tradingStyle),
      buildAppliedSetting("Allowed Assets", setup.allowedAssets || "—", tradingAllowedAssets),
      buildAppliedSetting("Allowed Chains", setup.allowedChains, tradingAllowedChains),
      buildAppliedSetting("Caution DD", setup.cautionDrawdownPct, tradingCautionDrawdownPct, "%"),
      buildAppliedSetting("Hard Stop", setup.hardStopPct, tradingHardStopPct, "%"),
      buildAppliedSetting("Profit Lock", setup.profitLockPct, tradingProfitLockPct, "%"),
      buildAppliedSetting("Max Slippage", setup.maxSlippagePct, tradingMaxSlippagePct, "%"),
      buildAppliedSetting("Max Trades", setup.maxTrades, tradingMaxTrades),
    ];

    const appliedChangedCount = setup.appliedSettings.filter((item) => item?.changed).length;
    const appliedUnchangedCount = Math.max(0, setup.appliedSettings.length - appliedChangedCount);

    // Internal learning queue event.
    // This stays invisible in the UI for now, but preserves the Strategist -> Trading decision
    // so future Learning Memory / AI Insight can evaluate setup quality and later outcomes.
    setup.executionQueue = buildTradingQueue(setup, setup.budgetSplits);

    setup.learningEvent = {
      id: `learn_${setup.id}`,
      type: "STRATEGIST_TRADING_SETUP_PREPARED",
      schemaVersion: "trading_learning_event_v1",
      ts: setup.ts,
      source: "Nexus Strategist",
      setupId: setup.id,
      symbol: setup.symbol,
      requestedSymbol: setup.requestedSymbol,
      suitability: setup.suitability,
      confidence: setup.confidence,
      riskMode: setup.riskMode,
      style: setup.style,
      runtimeHours: setup.runtimeHours,
      maxTrades: setup.maxTrades,
      maxSlippagePct: setup.maxSlippagePct,
      budgetSplits: setup.budgetSplits,
      executionQueue: setup.executionQueue,
      cautionDrawdownPct: setup.cautionDrawdownPct,
      hardStopPct: setup.hardStopPct,
      profitLockPct: setup.profitLockPct,
      allowedAssets: setup.allowedAssets,
      allowedChains: setup.allowedChains,
      guard: {
        executable: setup.executable,
        warning: setup.guardWarning || "",
      },
      applied: {
        changedCount: appliedChangedCount,
        unchangedCount: appliedUnchangedCount,
        settings: setup.appliedSettings,
      },
      userAction: {
        approvedBudget: false,
        armed: false,
        started: false,
        paused: false,
        stopped: false,
      },
      outcome: {
        status: "pending",
        pnlUsd: null,
        drawdownPct: null,
        durationMinutes: null,
        notes: "",
      },
      context: {
        tacticalReason: setup.tacticalReason,
        invalidation: setup.invalidation,
        rawStrategistNotes: setup.notes,
        executionQueue: setup.executionQueue,
      },
    };

    setTradingPreparedSetup(setup);
    setTradingExecutionQueue(setup.executionQueue || []);
    setTradingAllowedAssets(setup.allowedAssets);
    setTradingAllowedChains(setup.allowedChains);
    setTradingRiskMode(setup.riskMode);
    setTradingRuntimeHours(setup.runtimeHours);
    setTradingCautionDrawdownPct(setup.cautionDrawdownPct);
    setTradingHardStopPct(setup.hardStopPct);
    setTradingProfitLockPct(setup.profitLockPct);
    setTradingMaxSlippagePct(setup.maxSlippagePct);
    setTradingMaxTrades(setup.maxTrades);
    setTradingConfidenceMin(setup.confidenceMin);
    setTradingStyle(setup.style);
    setTradingSessionStatus("PREPARED");
    setTradingSessionUpdatedTs(Date.now());

    setTradingLearningSetups((prev) => [setup, ...((Array.isArray(prev) ? prev : []).slice(0, 24))]);
    setStrategistAppliedOpen(false);

    setStrategistBridge({
      type: setup.executable ? "trading" : "blocked",
      sym: preparedSym || sym || "—",
      label: "Nexus Trading",
      confidence: setup.executable ? setup.confidence : "Blocked",
      note: setup.executable ? `` : `${guard.warning || "Trading setup could not be matched to a supported asset."}`,
      appliedSettings: setup.appliedSettings,
      executionQueue: setup.executionQueue,
      ts: Date.now(),
    });

    setErrorMsg("");
  }, [
    extractStrategistSymbol,
    strategistExecutionGuard,
    deriveStrategistRiskPreset,
    deriveStrategistTraderSetup,
    setGridMode,
    openGridPanel,
    tradingPreparedSetup,
    tradingBudgetUsd,
    tradingBudgetSplitInput,
    parseTradingBudgetSplits,
    buildTradingQueue,
    tradingRuntimeHours,
    tradingRiskMode,
    tradingStyle,
    tradingAllowedAssets,
    tradingAllowedChains,
    tradingCautionDrawdownPct,
    tradingHardStopPct,
    tradingProfitLockPct,
    tradingMaxSlippagePct,
    tradingMaxTrades,
    setTradingPreparedSetup,
    setTradingExecutionQueue,
    setTradingAllowedAssets,
    setTradingAllowedChains,
    setTradingRiskMode,
    setTradingRuntimeHours,
    setTradingCautionDrawdownPct,
    setTradingHardStopPct,
    setTradingProfitLockPct,
    setTradingMaxSlippagePct,
    setTradingMaxTrades,
    setTradingConfidenceMin,
    setTradingStyle,
    setTradingSessionStatus,
    setTradingSessionUpdatedTs,
    setTradingLearningSetups,
    setErrorMsg,
  ]);

  // Level 4 Light:
  // While a grid/order is running, AI can softly adapt only UI parameters
  // (price preset + slippage). It never edits, creates, cancels or moves orders.
  useEffect(() => {
    if (!hasOpenGridOrders) {
      aiGridAssistKeyRef.current = "";
      return;
    }
    if (aiGridManualOverride) return;
    if (!aiExplainData) return;
  
    const rec = deriveAiGridAssist(aiExplainData);
    const key = [
      String(aiExplainData?.pair || aiExplainData?.winner || ""),
      String(aiExplainData?.gridRange || aiExplainData?.range || ""),
      String(aiExplainData?.mode || ""),
      String(aiExplainData?.risk || ""),
      rec.preset,
      rec.slippage,
    ].join("|");
  
    if (aiGridAssistKeyRef.current === key) return;
    aiGridAssistKeyRef.current = key;
  
    setManualPricePreset(rec.preset);
    setManualSlippagePct(String(rec.slippage));
    setAiGridAssistState({ active: true, preset: rec.preset, slippage: rec.slippage, note: rec.note });
  }, [hasOpenGridOrders, aiGridManualOverride, aiExplainData, deriveAiGridAssist]);

  const inferOrderChainKey = useCallback((o) => {
    const raw =
      o?.chain ||
      o?.chainKey ||
      o?.chain_key ||
      o?.network ||
      o?.vault_chain ||
      o?.vaultChain ||
      o?.item_chain ||
      o?.itemChain ||
      (typeof o?.item === "string" ? String(o.item).split(":")[0] : "") ||
      (typeof o?.gridItemId === "string" ? String(o.gridItemId).split(":")[0] : "") ||
      activeGridChainKey ||
      DEFAULT_CHAIN;
    const norm = String(raw || DEFAULT_CHAIN).toUpperCase().trim();
    return ["POL", "BNB", "ETH"].includes(norm)
      ? norm
      : String(activeGridChainKey || DEFAULT_CHAIN).toUpperCase();
  }, [activeGridChainKey]);

  const inferOrderPayoutAsset = useCallback((o) => {
    const raw =
      o?.payout_asset ||
      o?.payoutAsset ||
      o?.payout ||
      o?.settlement_asset ||
      o?.settlementAsset ||
      o?.settlement ||
      o?.asset_out ||
      o?.assetOut ||
      o?.quote_asset ||
      o?.quoteAsset ||
      o?.return_asset ||
      o?.returnAsset ||
      o?.meta?.payout_asset ||
      o?.meta?.payoutAsset ||
      o?.meta?.settlement_asset ||
      o?.meta?.settlementAsset ||
      o?.data?.payout_asset ||
      o?.data?.payoutAsset ||
      o?.data?.settlement_asset ||
      o?.data?.settlementAsset ||
      manualPayoutAsset ||
      "—";
    return String(raw || "—").toUpperCase();
  }, [manualPayoutAsset]);

  const inferOrderStatus = useCallback((o) => {
    const raw = String(o?.status || o?.state || "OPEN").toUpperCase();
    if (["OPEN", "ACTIVE", "RUNNING", "LIVE"].includes(raw)) return "OPEN";
    if (["STOPPED", "STOP", "PAUSED", "CANCELLED", "CANCELED", "CANCELLING", "PAUSING"].includes(raw)) return "PAUSED";
    if (["FILLED", "EXECUTED", "DONE", "COMPLETED", "SETTLED"].includes(raw)) return "FILLED";
    if (["FAILED", "ERROR", "REJECTED"].includes(raw)) return "FAILED";
    if (["DELETED", "REMOVED"].includes(raw)) return "DELETED";
    return raw || "OPEN";
  }, []);

  const orderNotionalUsd = useCallback((o) => {
    const px = Number(o?.price || o?.limit_price || 0);
    const qty = Number(o?.qty || o?.quantity || 0);
    if (Number.isFinite(px) && px > 0 && Number.isFinite(qty) && qty > 0) return px * qty;
    return 0;
  }, []);

  const openGridOrders = useMemo(() => {
    return (Array.isArray(gridOrders) ? gridOrders : []).filter((o) => inferOrderStatus(o) === "OPEN");
  }, [gridOrders, inferOrderStatus]);

  const visibleGridOrders = useMemo(() => {
    return (Array.isArray(gridOrders) ? gridOrders : []).filter((o) => inferOrderStatus(o) !== "DELETED");
  }, [gridOrders, inferOrderStatus]);

  const gridOrdersGroupedByChain = useMemo(() => {
    const map = {};
    for (const o of visibleGridOrders) {
      const ck = inferOrderChainKey(o);
      if (!map[ck]) map[ck] = [];
      map[ck].push(o);
    }
    const pref = ["POL", "BNB", "ETH"];
    return Object.entries(map).sort((a, b) => {
      const ai = pref.indexOf(a[0]);
      const bi = pref.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [visibleGridOrders, inferOrderChainKey]);

  const manualOrderNotionalUsd = useMemo(() => {
    const px = Number(manualPrice || shownGridPrice || 0);
    const qty = Number(manualQty || 0);
    if (Number.isFinite(px) && px > 0 && Number.isFinite(qty) && qty > 0) return px * qty;
    const usd = Number(manualUsd || 0);
    return Number.isFinite(usd) && usd > 0 ? usd : 0;
  }, [manualPrice, shownGridPrice, manualQty, manualUsd]);

  const manualOpenExposureUsd = useMemo(() => {
    const chain = String(activeGridChainKey || DEFAULT_CHAIN).toUpperCase();
    return openGridOrders
      .filter((o) => inferOrderChainKey(o) === chain)
      .reduce((sum, o) => sum + orderNotionalUsd(o), 0);
  }, [openGridOrders, inferOrderChainKey, orderNotionalUsd, activeGridChainKey]);

  const manualExposureAfterUsd = useMemo(() => {
    return manualOpenExposureUsd + manualOrderNotionalUsd;
  }, [manualOpenExposureUsd, manualOrderNotionalUsd]);

  const manualSettlementPreview = useMemo(() => {
    const payout = normalizeStablePayoutAsset(manualPayoutAsset || "USDC", "USDC");
    return `On target or max-loss -> Vault sell into ${payout} only (no token payout) -> hold until withdraw.`;
  }, [manualPayoutAsset]);


  const persistWatchOrder = useCallback(async (nextItems) => {
    const normalized = normalizeWatchItems(nextItems || []);
    markWatchDirty();
    setWatchItems(normalized);
    setWatchRows((prev) => {
      const prevMap = new Map((Array.isArray(prev) ? prev : []).map((row) => [_watchKeyFromRow(row), row]));
      const ordered = normalized.map((it) => {
        const key = _watchKeyFromItem(it);
        return prevMap.get(key) || {
          symbol: String(it?.symbol || "").toUpperCase(),
          mode: String(it?.mode || "market"),
          chain: it?.chain || undefined,
          contract: it?.contract || it?.tokenAddress || undefined,
          tokenAddress: it?.tokenAddress || it?.contract || undefined,
          coingecko_id: String(it?.coingecko_id || it?.id || ""),
          id: String(it?.coingecko_id || it?.id || ""),
          name: it?.name || String(it?.symbol || "").toUpperCase(),
          price: null,
          chg_24h: null,
          change24h: null,
          vol: null,
          volume24h: null,
          source: "pending",
        };
      });
      try { localStorage.setItem(LS_WATCH_ROWS_CACHE, JSON.stringify(ordered)); } catch {}
      return ordered;
    });
    if (wallet) {
      try {
        await saveWatchlistToServer(normalized);
        setWatchSyncedWallet(resolveWalletAddress(wallet) || "");
      } catch (_) {}
    }
    fetchWatchSnapshot(normalized, { force: true, user: false });
  }, [normalizeWatchItems, setWatchItems, wallet, saveWatchlistToServer, setWatchSyncedWallet, markWatchDirty]);

  const reorderWatchItems = useCallback(async (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const items = Array.isArray(watchItems) ? [...watchItems] : [];
    const fromIndex = items.findIndex((it) => _watchKeyFromItem(it) === fromKey);
    const toIndex = items.findIndex((it) => _watchKeyFromItem(it) === toKey);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    await persistWatchOrder(items);
  }, [watchItems, persistWatchOrder]);

  function handleWatchDragStart(item) {
    setWatchDragKey(_watchKeyFromRow(item) || "");
    setWatchDropKey("");
  }

  function handleWatchDragOver(e, item) {
    e.preventDefault();
    const key = _watchKeyFromRow(item) || "";
    if (key && key !== watchDropKey) setWatchDropKey(key);
  }

  async function handleWatchDrop(e, item) {
    e.preventDefault();
    const toKey = _watchKeyFromRow(item) || "";
    const fromKey = watchDragKey || "";
    setWatchDropKey("");
    setWatchDragKey("");
    await reorderWatchItems(fromKey, toKey);
  }

  function handleWatchDragEnd() {
    setWatchDragKey("");
    setWatchDropKey("");
  }

  // watchlist actions
  function toggleCompare(sym) {
    const S = String(sym || "").toUpperCase();
    if (!S) return;
    setCompareSet((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      const has = arr.includes(S);
      if (has) return arr.filter((x) => x !== S);
      if (arr.length >= 20) return arr;
      arr.push(S);
      return arr;
    });
  }

  const [addOpen, setAddOpen] = useState(false);

// Add-Coin modal (old-app style): Market (CoinGecko search) + DEX (Contract)
const [addTab, setAddTab] = useState("market"); // "market" | "dex"
const [addQuery, setAddQuery] = useState("");
const [addSearching, setAddSearching] = useState(false);
const [addResults, setAddResults] = useState([]); // [{id,symbol,name,market_cap_rank}]
const [addSearchErr, setAddSearchErr] = useState("");

const addSearchAbortRef = useRef(null);

// Search is triggered only when user presses Search (or Enter).

// DEX tab inputs
const [addChain, setAddChain] = useState("polygon");
const [addContract, setAddContract] = useState("");

const resetAddModal = () => {
  setAddOpen(false);
  setAddTab("market");
  setAddQuery("");
  setAddSearching(false);
  setAddResults([]);
  setAddSearchErr("");
  setAddChain("eth");
  setAddContract("");
};

const runMarketSearch = async (opts = {}) => {
  const q = String(addQuery || "").trim();
  if (!q) return;
  const qKey = q.toLowerCase();

  // Abort previous in-flight search (keeps UI responsive)
  if (addSearchAbortRef.current) { try { addSearchAbortRef.current.abort(); } catch {} }
  const ac = new AbortController();
  addSearchAbortRef.current = ac;

  setAddSearchErr("");

  // 1) Instant result from client cache (if present)
  const cached = _cgSearchGet(qKey);
  if (cached) {
    setAddResults(cached);
    if (!cached.length) setAddSearchErr("No results.");
    // revalidate in background (do not block UI)
    (async () => {
      try {
        const r = await apiSearchCoins(q, { signal: ac.signal });
        const list = Array.isArray(r) ? r : Array.isArray(r?.coins) ? r.coins : Array.isArray(r?.results) ? r.results : [];
        const norm = (list || [])
          .map((x) => ({
            id: String(x.id || x.coingecko_id || x.cg_id || "").trim(),
            symbol: String(x.symbol || "").trim(),
            name: String(x.name || "").trim(),
            market_cap_rank: x.market_cap_rank ?? x.rank ?? null,
          }))
          .filter((x) => x.id && x.symbol);
        _cgSearchPut(qKey, norm);
        // only update if query didn't change
        if (String(addQuery || "").trim().toLowerCase() === qKey) {
          setAddResults(norm);
          setAddSearchErr(norm.length ? "" : "No results.");
        }
      } catch {
        // ignore background refresh errors
      }
    })();
    return;
  }

  // 2) No cache => do a normal fetch, but show spinner
  setAddSearching(true);
  try {
    // If Render is sleeping or the backend hangs, do not keep the UI in
    // "Searching..." forever.
    const r = await apiSearchCoins(q, { signal: ac.signal });
    const list = Array.isArray(r) ? r : Array.isArray(r?.coins) ? r.coins : Array.isArray(r?.results) ? r.results : [];
    const norm = (list || [])
      .map((x) => ({
        id: String(x.id || x.coingecko_id || x.cg_id || "").trim(),
        symbol: String(x.symbol || "").trim(),
        name: String(x.name || "").trim(),
        market_cap_rank: x.market_cap_rank ?? x.rank ?? null,
      }))
      .filter((x) => x.id && x.symbol);

    _cgSearchPut(qKey, norm);
    setAddResults(norm);
    if (!norm.length) setAddSearchErr("No results.");
  } catch (e) {
    // If request was aborted (due to debounce / new query), ignore silently.
    if (e && (e.name === "AbortError" || String(e).includes("AbortError") || String(e).toLowerCase().includes("aborted"))) {
      return;
    }
    const msg = String(e?.message || e);
    if (msg.toLowerCase().includes("timeout") || msg.toLowerCase().includes("aborted")) {
      setAddSearchErr("Search timed out. Backend may be sleeping — try again.");
    } else {
      setAddSearchErr(msg);
    }
    setAddResults([]);
  } finally {
    setAddSearching(false);
  }
};

const addMarketCoin = async (coin) => {
  const sym = String(coin?.symbol || "").trim().toUpperCase();
  const cgId = String(coin?.id || "").trim();
  if (!sym || !cgId) return;

  const item = { symbol: sym, mode: "market", coingecko_id: cgId, name: coin?.name || "", rank: coin?.market_cap_rank ?? null };

  // If user previously removed this coin, clear its tombstone so it can appear again.
  _clearTombstone(_watchKeyFromItem(item));

  // Optimistic: update local state immediately (never wait for backend)
  const prevWatchItemsForAdd = normalizeWatchItems(watchItems || []);
  const addKey = `${item.mode}|${item.symbol}|${item.coingecko_id}`.toLowerCase();
  const addExists = prevWatchItemsForAdd.some((x) => {
    const xs = String(x?.symbol || "").trim().toUpperCase();
    const xm = String(x?.mode || "market").toLowerCase();
    const xid = String(x?.coingecko_id || x?.id || "").toLowerCase();
    return `${xm}|${xs}|${xid}`.toLowerCase() === addKey;
  });
  const nextItems = addExists ? prevWatchItemsForAdd : [...prevWatchItemsForAdd, item];
  markWatchDirty();
  setWatchItems(nextItems);

  // Ensure it shows instantly in the table even if snapshot is down (placeholder row).
  setWatchRows((prev0) => {
    const prev = Array.isArray(prev0) ? prev0 : [];
    const has = prev.some((r) => {
      const rs = String(r?.symbol || "").toUpperCase().trim();
      const rm = String(r?.mode || "market").toLowerCase();
      const rid = String(r?.coingecko_id || r?.id || "").toLowerCase();
      return `${rm}|${rs}|${rid}` === `market|${sym}|${String(cgId || "").toLowerCase()}`;
    });
    if (has) return prev;
    return [
      ...prev,
      {
        symbol: sym,
        mode: "market",
        coingecko_id: cgId,
        name: item.name || sym,
        price: null,
        chg_24h: null,
        vol: null,
        source: "pending",
      },
    ];
  });

  // IMPORTANT: adding a coin to Watchlist must NOT auto-check it for Compare.
  // If this symbol was still present in persisted/desktop compare state from an older session,
  // remove it explicitly so the checkbox stays empty until the user ticks it manually.
  setCompareSet((prev) => {
    const arr = Array.isArray(prev) ? prev : [];
    return arr.filter((x) => String(x || "").toUpperCase() !== sym);
  });

  // Kick a background snapshot refresh (best-effort). Never block the click.
  // Save first, then snapshot, and also try an exact-id direct price hydration for instant UX.
  try {
    setTimeout(async () => {
      try {
        await saveWatchlistToServer(nextItems || null);
        try { setWatchSyncedWallet(resolveWalletAddress(wallet) || ""); } catch {}
      } catch {}

      try {
        const qs = new URLSearchParams({
          ids: String(cgId || "").toLowerCase(),
          vs_currencies: "usd",
          include_24hr_change: "true",
          include_24hr_vol: "true",
          include_market_cap: "true",
        }).toString();
        const direct = await api(`/api/coingecko/simple_price?${qs}`, { method: "GET", token, wallet });
        const d = direct?.[String(cgId || "").toLowerCase()];
        const p = Number(d?.usd);
        if (Number.isFinite(p) && p > 0) {
          setWatchRows((prev0) => {
            const prev = Array.isArray(prev0) ? prev0 : [];
            const next = prev.map((r) => {
              const rs = String(r?.symbol || "").toUpperCase().trim();
              const rid = String(r?.coingecko_id || r?.id || "").toLowerCase();
              if (rs !== sym || rid !== String(cgId || "").toLowerCase()) return r;
              const ch24 = Number.isFinite(Number(d?.usd_24h_change)) ? Number(d.usd_24h_change) : (r?.change24h ?? r?.chg_24h ?? null);
              const v24 = Number.isFinite(Number(d?.usd_24h_vol)) ? Number(d.usd_24h_vol) : (r?.volume24h ?? r?.vol ?? null);
              const mc = Number.isFinite(Number(d?.usd_market_cap)) ? Number(d.usd_market_cap) : (r?.marketCap ?? r?.market_cap ?? null);
              return {
                ...r,
                price: p,
                change24h: ch24,
                chg_24h: ch24,
                volume24h: v24,
                vol: v24,
                marketCap: mc,
                market_cap: mc,
                source: "coingecko-id",
              };
            });
            try { localStorage.setItem(LS_WATCH_ROWS_CACHE, JSON.stringify(next)); } catch {}
            return next;
          });
        }
      } catch {}

      try {
        await fetchWatchSnapshot(nextItems || null, { force: true, user: true });
      } catch {}
    }, 0);
  } catch {}

  // keep modal open to allow adding multiple, but clear search to reduce confusion
  setAddQuery("");
  setAddResults([]);
  setAddSearchErr("");
};

const addDexToken = async () => {
  const contract = String(addContract || "").trim();
  const chain = String(addChain || "pol").trim();
  if (!contract) return setErrorMsg("Contract address required.");

  // We store contract in both "contract" (UI) and "tokenAddress" (backward compat for older backend)
  const item = { symbol: contract.slice(0, 10).toUpperCase(), mode: "dex", contract, tokenAddress: contract, chain };

  _clearTombstone(_watchKeyFromItem(item));

  const prevWatchItemsForDexAdd = normalizeWatchItems(watchItems || []);
  const dexKey = `${item.mode}|${item.contract}`.toLowerCase();
  const dexExists = prevWatchItemsForDexAdd.some((x) => `${String(x?.mode || "market").toLowerCase()}|${String(x?.contract || x?.tokenAddress || "").toLowerCase()}` === dexKey);
  const nextItems = dexExists ? prevWatchItemsForDexAdd : [...prevWatchItemsForDexAdd, item];
  markWatchDirty();
  setWatchItems(nextItems);

  // placeholder row so user sees it instantly
  setWatchRows((prev0) => {
    const prev = Array.isArray(prev0) ? prev0 : [];
    const addr = contract.toLowerCase();
    const has = prev.some((r) => String(r?.contract || r?.tokenAddress || "").toLowerCase() === addr);
    if (has) return prev;
    return [
      ...prev,
      {
        symbol: item.symbol,
        mode: "dex",
        contract,
        tokenAddress: contract,
        chain,
        name: item.symbol,
        price: null,
        chg_24h: null,
        vol: null,
        source: "pending",
      },
    ];
  });

  // IMPORTANT: adding a DEX token to Watchlist must NOT auto-check it for Compare.
  // Keep the checkbox empty until the user selects it manually.
  setCompareSet((prev) => {
    const arr = Array.isArray(prev) ? prev : [];
    return arr.filter((x) => String(x || "").toUpperCase() !== String(item.symbol || "").toUpperCase());
  });

  // Background snapshot refresh (best-effort)
  try {
    setTimeout(() => {
      fetchWatchSnapshot(nextItems || null, { force: true, user: true });
    }, 0);
  } catch {}

  try {
    setTimeout(async () => {
      await saveWatchlistToServer(nextItems || null);
      try { setWatchSyncedWallet(resolveWalletAddress(wallet) || ""); } catch {}
    }, 0);
  } catch {}

  // keep modal open; clear contract for next add
  setAddContract("");
};

  function removeWatchItemByKey({ symbol, mode = "market", tokenAddress = "", contract = "" }) {
  const sym = String(symbol || "").toUpperCase();
  const m = String(mode || "market").toLowerCase();
  const addr = String(tokenAddress || contract || "").toLowerCase();

// Tombstone: prevent backend snapshots from resurrecting removed items.
// Derive stable key from stored watchItems when possible.
const removedItem = (watchItems || []).find((x) => {
  const xs = String(x?.symbol || "").toUpperCase();
  const xm = String(x?.mode || "market").toLowerCase();
  const xa = String(x?.contract || x?.tokenAddress || "").toLowerCase();
  return xs === sym && xm === m && xa === addr;
});
const removedKey = removedItem
  ? _watchKeyFromItem(removedItem)
  : (m === "dex" ? `dex|${addr}` : `market|${sym}|`);
_setTombstone(removedKey);

  // Build next "items" array (the true source of truth we send to backend)
  const nextItems = (watchItems || []).filter((x) => {
    if (!x) return false;
    const xs = String(x.symbol || "").toUpperCase();
    const xm = String(x.mode || "market").toLowerCase();
    const xa = String(x.contract || x.tokenAddress || "").toLowerCase();
    const xid = String(x.coingecko_id || x.id || "").toLowerCase();
    if (m === "dex") return !(xs === sym && xm === m && xa === addr);
    return !(xs === sym && xm === m);
  });

  // Optimistic UI update (so it disappears immediately)
  markWatchDirty();
  setWatchItems(nextItems);
  setWatchRows((prev) =>
    (prev || []).filter((r) => {
      if (!r) return false;
      const rs = String(r.symbol || "").toUpperCase();
      const rm = String(r.mode || "market").toLowerCase();
      const ra = String(r.contract || r.tokenAddress || "").toLowerCase();
      if (m === "dex") return !(rs === sym && rm === m && ra === addr);
      return !(rs === sym && rm === m);
    })
  );

  // Keep compare selection consistent (avoid "ghost" selections)
  setCompareSet((prev) => {
    const p = Array.isArray(prev) ? prev : [];
    return p.filter((s) => String(s || "").toUpperCase() !== sym);
  });

  // If you removed the last watch item, also clear compare selection + chart cache
  if (!nextItems.length) {
    setCompareSet([]);
    setCompareSeries({});
    lastGoodCompareRef.current = {};
    try { localStorage.setItem(LS_COMPARE_SERIES_CACHE, JSON.stringify({})); } catch {}
  }

  // Persist: ask backend to recompute snapshot for the new items list
  // (This makes sure the item doesn't come back on next poll.)
  (async () => {
    try {
      try { await saveWatchlistToServer(nextItems); setWatchSyncedWallet(resolveWalletAddress(wallet) || ""); } catch (_) {}

      const wa = resolveWalletAddress(wallet);
      const snapPath = wa ? `/api/watchlist/snapshot?wallet=${encodeURIComponent(wa)}&wallet_address=${encodeURIComponent(wa)}&force=1&_=${Date.now()}` : `/api/watchlist/snapshot?force=1&_=${Date.now()}`;
      const data = await api(snapPath, {
        method: "POST",
        token,
        wallet: wa || wallet,
        body: { wallet: wa || wallet, wallet_address: wa || wallet, items: nextItems },
      });

      const nextRowsRaw = data?.results || data?.rows || [];
      const allowedKeys = new Set((nextItems || []).map(_watchKeyFromItem));
      const tomb = _loadTombstones();
      const nextRows = (Array.isArray(nextRowsRaw) ? nextRowsRaw : []).filter((row) => {
        const k = _watchKeyFromRow(row);
        if (tomb && tomb[k] != null) return false;
        return allowedKeys.has(k);
      });
      if (Array.isArray(nextRows)) setWatchRows(nextRows);
if (Array.isArray(data?.symbols)) setCompareSet((prev) => (Array.isArray(prev) && prev.length ? prev : data.symbols));
if (data?.cached != null) setWatchCached(Boolean(data.cached));
      setWatchErr("");
    } catch (e) {
      setWatchErr(String(e?.message || e));
    }
  })();
  }

  // AI
  const aiCompareContextModes = useMemo(() => new Set(["research", "daily_report"]), []);
  const aiUsesCompareContext = aiCompareContextModes.has(aiKind);
  
  const normalizeAiOutput = (rawText, tf, q, seriesStats) => {
    const TF = String(tf || "").toUpperCase();
    const is1D = TF === "1D" || TF === "24H" || TF === "24HRS" || TF === "1DAY";
    let text = String(rawText || "");

    if (is1D) return text;

    // Remove obvious 24h-only lines/metrics to avoid misleading output when timeframe != 1D
    const lines = text.split(/\r?\n/);
    const filtered = lines.filter((ln) => {
      const s = ln.toLowerCase();
      if (s.includes("24h") || s.includes("24 h") || s.includes("24-stunden") || s.includes("24 stunden")) return false;
      if (s.includes("price_change_percentage_24h") || s.includes("volume_24h") || s.includes("high_24h") || s.includes("low_24h")) return false;
      return true;
    });

    // Build a factual timeframe summary from chart series stats (same data as overlay)
    const entries = Object.entries(seriesStats || {});
    if (!entries.length) return filtered.join("\n");

    const isGerman = /\b(wie|was|warum|in|den|letzten|tage|wochen|markt|zukunft)\b/i.test(q || "");
    const headTitle = isGerman ? `Zeitraum-Stats (${TF})` : `Timeframe stats (${TF})`;

    const fmt = (n) => (n == null || !Number.isFinite(n) ? "—" : n.toFixed(2));
    const bullets = entries.map(([sym, stats]) => {
      const ch = stats?.changePct;
      const vol = stats?.volPct;
      const mdd = stats?.maxDDPct;
      const last = stats?.last;
      const first = stats?.first;
      if (isGerman) {
        return `- ${sym}: Start ${fmtUsd(first)}, Ende ${fmtUsd(last)}, Veränderung ${fmt(ch)}%, Volatilität ${fmt(vol)}%, MaxDD ${fmt(mdd)}%`;
      }
      return `- ${sym}: start ${fmtUsd(first)}, end ${fmtUsd(last)}, change ${fmt(ch)}%, vol ${fmt(vol)}%, maxDD ${fmt(mdd)}%`;
    });

    return `${headTitle}\n${bullets.join("\n")}\n\n${filtered.join("\n")}`.trim();
  };

function detectNexusUserLanguage(text = "") {
  const raw = String(text || "");
  const q = ` ${raw.toLowerCase()} `;
  if (/[А-Яа-яЁё]/.test(raw)) return "ru";
  if (/[أ-ي]/.test(raw)) return "ar";
  if (/[ぁ-んァ-ン一-龯]/.test(raw)) return "ja";
  if (/[가-힣]/.test(raw)) return "ko";
  const lexicons = {
    de: [" der ", " die ", " das ", " und ", " oder ", " welche", " welcher", " welches", "wieso", "warum", "kaufen", "verkaufen", "teurer", "günstig", "guenstig", "börse", "boerse", "bitte", "mache", "zeige", "suche", " wo ", " wie ", " ist ", " sind "],
    en: [" the ", " and ", " or ", " which", " what", " why", " buy", " sell", " cheaper", " expensive", " show", " find", " where ", " how ", " is ", " are "],
    fr: [" le ", " la ", " les ", " et ", " ou ", " quel", " quelle", " acheter", " vendre", " moins cher", " plus cher", " pourquoi", " comment"],
    es: [" el ", " la ", " los ", " las ", " y ", " o ", " cuál", " cual", " comprar", " vender", " barato", " caro", " donde", " dónde"],
    it: [" il ", " la ", " gli ", " le ", " e ", " o ", " quale", " comprare", " vendere", " economico", " caro", " dove"],
    pt: [" o ", " a ", " os ", " as ", " e ", " ou ", " qual", " comprar", " vender", " barato", " caro", " onde"],
    tr: [" ve ", " veya ", " hangi", " nerede", " almak", " satmak", " ucuz", " pahalı", " pahali"],
    nl: [" de ", " het ", " en ", " of ", " welke", " kopen", " verkopen", " goedkoper", " duurder", " waar"],
  };
  let best = "en";
  let bestScore = 0;
  Object.entries(lexicons).forEach(([lang, words]) => {
    const score = words.reduce((n, w) => n + (q.includes(w) ? 1 : 0), 0);
    if (score > bestScore) { best = lang; bestScore = score; }
  });
  return bestScore > 0 ? best : "en";
}

function detectNexusUserIntent(text = "") {
  const q = String(text || "").toLowerCase();
  // Strict query router: latest user wording decides the Strategist mode.
  if (/(günstig|guenstig|billig|teurer|verkaufen|arbitrage|spread|exchange|börse|boerse|preisunterschied|premium|discount|wo.*kaufen|wo.*verkaufen|wo.*besser|wo.*mehr wert|anderer preis|lohnt|different price|cheaper|buy cheaper|sell higher|where.*buy|where.*sell|where.*better|higher price|more expensive there|worth it)/i.test(q)) return "rotation_spread";
  // Coin / asset ranking questions must not fall back to a generic market overview.
  if (/(welche\s+coin|welche\s+coins|welcher\s+coin|welche\s+assets?|welche\s+token|best\s+coin|best\s+coins|which\s+coin|which\s+coins|which\s+assets?|top\s+coins?|gute\s+coins?|coins?\s+sind\s+(denn\s+)?gut|assets?\s+sind\s+(denn\s+)?gut|was\s+handeln|what\s+to\s+trade|was\s+ist\s+stark)/i.test(q)) return "rotation";
  if (/(rotation|rotieren|relative|stärke|staerke|weakness|strength|kapitalfluss|capital flow|outperform|underperform|welcher.*stärker|welcher.*staerker|which.*stronger|besserer coin|better coin|stärker als|staerker als|stronger than)/i.test(q)) return "rotation";
  if (/(grid|range|seitwärts|seitwaerts|sideways|levels|raster)/i.test(q)) return "grid";
  if (/(trading|autonom|runtime|slot|allocation|budget|position|vault|execute|execution)/i.test(q)) return "trading";
  if (/(risiko|risk|fake|manipul|gefährlich|gefaehrlich|danger|überhitzt|ueberhitzt|overheat|trap|liquidität|liquidity)/i.test(q)) return "risk";
  if (/(report|daily|täglich|taeglich|markt|market|overview|überblick|ueberblick)/i.test(q)) return "market";
  return "general";
}

/** Only Nexus / crypto / market topics are allowed in the Strategist chat. */
function isNexusCryptoScopeQuestion(text = "") {
  const q = String(text || "").trim().toLowerCase();
  if (!q) return false;
  // Explicit off-topic patterns
  if (/(wetter|rezept|kochen|fußball|fussball|politik(?!\s*crypto)|wahlkampf|medizin|arzt|liebe|beziehung|hausaufgaben|matheaufgabe|windows\s*install|iphone\s*reset)/i.test(q)
    && !/(crypto|bitcoin|eth|token|coin|defi|nft|blockchain|trading|markt)/i.test(q)) {
    return false;
  }
  // Allow if clearly crypto / Nexus / market / trading related
  if (/(nexus|nkr|grid|vault|watchlist|crypto|krypto|bitcoin|btc|ethereum|eth|bnb|sol|pol|matic|token|coin|defi|dex|cex|blockchain|on-?chain|trading|trader|markt|market|preis|price|volumen|volume|volatilit|spread|rotation|liquidit|whale|futures|perp|spot|usdc|usdt|stable|rvol|momentum|breakout|drawdown|backtest|pine\s*script|tradingview|long|short|leverage|funding|dominance|altcoin|memecoin|gas\s*fee|wallet|privy)/i.test(q)) {
    return true;
  }
  // Short follow-ups in an existing crypto thread are allowed by caller via history
  if (q.length <= 48 && /^(und|aber|warum|wieso|welche|welcher|wie|was|ok|ja|nein|weiter|nochmal|and|but|why|which|what|how|yes|no|continue|again)\b/i.test(q)) {
    return true;
  }
  return false;
}

function nexusStrategistResponseProfile(intent = "general") {
  const i = String(intent || "general").toLowerCase();
  if (i === "rotation_spread") return "ROTATION_SPREAD_ANALYSIS";
  if (i === "rotation") return "ROTATION_ANALYSIS";
  if (i === "grid") return "GRID_ANALYSIS";
  if (i === "trading") return "TRADING_ALLOCATION_ANALYSIS";
  if (i === "risk") return "RISK_ANALYSIS";
  if (i === "market") return "MARKET_ANALYSIS";
  return "GENERAL_MARKET_ANALYSIS";
}

function nexusStrategistCanShowAction(section, userIntent = "general") {
  const key = String(section?.key || "");
  const body = String(section?.body || "").toLowerCase();
  const allowedKeys = ["nexus_grid", "nexus_rotation", "nexus_trading", "exchange_spread"];
  if (!allowedKeys.includes(key)) return false;
  if (/(watch only|nur beobachten|kein sauberer vorteil|no clean edge|nicht bestätigt|not confirmed|risk only|zu riskant|not cleanly confirmed|nicht sauber bestätigt)/i.test(body)) return false;

  const intent = String(userIntent || "general").toLowerCase();

  // Exchange/Spread is an input source for Rotation, but only for spread/buy-sell questions.
  if (key === "exchange_spread") {
    if (!["rotation_spread", "rotation"].includes(intent)) return false;
    return /(kaufen\s*\/\s*verkaufen|buy\s*\/\s*sell|günstiger kaufen|teurer verkaufen|cheaper buy|higher sell|cheapest|highest|spread|premium|differenz|difference|exchange|börse|boerse)/i.test(body);
  }

  if (intent === "rotation_spread" && key !== "nexus_rotation") return false;
  if (intent === "grid" && key !== "nexus_grid") return false;
  if (intent === "trading" && key !== "nexus_trading") return false;
  return /(action ready|aktion bereit|prepared|vorbereitet|setup|rotation|grid|allocation)/i.test(body);
}


function aiTaskPlaceholder(kind) {
    const k = String(kind || "");
    if (k === "strategy_builder") return "Example: Build a low-risk ETH breakout strategy using RVOL confirmation, EMA trend filter, clear invalidation rules, and alert logic.";
    if (k === "backtest_review") return "Example: Paste my backtest summary and explain where this strategy is weak, whether it looks overfitted, and which market regimes hurt performance.";
    if (k === "pine_tradingview") return "Example: Create a Pine Script indicator that shows overextension from the 50 EMA with alerts when RVOL confirms the move.";
    if (k === "daily_report") return "Example: Create today's market report for BTC, ETH, SOL and my watchlist. Highlight risk, rotation, strongest/weakest assets, and what to watch next.";
    if (k === "diagnostics") return "Example: Review my recent trades/orders and identify execution mistakes, overtrading risk, position sizing issues, and behavior patterns.";
    return "Example: Analyze ETH vs BTC relative strength, rotation signals, unusual volume, whale activity, and whether momentum looks healthy or unstable.";
  }

  const AI_ANALYST_SECTION_META_BY_LANG = {
    de: {
      direct_view: { title: "Direkte Einschätzung", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Preisunterschiede" },
      market_read: { title: "Marktlage", sub: "Aktuelle Struktur" },
      nexus_rotation: { title: "Nexus NKR", sub: "Kapitalrotation" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / Zyklus" },
      nexus_trading: { title: "Nexus Trading", sub: "Kontrollierte autonome Ausführung" },
      risk_context: { title: "Risikokontext", sub: "Was kippen kann" },
      tactical_take: { title: "Taktische Einordnung", sub: "Indirekte nächste Schritte" },
      strategist_check: { title: "Strategist Check", sub: "Warum / Risiko / Invalidation / Confidence" },
      next_check: { title: "Nächste Prüfung", sub: "Was zu beobachten ist" },
      output: { title: "Antwort", sub: "Nexus Strategist" },
    },
    en: {
      direct_view: { title: "Direct Assessment", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Price differences" },
      market_read: { title: "Market Read", sub: "Current structure" },
      nexus_rotation: { title: "Nexus NKR", sub: "Capital rotation" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / cycle" },
      nexus_trading: { title: "Nexus Trading", sub: "Controlled autonomous execution" },
      risk_context: { title: "Risk Context", sub: "What can go wrong" },
      tactical_take: { title: "Tactical Take", sub: "Indirect next steps" },
      strategist_check: { title: "Strategist Check", sub: "Why / Risk / Invalidation / Confidence" },
      next_check: { title: "Next Check", sub: "What to monitor" },
      output: { title: "Answer", sub: "Nexus Strategist" },
    },
    fr: {
      direct_view: { title: "Évaluation directe", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Différences de prix" },
      market_read: { title: "Lecture du marché", sub: "Structure actuelle" },
      nexus_rotation: { title: "Nexus NKR", sub: "Rotation du capital" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / cycle" },
      nexus_trading: { title: "Nexus Trading", sub: "Exécution autonome contrôlée" },
      risk_context: { title: "Contexte de risque", sub: "Ce qui peut mal tourner" },
      tactical_take: { title: "Lecture tactique", sub: "Prochaines étapes indirectes" },
      next_check: { title: "Prochaine vérification", sub: "À surveiller" },
      output: { title: "Réponse", sub: "Nexus Strategist" },
    },
    es: {
      direct_view: { title: "Evaluación directa", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Diferencias de precio" },
      market_read: { title: "Lectura del mercado", sub: "Estructura actual" },
      nexus_rotation: { title: "Nexus NKR", sub: "Rotación de capital" },
      nexus_grid: { title: "Nexus Grid", sub: "Rango / ciclo" },
      nexus_trading: { title: "Nexus Trading", sub: "Ejecución autónoma controlada" },
      risk_context: { title: "Contexto de riesgo", sub: "Qué puede fallar" },
      tactical_take: { title: "Lectura táctica", sub: "Próximos pasos indirectos" },
      strategist_check: { title: "Strategist Check", sub: "Por qué / Riesgo / Invalidation / Confidence" },
      next_check: { title: "Próxima revisión", sub: "Qué vigilar" },
      output: { title: "Respuesta", sub: "Nexus Strategist" },
    },
    it: {
      direct_view: { title: "Valutazione diretta", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Differenze di prezzo" },
      market_read: { title: "Lettura del mercato", sub: "Struttura attuale" },
      nexus_rotation: { title: "Nexus NKR", sub: "Rotazione capitale" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / ciclo" },
      nexus_trading: { title: "Nexus Trading", sub: "Esecuzione autonoma controllata" },
      risk_context: { title: "Contesto di rischio", sub: "Cosa può andare storto" },
      tactical_take: { title: "Lettura tattica", sub: "Prossimi passi indiretti" },
      strategist_check: { title: "Strategist Check", sub: "Perché / Rischio / Invalidation / Confidence" },
      next_check: { title: "Prossimo controllo", sub: "Cosa monitorare" },
      output: { title: "Risposta", sub: "Nexus Strategist" },
    },
    pt: {
      direct_view: { title: "Avaliação direta", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Diferenças de preço" },
      market_read: { title: "Leitura do mercado", sub: "Estrutura atual" },
      nexus_rotation: { title: "Nexus NKR", sub: "Rotação de capital" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / ciclo" },
      nexus_trading: { title: "Nexus Trading", sub: "Execução autônoma controlada" },
      risk_context: { title: "Contexto de risco", sub: "O que pode dar errado" },
      tactical_take: { title: "Leitura tática", sub: "Próximos passos indiretos" },
      next_check: { title: "Próxima verificação", sub: "O que monitorar" },
      output: { title: "Resposta", sub: "Nexus Strategist" },
    },
    tr: {
      direct_view: { title: "Doğrudan değerlendirme", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Fiyat farkları" },
      market_read: { title: "Piyasa okuması", sub: "Mevcut yapı" },
      nexus_rotation: { title: "Nexus NKR", sub: "Sermaye rotasyonu" },
      nexus_grid: { title: "Nexus Grid", sub: "Aralık / döngü" },
      nexus_trading: { title: "Nexus Trading", sub: "Kontrollü otonom yürütme" },
      risk_context: { title: "Risk bağlamı", sub: "Ne ters gidebilir" },
      tactical_take: { title: "Taktik değerlendirme", sub: "Dolaylı sonraki adımlar" },
      next_check: { title: "Sonraki kontrol", sub: "Ne izlenmeli" },
      output: { title: "Yanıt", sub: "Nexus Strategist" },
    },
    nl: {
      direct_view: { title: "Directe beoordeling", sub: "Nexus Strategist" },
      exchange_spread: { title: "Exchange / Spread", sub: "Prijsverschillen" },
      market_read: { title: "Marktlezing", sub: "Huidige structuur" },
      nexus_rotation: { title: "Nexus NKR", sub: "Kapitaalrotatie" },
      nexus_grid: { title: "Nexus Grid", sub: "Range / cyclus" },
      nexus_trading: { title: "Nexus Trading", sub: "Gecontroleerde autonome uitvoering" },
      risk_context: { title: "Risicocontext", sub: "Wat mis kan gaan" },
      tactical_take: { title: "Tactische inschatting", sub: "Indirecte volgende stappen" },
      strategist_check: { title: "Strategist Check", sub: "Waarom / Risico / Invalidation / Confidence" },
      next_check: { title: "Volgende controle", sub: "Wat te volgen" },
      output: { title: "Antwoord", sub: "Nexus Strategist" },
    },
  };

  const aiOutputLanguage = useMemo(() => {
    return detectNexusUserLanguage(aiQuestion || aiOutput || "");
  }, [aiQuestion, aiOutput]);

  const aiAnalystSectionMeta = useMemo(() => {
    return AI_ANALYST_SECTION_META_BY_LANG[aiOutputLanguage] || AI_ANALYST_SECTION_META_BY_LANG.en;
  }, [aiOutputLanguage]);

  const parseAiAnalystOutput = useCallback((raw) => {
    const source = String(raw || "").trim();
    if (!source) return [];

    const headingMap = {
      "DIRECT VIEW": "direct_view",
      "DIREKTE EINSCHÄTZUNG": "direct_view",
      "DIREKTE EINSCHAETZUNG": "direct_view",
      "ANSWER": "output",
      "ANTWORT": "output",
      "EXCHANGE / SPREAD": "exchange_spread",
      "BUY / SELL": "exchange_spread",
      "KAUFEN / VERKAUFEN": "exchange_spread",
      "BÖRSE / SPREAD": "exchange_spread",
      "BOERSE / SPREAD": "exchange_spread",
      "MARKET READ": "market_read",
      "MARKTLAGE": "market_read",
      "NEXUS ROTATION": "nexus_rotation",
      "ROTATION / RELATIVER WERT": "nexus_rotation",
      "NEXUS GRID": "nexus_grid",
      "NEXUS TRADING": "nexus_trading",
      "RISK CONTEXT": "risk_context",
      "RISIKOKONTEXT": "risk_context",
      "TACTICAL TAKE": "tactical_take",
      "TAKTISCHE EINORDNUNG": "tactical_take",
      "STRATEGIST CHECK": "strategist_check",
      "STRATEGIST-CHECK": "strategist_check",
      "NEXUS STRATEGIST CHECK": "strategist_check",
      "WHY / RISK / INVALIDATION / CONFIDENCE": "strategist_check",
      "WARUM / RISIKO / INVALIDATION / CONFIDENCE": "strategist_check",
      "NEXT CHECK": "next_check",
      "NÄCHSTE PRÜFUNG": "next_check",
      "NAECHSTE PRUEFUNG": "next_check",
    };

    const lines = source.split(/\r?\n/);
    const sections = [];
    let current = null;

    const flush = () => {
      if (!current) return;
      const body = current.lines
        .join("\n")
        .replace(/^\s*-\s+/gm, "• ")
        .replace(/^\s*\*\*([^*]+)\*\*/gm, "• $1")
        .trim();
      if (body) sections.push({ key: current.key, body });
      current = null;
    };

    for (const line of lines) {
      const clean = String(line || "").replace(/^\s*#{1,4}\s*/, "").replace(/^\s*[-*]\s*/, "").trim();
      const normalized = clean.replace(/[:：]+\s*$/, "").toUpperCase();
      const key = headingMap[normalized];

      if (key) {
        flush();
        current = { key, lines: [] };
      } else if (current) {
        current.lines.push(line);
      }
    }
    flush();

    if (!sections.length) {
      return [{ key: "output", body: source }];
    }

    const order = ["direct_view", "exchange_spread", "market_read", "nexus_rotation", "nexus_grid", "nexus_trading", "risk_context", "tactical_take", "strategist_check", "next_check", "output"];
    return sections.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, []);

  const aiOutputSections = useMemo(() => parseAiAnalystOutput(aiOutput), [aiOutput, parseAiAnalystOutput]);
  const strategistCompactInput = !!aiOutput && !aiLoading;


async function runAi() {
    setErrorMsg("");
    if (!requireStrategistAccess("Nexus Strategist")) return;
    const q = (aiQuestion || "").trim();
    if (!q) return setErrorMsg("Type a crypto / market / Nexus question in the chat.");
    // Capture previous output BEFORE clearing — otherwise chat context is lost.
    const previousOutput = String(aiOutput || "");
    const previousHistory = Array.isArray(aiHistory) ? aiHistory.slice(-10) : [];
    const hasChatThread = previousHistory.length > 0 || !!previousOutput;
    // Continuous chat: any message after the first is a follow-up (no checkbox required).
    const isFollowUpAsk = hasChatThread;
    // Scope: only Nexus / crypto / market. Allow short follow-ups inside an open thread.
    if (!isNexusCryptoScopeQuestion(q) && !hasChatThread) {
      const scopeMsg = detectNexusUserLanguage(q) === "de"
        ? "Nexus Strategist beantwortet nur Fragen zu Krypto, Markt, Watchlist, NKR, Grid, Trading und Nexus. Allgemeine Themen sind nicht erlaubt."
        : "Nexus Strategist only answers crypto, market, watchlist, NKR, Grid, Trading and Nexus questions. General topics are not allowed.";
      setErrorMsg(scopeMsg);
      setAiHistory((prev) => [...(Array.isArray(prev) ? prev : []), { role: "user", content: q }, { role: "assistant", content: scopeMsg }].slice(-10));
      setAiOutput(scopeMsg);
      setAiQuestion("");
      return;
    }
    setAiOutput("");
    const userLang = detectNexusUserLanguage(q);
    const isGermanAsk = userLang === "de";
    const isEnglishAsk = userLang === "en";
    const userIntent = detectNexusUserIntent(q);

    // Compare/watchlist data may still be useful as hidden context for Research and Daily Report,
    // but coin chips are no longer shown in the AI Analyst UI.
    const syms = aiUsesCompareContext ? (compareSymbols || []) : [];

    const aiKindPrompts = isGermanAsk ? {
      research: `Arbeite als Nexus Strategist. Verstehe die Nutzerfrage zuerst, erkenne den passenden Marktmodus und antworte direkt. Nutze Rotation, relative Stärke, Spread, Volumen, Marktstruktur und Exchange-Kontext nur wenn relevant. Keine internen Module erwähnen.`,
      strategy_builder: `Arbeite als Nexus Strategist und Strategy Builder. Übersetze die Idee in eine verständliche, nicht-anweisende Strategie-Logik. Wenn der Nutzer nach günstig kaufen / teurer verkaufen / Rotation fragt, behandle es als Rotation-/Spread-Analyse und nicht als allgemeinen Trading-Report.`,
      backtest_review: `Arbeite als Backtest-Prüfer. Bewerte Robustheit, Drawdown, Marktphasen und Schwachstellen.`,
      pine_tradingview: `Arbeite als TradingView/Pine-Assistent. Hilf mit Pine Script, Logik und Alerts.`,
      daily_report: `Erstelle einen kompakten Tagesbericht mit stärksten/schwächsten Assets, Risiko, Rotation und Beobachtungspunkten.`,
      diagnostics: `Arbeite als Trading-Diagnostiker. Erkläre Risiko, Ausführungsqualität und Schwachstellen ruhig und nicht-befehlend.`,
    } : {
      research: `Act as Nexus Strategist. Understand the user's intent first, choose the correct market mode, and answer directly. Use rotation, relative strength, spread, volume, market structure, and exchange context only when relevant. Never mention internal modules.`,
      strategy_builder: `Act as Nexus Strategist and strategy builder. Translate the idea into clear non-prescriptive strategy logic. If the user asks about buying cheaper / selling higher / rotation, treat it as rotation/spread analysis, not as a generic trading report.`,
      backtest_review: `Act as a backtest reviewer. Evaluate robustness, drawdown, regimes and weak points.`,
      pine_tradingview: `Act as a TradingView/Pine assistant. Help with Pine Script, logic and alerts.`,
      daily_report: `Create a compact daily report with strongest/weakest assets, risk, rotation and watch points.`,
      diagnostics: `Act as a trading diagnostics analyst. Explain risk, execution quality and weak points calmly and non-prescriptively.`,
    };

    const responseFormatPrompt = isGermanAsk ? `

SPRACHE:
- Antworte vollständig auf Deutsch.
- Keine englischen Überschriften, keine englischen Bulletpoints, keine englischen Begriffe außer etablierten Produktnamen wie Nexus Trading oder Exchange.
- Wenn die Nutzereingabe deutsch ist, muss die komplette Ausgabe deutsch sein.

Antwortprofil:
- Nutze nur die Blöcke, die zur Frage passen.
- Wenn die Frage nach Rotation, günstig kaufen, teurer verkaufen, Spread, Börsen oder Arbitrage klingt, nutze bevorzugt:
DIREKTE EINSCHÄTZUNG
ROTATION / RELATIVER WERT
EXCHANGE / SPREAD
RISIKOKONTEXT
NÄCHSTE PRÜFUNG
- Bei dieser Frage KEIN Nexus Trading, KEIN Grid und KEIN kompletter Multi-Report, außer der Nutzer fragt ausdrücklich danach.
- Beginne mit einer klaren Antwort: Vorteil vorhanden / kein sauberer Vorteil / nur beobachten.
- Nutze konkrete Prozentwerte nur, wenn sie im Kontext vorhanden sind.
- Erfinde keine Börsen, Preise oder Spreads.
- Keine direkten Kauf-/Verkaufsbefehle.
- Verstehe normale Sprache: "wo besser", "lohnt sich das", "ist das echt", "wo mehr gehandelt" und übersetze es intern in Spread/Rotation/Liquidität/Risiko.
- Bei "wo kaufen/wo verkaufen" zuerst Coin + günstige Börse + teurere Börse nennen. Paare nur als Zusatz verwenden.` : `

LANGUAGE:
- Answer fully in English.
- Do not use German headings or German bullet text.

Response profile:
- Use only the blocks that match the question.
- If the question is about rotation, buying cheaper, selling higher, spread, exchanges, or arbitrage, prefer:
DIRECT VIEW
ROTATION / RELATIVE VALUE
EXCHANGE / SPREAD
RISK CONTEXT
NEXT CHECK
- For this question do NOT output Nexus Trading, Grid, or a full multi-report unless the user explicitly asks for it.
- Start with a clear answer: edge present / no clean edge / watch only.
- Use concrete percentages only when they exist in context.
- Do not invent exchanges, prices, or spreads.
- No direct buy/sell commands.
- Understand casual wording: "where is better", "is it worth it", "is this real", "where is it traded more" and map it internally to spread/rotation/liquidity/risk.
- For "where to buy/where to sell" questions, name coin + cheaper exchange + higher exchange first. Use pairs only as support.`;

    const basePrompt = aiKindPrompts[aiKind] || `Provide a ${aiProfile} analyst response based on the current task, timeframe, and available context.`;
    const qFinal = isFollowUpAsk
      ? `${basePrompt}${responseFormatPrompt}

Follow-up mode (CRITICAL):
- The user already received a previous answer. Do NOT repeat that answer.
- Answer the NEW follow-up question directly and specifically.
- If the user asks which coins/assets look good, rank concrete symbols from the provided market/watchlist context (relative strength, momentum, volume quality). Do not restate a generic market overview.
- Keep prior context only as background; the new question is the priority.

Previous Strategist output (context only, do not copy):
${previousOutput.slice(0, 900)}

User follow-up (answer THIS):
${q}`
      : `${basePrompt}${responseFormatPrompt}

User intent profile:
${nexusStrategistResponseProfile(userIntent)}

User task:
${q}`;

    setAiLoading(true);
    try {
      await ensureBackendAuthToken();

      const uiTf = String(timeframe || "").toUpperCase();
      const explicitTf = _extractExplicitTfFromQuestion(qFinal);
      const tf = String(explicitTf || uiTf || "90D").toUpperCase();

      const slicedSeries = sliceCompareSeries(compareSeries || {}, tf);
      const seriesStats = _seriesStatsFromSeriesMap(slicedSeries, syms);
      const insightWindows = _buildInsightWindows(compareSeries || {}, syms);
      const aiSignalContext = buildAiSignalContext({
        syms,
        watchRows,
        ratingSummaryBySymbol,
        onchainBySymbol,
        marketConditionBySymbol,
        bestPairsToShow,
        compareWeights: activeCompareWeights,
        aiMode: normalizedAiInsightMode,
        nexusTradingContext: {
          prepared_setup: tradingPreparedSetup || null,
          learning_count: Array.isArray(tradingLearningSetups) ? tradingLearningSetups.length : 0,
          latest_learning_event: Array.isArray(tradingLearningSetups) && tradingLearningSetups[0]?.learningEvent ? tradingLearningSetups[0].learningEvent : null,
          configured_budget_usd: tradingBudgetUsd || "",
          risk_mode: tradingRiskMode || "",
        },
      });
      const aiSignalText = formatAiSignalContextForPrompt(aiSignalContext);

      const statsText = Object.entries(seriesStats)
        .map(([s, stats]) => `${s}: change=${(stats.changePct ?? 0).toFixed(2)}%, vol=${(stats.volPct ?? 0).toFixed(2)}%, maxDD=${(stats.maxDDPct ?? 0).toFixed(2)}%, range=[${stats.min}, ${stats.max}], points=${stats.points}`)
        .join("\n");

      const insightText = Object.entries(insightWindows)
        .map(([windowTf, bySym]) => {
          const lines = Object.entries(bySym || {}).map(([s, stats]) =>
            `${s}: change=${(stats.changePct ?? 0).toFixed(2)}%, vol=${(stats.volPct ?? 0).toFixed(2)}%, maxDD=${(stats.maxDDPct ?? 0).toFixed(2)}%, points=${stats.points}`
          );
          return lines.length ? `Insight window ${windowTf}:\n${lines.join("\n")}` : "";
        })
        .filter(Boolean)
        .join("\n\n");

      const trimmedHist = previousHistory;
      const historyText = trimmedHist
        .map((m) => `${m.role === "assistant" ? "Assistant" : "User"}: ${String(m.content || "").slice(0, 600)}`)
        .join("\n");

      const header = isGermanAsk
        ? (`USER_LANGUAGE: de
USER_INTENT: ${userIntent}
STRATEGIST_FOLLOWUP: ${isFollowUpAsk ? "yes" : "no"}
LATEST_USER_QUESTION: ${q}
` +
          `UI-Zeitraum: ${uiTf}.
` +
          `Aktiver Analysezeitraum: ${tf}.
` +
          (explicitTf
            ? `Der Nutzer hat ausdrücklich ${explicitTf} gefragt; das überschreibt den UI-Zeitraum.
`
            : `Kein expliziter Zeitraum in der Nutzerfrage; nutze den aktuellen UI-Zeitraum.
`) +
          (syms.length ? `Verdeckter Markt-Kontext Symbole: ${syms.join(", ")}
` : "Keine sichtbaren Coins gewählt; nutze den verdeckten Watchlist-/Compare-Kontext.\n") +
          (statsText ? `Serienstatistik (${tf}):
${statsText}
` : "") +
          (insightText ? `
Multi-Zeitraum-Marktkontext für Nexus Strategist:
${insightText}
` : "") +
          (aiSignalText ? `
Verdeckter Markt-, Rating-, Community-, Marktphasen-, On-chain-, Rotation- und Pair-Kontext. Nutze ihn still, liste ihn nicht mechanisch und erwähne keine internen Module:
${aiSignalText}
` : ""))
        : (`USER_LANGUAGE: en
USER_INTENT: ${userIntent}
STRATEGIST_FOLLOWUP: ${isFollowUpAsk ? "yes" : "no"}
LATEST_USER_QUESTION: ${q}
` +
          `UI timeframe: ${uiTf}.
` +
          `Active analysis timeframe: ${tf}.
` +
          (explicitTf
            ? `The user explicitly asked for ${explicitTf}, so this overrides the current UI timeframe.
`
            : `No explicit timeframe was found in the user's question, so use the current UI timeframe.
`) +
          (syms.length ? `Hidden market context symbols: ${syms.join(", ")}
` : "No visible coin scope selected; use hidden watchlist/compare context.\n") +
          (statsText ? `Series stats (${tf}):
${statsText}
` : "") +
          (insightText ? `
Multi-timeframe market context for Nexus Strategist:
${insightText}
` : "") +
          (aiSignalText ? `
Hidden market, rating, community, market-condition, on-chain, rotation and pair context for Nexus Strategist. Use it silently when relevant; do not list it mechanically and do not mention internal modules:
${aiSignalText}
` : ""));

      const questionText =
        isFollowUpAsk && historyText
          ? `${header}\nPrior conversation:\n${historyText}\n\nLatest user question (answer this, do not repeat prior answer):\n${q}\n\n${qFinal}`
          : `${header}\nUser: ${qFinal}`;

      const body = {
        kind: aiKind,
        symbols: syms.length ? syms : (aiSignalContext?.analysis_symbols || []),
        profile: aiProfile,
        question: questionText,
        timeframe: tf,
        selected_timeframe: uiTf,
        explicit_question_timeframe: explicitTf || null,
        index_mode: !!indexMode,
        history: isFollowUpAsk ? trimmedHist : previousHistory,
        series_stats: seriesStats,
        insight_windows: insightWindows,
        ai_signal_context: {
          ...(aiSignalContext && typeof aiSignalContext === "object" ? aiSignalContext : {}),
          strategist_followup: !!isFollowUpAsk,
          previous_response_summary: isFollowUpAsk ? previousOutput.slice(0, 1200) : "",
          raw_user_question: q,
          user_intent: userIntent,
        },
        user_language: userLang,
        raw_user_question: q,
        user_intent: userIntent,
        response_profile: nexusStrategistResponseProfile(userIntent),
        strategist_phase: "phase2_depth_engine",
        strategist_intelligence_focus: "hidden_why_risk_context_invalidation_confidence",
        strategist_quality_gate: true,
        strategist_followup: !!isFollowUpAsk,
        previous_response_summary: isFollowUpAsk ? previousOutput.slice(0, 1200) : "",
      };

      if (!token) throw new Error("Please reconnect your wallet to authorize AI.");
      const r = await api("/api/ai/run", { method: "POST", token, body });

      let text =
        r?.answer ??
        r?.output ??
        r?.text ??
        r?.message ??
        (typeof r === "string" ? r : "");

      if (!text) text = "No AI response.";
      text = String(text).replace(/\n/g, "\n");
      text = normalizeAiOutput(text, tf, qFinal, seriesStats);
      setAiOutput(text);

      // Continuous chat memory — every turn stays in the thread.
      setAiHistory((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const next = [...base, { role: "user", content: q }, { role: "assistant", content: text }];
        return next.slice(-10);
      });
      setAiQuestion("");
    } catch (e) {
      const msg = String(e?.message || e || "AI Analyst request failed.");
      setErrorMsg(msg);
      setAiOutput(`Nexus Strategist could not run.\n\n${msg}`);
    } finally {
      setAiLoading(false);
    }
  }

  // compare live list
  const liveList = useMemo(() => {
    const bySym = new Map();
    for (const r of watchRows || []) {
      const sym = String(r.symbol || "").toUpperCase();
      if (sym) bySym.set(sym, r);
    }
    return visibleCompareSymbols.map((sym) => ({ sym, row: bySym.get(sym) || null }));
  }, [watchRows, visibleCompareSymbols]);

// --- Grid Vault usage (vault-contract only) ---
// IMPORTANT:
// Use the on-chain vault balance as the single source of truth.
// Do NOT trust accumulated backend vault/free values here, because they can drift during
// start/stop/resume/delete test cycles.
// Reserved includes OPEN + PAUSED orders, so a paused order still keeps funds allocated.
const reservedQtyOpen = useMemo(() => {
  try {
    return (gridOrders || [])
      .filter((o) => {
        const st = String(o?.status || "").toUpperCase();
        return st === "OPEN" || st === "PAUSED";
      })
      .reduce((s, o) => s + (Number(o.qty) || 0), 0);
  } catch {
    return 0;
  }
}, [gridOrders]);

const gridV5VaultAccount = useMemo(() => {
  const selectedChain = String(activeGridChainKey || "").toUpperCase();
  const selectedVault = coreVaultOnchainByChain?.[selectedChain] || null;
  if (!selectedVault?.connected || !selectedChain) return null;
  const symbol = String(gridItem || activeGridChainSymbol || "").toUpperCase();
  return selectedVault?.tokens?.[symbol]?.account || null;
}, [coreVaultOnchainByChain, activeGridChainKey, gridItem, activeGridChainSymbol]);

const vaultNativeBal = useMemo(() => {
  if (gridV5VaultAccount) {
    return Math.max(0,
      Number(gridV5VaultAccount?.baseCapital || 0) +
      Number(gridV5VaultAccount?.totalSecuredProfit || 0)
    );
  }
  // Legacy fallback only. V5 Grid normally uses gridV5VaultAccount above.
  return Number(vaultState?.polBalance) || 0;
}, [gridV5VaultAccount, vaultState]);

const vaultFreeQty = useMemo(() => {
  if (gridV5VaultAccount) {
    const total = Math.max(0,
      Number(gridV5VaultAccount?.baseCapital || 0) +
      Number(gridV5VaultAccount?.totalSecuredProfit || 0)
    );
    return Math.max(0, total - Number(gridV5VaultAccount?.totalAllocated || 0));
  }
  return Math.max(0, (Number(vaultNativeBal) || 0) - (Number(reservedQtyOpen) || 0));
}, [gridV5VaultAccount, vaultNativeBal, reservedQtyOpen]);

const manualVaultAvailableQty = useMemo(() => Number(vaultFreeQty) || 0, [vaultFreeQty]);

const manualVaultAllocatedQty = useMemo(() => {
  if (gridV5VaultAccount) return Number(gridV5VaultAccount?.allocatedGrid || 0) || 0;
  return Number(reservedQtyOpen) || 0;
}, [gridV5VaultAccount, reservedQtyOpen]);

const manualVaultTotalQty = useMemo(() => Number(vaultNativeBal) || 0, [vaultNativeBal]);

const manualVaultSettledQty = useMemo(() => {
  const v = Number(vaultState?.heldTokenBal);
  return Number.isFinite(v) ? v : 0;
}, [vaultState]);

const manualPoolLiquidityUsd = useMemo(() => {
  if (!Number.isFinite(Number(activeGridNativeUsd)) || Number(activeGridNativeUsd) <= 0) return null;
  const qty = Number(manualVaultTotalQty || 0);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  return qty * Number(activeGridNativeUsd);
}, [manualVaultTotalQty, activeGridNativeUsd]);

const manualEstimatedImpactPct = useMemo(() => {
  const liq = Number(manualPoolLiquidityUsd);
  const after = Number(manualExposureAfterUsd);
  if (!Number.isFinite(liq) || liq <= 0 || !Number.isFinite(after) || after <= 0) return null;
  return (after / liq) * 100;
}, [manualPoolLiquidityUsd, manualExposureAfterUsd]);

const manualRiskState = useMemo(() => {
  const liq = Number(manualPoolLiquidityUsd);
  const impact = Number(manualEstimatedImpactPct);

  if (!Number.isFinite(liq)) {
    return {
      key: "pending",
      label: "⏳ Checking liquidity",
      tone: "rgba(245, 193, 108, 0.18)",
      border: "1px solid rgba(245, 193, 108, 0.28)",
      color: "#f5c16c",
    };
  }

  if (liq <= 0) {
    return {
      key: "no_liquidity",
      label: "🟡 No vault liquidity",
      tone: "rgba(245, 193, 108, 0.16)",
      border: "1px solid rgba(245, 193, 108, 0.30)",
      color: "#f5c16c",
    };
  }

  if (!Number.isFinite(impact) || impact < 0) {
    return {
      key: "input_needed",
      label: "🟡 Enter order amount",
      tone: "rgba(245, 193, 108, 0.16)",
      border: "1px solid rgba(245, 193, 108, 0.28)",
      color: "#f5c16c",
    };
  }

  let greenMax = 1;
  let yellowMax = 2.5;

  if (liq < 5000) {
    greenMax = 1;
    yellowMax = 2.5;
  } else if (liq < 25000) {
    greenMax = 1.75;
    yellowMax = 4;
  } else if (liq < 100000) {
    greenMax = 2.5;
    yellowMax = 6;
  } else if (liq < 350000) {
    greenMax = 3.5;
    yellowMax = 8;
  } else {
    greenMax = 5;
    yellowMax = 10;
  }

  if (impact < greenMax) {
    return {
      key: "green",
      label: "🟢 Green · normal execution",
      tone: "rgba(34, 197, 94, 0.16)",
      border: "1px solid rgba(34, 197, 94, 0.28)",
      color: "#86efac",
    };
  }
  if (impact <= yellowMax) {
    return {
      key: "yellow",
      label: "🟡 Yellow · warning, review before submit",
      tone: "rgba(245, 193, 108, 0.16)",
      border: "1px solid rgba(245, 193, 108, 0.28)",
      color: "#f5c16c",
    };
  }
  return {
    key: "red",
    label: "🔴 Red · high impact / critical",
    tone: "rgba(239, 68, 68, 0.15)",
    border: "1px solid rgba(239, 68, 68, 0.28)",
    color: "#fca5a5",
  };
}, [manualPoolLiquidityUsd, manualEstimatedImpactPct]);

const isWatchSidebarCompact = isDesktopWide && !!activePanel && activePanel !== "watchlist";
const isGridSidebarCompact = isDesktopWide && !!activePanel && activePanel !== "vault";
const handlePanelActivate = useCallback((name) => (e) => {
  if (typeof window !== "undefined" && window.innerWidth <= 980) return;
  const el = e?.target;
  if (!el || typeof el.closest !== "function") return;

  // Buttons/inputs/lists inside the tile still do their own job
  const interactive = el.closest('button, input, select, textarea, label, a, [role="dialog"], .infoBtn, .iconBtn, .chip, .btn, .btnGhost, .btnDanger, .btnPill, .pill, .pairRow, .pairsScroll, .watchRow, .sessionCard, .rotationSessionCard, .tradingSessionCard, .orderRow, .watchTable, .ordersList, .aiPanel, canvas, svg');
  if (interactive) return;

  // BUILD458: entire panel surface activates focus (not only the header / upper area)
  setActivePanel(name);
}, []);

  return (
    <div className="app nexusApp">
      
      <style>{`
        @keyframes nexusMovementPulse {
          0%, 100% {
            color: rgba(255, 209, 102, 0.62);
            text-shadow: 0 0 0 rgba(255, 184, 0, 0);
            transform: scale(1);
          }
          50% {
            color: rgba(255, 209, 102, 1);
            text-shadow: 0 0 10px rgba(255, 184, 0, 0.55);
            transform: scale(1.06);
          }
        }
        .movementChanceRankPulse {
          display: inline-block;
          font-weight: 900 !important;
          color: #ffd166 !important;
          animation: nexusMovementPulse 2.15s ease-in-out infinite;
          will-change: transform, text-shadow, color;
        }
        .movementChanceRankPulse.extreme {
          animation-duration: 1.45s;
          text-shadow: 0 0 12px rgba(255, 133, 51, 0.45);
        }
        @media (prefers-reduced-motion: reduce) {
          .movementChanceRankPulse,
          .movementChanceRankPulse.extreme {
            animation: none !important;
          }
        }

        /* Mobile Watchlist fix:
           Keep the same desktop columns on phone, only smaller and horizontally scrollable.
           Nothing is hidden: Market Cap, 7D Chart and X remain visible. */
        @media (max-width: 780px) {
          .section-watch{
            overflow: hidden !important;
          }

          .section-watch .panelScroll,
          .section-watch .watchTable,
          .section-watch .watchScroll{
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            -webkit-overflow-scrolling: touch;
          }

          .section-watch .watchTable{
            position: relative !important;
          }

          .section-watch .watchHead.watchStickyHead{
            display: grid !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 9 !important;
            background: rgba(2, 18, 17, 0.96) !important;
            backdrop-filter: blur(8px);
          }

          .section-watch .watchHead,
          .section-watch .watchRow{
            display: grid !important;
            width: 980px !important;
            min-width: 980px !important;
            max-width: none !important;
            grid-template-columns: 34px 100px 72px 128px 138px 150px 122px 132px 42px !important;
            gap: 7px !important;
            align-items: center !important;
          }

          .section-watch .watchHead > *,
          .section-watch .watchRow > *{
            min-width: 0 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .section-watch .watchRow{
            padding: 7px 8px !important;
            min-height: 46px !important;
          }

          .section-watch .watchHead{
            padding-left: 8px !important;
            padding-right: 8px !important;
          }

          .section-watch .watchRow > :nth-child(1),
          .section-watch .watchHead > :nth-child(1){
            justify-content: center !important;
            text-align: center !important;
          }

          .section-watch .watchRow > :nth-child(2),
          .section-watch .watchHead > :nth-child(2){
            justify-content: flex-start !important;
            text-align: left !important;
          }

          .section-watch .watchRow > :nth-child(3),
          .section-watch .watchRow > :nth-child(4),
          .section-watch .watchRow > :nth-child(5),
          .section-watch .watchRow > :nth-child(6),
          .section-watch .watchHead > :nth-child(3),
          .section-watch .watchHead > :nth-child(4),
          .section-watch .watchHead > :nth-child(5),
          .section-watch .watchHead > :nth-child(6){
            justify-content: flex-end !important;
            text-align: right !important;
            font-size: 10.5px !important;
            line-height: 1.05 !important;
          }

          .section-watch .watchRow > :nth-child(7),
          .section-watch .watchHead > :nth-child(7){
            justify-content: center !important;
            text-align: center !important;
            overflow: visible !important;
          }

          .section-watch .watchRow > :nth-child(8),
          .section-watch .watchHead > :nth-child(8){
            justify-content: flex-start !important;
            text-align: left !important;
            overflow: visible !important;
          }

          .section-watch .watchRow > :nth-child(9),
          .section-watch .watchHead > :nth-child(9){
            display: flex !important;
            justify-content: center !important;
            text-align: center !important;
            overflow: visible !important;
          }

          .section-watch .watchSym{
            font-size: 11.5px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          .section-watch .watchSignals{
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 4px !important;
            align-items: center !important;
          }

          .section-watch .watchSignals input{
            margin: 0 !important;
            transform: scale(.86) !important;
          }

          .section-watch .watchSignals .pill{
            font-size: 8.5px !important;
            line-height: 1 !important;
          }

          .section-watch .watchMiniSpark,
          .section-watch .watchSpark,
          .section-watch canvas{
            max-width: 116px !important;
          }
        }


        /* RSI legend */
        .rsiLegend{
          display:flex;
          gap:8px;
          margin:6px 0 10px 0;
          flex-wrap:wrap;
        }
        .rsiLegend .pill{
          font-size:11px;
          padding:4px 10px;
          border-radius:999px;
          opacity:0.9;
        }
        .rsiOverbought{
          background: rgba(239,68,68,0.22);
          color:#ff6b6b;
          border:1px solid rgba(239,68,68,0.55);
        }
        .rsiNeutral{
          background: rgba(255,184,0,0.18);
          color:#ffd34d;
          border:1px solid rgba(255,184,0,0.55);
        }
        .rsiOversold{
          background: rgba(57,217,138,0.18);
          color:#39d98a;
          border:1px solid rgba(57,217,138,0.45);
        }

.btnPill, .btnPill *, .btnGhost, .btnGhost * { 
          color: #fff !important; 
          -webkit-text-fill-color: #fff !important;
        }

        .desktopMarketDeskPanel {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto 185px;
          align-items: center;
          gap: 12px;
          height: 74px;
          min-height: 74px;
          flex: 1 1 760px;
          max-width: 980px;
          min-width: 360px;
          margin: 0 18px 0 28px;
          padding: 0;
          border-radius: 0;
          border: 0;
          background: transparent;
          box-shadow: none;
          overflow: hidden;
        }
        .marketDeskCopy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 7px;
        }
        .marketDeskKicker {
          color: #ff5252;
          font-size: 12px;
          font-weight: 1000;
          line-height: 1;
          letter-spacing: 1.1px;
          text-transform: uppercase;
          text-shadow: 0 0 12px rgba(255,82,82,0.42);
        }
        .marketDeskHeadline {
          color: #64ddff;
          font-size: clamp(21px, 1.45vw, 28px);
          line-height: 1.05;
          font-weight: 1000;
          letter-spacing: -0.02em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow: 0 0 18px rgba(100,221,255,0.38);
        }
        .marketDeskDetail {
          color: rgba(221,255,247,0.74);
          font-size: 12px;
          font-weight: 800;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .marketDeskChartBox {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          height: 68px;
          border-left: 1px solid rgba(98,214,255,0.16);
          padding-left: 14px;
        }
        .marketDeskMetric {
          position: absolute;
          top: 0;
          right: 2px;
          color: #64ddff;
          font-size: 12px;
          font-weight: 1000;
          font-variant-numeric: tabular-nums;
          text-shadow: 0 0 12px rgba(100,221,255,0.36);
          z-index: 2;
        }
        .marketDeskChartSvg {
          width: 172px;
          height: 62px;
          overflow: visible;
        }
        .marketDeskNoChart {
          width: 172px;
          height: 62px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(100,221,255,0.76);
          font-size: 12px;
          font-weight: 1000;
          letter-spacing: 1px;
          border: 1px dashed rgba(100,221,255,0.22);
          border-radius: 12px;
        }
        .marketDeskAxis { stroke: rgba(255,255,255,0.16); stroke-width: 1; }
        .marketDeskArea { fill: rgba(100,221,255,0.11); }
        .marketDeskLine { fill: none; stroke: #64ddff; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; filter: drop-shadow(0 0 7px rgba(100,221,255,0.45)); }
        .marketDeskDot { fill: #64ddff; stroke: rgba(0,0,0,0.55); stroke-width: 2; filter: drop-shadow(0 0 7px rgba(100,221,255,0.7)); }
        .marketDeskBar { fill: #64ddff; opacity: 0.78; filter: drop-shadow(0 0 5px rgba(100,221,255,0.34)); }
        .marketDeskChartSvg.negative .marketDeskLine,
        .marketDeskChartSvg.negative .marketDeskDot,
        .marketDeskChartSvg.negative .marketDeskBar { stroke: #ff6b6b; fill: #ff6b6b; }
        .marketDeskChartSvg.negative .marketDeskArea { fill: rgba(255,107,107,0.12); }
        .marketDeskChartSvg.warning .marketDeskLine,
        .marketDeskChartSvg.warning .marketDeskDot,
        .marketDeskChartSvg.warning .marketDeskBar { stroke: #ffd166; fill: #ffd166; }
        .marketDeskChartSvg.warning .marketDeskArea { fill: rgba(255,209,102,0.12); }
        .marketDeskChartSvg.positive .marketDeskLine,
        .marketDeskChartSvg.positive .marketDeskDot,
        .marketDeskChartSvg.positive .marketDeskBar,
        .marketDeskChartSvg.bull .marketDeskLine,
        .marketDeskChartSvg.bull .marketDeskDot,
        .marketDeskChartSvg.bull .marketDeskBar,
        .marketDeskChartSvg.green .marketDeskLine,
        .marketDeskChartSvg.green .marketDeskDot,
        .marketDeskChartSvg.green .marketDeskBar { stroke: #22c55e; fill: #22c55e; }
        .marketDeskChartSvg.positive .marketDeskArea,
        .marketDeskChartSvg.bull .marketDeskArea,
        .marketDeskChartSvg.green .marketDeskArea { fill: rgba(34,197,94,0.14); }
        .marketDeskFadeKey {
          animation: marketDeskSoftIn 420ms ease both;
        }
        @keyframes marketDeskSoftIn {
          from { opacity: 0; transform: translateY(4px); filter: blur(2px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @media (max-width: 1100px) {
          .desktopMarketDeskPanel { grid-template-columns: minmax(0, 1fr) 150px; min-width: 280px; margin-left: 18px; margin-right: 12px; }
          .marketDeskChartSvg { width: 145px; }
          .marketDeskHeadline { font-size: 22px; }
        }

        /* --- Mobile / small screens: prevent horizontal overflow and wrap topbar controls --- */
        html, body { max-width: 100%; overflow-x: hidden; }
        #root { max-width: 100%; overflow-x: hidden; }

        @media (max-width: 820px) {
          header.topbar {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            justify-content: space-between !important;
            align-items: center !important;
            row-gap: 4px !important;
            column-gap: 8px !important;
            padding-top: 4px !important;
            padding-bottom: 4px !important;
            min-height: 0 !important;
          }
          header.topbar .brand {
            flex: 1 1 auto !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            gap: 8px !important;
          }
          header.topbar .brandTitle {
            line-height: 1.05 !important;
            margin: 0 !important;
          }
          header.topbar .brandSub {
            margin-top: 1px !important;
          }

        @media (max-width: 1180px) {
          .desktopMarketDeskPanel {
            grid-template-columns: minmax(0, 1fr) auto !important;
            height: auto !important;
            min-height: 58px !important;
          }
          .desktopMarketDeskPanel .marketDeskChartBox {
            display: none !important;
          }
          .nkrBannerChip svg {
            display: none !important;
          }
          header.topbar .logoBox,
          header.topbar .logoBox img {
            width: 46px !important;
            height: 46px !important;
          }
        }

          .desktopMarketDeskPanel {
            display: none !important;
          }
          header.topbar .walletBox {
            order: 2 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: calc(100% - 8px) !important;
            display: flex !important;
            flex-direction: column !important;
            flex-wrap: nowrap !important;
            align-items: flex-end !important;
            justify-content: center !important;
            gap: 0 !important;
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          header.topbar .walletRow {
            flex-wrap: nowrap !important;
            justify-content: flex-end !important;
            width: auto !important;
          }
          header.topbar .nkrMobileRow {
            order: 5 !important;
            width: 100% !important;
            margin-top: 2px !important;
          }
          header.topbar .walletBox > * {
            flex: 0 0 auto;
          }
          header.topbar .btnGhost,
          header.topbar .btnPill {
            white-space: nowrap;
          }

          /* Main layout: stack grids/columns to avoid sideways scroll */
          main.main {
            max-width: 100%;
            overflow-x: hidden;
          }
          .chartGrid {
            display: block !important;
          }
          .sparkGridWrap, .sparkGrid {
            max-width: 100%;
            overflow-x: auto;
          }
        }

      

        /* --- Compare header chips: wrap on small screens to avoid horizontal scroll --- */
        @media (max-width: 820px) {
          .cardHead { flex-wrap: wrap; align-items: flex-start; gap: 8px; }
          .cardTitle { flex: 1 1 180px; }
          .cardActions {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 8px;
            max-width: 100%;
            overflow-x: hidden;
          }
          .cardActions .chip { white-space: nowrap; }
        }

        /* --- Grid layout: left controls, right orders --- */
        .gridLayout{
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 16px;
          align-items: start;
        }
        .gridLeft{min-width:0;}
        .gridLeft{justify-self:start;}

        /* Ensure controls stack and never center */
        .gridLeft .gridWrap,
        .gridLeft .gridControls{
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .gridLeft .gridControls > *{ width: 100%; }
        .gridLeft .formRow{ width: 100%; display: flex; flex-direction: column; align-items: flex-start; }

        /* --- Force manual/grid inputs to align left (no centering) --- */
        .gridLeft .formRow select,
        .gridLeft .formRow input,
        .gridLeft select,
        .gridLeft input {
          align-self: flex-start;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        .gridLeft .formRow select,
        .gridLeft .formRow input {
          width: 100%;
          max-width: 560px;
        }
        .gridLayout.tradingDesktopLayout .tradingSetupToggle{
          width: 100%;
          border: 1px solid rgba(34,197,94,.26);
          background: linear-gradient(180deg, rgba(34,197,94,.09), rgba(0,0,0,.18));
          color: #dfffee;
          border-radius: 12px;
          padding: 9px 11px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          cursor: pointer;
          font-weight: 950;
        }
        .gridLayout.tradingDesktopLayout .tradingSetupBody{
          width: 100%;
          display: grid;
          gap: 10px;
        }
        .gridLayout.tradingDesktopLayout .tradingSetupBody.isCollapsed{
          display: none !important;
        }
        @media (min-width: 821px){
          .gridLayout.tradingDesktopLayout{
            grid-template-columns: 1fr !important;
          }
          .gridLayout.tradingDesktopLayout .gridRight{
            display: none !important;
          }
          .gridLayout.tradingDesktopLayout .gridLeft,
          .gridLayout.tradingDesktopLayout .gridWrap,
          .gridLayout.tradingDesktopLayout .gridControls{
            width: 100% !important;
            max-width: none !important;
            justify-self: stretch !important;
            align-items: stretch !important;
          }
          .gridLayout.tradingDesktopLayout .gridControls > *{
            width: 100% !important;
          }
          .gridLayout.tradingDesktopLayout .tradingSetupBody.isCollapsed{
            display: none !important;
          }
          .gridLayout.rotationDesktopLayout{
            grid-template-columns: 1fr !important;
          }
          .gridLayout.rotationDesktopLayout .gridRight{
            display: none !important;
          }
          .gridLayout.rotationDesktopLayout .gridLeft,
          .gridLayout.rotationDesktopLayout .gridWrap,
          .gridLayout.rotationDesktopLayout .gridControls{
            width: 100% !important;
            max-width: none !important;
            justify-self: stretch !important;
            align-items: stretch !important;
          }
          .gridLayout.rotationDesktopLayout .gridControls > *{
            width: 100% !important;
          }
          .gridLayout.gridDesktopLayout{
            grid-template-columns: 1fr !important;
          }
          .gridLayout.gridDesktopLayout .gridRight{
            display: none !important;
          }
          .gridLayout.gridDesktopLayout .gridLeft,
          .gridLayout.gridDesktopLayout .gridWrap,
          .gridLayout.gridDesktopLayout .gridControls{
            width: 100% !important;
            max-width: none !important;
            justify-self: stretch !important;
            align-items: stretch !important;
          }
          .gridLayout.gridDesktopLayout .gridControls > *{
            width: 100% !important;
          }
        }
        /* Keep inline rows (slippage/deadline/quick steps) anchored left */
        .gridLeft .row {
          justify-content: flex-start !important;
          align-items: center;
        }
        
        .gridLeft .row{justify-content:flex-start;}
        
        .gridRight{
          position: sticky;
          top: 16px;
          justify-self: end;
          width: min(420px, 100%);
        }
        @media (max-width: 980px){
          .gridLayout{
            grid-template-columns: 1fr;
          }
          .gridRight{
            position: static;
          }
          .section-grid .gridRight{
            display: none !important;
          }
        }

        /* --- Desktop dashboard overview (Compare / Vault / Watchlist / AI) --- */
        .brandTag {
          font-size: 12px;
          color: rgba(232,242,240,.72);
          margin-top: 2px;
          font-weight: 700;
        }
        .dashboardGrid {
          display: block;
        }
        .dashboardPanel {
          transition: transform .22s ease, opacity .22s ease, box-shadow .22s ease;
        }
        @media (min-width: 981px) {
          .dashboardGrid {
            display: grid;
            grid-template-columns: minmax(0,1fr) minmax(0,1fr);
            grid-template-rows: minmax(320px,auto) minmax(320px,auto);
            gap: 18px;
            align-items: stretch;
          }
          .dashboardPanel {
            margin-bottom: 0;
            min-width: 0;
            min-height: 320px;
            overflow: hidden;
            cursor: pointer;
          }
          .dashboardGrid.hasFocus {
            grid-template-columns: minmax(0,1.35fr) minmax(0,1.35fr) minmax(280px,0.9fr);
            grid-template-rows: repeat(3, minmax(180px, auto));
          }
          .dashboardGrid.hasFocus .dashboardPanel {
            min-height: 180px;
            opacity: .84;
            transform: scale(.97);
          }
          .dashboardGrid.hasFocus .dashboardPanel:hover {
            opacity: .95;
          }
          .dashboardGrid.hasFocus .dashboardPanel.panelActive {
            opacity: 1;
            transform: none;
            box-shadow: 0 20px 60px rgba(0,0,0,.42), inset 0 0 0 1px rgba(46,204,113,.10);
          }
          .dashboardGrid.focus-compare .section-compare { grid-column: 1 / span 2; grid-row: 1 / span 3; }
          .dashboardGrid.focus-compare .section-grid { grid-column: 3; grid-row: 1; }
          .dashboardGrid.focus-compare .section-watch { grid-column: 3; grid-row: 2; }
          .dashboardGrid.focus-compare .section-ai { grid-column: 3; grid-row: 3; }

          .dashboardGrid.focus-vault .section-grid { grid-column: 1 / span 2; grid-row: 1 / span 3; }
          .dashboardGrid.focus-vault .section-compare { grid-column: 3; grid-row: 1; }
          .dashboardGrid.focus-vault .section-watch { grid-column: 3; grid-row: 2; }
          .dashboardGrid.focus-vault .section-ai { grid-column: 3; grid-row: 3; }

          .dashboardGrid.focus-watchlist .section-watch { grid-column: 1 / span 2; grid-row: 1 / span 3; }
          .dashboardGrid.focus-watchlist .section-compare { grid-column: 3; grid-row: 1; }
          .dashboardGrid.focus-watchlist .section-grid { grid-column: 3; grid-row: 2; }
          .dashboardGrid.focus-watchlist .section-ai { grid-column: 3; grid-row: 3; }

          .dashboardGrid.focus-ai .section-ai { grid-column: 1 / span 2; grid-row: 1 / span 3; }
          .dashboardGrid.focus-ai .section-compare { grid-column: 3; grid-row: 1; }
          .dashboardGrid.focus-ai .section-grid { grid-column: 3; grid-row: 2; }
          .dashboardGrid.focus-ai .section-watch { grid-column: 3; grid-row: 3; }
        }

        /* --- FINAL OVERRIDE: whole panel scroll on desktop --- */
        @media (min-width: 981px) {
          html, body, #root, .app { height: 100%; }
          body { overflow: hidden; }
          .main { height: calc(100vh - 96px); overflow: hidden; }

          .dashboardGrid{
            height: 100%;
            min-height: 0;
            display: grid;
            grid-template-columns: minmax(0,1fr) minmax(0,1fr);
            grid-template-rows: minmax(0,1fr) minmax(0,1fr);
            gap: 18px;
          }
          .dashboardGrid.hasFocus{
            grid-template-columns: minmax(0,1.18fr) minmax(0,1.18fr) minmax(260px,0.82fr);
            grid-template-rows: minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);
          }

          .dashboardPanel{
            min-height: 0 !important;
            height: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboardPanel.panelActive{
            transform: none;
          }
          .panelScroll{
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            padding-right: 6px;
            scrollbar-gutter: stable;
          }
          .panelScroll::-webkit-scrollbar{
            width: 10px;
          }
          .panelScroll::-webkit-scrollbar-thumb{
            background: rgba(210,220,230,.22);
            border-radius: 999px;
            border: 2px solid rgba(0,0,0,.10);
          }
          .panelScroll::-webkit-scrollbar-track{
            background: rgba(0,0,0,.10);
            border-radius: 999px;
          }

          /* disable inner desktop scroll strips (NOT Strategist chat — needs scroll) */
          .dashboardPanel .watchScroll,
          .dashboardPanel .pairsScroll,
          .dashboardPanel .liveListBox,
          .dashboardPanel .ordersList,
          .dashboardPanel .aiSelect,
          .dashboardPanel .gridLeft,
          .dashboardPanel .gridRight{
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* BUILD463: Strategist chat must scroll when answer is long */
          .dashboardPanel .aiPanel,
          .section-ai .aiPanel{
            overflow-x: hidden !important;
            overflow-y: auto !important;
            max-height: min(52vh, 520px) !important;
            min-height: 200px !important;
            height: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .dashboardPanel .aiOut{
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          .section-ai .panelScroll{
            overflow-x: hidden !important;
            overflow-y: auto !important;
            max-height: calc(100vh - 160px) !important;
            min-height: 0 !important;
            -webkit-overflow-scrolling: touch;
          }
          .dashboardGrid.focus-ai .section-ai .panelScroll{
            max-height: calc(100vh - 120px) !important;
          }
          .dashboardGrid.focus-ai .section-ai .aiPanel{
            max-height: min(60vh, 640px) !important;
          }
          .dashboardPanel .watchTable,
          .dashboardPanel .compareGrid,
          .dashboardPanel .gridLayout,
          .dashboardPanel .aiWrap{
            overflow: visible !important;
            min-height: 0 !important;
          }
          .gridRight{
            position: static !important;
            top: auto !important;
          }

          /* re-enable Compare internal scrolls after the global desktop override */
          .section-compare .compareGrid{
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            align-items: start !important;
            min-height: 0 !important;
            gap: 12px !important;
          }
          .section-compare .compareChart{
            display: flex !important;
            flex-direction: column !important;
            min-height: 0 !important;
          }
          .section-compare .pairsBox{
            display: flex !important;
            flex-direction: column !important;
            min-height: 0 !important;
            flex: 1 1 auto !important;
          }
          .section-compare .liveListBox{
            max-height: clamp(260px, 34vh, 420px) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }
          .section-compare .pairsScroll{
            flex: 1 1 auto !important;
            min-height: 0 !important;
            max-height: clamp(230px, 31vh, 360px) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            padding-right: 8px !important;
            padding-bottom: 10px !important;
            margin-top: 6px !important;
            margin-bottom: 0 !important;
            scroll-padding-bottom: 10px !important;
            overscroll-behavior: contain !important;
            box-shadow: none !important;
          }
          .section-compare .liveListBox::-webkit-scrollbar,
          .section-compare .pairsScroll::-webkit-scrollbar{
            width: 10px;
          }
          .section-compare .liveListBox::-webkit-scrollbar-thumb,
          .section-compare .pairsScroll::-webkit-scrollbar-thumb{
            background: rgba(210,220,230,.22);
            border-radius: 999px;
            border: 2px solid rgba(0,0,0,.10);
          }
          .section-compare .liveListBox::-webkit-scrollbar-track,
          .section-compare .pairsScroll::-webkit-scrollbar-track{
            background: rgba(0,0,0,.10);
            border-radius: 999px;
          }

          /* focused desktop: give Compare more usable pair-list height */
          .dashboardGrid.hasFocus.focus-compare .section-compare .pairsScroll{
            min-height: 0 !important;
            max-height: clamp(230px, 31vh, 360px) !important;
            padding-bottom: 10px !important;
            scroll-padding-bottom: 10px !important;
          }

          .dashboardGrid.hasFocus.focus-compare .section-compare .pairsBox{
            flex: 1 1 auto !important;
          }

          .section-compare .pairsScroll::after,
          .section-compare .pairsScroll::before{
            content: none !important;
            display: none !important;
            background: none !important;
          }

                    /* focused desktop: compact sidebar panels */
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .cardHead,
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .cardHead,
          .dashboardGrid.hasFocus .section-ai:not(.panelActive) .cardHead{
            flex-wrap: wrap !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .cardActions,
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .cardActions,
          .dashboardGrid.hasFocus .section-ai:not(.panelActive) .cardActions{
            display: flex !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
            gap: 8px !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }



        /* --- Mobile browser in "Desktop site" mode fix ---
           Chrome mobile can report a desktop-like viewport, so the desktop
           dashboard rules above force panels to 100vh and leave the Compare
           card with a large empty block before Nexus Trading. On narrow or
           short viewports we switch the dashboard back to natural document
           flow: content decides the height, no forced 3-row focus layout. */
        @media (max-width: 900px) {
          body {
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }
          .main {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          .dashboardGrid,
          .dashboardGrid.hasFocus {
            display: block !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          .dashboardGrid.hasFocus .section-compare,
          .dashboardGrid.hasFocus .section-grid,
          .dashboardGrid.hasFocus .section-watch,
          .dashboardGrid.hasFocus .section-ai,
          .dashboardGrid.focus-compare .section-compare,
          .dashboardGrid.focus-vault .section-grid,
          .dashboardGrid.focus-watchlist .section-watch,
          .dashboardGrid.focus-ai .section-ai {
            grid-column: auto !important;
            grid-row: auto !important;
          }
          .dashboardPanel,
          .dashboardPanel.panelActive,
          .dashboardGrid.hasFocus .dashboardPanel,
          .dashboardGrid.hasFocus .dashboardPanel.panelActive {
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
            transform: none !important;
            opacity: 1 !important;
            margin-bottom: 16px !important;
          }
          .panelScroll {
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
            padding-right: 0 !important;
          }
          .section-compare .compareGrid,
          .section-compare .compareChart,
          .section-compare .pairsBox {
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          .section-compare .pairsScroll {
            height: auto !important;
            min-height: 0 !important;
            max-height: min(360px, 45vh) !important;
            overflow-y: auto !important;
          }
          .section-compare .liveListBox {
            max-height: min(420px, 52vh) !important;
            overflow-y: auto !important;
          }

          /* Mobile desktop-site width fix: the card was full width, but the
             Compare content stayed in a narrow left column. Stretch the inner
             chart/list containers to the full panel width like Nexus Grid. */
          .section-compare,
          .section-compare .panelScroll,
          .section-compare .compareGrid,
          .section-compare .compareChart,
          .section-compare .chartHeader,
          .section-compare .chartWrap,
          .section-compare .pairsBox,
          .section-compare .pairsScroll {
            width: 100% !important;
            max-width: none !important;
            box-sizing: border-box !important;
          }
          .section-compare .compareGrid {
            display: block !important;
            justify-items: stretch !important;
            align-items: stretch !important;
          }
          .section-compare .compareChart > svg,
          .section-compare .chartWrap > svg,
          .section-compare svg.svgChart,
          .section-compare svg.chartSvg {
            width: 100% !important;
            max-width: 100% !important;
          }
        }

          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridLayout{
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridRight{
            position: static !important;
            width: 100% !important;
            justify-self: stretch !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .ordersHead{
            gap: 8px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .ordersList{
            max-height: 220px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridControls .hint,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridControls .muted,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridControls .tiny{
            font-size: 11px !important;
            line-height: 1.3 !important;
          }

          .watchMiniSpark{
            width: 88px !important;
            height: 28px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            overflow: hidden !important;

            width: 92px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            background: rgba(255,255,255,.025);
            box-shadow: inset 0 0 0 1px rgba(255,255,255,.04);
            overflow: hidden;
          }
          .watchMiniSpark svg{
            width: 100%;
            height: 100%;
            display: block;
          }
          .watchMiniSpark.empty{
            font-size: 11px;
            color: rgba(232,242,240,.45);
          }
          .watchCompact{
            display: grid;
            gap: 8px;
          }
          .watchCompactCard{
            display: grid;
            grid-template-columns: auto 1fr auto;
            gap: 8px;
            align-items: center;
            padding: 10px 12px;
            border: 1px solid rgba(255,255,255,.06);
            border-radius: 14px;
            background: rgba(255,255,255,.03);
          }
          .watchCompactMain{
            min-width: 0;
            display: grid;
            gap: 4px;
          }
          .watchCompactTop{
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
          }
          .watchCompactMeta{
            display: grid;
            gap: 2px;
            min-width: 0;
          }
          .watchCompactPrice{
            text-align: right;
            display: grid;
            gap: 2px;
            justify-items: end;
            min-width: 0;
          }
          .watchCompactStats{
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            align-items: center;
            min-width: 0;
          }
          .watchCompactStats .muted{
            white-space: nowrap;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchTable{
            display: block !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchHead{
            display: none !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchScroll{
            display: grid !important;
            gap: 8px !important;
            overflow-y: auto !important;
            max-height: clamp(180px, 26vh, 320px) !important;
            padding-right: 6px !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchRow{
            display: grid !important;
            grid-template-columns: 36px minmax(88px,1fr) 72px auto !important;
            gap: 8px !important;
            align-items: center !important;
            padding: 10px 12px !important;
            border: 1px solid rgba(255,255,255,.06) !important;
            border-radius: 14px !important;
            background: rgba(255,255,255,.03) !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchRow > :nth-child(5),
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchRow > :nth-child(6){
            display: none !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchCoin{
            min-width: 0 !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchCoin > div:last-child{
            min-width: 0 !important;
          }
          .dashboardGrid.hasFocus .section-watch:not(.panelActive) .watchSym{
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }

        @media (max-width: 820px) {
          /*
            Mobile/small screens:
            Keep the desktop table/list layout, only make it smaller.
            No card stacking, no giant chart cells, no hidden chart column.
          */
          .section-compare .panelScroll,
          .section-watch .panelScroll{
            overflow-x: hidden !important;
          }

          .section-compare .compareGrid{
            display: block !important;
            min-width: 0 !important;
          }
          .section-compare .compareLive{
            margin-bottom: 10px !important;
          }
          .section-compare .chartHeader{
            flex-wrap: wrap !important;
            gap: 7px !important;
          }
          .section-compare .rowBtn{
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 5px !important;
          }
          .section-compare .chartWrap{
            width: 100% !important;
            max-width: 100% !important;
          }
          .section-compare svg,
          .section-compare .svgChart,
          .section-compare .chartSvg{
            width: 100% !important;
            max-width: 100% !important;
          }
          .section-compare .pairsBox{
            min-width: 0 !important;
          }
          .section-compare .pairsBox > div[style*="justify-content: space-between"]{
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 8px !important;
          }

          /* Best pairs: same row layout as desktop, just smaller */
          .section-compare .pairsScroll{
            overflow-x: auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
            padding: 6px !important;
          }
          .section-compare .pairRow{
            display: flex !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            min-width: 620px !important;
            padding: 6px 6px !important;
            gap: 6px !important;
          }
          .section-compare .pairRow > span:first-child{
            flex: 0 0 24px !important;
            width: 24px !important;
            font-size: 10.5px !important;
          }
          .section-compare .pairRow > div{
            display: grid !important;
            grid-template-columns: minmax(86px, 1fr) 86px 86px 42px 60px 76px 52px !important;
            gap: 5px !important;
            align-items: center !important;
            min-width: 560px !important;
            flex: 1 0 auto !important;
          }
          .section-compare .pairName{
            min-width: 0 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            font-size: 11px !important;
          }
          .section-compare .pairRow .pill{
            width: auto !important;
            min-width: 0 !important;
            max-width: 100% !important;
            font-size: 10px !important;
            padding: 3px 5px !important;
            line-height: 1.05 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          /* Watchlist: same table as desktop, only smaller */
          .section-watch .watchTable{
            overflow-x: hidden !important;
            max-width: 100% !important;
          }
          .section-watch .watchScroll{
            overflow-x: auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .section-watch .watchHead,
          .section-watch .watchRow{
            min-width: 520px !important;
            grid-template-columns: 28px 54px 46px 68px 92px 94px 76px 28px !important;
            gap: 4px !important;
            align-items: center !important;
          }
          .section-watch .watchHead{
            font-size: 9.5px !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          .section-watch .watchRow{
            padding: 7px 4px !important;
            min-height: 44px !important;
          }
          .section-watch .watchCoin{
            min-width: 0 !important;
            white-space: nowrap !important;
          }
          .section-watch .watchSym{
            font-size: 10.5px !important;
            line-height: 1 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .section-watch .watchRow .coinLogo.small{
            width: 16px !important;
            height: 16px !important;
            font-size: 9px !important;
          }
          .section-watch .watchRow .mono,
          .section-watch .watchRow .muted,
          .section-watch .watchRow button,
          .section-watch .watchRow input{
            font-size: 9.5px !important;
            line-height: 1.05 !important;
          }
          .section-watch .watchRow .mono{
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .section-watch .watchRow .iconBtn{
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            font-size: 11px !important;
          }
          .section-watch .watchRow svg,
          .section-watch .watchRow canvas{
            max-width: 76px !important;
            width: 76px !important;
          }
        }

        @media (max-width: 560px) {
          /* Very narrow phones: keep desktop-style rows, but allow a small horizontal swipe */
          .section-compare .pairRow{
            min-width: 620px !important;
          }
          .section-watch .watchHead,
          .section-watch .watchRow{
            min-width: 520px !important;
          }
        }

        @media (max-width: 980px) {
          .panelScroll{
            overflow: visible !important;
            padding-right: 0;
          }
        }

        /* FINAL MOBILE WATCHLIST FIX:
           One horizontal scroll container for BOTH header and rows.
           This keeps the header aligned and restores left/right swipe on phones. */
        @media (max-width: 820px) {
          .section-watch .panelScroll{
            overflow-x: hidden !important;
          }

          .section-watch .watchTable{
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior-x: contain !important;
            touch-action: pan-x pan-y !important;
          }

          .section-watch .watchHead.watchStickyHead{
            display: grid !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
            background: rgba(2,18,17,.96) !important;
            backdrop-filter: blur(8px);
          }

          .section-watch .watchScroll{
            display: block !important;
            width: max-content !important;
            min-width: 720px !important;
            max-width: none !important;
            overflow: visible !important;
          }

          .section-watch .watchHead,
          .section-watch .watchRow{
            width: 720px !important;
            min-width: 720px !important;
            max-width: none !important;
            grid-template-columns: 30px 62px 50px 82px 112px 118px 86px 32px !important;
            gap: 5px !important;
            align-items: center !important;
            box-sizing: border-box !important;
            touch-action: pan-x pan-y !important;
          }

          .section-watch .watchHead{
            font-size: 9.5px !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }

          .section-watch .watchRow{
            padding: 7px 4px !important;
            min-height: 46px !important;
          }

          .section-watch .watchRow svg,
          .section-watch .watchRow canvas{
            width: 86px !important;
            max-width: 86px !important;
          }

          .section-watch .watchRow .mono,
          .section-watch .watchRow .muted,
          .section-watch .watchRow button,
          .section-watch .watchRow input{
            font-size: 9.5px !important;
            line-height: 1.05 !important;
          }

          .section-watch .watchRow .iconBtn{
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            font-size: 11px !important;
          }
        }

        /* FINAL Watchlist sparkline precision: CoinGecko-like 7D mini charts */
        .watchMiniSpark.watchMiniSparkCg{
          width: 132px !important;
          height: 38px !important;
          background: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        .watchMiniSpark.watchMiniSparkCg svg{
          width: 100% !important;
          height: 100% !important;
          display: block !important;
          overflow: visible !important;
        }

        @media (max-width: 820px) {
          .section-watch .watchHead,
          .section-watch .watchRow{
            width: 780px !important;
            min-width: 780px !important;
            grid-template-columns: 26px 30px 62px 50px 82px 112px 118px 110px 32px !important;
          }
          .section-watch .watchScroll{
            min-width: 780px !important;
            width: max-content !important;
          }
          .section-watch .watchRow svg,
          .section-watch .watchRow canvas{
            width: 110px !important;
            max-width: 110px !important;
          }
          .section-watch .watchMiniSpark.watchMiniSparkCg{
            width: 110px !important;
            height: 34px !important;
          }
        }


        /* FINAL MOBILE WATCHLIST FIT - keep the delete button in-row without crushing columns.
           Uses the available row width first, then falls back to one horizontal scroll on very small screens. */
        @media (max-width: 820px) {
          .section-watch .panelScroll{ overflow-x: hidden !important; }
          .section-watch .watchTable{
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-x pan-y !important;
          }
          .section-watch .watchScroll{
            display: block !important;
            overflow: visible !important;
            max-width: none !important;
            width: max(100%, 790px) !important;
            min-width: 790px !important;
          }
          .section-watch .watchHead,
          .section-watch .watchRow{
            display: grid !important;
            width: max(100%, 790px) !important;
            min-width: 790px !important;
            max-width: none !important;
            grid-template-columns: 28px 64px 54px 86px 112px 108px 104px 150px 38px !important;
            gap: 5px !important;
            align-items: center !important;
            box-sizing: border-box !important;
          }
          .section-watch .watchHead.watchStickyHead{
            position: sticky !important;
            top: 0 !important;
            z-index: 9 !important;
            background: rgba(2, 18, 17, 0.96) !important;
            backdrop-filter: blur(8px) !important;
          }
          .section-watch .watchHead{
            padding-left: 5px !important;
            padding-right: 5px !important;
            font-size: 9.5px !important;
          }
          .section-watch .watchRow{
            padding: 7px 5px !important;
            min-height: 46px !important;
          }
          .section-watch .watchRow > *,
          .section-watch .watchHead > *{ min-width: 0 !important; }
          .section-watch .watchCoin,
          .section-watch .watchSym{ min-width: 0 !important; white-space: nowrap !important; }
          .section-watch .watchRow .mono{
            font-size: 10.5px !important;
            line-height: 1.05 !important;
            white-space: nowrap !important;
            font-variant-numeric: tabular-nums !important;
          }
          .section-watch .watchMiniSpark.watchMiniSparkCg{ width: 86px !important; height: 32px !important; }
          .section-watch .watchRow svg,
          .section-watch .watchRow canvas{ width: 86px !important; max-width: 86px !important; }
          .section-watch .watchRow .iconBtn{
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            margin-left: auto !important;
            justify-self: end !important;
            flex: 0 0 auto !important;
          }
          .section-watch .watchSignals{
            display: flex !important;
            flex-wrap: nowrap !important;
            justify-content: flex-start !important;
            align-items: center !important;
            gap: 5px !important;
            overflow: visible !important;
            min-width: 150px !important;
          }
          .section-watch .watchSignals .pill{
            flex: 0 0 auto !important;
          }
          .section-watch .watchRow > :nth-child(9),
          .section-watch .watchHead > :nth-child(9){
            overflow: visible !important;
            justify-content: center !important;
          }

        }
`}</style>
<header className="topbar">
        <div className="brand">
          <div
            className="logoBox"
            title="Nexus Analyt"
            style={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              overflow: "hidden",
              background: "#071512",
              border: "1px solid rgba(125,255,194,0.28)",
              boxShadow: "0 0 12px rgba(125,255,194,0.18)",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="data:image/png;base64,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"
              alt="Nexus"
              width="58"
              height="58"
              style={{ width: 58, height: 58, objectFit: "cover", display: "block", borderRadius: "50%" }}
            />
          </div>
          <div>
            <div className="brandTitle">Nexus Analyt</div>
            <div className="brandSub">Live Vault Trading</div>
            <div className="brandTag">Secured by GoPlus</div>
          </div>
        </div>


        <section
          className="desktopMarketDeskPanel"
          title={`${activeMarketBanner.label}: ${activeMarketBanner.value}`}
          aria-label={`Trader intelligence banner: ${activeMarketBanner.label} ${activeMarketBanner.value}`}
        >
          <div className="marketDeskCopy">
            <div className="marketDeskKicker">{activeMarketBanner?.label || "Nexus Market Desk"}</div>
            <div className="marketDeskHeadline">{activeMarketBanner?.value || "Loading liquidity, volatility and risk…"}</div>
            <div className="marketDeskDetail">{activeMarketBanner?.detail || "Smart global market intelligence rotates every 8 seconds."}</div>
          </div>
          <div
            className="nkrBannerChip"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
              padding: "6px 8px 6px 10px",
              borderRadius: 12,
              border: "1px solid rgba(125,255,194,0.22)",
              background: "rgba(7,21,18,0.55)",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: "rgba(221,255,247,0.78)" }}>NKR</span>
            {nkrSpot && nkrSpot.price > 0 ? (
              <span style={{ fontWeight: 800, fontSize: 12, color: "#d9fff0" }}>
                ${formatNkrUsd(nkrSpot.price)}
                <span style={{ marginLeft: 6, color: nkrSpot.change24h >= 0 ? "#8dffd0" : "#ff9b9b" }}>
                  {nkrSpot.change24h >= 0 ? "+" : ""}{Number(nkrSpot.change24h).toFixed(1)}%
                </span>
              </span>
            ) : (
              <span className="muted" style={{ fontSize: 11 }}>Uniswap</span>
            )}
            <svg viewBox="0 0 72 20" width="72" height="18" aria-hidden="true">
              <path d="M0 13 C8 13 10 6 16 8 C22 10 26 5 32 6 C38 7 42 14 50 9 C58 4 64 4 72 10" fill="none" stroke="rgba(125,255,194,0.9)" strokeWidth="1.5" />
            </svg>
            <button
              type="button"
              className="iconBtn"
              title="What is NKR?"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNkrTokenInfoOpen(true); }}
              style={{ width: 22, height: 22, minWidth: 22, fontSize: 12, fontWeight: 900, flex: "0 0 auto" }}
            >
              i
            </button>
          </div>
          <div className="marketDeskChartBox">
            <div className="marketDeskMetric">{activeMarketBanner?.metric || "—"}</div>
            {renderMarketDeskChart(activeMarketBanner)}
          </div>
        </section>

        <div className="walletBox" style={{ position: "relative" }}>
          <div
            className="walletRow"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: isCompactMobile ? 5 : 8,
              flexWrap: isCompactMobile ? "wrap" : "nowrap",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {/* Wallet pill is DISPLAY-ONLY: opens modal, must never trigger external wallet connect */}
            <button
              type="button"
              className="pill silver"
              data-nexus-wallet-trigger="1"
              style={{ cursor: "pointer", flex: "0 0 auto" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setWalletModalOpen((v) => !v);
              }}
              aria-label="Open wallet details"
              title="Open wallet details"
            >
              {wallet || authenticated ? "Wallet" : "Wallet"}
            </button>


            <button
              id="nkr-presale-buy-btn"
              className="btn"
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setBillingMenuOpen(false);
                setHeaderMenuOpen(false);
                setPresalePanelOpen(false);
                try {
                  window.open(
                    "https://app.uniswap.org/explore/tokens/ethereum/0xaa120cFc79C79830B13728a6ebdd379a572880C8",
                    "_blank",
                    "noopener,noreferrer"
                  );
                } catch (_) {}
              }}
              title="Buy NKR on Uniswap (NKR/WETH pool)"
              style={{
                height: 30,
                padding: "0 10px",
                fontSize: 12,
                lineHeight: "30px",
                background: "linear-gradient(90deg,#1a6b4a,#2a9d6a)",
                border: "1px solid rgba(80,220,160,.45)",
                fontWeight: 800,
                whiteSpace: "nowrap",
                flex: "0 0 auto",
              }}
            >
              Buy NKR
              {nkrSpot && Number.isFinite(nkrSpot.change24h) ? (
                <span style={{
                  marginLeft: 6,
                  fontWeight: 800,
                  color: nkrSpot.change24h >= 0 ? "#b8ffd4" : "#ffb3b3",
                }}>
                  {nkrSpot.change24h >= 0 ? "+" : ""}{Number(nkrSpot.change24h).toFixed(1)}%
                </span>
              ) : null}
            </button>
            <div style={{ position: "relative", flex: "0 0 auto" }}>
              <button
                type="button"
                className="btnGhost"
                aria-label="More"
                title="More"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHeaderMenuOpen((v) => !v);
                  setBillingMenuOpen(false);
                }}
                style={{ height: 30, minWidth: 34, padding: "0 10px", fontWeight: 900 }}
              >
                ⋯
              </button>
              {headerMenuOpen ? (
                <>
                  <button type="button" aria-label="Close menu" onClick={() => setHeaderMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 3497, border: 0, background: "transparent", padding: 0 }} />
                  <div
                    role="menu"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      minWidth: 210,
                      padding: 8,
                      borderRadius: 12,
                      zIndex: 3500,
                      background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                      border: "1px solid rgba(255,255,255,0.10)",
                      boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
                    }}
                  >
                    {wallet ? (
                      <button className="btnGhost" type="button" role="menuitem" style={{ width: "100%", textAlign: "left", marginBottom: 6 }} onClick={() => { setHeaderMenuOpen(false); setBillingMenuOpen(true); }}>
                        Billing
                      </button>
                    ) : null}
                    <button className="btnGhost" type="button" role="menuitem" style={{ width: "100%", textAlign: "left", marginBottom: 6 }} onClick={() => { setHeaderMenuOpen(false); setSupportOpen(true); setSupportMsg(""); }}>
                      Support
                    </button>
                    <button className="btnGhost" type="button" role="menuitem" style={{ width: "100%", textAlign: "left", marginBottom: 6 }} onClick={() => { setHeaderMenuOpen(false); if (authenticated) disconnectWallet(); else connectWallet(); }}>
                      {authenticated ? "Disconnect" : "Connect"}
                    </button>
                    <div className="muted" style={{ fontSize: 11, padding: "6px 4px 2px" }}>
                      Access: {isPro ? "ACTIVE" : accessLoading && !access ? "…" : "OFF"}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div className="nkrMobileRow" style={{
            display: isCompactMobile ? "flex" : "none",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            width: "100%",
            marginTop: 0,
            padding: "5px 8px",
            borderRadius: 12,
            border: "1px solid rgba(125,255,194,0.22)",
            background: "rgba(7,21,18,0.72)",
            minHeight: 34,
          }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: "rgba(221,255,247,0.78)" }}>NKR</span>
            <span style={{ fontWeight: 800, fontSize: 13, color: "#d9fff0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {nkrSpot && nkrSpot.price > 0 ? (
                <>
                  ${formatNkrUsd(nkrSpot.price)}
                  <span style={{ marginLeft: 8, color: nkrSpot.change24h >= 0 ? "#8dffd0" : "#ff9b9b" }}>
                    {nkrSpot.change24h >= 0 ? "+" : ""}{Number(nkrSpot.change24h).toFixed(1)}%
                  </span>
                </>
              ) : "Uniswap"}
            </span>
            <button
              type="button"
              className="iconBtn"
              title="What is NKR?"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNkrTokenInfoOpen(true); }}
              style={{ width: 26, height: 26, minWidth: 26, fontSize: 13, fontWeight: 900 }}
            >
              i
            </button>
          </div>


          {supportOpen && (
            <>
              <div
                onClick={() => setSupportOpen(false)}
                style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 3000 }}
              />
              <div
                role="dialog"
                aria-label="Support"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "fixed",
                  top: 78,
                  right: 24,
                  width: 420,
                  maxWidth: "calc(100vw - 24px)",
                  background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 14,
                  padding: 14,
                  zIndex: 4000,
                  boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div className="cardTitle" style={{ margin: 0 }}>Support</div>
                  <button className="iconBtn" type="button" onClick={() => setSupportOpen(false)}>×</button>
                </div>
                <div className="muted" style={{ marginTop: 8 }}>
                  Send a support request. App state, wallet and access mode are attached automatically when available.
                </div>
                <div className="hr" style={{ margin: "12px 0" }} />
                <div className="formRow">
                  <label className="label">Category</label>
                  <select className="select" value={supportCategory} onChange={(e) => setSupportCategory(e.target.value)}>
                    <option>General</option>
                    <option>Subscription</option>
                    <option>Strategist</option>
                    <option>Nexus Trading</option>
                    <option>AI Insight</option>
                    <option>Wallet</option>
                    <option>Payment</option>
                    <option>Bug Report</option>
                    <option>Feature Request</option>
                  </select>
                </div>
                <div className="formRow">
                  <label className="label">Email <span className="muted tiny">(required)</span></label>
                  <input
                    className="input"
                    type="email"
                    required
                    value={supportEmail}
                    onChange={(e) => setSupportEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
                <div className="formRow">
                  <label className="label">Subject</label>
                  <input className="input" value={supportSubject} onChange={(e) => setSupportSubject(e.target.value)} placeholder="Short summary" />
                </div>
                <div className="formRow">
                  <label className="label">Message</label>
                  <textarea className="input" value={supportMessage} onChange={(e) => setSupportMessage(e.target.value)} placeholder="Describe what happened..." style={{ minHeight: 110, resize: "vertical" }} />
                </div>
                <div className="row" style={{ gap: 8, marginTop: 8 }}>
                  <button className="btn" type="button" disabled={supportBusy} onClick={submitSupportTicket}>{supportBusy ? "..." : "Send Support"}</button>
                  <button className="btnGhost" type="button" onClick={() => setSupportOpen(false)}>Close</button>
                </div>
                {supportMsg ? <div className="hint" style={{ marginTop: 8 }}>{supportMsg}</div> : null}
              </div>
            </>
          )}

          {/* Access (Billing / Redeem / Subscribe) */}
          {wallet && (
            <div className="flex items-center gap-2" style={{ flexWrap: "wrap" }}>
              <div style={{ position: "relative" }}>
                {null}

                {billingMenuOpen && (
                  <>
                    <button
                      type="button"
                      aria-label="Close billing menu"
                      onClick={() => setBillingMenuOpen(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 3498, border: 0, background: "transparent", padding: 0 }}
                    />
                    <div
                      role="menu"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        right: 0,
                        zIndex: 3499,
                        width: 230,
                        padding: 8,
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "linear-gradient(180deg, rgba(10,32,28,0.99), rgba(7,24,22,0.99))",
                        boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
                      }}
                    >
                      <button
                        className="btnGhost"
                        type="button"
                        role="menuitem"
                        style={{ width: "100%", textAlign: "left", marginBottom: 6 }}
                        onClick={() => {
                          setBillingMenuOpen(false);
                          setAccessTab("redeem");
                          setRedeemMsg("");
                          setAccessModalOpen(true);
                        }}
                      >
                        Redeem Code
                      </button>
                      <button
                        className="btnGhost"
                        type="button"
                        role="menuitem"
                        style={{ width: "100%", textAlign: "left", marginBottom: 6 }}
                        onClick={() => {
                          setBillingMenuOpen(false);
                          setAccessTab("subscribe");
                          setSubPlan("core");
                          setSubMsg("");
                          setAccessModalOpen(true);
                        }}
                      >
                        Nexus Core · ${SUB_PRICE_USD} / 30 days
                      </button>
                      <button
                        className="btnGhost"
                        type="button"
                        role="menuitem"
                        style={{ width: "100%", textAlign: "left", marginBottom: 6 }}
                        onClick={() => {
                          setBillingMenuOpen(false);
                          setAccessTab("subscribe");
                          setSubPlan("strategist_weekly");
                          setSubMsg("");
                          setAccessModalOpen(true);
                        }}
                      >
                        Nexus AI · ${STRATEGIST_WEEKLY_PRICE_USD} / 7 days
                      </button>
                      <button
                        className="btnGhost"
                        type="button"
                        role="menuitem"
                        style={{ width: "100%", textAlign: "left" }}
                        onClick={() => {
                          setBillingMenuOpen(false);
                          setAccessTab("subscribe");
                          setSubPlan("strategist_monthly");
                          setSubMsg("");
                          setAccessModalOpen(true);
                        }}
                      >
                        Nexus AI · ${STRATEGIST_MONTHLY_PRICE_USD} / 30 days
                      </button>
                    </div>
                  </>
                )}
              </div>
              {/* BUILD469: Buy NKR as flex sibling — always visible when wallet connected */}
              <div style={{ position: "relative" }}>
                {presalePanelOpen ? (
                  <div
                    id="nkr-presale-panel-root"
                    role="dialog"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "110%",
                      right: 0,
                      width: 300,
                      zIndex: 5000,
                      padding: 12,
                      borderRadius: 12,
                      background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                      border: "1px solid rgba(80,220,160,.35)",
                      boxShadow: "0 12px 40px rgba(0,0,0,.5)",
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 13 }}>NKR Presale</div>
                    <div className="muted tiny" style={{ marginTop: 4, lineHeight: 1.35 }}>
                      $0.0005 / NKR · min $5 · <b>no bonus</b>
                      {nkrPresale?.presale_active === true ? (
                        <span style={{ color: "#8dffc0" }}> · live</span>
                      ) : nkrPresale?.error ? (
                        <span style={{ color: "#ffb3b3" }}> · status: {String(nkrPresale.error).slice(0, 80)}</span>
                      ) : nkrPresale?.presale_active === false ? (
                        <span style={{ color: "#ffb3b3" }}> · Presale not active</span>
                      ) : (
                        <span className="muted"> · checking…</span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                      {[5, 10, 20, 50].map((u) => (
                        <button
                          key={u}
                          type="button"
                          className="btnGhost"
                          style={{
                            fontSize: 12,
                            padding: "4px 8px",
                            borderColor: presaleUsd === u ? "rgba(80,220,160,.7)" : undefined,
                          }}
                          onClick={() => setPresaleUsd(u)}
                        >
                          ${u}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      min={5}
                      max={50000}
                      value={presaleUsd}
                      onChange={(e) => setPresaleUsd(Math.max(5, Number(e.target.value) || 5))}
                      style={{
                        marginTop: 8,
                        width: "100%",
                        padding: "6px 8px",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,.12)",
                        background: "rgba(0,0,0,.25)",
                        color: "#e8fff4",
                      }}
                    />
                    <button
                      className="btn"
                      type="button"
                      disabled={presaleBusy || !wallet}
                      style={{ width: "100%", marginTop: 10 }}
                      onClick={() => buyNkrPresaleUsd(presaleUsd)}
                    >
                      {presaleBusy ? "Confirm in wallet…" : `Buy ~$${presaleUsd} NKR with ETH`}
                    </button>
                    {presaleMsg ? (
                      <div className="muted tiny" style={{ marginTop: 8, wordBreak: "break-word" }}>{presaleMsg}</div>
                    ) : null}
                  </div>
                ) : null}
              </div>



              {/* Access modal */}
              
          {accessModalOpen && (
            <>
              {/* click-outside catcher */}
              <div
                onClick={() => setAccessModalOpen(false)}
                style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 3000 }}
              />

              {/* top-right panel */}
              <div
                role="dialog"
                aria-label="Access / Redeem"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{
                  position: "fixed",
                  top: 78,
                  right: 24,
                  width: 380,
                  maxWidth: "calc(100vw - 24px)",
                  background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                  border: "none",
                  borderRadius: 14,
                  padding: 14,
                  zIndex: 4000,
                  boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="cardTitle" style={{ margin: 0 }}>Access</div>
                    <button
                      type="button"
                      className="iconBtn"
                      title="Access info"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAccessHelpOpen(true); }}
                      style={{ width: 22, height: 22, fontSize: 13, fontWeight: 900 }}
                    >
                      i
                    </button>
                  </div>
                  <button
                    className="iconBtn"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAccessModalOpen(false);
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

{accessTab === "redeem" ? (
                  <div>
                    <div className="hint">Enter your permanent code:</div>
                    <div className="row" style={{ gap: 8, marginTop: 8 }}>
                      <input
                        className="input"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value)}
                        placeholder="XXXX-XXXX"
                      />
                      <button className="btn" disabled={redeemBusy} onClick={redeemAccess}>
                        {redeemBusy ? "..." : "Redeem"}
                      </button>
                    </div>
                    {redeemMsg ? <div className="hint" style={{ marginTop: 8 }}>{redeemMsg}</div> : null}
                  </div>
                ) : (
                  <div>
                                        <div
                      className="btnGhost active"
                      style={{
                        textAlign: "left",
                        marginBottom: 12,
                        borderColor: "rgba(57,217,138,0.45)",
                        background: "rgba(57,217,138,0.10)",
                        cursor: "default",
                      }}
                    >
                      {subPlan === "strategist_weekly" ? (
                        <b>Strategist Weekly · ${STRATEGIST_WEEKLY_PRICE_USD}/7 days</b>
                      ) : subPlan === "strategist_monthly" ? (
                        <b>Strategist Monthly · ${STRATEGIST_MONTHLY_PRICE_USD}/30 days</b>
                      ) : (
                        <b>Nexus Core · ${SUB_PRICE_USD}/30 days</b>
                      )}
                    </div>

                    <div className="formRow" style={{ marginBottom: 10 }}>
                      <label className="label">Billing email (optional)</label>
                      <input
                        className="input"
                        value={billingEmail}
                        onChange={(e) => setBillingEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>

                    <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: "center" }}>
                      <div className="hint" style={{ margin: 0, opacity: 0.9 }}>Network:</div>
                      <select
                        className="select"
                        value={subChain}
                        onChange={(e) => setSubChain(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="BNB">BNB Chain (BNB)</option>
                        <option value="POL">Polygon (POL)</option>
                      </select>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
                      <div className="hint" style={{ flex: 1, margin: 0 }}>Payment asset:</div>
                      {selectedSubPaymentAssets.map((asset) => (
                        <button
                          key={asset}
                          type="button"
                          className={`pill ${subToken === asset ? "active" : ""}`}
                          style={{ color: "#fff", background: subToken === asset ? "rgba(57,217,138,0.22)" : "rgba(0,0,0,0.18)", border: "none", cursor: "pointer" }}
                          onClick={() => setSubToken(asset)}
                        >
                          {asset}
                        </button>
                      ))}
                    </div><div className="hint" style={{ marginBottom: 8, opacity: 0.9 }}>
                      Selected: <b>{selectedSubLabel} · ${selectedSubPriceUsd}</b> · <b>{subToken}</b>
                      {subToken === "NKR" ? " (NKR amount is quoted from the configured NKR/USD price before signing)" : ""}
                    </div>

                    <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.02)", margin: "10px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 900 }}>Auto Renew</div>
                          <div className="hint" style={{ marginTop: 4, opacity: 0.82 }}>
                            Optional for stablecoin plans. Core and Strategist Monthly use USDC/USDT. Strategist Weekly is NKR only.
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`pill ${access?.auto_renew_enabled ? "active" : ""}`}
                          disabled={autoRenewBusy || subToken === "NKR"}
                          onClick={() => subToken !== "NKR" && setAutoRenewPreference(!access?.auto_renew_enabled)}
                          style={{
                            color: "#fff",
                            background: access?.auto_renew_enabled ? "rgba(57,217,138,0.24)" : "rgba(0,0,0,0.18)",
                            border: access?.auto_renew_enabled ? "1px solid rgba(57,217,138,0.45)" : "1px solid rgba(255,255,255,0.12)",
                            cursor: autoRenewBusy ? "wait" : "pointer"
                          }}
                        >
                          {subToken === "NKR" ? "Not available for NKR" : (autoRenewBusy ? "..." : (access?.auto_renew_enabled ? "Auto Renew: ON" : "Auto Renew: OFF"))}
                        </button>
                      </div>

                      <div className="hint" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, opacity: 0.9 }}>
                        <span>Expires: <b>{fmtAccessDate(access?.expires_at)}</b></span>
                        <span>Next billing: <b>{fmtAccessDate(access?.next_billing_ts || access?.expires_at)}</b></span>
                        <span>Token: <b>{access?.preferred_token || subToken}</b></span>
                        <span>Chain: <b>{access?.preferred_chain || subChain}</b></span>
                      </div>
                      {autoRenewMsg ? <div className="hint" style={{ marginTop: 8 }}>{autoRenewMsg}</div> : null}
                    </div>

                    <div className="row" style={{ gap: 8, marginTop: 8 }}>
                      <button className="btn" disabled={subBusy} onClick={subscribePay}>
                        {subBusy ? "..." : `Pay ${subToken === "NKR" ? "NKR quote" : selectedSubPriceUsd + " " + subToken} & Activate`}
                      </button>
                      <button
                        className="btnGhost"
                        type="button"
                        onClick={() => {
                          setSubMsg("");
                          refreshAccess();
                        }}
                      >
                        Refresh
                      </button>
                    </div>

                    {subMsg ? <div className="hint" style={{ marginTop: 8 }}>{subMsg}</div> : null}

                  </div>
                )}

                {showAccessReminder ? (
                  <div style={{
                    marginTop: 12,
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "rgba(255,180,0,0.12)",
                    border: "1px solid rgba(255,180,0,0.32)"
                  }}>
                    <div style={{ fontWeight: 900 }}>⚠ Subscription expires in {Math.max(0, accessDaysLeft)} day{Math.max(0, accessDaysLeft) === 1 ? "" : "s"}</div>
                    <div className="hint" style={{ marginTop: 5, opacity: 0.9 }}>
                      Renew now to keep opening new grids and NKR actions. Existing grids continue running.
                    </div>
                    <button
                      type="button"
                      className="btnGhost"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        setAccessTab("subscribe");
                        setSubMsg("");
                      }}
                    >
                      Renew now
                    </button>
                  </div>
                ) : null}

                {showAccessExpiredNotice ? (
                  <div style={{
                    marginTop: 12,
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "rgba(255,92,92,0.12)",
                    border: "1px solid rgba(255,92,92,0.32)"
                  }}>
                    <div style={{ fontWeight: 900 }}>Subscription expired</div>
                    <div className="hint" style={{ marginTop: 5, opacity: 0.9 }}>
                      Existing grids continue running, but new grids and new NKR actions require renewal.
                    </div>
                    <button
                      type="button"
                      className="btnGhost"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        setAccessTab("subscribe");
                        setSubMsg("");
                      }}
                    >
                      Renew access
                    </button>
                  </div>
                ) : null}

              </div>
            </>
          )}
            </div>
          )}


          {accessHelpOpen ? (
            <div className="modalBackdrop" onClick={() => setAccessHelpOpen(false)} style={{ zIndex: 99999 }}>
              <div
                className="modal modalHelp"
                onClick={(e) => e.stopPropagation()}
                style={{ background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))", maxHeight: "82vh", overflowY: "auto", maxWidth: 560 }}
              >
                <div className="modalHead">
                  <div className="cardTitle">Access</div>
                  <button className="iconBtn" type="button" onClick={() => setAccessHelpOpen(false)}>×</button>
                </div>
                <div className="helpBody">
                  <Help
                    de={
                      <>
                        <p>Pläne wählst du oben unter <b>Billing</b>. Das Fenster zeigt nur den gewählten Plan.</p>
                        <p><b>Nexus Core</b> — 30 Tage Trading + AI Insight, Zahlung USDC/USDT.</p>
                        <p><b>Strategist</b> — Extra für den Strategisten, 7 oder 30 Tage, Zahlung USDC/USDT.</p>
                        <p>Zahlen mit NKR kommt erst, wenn der Live-Preis 0,000009 $ erreicht.</p>
                        <p>Redeem-Code schaltet dauerhaft frei. Im Wallet muss der Planbetrag plus Gas liegen.</p>
                      </>
                    }
                    en={
                      <>
                        <p>Pick a plan under <b>Billing</b>. This window only shows the plan you selected.</p>
                        <p><b>Nexus Core</b> — 30 days trading + AI Insight, paid in USDC/USDT.</p>
                        <p><b>Strategist</b> — add-on for Strategist, 7 or 30 days, paid in USDC/USDT.</p>
                        <p>Paying with NKR unlocks when the live price reaches $0.000009.</p>
                        <p>A redeem code unlocks permanently. Keep the plan amount plus gas in the wallet.</p>
                      </>
                    }
                  />
                </div>
              </div>
            </div>
          ) : null}

{/* Nexus Core Vault wallet panel (top-right dropdown) */}
          {walletModalOpen && (
            <div
              ref={walletPanelRef}
              role="dialog"
              aria-label="Privy Wallet and Nexus Core Vault"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute", top: 52, right: 0,
                width: "min(430px, calc(100vw - 24px))", maxHeight: "min(86vh, 820px)",
                overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch",
                background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                border: "1px solid rgba(0,255,166,0.15)", borderRadius: 16, padding: 14,
                zIndex: 2000, boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div className="cardTitle" style={{ margin: 0 }}>{walletPanelTab === "WALLET" ? "Wallet" : "Core Vault"}</div>
                    {walletPanelTab === "WALLET" ? (
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#d9fff0" }}>
                        {walletUsdLoading ? "…" : fmtUsd(Number(walletUsd?.total || 0))}
                      </span>
                    ) : null}
                    <InfoButton title={walletPanelTab === "WALLET" ? "Privy Wallet explained" : "Nexus Core Vault explained"}>
                      {walletPanelTab === "WALLET" ? (
                        <Help
                          de={
                            <>
                              <p><b>Privy Wallet</b> ist deine persönliche Wallet innerhalb von Nexus. Hier siehst du alle erkannten Assets und deren aktuellen Wert.</p>
                              <p><b>Nur EVM-kompatible Netzwerke:</b> NKR unterstützt derzeit ausschliesslich EVM-kompatible Netzwerke.</p>
                              <p><b>Native Layer-1-Assets:</b> BTC wird als WBTC auf Ethereum verwendet, SOL als Wrapped SOL auf Ethereum und XRP als XRP auf der BNB Chain.</p>
                              <p>Weitere Netzwerke und native Assets können in zukünftigen Versionen unterstützt werden.</p>
                              <p><b>Wallet und Core Vault sind getrennt:</b> Guthaben in der Privy Wallet ist noch nicht im Core Vault geschützt oder für Nexus-Systeme reserviert.</p>
                              <p><b>Alle Assets bleiben sichtbar:</b> Nexus blendet unbekannte oder riskante Token nicht aus. Nicht bewertbare Assets werden angezeigt, aber nicht in den USD-Gesamtwert eingerechnet.</p>
                              <p><b>Einzahlung in den Core Vault:</b> Nur der exakte freigegebene Token-Vertrag auf der richtigen Chain darf eingezahlt werden. Symbol, Name oder Logo allein reichen nicht.</p>
                              <p><b>Sicherheitsprüfung:</b> Chain-ID, Contract-Adresse, Owner-Allowlist und Token-Sicherheitsprüfung müssen gemeinsam bestanden werden.</p>
                            </>
                          }
                          en={
                            <>
                              <p><b>Privy Wallet</b> is your personal wallet inside Nexus. It shows all detected assets and their current value.</p>
                              <p><b>EVM-compatible networks only:</b> NKR currently supports EVM-compatible networks only.</p>
                              <p><b>Native Layer-1 assets:</b> BTC is used as WBTC on Ethereum, SOL as Wrapped SOL on Ethereum, and XRP as XRP on BNB Chain.</p>
                              <p>Additional networks and native assets may be supported in future updates.</p>
                              <p><b>Wallet and Core Vault are separate:</b> Funds in the Privy Wallet are not yet protected inside the Core Vault or allocated to Nexus systems.</p>
                              <p><b>All assets remain visible:</b> Nexus does not hide unknown or risky tokens. Unpriced assets remain visible but are excluded from the USD total.</p>
                              <p><b>Core Vault deposits:</b> Only the exact approved token contract on the correct chain can enter the Vault. A symbol, name or logo is never sufficient.</p>
                              <p><b>Security gate:</b> Chain ID, contract address, Owner allowlist and token security checks must all pass.</p>
                            </>
                          }
                        />
                      ) : (
                        <Help
                          de={
                            <>
                              <p><b>Nexus Core Vault</b> ist der geschützte Finanzbereich für Basiskapital, System-Zuteilungen, realisierte Gewinne und Auszahlungen.</p>
                              <p><b>Base Capital Protected:</b> Eingezahltes Basiskapital, das im Vault verbucht ist. Offene Marktgewinne zählen nicht als gesichert.</p>
                              <p><b>Total Secured Profit:</b> Nur geschlossene Netto-Ergebnisse von NKR, Trader und Grid werden als gesicherter Gewinn verbucht.</p>
                              <p><b>Available for Withdraw:</b> Betrag, der nach den Vault-Regeln aktuell zur Auszahlung verfügbar ist. Im Shadow-Modus bleibt dieser Wert null.</p>
                              <p><b>Auszahlungsgebühr:</b> Insgesamt 3 %. Standardmäßig fließen 2 % in die dauerhafte Liquidität und 1 % in die Vault-Reserve.</p>
                              <p><b>Selected Core Vault:</b> {coreVaultOnchainLoading ? "Status wird gelesen." : coreVaultOnchain?.connected ? "Der Vertrag ist verbunden." : "Der Vertragsstatus ist derzeit nicht verfügbar."}</p>
                              <p><b>Contract:</b> {coreVaultOnchain?.contractAddress || "Nicht verfügbar"}</p>
                              <p><b>Status:</b> {coreVaultOnchain?.connected ? (coreVaultOnchain?.paused ? "Pausiert" : "Aktiv") : "Nicht verbunden"} · <b>Solvenz:</b> {coreVaultOnchain?.connected ? (coreVaultOnchain?.allConfiguredTokensSolvent ? "OK" : "Prüfen") : "Nicht verfügbar"}</p>
                              <p><b>Token:</b> USDC und USDT sind für Einzahlungen und Auszahlungen freigegeben. Trading-Ausführung bleibt deaktiviert, bis der geprüfte Executor später ausdrücklich aktiviert wird.</p>
                              <p><b>Shadow und Live:</b> Shadow verwendet simulierte Werte nach denselben Buchungsregeln. Erst bestätigte On-chain-Einzahlungen erscheinen im Live Vault.</p>
                              <button type="button" className="btnGhost" onClick={refreshCoreVaultOnchain} disabled={coreVaultOnchainLoading} style={{ width: "100%", marginTop: 8 }}>{coreVaultOnchainLoading ? "Reading contract…" : "Refresh technical status"}</button>
                            </>
                          }
                          en={
                            <>
                              <p><b>Nexus Core Vault</b> is the protected financial area for base capital, system allocations, realized profit and withdrawals.</p>
                              <p><b>Base Capital Protected:</b> Deposited base capital recorded inside the Vault. Open market profit is not secured profit.</p>
                              <p><b>Total Secured Profit:</b> Only closed net results from NKR, Trader and Grid become secured profit.</p>
                              <p><b>Available for Withdraw:</b> Amount currently available under the Vault rules. In Shadow mode this remains zero.</p>
                              <p><b>Withdrawal fee:</b> 3% total. By default, 2% supports permanent liquidity and 1% remains in the Vault reserve.</p>
                              <p><b>Selected Core Vault:</b> {coreVaultOnchainLoading ? "Reading status." : coreVaultOnchain?.connected ? "The contract is connected." : "The contract status is currently unavailable."}</p>
                              <p><b>Contract:</b> {coreVaultOnchain?.contractAddress || "Unavailable"}</p>
                              <p><b>Status:</b> {coreVaultOnchain?.connected ? (coreVaultOnchain?.paused ? "Paused" : "Active") : "Not connected"} · <b>Solvency:</b> {coreVaultOnchain?.connected ? (coreVaultOnchain?.allConfiguredTokensSolvent ? "OK" : "Check") : "Unavailable"}</p>
                              <p><b>Tokens:</b> USDC and USDT are enabled for deposits and withdrawals. Trading execution remains disabled until the audited executor is explicitly activated later.</p>
                              <p><b>Shadow and Live:</b> Shadow uses simulated values with the same accounting rules. Only confirmed on-chain deposits appear in the Live Vault.</p>
                              <button type="button" className="btnGhost" onClick={refreshCoreVaultOnchain} disabled={coreVaultOnchainLoading} style={{ width: "100%", marginTop: 8 }}>{coreVaultOnchainLoading ? "Reading contract…" : "Refresh technical status"}</button>
                            </>
                          }
                        />
                      )}
                    </InfoButton>
                  </div>
                </div>
                <button className="iconBtn" type="button" onClick={() => setWalletModalOpen(false)} aria-label="Close">×</button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 12 }}>
                <button type="button" className={walletPanelTab === "WALLET" ? "btn" : "btnGhost"} onClick={() => setWalletPanelTab("WALLET")}>Wallet</button>
                <button type="button" className={walletPanelTab === "VAULT" ? "btn" : "btnGhost"} onClick={() => setWalletPanelTab("VAULT")}>Core Vault</button>
              </div>

              {walletPanelTab === "WALLET" ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 10 }}>
                    <button type="button" className={walletViewTab === "ASSETS" ? "btn" : "btnGhost"} onClick={() => setWalletViewTab("ASSETS")}>Assets</button>
                    <button type="button" className={walletViewTab === "TRANSACTIONS" ? "btn" : "btnGhost"} onClick={() => setWalletViewTab("TRANSACTIONS")}>Transactions · {walletTransactions.length}</button>
                  </div>
                  {walletViewTab === "ASSETS" ? (
                    <>
                  <div style={{ marginTop: 12, padding: 11, borderRadius: 12, background: "rgba(0,255,166,0.055)", border: "1px solid rgba(0,255,166,0.14)" }}>
                    <div className="muted" style={{ fontSize: 11 }}>Total wallet value</div>
                    <div className="mono" style={{ marginTop: 4, fontWeight: 900, fontSize: 18 }}>{walletUsdLoading ? "Loading…" : fmtUsd(Number(walletUsd?.total || 0))}</div>
                    <div className="muted" style={{ marginTop: 4, fontSize: 10 }}>Unpriced assets remain visible and are excluded from the USD total.</div>
                  </div>

                  <div style={{ marginTop: 10, display: "grid", gap: 7 }}>
                    {walletAssetRows.length ? walletAssetRows.map((row) => {
                      const tone = row.vaultStatus === "VERIFIED" ? "#43e38d" : row.vaultStatus === "BLOCKED" ? "#ff7b7b" : row.vaultStatus === "PENDING" ? "#ffd166" : "#b7c5c0";
                      return (
                        <div key={row.key} style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,0.09)", background: "rgba(255,255,255,0.025)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 900 }}>{row.symbol} <span className="muted" style={{ fontSize: 10, fontWeight: 700 }}>· {walletChainDisplayName(row.chain)}</span></div>
                              <div className="muted" style={{ fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div className="mono" style={{ fontWeight: 900 }}>{fmtQty(row.amount)}</div>
                              <div className="muted" style={{ fontSize: 10 }}>{row.usd == null ? "Price unavailable" : fmtUsd(row.usd)}</div>
                            </div>
                          </div>
                          <div style={{ marginTop: 7, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <span title={toUserFacingMessage(row.security?.reason, "Vault admission status")} style={{ color: tone, fontSize: 10, fontWeight: 900 }}>● {row.vaultLabel}</span>
                            {row.address && row.address !== "native" ? <span className="muted" style={{ fontFamily: "monospace", fontSize: 9 }}>{row.address.slice(0, 8)}…{row.address.slice(-6)}</span> : null}
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="muted" style={{ padding: 16, textAlign: "center" }}>{balLoading ? "Loading wallet assets…" : "No funded tracked assets found."}</div>
                    )}
                  </div>


                  <details style={{ marginTop: 12, padding: 12, borderRadius: 14, background: "rgba(64,196,255,0.055)", border: "1px solid rgba(64,196,255,0.22)" }}>
                    <summary style={{ fontWeight: 900, fontSize: 13, cursor: "pointer", listStyle: "none" }}>Send asset</summary>
                    <select className="input" value={walletSendAssetKey} onChange={(e) => { setWalletSendAssetKey(e.target.value); setWalletSendMsg(""); }} disabled={walletSendBusy || !walletAssetRows.length} style={{ width: "100%", height: 42, marginTop: 9 }}>
                      {walletAssetRows.map((row) => <option key={row.key} value={row.key}>{row.symbol} · {walletChainDisplayName(row.chain)} · {fmtQty(row.amount)}</option>)}
                    </select>
                    <input className="input" value={walletSendTo} onChange={(e) => { setWalletSendTo(e.target.value); setWalletSendMsg(""); }} placeholder="Recipient wallet address (0x…)" disabled={walletSendBusy} style={{ width: "100%", height: 42, marginTop: 8 }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 8 }}>
                      <input className="input" inputMode="decimal" value={walletSendAmount} onChange={(e) => { setWalletSendAmount(e.target.value); setWalletSendMsg(""); }} placeholder="Amount" disabled={walletSendBusy} style={{ height: 42 }} />
                      <button type="button" className="miniBtn" onClick={() => { const row = walletAssetRows.find((item) => item.key === walletSendAssetKey); if (row) setWalletSendAmount(String(row.amount)); }} disabled={walletSendBusy || !walletSendAssetKey}>MAX</button>
                    </div>
                    <button type="button" className="btn" onClick={sendPrivyWalletAsset} disabled={!wallet || walletSendBusy || !walletSendAssetKey || !walletSendTo || !walletSendAmount} style={{ width: "100%", height: 44, marginTop: 8 }}>
                      {walletSendBusy ? "Sending…" : "Send from Privy Wallet"}
                    </button>
                    {walletSendMsg && <div className="muted" style={{ fontSize: 11, marginTop: 7, wordBreak: "break-word" }}>{walletSendMsg}</div>}
                  </details>

                  <button type="button" className="btnGhost" onClick={() => { setWalletAssetSecurityByKey({}); refreshBalances(); }} disabled={balLoading || !wallet} style={{ width: "100%", marginTop: 10 }}>
                    {balLoading ? "Refreshing…" : "Refresh Wallet Assets"}
                  </button>
                    </>
                  ) : (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      <div className="muted" style={{ fontSize: 10, lineHeight: 1.45 }}>
                        Only transactions signed by this Privy Wallet inside Nexus are stored here. “Signed and broadcast” is separated from the later on-chain confirmation.
                      </div>
                      {walletTransactions.length ? walletTransactions.map((tx) => {
                        const status = String(tx?.status || "PENDING").toUpperCase();
                        const statusColor = status === "CONFIRMED" ? "#86efac" : status === "FAILED" ? "#ff8a8a" : "#ffd166";
                        const explorer = walletExplorerTxUrl(tx?.chain, tx?.txHash);
                        return (
                          <div key={tx.id || tx.txHash} style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.025)", display: "grid", gap: 5 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <b style={{ fontSize: 12 }}>{tx.type || "WALLET TRANSACTION"}</b>
                              <b style={{ color: statusColor, fontSize: 10 }}>{status}</b>
                            </div>
                            <div className="muted" style={{ fontSize: 10 }}>
                              {tx.chain || "—"} · {tx.amount ? `${tx.amount} ` : ""}{tx.asset || ""} · {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : ""}
                            </div>
                            {tx.to ? <div className="muted" style={{ fontSize: 9, wordBreak: "break-all" }}>To: {tx.to}</div> : null}
                            {tx.txHash ? (
                              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                <span className="muted" style={{ fontFamily: "monospace", fontSize: 9 }}>{tx.txHash.slice(0, 10)}…{tx.txHash.slice(-8)}</span>
                                <button type="button" className="miniBtn" onClick={() => navigator.clipboard?.writeText(tx.txHash)}>Copy hash</button>
                                {explorer ? <a className="miniBtn" href={explorer} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>On-chain</a> : null}
                              </div>
                            ) : null}
                            {tx.blockNumber ? <div className="muted" style={{ fontSize: 9 }}>Block: {String(tx.blockNumber)}</div> : null}
                            {tx.note ? <div style={{ color: "#ffb3b3", fontSize: 9 }}>{tx.note}</div> : null}
                          </div>
                        );
                      }) : (
                        <div className="muted" style={{ padding: 18, textAlign: "center", border: "1px dashed rgba(255,255,255,.10)", borderRadius: 12 }}>
                          No Privy Wallet transactions saved yet.
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ marginTop: 12, padding: 10, borderRadius: 12, background: "rgba(64,196,255,0.045)", border: "1px solid rgba(64,196,255,0.20)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 12 }}>EVM Vault Network</div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 900, color: "#86efac" }}>{String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase()}</span>
                    </div>
                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 6 }}>
                      {PRIMARY_EVM_CHAINS.map((chainKey) => {
                        const selected = String(balActiveChain || wsChainKey || DEFAULT_CHAIN).toUpperCase() === chainKey;
                        const knownVault = _isAddr(_getVaultAddrForChain(chainKey)) || _isAddr(CORE_VAULT_ADDRESS_BY_CHAIN?.[chainKey]);
                        const currentConnected = selected && Boolean(coreVaultOnchain?.connected);
                        return (
                          <button
                            key={chainKey}
                            type="button"
                            className={selected ? "btn" : "btnGhost"}
                            onClick={() => {
                              setBalActiveChain(chainKey);
                              setWsChainKey(chainKey);
                              setCoreDepositAsset("");
                              setCoreDepositAmount("");
                              setCoreDepositMsg("");
                              setTimeout(() => { try { refreshVaultState(chainKey); } catch (_) {} }, 0);
                              setTimeout(() => { try { refreshCoreVaultOnchain(); } catch (_) {} }, 50);
                            }}
                            style={{ minWidth: 0, height: 42, padding: "5px 4px", fontSize: 11 }}
                            title={knownVault ? `${chainKey} V5 Vault configured` : `${chainKey} is listed and will activate automatically after its V5 Vault is configured`}
                          >
                            <div style={{ fontWeight: 950 }}>{chainKey}</div>
                            <div style={{ fontSize: 8, marginTop: 2, opacity: 0.8 }}>{currentConnected ? "LIVE" : knownVault ? "READY" : "PENDING"}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginTop: 12, padding: 10, borderRadius: 12, background: coreVaultOverview.isShadow ? "rgba(255,193,7,0.10)" : "rgba(0,255,166,0.08)", border: coreVaultOverview.isShadow ? "1px solid rgba(255,193,7,0.32)" : "1px solid rgba(0,255,166,0.25)" }}>
                    <div style={{ fontWeight: 900 }}>Mode: {coreVaultOverview.mode}</div>
                  </div>

                  {hasCoreAccess ? (
                    <div style={{ marginTop: 9, padding: "9px 10px", borderRadius: 12, border: "1px solid rgba(139,220,255,.18)", background: "rgba(64,196,255,.045)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: 12 }}>Shadow Mode</div>
                      </div>
                      <button type="button" className={shadowUiEnabled ? "btn" : "btnGhost"} onClick={() => setShadowVisibilityForWallet(!shadowUiEnabled)} style={{ minWidth: 82, height: 34 }}>
                        {shadowUiEnabled ? "ON" : "OFF"}
                      </button>
                    </div>
                  ) : null}

                  <details style={{ marginTop: 12, padding: 12, borderRadius: 14, background: "rgba(64,196,255,0.055)", border: "1px solid rgba(64,196,255,0.22)" }}>
                    <summary style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, cursor: "pointer", listStyle: "none" }}>
                      <span style={{ fontWeight: 900, fontSize: 13 }}>Live Core Vault</span>
                      <span style={{ fontSize: 10, fontWeight: 900, color: coreVaultOnchain?.connected ? "#86efac" : "#ffb3b3" }}>
                        {coreVaultOnchainLoading ? "REFRESHING" : coreVaultOnchain?.connected ? "CONNECTED" : "UNAVAILABLE"}
                      </span>
                    </summary>
                    {(() => {
                      const tokenEntries = Object.entries(coreVaultOnchain?.tokens || {})
                        .map(([symbol, tokenState]) => {
                          const account = tokenState?.account || {};
                          const base = Number(account?.baseCapital || 0);
                          const secured = Number(account?.totalSecuredProfit || 0);
                          const allocated = Number(account?.totalAllocated || 0);
                          const liveTotal = base + secured;
                          const free = Number(tokenState?.freeBase ?? account?.freeBase ?? Math.max(0, liveTotal - allocated));
                          return { symbol, tokenState, base, secured, allocated, liveTotal, free };
                        })
                        .filter((row) => row.liveTotal > 0 || row.allocated > 0 || row.free > 0)
                        .sort((a, b) => b.liveTotal - a.liveTotal || a.symbol.localeCompare(b.symbol));

                      const formatVaultAmount = (symbol, value) => {
                        const n = Number(value || 0);
                        return symbol === "ETH" || symbol === "BNB" || symbol === "POL"
                          ? n.toFixed(6)
                          : n.toFixed(2);
                      };

                      if (!tokenEntries.length) {
                        return (
                          <div className="muted" style={{ marginTop: 9, padding: 12, textAlign: "center", borderRadius: 12, border: "1px dashed rgba(255,255,255,0.10)" }}>
                            No funded assets in this Vault.
                          </div>
                        );
                      }

                      return (
                        <div style={{ marginTop: 9, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 8 }}>
                          {tokenEntries.map(({ symbol, base, secured, allocated, liveTotal, free }) => (
                            <div key={symbol} style={{ padding: 10, borderRadius: 12, background: "rgba(0,0,0,0.16)", border: "1px solid rgba(255,255,255,0.08)" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                                <b>{symbol}</b>
                                <span className="mono" style={{ fontWeight: 900 }}>{formatVaultAmount(symbol, liveTotal)}</span>
                              </div>
                              <div className="muted" style={{ fontSize: 9, marginTop: 5 }}>
                                Base {formatVaultAmount(symbol, base)} · Profit {formatVaultAmount(symbol, secured)}
                              </div>
                              <div className="muted" style={{ fontSize: 9, marginTop: 2 }}>
                                Free {formatVaultAmount(symbol, free)} · Allocated {formatVaultAmount(symbol, allocated)}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span />                      <button type="button" className="miniBtn" onClick={refreshCoreVaultOnchain} disabled={coreVaultOnchainLoading || !wallet}>Refresh</button>
                    </div>
                  </details>

                  {shadowUiEnabled ? (
                    <>
                      <div style={{ marginTop: 10, fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.62)", letterSpacing: 0.4, textTransform: "uppercase" }}>Shadow accounting</div>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      ["Vault Stable Balance", coreVaultOverview.stableBalanceUsd, "Simulated USDC / USDT in Shadow accounting"],
                      ["Base Capital Protected", coreVaultOverview.protectedBaseUsd, "Protected stable capital excluding secured profit"],
                      ["Total Secured Profit", coreVaultOverview.securedProfitUsd, "Closed net Grid, NKR and Trader profit"],
                      ["Available for Withdraw", coreVaultOverview.availableForWithdrawUsd, "Based on the selected withdraw source"],
                    ].map(([label, value, hint]) => (
                      <div key={label} title={hint} style={{ padding: 10, borderRadius: 12, background: "rgba(0,255,166,0.055)", border: "1px solid rgba(0,255,166,0.14)" }}>
                        <div className="muted" style={{ fontSize: 11, lineHeight: 1.2 }}>{label}</div>
                        <div className="mono" style={{ marginTop: 5, fontWeight: 900, fontSize: 15 }}>{fmtUsd(Number(value || 0))}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 9, display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}><span className="muted">Allocated to active systems</span><b>{fmtUsd(coreVaultOverview.allocatedUsd)}</b></div>
                  <div className="muted" style={{ marginTop: 4, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 5, fontSize: 10 }}>
                    <span>NKR {fmtUsd(coreVaultOverview.allocationBreakdown.nkrUsd)}</span>
                    <span>Trader {fmtUsd(coreVaultOverview.allocationBreakdown.traderUsd)}</span>
                    <span>Grid {fmtUsd(coreVaultOverview.allocationBreakdown.gridUsd)}</span>
                  </div>
                  <div style={{ marginTop: 7, display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}><span className="muted">Secured profit breakdown</span><b>{fmtUsd(coreVaultOverview.securedProfitUsd)}</b></div>
                  <div className="muted" style={{ marginTop: 4, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 5, fontSize: 10 }}>
                    <span>NKR {fmtUsd(coreVaultOverview.profitBreakdown.nkrUsd)}</span>
                    <span>Trader {fmtUsd(coreVaultOverview.profitBreakdown.traderUsd)}</span>
                    <span>Grid {fmtUsd(coreVaultOverview.profitBreakdown.gridUsd)}</span>
                  </div>
                  <div style={{ marginTop: 7, display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}><span className="muted">Realized Net P&amp;L (live changing)</span><b style={{ color: coreVaultOverview.realizedNetBreakdown.totalUsd >= 0 ? "#86efac" : "#ff8a8a" }}>{fmtUsd(coreVaultOverview.realizedNetBreakdown.totalUsd)}</b></div>
                  <div className="muted" style={{ fontSize: 10, marginTop: 3 }}>NKR {fmtUsd(coreVaultOverview.realizedNetBreakdown.nkrUsd)} · Trader {fmtUsd(coreVaultOverview.realizedNetBreakdown.traderUsd)} · Grid {fmtUsd(coreVaultOverview.realizedNetBreakdown.gridUsd)}</div>
                  <div style={{ marginTop: 7, display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}><span className="muted">Stable reserve</span><b>{fmtUsd(coreVaultOverview.reserveUsd)}</b></div>
                    </>
                  ) : null}
                  <details style={{ marginTop: 12, padding: 12, borderRadius: 14, background: "rgba(0,255,166,0.045)", border: "1px solid rgba(0,255,166,0.14)" }}>
                    <summary style={{ fontWeight: 900, fontSize: 13, cursor: "pointer", listStyle: "none" }}>Deposit to Vault</summary>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1fr) 1fr", gap: 8, marginTop: 9 }}>
                      <select className="input" value={coreDepositAsset} onChange={(e) => { setCoreDepositAsset(e.target.value); setCoreDepositMsg(""); }} disabled={coreDepositBusy || !coreDepositWalletAssets.length} style={{ height: 42 }}>
                        {!coreDepositWalletAssets.length ? <option value="">No wallet assets found</option> : null}
                        {coreDepositWalletAssets.map((asset) => (
                          <option key={asset.key} value={asset.key}>
                            {asset.symbol} · {asset.balance} {asset.vaultApproved ? "· Vault ready" : asset.isNative ? "· Native not configured" : "· approval required"}
                          </option>
                        ))}
                      </select>
                      <input className="input" inputMode="decimal" value={coreDepositAmount} onChange={(e) => { setCoreDepositAmount(e.target.value); setCoreDepositMsg(""); }} placeholder={selectedCoreDepositAsset ? `Max ${selectedCoreDepositAsset.balance} ${selectedCoreDepositAsset.symbol}` : "Amount"} disabled={coreDepositBusy || !selectedCoreDepositAsset} style={{ height: 42 }} />
                    </div>
                    {selectedCoreDepositAsset ? (
                      <div className="muted" style={{ fontSize: 10, marginTop: 6, lineHeight: 1.35 }}>
                        Wallet balance: <b>{selectedCoreDepositAsset.balance} {selectedCoreDepositAsset.symbol}</b> · {selectedCoreDepositAsset.note}
                      </div>
                    ) : null}
                    <button type="button" className="btn" onClick={depositToCoreVault} disabled={!wallet || coreDepositBusy || !coreVaultOnchain?.connected || coreVaultOnchain?.paused} style={{ width: "100%", height: 44, marginTop: 8 }}>
                      {coreDepositBusy ? "Processing…" : "Deposit to Vault"}
                    </button>
                    {coreDepositMsg && <div className="muted" style={{ fontSize: 11, marginTop: 7, wordBreak: "break-word" }}>{coreDepositMsg}</div>}
                  </details>
                  <button type="button" className="btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWalletModalOpen(false); setWithdrawSendOpen(true); }} disabled={!wallet} style={{ height: 44, width: "100%", marginTop: 12, fontSize: 14 }}>Open Withdraw &amp; Payout</button>
                  <button type="button" className="btnGhost" onClick={() => { refreshBalances(); refreshCoreVaultOnchain(); refreshCoreVaultAccounting(); }} disabled={balLoading || !wallet} style={{ width: "100%", marginTop: 8 }}>{balLoading ? "Refreshing…" : "Refresh Vault Overview"}</button>
                </>
              )}

              <div className="muted" style={{ marginTop: 10, fontSize: 10, wordBreak: "break-all" }}>
                Connected wallet: <span style={{ fontFamily: "monospace" }}>{wallet ? `${wallet.slice(0, 8)}…${wallet.slice(-6)}` : "Not connected"}</span>
                <button className="miniBtn" style={{ marginLeft: 8 }} onClick={() => wallet && navigator.clipboard?.writeText(wallet)} disabled={!wallet}>Copy</button>
              </div>
            </div>
          )}

          {/* Nexus Core Vault Withdraw & Payout */}
          {withdrawSendOpen && (
            <div className="modalBackdrop" onClick={() => setWithdrawSendOpen(false)} style={{ zIndex: 3000 }}>
              <div
                className="modal"
                role="dialog"
                aria-label="Nexus Core Vault Withdraw and Payout"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "min(980px, calc(100vw - 24px))",
                  maxWidth: 980,
                  maxHeight: "88vh",
                  overflowY: "auto",
                  background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                  border: "1px solid rgba(0,255,166,0.16)",
                  borderRadius: 18,
                  padding: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <div className="cardTitle" style={{ margin: 0 }}>Nexus Core Vault</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>Withdraw &amp; secured-profit payout</div>
                  </div>
                  <button className="iconBtn" type="button" onClick={() => setWithdrawSendOpen(false)} aria-label="Close">×</button>
                </div>

                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                  {[
                    ["Vault Stable Balance", coreVaultOverview.stableBalanceUsd],
                    ["Base Capital Protected", coreVaultOverview.protectedBaseUsd],
                    ["Total Secured Profit", coreVaultOverview.securedProfitUsd],
                    ["Available for Withdraw", coreVaultOverview.availableForWithdrawUsd],
                  ].map(([label, value]) => (
                    <div key={label} style={{ padding: 11, borderRadius: 12, background: "rgba(0,255,166,0.055)", border: "1px solid rgba(0,255,166,0.14)" }}>
                      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
                      <div className="mono" style={{ marginTop: 5, fontWeight: 900 }}>{fmtUsd(Number(value || 0))}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 9, padding: 10, borderRadius: 12, background: "rgba(0,255,166,0.035)", border: "1px solid rgba(0,255,166,0.10)", display: "grid", gap: 6 }}>
                  <div style={{ fontWeight: 900, fontSize: 12 }}>Secured profit by system</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, fontSize: 12 }}>
                    <div><span className="muted">NKR</span><br /><b>{fmtUsd(coreVaultOverview.profitBreakdown.nkrUsd)}</b></div>
                    <div><span className="muted">Trader</span><br /><b>{fmtUsd(coreVaultOverview.profitBreakdown.traderUsd)}</b></div>
                    <div><span className="muted">Grid</span><br /><b>{fmtUsd(coreVaultOverview.profitBreakdown.gridUsd)}</b></div>
                  </div>
                  <div style={{ marginTop: 10, padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.025)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span className="muted">Realized Net P&amp;L</span><b style={{ color: coreVaultOverview.realizedNetBreakdown.totalUsd >= 0 ? "#86efac" : "#ff8a8a" }}>{fmtUsd(coreVaultOverview.realizedNetBreakdown.totalUsd)}</b></div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 5 }}>This value updates automatically and may rise or fall. Secured Profit above only increases when a positive closed-net delta is protected.</div>
                  </div>
                  {coreVaultOverview.isShadow && <div className="muted" style={{ fontSize: 11 }}>Shadow follows the later live accounting rules. Only closed net results count, but nothing is withdrawable.</div>}
                </div>

                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12, alignItems: "start" }}>
                  <div style={{ padding: 14, borderRadius: 14, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="cardTitle" style={{ margin: 0, fontSize: 15 }}>Manual Withdraw</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>Choose the funding source. Nexus shows all estimated costs before confirmation.</div>

                    <div style={{ marginTop: 12 }}>
                      <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Source</div>
                      <select className="input" value={coreWithdrawSource} onChange={(e) => { setCoreWithdrawSource(e.target.value); setCoreWithdrawQuote(null); }} style={{ width: "100%", height: 42 }}>
                        <option value="BASE_CAPITAL">Free Base Capital</option>
                        <option value="SECURED_NKR">Secured NKR Profit</option>
                        <option value="SECURED_TRADER">Secured Trader Profit</option>
                        <option value="SECURED_GRID">Secured Grid Profit</option>
                      </select>
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 130px", gap: 8 }}>
                      <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Amount</div>
                        <input className="input" value={coreWithdrawAmount} onChange={(e) => { setCoreWithdrawAmount(e.target.value); setCoreWithdrawQuote(null); }} placeholder="0.00" inputMode="decimal" style={{ width: "100%", height: 42 }} />
                      </div>
                      <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Asset</div>
                        <select className="input" value={coreWithdrawAsset} onChange={(e) => { setCoreWithdrawAsset(e.target.value); setCoreWithdrawQuote(null); }} style={{ width: "100%", height: 42 }}>
                          {coreWithdrawAssetRows.map((row) => (
                            <option key={row.symbol} value={row.symbol}>
                              {row.symbol}{row.native ? " (native · for gas)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Destination address</div>
                      <input className="input" value={coreWithdrawAddress} onChange={(e) => { setCoreWithdrawAddress(e.target.value); setCoreWithdrawQuote(null); }} placeholder="0x…" style={{ width: "100%", height: 42, fontFamily: "monospace" }} />
                    </div>

                    <div style={{ marginTop: 9, display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
                      <span className="muted">Available from selected source</span>
                      <b>{coreWithdrawAvailable.toFixed(coreWithdrawAssetRows.find((row) => row.symbol === coreWithdrawAsset)?.native ? 6 : 2)} {coreWithdrawAsset}</b>
                    </div>

                    <button type="button" className="btn" onClick={previewCoreVaultWithdraw} disabled={coreWithdrawQuoteBusy || !wallet} style={{ width: "100%", height: 44, marginTop: 12 }}>
                      {coreWithdrawQuoteBusy ? "Preparing preview…" : "Preview Withdraw"}
                    </button>
                  </div>

                  <div style={{ padding: 14, borderRadius: 14, background: "rgba(0,255,166,0.045)", border: "1px solid rgba(0,255,166,0.14)" }}>
                    <div className="cardTitle" style={{ margin: 0, fontSize: 15 }}>Withdraw Preview</div>
                    {!coreWithdrawQuote ? (
                      <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 10 }}>Enter the amount and destination, then select <b>Preview Withdraw</b>. No funds move during preview.</div>
                    ) : (
                      <div style={{ marginTop: 10, display: "grid", gap: 8, fontSize: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span className="muted">Withdraw amount</span><b>{Number(coreWithdrawQuote.grossAmount || 0).toFixed(coreWithdrawAssetRows.find((row) => row.symbol === coreWithdrawAsset)?.native ? 6 : 2)} {coreWithdrawAsset}</b></div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span className="muted">Network / gas fee</span><b>{fmtUsd(Number(coreWithdrawQuote.costsIncluded?.gasUsd || 0))}</b></div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span className="muted">Swap / routing cost</span><b>{fmtUsd(Number(coreWithdrawQuote.costsIncluded?.swapUsd || 0) + Number(coreWithdrawQuote.costsIncluded?.slippageUsd || 0))}</b></div>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span className="muted">Routing buffer</span><b>{fmtUsd(Number(coreWithdrawQuote.costsIncluded?.routingBufferUsd || 0))}</b></div>
                        <div className="hr" style={{ margin: "2px 0" }} />
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 }}><span>Receive</span><b style={{ color: "#54f0a4" }}>{Number(coreWithdrawQuote.netAmount || 0).toFixed(coreWithdrawAssetRows.find((row) => row.symbol === coreWithdrawAsset)?.native ? 6 : 2)} {coreWithdrawAsset}</b></div>
                        <div className="muted" style={{ fontSize: 11, lineHeight: 1.4 }}>{coreWithdrawQuote.gasPaidSeparately ? "Direct CoreVault withdrawal. Network gas is paid separately in ETH; no swap or routing buffer is deducted." : "Preview only. Unused routing buffer returns to the user stable balance."}</div>
                        <button type="button" className="btn" onClick={confirmCoreVaultWithdraw} disabled={coreWithdrawExecuteBusy || !coreWithdrawQuote || Number(coreWithdrawQuote.netAmount || 0) <= 0} style={{ width: "100%", height: 42, marginTop: 3 }}>{coreWithdrawExecuteBusy ? "Withdrawing…" : "Confirm Withdraw"}</button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 12, padding: 14, borderRadius: 14, background: "rgba(0,255,166,0.045)", border: "1px solid rgba(0,255,166,0.14)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div>
                      <div className="cardTitle" style={{ margin: 0, fontSize: 15 }}>Automatic Profit Payout</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Only realized profit already secured in the selected V5 settlement asset can be paid out. Open-position profit never counts.</div>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800 }}>
                      <input
                        type="checkbox"
                        checked={profitPayoutEnabled && profitPayoutSchedule !== "MANUAL_ONLY"}
                        disabled={profitPayoutSchedule === "MANUAL_ONLY"}
                        onChange={(e) => setProfitPayoutEnabled(e.target.checked)}
                      /> Enabled
                    </label>
                  </div>
                  <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, alignItems: "end" }}>
                    <div>
                      <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Schedule</div>
                      <select
                        className="input"
                        value={profitPayoutSchedule}
                        onChange={(e) => {
                          const next = e.target.value;
                          setProfitPayoutSchedule(next);
                          if (next === "MANUAL_ONLY") setProfitPayoutEnabled(false);
                        }}
                        style={{ width: "100%", height: 38 }}
                      >
                        <option value="IMMEDIATE">Immediate</option>
                        <option value="EVERY_DAYS">Every X days</option>
                        <option value="MANUAL_ONLY">Manual only</option>
                      </select>
                    </div>
                    {profitPayoutSchedule === "EVERY_DAYS" && (
                      <div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Every days</div>
                        <input className="input" type="number" min="1" max="365" step="1" value={profitPayoutEveryDays} onChange={(e) => setProfitPayoutEveryDays(e.target.value)} style={{ width: "100%", height: 38 }} />
                      </div>
                    )}
                    <div><div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Payout %</div><input className="input" value={profitPayoutPct} onChange={(e) => setProfitPayoutPct(e.target.value)} inputMode="decimal" style={{ width: "100%", height: 38 }} /></div>
                    <div><div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Minimum payout (USD)</div><input className="input" value={profitPayoutMinUsd} onChange={(e) => setProfitPayoutMinUsd(e.target.value)} inputMode="decimal" style={{ width: "100%", height: 38 }} /></div>
                    <div><div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Asset</div><select className="input" value={profitPayoutAsset} onChange={(e) => setProfitPayoutAsset(e.target.value)} style={{ width: "100%", height: 38 }}><option value="USDT">USDT</option><option value="USDC">USDC</option></select></div>
                    <div><div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>Source</div><div className="pill" style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>Secured Profit Only</div></div>
                    <button type="button" className="btn" onClick={saveProfitPayoutSettings} disabled={profitPayoutBusy || !wallet} style={{ height: 38 }}>{profitPayoutBusy ? "Saving…" : "Save"}</button>
                  </div>
                  <div className="muted" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 9 }}>
                    {profitPayoutSchedule === "IMMEDIATE"
                      ? "Immediate means after profit is realized, secured in a stablecoin, meets the minimum amount, and passes Vault safety checks."
                      : profitPayoutSchedule === "EVERY_DAYS"
                        ? `The next payout window is every ${Math.max(1, Number(profitPayoutEveryDays || 1))} day(s). Only secured profit available at that time is eligible.`
                        : "No automatic payout. The user initiates payout manually from Withdraw & Payout."}
                    {profitPayoutPreview?.nextPayoutLabel ? ` Next: ${profitPayoutPreview.nextPayoutLabel}.` : ""}
                  </div>
                </div>

                <div style={{ marginTop: 12, padding: 10, borderRadius: 12, background: "rgba(255,193,7,0.07)", border: "1px solid rgba(255,193,7,0.22)", fontSize: 12, lineHeight: 1.45 }}>
                  <b>Core Vault V3 is connected.</b> Deposits and on-chain balances use the verified V3 interface. Live trades remain locked until USDC execution and the ETH/USDC route are configured and verified.
                </div>

                {txMsg ? <div style={{ marginTop: 10, fontSize: 12, padding: "8px 10px", borderRadius: 12, background: "rgba(255,255,255,0.04)" }}>{txMsg}</div> : null}
              </div>
            </div>
          )}
          {addTokenOpen && (
            <div
              role="dialog"
              aria-label="Add token"
              onMouseDown={(e) => { e.stopPropagation(); }}
              onClick={(e) => { e.stopPropagation(); }}
              style={{
                position: "absolute",
                top: 52,
                right: 0,
                width: 340,
                background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                border: "none",
                borderRadius: 14,
                padding: 14,
                zIndex: 2100,
                boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div className="cardTitle" style={{ margin: 0 }}>Add token</div>
                <button
                  className="iconBtn"
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddTokenOpen(false); }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                <div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Chain</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {ENABLED_CHAINS.map((chainOpt) => {
                      const opt = normalizeWalletChainKey(chainOpt);
                      const active = normalizeWalletChainKey(addTokenChain) === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={active ? "btn" : "btnGhost"}
                          style={{ padding: "8px 10px", borderRadius: 999, fontSize: 12, fontWeight: 800 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddTokenChain(opt);
                            setBalActiveChain(opt);
                            setWsChainKey(opt);
                            setAddTokenErr("");
                            loadTokenList(opt);
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                    Active: <b>{walletChainDisplayName(addTokenChain)}</b> ({normalizeWalletChainKey(addTokenChain)})
                  </div>
                </div>

                <div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Search (symbol/name)</div>
                  <input
                    className="input"
                    value={addTokenQuery}
                    onChange={(e) => setAddTokenQuery(e.target.value)}
                    placeholder="e.g. PEPE, CAKE, XRP"
                  />
                </div>

                <div className="muted" style={{ textAlign: "center", fontSize: 12 }}>or</div>

                <div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Contract address</div>
                  <input
                    className="input"
                    value={addTokenContract}
                    onChange={(e) => setAddTokenContract(e.target.value)}
                    placeholder="0x…"
                  />
                </div>

                {addTokenErr && (
                  <div style={{ color: "#ffb3b3", fontSize: 12 }}>{addTokenErr}</div>
                )}

                <button
                  type="button"
                  className="btn"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); addWalletToken(); }}
                  disabled={!wallet || addTokenBusy}
                >
                  {addTokenBusy ? "Adding…" : "Add"}
                </button>

                {/* Search results */}
                {String(addTokenQuery || "").trim() && (
                  <div style={{ marginTop: 6 }}>
                    <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Results (click to add)</div>
                    <div style={{ maxHeight: 220, overflow: "auto", border: "none", borderRadius: 12 }}>
                      {(() => {
                        const q = String(addTokenQuery || "").trim().toLowerCase();
                        const pool = tokenListCache?.[addTokenChain] || [];
                        const out = pool
                          .filter((t) => (t.symbol || "").toLowerCase().includes(q) || (t.name || "").toLowerCase().includes(q))
                          .slice(0, 80);
                        if (!out.length) return <div className="muted" style={{ padding: 10, fontSize: 12 }}>No matches. Paste contract address instead.</div>;
                        return out.map((t) => (
                          <button
                            key={t.address}
                            type="button"
                            className="rowBtn"
                            style={{
                              width: "100%",
                              textAlign: "left",
                              padding: "8px 10px",
                              border: "none",
                              background: "transparent",
                              color: "rgba(255,255,255,0.92)",
                              borderBottom: "1px solid rgba(255,255,255,0.06)",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setAddTokenContract(t.address);
                              setAddTokenQuery(t.symbol);
                              setAddTokenErr("");
                            }}
                            title={t.address}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <div style={{ fontWeight: 700 }}>{t.symbol}</div>
                              <div className="muted" style={{ fontSize: 12 }}>{t.address.slice(0, 6)}…{t.address.slice(-4)}</div>
                            </div>
                            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{t.name}</div>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      

</header>

      <main className="main mobileStack">
{nkrTokenInfoOpen ? (
          <div
            className="modalBackdrop"
            onClick={() => setNkrTokenInfoOpen(false)}
            style={{ zIndex: 99999 }}
          >
            <div
              className="modal modalHelp"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
                maxHeight: "82vh",
                overflowY: "auto",
                maxWidth: 560,
              }}
            >
              <div className="modalHead">
                <div className="cardTitle">NKR Token</div>
                <button className="iconBtn" type="button" onClick={() => setNkrTokenInfoOpen(false)}>×</button>
              </div>
              <div className="helpBody">
                <Help
                  de={
                    <>
                      <p><b>NKR</b> ist der Token von Nexus Analyt. Du kaufst ihn auf Uniswap (Pool NKR/WETH auf Ethereum).</p>
                      <p><b>Geschlossener Kreis:</b> Ein Teil der System-Gebühren (2 % vom Gewinn) geht in den Liquidity-Vault. Ab ca. 100–1000 $ wird daraus wieder Liquidität in denselben, gesperrten Pool gelegt.</p>
                      <p>Die Uniswap-Position (NFT) liegt im Vault. Niemand kann die LP abziehen — auch der Owner nicht.</p>
                      <p>Nexus Core / Strategist zahlst du vorerst in USDC/USDT. NKR als Zahlung kommt erst, wenn der Token-Preis dafür reicht.</p>
                      <p>NKR der Token ist nicht dasselbe wie Nexus NKR der Trader. Der Trader verwaltet Sessions. Der Token trägt den Kreis.</p>
                    </>
                  }
                  en={
                    <>
                      <p><b>NKR</b> is the Nexus Analyt token. You buy it on Uniswap (NKR/WETH pool on Ethereum).</p>
                      <p><b>Closed loop:</b> Part of system fees (2% of profit) goes to the liquidity vault. From about $100–$1000 it is added back into the same locked pool.</p>
                      <p>The Uniswap position NFT sits in the vault. Nobody can pull the LP — including the owner.</p>
                      <p>Nexus Core / Strategist is paid in USDC/USDT for now. Paying with NKR comes later, once the price supports it.</p>
                      <p>The NKR token is not the Nexus NKR trader. The trader runs sessions. The token funds the loop.</p>
                    </>
                  }
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className={`dashboardGrid ${activePanel ? `hasFocus focus-${activePanel}` : ""}`}>
        {/* Compare */}
        <section className={`card section-compare dashboardPanel ${activePanel === "compare" ? "panelActive" : ""}`} onClick={handlePanelActivate("compare")}>
          <div className="cardHead">
            <div className="cardTitle">Compare (user-defined)</div>
            <div className="cardActions">
              {TIMEFRAMES.map((tf) => {
                const k = String(tf.key || "").toUpperCase();
                const is2y = k === "2Y";
                const is1d = k === "1D";
                const loading2y = is2y && pendingTf === "2Y";
                const disabled = (compareSymbols.length === 0) || loading2y;
                const title =
                  compareSymbols.length === 0
                    ? "Select coins first (Watchlist → Compare)"
                    : is2y
                      ? (has2YData
                          ? "2Y (self collected)"
                          : "2Y will unlock after enough history is collected. Click to try loading.")
                      : (is1d ? "1D may load intraday data" : "");

                return (
                  <button
                    key={tf.key}
                    className={`chip ${timeframe === tf.key ? "active" : ""}`}
                    onClick={() => {
                      if (disabled) return;
                      if (is2y) {
                        if (has2YData) {
                          setTimeframe("2Y");
                        } else {
                          setErrorMsg("");
                          setPendingTf("2Y");
                          fetchCompare({ force: true, fetchRangeOverride: "2Y" });
                        }
                        return;
                      }
                      setTimeframe(tf.key);
                    }}
                    disabled={disabled}
                    title={title}
                  >
                    {loading2y ? "2Y…" : tf.label}
                  </button>
                );
              })}

              <button className={`chip ${viewMode === "overlay" ? "active" : ""}`} onClick={() => setViewMode("overlay")}>Overlay</button>
              <button className={`chip ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}>Grid</button>

              <button className={`chip ${indexMode ? "active" : ""}`} onClick={() => setIndexMode((v) => !v)} title="Toggle Price / Index(100)">
                {indexMode ? "Index 100" : "Price"}
              </button>

              <InfoButton title="Compare">
                <Help showClose dismissable
                  de={
                    <>
                      <p><b>Was ist das?</b> Compare vergleicht bis zu 20 Coins aus deiner Watchlist-Compare-Auswahl.</p>
                      <p><b>Price</b> zeigt die echten Preiswerte. <b>Index 100</b> normalisiert alle Coins auf denselben Startwert 100 und ist besser, wenn viele Coins gleichzeitig verglichen werden.</p>
                      <p><b>Overlay</b> zeigt alle gewählten Coins in einem gemeinsamen Chart. <b>Grid</b> zeigt pro Coin eine kleine Kachel.</p>
                      <p><b>First 10 / Next 10 / All</b> steuert, ob du die ersten 10, die zweiten 10 oder alle Compare-Coins sehen willst.</p>
                      <p><b>Grid-Detail:</b> Klick auf eine Kachel öffnet den großen Chart. Dort kannst du direkt zwischen Price und Index 100 umschalten.</p>
                      <p><b>Legende:</b> Farbe → Coin. Klick auf einen Eintrag hebt einen Coin hervor.</p>
                      <p><b>Custom Weighting:</b> Wenn OFF aktiv ist, nutzt Compare die System-Gewichtung. Wenn ON aktiv ist, bleibt der Bereich trotzdem kompakt; über Settings kannst du die 5 Regler aufklappen. Die Summe kann nie über 100% gehen.</p>
                      <p><b>Pair-Liste:</b> zeigt die stärksten Coin-Paare aus den aktuellen Compare-Daten.</p>
                      <ul>
                        <li><b>Pair Score</b> → Stärke der Pair-Struktur</li>
                        <li><b>Spread</b> → Performance-Abstand zwischen zwei Coins</li>
                        <li><b>Δ</b> → Stärke-Abweichung innerhalb des Pairs</li>
                        <li><b>⚡ Movement</b> → ungewöhnliche Bewegung / erhöhte Aktivität, kein Buy-Signal</li>
                      </ul>
                      <p><b>Klick auf ein Pair</b> öffnet Details wie Performance, tägliche Moves, Spread und Erklärung.</p>
                      <p><b>RSI (Relative Strength Index)</b> zeigt Momentum, nicht echtes Kaufvolumen.</p>
                      <ul>
                        <li><b>Overbought (Rot)</b> → stark gestiegen / eventuell überhitzt.</li>
                        <li><b>Neutral (Gelb)</b> → ausgeglichener Markt.</li>
                        <li><b>Oversold (Grün)</b> → stark gefallen / möglicher Rebound.</li>
                      </ul>
                      <p className="muted tiny">RSI zeigt Extreme, aber keine direkten Buy/Sell-Signale.</p>

                      <p><b>AI Insight (Klick auf ein Pair → AI Insight):</b></p>
                      <p>AI Insight ist die kompakte Markt-Interpretation für das gewählte Pair. Es ist Analyse und Orientierung — keine automatische Order und keine Finanzberatung.</p>
                      <ul>
                        <li><b>RSI &amp; Correlation:</b> Momentum und wie eng die beiden Coins zusammenlaufen.</li>
                        <li><b>Risk / Grid Fit:</b> Health Score und ob die Pair-Struktur eher für Grid oder vorsichtigen Einsatz passt.</li>
                        <li><b>Vol / Drawdown:</b> lokale Volatilität und Rücksetzer der beiden Seiten.</li>
                        <li><b>Longer-Term Reversion Idea:</b> z. B. „SELL X later-view“ / „BUY Y later-view“ — nur eine längerfristige Idee (Mean Reversion). Kein direkter Button und kein automatischer Trade.</li>
                        <li><b>Suggested Grid / Mode / Horizon:</b> grobe Hinweise zu Grid-Breite und Zeithorizont, falls du manuell planst.</li>
                        <li><b>Why this setup:</b> kurze Begründung (Korrelation, Spread, stärkere/schwächere Seite).</li>
                      </ul>
                      <p><b>Unterschied Strategist:</b> AI Insight erklärt das aktuelle Pair. Nexus Strategist ist der Arbeitsbereich für Research, Strategie, Backtest und längere Aufgaben.</p>
                      <p className="muted tiny">AI Insight erstellt keine Buy/Sell-Orders. Alle Entscheidungen bleiben bei dir.</p>
                    </>
                  }
                  en={
                    <>
                      <p><b>What is this?</b> Compare compares up to 20 coins from your Watchlist Compare selection.</p>
                      <p><b>Price</b> shows real price values. <b>Index 100</b> normalizes all coins to the same starting value of 100 and is better when many coins are compared at the same time.</p>
                      <p><b>Overlay</b> shows all selected coins in one shared chart. <b>Grid</b> shows a small tile for each coin.</p>
                      <p><b>First 10 / Next 10 / All</b> controls whether you see the first 10, the next 10, or all selected Compare coins.</p>
                      <p><b>Grid detail:</b> Click a tile to open the large chart. There you can switch directly between Price and Index 100.</p>
                      <p><b>Legend:</b> Color → Coin. Click a legend entry to highlight one coin.</p>
                      <p><b>Custom Weighting:</b> When OFF is active, Compare uses system weighting. When ON is active, the area stays compact; open Settings to adjust the 5 sliders. The total can never exceed 100%.</p>
                      <p><b>Pair list:</b> shows the strongest coin pairs from the current Compare data.</p>
                      <ul>
                        <li><b>Pair Score</b> → strength of the pair structure</li>
                        <li><b>Spread</b> → performance distance between two coins</li>
                        <li><b>Δ</b> → strength gap inside the pair</li>
                        <li><b>⚡ Movement</b> → unusual movement / elevated activity, not a buy signal</li>
                      </ul>
                      <p><b>Click a pair</b> to open details such as performance, daily moves, spread and explanation.</p>
                      <p><b>RSI (Relative Strength Index)</b> shows momentum, not actual buy volume.</p>
                      <ul>
                        <li><b>Overbought (Red)</b> → strong recent rise, may be overheated.</li>
                        <li><b>Neutral (Yellow)</b> → balanced market.</li>
                        <li><b>Oversold (Green)</b> → strong recent selling, possible rebound.</li>
                      </ul>
                      <p className="muted tiny">RSI indicates extremes, not direct buy/sell signals.</p>

                      <p><b>AI Insight (click a pair → AI Insight):</b></p>
                      <p>AI Insight is the compact market interpretation for the selected pair. It is analysis and orientation — not an automatic order and not financial advice.</p>
                      <ul>
                        <li><b>RSI &amp; Correlation:</b> momentum and how tightly the two coins move together.</li>
                        <li><b>Risk / Grid Fit:</b> health score and whether the pair structure is more suitable for grid-style or cautious use.</li>
                        <li><b>Vol / Drawdown:</b> local volatility and drawdown on both sides.</li>
                        <li><b>Longer-Term Reversion Idea:</b> e.g. “SELL X later-view” / “BUY Y later-view” — only a longer-term mean-reversion idea. No apply button and no automatic trade.</li>
                        <li><b>Suggested Grid / Mode / Horizon:</b> rough hints for grid width and time horizon if you plan manually.</li>
                        <li><b>Why this setup:</b> short rationale (correlation, spread, stronger/weaker side).</li>
                      </ul>
                      <p><b>Difference vs Strategist:</b> AI Insight explains the current pair. Nexus Strategist is the workspace for research, strategy, backtests, and longer tasks.</p>
                      <p className="muted tiny">AI Insight does not create buy/sell orders. All decisions remain yours.</p>
                    </>
                  }
                />
              </InfoButton>
            </div>
          </div>

          <div
            className="panelScroll"
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              paddingBottom: 14
            }}
          >
            <div
              style={{
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 16,
                padding: "8px 12px",
                marginBottom: 8,
                background: customCompareWeightsOn ? "rgba(57,217,138,.06)" : "rgba(255,255,255,.025)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div className="label" style={{ marginBottom: 0 }}>Compare Score Weighting</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="ghostBtn tiny"
                    onClick={() => {
                      setCustomCompareWeightsOn((v) => {
                        const next = !v;
                        if (!next) setCompareWeightsExpanded(false);
                        return next;
                      });
                    }}
                    title={customCompareWeightsOn ? "Disable custom weighting and use system values" : "Enable custom weighting"}
                    style={{
                      background: "rgba(255,255,255,.025)",
                      border: "1px solid rgba(255,255,255,.10)",
                      color: "rgba(255,255,255,.72)",
                      boxShadow: "none"
                    }}
                  >
                    Custom Weighting:{" "}
                    <span
                      style={{
                        color: customCompareWeightsOn ? "#22c55e" : "#ef4444",
                        fontWeight: 800
                      }}
                    >
                      {customCompareWeightsOn ? "ON" : "OFF"}
                    </span>
                  </button>
                  {customCompareWeightsOn && (
                    <button
                      className="ghostBtn tiny"
                      onClick={() => setCompareWeightsExpanded((v) => !v)}
                      title={compareWeightsExpanded ? "Hide weighting settings" : "Open weighting settings"}
                      style={{
                        background: "rgba(255,255,255,.025)",
                        border: "1px solid rgba(255,255,255,.10)",
                        color: "rgba(255,255,255,.72)",
                        boxShadow: "none"
                      }}
                    >
                      Settings {compareWeightsExpanded ? "▲" : "▼"}
                    </button>
                  )}
                </div>
              </div>

              {customCompareWeightsOn && compareWeightsExpanded && (
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
                  <div className="muted tiny" style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>Total {compareWeightsTotal}% · Remaining {compareWeightsRemaining}%</span>
                    <button className="ghostBtn tiny" onClick={resetCompareWeights} title="Reset to system default weights">
                      Reset
                    </button>
                  </div>
                  {COMPARE_WEIGHT_KEYS.map((key) => {
                    const current = Number(safeCompareWeights[key] || 0);
                    const otherTotal = COMPARE_WEIGHT_KEYS.filter((k) => k !== key).reduce((sum, k) => sum + Number(safeCompareWeights[k] || 0), 0);
                    const maxAllowed = Math.max(0, 100 - otherTotal);
                    return (
                      <label key={key} style={{ display: "flex", flexDirection: "column", gap: 5 }} title={COMPARE_WEIGHT_HELP[key]}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span className="muted tiny" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{COMPARE_WEIGHT_LABELS[key]} <span title={COMPARE_WEIGHT_HELP[key]} style={{ opacity: 0.75, cursor: "help" }}>ⓘ</span></span>
                          <b className="tiny">{current}%</b>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={maxAllowed}
                          step="1"
                          value={current}
                          onChange={(e) => updateCompareWeight(key, e.target.value)}
                        />
                        <div className="muted tiny">Max now: {maxAllowed}%</div>
                      </label>
                    );
                  })}
                  <div className="muted tiny" style={{ gridColumn: "1 / -1" }}>
                    The app does not auto-distribute the rest. You decide the full 100% manually. Scores use system values when Custom Weighting is OFF.
                  </div>
                </div>
              )}
            </div>

			<div className="compareGrid">
            {/* Chart */}

            <div className="compareChart" style={{ minHeight: 0, overflow: "visible" }}>
              <div className="chartHeader">
                <div className="label"></div>
                <div className="rowBtn">
                  <button className={`chip ${comparePage === "first10" ? "active" : ""}`} onClick={() => setComparePage("first10")} disabled={compareSymbols.length === 0}>First 10</button>
                  <button className={`chip ${comparePage === "next10" ? "active" : ""}`} onClick={() => setComparePage("next10")} disabled={compareSymbols.length <= 10}>Next 10</button>
                  <button className={`chip ${comparePage === "all" ? "active" : ""}`} onClick={() => setComparePage("all")} disabled={compareSymbols.length === 0}>All</button>
                </div>
                <div className="muted tiny">{compareLoading ? "Loading…" : `${visibleHighlightedSyms.length || visibleCompareSymbols.length}/${visibleCompareSymbols.length} active`}</div>
              </div>

              {viewMode === "overlay" ? (
              <>
                {visibleCompareSymbols.length === 0 ? (
                  <div className="chartEmpty" style={{ minHeight: 240 }}>
                    <div className="muted">No coins in this compare range.</div>
                  </div>
                ) : (
                  <>
                    <SvgChart chart={chartRaw} height={movementPanelOpen ? 205 : 255} highlightedSyms={visibleHighlightedSyms} onHoverSym={() => {}} indexMode={indexMode} timeframe={timeframe} colorForSym={colorForSym} lineClassForSym={lineClassForSym} />
                    <div style={{ marginTop: 6 }}>
                      <Legend symbols={visibleCompareSymbols} highlightedSyms={visibleHighlightedSyms} setHighlightedSyms={setHighlightedSyms} colorForSym={colorForSym} lineClassForSym={lineClassForSym} />
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="sparkGridWrap">
                {visibleCompareSymbols.length === 0 ? (
                  <div className="chartEmpty" style={{ minHeight: 220 }}>
                    <div className="muted">No coins in this compare range.</div>
                  </div>
                ) : (
                <div className="sparkGrid">
                  {visibleCompareSymbols.map((sym, idx) => (
                    <SmallSpark
                      key={sym}
                      sym={sym}
                      idx={idx}
                      chart={chartRaw}
                      indexMode={indexMode}
                      timeframe={timeframe}
                      active={visibleHighlightedSyms.length === 0 || visibleHighlightedSyms.includes(sym)}
                      onClick={() => setGridModalSym(sym)}
                      colorForSym={colorForSym}
                      lineClassForSym={lineClassForSym}
                    />
                  ))}
                </div>
                )}
                <div className="muted tiny" style={{ marginTop: 8 }}>
                  Tip: Use <b>Overlay</b> for correlation. Use <b>Grid</b> for 10 coins (clear view).
                </div>
              </div>
            )}

                            <div
                              className="pairsBox"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0
                              }}
                            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <button
                      className={String(bestPairsSortMode || "score") === "spread" ? "btn tiny" : "ghostBtn tiny"}
                      title="Sort by largest 30D spread first. Click again to return to score ranking."
                      onClick={() => toggleBestPairsSort("spread")}
                    >
                      Spread
                    </button>
                    <button
                      className={movementPanelOpen ? "btn tiny" : "ghostBtn tiny"}
                      title="Open Movement Opportunities. This does not change the main pair ranking."
                      onClick={() => setMovementPanelOpen((v) => !v)}
                    >
                      ⚡ Movement{bestPairUiAlerts.length ? ` (${bestPairUiAlerts.length})` : ""}
                    </button>
                    <button className="ghostBtn tiny" onClick={() => setShowTop10Pairs(v => !v)}>
                      {showTop10Pairs ? "Show all pairs" : "Show top 10"}
                    </button>
                  </div>
                </div>

                
                {movementPanelOpen && bestPairUiAlerts.length ? (
                  <div style={{
                    border: "1px solid rgba(255,184,0,.14)",
                    borderRadius: 12,
                    background: "rgba(255,184,0,.04)",
                    padding: "8px 10px",
                    marginBottom: 10,
                    height: 270,
                    maxHeight: 270,
                    minHeight: 270,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      marginBottom: 8
                    }}>
                      <div>
                        <div className="label" style={{ marginBottom: 2 }}>⚡ Movement Opportunities ({bestPairUiAlerts.length})</div>
                        <div className="muted tiny">
                          Discovery-only setups. Main pair ranking stays purely data-fit based.
                        </div>
                      </div>

                      <button
                        className="ghostBtn tiny"
                        onClick={() => setMovementPanelOpen(false)}
                      >
                        Close
                      </button>
                    </div>

                    <div style={{
                      display: "grid",
                      gap: 5,
                      height: 205,
                      maxHeight: 205,
                      minHeight: 205,
                      overflowY: "auto",
                      paddingRight: 4,
                      paddingBottom: 6,
                      alignContent: "start"
                    }}>
                      {(bestPairUiAlerts || [])
                        .map((al) => {
                          const match = (bestPairsAll || []).find(
                            (p) => String(p?.pair || "").toUpperCase() === String(al?.pair || "").toUpperCase()
                          );

                          return (
                            <button
                              key={`${al.pair}-${al.type}`}
                              type="button"
                              className="ghostBtn tiny"
                              onClick={() => {
                                if (match) openPairExplain(match);
                              }}
                              style={{
                                justifyContent: "flex-start",
                                textAlign: "left",
                                padding: "6px 8px",
                                opacity: 0.9
                              }}
                              title={`Open movement opportunity${al.source_rank ? ` · source rank #${al.source_rank}` : ""}${Number.isFinite(Number(al.score)) ? ` · score ${Number(al.score).toFixed(1)}` : ""}`}
                            >
                              ⚡ <b>{al.pair}</b>
                              <span className="muted tiny" style={{ marginLeft: 8 }}>
                                {aiTagLabel(al.type)}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ) : null}


                <div
                  className="pairsScroll"
                  style={{
                    border: "1px solid rgba(255,255,255,.06)",
                    borderRadius: 14,
                    background: "rgba(255,255,255,.02)",
                    boxSizing: "border-box",
                    marginTop: movementPanelOpen ? 0 : 6,

                    minHeight: 0,
                    maxHeight: movementPanelOpen ? "0px" : "clamp(230px, 31vh, 360px)",
                    height: movementPanelOpen ? "0px" : "clamp(230px, 31vh, 360px)",
                    padding: movementPanelOpen ? "0px" : "8px",
                    borderWidth: movementPanelOpen ? 0 : 1,
                    opacity: movementPanelOpen ? 0 : 1,
                    pointerEvents: movementPanelOpen ? "none" : "auto",
                    overflowY: movementPanelOpen ? "hidden" : "auto",
                    overflowX: "hidden",
                    paddingBottom: movementPanelOpen ? 0 : 8,
                    scrollbarGutter: "stable",
                  }}
                >
                  {bestPairsToShow.length ? (
                    bestPairsToShow.map((p, i) => {
                      const rsiAState = _rsiBadgeState(p.rsiA);
                      const rsiBState = _rsiBadgeState(p.rsiB);
                      const rsiGapTone = Number.isFinite(p.rsiGap)
                        ? p.rsiGap >= 15
                          ? "rgba(57,217,138,0.14)"
                          : p.rsiGap >= 8
                            ? "rgba(255,184,0,0.14)"
                            : "rgba(255,255,255,0.06)"
                        : "rgba(255,255,255,0.06)";
                      const quality = _pairQualityMeta(p.score);
                      const movementPairKey = String(p?.pair || "").toUpperCase().trim();
                      const hasMovementChance = bestPairAlertPairSet.has(movementPairKey);
                      const movementTone = bestPairAlertToneByPair[movementPairKey] || "low";
                      const movementChanceScore = Number.isFinite(Number(p?.movement_chance_score ?? p?.movementChanceScore))
                        ? Number(p?.movement_chance_score ?? p?.movementChanceScore)
                        : Number(bestPairAlertScoreByPair[movementPairKey]);

                      return (
                        <div
                          key={p.pair}
                          className="pairRow"
                          style={{
                            gap: 8,
                            cursor: "pointer",
                            marginBottom: 0,
                            alignItems: "center",
                          }}
                          onClick={(e) => { e.stopPropagation(); openPairExplain(p); }}
                        >
                          <span
                            className="muted"
                            title={hasMovementChance ? `${p.pair} has a Movement Opportunity. Position is unchanged; movement does not affect ranking.` : undefined}
                            style={{
                              width: 34,
                              textAlign: "right",
                              flex: "0 0 34px",
                              color: hasMovementChance ? "#ffd166" : undefined,
                            }}
                          >#{i + 1}</span>

                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "grid",
                              gridTemplateColumns: "minmax(88px, 1.2fr) 120px 120px 56px 72px auto auto",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="pairName"
                              style={{
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                overflow: "hidden",
                              }}
                            >
                              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{p.pair}</span>
                            </span>

                            <span
                              className="pill"
                              title={`${p.a || p.pair.split("/")[0]} RSI: ${Number.isFinite(p.rsiA) ? p.rsiA.toFixed(0) : "—"} · ${Number.isFinite(p.rsiA) ? (p.rsiA >= 70 ? "Overbought: strong recent buying momentum, may be overextended." : p.rsiA <= 30 ? "Oversold: strong recent selling momentum, possible rebound zone." : "Neutral: balanced momentum.") : "No RSI data available."}`}
                              style={{
                                width: 120,
                                justifyContent: "center",
                                padding: "4px 8px",
                                fontSize: 12,
                                lineHeight: 1,
                                background: rsiAState.tone,
                                borderColor: rsiAState.border,
                                color: rsiAState.color,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.a || p.pair.split("/")[0]} {Number.isFinite(p.rsiA) ? p.rsiA.toFixed(0) : "—"} RSI
                            </span>

                            <span
                              className="pill"
                              title={`${p.b || p.pair.split("/")[1]} RSI: ${Number.isFinite(p.rsiB) ? p.rsiB.toFixed(0) : "—"} · ${Number.isFinite(p.rsiB) ? (p.rsiB >= 70 ? "Overbought: strong recent buying momentum, may be overextended." : p.rsiB <= 30 ? "Oversold: strong recent selling momentum, possible rebound zone." : "Neutral: balanced momentum.") : "No RSI data available."}`}
                              style={{
                                width: 120,
                                justifyContent: "center",
                                padding: "4px 8px",
                                fontSize: 12,
                                lineHeight: 1,
                                background: rsiBState.tone,
                                borderColor: rsiBState.border,
                                color: rsiBState.color,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.b || p.pair.split("/")[1]} {Number.isFinite(p.rsiB) ? p.rsiB.toFixed(0) : "—"} RSI
                            </span>

                            <span
                              className="pill"
                              title={`RSI gap: ${Number.isFinite(p.rsiGap) ? p.rsiGap.toFixed(0) : "—"} · Difference between both pair RSI values.`}
                              style={{
                                width: 56,
                                justifyContent: "center",
                                padding: "4px 8px",
                                fontSize: 12,
                                lineHeight: 1,
                                background: rsiGapTone,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Δ {Number.isFinite(p.rsiGap) ? p.rsiGap.toFixed(0) : "—"}
                            </span>

                            <span
                              className="pill"
                              title={`30D spread: ${Number.isFinite(p.spreadPct) ? p.spreadPct.toFixed(2) + "%" : "—"} · Larger values rank higher when Spread sorting is active.`}
                              style={{
                                width: 72,
                                justifyContent: "center",
                                padding: "4px 8px",
                                fontSize: 12,
                                lineHeight: 1,
                                background: String(bestPairsSortMode || "score") === "spread" ? "rgba(57,217,138,0.14)" : "rgba(255,255,255,0.06)",
                                borderColor: String(bestPairsSortMode || "score") === "spread" ? "rgba(57,217,138,0.28)" : "rgba(255,255,255,0.10)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              S {Number.isFinite(p.spreadPct) ? p.spreadPct.toFixed(1) + "%" : "—"}
                            </span>

                            <span
                              className="pill"
                              title={`Composite score: ${p.score} · Corr ${p.corrScore ?? "—"} · Momentum ${p.momentumScore ?? "—"} · Spread ${p.opportunityScore ?? "—"} · Stability ${p.stabilityScore ?? "—"}`}
                              style={{
                                justifySelf: "end",
                                whiteSpace: "nowrap",
                                fontSize: 12,
                                padding: "4px 8px",
                                lineHeight: 1,
                                background: quality.bg,
                                borderColor: quality.border,
                                color: quality.color
                              }}
                            >
                              {quality.label} {p.score}
                            </span>
                            {Number.isFinite(Number(p?.movement_quality_score)) ? (() => {
                              const mq = getMovementQualityUi(p.movement_quality_score);
                              return (
                                <span
                                  className="tiny"
                                  title={(Array.isArray(p?.movement_quality_reasons) ? p.movement_quality_reasons.join(", ") : p?.movement_rejection_reason) || "Movement Quality Filter v2"}
                                  style={{ border: `1px solid ${mq.border}`, borderRadius: 999, padding: "2px 6px", color: mq.color, fontWeight: 950 }}
                                >
                                  MQ {mq.label} {Math.round(Number(p.movement_quality_score))}
                                </span>
                              );
                            })() : null}
                            <span className="pill" title="Correlation of indexed price lines" style={{ justifySelf: "end", whiteSpace: "nowrap" }}>{(p.corr >= 0 ? "+" : "") + p.corr.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="muted">Not enough chart data yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div></div>
        </section>

        {/* Grid chart modal */}
        {gridModalSym && (
          <div
            className="modalBackdrop"
            onClick={() => setGridModalSym(null)}
            style={{ zIndex: 10010, padding: 16 }}
          >
            <div
              className="card"
              style={{
                width: "min(1100px, 96vw)",
                maxHeight: "88vh",
                overflow: "auto",
                cursor: "default",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cardHead" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div>
                  <div className="cardTitle">{gridModalSym} chart</div>
                  <div className="muted tiny" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                    <span>{timeframe}</span>
                    <span>{indexMode ? "Index 100" : "Price (USD)"}</span>
                    {gridModalRow ? <span>{fmtUsd(Number(gridModalRow?.price))}</span> : null}
                    {gridModalRow?.change24h != null ? (
                      <span style={{ color: Number(gridModalRow?.change24h) >= 0 ? "var(--green)" : "var(--red)" }}>
                        {fmtPct(Number(gridModalRow?.change24h))}
                      </span>
                    ) : null}
                    <span>Hover for date + value</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    className={`chip ${!indexMode ? "active" : ""}`}
                    onClick={() => setIndexMode(false)}
                    type="button"
                    title="Show real price chart"
                  >
                    Price
                  </button>
                  <button
                    className={`chip ${indexMode ? "active" : ""}`}
                    onClick={() => setIndexMode(true)}
                    type="button"
                    title="Show Index 100 chart"
                  >
                    Index 100
                  </button>
                  <button className="btnGhost" onClick={() => setGridModalSym(null)} type="button">
                    Close
                  </button>
                </div>
              </div>

              <div className="cardBody" style={{ display: "grid", gap: 12 }}>
                <SvgChart
                  chart={gridModalChart}
                  height={440}
                  highlightedSyms={[]}
                  onHoverSym={() => {}}
                  indexMode={indexMode}
                  timeframe={timeframe}
                  colorForSym={colorForSym}
                  lineClassForSym={lineClassForSym}
                />
                <div className="muted tiny">
                  Tip: Move the mouse over the chart to see the exact date and value.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pair explain modal */}
        {selectedPair && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: 16,
            }}
            onClick={() => setSelectedPair(null)}
          >
            <div
              className="card"
              style={{
                maxWidth: 760,
                width: "100%",
                maxHeight: "80vh",
                overflow: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cardHead" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div className="cardTitle">Why this score?</div>
                  <div className="muted tiny">
                    {selectedPair.pair} · 30D · Score {selectedPair.score} · Corr{" "}
                    {(selectedPair.corr >= 0 ? "+" : "") + selectedPair.corr.toFixed(2)}
                  </div>
                </div>
                <button className="btnGhost" onClick={() => setSelectedPair(null)}>
                  Close
                </button>
              </div>

              <div className="cardBody" style={{ display: "grid", gap: 14 }}>{(() => {
                  const [a, b] = _pairSyms(selectedPair.pair);
                  const ra = _retPctForExplain(a);
                  const rb = _retPctForExplain(b);
                  const spread = Number.isFinite(ra) && Number.isFinite(rb) ? ra - rb : null;
                  const winner = Number.isFinite(spread) ? (spread >= 0 ? a : b) : null;
                  const loser = Number.isFinite(spread) ? (spread >= 0 ? b : a) : null;

                  return (
                    <>
                      <div style={{ display: "grid", gap: 8 }}>
                        <div className="label">Performance (same period)</div>
                        {pairExplainLoading && <div className="muted tiny">Loading pair data…</div>}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <span className="pill silver">{a}: <span style={_pctColorStyle(ra)}>{_fmtPctLocal(ra)}</span></span>
                          <span className="pill silver">{b}: <span style={_pctColorStyle(rb)}>{_fmtPctLocal(rb)}</span></span>
                          <span className="pill">
                            Spread: {_fmtPctLocal(spread)} {winner ? `( ${winner} vs ${loser} )` : ""}
                          </span>
                        </div>


                        {/* Trader quick spread view */}
                        {(() => {
                          const rows = _buildDailyRows(pairExplainSeries, a, b, 30);
                          if (!rows.length) return null;

                          const spreads = rows
                            .map((r) => Number(r?.d))
                            .filter((v) => Number.isFinite(v));
                          if (!spreads.length) return null;

                          const latestSpreadQuick = spreads[spreads.length - 1];
                          const svgWq = 520;
                          const svgHq = 120;
                          const padLq = 38;
                          const padRq = 18;
                          const padTq = 10;
                          const padBq = 22;
                          const innerWq = Math.max(1, svgWq - padLq - padRq);
                          const innerHq = Math.max(1, svgHq - padTq - padBq);
                          const maxAbsQ = Math.max(1, ...spreads.map((v) => Math.abs(v)));
                          const yMinQ = -maxAbsQ;
                          const yMaxQ = maxAbsQ;
                          const sxQ = (i) => padLq + (rows.length <= 1 ? 0 : (i / (rows.length - 1)) * innerWq);
                          const syQ = (v) => padTq + ((yMaxQ - v) / Math.max(0.000001, yMaxQ - yMinQ)) * innerHq;
                          const pathDQ = spreads.map((v, i) => `${i === 0 ? "M" : "L"} ${sxQ(i)} ${syQ(v)}`).join(" ");
                          const latestXQ = sxQ(spreads.length - 1);
                          const latestYQ = syQ(latestSpreadQuick);
                          const zeroYQ = syQ(0);

                          return (
                            <div style={{ display: "grid", gap: 6, marginTop: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px", background: "rgba(255,255,255,0.02)" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <div>
                                  <div className="label" style={{ marginBottom: 0 }}>Spread Analysis</div>
                                  <div className="muted tiny" style={{ marginTop: 2 }}>30D Relative Spread ({a} vs {b}) · Positive = {a} stronger · Negative = {b} stronger</div>
                                </div>
                                <span className="pill silver">Latest: {_fmtPctLocal(latestSpreadQuick)}</span>
                              </div>

                              <svg viewBox={`0 0 ${svgWq} ${svgHq}`} preserveAspectRatio="none" style={{ width: "100%", height: 125, display: "block" }}>
                                {[yMinQ, 0, yMaxQ].map((tick, idx) => (
                                  <g key={idx}>
                                    <line x1={padLq} x2={svgWq - padRq} y1={syQ(tick)} y2={syQ(tick)} stroke={tick === 0 ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)"} strokeDasharray={tick === 0 ? "4 4" : "3 5"} />
                                    <text x={6} y={syQ(tick) + 4} fill="rgba(232,242,240,0.88)" fontSize="11" fontWeight="700">{_fmtPctLocal(tick)}</text>
                                  </g>
                                ))}
                                <path d={pathDQ} fill="none" stroke="#35e0a1" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                                {Number.isFinite(latestSpreadQuick) ? (
                                  <>
                                    <circle cx={latestXQ} cy={latestYQ} r="4" fill="#35e0a1" />

                                  </>
                                ) : null}
                                <text x={padLq} y={svgHq - 6} fill="rgba(232,242,240,0.72)" fontSize="10">Start</text>
                                <text x={padLq + (innerWq / 2)} y={svgHq - 6} textAnchor="middle" fill="rgba(232,242,240,0.72)" fontSize="10">Mid</text>
                                <text x={svgWq - padRq} y={svgHq - 6} textAnchor="end" fill="rgba(232,242,240,0.72)" fontSize="10">Now</text>
                                <text x={svgWq - padRq} y={Math.max(13, zeroYQ - 5)} textAnchor="end" fill="rgba(232,242,240,0.58)" fontSize="9">Mean reversion line</text>
                              </svg>
                            </div>
                          );
                        })()}

                        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                          <div className="label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Daily moves (last {30} days)</span>
                            <span className="muted tiny">{selectedPair.pair}</span>
                          </div>

                          {(() => {
                            const [a, b] = _pairSyms(selectedPair.pair);
                            const maxDays = 30;
                            const rows = _buildDailyRows(pairExplainSeries, a, b, maxDays);
                            if (!rows.length) return <div className="muted tiny">No daily series yet.</div>;

                            return (
                              <div style={{ border: "none", borderRadius: 12, overflow: "hidden" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr 1fr", gap: 8, padding: "8px 10px", background: "rgba(255,255,255,0.04)" }} className="muted tiny">
                                  <div>Date</div>
                                  <div>{a}</div>
                                  <div>{b}</div>
                                  <div>Diff</div>
                                </div>
                                <div style={{ maxHeight: 180, overflow: "auto" }}>
                                  {rows.slice().reverse().map((r, i) => (
                                    <div key={i} style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr 1fr", gap: 8, padding: "7px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }} className="tiny">
                                      <div className="muted">{_fmtDay(r.t)}</div>
                                      <div style={_pctColorStyle(r.a)}>{_fmtPctLocal(r.a)}</div>
                                      <div style={_pctColorStyle(r.b)}>{_fmtPctLocal(r.b)}</div>
                                      <div>{_fmtPctLocal(r.d)}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="muted tiny">
                          Score now combines correlation, RSI gap, 30D spread and volatility balance. A higher score means the pair is more useful for grid/rebalance ideas, not just more correlated.
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <div className="label">Short-Term Momentum</div>
                        {winner ? (() => {
                          const quality = _pairQualityMeta(selectedPair?.score);

                          return (
                            <div style={{
                              display: "grid",
                              gap: 8,
                              padding: "12px",
                              borderRadius: "12px",
                              background: quality.bg,
                              border: `1px solid ${quality.border}`
                            }}>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                                <span style={{ color: quality.color, fontWeight: 900 }}>{quality.label}</span>
                                <span className="pill silver">Score {selectedPair.score}</span>
                              </div>
                              <div className="muted">
                                <b>Now:</b> <b>{winner}</b> &gt; <b>{loser}</b> (short-term momentum / 30D view).
                              </div>
                              <div className="tiny" style={{ display: "flex", flexWrap: "wrap", gap: 8, color: quality.color }}>
                                <span>Corr {(selectedPair.corr >= 0 ? "+" : "") + selectedPair.corr.toFixed(2)}</span>
                                <span>Spread {_fmtPctLocal(spread)}</span>
                                {Number.isFinite(selectedPair?.momentumScore) && <span>Momentum {selectedPair.momentumScore}</span>}
                                {Number.isFinite(selectedPair?.stabilityScore) && <span>Stability {selectedPair.stabilityScore}</span>}
                              </div>
                            </div>
                          );
                        })() : (
                          <div className="muted">Not enough data for a reliable pair summary yet.</div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {selectedPairLocalAlerts.length ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 8,
                      border: selectedPairAlertTone === "high" ? "1px solid rgba(255,184,0,0.42)" : "1px solid rgba(255,193,7,0.26)",
                      borderRadius: 12,
                      padding: "12px",
                      background: selectedPairAlertTone === "high" ? "rgba(255,184,0,0.10)" : "rgba(255,193,7,0.06)",
                      boxShadow: selectedPairAlertTone === "high" ? "0 0 22px rgba(255,184,0,0.08)" : "none",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <div className="muted tiny" style={{ color: "#ffd166", fontWeight: 900 }}>AI Movement Alert Preview</div>
                        <div style={{ fontWeight: 900, marginTop: 4 }}>
                          Hey — {selectedPairMainAlert?.pair || selectedPair?.pair} shows unusual movement pressure.
                        </div>
                      </div>
                      <span
                        className="pill"
                        style={{
                          background: selectedPairAlertTone === "high" ? "rgba(255,184,0,0.16)" : "rgba(255,255,255,0.05)",
                          borderColor: selectedPairAlertTone === "high" ? "rgba(255,184,0,0.55)" : "rgba(255,193,7,0.28)",
                          color: "#ffd166",
                          fontWeight: 900,
                        }}
                      >
                        {String(selectedPairMainAlert?.strength || "watch").toUpperCase()}
                      </span>
                    </div>
                    <div className="muted" style={{ lineHeight: 1.5 }}>
                      This pair is not necessarily a clean top setup, but the scanner found {selectedPairLocalAlerts.map((a) => (a.reasons || []).join(" / ")).filter(Boolean).join(" · ") || "spread, RSI or momentum anomalies"}.
                      Even weak or lower-ranked pairs can create short-term profit chances when volatility, spread movement, RSI divergence or rebound pressure increases. Risk stays elevated, so this should be treated as a movement signal, not a quality recommendation.
                    </div>
                    <div className="muted tiny" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {selectedPairMainAlert?.spread_pct != null ? <span>Spread {Number(selectedPairMainAlert.spread_pct).toFixed(1)}%</span> : null}
                      {selectedPairMainAlert?.rsi_gap != null ? <span>RSI gap {Number(selectedPairMainAlert.rsi_gap).toFixed(0)}</span> : null}
                      {selectedPairMainAlert?.corr != null ? <span>Corr {Number(selectedPairMainAlert.corr).toFixed(2)}</span> : null}
                      <span>Open AI Insight for the full explanation before taking action.</span>
                    </div>
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div className="label">AI commentary (optional)</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <button
                        className={normalizedAiInsightMode === "extreme" ? "btn" : "btnGhost"}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setAiInsightMode(normalizedAiInsightMode === "extreme" ? "standard" : "extreme");
                        }}
                        title="Toggle AI Insight between Standard and Extreme mode"
                      >
                        {normalizedAiInsightMode === "extreme" ? "Extreme ON" : "Standard AI"}
                      </button>
                    <button
                      className="btnGhost"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        runAiExplain();
                      }}
                      disabled={aiExplainLoading}
                      title={!isPro ? "Redeem or active access required to use AI" : "Run AI Insight"}
                    >
                      {aiExplainLoading ? "Thinking…" : (isPro ? "AI Insight" : "Access required")}
                    </button>
                    </div>
                  </div>
                  {aiExplainData ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                        <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                          <div className="muted tiny">AI Verdict</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData.setup}</div>
                        </div>
                        <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                          <div className="muted tiny">Confidence</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData.confidenceLabel} ({aiExplainData.confidence}/10)</div>
                        </div>
                        <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                          <div className="muted tiny">Risk</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData.risk}</div>
                        </div>
                        <div style={{
                          border: `1px solid ${aiExplainData.aiMode === "extreme" ? "rgba(255,184,0,0.35)" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: 12,
                          padding: "8px 10px",
                          background: aiExplainData.aiMode === "extreme" ? "rgba(255,184,0,0.08)" : "rgba(255,255,255,0.03)"
                        }}>
                          <div className="muted tiny">Insight Mode</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData.effectiveMode || (aiExplainData.aiMode === "extreme" ? "Extreme" : "Standard")}</div>
                        </div>
                        <div style={{
                          border: `1px solid ${aiExplainData?.adaptiveThresholds?.customOn ? "rgba(34,197,94,0.32)" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: 12,
                          padding: "8px 10px",
                          background: aiExplainData?.adaptiveThresholds?.customOn ? "rgba(34,197,94,0.07)" : "rgba(255,255,255,0.03)"
                        }}>
                          <div className="muted tiny">Adaptive Lens</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData?.adaptiveProfile || "Balanced adaptive"}</div>
                        </div>
                        <div style={{
                          border: `1px solid ${aiExplainData?.strategistMemory?.used ? "rgba(96,165,250,0.32)" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: 12,
                          padding: "8px 10px",
                          background: aiExplainData?.strategistMemory?.used ? "rgba(96,165,250,0.07)" : "rgba(255,255,255,0.03)"
                        }}>
                          <div className="muted tiny">Strategist Memory</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData?.strategistMemory?.used ? (aiExplainData?.strategistMemory?.bias || "active") : "learning"}</div>
                        </div>
                        <div style={{
                          border: `1px solid ${aiExplainData?.aiInsightBridge?.used ? "rgba(168,85,247,0.32)" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: 12,
                          padding: "8px 10px",
                          background: aiExplainData?.aiInsightBridge?.used ? "rgba(168,85,247,0.07)" : "rgba(255,255,255,0.03)"
                        }}>
                          <div className="muted tiny">AI Insight Bridge</div>
                          <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData?.aiInsightBridge?.used ? (aiExplainData?.aiInsightBridge?.bias || "active") : "learning"}</div>
                        </div>
                      </div>

                      {(() => {
                        const aiSections = _parseAiConclusionSections(aiExplainData);
                        const hasSections = Object.values(aiSections).some(Boolean);
                        return (
                          <div style={{ display: "grid", gap: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                            <div className="label" style={{ marginBottom: 0 }}>AI Conclusion</div>
                            {hasSections ? (
                              <div style={{ display: "grid", gap: 8 }}>
                                <AiInsightCard title="Market Structure" value={aiSections.market_structure} tone="structure" />
                                <AiInsightCard title="Liquidity State" value={aiSections.liquidity_state} tone="liquidity" />
                                <AiInsightCard title="Risk Posture" value={aiSections.risk_posture} tone="risk" />
                                <AiInsightCard title="Tactical Read" value={aiSections.tactical_read} tone="tactical" />
                              </div>
                            ) : (
                              <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.4 }}>
                                {aiExplainData.verdictText || "No clear AI conclusion available yet."}
                              </div>
                            )}
                            <div className="muted tiny" style={{ lineHeight: 1.5 }}>
                              {aiExplainData.winner && aiExplainData.loser
                                ? `Observed relative bias: ${aiExplainData.winner} is currently stronger than ${aiExplainData.loser} in this read.`
                                : "This conclusion is descriptive only and should not be read as an automatic trade command."}
                            </div>
                          </div>
                        );
                      })()}

                      {(aiExplainData.trendStructure || aiExplainData.momentumShift || aiExplainData.insightSummary) ? (
                        <div style={{ display: "grid", gap: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                          <div className="label" style={{ marginBottom: 0 }}>Multi-timeframe AI Insight</div>
                          {aiExplainData.trendStructure ? (
                            <div><span className="muted tiny">Trend Structure</span><div style={{ fontWeight: 800, marginTop: 4 }}>{aiExplainData.trendStructure}</div></div>
                          ) : null}
                          {aiExplainData.momentumShift ? (
                            <div><span className="muted tiny">Momentum Shift</span><div style={{ marginTop: 4 }}>{aiExplainData.momentumShift}</div></div>
                          ) : null}
                          {aiExplainData.insightSummary ? (
                            <div><span className="muted tiny">Insight Summary</span><div style={{ marginTop: 4 }}>{aiExplainData.insightSummary}</div></div>
                          ) : null}
                        </div>
                      ) : null}

                      {(() => {
                        const pairLabel = String(selectedPair?.pair || "");
                        const [a, b] = _pairSyms(pairLabel);
                        const rows = _buildDailyRows(pairExplainSeries || {}, a, b, 30);
                        const latestSpread = rows.length ? Number(rows[rows.length - 1]?.d) : null;
                        const prevSpread = rows.length > 1 ? Number(rows[rows.length - 2]?.d) : null;
                        const spreadDirection = Number.isFinite(latestSpread) && Number.isFinite(prevSpread)
                          ? (latestSpread < prevSpread ? "Falling" : latestSpread > prevSpread ? "Rising" : "Flat")
                          : "n/a";
                        const reading = Number.isFinite(latestSpread)
                          ? (latestSpread > 0 ? `${a} stronger` : latestSpread < 0 ? `${b} stronger` : "Balanced pair")
                          : "n/a";
                        const corrVal = Number(aiExplainData?.corr ?? selectedPair?.corr);
                        const corrText = Number.isFinite(corrVal) ? `${corrVal >= 0 ? "+" : ""}${corrVal.toFixed(2)}` : "—";
                        const windows = aiExplainData?.windows || {};
                        const st30A = windows?.["30D"]?.[a] || _seriesStatsFallback((pairExplainSeries || {})[a]);
                        const st30B = windows?.["30D"]?.[b] || _seriesStatsFallback((pairExplainSeries || {})[b]);
                        const rsiA = _calcSimpleRsi((pairExplainSeries || {})[a], 14);
                        const rsiB = _calcSimpleRsi((pairExplainSeries || {})[b], 14);
                        const rsiAState = _rsiBadgeState(rsiA);
                        const rsiBState = _rsiBadgeState(rsiB);
                        const healthScore = Math.max(0, Math.min(100,
                          Math.round((Number.isFinite(corrVal) ? Math.abs(corrVal) * 55 : 25) + (Number.isFinite(latestSpread) ? Math.min(30, Math.abs(latestSpread) * 3) : 0) + (String(aiExplainData?.setup || "").includes("MEAN") ? 15 : String(aiExplainData?.setup || "").includes("TREND") ? 8 : 0))
                        ));
                        const gridFit = String(aiExplainData?.gridMode || "").toLowerCase() === "wait"
                          ? "Weak"
                          : healthScore >= 75 ? "Strong" : healthScore >= 55 ? "Moderate" : "Weak";
                        const svgW = 760;
                        const svgH = 180;
                        const padL = 46;
                        const padR = 8;
                        const padT = 10;
                        const padB = 24;
                        const innerW = svgW - padL - padR;
                        const innerH = svgH - padT - padB;
                        const dVals = rows.map((r) => Number(r?.d)).filter((v) => Number.isFinite(v));
                        const maxAbs = dVals.length ? Math.max(6, ...dVals.map((v) => Math.abs(v))) : 6;
                        const yMax = Math.ceil(maxAbs / 3) * 3;
                        const yMin = -yMax;
                        const sx = (i) => padL + ((i * innerW) / Math.max(1, rows.length - 1));
                        const sy = (v) => {
                          const t = (Number(v) - yMin) / Math.max(1e-9, (yMax - yMin));
                          return padT + (1 - t) * innerH;
                        };
                        let pathD = "";
                        rows.forEach((r, i) => {
                          const v = Number(r?.d);
                          if (!Number.isFinite(v)) return;
                          const X = sx(i);
                          const Y = sy(v);
                          pathD += pathD ? ` L ${X} ${Y}` : `M ${X} ${Y}`;
                        });
                        const zeroY = sy(0);
                        const latestX = rows.length ? sx(rows.length - 1) : padL;
                        const latestY = Number.isFinite(latestSpread) ? sy(latestSpread) : zeroY;
                        return (
                          <>
                            <div style={{ display: "grid", gap: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                              <div className="label" style={{ marginBottom: 0 }}>RSI / Pair State</div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: rsiAState.tone }}>
                                  <div className="muted tiny">{a} RSI</div>
                                  <div style={{ fontWeight: 900, marginTop: 4 }}>{Number.isFinite(rsiA) ? rsiA.toFixed(1) : "—"}</div>
                                  <div className="pill" style={{ marginTop: 8, width: "fit-content", background: rsiAState.tone, borderColor: rsiAState.border, color: rsiAState.color }}>{rsiAState.label}</div>
                                </div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: rsiBState.tone }}>
                                  <div className="muted tiny">{b} RSI</div>
                                  <div style={{ fontWeight: 900, marginTop: 4 }}>{Number.isFinite(rsiB) ? rsiB.toFixed(1) : "—"}</div>
                                  <div className="pill" style={{ marginTop: 8, width: "fit-content", background: rsiBState.tone, borderColor: rsiBState.border, color: rsiBState.color }}>{rsiBState.label}</div>
                                </div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                                  <div className="muted tiny">Correlation</div>
                                  <div style={{ fontWeight: 900, marginTop: 4 }}>{corrText}</div>
                                  <div className="muted tiny" style={{ marginTop: 8 }}>{Number.isFinite(corrVal) ? (Math.abs(corrVal) >= 0.8 ? "High pair linkage" : Math.abs(corrVal) >= 0.6 ? "Moderate linkage" : "Low linkage") : "Pair linkage unclear"}</div>
                                </div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                                  <div className="muted tiny">30D Spread</div>
                                  <div style={{ fontWeight: 900, marginTop: 4 }}>{_fmtPctLocal(latestSpread)}</div>
                                  <div className="muted tiny" style={{ marginTop: 8 }}>{reading}</div>
                                </div>
                              </div>
                              <div className="muted tiny" style={{ lineHeight: 1.45 }}>RSI helps identify overbought vs oversold conditions. Correlation + spread show whether the pair is more suitable for trend continuation or mean reversion.</div>
                            </div>

                            <div style={{ display: "grid", gap: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                              <div className="label" style={{ marginBottom: 0 }}>Risk / Grid Fit</div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}><div className="muted tiny">Health Score</div><div style={{ fontWeight: 900, marginTop: 4 }}>{healthScore}/100</div></div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}><div className="muted tiny">Grid Fit</div><div style={{ fontWeight: 900, marginTop: 4 }}>{gridFit}</div></div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}><div className="muted tiny">{a} Vol / DD</div><div style={{ fontWeight: 900, marginTop: 4 }}>{_fmtPctLocal(st30A?.volPct)} / {_fmtPctLocal(st30A?.maxDDPct)}</div></div>
                                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}><div className="muted tiny">{b} Vol / DD</div><div style={{ fontWeight: 900, marginTop: 4 }}>{_fmtPctLocal(st30B?.volPct)} / {_fmtPctLocal(st30B?.maxDDPct)}</div></div>
                              </div>
                              <div className="muted tiny" style={{ lineHeight: 1.45 }}>This panel currently reflects local risk metrics based on volatility and drawdown.</div>
                            </div>


                          </>
                        );
                      })()}

                      <div style={{ display: "grid", gap: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                        <div className="label" style={{ marginBottom: 0 }}>Longer-Term Reversion Idea</div>

                        {aiExplainData.winner && aiExplainData.loser ? (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <span className="pill" style={{ background: "rgba(255,92,92,0.18)", borderColor: "rgba(255,92,92,0.35)" }}>SELL {aiExplainData.winner} later-view</span>
                            <span className="pill" style={{ background: "rgba(57,217,138,0.18)", borderColor: "rgba(57,217,138,0.35)" }}>BUY {aiExplainData.loser} later-view</span>
                          </div>
                        ) : null}

                        <div className="muted tiny" style={{ lineHeight: 1.45 }}>
                          Above is the <b>longer-term</b> idea. The box at the top shows <b>current momentum</b>. Both can differ.
                        </div>

                        <div style={{ display: "grid", gap: 6 }} className="muted">
                          {String(aiExplainData.action || "")
                            .split("\n")
                            .filter(Boolean)
                            .map((line, idx) => (
                              <div key={idx} style={{ fontSize: 14 }}>
                                {line}
                              </div>
                            ))}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                          <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                            <div className="muted tiny">Suggested Grid</div>
                            <div style={{ fontWeight: 900, marginTop: 4 }}>{aiExplainData.gridRange || aiExplainData.range || "—"}</div>
                          </div>
                          <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                            <div className="muted tiny">Mode</div>
                            <div style={{ fontWeight: 900, marginTop: 4 }}>
                              {aiExplainData.aiMode === "extreme" ? (aiExplainData.gridMode || "Extreme Scout") : (aiExplainData.gridMode || aiExplainData.mode || "—")}
                            </div>
                          </div>
                          <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)" }}>
                            <div className="muted tiny">Time horizon</div>
                            <div style={{ fontWeight: 900, marginTop: 4 }}>
                              {String(aiExplainData.setup || "").includes("MEAN")
                                ? "Short to medium term"
                                : String(aiExplainData.setup || "").includes("TREND")
                                  ? "Short-term tactical"
                                  : "Wait / monitor"}
                            </div>
                          </div>
                        </div>

                        {aiExplainData.winner && aiExplainData.loser ? (
                          <>
                            <div className="tiny" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <span className="pill silver">Stronger: <b>{aiExplainData.winner}</b></span>
                              <span className="pill silver">Weaker: <b>{aiExplainData.loser}</b></span>
                            </div>
                            <div className="muted tiny" style={{ marginTop: 2, lineHeight: 1.45 }}>
                              Strategy: Mean Reversion — the stronger coin may cool off while the weaker one catches up.
                            </div>
                          </>
                        ) : null}
                      </div>

                      <div style={{ display: "grid", gap: 6, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                        <div className="label" style={{ marginBottom: 0 }}>Why this setup</div>
                        <div className="muted tiny" style={{ display: "grid", gap: 4 }}>
                          {(Array.isArray(aiExplainData.why) ? aiExplainData.why : Array.isArray(aiExplainData.bullets) ? aiExplainData.bullets : []).map((line, idx) => (
                            <div key={idx}>• {line}</div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 6, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                        <div className="label" style={{ marginBottom: 0 }}>Invalidation</div>
                        <div className="muted tiny" style={{ display: "grid", gap: 4 }}>
                          {(() => {
                            const lines = [];
                            if (aiExplainData.winner && aiExplainData.loser) {
                              lines.push(`If ${aiExplainData.winner} keeps outperforming while ${aiExplainData.loser} stays weak, the catch-up idea weakens.`);
                            }
                            if (String(aiExplainData.gridMode || aiExplainData.mode || "").toLowerCase() === "wait") {
                              lines.push("If spread and correlation do not improve, there is still no valid entry.");
                            } else {
                              lines.push("If the spread widens sharply without any stabilization, reduce trust in mean reversion.");
                            }
                            lines.push("If short-term structure deteriorates further across 7D and 30D, the setup should be reassessed.");
                            return lines.map((line, idx) => <div key={idx}>• {line}</div>);
                          })()}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 6, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", background: "rgba(255,255,255,0.02)" }}>
                        <div className="label" style={{ marginBottom: 0 }}>Best for</div>
                        <div className="muted tiny" style={{ display: "grid", gap: 4 }}>
                          {(() => {
                            const lines = [];
                            const riskTxt = String(aiExplainData.risk || "").toLowerCase();
                            if (riskTxt.includes("high")) lines.push("Best suited for active users who can handle medium to high risk.");
                            else if (riskTxt.includes("medium")) lines.push("Best suited for users comfortable with moderate tactical positioning.");
                            else lines.push("Best suited for cautious users who prefer patience over aggressive entries.");
                            if (String(aiExplainData.gridMode || aiExplainData.mode || "").toLowerCase() === "wait") {
                              lines.push("Less suitable for users who want an immediate trade right now.");
                            } else {
                              lines.push("Better for users who understand pair rotation and short-term reversion setups.");
                            }
                            lines.push("Not ideal as a blind long-term allocation decision without rechecking the structure.");
                            return lines.map((line, idx) => <div key={idx}>• {line}</div>);
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : aiExplainText ? (
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }} className="muted tiny">
                      {aiExplainText}
                    </pre>
                  ) : (
                    <div className="muted tiny">Runs locally (no web). Later we can add “AI + News” with sources.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <section className={`card section-grid dashboardPanel ${activePanel === "vault" ? "panelActive" : ""}`} onClick={handlePanelActivate("vault")}>
          <div className="cardHead" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div className="cardTitle" style={{ flex: "0 0 auto" }}>{gridMode === "rotation" ? "Nexus NKR" : gridMode === "trading" ? "Nexus Trading" : "Nexus Grid"}</div>

            {!isCompactMobile && (
              <div
                className="gridModeSwitcher"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 8,
                  flex: "1 1 auto",
                  maxWidth: 760,
                  minWidth: 0,
                  padding: 4,
                  borderRadius: 14,
                  background: "rgba(255,255,255,.04)",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                {[
                  ["normal", "Nexus Grid"],
                  ["rotation", "Nexus NKR"],
                  ["trading", "Nexus Trading"],
                ].map(([mode, label]) => {
                  const active = String(gridMode || "normal") === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setGridMode(mode)}
                      style={{
                        height: 36,
                        borderRadius: 12,
                        border: active ? "1px solid rgba(0,255,136,.45)" : "1px solid rgba(255,255,255,.10)",
                        background: active ? "linear-gradient(180deg, rgba(34,197,94,.95), rgba(22,163,74,.88))" : "rgba(255,255,255,.04)",
                        color: active ? "#02130a" : "rgba(235,255,247,.92)",
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: active ? "0 0 18px rgba(34,197,94,.18)" : "none",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="cardActions" style={{ alignItems: "center", flex: "0 0 auto" }}>
             
              {/* BUILD417: remove the shared Grid price pill. It reflected the Grid selection
                  even while NKR/Trader was active and therefore looked like a live engine price. */}
              <InfoButton title="Grid Trader – Info">
                <Help showClose dismissable
                  de={
                    <>
                      <p><b>Grid Trader</b> ist der gemeinsame Order-Bereich für <b>Nexus Grid</b>, <b>Nexus NKR</b> und <b>Nexus Trading</b>.</p>
                      <p>Alle drei Bereiche verwenden denselben übersichtlichen Order-Ablauf. Stoppen, Löschen und Fortsetzen bleiben an das Wallet gebunden und werden synchronisiert.</p>

                      <p><b>Budget-System:</b> Das Budget ist der maximale Betrag, den der ausgewählte Modus verwenden darf. Nexus Trading darf nicht mehr Kapital einsetzen, als der Benutzer freigegeben hat.</p>
                      <p><b>Slot-System:</b> Das Budget wird in taktische Slots aufgeteilt. Jeder Slot besitzt eine eigene Priorität und kann abhängig von Konfidenz, Liquidität, Risiko und Marktstruktur warten, aktiviert werden oder blockiert bleiben.</p>

                      <p><b>Slot-Farben:</b></p>
                      <p><b style={{ color: '#21d07a' }}>Grün / ACTIVE:</b> Die Bedingungen sind ausreichend stark. Der Slot ist bereit oder aktiv.</p>
                      <p><b style={{ color: '#ffc107' }}>Gelb / WAIT:</b> Die Richtung ist interessant, aber eine Bestätigung fehlt noch. Die KI überwacht den Markt weiter und wartet auf bessere Bedingungen.</p>
                      <p><b style={{ color: '#ff6b6b' }}>Rot / BLOCKED:</b> Risiko, Konfidenz oder Liquidität sind nicht ausreichend. Die KI blockiert den Slot, anstatt einen schwachen Einstieg auszuführen.</p>

                      <p><b>Nexus Grid:</b> Ein einfacher Exit-Manager für Token, die du bereits besitzt. Du bestimmst Netzwerk, Coin, Menge, Verkaufsziel, maximalen Verlust und Auszahlungs-Asset. Grid kauft keine Assets, sucht keine Assets und verwendet den Strategist nicht.</p>
                      <p><b>Vor dem Start:</b> Die Token müssen zuerst in den <b>Core Vault</b> eingezahlt werden. Erst danach erscheinen sie unter „Coin in Vault“ und können für eine Grid-Regel verwendet werden. Token, die sich nur im verbundenen Wallet befinden, können von Grid nicht genutzt werden.</p>
                      <p><b>Ablauf:</b> Grid reserviert nur die ausgewählte Token-Menge, überwacht den Preis und verkauft beim festgelegten Zielpreis oder beim definierten Verlustlimit. Es wird keine Kauforder erstellt.</p>
                      <p><b>Auszahlung:</b> Bei erfolgreichem Verkauf wird in <b>USDC oder USDT</b> (je nach Einstellung) getauscht. Der Betrag bleibt in deinem <b>Privy Wallet</b> — nicht automatisch zurück im Vault.</p>

                      <p><b>Nexus NKR:</b> Kapitalrotation. Nexus NKR überwacht fortlaufend die Watchlist, nutzt die stärksten Marktchancen, kann schwache Sessions stoppen und Kapital zu stärkeren Assets rotieren. Die ausgewählte Anzahl Sessions ist ein Maximum und keine Pflichtanzahl.</p>

                      <p><b>Nexus Trading:</b> Ein autonomer Trading-Modus nach der Budgetfreigabe. Du definierst Budget, Slots, Laufzeit, Stil, erlaubte Assets und Chains, Risikomodus, Drawdown, Profit Lock, Slippage und maximale Trades. Danach arbeitet Nexus Trading innerhalb dieser Grenzen selbstständig.</p>
                      <p><b>HOLD / OBSERVE:</b> Nach einem Risk Exit, Schutzereignis oder Stop wird das Kapital zuerst geschützt. HOLD ist eine minimale Schutzdauer von 1–12 Stunden. Danach steigt Nexus nicht blind wieder ein; der Strategist bewertet weiterhin Marktstruktur, Liquidität, RVOL und Risiko.</p>
                      <p><b>Kein blinder Wiedereinstieg:</b> Bleibt der Markt nach HOLD schwach, bleibt der Slot in OBSERVE. Nach Ablauf der maximalen Beobachtungszeit muss der Benutzer das Kapital erneut freigeben, bevor Nexus Trading es wieder zuweisen darf.</p>
                      <p><b>Wichtig:</b> WAIT oder BLOCKED ist kein Fehler. Das System wartet oder blockiert bewusst, wenn Qualität und Risiko nicht geeignet sind. Kein Trade ist besser als ein schlechter Trade.</p>

                      <p><b>Auszahlung:</b> Vor dem Start bestimmst du, ob der Verkaufserlös als USDC oder USDT im Vault verbleibt oder – mit einer ausdrücklich definierten Rückkaufregel – wieder in den ursprünglichen Token zurückgeführt wird.</p>
                    </>
                  }
                  en={
                    <>
                      <p><b>Grid Trader</b> is the shared order area for <b>Nexus Grid</b>, <b>Nexus NKR</b>, and <b>Nexus Trading</b>.</p>
                      <p>All three areas use the same clean order flow. Stop, Delete and Resume stay wallet-bound and synchronized.</p>

                      <p><b>Budget System:</b> The budget is the maximum amount the selected mode may use. Nexus Trading may not use more capital than the user has approved.</p>
                      <p><b>Slot System:</b> The budget is divided into tactical slots. Each slot is its own setup with its own priority. A slot can wait, activate or stay blocked depending on confidence, liquidity, risk and market structure.</p>

                      <p><b>Slot Colors:</b></p>
                      <p><b style={{ color: '#21d07a' }}>Green / ACTIVE:</b> Conditions are strong enough. The slot is ready or active.</p>
                      <p><b style={{ color: '#ffc107' }}>Yellow / WAIT:</b> The direction is interesting, but confirmation is still missing. The AI keeps monitoring and waits for better conditions.</p>
                      <p><b style={{ color: '#ff6b6b' }}>Red / BLOCKED:</b> Risk, confidence or liquidity are not good enough yet. The AI intentionally blocks the slot instead of taking a weak entry.</p>

                      <p><b>Nexus Grid:</b> a simple exit manager for tokens you already own. You choose network, coin, amount, sell target, maximum loss and payout. Grid never buys, discovers assets or uses the Strategist.</p>
                      <p><b>Before starting:</b> The tokens must first be deposited into the <b>Core Vault</b>. Only then do they appear under “Coin in Vault” and become available for a Grid rule. Tokens held only in the connected wallet cannot be used by Grid.</p>
                      <p><b>Flow:</b> Grid reserves only the selected token amount, monitors the price and sells at the target price or the defined loss limit. No buy order is created.</p>
                      <p><b>Payout:</b> On a successful sell, the asset is swapped to <b>USDC or USDT</b> (per your setting). Funds stay in your <b>Privy Wallet</b> — not automatically back in the Vault.</p>

                      <p><b>Nexus NKR:</b> capital rotation. Nexus NKR continuously watches the watchlist, uses the strongest market opportunities, can stop weak sessions and redirect capital toward stronger assets. The selected session number is only the maximum, not a required count.</p>

                      <p><b>Nexus Trading:</b> autonomous trading mode after budget approval. The user defines budget, slots, runtime, style, allowed assets/chains, risk mode, drawdown, profit lock, slippage and max trades. After that, Nexus Trading works independently inside those limits.</p>
                      <p><b>HOLD / OBSERVE:</b> After a Risk Exit, Protect or Stop, capital is protected first. HOLD is a minimum protection period from 1-12h. When it expires, Nexus must not blindly re-enter; the Strategist keeps checking market structure, liquidity, RVOL and risk.</p>
                      <p><b>No blind re-entry:</b> If the market is still weak after HOLD, the slot stays in OBSERVE. After the maximum observation window, the user must release the capital again before Nexus Trading may allocate it.</p>
                      <p><b>Important:</b> WAIT or BLOCKED is not an error. It means the system prefers to wait or block if quality and risk are not good enough. No trade is better than a bad trade.</p>

                      <p><b>Payout:</b> Before starting, you choose whether the sale proceeds remain in the Vault as USDC, USDT or – with an explicitly defined repurchase rule – return to the original token.</p>

                      
                    </>
                  }
                />
              </InfoButton>
            </div>
          </div>

          <div className="panelScroll"><div className={`gridLayout ${String(gridMode || "normal") === "trading" ? "tradingDesktopLayout" : String(gridMode || "normal") === "rotation" ? "rotationDesktopLayout" : "gridDesktopLayout"}`}>
            <div className="gridLeft">
              {isCompactMobile && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 8,
                  marginBottom: 10,
                  padding: 4,
                  borderRadius: 14,
                  background: "rgba(255,255,255,.04)",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                {[
                  ["normal", "Nexus Grid"],
                  ["rotation", "Nexus NKR"],
                  ["trading", "Nexus Trading"],
                ].map(([mode, label]) => {
                  const active = String(gridMode || "normal") === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setGridMode(mode)}
                      style={{
                        height: 36,
                        borderRadius: 11,
                        border: active ? "1px solid rgba(34,197,94,.65)" : "1px solid rgba(255,255,255,.08)",
                        background: active ? "linear-gradient(90deg, #22c55e, #16a34a)" : "rgba(255,255,255,.04)",
                        color: active ? "#071512" : "#d9fff0",
                        fontWeight: 900,
                        cursor: "pointer",
                        boxShadow: active ? "0 0 16px rgba(34,197,94,.22)" : "none",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              )}

              {(() => {
                const modeKey = String(gridMode || "normal");
                const selectedChain = String(liveVaultChainByMode?.[modeKey] || "ETH").toUpperCase();
                const selectedVaultState = coreVaultOnchainByChain?.[selectedChain] || null;
                const tokenEntries = Object.entries(selectedVaultState?.tokens || {}).filter(([, tokenState]) => {
                  const account = tokenState?.account || {};
                  const total = Number(account?.baseCapital || 0) + Number(account?.totalSecuredProfit || 0);
                  return total > 0;
                });
                const fallbackSymbol = tokenEntries[0]?.[0] || "USDC";
                const selectedSymbolRaw = String(liveVaultAssetByMode?.[modeKey] || fallbackSymbol).toUpperCase();
                const selectedSymbol = tokenEntries.some(([symbol]) => symbol === selectedSymbolRaw) ? selectedSymbolRaw : fallbackSymbol;
                const tokenState = selectedVaultState?.tokens?.[selectedSymbol] || {};
                const account = tokenState?.account || {};
                const total = Number(account?.baseCapital || 0) + Number(account?.totalSecuredProfit || 0);
                const totalAllocated = Number(account?.totalAllocated || 0);
                const free = Math.max(0, total - totalAllocated);
                const reserved = modeKey === "rotation"
                  ? Number(account?.allocatedNkr || 0)
                  : modeKey === "trading"
                    ? Number(account?.allocatedTrader || 0)
                    : Number(account?.allocatedGrid || 0);
                const systemLabel = modeKey === "rotation" ? "NKR" : modeKey === "trading" ? "Trader" : "Grid";
                const chainConnected = !!selectedVaultState?.connected;
                // Any USDC/USDT variant is 1:1 USD — never price USDC_NATIVE via NKR/grid quote (~$0.07).
                const isStableSymbol = (symbol) => {
                  const s = String(symbol || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
                  if (["USDC", "USDT", "USD", "DAI"].includes(s)) return true;
                  if (s.includes("USDC") || s.includes("USDT")) return true; // USDC_NATIVE, NATIVE_USDC, …
                  return false;
                };
                const resolveVaultUsdPrice = (symbol) => {
                  const sym = String(symbol || "").toUpperCase();
                  if (isStableSymbol(sym)) return 1;
                  // Map wraps to display symbols for price lookup
                  const lookup = { WETH: "ETH", WBNB: "BNB", WMATIC: "POL", MATIC: "POL", CBBTC: "BTC", WBTC: "BTC", BTCB: "BTC" }[sym] || sym;
                  const rows = Array.isArray(watchRows) ? watchRows : [];
                  const row = rows.find((r) => String(r?.symbol || r?.sym || r?.coin || "").toUpperCase() === lookup)
                    || rows.find((r) => String(r?.symbol || r?.sym || r?.coin || "").toUpperCase() === sym);
                  if (row) {
                    const px = Number(row?.price ?? row?.priceUsd ?? row?.usd ?? row?.current_price ?? row?.last ?? 0);
                    if (px > 0) return px;
                  }
                  // Exact lookup only — never fall back to activeGridNativeUsd / NKR price ($0.07).
                  try {
                    if (typeof getNexusOrderPriceUsd === "function") {
                      const p = Number(getNexusOrderPriceUsd(lookup) || 0);
                      // Reject absurd "prices" that match the UI token ticker (~0.07 for NKR)
                      if (p > 0.5 || lookup === "ETH" || lookup === "BNB" || lookup === "BTC") {
                        if (p > 0) return p;
                      }
                      // POL/MATIC can be <$1 — accept any positive only if row matched lookup above
                    }
                  } catch {}
                  return 0;
                };
                const formatVaultAmount = (value, symbol) => {
                  const n = Number(value || 0);
                  const nativeLike = !isStableSymbol(symbol);
                  return nativeLike
                    ? n.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 8 })
                    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
                };
                const formatVaultUsdHint = (value, symbol) => {
                  const px = resolveVaultUsdPrice(symbol);
                  if (!(px > 0)) return "";
                  const usd = Number(value || 0) * px;
                  // Stables: show 1:1 value so 19 USDC_NATIVE → ≈ $19.00
                  return `≈ $${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                };
                const formatVaultPrimary = (value, symbol) => {
                  const amt = `${formatVaultAmount(value, symbol)} ${symbol}`;
                  const usd = formatVaultUsdHint(value, symbol);
                  return usd ? `${amt} · ${usd}` : amt;
                };
                return (
                  <div className="liveCoreVaultBlock" style={{ display: modeKey === "normal" ? "none" : "block", marginBottom: 10, padding: 11, borderRadius: 13, border: "1px solid rgba(64,196,255,.22)", background: "rgba(64,196,255,.05)", minWidth: 0, maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 950, fontSize: 12 }}>Live Core Vault Capital</div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 900, color: chainConnected ? "#86efac" : "#ffd166" }}>
                        {coreVaultAllChainsLoading ? "REFRESHING ALL CHAINS" : chainConnected ? "LIVE CONNECTED" : "VAULT NOT AVAILABLE"}
                      </span>
                    </div>
                    {/* Controls stay compact even when the NKR panel is expanded full-width */}
                    <div className="liveCoreVaultControls">
                      {/* Row 1: Chain + Asset */}
                      <div className="liveCoreVaultSelects" style={{ marginTop: 9, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, alignItems: "stretch" }}>
                        <label style={{ display: "grid", gap: 4, minWidth: 0 }}>
                          <span className="muted tiny">Chain</span>
                          <select value={selectedChain} onChange={(e) => setLiveVaultChainByMode((prev) => ({ ...(prev || {}), [modeKey]: String(e.target.value || "ETH").toUpperCase() }))} style={{ width: "100%", minWidth: 0 }}>
                            <option value="ETH">Ethereum</option>
                            <option value="BNB">BNB Chain</option>
                            <option value="POL">Polygon</option>
                            <option value="BASE">Base</option>
                            <option value="ARB">Arbitrum</option>
                          </select>
                        </label>
                        <label style={{ display: "grid", gap: 4, minWidth: 0 }}>
                          <span className="muted tiny">Vault asset</span>
                          <select
                            value={selectedSymbol}
                            disabled={!chainConnected || !tokenEntries.length}
                            onChange={(e) => setLiveVaultAssetByMode((prev) => ({ ...(prev || {}), [modeKey]: String(e.target.value || "USDC").toUpperCase() }))}
                            style={{ width: "100%", minWidth: 0 }}
                          >
                            {tokenEntries.length ? tokenEntries.map(([symbol, state]) => {
                              const a = state?.account || {};
                              const amount = Number(a?.baseCapital || 0) + Number(a?.totalSecuredProfit || 0);
                              const usd = formatVaultUsdHint(amount, symbol);
                              return <option key={symbol} value={symbol}>{symbol} · {formatVaultAmount(amount, symbol)}{usd ? ` (${usd})` : ""}</option>;
                            }) : <option value="USDC">No funded asset</option>}
                          </select>
                        </label>
                      </div>
                      {/* Row 2: Balance / Free / Reserved */}
                      <div className="liveCoreVaultMetrics" style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, alignItems: "stretch" }}>
                        {[
                          ["Balance", total],
                          ["Free", free],
                          [`${systemLabel} reserved`, reserved],
                        ].map(([label, value]) => (
                          <div key={label} style={{ padding: "8px 8px", borderRadius: 11, border: "1px solid rgba(255,255,255,.08)", background: "rgba(0,0,0,.14)", minWidth: 0, overflow: "hidden" }}>
                            <div className="muted tiny" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                            <div className="mono" style={{ marginTop: 4, fontWeight: 950, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={chainConnected ? formatVaultPrimary(value, selectedSymbol) : ""}>
                              {chainConnected ? `${formatVaultAmount(value, selectedSymbol)} ${selectedSymbol}` : "—"}
                            </div>
                            {chainConnected && formatVaultUsdHint(value, selectedSymbol) ? (
                              <div className="muted tiny mono" style={{ marginTop: 2, color: "#8de8ff", fontWeight: 800 }}>
                                {formatVaultUsdHint(value, selectedSymbol)}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Hide single-asset green chip — it only duplicates Balance/Free/Reserved above */}
                    {chainConnected && tokenEntries.length > 1 ? (
                      <div className="liveCoreVaultAssets" style={{ marginTop: 9, display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, maxWidth: 560 }}>
                        {tokenEntries.map(([symbol, state]) => {
                          const a = state?.account || {};
                          const assetTotal = Number(a?.baseCapital || 0) + Number(a?.totalSecuredProfit || 0);
                          const assetAllocated = Number(a?.totalAllocated || 0);
                          const assetFree = Math.max(0, assetTotal - assetAllocated);
                          const assetReserved = modeKey === "rotation"
                            ? Number(a?.allocatedNkr || 0)
                            : modeKey === "trading"
                              ? Number(a?.allocatedTrader || 0)
                              : Number(a?.allocatedGrid || 0);
                          const active = symbol === selectedSymbol;
                          const totalUsd = formatVaultUsdHint(assetTotal, symbol);
                          return (
                            <button
                              key={`vault-all-${modeKey}-${symbol}`}
                              type="button"
                              onClick={() => setLiveVaultAssetByMode((prev) => ({ ...(prev || {}), [modeKey]: symbol }))}
                              style={{
                                textAlign: "left",
                                padding: "9px 10px",
                                borderRadius: 11,
                                border: active ? "1px solid rgba(34,197,94,.62)" : "1px solid rgba(255,255,255,.08)",
                                background: active ? "rgba(34,197,94,.11)" : "rgba(0,0,0,.13)",
                                color: "inherit",
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                                <strong>{symbol}</strong>
                                <span className="mono" style={{ fontWeight: 950 }}>{formatVaultAmount(assetTotal, symbol)}</span>
                              </div>
                              {totalUsd ? (
                                <div className="muted tiny mono" style={{ marginTop: 2, color: "#8de8ff", fontWeight: 800 }}>{totalUsd}</div>
                              ) : null}
                              <div className="muted tiny" style={{ marginTop: 5, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                                <span>Free {formatVaultAmount(assetFree, symbol)}{formatVaultUsdHint(assetFree, symbol) ? ` · ${formatVaultUsdHint(assetFree, symbol)}` : ""}</span>
                                <span>{systemLabel} {formatVaultAmount(assetReserved, symbol)}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                    {!chainConnected ? (
                      <div style={{ marginTop: 8, color: "#ffd166", fontSize: 11 }}>Core Vault not available on this chain yet. Live budget approval remains disabled.</div>
                    ) : tokenEntries.length === 0 ? (
                      <div style={{ marginTop: 8, color: "#ffd166", fontSize: 11 }}>No funded Vault asset is available on this chain.</div>
                    ) : null}
                  </div>
                );
              })()}

              {strategistBridge && String(gridMode || "normal") !== "normal" ? (
                <div
                  style={{
                    marginBottom: 10,
                    padding: "9px 11px",
                    borderRadius: 12,
                    border: "1px solid rgba(34,197,94,.34)",
                    background: "linear-gradient(180deg, rgba(34,197,94,.12), rgba(34,197,94,.055))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "grid", gap: 3 }}>
                    <div style={{ fontWeight: 900, fontSize: 12, letterSpacing: ".04em", textTransform: "uppercase", color: "#dfffee" }}>
                      Strategist Setup Loaded
                    </div>
                    <div className="muted tiny">
                      <b>{strategistBridge.sym}</b> → <b>{strategistBridge.label}</b>{strategistBridge.confidence ? <> · Confidence: <b>{strategistBridge.confidence}</b></> : null}
                    </div>
                    {strategistBridge.note ? (
                      <div className="muted tiny">
                        {strategistBridge.note}
                      </div>
                    ) : null}
                    {String(gridMode || "normal") === "rotation" && Array.isArray(strategistRotationCandidates) && strategistRotationCandidates.length > 1 ? (
                      <div
                        style={{
                          marginTop: 7,
                          padding: "7px 8px",
                          borderRadius: 10,
                          border: "1px solid rgba(34,197,94,.24)",
                          background: "rgba(34,197,94,.08)",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div className="muted tiny" style={{ fontWeight: 900, color: "#dfffee" }}>
                          Strategist candidates · direkt wählen
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {strategistRotationCandidates.map((c, idx) => {
                            const active = String(rotationSelectedPick?.source || "").toUpperCase() === String(c?.sym || "").toUpperCase();
                            return (
                              <button
                                key={`setup-${c.sym}-${idx}`}
                                type="button"
                                className="btnGhost"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRotationPickToGrid(c);
                                  setErrorMsg(`Prepared ${c.sym} from Strategist candidates in NKR.`);
                                }}
                                title={c.sourceLine || "Select Strategist candidate for NKR"}
                                style={{
                                  height: 26,
                                  paddingInline: 9,
                                  fontSize: 12,
                                  borderColor: active ? "rgba(34,197,94,.75)" : c.rank === "AVOID" ? "rgba(239,68,68,.35)" : undefined,
                                  background: active ? "rgba(34,197,94,.18)" : c.rank === "AVOID" ? "rgba(239,68,68,.08)" : undefined,
                                  color: active ? "#dfffee" : c.rank === "AVOID" ? "#fecaca" : undefined,
                                }}
                              >
                                {c.rank === "BEST" ? "BEST · " : c.rank === "AVOID" ? "AVOID · " : c.rank === "SECONDARY" ? "ALT · " : ""}{c.sym}{Number.isFinite(Number(c.spreadPct)) ? ` · ${Number(c.spreadPct).toFixed(2)}%` : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {Array.isArray(strategistBridge.appliedSettings) && strategistBridge.appliedSettings.length ? (() => {
                      const appliedSettings = strategistBridge.appliedSettings;
                      const changedCount = appliedSettings.filter((item) => item?.changed).length;
                      const unchangedCount = Math.max(0, appliedSettings.length - changedCount);
                      return (
                        <div
                          style={{
                            marginTop: 7,
                            borderRadius: 10,
                            border: "1px solid rgba(34,197,94,.22)",
                            background: "rgba(0,0,0,.16)",
                            overflow: "hidden",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setStrategistAppliedOpen((v) => !v)}
                            aria-expanded={strategistAppliedOpen}
                            title="Show or hide applied Strategist trading settings"
                            style={{
                              width: "100%",
                              border: "none",
                              background: "transparent",
                              color: "#dfffee",
                              padding: "7px 9px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                              <span style={{ fontSize: 12, lineHeight: 1 }}>{strategistAppliedOpen ? "▾" : "▸"}</span>
                              <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: ".04em", textTransform: "uppercase" }}>
                                Strategist Applied Settings
                              </span>
                            </span>
                            <span className="muted tiny" style={{ whiteSpace: "nowrap" }}>
                              {changedCount} updated · {unchangedCount} unchanged
                            </span>
                          </button>

                          {strategistAppliedOpen ? (
                            <div
                              style={{
                                padding: "2px 9px 8px",
                                display: "grid",
                                gridTemplateColumns: isCompactMobile ? "1fr" : "1fr 1fr",
                                gap: 6,
                              }}
                            >
                              {appliedSettings.map((item) => (
                                <div key={item.label} className="muted tiny" style={{ lineHeight: 1.35 }}>
                                  <b style={{ color: "#eafff5" }}>{item.label}:</b> {item.value} <span style={{ color: item.changed ? "#86efac" : "#9fb8ad" }}>· {item.changed ? "changed" : "already same"}</span>
                                  <br />
                                  <span style={{ color: "#8fb2a4" }}>Source: Strategist · Before: {item.previous}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })() : null}
                  </div>
                  <button
                    className="btnGhost"
                    type="button"
                    onClick={() => setStrategistBridge(null)}
                    title="Hide Strategist setup notice"
                    style={{ height: 28, paddingInline: 10, fontSize: 12 }}
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              {String(gridMode || "normal") === "rotation" ? (
                <div className="gridWrap rotationDesktopWrap" style={{ gridTemplateColumns: "1fr", width: "100%" }}>
                  <div className="gridControls" style={{ display: "grid", gap: 10 }}>
                    {(() => {
                      // Live NKR list: running, paused, stopping/closing stay visible.
                      // PAUSE is not STOP — cards must remain until FINALIZED / explicit delete.
                      const isRelevantLiveRotationRow = (row) => {
                        const st = String(row?.status || row?.statusLabel || "").toUpperCase();
                        const openCount = Number(row?.openAssetCount ?? row?.open_asset_count ?? row?.meta?.open_asset_count ?? 0) || 0;
                        if (["FINALIZED", "COMPLETE", "COMPLETED", "CLOSED", "RELEASED", "REBALANCED_OUT", "DELETED", "ARCHIVED", "CANCELLED"].includes(st)) return false;
                        // Explicitly keep paused sessions (with or without an open position).
                        if (["ACTIVE", "RUNNING", "APPROVED", "EXECUTOR", "OPEN", "PAUSED", "CLOSING", "STOPPING", "STOPPED", "EXITING", "EXIT_REQUESTED", "EXIT_PENDING", "EXIT_FAILED"].includes(st)) return true;
                        const sid = String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.meta?.onchain_session_id ?? row?.meta?.core_vault_session_id ?? "");
                        if (sid) {
                          const ch = String(row?.chain || row?.chainKey || row?.meta?.chain || row?.chainId || row?.meta?.chain_id || "").toUpperCase();
                          const chN = ch === "ETHEREUM" || ch === "1" ? "ETH" : ch === "BSC" || ch === "56" ? "BNB" : ch === "POLYGON" || ch === "MATIC" || ch === "137" ? "POL" : ch;
                          if (finalizedOnChainIds.has(`${chN || "UNK"}:${sid}`)) return false;
                        }
                        return openCount > 0;
                      };
                      const finalizedOnChainIds = readFinalizedNkrIds();
                      const rawLocalRotationRows = (Array.isArray(rotationSessions) ? rotationSessions : []).filter((row) => {
                        const sid = String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.meta?.onchain_session_id ?? row?.meta?.core_vault_session_id ?? "");
                        if (!sid) return true;
                        const ch = String(row?.chain || row?.chainKey || row?.meta?.chain || row?.chainId || row?.meta?.chain_id || "").toUpperCase();
                        const chN = ch === "ETHEREUM" || ch === "1" ? "ETH" : ch === "BSC" || ch === "56" ? "BNB" : ch === "POLYGON" || ch === "MATIC" || ch === "137" ? "POL" : ch;
                        // Accept both chain-scoped keys and legacy bare ids during migration
                        if (finalizedOnChainIds.has(`${chN || "UNK"}:${sid}`)) return false;
                        if (!chN && finalizedOnChainIds.has(sid)) return false;
                        return true;
                      });
                      const previewSessions = Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [];
                      const authoritativeOnChainIds = new Set(previewSessions
                        .filter((row) => {
                          const st = String(row?.statusLabel || "").toUpperCase();
                          const openCount = Number(row?.openAssetCount || 0) || 0;
                          return openCount > 0 || ["ACTIVE", "PAUSED", "CLOSING"].includes(st);
                        })
                        .map((row) => String(row?.sessionId || ""))
                        .filter(Boolean));
                      // Once an on-chain scan exists, it is authoritative. Historical local
                      // placeholders without a matching live CoreVault session are excluded.
                      // Multi-chain: never filter by the selected-chain CoreVault preview.
                      // Backend /api/rotation-sessions is the source of truth for ETH+BNB+POL cards.
                      const localRotationRows = rawLocalRotationRows.filter((row) => isRelevantLiveRotationRow(row));
                      const localOnChainIds = new Set(localRotationRows.map((row) => String(
                        row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.meta?.onchain_session_id ?? row?.meta?.core_vault_session_id ?? ""
                      )).filter(Boolean));
                      // System Info refresh is intentionally independent from the user-facing live list.
                      // It must never add/remove cards in Active NKR Sessions.
                      const onChainRotationRows = ([])
                        .filter((row) => {
                          const st = String(row?.statusLabel || "").toUpperCase();
                          const openCount = Number(row?.openAssetCount || 0) || 0;
                          // PAUSED stays visible even with $0 position / settlement (pause ≠ stop).
                          const relevant = openCount > 0 || ["ACTIVE", "PAUSED", "CLOSING", "STOPPING"].includes(st);
                          return relevant && !localOnChainIds.has(String(row?.sessionId || ""));
                        })
                        .map((row) => {
                          const budgetUnits = Number(row?.budgetUnits || 0) || 0;
                          const settlementUnits = Number(row?.settlementCashUnits || 0) || 0;
                          const budgetUsd = budgetUnits / 1e6;
                          const settlementUsd = settlementUnits / 1e6;
                          return {
                            id: `COREVAULT-${row.sessionId}`,
                            session_id: `COREVAULT-${row.sessionId}`,
                            onchainSessionId: Number(row.sessionId),
                            chain: "ETHEREUM",
                            baseAsset: "USDC",
                            payoutAsset: "USDC",
                            targetAsset: Number(row?.openAssetCount || 0) > 0 ? "ETH" : "WAITING",
                            positionAsset: Number(row?.openAssetCount || 0) > 0 ? "ETH" : "WAITING",
                            status: String(row?.statusLabel || "PAUSED").toUpperCase(),
                            budgetUsd,
                            workingCapitalUsd: Math.max(0, budgetUsd - settlementUsd),
                            positionValueUsd: Math.max(0, budgetUsd - settlementUsd),
                            exitReason: Number(row?.openAssetCount || 0) > 0
                              ? "On-chain CoreVault position remains open. Resume or Stop & Exit is required."
                              : "On-chain CoreVault session is waiting for finalization.",
                            meta: {
                              onchain_session_id: Number(row.sessionId),
                              core_vault_session_id: Number(row.sessionId),
                              open_asset_count: Number(row?.openAssetCount || 0),
                              reconstructed_from_onchain: true,
                              nkr_exit_reason: Number(row?.openAssetCount || 0) > 0
                                ? "On-chain CoreVault position remains open. Resume or Stop & Exit is required."
                                : "On-chain CoreVault session is waiting for finalization."
                            }
                          };
                        });
                      const liveReservedNkrUsd = Object.values(coreVaultOnchain?.tokens || {}).reduce((sum, tokenState) => {
                        const account = tokenState?.account || {};
                        return sum + Math.max(0, Number(account?.allocatedNkr || 0) || 0);
                      }, 0);
                      const nkrControlU = String(nkrControlState || "WAITING").toUpperCase();
                      const needsControlFallback = false;
                      const controlFallbackRows = false ? [{
                        id: String(activeRotationSessionId || `NKR-CONTROL-${String(wallet || "wallet").toLowerCase()}`),
                        session_id: String(activeRotationSessionId || `NKR-CONTROL-${String(wallet || "wallet").toLowerCase()}`),
                        type: "NKR",
                        chain: String(rotationNetworkScope || activeGridChainKey || "ETH").toUpperCase(),
                        baseAsset: String(manualPayoutAsset || "USDC").toUpperCase(),
                        payoutAsset: String(manualPayoutAsset || "USDC").toUpperCase(),
                        targetAsset: "WAITING",
                        positionAsset: "WAITING",
                        status: nkrControlU === "PAUSED" ? "PAUSED" : ["CLOSING", "STOPPING"].includes(nkrControlU) ? "CLOSING" : "RUNNING",
                        lifecycleState: nkrControlU,
                        budgetUsd: liveReservedNkrUsd > 0 ? liveReservedNkrUsd : Math.max(0, Number(String(rotationBudgetRelease || "0").replace(",", ".")) || 0),
                        workingCapitalUsd: 0,
                        positionValueUsd: 0,
                        startedAt: Date.now(),
                        campaignStartedAt: Date.now(),
                        meta: { nkr_session: true, control_fallback: true, open_asset_count: 0, nkr_exit_reason: "NKR is running and scanning for an executable entry." }
                      }] : [];
                      // Dedupe by chain + on-chain session id. Session ids restart per vault/chain
                      // (ETH #1 and BNB #1 are different sessions) — never collapse them into one card.
                      const rotationRowMap = new Map();
                      for (const row of [...localRotationRows, ...controlFallbackRows, ...onChainRotationRows]) {
                        const oid = String(row?.onchainSessionId ?? row?.onchain_session_id ?? row?.coreVaultSessionId ?? row?.meta?.onchain_session_id ?? row?.meta?.core_vault_session_id ?? "");
                        const ch = normalizeNkrChainKey(row?.chain || row?.chainKey || row?.chain_key || row?.meta?.chain || row?.meta?.chain_key || row?.chainId || row?.chain_id || row?.meta?.chain_id || "");
                        const id = String(row?.id || row?.session_id || "");
                        const key = oid ? `${ch || "UNK"}:${oid}` : (id || `local-${Math.random()}`);
                        const old = rotationRowMap.get(key);
                        const score = (x) => Number(x?.positionQty || x?.positionAmount || x?.meta?.nkr_position_qty || 0) > 0 ? 100 : Number(x?.positionValueUsd || x?.investedUsd || 0) > 0 ? 80 : Number(x?.openAssetCount || x?.meta?.open_asset_count || 0) > 0 ? 60 : x?.meta?.reconstructed_from_onchain ? 10 : 20;
                        if (!old || score(row) >= score(old)) rotationRowMap.set(key, row);
                      }
                      const rotationRows = Array.from(rotationRowMap.values());
                      const rotationNow = Number(tradingRuntimeNowMs || Date.now());
                      const fmtRotationDuration = (ms) => {
                        const totalMin = Math.max(0, Math.floor(Number(ms || 0) / 60000));
                        const h = Math.floor(totalMin / 60);
                        const m = totalMin % 60;
                        if (h >= 24) {
                          const d = Math.floor(h / 24);
                          const rh = h % 24;
                          return `${d}d ${rh}h`;
                        }
                        return `${h}h ${m}m`;
                      };
                      const getRotationDerivedStatus = (sess) => {
                        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                        const rawStatusId = Number(
                          sess?.rawStatusId ?? sess?.statusId ?? sess?.onchainStatusId ??
                          meta?.raw_status_id ?? meta?.status_id ?? meta?.onchain_status_id
                        );
                        if (rawStatusId === 1) return "ACTIVE";
                        if (rawStatusId === 2) return "PAUSED";
                        if (rawStatusId === 3) return "CLOSING";
                        if (rawStatusId === 4) return "FINALIZED";
                        const st = String(
                          sess?.onchainStatus || sess?.statusLabel || sess?.rawStatusLabel ||
                          meta?.onchain_status || meta?.status_label || sess?.status || sess?.lifecycleState || "APPROVED"
                        ).toUpperCase();
                        const exp = Number(sess?.expiresAt || sess?.expires_at || sess?.meta?.expires_at || 0);
                        if (!["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "COMPLETE", "RELEASED", "REBALANCED_OUT"].includes(st) && exp && exp <= rotationNow) return "EXPIRED";
                        return st;
                      };
                      const getRotationDisplayStatus = (sess) => {
                        const derived = getRotationDerivedStatus(sess);
                        const exitKey = nkrSessionControlKey(sess);
                        const exitUi = String(nkrExitUiState?.[exitKey] || "").toUpperCase();
                        // Per-session state is authoritative. A global STOPPING flag must not
                        // force unrelated cards into EXITING and must not hide the exact chain/session.
                        if (["REQUESTED", "PENDING", "EXITING"].includes(exitUi)) {
                          if (["PAUSED", "STOPPING", "CLOSING", "FINALIZING", "EXITING", "RUNNING", "ACTIVE", "WAITING", "EXECUTOR"].includes(derived) || exitUi) {
                            return "EXITING";
                          }
                        }
                        if (["STOPPING", "CLOSING", "FINALIZING", "EXITING"].includes(derived)) return "EXITING";
                        if (["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "COMPLETE", "PROTECTED", "RELEASED", "REBALANCED_OUT"].includes(derived)) {
                          return derived === "CLOSED" ? "COMPLETE" : derived;
                        }
                        const action = String(sess?.meta?.rotation_action || sess?.rotationAction || rotationShadowSnapshot?.action || "").toUpperCase();
                        if (["WAIT_NET_EDGE", "SEARCHING", "READY", "WAIT", "WAIT_SCORE"].includes(action)) return "WAITING";
                        if (["READY_DISPATCHED", "EXECUTOR_ACTIVE", "DISPATCHED"].includes(action)) return "EXECUTOR";
                        if (["ROTATION_OPEN", "POSITION_OPEN", "OPEN"].includes(action)) return "OPEN";
                        if (["EXIT_PENDING", "EXITING", "STOPPING", "CLOSING", "STOP_EXIT"].includes(action)) return "EXITING";
                        if (["USER_PAUSED"].includes(action)) return "PAUSED";
                        if (["SESSION_COMPLETE"].includes(action)) return "COMPLETE";
                        if (["PROTECTED"].includes(action)) return "PROTECTED";
                        if (["ACTIVE", "APPROVED"].includes(derived)) return "WAITING";
                        return derived || "WAITING";
                      };
                      const getRotationStatusTone = (status) => {
                        const st = String(status || "").toUpperCase();
                        const cardBg = "rgba(0,0,0,.16)"; // User UI: cards stay neutral; market/live price text carries green/red meaning.
                        if (["OPEN", "ACTIVE", "RUNNING", "EXECUTOR"].includes(st)) return { border: "rgba(34,197,94,.38)", bg: cardBg, pill: "green", color: "#86efac" };
                        if (["WAITING", "WAIT_NET_EDGE", "SEARCHING"].includes(st)) return { border: "rgba(255,209,102,.34)", bg: cardBg, pill: "silver", color: "#ffd166" };
                        if (["EXITING"].includes(st)) return { border: "rgba(139,220,255,.34)", bg: cardBg, pill: "silver", color: "#8bdcff" };
                        if (["PAUSED"].includes(st)) return { border: "rgba(255,193,7,.32)", bg: cardBg, pill: "silver", color: "#ffc107" };
                        if (["PROTECTED", "STOPPED"].includes(st)) return { border: "rgba(255,107,107,.36)", bg: cardBg, pill: "silver", color: "#ff8a8a" };
                        return { border: "rgba(255,255,255,.12)", bg: cardBg, pill: "silver", color: "rgba(235,255,247,.78)" };
                      };
                      const rotationMaxActive = Math.max(1, Math.floor(Number(String(rotationMaxActiveSessions || "3").replace(",", ".")) || 3));
                      const NKR_INACTIVE_STATUSES = ["EXPIRED", "CLOSED", "COMPLETE", "FINALIZED", "CANCELLED", "RELEASED", "ARCHIVED", "REBALANCED_OUT", "WAITING_REALLOCATION", "WATCH_POOL", "CAPITAL_RELEASED", "INACTIVE"]; // ENGINE-232: PAUSED/STOPPING/STOPPED remain visible until the exit is confirmed and the session is FINALIZED.
                      const rotationVisibleActiveRows = rotationRows.filter((s) => !NKR_INACTIVE_STATUSES.includes(getRotationDerivedStatus(s)));
                      const rotationAllocatableRows = rotationVisibleActiveRows.filter((s) => !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "ARCHIVED", "REBALANCED_OUT", "WAITING_REALLOCATION", "WATCH_POOL"].includes(getRotationDerivedStatus(s)));
                      const getNkrSessionWorkingCapitalUsd = (sess) => {
                        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                        const candidates = [
                          sess?.workingCapitalUsd,
                          sess?.sessionCapitalUsd,
                          sess?.currentCapitalUsd,
                          sess?.allocatedUsd,
                          sess?.reservedCapitalUsd,
                          sess?.budgetUsd,
                          meta?.workingCapitalUsd,
                          meta?.working_capital_usd,
                          meta?.sessionCapitalUsd,
                          meta?.session_capital_usd,
                          meta?.currentCapitalUsd,
                          meta?.current_capital_usd,
                          meta?.allocatedUsd,
                          meta?.allocated_usd,
                          meta?.reservedCapitalUsd,
                          meta?.reserved_capital_usd,
                          meta?.budgetUsd,
                          meta?.budget_usd,
                        ];
                        for (const v of candidates) {
                          const n = Number(v);
                          if (Number.isFinite(n) && n > 0) return n;
                        }
                        return 0;
                      };
                      const rotationAllocatedUsd = rotationAllocatableRows.reduce((sum, sess) => sum + getNkrSessionWorkingCapitalUsd(sess), 0);
                      const rotationProfitUsd = rotationRows.reduce((sum, sess) => {
                        const candidates = [
                          sess?.profitUsd,
                          sess?.sessionProfitUsd,
                          sess?.rotationProfitUsd,
                          sess?.collectedProfitUsd,
                          sess?.meta?.profitUsd,
                          sess?.meta?.sessionProfitUsd,
                          sess?.meta?.rotationProfitUsd,
                          sess?.meta?.collectedProfitUsd,
                        ];
                        const v = candidates.map(Number).find((n) => Number.isFinite(n));
                        return sum + (Number.isFinite(v) ? v : 0);
                      }, 0);
                      // ENGINE-377: A paused session stays visible and its vault still belongs
                      // to the NKR overview totals, but it is not an active/running session.
                      // Keep the two counts separate so one paused chain cannot make the whole
                      // multi-chain overview say PAUSED while another chain is still running.
                      const pausedRotations = rotationVisibleActiveRows.filter((s) => getRotationDerivedStatus(s) === "PAUSED").length;
                      const activeRotations = rotationVisibleActiveRows.filter((s) => {
                        const st = String(getRotationDerivedStatus(s) || "").toUpperCase();
                        return !["PAUSED", "STOPPED", "CLOSING", "STOPPING", "FINALIZING", "EXITING"].includes(st);
                      }).length;
                      const visibleNkrSessions = rotationVisibleActiveRows.length;
                      const controllableRotations = rotationRows.filter((s) => !["STOPPED", "CLOSED", "EXPIRED", "CANCELLED", "RELEASED", "ARCHIVED"].includes(getRotationDerivedStatus(s))).length;
                      const firstRotation = rotationRows.find((s) => String(s?.id || "") === String(activeRotationSessionId || "")) || rotationRows[0] || null;
                      const typedNkrBudgetUsd = Number(String(rotationBudgetRelease || "").replace(",", "."));
                      const activeNkrBudgetUsd = Number(
                        firstRotation?.budgetUsd || firstRotation?.approvedBudgetUsd || firstRotation?.reservedCapitalUsd ||
                        firstRotation?.allocatedUsd || firstRotation?.workingCapitalUsd || firstRotation?.sessionCapitalUsd ||
                        firstRotation?.meta?.budget_usd || firstRotation?.meta?.working_capital_usd || firstRotation?.meta?.session_capital_usd || 0
                      );
                      // ENGINE-189: Overview shows only capital belonging to a running NKR campaign.
                      // The typed setup amount remains in NKR Setup, but must not appear as active budget.
                      const hasRunningNkrCampaign = activeRotations > 0 && String(nkrControlState || "WAITING").toUpperCase() !== "STOPPED";
                      const nkrBudgetUsd = hasRunningNkrCampaign
                        ? (Number.isFinite(typedNkrBudgetUsd) && typedNkrBudgetUsd > 0
                            ? typedNkrBudgetUsd
                            : Number.isFinite(activeNkrBudgetUsd) && activeNkrBudgetUsd > 0
                              ? activeNkrBudgetUsd
                              : 0)
                        : 0;
                      // ENGINE-096: Top Profit Asset must describe the currently active NKR book.
                      // Rebalanced-out/closed sessions remain in Event History, but must not keep
                      // POL or any old winner displayed as top asset after it was removed.
                      const topProfitRotation = rotationVisibleActiveRows
                        .map((sess) => {
                          const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                          const profit = Number(
                            sess?.collectedProfitUsd ??
                            sess?.rotationProfitUsd ??
                            sess?.sessionProfitUsd ??
                            sess?.profitUsd ??
                            meta?.collectedProfitUsd ??
                            meta?.rotationProfitUsd ??
                            meta?.sessionProfitUsd ??
                            meta?.profitUsd ??
                            0
                          ) || 0;
                          const sym = String(sess?.sourceSymbol || sess?.symbol || sess?.targetAsset || meta?.sourceSymbol || meta?.symbol || "").toUpperCase();
                          return { sess, profit, sym };
                        })
                        .filter((x) => x.sym)
                        .sort((a, b) => b.profit - a.profit)[0] || null;
                      const nkrTarget = String(
                        (topProfitRotation && topProfitRotation.profit > 0 ? topProfitRotation.sym : "") ||
                        firstRotation?.sourceSymbol ||
                        firstRotation?.symbol ||
                        firstRotation?.targetAsset ||
                        rotationSelectedPick?.sym ||
                        rotationSelectedPick?.coin ||
                        rotationSelectedPick?.source ||
                        "waiting"
                      ).toUpperCase();
                      const nkrOpenPositionRows = rotationVisibleActiveRows.filter((sess) => {
                        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                        const op = sess?.openRotation && typeof sess.openRotation === "object" ? sess.openRotation : {};
                        const asset = String(sess?.positionAsset || op?.asset || sess?.targetAsset || sess?.asset || sess?.symbol || meta?.nkr_active_asset || "").toUpperCase();
                        const state = String(sess?.positionState || meta?.position_state || "").toUpperCase();
                        const onchain = String(sess?.onchainStatus || meta?.onchain_status || "").toUpperCase();
                        const qty = Number(sess?.positionQty ?? op?.quantity ?? meta?.nkr_position_qty ?? 0);
                        const txHash = String(sess?.lastTxHash || sess?.txHash || sess?.tradeTxHash || op?.txHash || meta?.nkr_live_buy_tx_hash || "");
                        const lifecycle = String(sess?.status || sess?.lifecycleState || "").toUpperCase();
                        const stillActive = !["FINALIZED", "CLOSED", "STOPPED", "CANCELLED", "RELEASED", "ARCHIVED"].includes(lifecycle);
                        return asset && !["ASSET", "WAITING", "NONE", "NULL"].includes(asset) && stillActive && (state === "OPEN" || onchain === "CONFIRMED" || qty > 0 || Boolean(txHash));
                      });
                      const nkrActiveAssets = Array.from(new Set(nkrOpenPositionRows.map((sess) => String(sess?.positionAsset || sess?.openRotation?.asset || sess?.targetAsset || sess?.asset || sess?.symbol || sess?.meta?.nkr_active_asset || "").toUpperCase()).filter((x) => x && !["ASSET", "WAITING", "NONE", "NULL"].includes(x))));
                      const nkrOpenPositionValueUsd = nkrOpenPositionRows.reduce((sum, sess) => {
                        const op = sess?.openRotation && typeof sess.openRotation === "object" ? sess.openRotation : {};
                        const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                        const direct = Number(sess?.positionValueUsd ?? op?.currentValueUsd ?? meta?.nkr_position_value_usd ?? 0);
                        if (Number.isFinite(direct) && direct > 0) return sum + direct;
                        const qty = Number(sess?.positionQty ?? op?.quantity ?? meta?.nkr_position_qty ?? 0);
                        const price = Number(sess?.currentPriceUsd ?? op?.currentPriceUsd ?? meta?.nkr_current_price_usd ?? 0);
                        if (Number.isFinite(qty) && qty > 0 && Number.isFinite(price) && price > 0) return sum + qty * price;
                        return sum + getNkrSessionWorkingCapitalUsd(sess);
                      }, 0);
                      const liveTopAsset = nkrActiveAssets[0] || (nkrTarget && !["ASSET", "WAITING", "NONE", "NULL"].includes(nkrTarget) ? nkrTarget : "");
                      const targetChain = String(firstRotation?.chain || rotationNetworkScope || activeGridChainKey || "ALL").toUpperCase();
                      const vaultTotalNative = Number(manualVaultTotalQty || 0);
                      const gridAllocatedNative = Number(manualVaultAllocatedQty || 0);
                      const px = Number(activeGridNativeUsd || 0);
                      const vaultTotalUsd = Number.isFinite(px) && px > 0 ? vaultTotalNative * px : 0;
                      const gridAllocatedUsd = Number.isFinite(px) && px > 0 ? gridAllocatedNative * px : 0;
                      const nkrProtectedReserveUsd = 0;
                      const nkrAvailableUsd = nkrBudgetUsd > 0 ? Math.max(0, nkrBudgetUsd - rotationAllocatedUsd) : 0;
                      const availableUsd = nkrBudgetUsd > 0 ? nkrAvailableUsd : Math.max(0, vaultTotalUsd - gridAllocatedUsd - rotationAllocatedUsd);
                      // ENGINE-374: The overview is an all-session summary. Never bind it to
                      // the most recently selected/created chain. Aggregate each distinct live
                      // chain + settlement asset account exactly once, so ETH + BNB + POL sessions
                      // are shown together without double-counting a shared account.
                      const nkrSelectedChain = String(liveVaultChainByMode?.rotation || activeNkrChainKey || "ETH").toUpperCase();
                      const nkrSelectedAsset = String(liveVaultAssetByMode?.rotation || firstRotation?.settlementAsset || firstRotation?.asset || "USDC").toUpperCase();
                      // ENGINE-376: Aggregate every active session vault, independent of the
                      // currently selected chain. Session payloads use several aliases (BNB Chain,
                      // BSC, Polygon, chain ids), so normalize them before looking up the shared
                      // all-chain CoreVault snapshot. Otherwise one active chain can silently drop
                      // out of Vault Total / Available even while its session card is visible.
                      const canonicalNkrSettlementAsset = (value, tokenAddress = "") => {
                        const raw = String(value || "").trim().toUpperCase().replace(/[^A-Z0-9_]/g, "");
                        const addr = String(tokenAddress || "").trim().toLowerCase();
                        if (addr === "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48") return "USDC";
                        if (addr === "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d") return "USDC";
                        if (["0x2791bca1f2de4661ed88a30c99a7a9449aa84174", "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359"].includes(addr)) return "USDC";
                        if (raw.includes("USDT")) return "USDT";
                        if (raw.includes("USDC")) return "USDC";
                        return raw || "USDC";
                      };
                      const nkrSessionVaultRefs = (Array.isArray(rotationVisibleActiveRows) ? rotationVisibleActiveRows : [])
                        .map((sess) => {
                          const meta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                          const chain = normalizeNkrChainKey(
                            sess?.chain || sess?.chainKey || meta?.chain || meta?.chain_key ||
                            sess?.chainId || sess?.chain_id || meta?.chain_id || ""
                          );
                          const settlementAddress = String(
                            sess?.settlementToken || sess?.settlementTokenAddress || sess?.settlement_address ||
                            meta?.settlement_token || meta?.settlement_token_address || meta?.settlement_address || ""
                          );
                          const asset = canonicalNkrSettlementAsset(
                            sess?.settlementAsset || sess?.settlementTokenSymbol || sess?.baseAsset ||
                            sess?.payoutAsset || meta?.settlement_asset || meta?.settlement_symbol || meta?.base_asset || "USDC",
                            settlementAddress
                          );
                          return chain ? { key: `${chain}|${asset}`, chain, asset, settlementAddress, session: sess } : null;
                        })
                        .filter(Boolean);
                      const nkrSessionVaultRefByKey = new Map();
                      for (const ref of nkrSessionVaultRefs) {
                        if (!nkrSessionVaultRefByKey.has(ref.key)) nkrSessionVaultRefByKey.set(ref.key, ref);
                      }
                      // When there is no active session, keep the selected account as a useful
                      // single-vault preview. With active sessions, every distinct session vault counts.
                      const nkrOverviewVaultRefs = nkrSessionVaultRefByKey.size
                        ? Array.from(nkrSessionVaultRefByKey.values())
                        : [{
                            key: `${normalizeNkrChainKey(nkrSelectedChain)}|${canonicalNkrSettlementAsset(nkrSelectedAsset)}`,
                            chain: normalizeNkrChainKey(nkrSelectedChain),
                            asset: canonicalNkrSettlementAsset(nkrSelectedAsset),
                            settlementAddress: "",
                            session: null,
                          }];
                      const nkrOverviewAccounts = nkrOverviewVaultRefs.map((ref) => {
                        const { chain, asset, settlementAddress } = ref;
                        const vaultState = coreVaultOnchainByChain?.[chain] || null;
                        const tokenRows = vaultState?.tokens && typeof vaultState.tokens === "object" ? vaultState.tokens : {};
                        const targetAddr = String(settlementAddress || "").toLowerCase();
                        const candidates = Object.entries(tokenRows).map(([rowKey, row]) => {
                          const rowAddress = String(row?.address || row?.token || row?.tokenAddress || "").toLowerCase();
                          const rowAsset = canonicalNkrSettlementAsset(
                            row?.symbol || row?.asset || row?.name || rowKey || "",
                            rowAddress
                          );
                          const account = row?.account && typeof row.account === "object" ? row.account : null;
                          const total = account ? Math.max(0,
                            Number(account?.baseCapital || 0) + Number(account?.totalSecuredProfit || 0)
                          ) : 0;
                          const allocated = account ? Math.max(0, Number(account?.totalAllocated || 0)) : 0;
                          const exactAddress = Boolean(targetAddr && rowAddress === targetAddr);
                          const exactKey = String(rowKey || "").toUpperCase() === String(asset || "").toUpperCase();
                          const sameAsset = rowAsset === asset;
                          const settlementConfigured = Boolean(row?.config?.settlementToken ?? row?.settlementToken);
                          return { rowKey, row, account, total, allocated, rowAsset, exactAddress, exactKey, sameAsset, settlementConfigured };
                        });

                        // ENGINE-379: Every chain must contribute its real wallet-bound settlement
                        // account. Do not fall back to the session budget: that produced totals such
                        // as $40.02 while ETH + BNB + POL actually held about $55.86. Prefer the exact
                        // token address, then the exact row key/symbol, then the configured settlement
                        // account with the highest wallet balance (important for POL USDC vs USDC_NATIVE).
                        const ranked = candidates
                          .filter((c) => c.account)
                          .sort((a, b) => {
                            const score = (c) =>
                              (c.exactAddress ? 1000000 : 0) +
                              (c.exactKey ? 100000 : 0) +
                              (c.sameAsset ? 10000 : 0) +
                              (c.settlementConfigured ? 1000 : 0) +
                              Math.min(999, Number(c.total || 0));
                            return score(b) - score(a);
                          });
                        const selected = ranked[0] || null;
                        const total = Math.max(0, Number(selected?.total || 0));
                        const allocated = Math.max(0, Number(selected?.allocated || 0));
                        return {
                          chain,
                          asset: selected?.rowAsset || asset,
                          total,
                          allocated,
                          free: Math.max(0, total - allocated),
                          connected: Boolean(vaultState?.connected),
                          accountReadable: Boolean(selected?.account),
                          source: selected?.account ? `ONCHAIN_ACCOUNT:${selected.rowKey}` : "ONCHAIN_ACCOUNT_MISSING",
                        };
                      });
                      const nkrVaultTotalLive = nkrOverviewAccounts
                        .filter((row) => row?.accountReadable)
                        .reduce((sum, row) => sum + Number(row?.total || 0), 0);
                      const usagePct = nkrBudgetUsd > 0 ? Math.min(100, Math.max(0, (rotationAllocatedUsd / nkrBudgetUsd) * 100)) : vaultTotalUsd > 0 ? Math.min(100, Math.max(0, ((gridAllocatedUsd + rotationAllocatedUsd) / vaultTotalUsd) * 100)) : 0;
                      const nkrCtrl = String(nkrControlState || "WAITING").toUpperCase();
                      const rotationShadowRuntimeStatus = rotationShadowBusy ? "READING LIVE DATA" : nkrCtrl === "STOPPED" ? "STOPPED" : nkrCtrl === "PAUSED" || pausedRotations > 0 ? "PAUSED" : activeRotations > 0 || nkrCtrl === "RUNNING" ? "RUNNING" : "READY";
                      const rotationShadowWorkStatus = rotationShadowBusy ? "SCANNING" : nkrCtrl === "STOPPED" ? "STOPPED" : nkrCtrl === "PAUSED" || pausedRotations > 0 ? "PAUSED" : activeRotations > 0 || nkrCtrl === "RUNNING" ? "RUNNING" : "WAITING";
                      const rotationShadowReadiness = rotationShadowBusy
                        ? "SCANNING_MARKET"
                        : rotationShadowSnapshot?.action
                          ? String(rotationShadowSnapshot.action).toUpperCase()
                          : activeRotations > 0
                            ? "SEARCHING"
                            : "WAITING_FOR_SESSION";

                      // NKR overview runtime: one clear campaign clock for the whole NKR run.
                      // Session cards can still show their own position runtime, but the overview
                      // must show when the current NKR campaign started and how long remains.
                      const normalizeNkrTimestampMs = (value) => {
                        const n = Number(value || 0);
                        if (!Number.isFinite(n) || n <= 0) return 0;
                        return n < 10_000_000_000 ? n * 1000 : n;
                      };
                      const nkrTimingRows = (Array.isArray(rotationVisibleActiveRows) ? rotationVisibleActiveRows : [])
                        .filter((sess) => {
                          const st = String(getRotationDerivedStatus(sess) || "").toUpperCase();
                          return !["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "COMPLETE", "RELEASED", "REBALANCED_OUT"].includes(st);
                        });
                      const nkrStartCandidates = nkrTimingRows
                        .map((sess) => normalizeNkrTimestampMs(
                          sess?.campaignStartedAt || sess?.campaign_started_at || sess?.meta?.campaign_started_at ||
                          sess?.startedAt || sess?.started_at || sess?.createdAt || sess?.created_at
                        ))
                        .filter((ts) => ts > 0);
                      const nkrExplicitEndCandidates = nkrTimingRows
                        .map((sess) => normalizeNkrTimestampMs(
                          sess?.campaignExpiresAt || sess?.campaign_expires_at || sess?.meta?.campaign_expires_at ||
                          sess?.expiresAt || sess?.expires_at || sess?.meta?.expires_at
                        ))
                        .filter((ts) => ts > 0);
                      // A persisted control flag is not proof of an active V5 session.
                      // After on-chain finalization the overview must stop immediately when
                      // the authoritative session list is empty.
                      const nkrHasLiveSession = nkrTimingRows.length > 0 || activeRotations > 0 ||
                        (Array.isArray(rotationVisibleActiveRows) && rotationVisibleActiveRows.some((sess) => {
                          const st = String(getRotationDerivedStatus(sess) || sess?.status || "").toUpperCase();
                          const qty = Number(sess?.positionQty ?? sess?.positionAmount ?? sess?.openRotation?.positionQty ?? sess?.meta?.nkr_position_qty ?? 0) || 0;
                          return qty > 0 || ["RUNNING", "ACTIVE", "EXECUTOR", "POSITION_ACTIVE", "CLOSING"].includes(st);
                        }));
                      const fallbackNkrStartTs = normalizeNkrTimestampMs(
                        rotationVisibleActiveRows?.[0]?.positionOpenedAt || rotationVisibleActiveRows?.[0]?.meta?.position_opened_at ||
                        rotationVisibleActiveRows?.[0]?.startedAt || rotationVisibleActiveRows?.[0]?.createdAt || 0
                      );
                      const nkrOverviewStartTs = nkrStartCandidates.length ? Math.min(...nkrStartCandidates) : fallbackNkrStartTs;
                      const configuredNkrEndTs = nkrOverviewStartTs > 0
                        ? nkrOverviewStartTs + normalizeNkrPeriodHours() * 60 * 60 * 1000
                        : 0;
                      const nkrOverviewEndTs = nkrExplicitEndCandidates.length
                        ? Math.max(...nkrExplicitEndCandidates)
                        : configuredNkrEndTs;
                      const nkrOverviewElapsedMs = nkrOverviewStartTs > 0 ? Math.max(0, rotationNow - nkrOverviewStartTs) : 0;
                      const nkrOverviewRemainingMs = nkrOverviewEndTs > 0 ? Math.max(0, nkrOverviewEndTs - rotationNow) : 0;
                      // ENGINE-214: Derive overview state from the authoritative live session/position,
                      // not only from the persisted setup control flag. A confirmed CoreVault position
                      // must never appear as WAITING or "not running" in the overview.
                      const nkrOverviewRunning = Boolean(nkrHasLiveSession || activeRotations > 0 || nkrOpenPositionRows.length > 0);
                      const nkrOverviewStatus = nkrOpenPositionRows.length > 0
                        ? "POSITION ACTIVE"
                        : activeRotations > 0
                          ? "ACTIVE"
                          : pausedRotations > 0
                            ? "PAUSED"
                            : nkrOverviewRunning
                              ? "ACTIVE"
                              : "WAITING";
                      const nkrOverviewAvailableUsd = (() => {
                        // Sum authoritative free capital for every active session account.
                        // Fall back only when no involved vault account is connected/readable.
                        const readableRows = nkrOverviewAccounts.filter((row) => row?.accountReadable);
                        if (readableRows.length > 0) {
                          return readableRows.reduce((sum, row) => sum + Number(row?.free || 0), 0);
                        }
                        return Math.max(0, availableUsd || 0);
                      })();
                      const nkrTopProfitAsset = topProfitRotation && Number(topProfitRotation.profit || 0) > 0
                        ? String(topProfitRotation.sym || "").toUpperCase()
                        : "";
                      const nkrOverviewBestCandidate = String(
                        rotationSelectedPick?.sym || rotationSelectedPick?.coin || rotationSelectedPick?.source || liveTopAsset || ""
                      ).toUpperCase();
                      // The running session mode is immutable. The setup selector may already
                      // be reset to Dynamic for the next run, so it must never drive the overview.
                      const nkrModeSourceRow = (Array.isArray(rotationVisibleActiveRows) ? rotationVisibleActiveRows : []).find((sess) =>
                        sess?.nkrCapitalMode || sess?.meta?.nkr_capital_mode || sess?.meta?.capital_mode
                      ) || firstRotation;
                      const persistedNkrActiveMode = "";
                      const nkrOverviewActiveMode = String(
                        nkrModeSourceRow?.nkrCapitalMode ||
                        nkrModeSourceRow?.capitalMode ||
                        nkrModeSourceRow?.meta?.nkr_capital_mode ||
                        nkrModeSourceRow?.meta?.capital_mode ||
                        nkrModeSourceRow?.meta?.nkrCapitalMode ||
                        // Only draft settings when NOTHING is running.
                        (!nkrOverviewRunning ? nkrCapitalMode : "") ||
                        (nkrOverviewRunning ? "LOCKED" : "DYNAMIC")
                      ).toUpperCase();
                      const nkrOverviewStartedLabel = nkrOverviewStartTs > 0
                        ? new Date(nkrOverviewStartTs).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                        : "not started";
                      const nkrOverviewEndsLabel = nkrOverviewEndTs > 0
                        ? new Date(nkrOverviewEndTs).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                        : "—";

                      return (
                        <>
                          <div
                            style={{
                              padding: "8px 10px",
                              borderRadius: 12,
                              background: "rgba(0,0,0,.18)",
                              border: "1px solid rgba(34,197,94,.22)",
                              display: "grid",
                              gap: 8,
                            }}
                          >
                            <div className="label" style={{ marginBottom: 0 }}>Nexus NKR overview</div>
                            <div
                              className="muted tiny"
                              style={{
                                display: "grid",
                                gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                                gap: 8,
                                lineHeight: 1.45,
                              }}
                            >
                              <div><b>Vault Total:</b> {fmtUsd(nkrVaultTotalLive)}</div>
                              <div style={{ color: "#22c55e", fontWeight: 900 }}><b>Available:</b> {fmtUsd(nkrOverviewAvailableUsd)}</div>
                              <div><b>Open Position:</b> {fmtUsd(nkrOpenPositionValueUsd)}</div>
                              <div><b>Collected Profit:</b> <span style={{ color: rotationProfitUsd >= 0 ? "#86efac" : "#ff8a8a", fontWeight: 900 }}>{rotationProfitUsd >= 0 ? "+" : ""}{fmtUsd(rotationProfitUsd)}</span></div>
                              
                              <div>
                                <b>Active Sessions:</b> {activeRotations}
                                {pausedRotations > 0 ? (
                                  <span style={{ marginLeft: 7, fontSize: 11, color: "#ffc107", fontWeight: 700 }}>
                                    · {pausedRotations} Paused
                                  </span>
                                ) : null}
                              </div>
                              <div><b>Status:</b> <span style={{ color: activeRotations > 0 || nkrOpenPositionRows.length > 0 ? "#22c55e" : pausedRotations > 0 ? "#ffc107" : "rgba(232,242,240,.72)", fontWeight: 900 }}>{nkrOverviewStatus}</span></div>
                              <div><b>Runtime:</b> {nkrOverviewRunning ? (nkrOverviewElapsedMs > 0 ? fmtRotationDuration(nkrOverviewElapsedMs) : "RUNNING") : "not running"}</div>
                              <div><b>Time Left:</b> <span style={{ color: nkrOverviewRunning && nkrOverviewRemainingMs > 0 ? "#8bdcff" : "rgba(232,242,240,.72)", fontWeight: 900 }}>{nkrOverviewRunning ? (nkrOverviewEndTs > 0 ? (nkrOverviewRemainingMs > 0 ? fmtRotationDuration(nkrOverviewRemainingMs) : "expired") : "—") : "—"}</span></div>
                            </div>
                            {/* Compact Strategist status — one glance, English, no wall of text */}
                            {nkrOverviewRunning || (nkrStrategistStatus && String(nkrStrategistStatus?.status || "").toUpperCase() === "RUNNING") ? (
                              <div
                                style={{
                                  marginTop: 2,
                                  padding: "7px 10px",
                                  borderRadius: 10,
                                  border: "1px solid rgba(139,220,255,.22)",
                                  background: "rgba(139,220,255,.05)",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: "6px 14px",
                                  lineHeight: 1.35,
                                }}
                              >
                                {(() => {
                                  const gate = String(nkrStrategistStatus?.gate || "—").toUpperCase();
                                  const decision = String(nkrStrategistStatus?.decision || "—").toUpperCase();
                                  const best = String(nkrStrategistStatus?.bestCandidate || "").toUpperCase();
                                  const score = Number(nkrStrategistStatus?.candidateScore || 0);
                                  const chain = String(nkrStrategistStatus?.sessionChain || "").toUpperCase();
                                  const overall = String(nkrStrategistStatus?.bestOverall || "").toUpperCase();
                                  const overallChain = String(nkrStrategistStatus?.bestOverallChain || "").toUpperCase();
                                  const overallScore = Number(nkrStrategistStatus?.bestOverallScore || 0);
                                  const multiTip = String(nkrStrategistStatus?.multiChainTip || nkrStrategistStatus?.recommendation || "").trim();
                                  const crossChain = overall && overallChain && chain && overallChain !== chain && overall !== best;
                                  // One short status phrase — no long paragraphs.
                                  let statusPhrase = "Waiting for entry";
                                  if (gate === "POSITION_ACTIVE" || decision === "HOLD") statusPhrase = "Position active";
                                  else if (decision === "SUBMITTING" || gate === "EXECUTION") statusPhrase = "Submitting trade";
                                  else if (gate === "SCORE_BLOCKED") statusPhrase = "Score below entry";
                                  else if (gate === "CHAIN_NO_CANDIDATE" || gate === "CHAIN_NOT_LIVE") statusPhrase = "No tradable asset on this chain";
                                  else if (gate === "WAITING_ENTRY" || decision === "WAIT" || decision === "STARTED") statusPhrase = "Watching · no buy yet";
                                  else if (decision && decision !== "—") statusPhrase = toUserFacingMessage(decision, "Waiting for entry");
                                  return (
                                    <>
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                        <b style={{ color: "#8bdcff", fontSize: 12 }}>Strategist</b>
                                        <span className="muted tiny" style={{ color: "#eafff5", fontWeight: 850 }}>{statusPhrase}</span>
                                      </span>
                                      <span className="muted tiny">
                                        {best ? (
                                          <><b>{best}</b>{score > 0 ? ` ${score.toFixed(0)}` : ""}{chain ? ` · ${chain}` : ""}</>
                                        ) : "—"}
                                        {multiTip && !/corevault|0x[a-f0-9]{8}|worker|rpc_|sessionof|ops_/i.test(multiTip) ? (
                                          <span style={{ color: "#ffd166", marginLeft: 8 }}>· tip: {toUserFacingMessage(multiTip.length > 72 ? multiTip.slice(0, 72) + "…" : multiTip, "")}</span>
                                        ) : crossChain ? (
                                          <span style={{ color: "#ffd166", marginLeft: 8 }}>
                                            · tip: {overall}{overallScore > 0 ? ` ${overallScore.toFixed(0)}` : ""} on {overallChain}
                                          </span>
                                        ) : null}
                                      </span>
                                      <span className="muted tiny" style={{ opacity: 0.75, marginLeft: "auto" }}>
                                        {/* BUILD396: raw gate codes stay in System Info only */}
                                      </span>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : null}
                          </div>

                          <div
                            style={{
                              padding: "8px 10px",
                              borderRadius: 12,
                              background: "rgba(0,0,0,.14)",
                              border: "1px solid rgba(34,197,94,.18)",
                              display: "grid",
                              gap: 8,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                              <div>
                                <div className="label" style={{ marginBottom: 0 }}>Active NKR Sessions</div>
                                
                              </div>
                              <span className="pill silver">{visibleNkrSessions} session{visibleNkrSessions === 1 ? "" : "s"} · all chains · {nkrActiveAssets.length}/{rotationMaxActive} assets</span>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gap: 10,
                                maxHeight: isCompactMobile ? "none" : 390,
                                overflowY: isCompactMobile ? "visible" : "auto",
                                paddingRight: isCompactMobile ? 0 : 5,
                              }}
                            >
                              {rotationVisibleActiveRows.length ? rotationVisibleActiveRows.map((sess, idx) => {
                                const chain = String(sess?.chain || "CHAIN").toUpperCase();
                                const sessionIdValue = String(sess?.id || sess?.session_id || "");
                                const oidKey = String(sess?.onchainSessionId || sess?.coreVaultSessionId || sess?.meta?.onchain_session_id || "");
                                const cachedStart = (oidKey && nkrStartSettingsCacheRef.current[`OID:${oidKey}`]) || {};
                                const immutableSessionSnapshot = {
                                  capitalMode: sess?.nkrCapitalMode || sess?.capitalMode || sess?.meta?.nkr_capital_mode || sess?.meta?.capital_mode || cachedStart.nkrCapitalMode || "",
                                  budgetUsd: Number(sess?.budgetUsd || sess?.sessionBudgetUsd || sess?.reservedUsd || sess?.meta?.session_budget_usd || cachedStart.budgetUsd || 0) || 0,
                                  observationWindow: sess?.nkrObservationWindow || sess?.meta?.nkr_observation_window || cachedStart.nkrObservationWindow || "",
                                  profitMode: sess?.nkrProfitMode || sess?.meta?.nkr_profit_mode || cachedStart.nkrProfitMode || "",
                                  periodHours: Number(sess?.nkrPeriodHours || sess?.runtimeHours || sess?.meta?.nkr_period_hours || cachedStart.nkrPeriodHours || 0) || 0,
                                  periodDays: Number(sess?.nkrPeriodDays || sess?.meta?.nkr_period_days || cachedStart.nkrPeriodDays || 0) || 0,
                                  periodUnit: String(sess?.nkrPeriodUnit || sess?.meta?.nkr_period_unit || cachedStart.nkrPeriodUnit || "days"),
                                  maxActiveAssets: Number(sess?.maxActiveAssets ?? sess?.meta?.nkr_max_active_assets ?? cachedStart.maxActiveAssets ?? 0) || 0,
                                  payoutAsset: String(sess?.payoutAsset || sess?.meta?.payout_asset || cachedStart.payoutAsset || "USDC").toUpperCase(),
                                };
                                const legacyPersistedActiveMode = String(cachedStart.nkrCapitalMode || "");
                                const budget = Number(sess?.budgetUsd || sess?.sessionBudgetUsd || sess?.reservedUsd || immutableSessionSnapshot?.budgetUsd || 0);
                                const sessionStatus = getRotationDerivedStatus(sess);
                                const status = getRotationDisplayStatus(sess);
                                const statusTone = getRotationStatusTone(status);
                                const positionStartTs = Number(sess?.positionOpenedAt || sess?.meta?.position_opened_at || sess?.startedAt || sess?.started_at || sess?.createdAt || 0) || rotationNow;
                                const campaignStartTs = Number(sess?.campaignStartedAt || sess?.meta?.campaign_started_at || positionStartTs) || positionStartTs;
                                const campaignExpiresTs = Number(sess?.campaignExpiresAt || sess?.meta?.campaign_expires_at || sess?.expiresAt || sess?.expires_at || sess?.meta?.expires_at || 0) || 0;
                                const runtimeText = fmtRotationDuration(Math.max(0, rotationNow - campaignStartTs));
                                const leftText = campaignExpiresTs ? (campaignExpiresTs > rotationNow ? fmtRotationDuration(campaignExpiresTs - rotationNow) : "expired") : "—";
                                const positionRuntimeText = fmtRotationDuration(Math.max(0, rotationNow - positionStartTs));
                                const baseAsset = String(sess?.baseAsset || sess?.payoutAsset || sess?.meta?.base_asset || manualPayoutAsset || "USDC").toUpperCase();
                                // Detect a real open position before choosing the route label.
                                // Without this, settlement defaults (ETH on Ethereum) look like an active ETH trade
                                // while the Strategist is actually watching another candidate (e.g. BNB).
                                const positionQtyEarly = Number(sess?.positionQty ?? sess?.positionAmount ?? sess?.openRotation?.positionQty ?? sess?.openRotation?.quantity ?? sess?.meta?.nkr_position_qty ?? 0) || 0;
                                const positionValueEarly = Number(sess?.positionValueUsd ?? sess?.openRotation?.currentValueUsd ?? sess?.meta?.nkr_position_value_usd ?? 0) || 0;
                                const openAssetCountEarly = Number(sess?.openAssetCount ?? sess?.meta?.open_asset_count ?? 0) || 0;
                                const positionStateEarly = String(sess?.positionState ?? sess?.meta?.position_state ?? "").toUpperCase();
                                const investedEarly = Number(sess?.investedUsd ?? sess?.workingCapitalUsd ?? sess?.meta?.nkr_position_value_usd ?? 0) || 0;
                                // BUILD402: on-chain buy may lag in qty/value fields — also trust OPEN state + invested
                                const hasOpenPosition = positionQtyEarly > 0 || positionValueEarly > 0 || openAssetCountEarly > 0
                                  || positionStateEarly === "OPEN" || investedEarly > 0.25;
                                const rawPosSym = String(
                                  sess?.positionAsset || sess?.openRotation?.asset || sess?.targetAsset || sess?.asset || sess?.symbol || sess?.meta?.nkr_active_asset || ""
                                ).toUpperCase();
                                // Settlement stables are never trade targets — USDC→USDC is invalid.
                                const isStableTradeSym = (s) => {
                                  const u = String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
                                  return !u || ["USDC", "USDT", "USD", "DAI"].includes(u) || u.includes("USDC") || u.includes("USDT");
                                };
                                const placeholderSyms = new Set(["", "WAITING", "NONE", "NULL", "ASSET", "SCANNING", baseAsset]);
                                // Per-session chain: ETH card must not show global POL candidate.
                                const sessChainRaw = String(sess?.chain || sess?.chainKey || sess?.meta?.chain || sess?.chainId || "").toUpperCase();
                                const sessChain = sessChainRaw === "ETHEREUM" || sessChainRaw === "1" ? "ETH"
                                  : sessChainRaw === "BSC" || sessChainRaw === "56" ? "BNB"
                                  : sessChainRaw === "POLYGON" || sessChainRaw === "MATIC" || sessChainRaw === "137" ? "POL"
                                  : sessChainRaw;
                                const nativeForChain = sessChain === "BNB" ? "BNB" : sessChain === "POL" ? "POL" : sessChain === "ETH" ? "ETH" : "";
                                const sessionTargetRaw = String(
                                  sess?.targetAsset || sess?.sourceSymbol || sess?.symbol || sess?.meta?.nkr_active_asset || sess?.meta?.selected_symbol || ""
                                ).toUpperCase();
                                // Best asset ON THIS session chain from watchlist.
                                // If none → scanning only (no forced native, no global POL bleed).
                                const homeChainOfSym = (sym) => {
                                  const u = String(sym || "").toUpperCase();
                                  if (["ETH", "WETH"].includes(u)) return "ETH";
                                  if (["BNB", "WBNB"].includes(u)) return "BNB";
                                  if (["POL", "MATIC", "WMATIC"].includes(u)) return "POL";
                                  const row = (Array.isArray(watchRows) ? watchRows : []).find(
                                    (r) => String(r?.symbol || r?.sym || r?.asset || "").toUpperCase() === u
                                  );
                                  const ch = String(row?.chain || row?.chainKey || row?.network || "").toUpperCase();
                                  if (ch === "ETHEREUM" || ch === "1" || ch === "ETH") return "ETH";
                                  if (ch === "BSC" || ch === "56" || ch === "BNB") return "BNB";
                                  if (ch === "POLYGON" || ch === "MATIC" || ch === "137" || ch === "POL") return "POL";
                                  return "";
                                };
                                const scoreOfRow = (r) => {
                                  const s = Number(r?.systemScore ?? r?.score ?? r?.strategistPerformanceScore ?? 0);
                                  const chg = Number(r?.change24h ?? r?.chg_24h ?? r?.usd_24h_change ?? 0) || 0;
                                  if (Number.isFinite(s) && s > 0) return s + Math.max(-5, Math.min(10, chg * 0.5));
                                  return 55 + chg * 3;
                                };
                                let bestOnChain = "";
                                let bestOnChainScore = -1e9;
                                for (const r of (Array.isArray(watchRows) ? watchRows : [])) {
                                  if (!r || typeof r !== "object") continue;
                                  const u = String(r?.symbol || r?.sym || r?.asset || "").toUpperCase();
                                  if (!u || placeholderSyms.has(u) || isStableTradeSym(u)) continue;
                                  const home = homeChainOfSym(u);
                                  // Include chain native + symbols tagged to this session chain only
                                  const onThis = (nativeForChain && u === nativeForChain) || (home && home === sessChain);
                                  if (!onThis) continue;
                                  const sc = scoreOfRow(r);
                                  if (sc > bestOnChainScore) {
                                    bestOnChainScore = sc;
                                    bestOnChain = u;
                                  }
                                }
                                const sessionTargetOk = sessionTargetRaw
                                  && !placeholderSyms.has(sessionTargetRaw)
                                  && !isStableTradeSym(sessionTargetRaw)
                                  && homeChainOfSym(sessionTargetRaw) === sessChain;
                                // Asset only when found — otherwise "" → route shows (scanning)
                                const strategistSym = sessionTargetOk
                                  ? sessionTargetRaw
                                  : (bestOnChain || "");
                                const sym = hasOpenPosition
                                  ? (rawPosSym && !placeholderSyms.has(rawPosSym) && !isStableTradeSym(rawPosSym) ? rawPosSym : (strategistSym || "ASSET"))
                                  : (strategistSym || "SCANNING");
                                const routeTitle = hasOpenPosition
                                  ? `${baseAsset} → ${sym} → ${baseAsset}`
                                  : (strategistSym
                                      ? `${baseAsset} → ${strategistSym} (watching) → ${baseAsset}`
                                      : `${baseAsset} → (scanning) → ${baseAsset}`);
                                const liveRow = (Array.isArray(watchRows) ? watchRows : []).find((r) => String(r?.symbol || r?.sym || r?.asset || "").toUpperCase() === sym) || null;
                                const livePrice = Number(liveRow?.price ?? liveRow?.usd ?? liveRow?.current_price ?? (hasOpenPosition ? (sess?.livePriceUsd ?? sess?.meta?.live_price_usd) : 0) ?? 0) || 0;
                                const liveChange = Number(liveRow?.change24h ?? liveRow?.chg_24h ?? liveRow?.usd_24h_change ?? (hasOpenPosition ? (sess?.marketChange24h ?? sess?.meta?.market_change_24h) : 0) ?? 0) || 0;
                                const liveVol = Number(liveRow?.volume24h ?? liveRow?.vol ?? liveRow?.total_volume ?? 0) || 0;
                                const liveTone = liveChange > 0 ? "#86efac" : liveChange < 0 ? "#ff8a8a" : "#d8fff1";
                                const workingCapital = getNkrSessionWorkingCapitalUsd(sess) || budget;
                                // ENGINE-223: Some live sessions expose `budgetUsd` as working capital only.
                                // Reconstruct the true total from immutable start data and the held reserve.
                                const reportedSessionBudgetUsd = Number(
                                  sess?.nkrTotalBudgetUsd ||
                                  sess?.meta?.nkr_total_budget_usd ||
                                  sess?.sessionBudgetUsd ||
                                  sess?.meta?.session_budget_usd ||
                                  immutableSessionSnapshot?.budgetUsd ||
                                  budget ||
                                  workingCapital ||
                                  0
                                ) || 0;
                                const reservePctByMode = {
                                  AGGRESSIVE: 10,
                                  DYNAMIC: 20,
                                  TACTICAL: 25,
                                  DEFENSIVE: 35,
                                };
                                const protectedReserveUsdRaw = Number(sess?.nkrCashReserveUsd ?? sess?.meta?.nkr_cash_reserve_usd ?? sess?.protectedReserveUsd ?? sess?.meta?.protected_reserve_usd);
                                const snapshotBudgetUsd = Number(immutableSessionSnapshot?.budgetUsd || 0) || 0;
                                // ENGINE-227: The Info dialog must show the mode frozen at session start.
                                // Never let mutable setup/session enrichment overwrite the immutable snapshot.
                                // For an already-running legacy session, the persisted start mode is preferred
                                // over live row fields because those fields may still mirror the setup selector.
                                // Prefer session-stamped mode; fall back to current NKR setup so Info never shows UNKNOWN
                                // while the user has already chosen Aggressive/Dynamic/Tactical/Defensive.
                                const sessionCapitalMode = String(
                                  immutableSessionSnapshot?.capitalMode ||
                                  legacyPersistedActiveMode ||
                                  sess?.nkrCapitalMode ||
                                  sess?.meta?.nkr_capital_mode ||
                                  sess?.capitalMode ||
                                  sess?.meta?.capital_mode ||
                                  cachedStart.nkrCapitalMode ||
                                  "DYNAMIC"
                                ).toUpperCase() || "DYNAMIC";
                                // ENGINE-242: The amount confirmed by the user is the complete session budget.
                                // Never inflate 20 USDC to 22.22 USDC from the capital-mode percentage.
                                // A reserve is displayed only when the backend/session explicitly reports one.
                                const explicitReserveUsd = Number.isFinite(protectedReserveUsdRaw) ? Math.max(0, protectedReserveUsdRaw) : 0;
                                const explicitTotalBudgetUsd = Math.max(reportedSessionBudgetUsd, snapshotBudgetUsd, budget, workingCapital);
                                const sessionBudgetUsd = explicitTotalBudgetUsd;
                                const protectedReserveUsd = explicitReserveUsd > 0
                                  ? Math.min(explicitReserveUsd, sessionBudgetUsd)
                                  : 0;
                                const plannedReserveUsd = protectedReserveUsd;
                                const reserveEpsilon = 0.005;
                                const reserveStatus = protectedReserveUsd <= reserveEpsilon
                                  ? "FULLY DEPLOYED"
                                  : (plannedReserveUsd > reserveEpsilon && protectedReserveUsd < plannedReserveUsd - reserveEpsilon
                                      ? "PARTIALLY DEPLOYED"
                                      : "HELD");
                                const reserveAvailableToDeployUsd = protectedReserveUsd;
                                const allocationBaseUsd = sessionBudgetUsd;
                                const savedAllocationPct = Number(sess?.nkrAllocationPct || sess?.meta?.nkr_allocation_pct || 0) || 0;
                                const liveAllocationPct = allocationBaseUsd > 0 ? (workingCapital / allocationBaseUsd) * 100 : savedAllocationPct;
                                const allocationLabel = Number.isFinite(liveAllocationPct) && liveAllocationPct > 0 ? `${liveAllocationPct.toFixed(1)}%` : "controlled";
                                const eventsHistoryCount = Array.isArray(sess?.rotationEvents) ? sess.rotationEvents.length : 0;
                                const rawTotalEvents = Number(sess?.totalEventCount ?? sess?.eventCount ?? sess?.meta?.nkr_total_event_count ?? 0);
                                const eventsCount = Number.isFinite(rawTotalEvents) && rawTotalEvents > 0 ? rawTotalEvents : eventsHistoryCount;
                                const lastEvent = sess?.lastRotationEvent || (Array.isArray(sess?.rotationEvents) ? sess.rotationEvents[0] : null) || null;
                                const profit = Number(sess?.collectedProfitUsd ?? sess?.profitUsd ?? sess?.sessionProfitUsd ?? sess?.rotationProfitUsd ?? 0) || 0;
                                const positionQty = Number(sess?.positionQty ?? sess?.positionAmount ?? sess?.openRotation?.positionQty ?? sess?.openRotation?.quantity ?? sess?.meta?.nkr_position_qty ?? 0) || 0;
                                const entryPrice = Number(sess?.entryPriceUsd ?? sess?.openRotation?.entryPriceUsd ?? sess?.meta?.nkr_entry_price_usd ?? 0) || 0;
                                const currentPositionPrice = Number(sess?.currentPriceUsd ?? sess?.openRotation?.currentPriceUsd ?? sess?.meta?.nkr_current_price_usd ?? livePrice ?? 0) || 0;
                                const markPrice = (currentPositionPrice > 0 ? currentPositionPrice : (livePrice > 0 ? livePrice : 0));
                                // BUILD460: mark value from qty × live price first — do NOT floor with invested
                                // (that hid real upside and left stale backend Gross negative while % was green).
                                const markFromQty = (positionQty > 0 && markPrice > 0) ? (positionQty * markPrice) : 0;
                                const positionValueReported = Number(
                                  sess?.positionValueUsd ?? sess?.openRotation?.currentValueUsd ?? sess?.meta?.nkr_position_value_usd ?? 0
                                ) || 0;
                                const positionValue = markFromQty > 0
                                  ? markFromQty
                                  : (positionValueReported > 0 ? positionValueReported : 0);
                                const investedLive = (() => {
                                  const fromEntry = (entryPrice > 0 && positionQty > 0) ? (entryPrice * positionQty) : 0;
                                  const fromSess = Number(sess?.investedUsd ?? sess?.workingCapitalUsd ?? sess?.meta?.nkr_entry_value_usd ?? 0) || 0;
                                  // Prefer explicit entry notional; avoid budget*0.85 inventing a fake cost basis.
                                  if (fromEntry > 0) return fromEntry;
                                  if (fromSess > 0) return fromSess;
                                  return 0;
                                })();
                                const markValue = positionValue > 0 ? positionValue : markFromQty;
                                // BUILD460: while position is open, ALWAYS recompute Gross/Net from mark − invested
                                // when we have both sides — ignore stale backend grossProfitUsd (was showing −$7 while +34%).
                                let gross = Number(sess?.grossProfitUsd ?? sess?.liveGrossProfitUsd ?? sess?.meta?.nkr_live_gross_profit_usd ?? sess?.meta?.grossProfitUsd ?? 0) || 0;
                                let costs = Number(sess?.costsUsd ?? sess?.liveCostsUsd ?? sess?.meta?.nkr_live_costs_usd ?? sess?.meta?.costsUsd ?? 0) || 0;
                                let net = Number(sess?.netProfitUsd ?? sess?.liveNetProfitUsd ?? sess?.meta?.nkr_live_net_profit_usd ?? sess?.meta?.netProfitUsd ?? 0) || 0;
                                if (hasOpenPosition && investedLive > 0 && markValue > 0) {
                                  gross = markValue - investedLive;
                                  if (costs <= 0) {
                                    costs = Math.max(0.01, Math.min(1.5, investedLive * 0.0035));
                                  }
                                  net = gross - costs;
                                } else if (hasOpenPosition && Math.abs(gross) < 1e-9 && investedLive > 0 && markValue > 0) {
                                  gross = markValue - investedLive;
                                  if (costs <= 0) costs = Math.max(0.01, Math.min(1.5, investedLive * 0.0035));
                                  net = gross - costs;
                                }
                                if (!hasOpenPosition && Math.abs(gross) < 1e-9 && Math.abs(profit) > 0) {
                                  gross = profit; net = profit;
                                }
                                const currentConfidence = Number(sess?.confidence ?? sess?.score ?? rotationSelectedPick?.score ?? 0);
                                const entryConfidence = Number(
                                  sess?.entryConfidence ?? sess?.entryScore ?? sess?.openRotation?.entryConfidence ?? sess?.openRotation?.entryScore ??
                                  sess?.meta?.nkr_entry_confidence ?? sess?.meta?.nkr_entry_score ??
                                  (Array.isArray(sess?.rotationEvents) ? sess.rotationEvents.find((ev) => ["BUY", "ENTRY", "OPEN"].includes(String(ev?.action || ev?.type || "").toUpperCase()))?.score : 0) ?? 0
                                );
                                const unrealizedPnl = Number(sess?.unrealizedPnlUsd ?? sess?.openRotation?.pnlUsd ?? sess?.meta?.nkr_unrealized_pnl_usd ?? 0) || 0;
                                const unrealizedPnlPct = Number(sess?.unrealizedPnlPct ?? sess?.openRotation?.pnlPct ?? sess?.meta?.nkr_unrealized_pnl_pct ?? 0) || 0;
                                const liveTxHash = String(sess?.lastTxHash || sess?.openRotation?.txHash || sess?.meta?.nkr_live_buy_tx_hash || "");
                                const roi = budget > 0 ? (profit / budget) * 100 : 0;
                                return (
                                  <div
                                    key={sess?.id || `rotation-${idx}-${sym}-${chain}`}
                                    style={{
                                      border: `1px solid ${statusTone.border}`,
                                      borderRadius: 14,
                                      background: statusTone.bg,
                                      padding: "10px 12px",
                                      display: "grid",
                                      gap: 8,
                                    }}
                                  >
                                    {/* ENGINE-226: compact session card with live gross, costs, net and exit movement block. */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: isCompactMobile ? "1fr" : "minmax(250px, 1fr) minmax(330px, 1.35fr) minmax(175px, .65fr) auto",
                                        gap: 10,
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                          <span className="pill" style={{
                                            fontSize: 10, fontWeight: 900, padding: "2px 8px", borderRadius: 999,
                                            background: chain === "BNB" ? "rgba(243,186,47,.18)" : chain === "POL" ? "rgba(130,71,229,.22)" : "rgba(64,196,255,.18)",
                                            border: chain === "BNB" ? "1px solid rgba(243,186,47,.45)" : chain === "POL" ? "1px solid rgba(130,71,229,.45)" : "1px solid rgba(64,196,255,.4)",
                                            color: chain === "BNB" ? "#f3ba2f" : chain === "POL" ? "#c4b5fd" : "#8de8ff",
                                          }}>{chain || "—"}</span>
                                          <b style={{ fontSize: 16, color: "#eafff5" }}>{routeTitle}</b>
                                        </div>
                                        <div className="muted tiny" style={{ whiteSpace: "nowrap" }}>
                                          {hasOpenPosition ? (
                                            <>
                                              <b style={{ color: liveTone }}>{sym} Live:</b> {currentPositionPrice > 0 ? fmtUsd(currentPositionPrice) : "waiting"}
                                              <span style={{ color: liveTone, fontWeight: 900, marginLeft: 8 }}>{liveChange >= 0 ? "+" : ""}{liveChange.toFixed(2)}%</span>
                                            </>
                                          ) : (
                                            <>
                                              <b style={{ color: liveTone }}>{sym === "SCANNING" ? "Watchlist" : `${sym} (candidate)`}:</b>{" "}
                                              {livePrice > 0 ? fmtUsd(livePrice) : "waiting"}
                                              {livePrice > 0 ? (
                                                <span style={{ color: liveTone, fontWeight: 900, marginLeft: 8 }}>{liveChange >= 0 ? "+" : ""}{liveChange.toFixed(2)}%</span>
                                              ) : null}
                                              <span style={{ color: "rgba(232,242,240,.65)", marginLeft: 8 }}>· no position yet</span>
                                            </>
                                          )}
                                        </div>
                                      </div>

                                      <div className="muted tiny" style={{ display: "grid", gap: 6, minWidth: 0 }}>
                                        <div><b style={{ color: "#d8fff1" }}>Open:</b> {hasOpenPosition ? (Number(positionValue) > 0 ? positionRuntimeText : `${sym} on-chain`) : "— (no position yet)"}</div>
                                        <div>
                                          <b style={{ color: "#8bdcff" }}>Invest:</b>{" "}
                                          {hasOpenPosition
                                            ? fmtUsd(Number(positionValue) > 0 ? positionValue : (Number(sess?.budgetUsd || sess?.reservedUsd || 0) || 0))
                                            : <span style={{ color: "rgba(232,242,240,.72)" }}>$0 · reserved only, no buy yet</span>}
                                        </div>
                                        {(() => {
                                          const gate = String(nkrStrategistStatus?.gate || "").toUpperCase();
                                          const decision = String(nkrStrategistStatus?.decision || "").toUpperCase();
                                          const best = String(nkrStrategistStatus?.bestCandidate || "").toUpperCase();
                                          const score = Number(nkrStrategistStatus?.candidateScore || 0);
                                          const detailRaw = String(nkrStrategistStatus?.detail || sess?.meta?.nkr_last_detail || "");
                                          // Never show another chain's candidate (e.g. POL) on ETH/BNB cards.
                                          const detailCandMatch = detailRaw.match(/Candidate\s+([A-Z0-9]+)/i);
                                          const detailCand = String(detailCandMatch?.[1] || "").toUpperCase();
                                          const detailIsForeign = !!(detailCand && strategistSym && detailCand !== strategistSym
                                            && detailCand !== nativeForChain
                                            && !(sessChain && detailCand === sessChain));
                                          const detail = detailIsForeign ? "" : detailRaw;
                                          let whyNoBuy = "";
                                          if (!hasOpenPosition && Number(positionValue) <= 0) {
                                            if (gate === "WAITING_ENTRY" || decision === "WAIT" || decision === "STARTED" || decision === "HOLD") {
                                              if (strategistSym) {
                                                whyNoBuy = `Why no buy: watching ${strategistSym} on ${sessChain || "this chain"} — entry not filled yet.`;
                                              } else if (score > 0 && score < 48 && best && (best === nativeForChain || best === strategistSym)) {
                                                whyNoBuy = `Why no buy: best candidate ${best} score ${score.toFixed(1)} is below entry threshold.`;
                                              } else if (detail && /reserve|balanced|0\/\d|settlement|selected|invest/i.test(detail) && !detailIsForeign) {
                                                whyNoBuy = `Why no buy: ${detail}`;
                                              } else {
                                                whyNoBuy = `Why no buy: scanning ${sessChain || "chain"} — waiting for a tradable candidate.`;
                                              }
                                            } else if (decision === "SUBMITTING" || gate === "EXECUTION") {
                                              whyNoBuy = "Why no buy: trade is submitting — waiting for on-chain confirmation.";
                                            } else if (gate.includes("ERROR") || gate === "TRADE_ROUTE_ERROR" || gate === "RPC_SESSION_READ" || gate === "SESSION_DECODE_FAILED" || gate === "SETTLEMENT_LOW" || gate === "LIVE_EXECUTION_DISABLED") {
                                              const err = toUserFacingMessage(String(nkrStrategistStatus?.lastError || nkrStrategistStatus?.detail || detailRaw || gate).slice(0, 180), "Temporary issue — retrying.");
                                              whyNoBuy = `Why no buy: ${err}`;
                                            } else {
                                              whyNoBuy = `Why no buy: no open position on ${sessChain || "this chain"} yet.`;
                                            }
                                          }
                                          const rawReason = String(
                                            sess?.exitReason
                                            || sess?.meta?.nkr_exit_reason
                                            || (Number(positionValue) <= 0
                                              ? (whyNoBuy || nkrStrategistStatus?.summary || "No open position yet — strategist is waiting for entry conditions.")
                                              : "Tracking live position.")
                                          );
                                          const reasonText = toUserFacingReason(rawReason);
                                          const whyClean = whyNoBuy ? toUserFacingMessage(whyNoBuy.replace(/^Why no buy:\s*/i, ""), "") : "";
                                          return (
                                            <>
                                              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                                <b style={{ color: "#8bdcff" }}>Reason:</b> {reasonText}
                                              </div>
                                              {Number(positionValue) <= 0 && whyClean && !String(reasonText).toLowerCase().includes("why no buy") && whyClean !== reasonText ? (
                                                <div style={{ minWidth: 0, overflowWrap: "anywhere", color: "#ffd166" }}>
                                                  <b>Why no buy:</b> {whyClean}
                                                </div>
                                              ) : null}
                                            </>
                                          );
                                        })()}
                                      </div>

                                      <div className="muted tiny" style={{ display: "grid", gap: 5, alignContent: "center" }}>
                                        <div><b style={{ color: gross >= 0 ? "#86efac" : "#ff8a8a" }}>Gross:</b> <span style={{ fontWeight: 850, color: gross >= 0 ? "#86efac" : "#ff8a8a" }}>{gross >= 0 ? "+" : ""}{fmtUsd(gross)}</span></div>
                                        <div><b style={{ color: "#ffd166" }}>Costs:</b> <span style={{ fontWeight: 850, color: "#ffd166" }}>{costs > 0 ? "-" : ""}{fmtUsd(Math.abs(costs))}</span></div>
                                        <div><b style={{ color: net >= 0 ? "#86efac" : "#ff8a8a" }}>Net:</b> <span style={{ fontSize: 15, fontWeight: 950, color: net >= 0 ? "#86efac" : "#ff8a8a" }}>{net >= 0 ? "+" : ""}{fmtUsd(net)}</span></div>
                                        <div><b style={{ color: ["REQUESTED","PENDING","EXITING"].includes(String(nkrExitUiState[nkrSessionControlKey(sess)] || "").toUpperCase()) ? "#8bdcff" : ((sess?.exitReady ?? sess?.meta?.nkr_exit_ready) ? "#86efac" : "#ffd166") }}>Exit:</b> {(() => { const k=nkrSessionControlKey(sess); const ui=String(nkrExitUiState[k] || "").toUpperCase(); const st=String(sess?.status || sess?.statusLabel || "").toUpperCase(); if (ui === "REQUESTED") return "REQUESTED"; if (ui === "PENDING" || ["CLOSING","STOPPING","EXITING","EXIT_PENDING"].includes(st)) return "PENDING"; if (ui === "FAILED" || st === "EXIT_FAILED") return "FAILED"; return (sess?.exitReady ?? sess?.meta?.nkr_exit_ready) ? "READY / WAITING" : "WAITING"; })()}</div>
                                      </div>

                                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch", justifyContent: "center", minWidth: 98 }}>
                                        <button
                                          className="miniBtn"
                                          type="button"
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNkrSessionInfoOpen({
                                            id: String(sess?.id || sess?.session_id || ""),
                                            route: routeTitle,
                                            status,
                                            chain,
                                            baseAsset,
                                            asset: sym,
                                            mode: sessionCapitalMode,
                                            observation: String(sess?.nkrObservationWindow || sess?.meta?.nkr_observation_window || immutableSessionSnapshot?.observationWindow || "—"),
                                            profitMode: String(sess?.nkrProfitMode || sess?.meta?.nkr_profit_mode || immutableSessionSnapshot?.profitMode || "—").toUpperCase(),
                                            periodDays: Number(sess?.nkrPeriodDays || sess?.meta?.nkr_period_days || immutableSessionSnapshot?.periodDays || 0),
                                            periodHours: Number(sess?.nkrPeriodHours || sess?.runtimeHours || sess?.meta?.nkr_period_hours || sess?.meta?.runtime_hours || immutableSessionSnapshot?.periodHours || 0),
                                            periodUnit: String(sess?.nkrPeriodUnit || sess?.meta?.nkr_period_unit || "days"),
                                            maxAssets: Number(sess?.maxActiveAssets ?? sess?.nkrMaxActiveAssets ?? sess?.meta?.nkr_max_active_assets ?? immutableSessionSnapshot?.maxActiveAssets ?? 0),
                                            payoutAsset: String(sess?.payoutAsset || sess?.meta?.payout_asset || immutableSessionSnapshot?.payoutAsset || manualPayoutAsset || baseAsset || "USDC").toUpperCase(),
                                            networkScope: String(sess?.networkScope || sess?.meta?.network_scope || immutableSessionSnapshot?.networkScope || rotationNetworkScope || chain),
                                            riskLimit: Number(sess?.riskLimit || sess?.meta?.risk_limit || sess?.riskLimitPct || immutableSessionSnapshot?.riskLimit || rotationRiskLimit || 0),
                                            maxSlippage: Number(sess?.maxSlippage || sess?.meta?.max_slippage || sess?.maxSlippagePct || immutableSessionSnapshot?.maxSlippage || rotationMaxSlippage || 0),
                                            minNetAdvantage: Number(sess?.minNetAdvantage || sess?.meta?.min_net_advantage || sess?.minNetAdvantagePct || immutableSessionSnapshot?.minNetAdvantage || rotationMinNetAdvantage || 0),
                                            sessionBudgetUsd: sessionBudgetUsd > 0 ? sessionBudgetUsd : (Number(sess?.budgetUsd || sess?.reservedUsd || workingCapital || 0) || 0),
                                            workingCapital: workingCapital > 0 ? workingCapital : (Number(sess?.workingCapitalUsd || sess?.budgetUsd || 0) || 0),
                                            protectedReserveUsd,
                                            plannedReserveUsd,
                                            reserveStatus,
                                            reserveAvailableToDeployUsd,
                                            allocationLabel,
                                            started: campaignStartTs,
                                            ends: campaignExpiresTs,
                                            runtimeText,
                                            leftText,
                                          }); }}
                                        >Info</button>
                                        {sessionStatus === "STOPPED" ? (
                                          <>
                                            <button className="miniBtn" type="button" onClick={async (e) => {
                                              e.preventDefault(); e.stopPropagation();
                                              const ch = String(sess?.chain || sess?.meta?.chain || sess?.meta?.chain_key || "").toUpperCase();
                                              const oid = String(sess?.onchainSessionId ?? sess?.meta?.onchain_session_id ?? sess?.coreVaultSessionId ?? "");
                                              if (!/^\d+$/.test(oid) || !ch) { setRotationBackendMsg("Exact chain/session id missing for this card."); return; }
                                              await applyNkrBackendControl("RESUME", { sessionId: oid, chain: ch });
                                            }}>Resume</button>
                                            <button className="miniBtn danger" type="button" onClick={async (e) => {
                                              e.preventDefault(); e.stopPropagation();
                                              const ch = String(sess?.chain || sess?.meta?.chain || sess?.meta?.chain_key || "").toUpperCase();
                                              const oid = String(sess?.onchainSessionId ?? sess?.meta?.onchain_session_id ?? sess?.coreVaultSessionId ?? "");
                                              const k = nkrSessionControlKey(ch, oid);
                                              setNkrExitUiState((p)=>({ ...p, [k]: "REQUESTED" }));
                                              const ok = await applyNkrBackendControl("STOP_EXIT", { sessionId: oid, chain: ch });
                                              setNkrExitUiState((p)=>({ ...p, [k]: ok ? "PENDING" : "FAILED" }));
                                            }}>Stop &amp; Exit</button>
                                          </>
                                        ) : (
                                          <>
                                            <button className="miniBtn" type="button"
                                              disabled={["PAUSE_PENDING","RESUME_PENDING","REQUESTED","PENDING","EXITING"].includes(String(nkrExitUiState[nkrSessionControlKey(sess)] || "").toUpperCase())}
                                              onClick={async (e) => {
                                              e.preventDefault(); e.stopPropagation();
                                              const ch = normalizeNkrChainKey(sess?.chain || sess?.meta?.chain || sess?.meta?.chain_key || sess?.chainId || sess?.meta?.chain_id || "");
                                              const oid = String(sess?.onchainSessionId ?? sess?.meta?.onchain_session_id ?? sess?.coreVaultSessionId ?? "");
                                              if (!/^\d+$/.test(oid) || !ch) { setRotationBackendMsg("Exact chain/session id missing for this card."); return; }
                                              const k = nkrSessionControlKey(ch, oid);
                                              const action = sessionStatus === "PAUSED" ? "RESUME" : "PAUSE";
                                              setNkrExitUiState((p) => ({ ...p, [k]: action === "PAUSE" ? "PAUSE_PENDING" : "RESUME_PENDING" }));
                                              const ok = await applyNkrBackendControl(action, { sessionId: oid, chain: ch });
                                              setNkrExitUiState((p) => { const n = { ...p }; if (ok) delete n[k]; else n[k] = "FAILED"; return n; });
                                            }}>{(() => { const ui=String(nkrExitUiState[nkrSessionControlKey(sess)] || "").toUpperCase(); if (ui === "PAUSE_PENDING") return "Pausing…"; if (ui === "RESUME_PENDING") return "Resuming…"; return sessionStatus === "PAUSED" ? "Resume" : "Pause"; })()}</button>
                                            <button className="miniBtn danger" type="button"
                                              disabled={["REQUESTED","PENDING","EXITING","PAUSE_PENDING","RESUME_PENDING"].includes(String(nkrExitUiState[nkrSessionControlKey(sess)] || "").toUpperCase())}
                                              onClick={async (e) => {
                                              e.preventDefault(); e.stopPropagation();
                                              const ch = normalizeNkrChainKey(sess?.chain || sess?.meta?.chain || sess?.meta?.chain_key || sess?.chainId || sess?.meta?.chain_id || "");
                                              const oid = String(sess?.onchainSessionId ?? sess?.meta?.onchain_session_id ?? sess?.coreVaultSessionId ?? "");
                                              const k = nkrSessionControlKey(ch, oid);
                                              if (!/^\d+$/.test(oid) || !ch) { setRotationBackendMsg("Exact chain/session id missing for this card."); return; }
                                              setNkrExitUiState((p)=>({ ...p, [k]: "REQUESTED" }));
                                              const ok = await applyNkrBackendControl("STOP_EXIT", { sessionId: oid, chain: ch });
                                              setNkrExitUiState((p)=>({ ...p, [k]: ok ? "PENDING" : "FAILED" }));
                                            }}>{["REQUESTED","PENDING","EXITING"].includes(String(nkrExitUiState[nkrSessionControlKey(sess)] || "").toUpperCase()) ? "Stopping…" : "Stop & Exit"}</button>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                  </div>
                                );                              }) : (
                                <div
                                  className="muted"
                                  style={{
                                    padding: "16px 12px",
                                    borderRadius: 12,
                                    border: "1px dashed rgba(255,255,255,.16)",
                                    background: "rgba(255,255,255,.025)",
                                  }}
                                >
                                  No active, paused or stopping NKR sessions yet. Approve a NKR budget and select a target to start tracking.
                                </div>
                              )}
                            </div>
                          </div>

                          {nkrSessionInfoOpen ? (
                            <div
                              className="modalBackdrop"
                              style={{ zIndex: 99999 }}
                              onClick={() => setNkrSessionInfoOpen(null)}
                            >
                              <div
                                className="modal modalHelp"
                                onClick={(e) => e.stopPropagation()}
                                style={{ maxWidth: 620, background: "linear-gradient(180deg, rgba(8,35,31,1), rgba(6,20,19,1))" }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                                  <div>
                                    <div className="label" style={{ marginBottom: 2 }}>NKR Session Info</div>
                                    <div className="muted tiny">{nkrSessionInfoOpen.route}</div>
                                  </div>
                                  <button className="miniBtn" type="button" onClick={() => setNkrSessionInfoOpen(null)}>Close</button>
                                </div>
                                <div
                                  className="muted tiny"
                                  style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "1fr 1fr", gap: 9, marginTop: 14, lineHeight: 1.5 }}
                                >
                                  <div><b>Status:</b> {nkrSessionInfoOpen.status}</div>
                                  <div><b>Session ID:</b> {nkrSessionInfoOpen.id || "—"}</div>
                                  <div><b>Chain:</b> {nkrSessionInfoOpen.chain}</div>
                                  <div><b>Route:</b> {nkrSessionInfoOpen.route}</div>
                                  <div><b>Capital Mode:</b> {String(nkrSessionInfoOpen.mode || "—").replaceAll("_", " ")}</div>
                                  <div><b>Observation Window:</b> {nkrSessionInfoOpen.observation}</div>
                                  <div><b>Profit Mode:</b> {String(nkrSessionInfoOpen.profitMode || "—").replaceAll("_", " ")}</div>
                                  <div><b>Period:</b> {Number(nkrSessionInfoOpen.periodHours || 0) > 0 ? (String(nkrSessionInfoOpen.periodUnit || "days").toLowerCase() === "hours" ? `${nkrSessionInfoOpen.periodHours} hours` : `${nkrSessionInfoOpen.periodDays} days`) : "—"}</div>
                                  <div><b>Max Active Assets:</b> {nkrSessionInfoOpen.maxAssets || "—"}</div>
                                  <div><b>Payout Asset:</b> {nkrSessionInfoOpen.payoutAsset || "—"}</div>
                                  <div><b>Network Scope:</b> {nkrSessionInfoOpen.networkScope || "—"}</div>
                                  <div style={{ gridColumn: "1 / -1", marginTop: 4, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,.10)", fontWeight: 900, color: "#eafff5" }}>CAPITAL</div>
                                  <div><b>Total Session Budget:</b> {fmtUsd(Number(nkrSessionInfoOpen.sessionBudgetUsd || 0))}</div>
                                  <div><b>Allocated for Trading:</b> {fmtUsd(Number(nkrSessionInfoOpen.workingCapital || 0))}</div>
                                  <div><b>Stable Reserve:</b> {fmtUsd(Number(nkrSessionInfoOpen.protectedReserveUsd || 0))}</div>
                                  <div><b>Planned Reserve:</b> {fmtUsd(Number(nkrSessionInfoOpen.plannedReserveUsd || 0))}</div>
                                  <div><b>Reserve Status:</b> {nkrSessionInfoOpen.reserveStatus || "HELD"}</div>
                                  <div><b>Available to Deploy:</b> {fmtUsd(Number(nkrSessionInfoOpen.reserveAvailableToDeployUsd || 0))}</div>
                                  <div><b>Allocation:</b> {nkrSessionInfoOpen.allocationLabel || "—"}</div>
                                  <div><b>Risk Limit:</b> {Number(nkrSessionInfoOpen.riskLimit || 0) > 0 ? `${nkrSessionInfoOpen.riskLimit}%` : "—"}</div>
                                  <div><b>Max Slippage:</b> {Number(nkrSessionInfoOpen.maxSlippage || 0) > 0 ? `${nkrSessionInfoOpen.maxSlippage}%` : "—"}</div>
                                  <div><b>Min Net Advantage:</b> {Number(nkrSessionInfoOpen.minNetAdvantage || 0) > 0 ? `${nkrSessionInfoOpen.minNetAdvantage}%` : "—"}</div>
                                  <div><b>Started:</b> {nkrSessionInfoOpen.started ? new Date(nkrSessionInfoOpen.started).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}</div>
                                  <div><b>Ends:</b> {nkrSessionInfoOpen.ends ? new Date(nkrSessionInfoOpen.ends).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}</div>
                                  <div><b>Runtime:</b> {nkrSessionInfoOpen.runtimeText || "—"}</div>
                                  <div><b>Time Left:</b> {nkrSessionInfoOpen.leftText || "—"}</div>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {/* ENGINE-111: Rotation movements are shown only in NKR Event History. */}

                          <details
                            style={{
                              borderRadius: 12,
                              border: "1px solid rgba(34,197,94,.24)",
                              background: "rgba(0,0,0,.16)",
                              overflow: "hidden",
                            }}
                          >
                            <summary
                              onMouseDown={(e) => { e.stopPropagation(); }}
                              onClick={(e) => { e.stopPropagation(); }}
                              style={{
                                cursor: "pointer",
                                padding: "9px 10px",
                                fontWeight: 950,
                                color: "#eafff5",
                                listStyle: "none",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>Nexus NKR Setup</span>
                              <span className="muted tiny">Show ▼</span>
                            </summary>
                            <div style={{ padding: "10px", display: "grid", gap: 12 }}>
                              <div style={{ display: "grid", gap: 12, width: "100%" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompactMobile ? "1fr" : "1fr 1fr",
                        gap: isCompactMobile ? 8 : 10,
                      }}
                    >
                      <div className="formRow">
                        <label>Nexus NKR Capital Mode</label>
                        <select value={nkrCapitalMode} onFocus={() => { nkrModeEditingRef.current = true; }} onBlur={() => { setTimeout(() => { nkrModeEditingRef.current = false; }, 500); }} onChange={(e) => { nkrModeEditingRef.current = true; handleNkrCapitalModeChange(e.target.value); }}>
                          <option value="DYNAMIC">Dynamic</option>
                          <option value="TACTICAL">Tactical</option>
                          <option value="AGGRESSIVE">Aggressive</option>
                          <option value="DEFENSIVE">Defensive</option>
                        </select>
                      </div>
                      <div className="formRow">
                        <label>Observation Window</label>
                        <select value={nkrObservationWindow} onChange={(e) => { setNkrObservationWindow(e.target.value); setRotationBudgetReleased(false); }}>
                          <option value="15m">15m</option>
                          <option value="1h">1h</option>
                          <option value="4h">4h</option>
                          <option value="12h">12h</option>
                          <option value="24h">24h</option>
                        </select>
                      </div>
                      <div className="formRow">
                        <label>Profit Mode</label>
                        <select value={nkrProfitMode} onChange={(e) => { setNkrProfitMode(e.target.value); setRotationBudgetReleased(false); }}>
                          <option value="REINVEST">Reinvest</option>
                          <option value="PAYOUT">Payout</option>
                          <option value="PAYOUT_PERCENTAGE">Payout Percentage</option>
                          <option value="HOLD_STABLE">Hold Stable</option>
                        </select>
                      </div>
                      <div className="formRow">
                        <label>NKR Period</label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8 }}>
                          <input
                            type="number"
                            min="0.01"
                            step="any"
                            value={nkrPeriodDays}
                            onFocus={() => { nkrPeriodEditingRef.current = true; }}
                            onBlur={() => {
                              nkrPeriodEditingRef.current = false;
                              const n = Number(String(nkrPeriodDays || "").replace(",", "."));
                              if (Number.isFinite(n) && n > 0) setNkrPeriodDays(String(n));
                            }}
                            onChange={(e) => { nkrPeriodEditingRef.current = true; setNkrPeriodDays(e.target.value); setRotationBudgetReleased(false); }}
                            placeholder={String(nkrPeriodUnit).toLowerCase() === "hours" ? "e.g. 2, 6, 12" : "e.g. 1, 7, 14"}
                          />
                          <select
                            value={nkrPeriodUnit}
                            onChange={(e) => {
                              nkrPeriodEditingRef.current = true;
                              const nextUnit = String(e.target.value || "days").toLowerCase();
                              const currentUnit = String(nkrPeriodUnit || "days").toLowerCase();
                              const currentValue = Number(String(nkrPeriodDays || "").replace(",", "."));
                              if (Number.isFinite(currentValue) && currentValue > 0 && nextUnit !== currentUnit) {
                                // Keep the same duration: 2 days ↔ 48 hours (no fractional spin).
                                let converted = currentValue;
                                if (currentUnit === "days" && nextUnit === "hours") converted = currentValue * 24;
                                else if (currentUnit === "hours" && nextUnit === "days") converted = currentValue / 24;
                                // Prefer whole numbers when possible for cleaner typing.
                                const rounded = Math.abs(converted - Math.round(converted)) < 1e-9
                                  ? Math.round(converted)
                                  : Math.round(converted * 100) / 100;
                                setNkrPeriodDays(String(rounded));
                              }
                              setNkrPeriodUnit(nextUnit);
                              setRotationBudgetReleased(false);
                              setTimeout(() => { nkrPeriodEditingRef.current = false; }, 5000);
                            }}
                            title="NKR period unit"
                          >
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                      </div>
                      <div className="formRow">
                        {(() => {
                          const normalizeNkrChain = (value) => {
                            const k = String(value || "").trim().toUpperCase();
                            if (k === "ETHEREUM" || k === "1") return "ETH";
                            if (k === "BSC" || k === "56") return "BNB";
                            if (k === "POLYGON" || k === "MATIC" || k === "137") return "POL";
                            return k || "";
                          };
                          const selectedNkrChain = normalizeNkrChain(liveVaultChainByMode?.rotation || "ETH") || "ETH";
                          const activeRows = Array.isArray(rotationSessions)
                            ? rotationSessions.filter((s) => !["STOPPED","PAUSED","EXPIRED","CLOSED","FINALIZED","STOPPING","FINALIZING","COMPLETE","COMPLETED","CANCELLED","RELEASED","DELETED","ARCHIVED"].includes(String(s?.status || "").toUpperCase()))
                            : [];
                          const hasActiveNkrRun = activeRows.length > 0;
                          const hasSessionOnSelectedChain = activeRows.some((s) => {
                            const ch = normalizeNkrChain(s?.chain || s?.meta?.chain || s?.meta?.chain_key || "");
                            return ch === selectedNkrChain || (!ch && selectedNkrChain === "ETH");
                          });
                          // Parallel start on a free chain uses the same amount field as top-up,
                          // so the user can type 20 and press Start NKR · BNB without a hidden field.
                          const startingParallel = hasActiveNkrRun && !hasSessionOnSelectedChain;
                          // BUILD393: no on-chain addBudget — field is only for starting a session budget.
                          const label = startingParallel
                            ? `New session budget on ${selectedNkrChain} ($)`
                            : hasActiveNkrRun && hasSessionOnSelectedChain
                              ? `Session budget on ${selectedNkrChain} (fixed on-chain)`
                              : "NKR Budget ($)";
                          return (
                            <>
                              <label>{label}</label>
                              <input
                                value={rotationBudgetRelease}
                                onChange={(e) => handleNkrBudgetInputChange(e.target.value)}
                                disabled={hasActiveNkrRun && hasSessionOnSelectedChain}
                                placeholder={startingParallel ? `e.g. 20 for ${selectedNkrChain}` : "e.g. 12000"}
                                title={hasActiveNkrRun && hasSessionOnSelectedChain ? "CoreVault session budget is fixed at createSession. Start another chain to allocate a new budget." : undefined}
                              />
                            </>
                          );
                        })()}
                      </div>
                      <div className="formRow">
                        <label>Max Active NKR Assets (soft · 0 = unlimited)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={rotationMaxActiveSessions}
                          onFocus={(e) => { nkrMaxAssetsEditingRef.current = true; setNkrMaxAssetsEditing(true); try { e.currentTarget.select(); } catch {} }}
                          onBlur={() => {
                            const n = Math.max(0, Math.floor(Number(rotationMaxActiveSessions || 0)));
                            setRotationMaxActiveSessions(String(Number.isFinite(n) ? n : 0));
                            nkrMaxAssetsEditingRef.current = false;
                            setNkrMaxAssetsEditing(false);
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "" || /^\d+$/.test(raw)) {
                              setRotationMaxActiveSessions(raw);
                              setRotationBudgetReleased(false);
                            }
                          }}
                          placeholder="0 = unlimited sessions/assets"
                        />
                      </div>
                    </div>


                    <div className="formRow" style={{ gridColumn: "1 / -1" }}>
                      <label>Allowed assets (optional)</label>
                      <div className="muted tiny" style={{ marginBottom: 6 }}>
                        Same as Trader: leave empty → Strategist picks the best asset on this session chain only.
                        Select e.g. ETH → NKR only trades that symbol (not every token on the chain).
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {(Array.isArray(nkrAssetOptions) ? nkrAssetOptions : []).map((sym) => {
                          const on = selectedNkrAssets.includes(sym);
                          return (
                            <button
                              key={`nkr-allow-${sym}`}
                              type="button"
                              onClick={() => toggleNkrAsset(sym)}
                              style={{
                                borderRadius: 999,
                                border: on ? "1px solid rgba(34,197,94,.55)" : "1px solid rgba(139,220,255,.22)",
                                background: on ? "rgba(34,197,94,.16)" : "rgba(255,255,255,.04)",
                                color: on ? "#86efac" : "#cfefff",
                                fontWeight: 800,
                                fontSize: 11,
                                padding: "4px 10px",
                                cursor: "pointer",
                              }}
                            >
                              {sym}
                            </button>
                          );
                        })}
                      </div>
                      <div className="muted tiny" style={{ marginTop: 6 }}>
                        Selected: {selectedNkrAssets.length ? selectedNkrAssets.join(", ") : "none (Strategist picks on chain)"}
                      </div>
                    </div>

                    {nkrAggressiveConsentOpen && (
                      <div
                        className="modalBackdrop"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelNkrAggressiveConsent(); }}
                        style={{ zIndex: 99999 }}
                      >
                        <div
                          className="modal modalHelp"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: "linear-gradient(180deg, rgba(34,18,12,1), rgba(12,18,17,1))",
                            border: "1px solid rgba(255,193,7,.35)",
                            maxWidth: 540,
                          }}
                        >
                          <div className="modalHead">
                            <div className="cardTitle">Nexus NKR Aggressive Mode Warning</div>
                            <button
                              className="iconBtn"
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelNkrAggressiveConsent(); }}
                            >
                              ×
                            </button>
                          </div>
                          <div className="helpBody">
                            <p><b>Warning:</b> Nexus NKR Aggressive gives the capital manager more freedom to rotate faster, stop weak sessions sooner and concentrate more capital into stronger assets.</p>
                            <p>This can create higher costs, stronger short-term fluctuations and losses. Hard safety, reserve, net-cost and Vault validation rules remain active.</p>
                            <p>Please confirm that you understand the increased risk before activating Nexus NKR Aggressive.</p>
                            <div className="row" style={{ gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
                              <button className="btnGhost" type="button" onClick={cancelNkrAggressiveConsent}>Cancel</button>
                              <button className="btn" type="button" onClick={confirmNkrAggressiveConsent}>I Understand and Accept the Risk</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {renderPayoutAssetSelector("Payout asset", { allowExtraAssets: false })}

                    {renderFundingPrompt("ROTATION")}

                    {(() => {
                      const normalizeNkrChain = (value) => {
                        const k = String(value || "").trim().toUpperCase();
                        if (k === "ETHEREUM" || k === "1") return "ETH";
                        if (k === "BSC" || k === "56") return "BNB";
                        if (k === "POLYGON" || k === "MATIC" || k === "137") return "POL";
                        return k || "";
                      };
                      const selectedNkrChain = normalizeNkrChain(liveVaultChainByMode?.rotation || activeNkrChainKey || "ETH") || "ETH";
                      const activeRows = Array.isArray(rotationVisibleActiveRows) ? rotationVisibleActiveRows : [];
                      const sessionChainOf = (s) => normalizeNkrChain(
                        s?.chain || s?.meta?.chain || s?.meta?.chain_key || s?.chainKey ||
                        ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(s?.chainId || s?.chain_id || s?.meta?.chain_id || 0)] || "")
                      );
                      // Prefer session on the selected vault chain for Pause / Stop / Top-up.
                      const activeNkrSession = activeRows.find((s) => sessionChainOf(s) === selectedNkrChain) ||
                        activeRows.find((s) => {
                          const st = String(getRotationDerivedStatus(s) || s?.status || "").toUpperCase();
                          const qty = Number(s?.positionQty ?? s?.positionAmount ?? s?.openRotation?.positionQty ?? s?.meta?.nkr_position_qty ?? 0) || 0;
                          return qty > 0 || !["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "FINALIZED"].includes(st);
                        }) ||
                        (Array.isArray(rotationSessions)
                          ? rotationSessions.find((s) => !["STOPPED", "PAUSED", "EXPIRED", "CLOSED", "FINALIZED"].includes(String(s?.status || "").toUpperCase()))
                          : null);
                      const hasActiveNkrRun = activeRows.length > 0 || Boolean(activeNkrSession);
                      const hasSessionOnSelectedChain = activeRows.some((s) => sessionChainOf(s) === selectedNkrChain);
                      // Session count is unlimited — user may run ETH + BNB + POL (+ more) in parallel.
                      // Soft "Max Active NKR Assets" only guides capital allocation, never blocks Start.
                      const blockStart = hasSessionOnSelectedChain;
                      const startTitle = hasSessionOnSelectedChain
                        ? `NKR is already running on ${selectedNkrChain}. Switch chain or stop that session to start another.`
                        : `Start NKR on ${selectedNkrChain}`;
                      const startLabel = hasSessionOnSelectedChain
                        ? `Running on ${selectedNkrChain}`
                        : privyAutomationBusy
                          ? "Activating Privy..."
                          : rotationBackendLoading
                            ? "Starting..."
                            : `Start NKR · ${selectedNkrChain}`;
                      return (
                        <div className="btnRow">
                          <button
                            className="btn"
                            type="button"
                            disabled={(() => {
                              const amount = Number(String(rotationBudgetRelease || rotationCapitalTopup || "").replace(",", "."));
                              return blockStart || rotationBackendLoading || privyAutomationBusy || !Number.isFinite(amount) || amount <= 0;
                            })()}
                            onClick={releaseRotationBudget}
                            title={startTitle}
                          >
                            {startLabel}
                          </button>
                          {hasActiveNkrRun && activeNkrSession && (
                            <>
                              <button
                                className="miniBtn"
                                type="button"
                                onClick={() => {
                                  const s = activeNkrSession;
                                  const ch = String(s?.chain || s?.meta?.chain || s?.meta?.chain_key || selectedNkrChain || "").toUpperCase();
                                  const oid = String(s?.onchainSessionId ?? s?.coreVaultSessionId ?? s?.meta?.onchain_session_id ?? "");
                                  const lid = String(s?.id || s?.session_id || (ch && oid ? `NKR-LIVE-${ch}-${oid}` : oid) || "");
                                  if (!lid) { setRotationBackendMsg("No active session selected for Pause/Resume."); return; }
                                  const rawId = Number(s?.rawStatusId ?? s?.statusId ?? s?.onchainStatusId ?? s?.meta?.raw_status_id ?? s?.meta?.status_id);
                                  const actualStatus = rawId === 2 ? "PAUSED" : String(s?.onchainStatus || s?.statusLabel || s?.status || "").toUpperCase();
                                  applyNkrBackendControl(actualStatus === "PAUSED" ? "RESUME" : "PAUSE", { sessionId: oid || lid, chain: ch });
                                }}
                                title={`Pause or resume NKR on ${sessionChainOf(activeNkrSession) || selectedNkrChain}.`}
                              >
                                {(() => { const s = activeNkrSession; const rid = Number(s?.rawStatusId ?? s?.statusId ?? s?.onchainStatusId ?? s?.meta?.raw_status_id ?? s?.meta?.status_id); const st = rid === 2 ? "PAUSED" : String(s?.onchainStatus || s?.statusLabel || s?.status || "").toUpperCase(); return st === "PAUSED" ? "Resume" : "Pause"; })()}
                              </button>
                              <button
                                className="miniBtn danger"
                                type="button"
                                onClick={() => applyNkrBackendControl("STOP_EXIT", { sessionId: String(activeNkrSession?.onchainSessionId ?? activeNkrSession?.coreVaultSessionId ?? activeNkrSession?.id ?? activeNkrSession?.session_id ?? "") })}
                                title={`Stop & Exit on ${sessionChainOf(activeNkrSession) || selectedNkrChain}: sell open position, then finalize.`}
                              >
                                Stop & Exit
                              </button>
                            </>
                          )}
                          {rotationBudgetReleased && !hasActiveNkrRun && (
                            <button className="miniBtn" type="button" onClick={resetRotationBudgetRelease}>Reset budget</button>
                          )}
                          {hasActiveNkrRun ? (
                            <div className="muted tiny" style={{ width: "100%", marginTop: 4 }}>
                              Active sessions: {activeRows.length} (unlimited)
                              {activeRows.length
                                ? ` · ${[...new Set(activeRows.map(sessionChainOf).filter(Boolean))].join(", ") || "—"}`
                                : ""}
                              {hasSessionOnSelectedChain
                                ? ` · ${selectedNkrChain} already has a session — pick another chain to start a parallel run`
                                : ` · ready to start on ${selectedNkrChain}`}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                    {privyAutomationError && (
                      <div className="muted tiny" style={{ marginTop: 6, color: "#ff8a8a" }}>
                        Privy automation: {privyAutomationError}
                      </div>
                    )}
                    {rotationBackendMsg ? (
                      <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 9, border: "1px solid rgba(245,193,108,.35)", background: "rgba(245,193,108,.08)", color: "#f5c16c", fontSize: 12, wordBreak: "break-word" }}>
                        {rotationBackendMsg}
                      </div>
                    ) : null}
                    {(() => {
                      const activeSession = (Array.isArray(rotationVisibleActiveRows) ? rotationVisibleActiveRows[0] : null) ||
                        (Array.isArray(rotationSessions)
                          ? rotationSessions.find((sess) => !["STOPPED", "EXPIRED", "CLOSED", "FINALIZED"].includes(String(sess?.status || "").toUpperCase()))
                          : null);
                      if (!activeSession) return <div className="muted tiny" style={{ marginTop: 8 }}>Status: Ready</div>;
                      const statusSession = activeSession;
                      const asset = String(statusSession?.symbol || statusSession?.targetAsset || "").toUpperCase();
                      const activeSessionId = String(statusSession?.id || statusSession?.session_id || "");
                      const activeWorkingCapital = getNkrSessionWorkingCapitalUsd(activeSession) || Number(statusSession?.budgetUsd || 0) || 0;
                      const activeReserveRaw = Number(statusSession?.nkrCashReserveUsd ?? statusSession?.meta?.nkr_cash_reserve_usd ?? statusSession?.protectedReserveUsd ?? statusSession?.meta?.protected_reserve_usd);
                      const activeReserve = Number.isFinite(activeReserveRaw) ? Math.max(0, activeReserveRaw) : 0;
                      // BUILD439: never use draft nkrCapitalMode for a running session.
                      const activeStartMode = String(statusSession?.nkrCapitalMode || statusSession?.capitalMode || statusSession?.meta?.nkr_capital_mode || statusSession?.meta?.capital_mode || statusSession?.meta?.nkrCapitalMode || "LOCKED").toUpperCase();
                      const activeReservePctByMode = { AGGRESSIVE: 10, DYNAMIC: 20, TACTICAL: 25, DEFENSIVE: 35 };
                      const activeAllocationFraction = Math.max(0.01, 1 - (Number(activeReservePctByMode[activeStartMode] ?? 0) / 100));
                      const explicitActiveTotalBudget = Math.max(
                        Number(statusSession?.nkrTotalBudgetUsd || statusSession?.meta?.nkr_total_budget_usd || statusSession?.sessionBudgetUsd || statusSession?.meta?.session_budget_usd || statusSession?.budgetUsd || 0) || 0,
                        Number(statusSession?.workingCapitalUsd || statusSession?.meta?.working_capital_usd || 0) || 0
                      );
                      const activeModeDerivedTotal = explicitActiveTotalBudget <= 0 && activeWorkingCapital > 0 ? activeWorkingCapital / activeAllocationFraction : 0;
                      const activeTotalBudget = explicitActiveTotalBudget > 0
                        ? explicitActiveTotalBudget
                        : Math.max(activeModeDerivedTotal, Math.max(0, activeWorkingCapital) + Math.max(0, activeReserve));
                      return (
                        <div className="muted tiny" style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
                          <span><b>Status:</b> {String(nkrControlState || statusSession?.status || "RUNNING").toUpperCase()}</span>
                          {asset ? <span><b>Asset:</b> {asset}</span> : null}
                          <span><b>Budget:</b> {fmtUsd(activeTotalBudget)}</span>
                        </div>
                      );
                    })()}

                    
                  </div>
                            </div>
                          </details>

                          {shadowUiEnabled ? (
                          <div
                            style={{
                              padding: "10px 12px",
                              borderRadius: 12,
                              background: "rgba(14,165,233,.08)",
                              border: "1px solid rgba(14,165,233,.24)",
                              display: "grid",
                              gap: 8,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <div style={{ color: "#8bdcff", fontWeight: 950 }}>Nexus NKR Shadow</div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button
                                  className="miniBtn"
                                  type="button"
                                  disabled={rotationShadowBusy}
                                  onClick={() => runRotationShadowSimulation()}
                                  title="Run paper-only NKR simulation with current Watchlist/market data and backend safety preview."
                                >
                                  {rotationShadowBusy ? "Reading..." : "Run NKR Shadow"}
                                </button>
                                <button
                                  className="miniBtn"
                                  type="button"
                                  disabled={rotationBackendLoading}
                                  onClick={startRotationSafeMode}
                                  title="Backend safety-readiness check only. No transaction is sent."
                                >
                                  {rotationBackendLoading ? "Checking..." : "Safety Check"}
                                </button>
                                <button
                                  className="miniBtn"
                                  type="button"
                                  disabled={rotationShadowBusy || (!controllableRotations && String(nkrControlState || "WAITING").toUpperCase() === "STOPPED")}
                                  onClick={() => {
                                    const isPaused = String(nkrControlState || "").toUpperCase() === "PAUSED" || (pausedRotations > 0 && activeRotations === 0);
                                    applyNkrBackendControl(isPaused ? "RESUME" : "PAUSE", { allSessions: true });
                                  }}
                                  title="Pause or resume all active NKR sessions. Paper-only; no Vault transaction is sent."
                                >
                                  {String(nkrControlState || "").toUpperCase() === "PAUSED" || (pausedRotations > 0 && activeRotations === 0) ? "Resume NKR" : "Pause NKR"}
                                </button>
                                <button
                                  className="miniBtn"
                                  type="button"
                                  disabled={!controllableRotations && String(nkrControlState || "WAITING").toUpperCase() === "WAITING"}
                                  onClick={() => applyNkrBackendControl("STOP_EXIT")}
                                  title="Stop & Exit: sell every open CoreVault position to the settlement asset, then finalize the session."
                                  style={{ borderColor: "rgba(255,107,107,.45)", color: "#ff8a8a" }}
                                >
                                  Stop & Exit
                                </button>
                                {String(nkrControlState || "").toUpperCase() === "STOPPED" ? (
                                  <>
                                    <button
                                      className="miniBtn"
                                      type="button"
                                      onClick={() => applyNkrBackendControl("RESUME")}
                                      title="Resume a stopped NKR session by explicit user action."
                                    >
                                      Resume NKR
                                    </button>
                                    <button
                                      className="miniBtn"
                                      type="button"
                                      onClick={() => applyNkrBackendControl("DELETE")}
                                      title="Delete stopped NKR sessions forever from backend. They will not come back after reload or on another device."
                                      style={{ borderColor: "rgba(255,107,107,.65)", color: "#ff8a8a" }}
                                    >
                                      Delete NKR
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                            <div className="muted tiny" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                              <EngineEventHistory engine="NKR" events={nkrEventHistory} />
                              <b style={{ color: rotationShadowSnapshot?.status === "error" ? "#ff8a8a" : "#86efac" }}>Runtime: {rotationShadowRuntimeStatus}</b>
                              <b style={{ color: rotationShadowWorkStatus === "RUNNING" ? "#86efac" : "#ffd166" }}>Status: {rotationShadowWorkStatus}</b>
                              <b style={{ color: "#8bdcff" }}>Readiness: {rotationShadowReadiness}</b>
                              <b style={{ color: "#ffd166" }}>Simulated NKR sessions: {activeRotations}</b>
                              <b>Best edge: {rotationShadowSnapshot?.bestScore ? `${rotationShadowSnapshot.bestScore}/100` : rotationSelectedPick?.score ? `${rotationSelectedPick.score}/100` : "—"}</b>
                              <b>Net: {Number.isFinite(Number(rotationShadowSnapshot?.netUsd)) ? fmtUsd(Number(rotationShadowSnapshot.netUsd)) : "—"}</b>
                              <b>Profit lock: {Number.isFinite(Number(rotationShadowSnapshot?.profitLockPct)) ? `${Number(rotationShadowSnapshot.profitLockPct).toFixed(2)}%` : "—"}</b>
                              <b>Safety: paper-only</b>
                            </div>
                            {rotationShadowSnapshot?.bestSymbol ? (
                              <div className="muted tiny" style={{ display: "grid", gap: 4, padding: "7px 8px", borderRadius: 10, background: "rgba(0,0,0,.14)", border: "1px solid rgba(255,255,255,.07)" }}>
                                <div><b style={{ color: "#8bdcff" }}>Current shadow target:</b> {rotationShadowSnapshot.baseAsset || manualPayoutAsset || "USDC"} → {rotationShadowSnapshot.bestSymbol} → {rotationShadowSnapshot.baseAsset || manualPayoutAsset || "USDC"} · action {rotationShadowSnapshot.action || "WAIT"}</div>
                                <div>Gross {fmtUsd(Number(rotationShadowSnapshot.grossUsd || 0))} · Costs {fmtUsd(Number(rotationShadowSnapshot.costsUsd || 0))} · Net {fmtUsd(Number(rotationShadowSnapshot.netUsd || 0))} {Number.isFinite(Number(rotationShadowSnapshot.netPct)) ? `(${Number(rotationShadowSnapshot.netPct).toFixed(2)}%)` : ""}</div>
                              </div>
                            ) : null}
                          </div>
                          ) : null}

                          {/* NKR Event History is independent from Shadow visibility/runtime. */}
                            {(() => {
                              const sessionEvents = (Array.isArray(rotationRows) ? rotationRows : []).flatMap((sess) => {
                                const arr = Array.isArray(sess?.rotationEvents) ? sess.rotationEvents : [];
                                return arr.map((ev) => ({ ...ev, sessionSymbol: sess?.targetAsset || sess?.symbol || ev?.targetAsset || "" }));
                              });
                              const localEvents = (Array.isArray(rotationShadowEvents) ? rotationShadowEvents : []).map((ev) => ({ ...ev, localOnly: true }));
                              const classifyEventKind = (ev) => {
                                const explicit = String(ev?.eventKind || ev?.event_kind || "").toUpperCase();
                                if (["ON_CHAIN", "ONCHAIN", "SHADOW", "MONITOR"].includes(explicit)) {
                                  return explicit === "ONCHAIN" ? "ON_CHAIN" : explicit;
                                }
                                const tx = String(ev?.txHash || ev?.tx_hash || ev?.lastTxHash || ev?.tx || "").trim();
                                const mode = String(ev?.mode || "").toLowerCase();
                                const st = String(ev?.status || ev?.action || "").toUpperCase();
                                const hasBuySell = ["BUY", "SELL", "OPEN", "CLOSE", "ADD", "REDUCE", "OPEN_POSITION", "BUY_OPEN", "CLOSED_PROFIT", "CLOSED_LOSS"].some((k) => st.includes(k));
                                if (tx && hasBuySell) return "ON_CHAIN";
                                if (mode === "live" && hasBuySell) return "ON_CHAIN";
                                if (["WAIT", "HOLD", "MONITOR", "TICK", "WAITING_ENTRY", "POSITION_TRACKING", "READY_DISPATCHED"].some((k) => st.includes(k))) return "MONITOR";
                                if (ev?.localOnly || mode === "shadow") return "SHADOW";
                                return hasBuySell ? "SHADOW" : "MONITOR";
                              };
                              const normalizedEvents = [...sessionEvents, ...localEvents]
                                .filter((ev) => ev && typeof ev === "object")
                                .map((ev) => {
                                  const kind = classifyEventKind(ev);
                                  return {
                                    ...ev,
                                    event_id: ev.event_id || ev.id || ev.eventId || "",
                                    trade_id: ev.trade_id || ev.tradeId || "",
                                    session_id: ev.session_id || ev.sessionId || "",
                                    buyPriceUsd: ev.buyPriceUsd ?? ev.entryPriceUsd ?? ev.entry_price_usd ?? ev.entryPrice ?? "",
                                    sellPriceUsd: ev.sellPriceUsd ?? ev.exitPriceUsd ?? ev.exit_price_usd ?? ev.exitPrice ?? "",
                                    buyUsd: ev.buyUsd ?? ev.amountInUsd ?? "",
                                    sellUsd: ev.sellUsd ?? ev.amountOutUsd ?? "",
                                    grossUsd: ev.grossUsd ?? ev.grossProfitUsd ?? "",
                                    netUsd: ev.netUsd ?? ev.netProfitUsd ?? "",
                                    capitalMovedUsd: ev.capitalMovedUsd ?? ev.availableStableDeltaUsd ?? "",
                                    capitalBeforeUsd: ev.capitalBeforeUsd ?? "",
                                    capitalAfterUsd: ev.capitalAfterUsd ?? "",
                                    eventKind: kind,
                                    onChain: kind === "ON_CHAIN",
                                    txHash: String(ev?.txHash || ev?.tx_hash || ev?.lastTxHash || ev?.tx || "").trim(),
                                  };
                                })
                                .filter((ev) => ev.status || ev.action || ev.targetAsset || ev.event_id || ev.trade_id || ev.text);
                              const byId = new Map();
                              normalizedEvents.forEach((ev) => {
                                const key = String(ev?.event_id || `${ev?.trade_id || "EV"}-${ev?.status || ev?.action || "EVENT"}-${ev?.ts || Math.random()}`);
                                if (!byId.has(key)) byId.set(key, ev);
                              });
                              const allEvents = Array.from(byId.values()).sort((a, b) => Number(b?.ts || 0) - Number(a?.ts || 0));
                              const onChainCount = allEvents.filter((ev) => ev.eventKind === "ON_CHAIN").length;
                              const filter = String(rotationShadowEventsFilter || "ALL").toUpperCase();
                              const filteredEvents = allEvents.filter((ev) => {
                                const st = String(ev?.status || ev?.action || "").toUpperCase();
                                const kind = String(ev?.eventKind || "").toUpperCase();
                                if (filter === "ALL") return true;
                                if (filter === "ON_CHAIN") return kind === "ON_CHAIN";
                                if (filter === "MONITOR") return kind === "MONITOR" || kind === "SHADOW";
                                if (filter === "OPEN") return ["OPEN_POSITION", "BUY_OPEN", "POSITION_TRACKING", "EXECUTOR_ACTIVE", "DISPATCHED", "READY_DISPATCHED", "OPEN"].includes(st);
                                if (filter === "CLOSED_PROFIT") return st === "CLOSED_PROFIT" || st === "SIMULATED_ROTATION_CLOSED";
                                if (filter === "CLOSED_LOSS") return st === "CLOSED_LOSS" || st === "CLOSED_LOSS_REALLOCATE" || st === "REBALANCED_OUT";
                                if (filter === "CAPITAL") return st === "CAPITAL_MOVEMENT" || st.includes("CAPITAL") || st.includes("REBALANCE");
                                if (filter === "PROTECTED") return st.includes("PROTECT");
                                if (filter === "SAFETY") return st.includes("SAFETY") || st.includes("PANIC");
                                if (filter === "ERROR") return st.includes("ERROR") || st.includes("FAIL");
                                return true;
                              });
                              const visibleEvents = rotationShadowEventsShowAll ? filteredEvents : filteredEvents.slice(0, 10);
                              const exportJson = () => {
                                const blob = new Blob([JSON.stringify(filteredEvents, null, 2)], { type: "application/json" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url; a.download = `nkr-event-history-${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
                              };
                              const exportCsv = () => {
                                const cols = ["event_id","trade_id","session_id","status","action","fromAsset","toAsset","targetAsset","route","buyTime","sellTime","buyPriceUsd","sellPriceUsd","buyUsd","sellUsd","grossUsd","costsUsd","netUsd","capitalBeforeUsd","capitalMovedUsd","capitalAfterUsd","netPct","addedToCollectedProfit","alreadyCounted","reason"];
                                const val = (ev, c) => {
                                  if (c === "event_id") return ev?.event_id || ev?.id || ev?.eventId || "";
                                  if (c === "trade_id") return ev?.trade_id || ev?.tradeId || "";
                                  if (c === "session_id") return ev?.session_id || ev?.sessionId || "";
                                  if (c === "buyPriceUsd") return ev?.buyPriceUsd ?? ev?.entryPriceUsd ?? ev?.entry_price_usd ?? ev?.entryPrice ?? "";
                                  if (c === "sellPriceUsd") return ev?.sellPriceUsd ?? ev?.exitPriceUsd ?? ev?.exit_price_usd ?? ev?.exitPrice ?? "";
                                  if (c === "buyUsd") return ev?.buyUsd ?? ev?.amountInUsd ?? "";
                                  if (c === "sellUsd") return ev?.sellUsd ?? ev?.amountOutUsd ?? "";
                                  if (c === "grossUsd") return ev?.grossUsd ?? ev?.grossProfitUsd ?? "";
                                  if (c === "netUsd") return ev?.netUsd ?? ev?.netProfitUsd ?? "";
                                  return ev?.[c] ?? "";
                                };
                                const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
                                const exportRows = filteredEvents.filter((ev) => ev && (ev.status || ev.action || ev.targetAsset || ev.event_id || ev.trade_id));
                                const lines = [cols.join(","), ...exportRows.map((ev) => cols.map((c) => esc(val(ev, c))).join(","))];
                                const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url; a.download = `nkr-event-history-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
                              };
                              // Keep the history component visible even when there are no events yet.
                              // It must never depend on Shadow being enabled or running.
                              return (
                              <div
                                style={{ borderRadius: 10, border: "1px solid rgba(255,255,255,.08)", background: "rgba(0,0,0,.10)", padding: "6px 8px" }}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                                onClick={(e) => { e.stopPropagation(); }}
                              >
                                <button
                                  type="button"
                                  className="muted tiny"
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRotationShadowEventsOpen((v) => !v); }}
                                  style={{ width: "100%", cursor: "pointer", fontWeight: 900, color: "#8bdcff", background: "transparent", border: 0, padding: 0, textAlign: "left" }}
                                >
                                  {rotationShadowEventsOpen ? "▼" : "▶"} NKR Event History · {allEvents.length} event{allEvents.length === 1 ? "" : "s"}
                                  {` · ${onChainCount} on-chain`}
                                  {` · ${Math.max(0, allEvents.length - onChainCount)} monitor`}
                                </button>
                                {rotationShadowEventsOpen ? (
                                  <div style={{ display: "grid", gap: 7, marginTop: 8 }}>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                      {["ALL","ON_CHAIN","MONITOR","OPEN","CLOSED_PROFIT","CLOSED_LOSS","CAPITAL","PROTECTED","SAFETY","ERROR"].map((f) => (
                                        <button key={f} type="button" className="miniBtn" onClick={() => setRotationShadowEventsFilter(f)} style={{ opacity: rotationShadowEventsFilter === f ? 1 : 0.68 }}>{f.replace("_", " ")}</button>
                                      ))}
                                      <button type="button" className="miniBtn" onClick={() => setRotationShadowEventsShowAll((v) => !v)}>{rotationShadowEventsShowAll ? "Show latest 10" : "Show Full History"}</button>
                                      <button type="button" className="miniBtn" onClick={exportCsv}>Export CSV</button>
                                      <button type="button" className="miniBtn" onClick={exportJson}>Export JSON</button>
                                    </div>
                                    {!visibleEvents.length ? (
                                      <div className="muted tiny" style={{ padding: "8px 4px" }}>
                                        No NKR events saved yet. History will appear here independently of Shadow.
                                      </div>
                                    ) : null}
                                    {visibleEvents.map((ev) => {
                                      const st = String(ev?.status || ev?.action || ev?.text || "EVENT").toUpperCase();
                                      const kind = String(ev?.eventKind || "MONITOR").toUpperCase();
                                      const kindColor = kind === "ON_CHAIN" ? "#86efac" : kind === "MONITOR" ? "#ffd166" : "#8bdcff";
                                      const buyTs = ev?.buyTime || ev?.openedAt || (kind === "ON_CHAIN" ? ev?.ts : null);
                                      const sellTs = ev?.sellTime || ev?.closedAt;
                                      const buyPrice = Number(ev?.buyPriceUsd ?? ev?.entryPriceUsd ?? ev?.entry_price_usd ?? ev?.entryPrice ?? 0);
                                      const sellPrice = Number(ev?.sellPriceUsd ?? ev?.exitPriceUsd ?? ev?.exit_price_usd ?? ev?.exitPrice ?? 0);
                                      const isMonitorOnly = kind === "MONITOR" || kind === "SHADOW";
                                      return (
                                        <div key={ev.event_id || ev.id || `${ev.trade_id}-${ev.ts}`} className="muted tiny" style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 5 }}>
                                          <div>
                                            <span
                                              style={{
                                                display: "inline-block",
                                                marginRight: 6,
                                                padding: "1px 6px",
                                                borderRadius: 6,
                                                border: `1px solid ${kindColor}`,
                                                color: kindColor,
                                                fontWeight: 900,
                                                fontSize: 10,
                                              }}
                                            >
                                              {kind === "ON_CHAIN" ? "ON-CHAIN" : kind === "MONITOR" ? "MONITOR" : "SHADOW"}
                                            </span>
                                            <b style={{ color: st.includes("CLOSED") ? "#86efac" : "#8bdcff" }}>
                                              {ev.ts ? new Date(ev.ts).toLocaleTimeString() : "—"}
                                              {" · "}
                                              {ev.targetAsset || ev.sessionSymbol || (isMonitorOnly ? "—" : "ASSET")}
                                              {" · "}
                                              {st}
                                            </b>
                                            {ev.addedToCollectedProfit ? " · added to Collected Profit" : ""}
                                          </div>
                                          {kind === "ON_CHAIN" ? (
                                            <>
                                              <div>Buy {buyTs ? new Date(buyTs).toLocaleTimeString() : "—"} @ {buyPrice > 0 ? fmtUsd(buyPrice) : "price missing"} → Sell {sellTs ? new Date(sellTs).toLocaleTimeString() : "open"} @ {sellPrice > 0 ? fmtUsd(sellPrice) : (sellTs ? "price missing" : "open")}</div>
                                              <div>Route {ev.route || ev.flow || "—"}</div>
                                              <div>In {fmtUsd(Number(ev.buyUsd || ev.amountInUsd || 0))} · Out {ev.sellUsd ? fmtUsd(Number(ev.sellUsd)) : "open"} · Gross {fmtUsd(Number(ev.grossUsd || ev.grossProfitUsd || 0))} · Costs {fmtUsd(Number(ev.costsUsd || 0))} · Net {fmtUsd(Number(ev.netUsd || ev.netProfitUsd || 0))}</div>
                                              <div>Trade {ev.trade_id || "—"} · Event {ev.event_id || ev.id || "—"}{ev.txHash ? ` · Tx ${String(ev.txHash).slice(0, 12)}…` : ""}</div>
                                            </>
                                          ) : (
                                            <>
                                              <div style={{ color: "rgba(232,242,240,.78)" }}>
                                                {toUserFacingMessage(ev.text || ev.reason || ev.detail, "Status / monitor tick — not an on-chain buy.")}
                                              </div>
                                              <div>Event {ev.event_id || ev.id || "—"} · no on-chain trade</div>
                                            </>
                                          )}
                                          {st === "CAPITAL_MOVEMENT" ? (
                                            <div><b style={{ color: "#ffd166" }}>Capital movement:</b> {ev.fromAsset || "—"} → {ev.toAsset || ev.baseAsset || "USDT"} · Before {fmtUsd(Number(ev.capitalBeforeUsd || 0))} · Moved {fmtUsd(Number(ev.capitalMovedUsd || ev.availableStableDeltaUsd || 0))} · After {fmtUsd(Number(ev.capitalAfterUsd || 0))} · not profit</div>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                              );
                            })()}

                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : String(gridMode || "normal") === "trading" ? (
                <div className="gridWrap tradingDesktopWrap">
                  <div className="gridControls" style={{ display: "grid", gap: 10 }}>
                    <div
                      style={{
                        padding: "8px 10px",
                        borderRadius: 12,
                        background: "rgba(0,0,0,.18)",
                        border: "1px solid rgba(34,197,94,.22)",
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      <div className="label" style={{ marginBottom: 0 }}>Trading Overview · CoreVault V5</div>
                      <div
                        className="muted tiny"
                        style={{
                          display: "grid",
                          gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                          gap: 6,
                          lineHeight: 1.45,
                        }}
                      >
                        {(() => {
                          const selectedVaultToken = String(manualPayoutAsset || "USDC").toUpperCase();
                          const tokenState = coreVaultOnchain?.tokens?.[selectedVaultToken] || {};
                          const account = tokenState?.account || {};
                          const vaultTotalUsd = Number(account?.baseCapital || 0) + Number(account?.totalSecuredProfit || 0);
                          const tradingAllocatedFromVault = Number(account?.allocatedTrader || 0);
                          const activeTradingSessions = (Array.isArray(openTradingSessions) ? openTradingSessions : []);
                          const onchainTraderSessions = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
                            .filter((sess) => String(sess?.engine || "").toUpperCase() === "TRADER")
                            .filter((sess) => !["FINALIZED", "COMPLETED", "CANCELLED"].includes(String(sess?.statusLabel || "").toUpperCase()));
                          const tradingAllocatedUsd = activeTradingSessions.reduce((sum, sess) => {
                            const st = String(sess?.status || "").toUpperCase();
                            if (["RELEASED", "CLOSED", "EXPIRED"].includes(st)) return sum;
                            return sum + getTradingSessionBudgetUsd(sess);
                          }, 0);
                          const authoritativeReservedUsd = Math.max(tradingAllocatedFromVault, tradingAllocatedUsd);
                          const availableUsd = Math.max(0, Number(account?.freeBase ?? (vaultTotalUsd - Number(account?.totalAllocated || 0))));
                          const localActiveCount = activeTradingSessions.length;
                          const onchainActiveCount = onchainTraderSessions.length;
                          const displayedActiveCount = Math.max(localActiveCount, onchainActiveCount);
                          const recoveryRunning = onchainTraderSessions.some((sess) => {
                            const key = `TRADER:${sess?.sessionId}`;
                            const job = coreVaultRecoveryJobs?.[key];
                            return ["QUEUED", "RUNNING"].includes(String(job?.job_status || "").toUpperCase());
                          });
                          const traderRuntimeStatus = recoveryRunning
                            ? "RECOVERY RUNNING"
                            : (onchainActiveCount > localActiveCount
                                ? "ON-CHAIN SESSION PENDING"
                                : (localActiveCount ? getTradingVaultRuntimeLabel(activeTradingSessions) : "IDLE"));
                          // User-facing Trader/Strategist status from backend (English). No System Info required.
                          const traderSt = nexusBackendState?.traderStatus || nexusBackendState?.strategist || null;
                          return (
                            <>
                              <div><b>Vault asset:</b> {selectedVaultToken}</div>
                              <div><b>Vault balance:</b> {fmtUsd(vaultTotalUsd)}</div>
                              <div><b>Trader reserved:</b> {fmtUsd(authoritativeReservedUsd)}</div>
                              <div style={{ color: "#22c55e", fontWeight: 900 }}><b>Free available:</b> {fmtUsd(availableUsd)}</div>
                              <div><b>Active sessions:</b> {displayedActiveCount}</div>
                              <div style={{ color: recoveryRunning || onchainActiveCount > localActiveCount ? "#ffd166" : "#8bdcff", fontWeight: 900 }}><b>Status:</b> {traderRuntimeStatus}</div>
                              {onchainTraderSessions.map((sess) => (
                                <div key={`trader-onchain-${sess.sessionId}`} style={{ gridColumn: isCompactMobile ? "1" : "1 / -1", color: "#ffd166", fontWeight: 900 }}>
                                  On-chain Trader session #{sess.sessionId}: {String(sess.statusLabel || "ACTIVE").toUpperCase()} · Budget {fmtUsd(Number(sess.budget || 0))} · Open assets {Number(sess.openAssetCount || 0)}
                                </div>
                              ))}

                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {(() => {
                      const allPendingTrader = (Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [])
                        .filter((sess) => String(sess?.engine || "").toUpperCase() === "TRADER")
                        .filter((sess) => !["FINALIZED", "COMPLETED", "CANCELLED"].includes(String(sess?.statusLabel || "").toUpperCase()));
                      const localOpenTrader = Array.isArray(openTradingSessions) ? openTradingSessions : [];
                      // BUILD416: the yellow control is orphan recovery only. Never show it for the
                      // normal active Trader session that is represented by the Trader card.
                      const pending = allPendingTrader.filter((onchain, idx) => {
                        const sid = Number(onchain?.sessionId || 0);
                        const chain = String(onchain?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(onchain?.chainId)] || "")).toUpperCase();
                        const exactLocal = localOpenTrader.some((local) => {
                          const lm = local?.meta && typeof local.meta === "object" ? local.meta : {};
                          const localSid = Number(local?.onchainSessionId || local?.onchain_session_id || local?.coreVaultSessionId || lm?.onchain_session_id || lm?.coreVaultSessionId || 0) || 0;
                          return localSid > 0 && localSid === sid;
                        });
                        if (exactLocal) return false;
                        // Backward compatibility for older cards that never persisted the CoreVault id:
                        // one local Trader card + one live CoreVault Trader session is the same runtime,
                        // even if the card still carries a stale asset/chain label.
                        if (localOpenTrader.length === 1 && allPendingTrader.length === 1) return false;
                        return true;
                      });
                      if (!pending.length) return null;
                      return (
                        <div style={{ padding: "9px 10px", borderRadius: 12, border: "1px solid rgba(255,209,102,.45)", background: "rgba(255,209,102,.08)", color: "#ffd166", fontWeight: 900 }}>
                          Trader recovery required: {pending.length} on-chain session{pending.length === 1 ? "" : "s"} still reserve capital.
                          {pending.map((sess) => ` #${sess.sessionId} (${String(sess.statusLabel || "ACTIVE").toUpperCase()})`).join(",")}
                          <div className="muted tiny" style={{ marginTop: 4 }}>
                            Capital remains reserved until finalization is confirmed on-chain. Orphan on-chain sessions can be stopped and finalized here even when the normal Trader card is no longer visible.
                          </div>
                          {typeof onRecoverExactCoreVaultSession === "function" ? (
                            <div style={{ marginTop: 7, display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {pending.map((sess) => {
                                const chain = String(sess?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(sess?.chainId)] || "")).toUpperCase();
                                const busyKey = `TRADER:${chain}:${Number(sess?.sessionId || 0)}`;
                                const busy = exactOrphanRecoveryBusyKey === busyKey;
                                return (
                                  <button key={`trader-orphan-stop-${chain}-${sess.sessionId}`} type="button" className="miniBtn" disabled={!!exactOrphanRecoveryBusyKey} onClick={() => onRecoverExactCoreVaultSession(sess.sessionId, "TRADER", chain, sess.chainId)}>
                                    {busy ? `Processing #${sess.sessionId}...` : `Stop & Finalize ${chain} #${sess.sessionId}`}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}

                    {!isCompactMobile && Array.isArray(openTradingSessions) && openTradingSessions.length ? (
                      <div
                        style={{
                          padding: "10px 10px",
                          borderRadius: 14,
                          background: "rgba(0,0,0,.16)",
                          border: "1px solid rgba(139,220,255,.14)",
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <div className="muted tiny" style={{ fontWeight: 950, color: "#8bdcff" }}>Active Trading Sessions</div>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gap: 10,
                            maxHeight: 430,
                            overflowY: "auto",
                            paddingRight: 5,
                          }}
                        >
                          {openTradingSessions.map((sess) => {
                            const sid = String(sess?.id || sess?.session_id || "").trim();
                            const sessionSlots = (() => {
                              const _sanSlots = (rows) => {
                                const list = Array.isArray(rows) ? rows : [];
                                return isTradingSessionLive(sess) ? list.map((sl) => sanitizeLiveTradingSlot(sl)) : list;
                              };
                              if (Array.isArray(sess?.queue) && sess.queue.length) return _sanSlots(sess.queue);
                              const fromExec = Array.isArray(tradingExecutionQueue) ? tradingExecutionQueue.filter((slot) => {
                                const slotSid = String(getTradingSlotSessionId(slot) || "").trim();
                                const slotOid = Number(slot?.onchainSessionId || slot?.onchain_session_id || slot?.meta?.onchain_session_id || 0) || 0;
                                const sessOid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || 0) || 0;
                                if (sid && slotSid && (slotSid === sid || slotSid.startsWith(sid) || sid.startsWith(slotSid))) return true;
                                if (sessOid > 0 && slotOid > 0 && sessOid === slotOid) return true;
                                return false;
                              }) : [];
                              if (fromExec.length) return _sanSlots(fromExec);
                              // BUILD429: rebuild display slots from settings cache (budget 8+8 → 2 slots).
                              const oid = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || 0) || 0;
                              const chain = String(sess?.chain || sess?.chainKey || (Array.isArray(sess?.chains) ? sess.chains[0] : "") || "").toUpperCase();
                              const cached = traderSessionSettingsCacheRef.current[sid]
                                || (oid ? traderSessionSettingsCacheRef.current[`OID:${oid}`] : null)
                                || (oid && chain ? traderSessionSettingsCacheRef.current[`${chain}:${oid}`] : null)
                                || {};
                              const splits = Array.isArray(cached.budgetSplits) ? cached.budgetSplits : (Array.isArray(cached.slotAmounts) ? cached.slotAmounts : []);
                              if (!splits.length) return [];
                              return splits.map((amount, idx) => ({
                                id: `display-slot-${sid || oid}-${idx + 1}`,
                                slot: idx + 1,
                                slot_id: idx + 1,
                                amountUsd: Number(amount) || 0,
                                reserved_capital_usd: Number(amount) || 0,
                                status: "WAIT",
                                state: "WAIT",
                                chain,
                                session_id: sid,
                                style: cached.style,
                                meta: { source: "settings_cache_display" },
                              }));
                            })();
                            const sessionAsset = getTradingSessionPrimaryAsset(sess) || "ASSET";
                            const sessionBudget = getTradingSessionBudgetUsd(sess);
                            const sessionProfit = getTradingSessionProfitUsd(sess);
                            const sessionProfitPct = sessionBudget > 0 ? (sessionProfit / sessionBudget) * 100 : 0;
                            const reusePct = Number(sess?.reuseProfitPct ?? sess?.reuse_profit_pct ?? sess?.profitReusePct ?? sess?.profit_reuse_pct ?? 0);
                            const maxCombined = Number(sess?.maxCombinedSlots ?? sess?.max_combined_slots ?? sess?.slotDonorCap ?? sess?.slot_donor_cap ?? 0);
                            const timing = getTradingSessionTiming(sess);
                            const counts = sessionSlots.reduce((acc, slot) => {
                              const st = String(slot?.status || slot?.state || "WAIT").toUpperCase();
                              acc[st] = (acc[st] || 0) + 1;
                              return acc;
                            }, {});
                            const activeSlot = sessionSlots.find((slot) => String(slot?.status || slot?.state || "").toUpperCase() === "ACTIVE") || sessionSlots.find((slot) => ["READY", "WAIT", "SIMULATED_EXIT"].includes(String(slot?.status || slot?.state || "").toUpperCase())) || null;
                            const aggregate = sessionSlots.reduce((acc, slot) => {
                              const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                              const gross = Number(slot.paper_gross_pnl_usd ?? meta.paper_gross_pnl_usd ?? 0);
                              const costs = Number(slot.paper_estimated_costs_usd ?? meta.paper_estimated_costs_usd ?? 0);
                              const net = Number(slot.paper_net_pnl_usd ?? meta.paper_net_pnl_usd ?? slot.paper_pnl_usd ?? meta.paper_pnl_usd ?? 0);
                              const cycle = Number(slot.paper_cycle_realized_usd ?? meta.paper_cycle_realized_usd ?? 0);
                              if (Number.isFinite(gross)) acc.gross += gross;
                              if (Number.isFinite(costs)) acc.costs += Math.abs(costs);
                              if (Number.isFinite(net)) acc.net += net;
                              if (Number.isFinite(cycle)) acc.cycle += cycle;
                              return acc;
                            }, { gross: 0, costs: 0, net: 0, cycle: 0 });
                            const progressPct = sessionBudget > 0 ? Math.max(0, Math.min(100, 50 + (sessionProfitPct * 8))) : 0;
                            const stateLabel = String(sess?.status || "ACTIVE").toUpperCase();
                            const tradeCount = getTradingSessionTradeCount(sess, sessionSlots);
                            const selected = sid && sid === selectedTradingSessionId;
                            const slotsOpen = !!expandedTradingSessionSlots?.[sid];
                            const detailsOpen = !!expandedTradingSessionDetails?.[sid];
                            const firstSessionSlot = Array.isArray(sessionSlots) && sessionSlots.length ? sessionSlots[0] : {};
                            const firstSessionMeta = firstSessionSlot?.meta && typeof firstSessionSlot.meta === "object" ? firstSessionSlot.meta : {};
                            const sessionMeta = sess?.meta && typeof sess.meta === "object" ? sess.meta : {};
                            const pickSessionValue = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "");
                            const sessionChain = String(pickSessionValue((Array.isArray(sess?.chains) && sess.chains[0]), sess?.chain, sess?.chain_key, sessionMeta.chain, firstSessionSlot?.chain, firstSessionSlot?.chain_key, firstSessionMeta.chain, activeGridChainKey, DEFAULT_CHAIN, "")).toUpperCase();
                            const oidForCache = Number(sess?.onchainSessionId || sess?.onchain_session_id || sess?.coreVaultSessionId || sessionMeta.onchain_session_id || 0) || 0;
                            const cachedSettings = (
                              traderSessionSettingsCacheRef.current[sid]
                              || (oidForCache ? traderSessionSettingsCacheRef.current[`OID:${oidForCache}`] : null)
                              || (oidForCache && sessionChain ? traderSessionSettingsCacheRef.current[`${sessionChain}:${oidForCache}`] : null)
                              || {}
                            );
                            const sessionStyleRaw = String(pickSessionValue(sess?.style, sess?.trading_style, sess?.strategy, sessionMeta.style, sessionMeta.trading_style, sessionMeta.strategy, cachedSettings.style, cachedSettings.trading_style, firstSessionSlot?.style, firstSessionSlot?.trading_style, firstSessionSlot?.strategy, firstSessionMeta.style, firstSessionMeta.trading_style, firstSessionMeta.strategy, "") || "").toUpperCase();
                            const sessionStyle = (sessionStyleRaw && sessionStyleRaw !== "UNDEFINED" && sessionStyleRaw !== "NULL") ? sessionStyleRaw : "";
                            const sessionRiskModeRaw = String(pickSessionValue(sess?.riskMode, sess?.risk_mode, sess?.trading_risk_mode, sessionMeta.riskMode, sessionMeta.risk_mode, sessionMeta.trading_risk_mode, cachedSettings.riskMode, cachedSettings.risk_mode, firstSessionSlot?.riskMode, firstSessionSlot?.risk_mode, firstSessionSlot?.trading_risk_mode, firstSessionMeta.riskMode, firstSessionMeta.risk_mode, firstSessionMeta.trading_risk_mode, "") || "").toUpperCase();
                            const sessionRiskMode = (sessionRiskModeRaw && sessionRiskModeRaw !== "UNDEFINED" && sessionRiskModeRaw !== "NULL") ? sessionRiskModeRaw : "";
                            const sessionMaxTrades = Number(pickSessionValue(sess?.maxTrades, sess?.max_trades, sessionMeta.maxTrades, sessionMeta.max_trades, cachedSettings.maxTrades, cachedSettings.max_trades, firstSessionSlot?.maxTrades, firstSessionSlot?.max_trades, firstSessionMeta.maxTrades, firstSessionMeta.max_trades, NaN));
                            const sessionHardStop = Number(pickSessionValue(sess?.hardStopPct, sess?.hard_stop_pct, sessionMeta.hardStopPct, sessionMeta.hard_stop_pct, cachedSettings.hardStopPct, cachedSettings.hard_stop_pct, firstSessionSlot?.hardStopPct, firstSessionSlot?.hard_stop_pct, firstSessionMeta.hardStopPct, firstSessionMeta.hard_stop_pct, NaN));
                            const sessionProfitLock = Number(pickSessionValue(sess?.profitLockPct, sess?.profit_lock_pct, sessionMeta.profitLockPct, sessionMeta.profit_lock_pct, cachedSettings.profitLockPct, cachedSettings.profit_lock_pct, firstSessionSlot?.profitLockPct, firstSessionSlot?.profit_lock_pct, firstSessionMeta.profitLockPct, firstSessionMeta.profit_lock_pct, NaN));
                            const sessionSlippage = Number(pickSessionValue(sess?.maxSlippagePct, sess?.max_slippage_pct, sessionMeta.maxSlippagePct, sessionMeta.max_slippage_pct, cachedSettings.maxSlippagePct, cachedSettings.max_slippage_pct, firstSessionSlot?.maxSlippagePct, firstSessionSlot?.max_slippage_pct, firstSessionMeta.maxSlippagePct, firstSessionMeta.max_slippage_pct, NaN));
                            const sessionLivePrice = getTradingSessionLivePriceInfo(sess, sessionSlots, sessionAsset, watchRows);
                            return (
                              <div
                                key={sid || `${sessionAsset}-${sessionBudget}`}
                                className="tradingSessionCard"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (sid) selectTradingSession(sid); }}
                                style={{
                                  border: "1px solid rgba(139,220,255,.16)",
                                  borderRadius: 14,
                                  background: "linear-gradient(135deg, rgba(255,255,255,.035), rgba(0,0,0,.16))",
                                  padding: "10px 12px",
                                  display: "grid",
                                  gap: 10,
                                  boxShadow: "0 0 0 1px rgba(255,255,255,.025) inset",
                                  cursor: sid ? "pointer" : "default",
                                }}
                              >
                                <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.1fr 1.1fr auto", gap: 12, alignItems: "center" }}>
                                  <div style={{ display: "grid", gap: 6 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                      <div style={{ color: "#eafff5", fontWeight: 950, fontSize: 14 }}>{sessionAsset} · {fmtUsd(sessionBudget)} · {(sessionSlots.length || getTradingSessionSlotCount(sess) || 0)} slots</div>
                                      <span className="tiny" style={{ padding: "3px 8px", borderRadius: 999, background: stateLabel === "ACTIVE" ? "rgba(34,197,94,.18)" : stateLabel === "PAUSED" ? "rgba(255,193,7,.16)" : "rgba(139,220,255,.12)", color: stateLabel === "ACTIVE" ? "#7cf7a2" : stateLabel === "PAUSED" ? "#ffd166" : "#8bdcff", fontWeight: 950 }}>{stateLabel}</span>
                                    </div>
                                    <div className="muted tiny" style={{ color: timing.isExpired ? "#ffd166" : "#8bdcff", fontWeight: 900 }}>⏱ Runtime: {timing.elapsedLabel} · Left: {timing.remainingLabel}</div>
                                    <div className="muted tiny">Closed trades {tradeCount} · Slots in use {counts.ACTIVE || 0}/{Math.max(1, getTradingSessionSlotCount(sess) || sessionSlots.length || 0)}</div>
                                  </div>

                                  <div style={{ display: "grid", gap: 5 }}>
                                    <div className="muted tiny" style={{ color: sessionProfit >= 0 ? "#22c55e" : "#ff6b6b", fontWeight: 950 }}>Session Profit: {`${sessionProfit >= 0 ? "+" : "-"}${fmtUsd(Math.abs(sessionProfit))}`} {sessionBudget > 0 ? `(${sessionProfitPct >= 0 ? "+" : ""}${sessionProfitPct.toFixed(2)}%)` : ""}</div>
                                    <div className="muted tiny" style={{ color: "#7cf7a2", fontWeight: 900 }}>Gross: {aggregate.gross >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.gross))} · Net: {aggregate.net >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.net))}</div>
                                    <div className="muted tiny" style={{ color: aggregate.costs > 0 ? "#ffd166" : "rgba(216,255,241,.68)", fontWeight: 850 }}>Costs: {aggregate.costs > 0 ? `-${fmtUsd(aggregate.costs)}` : fmtUsd(0)} · Cycle: {aggregate.cycle >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.cycle))}</div>
                                  </div>

                                  <div style={{ display: "grid", gap: 5 }}>
                                    <div className="muted tiny" style={{ color: "#8bdcff", fontWeight: 900, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                      <span>Performance: {sessionStyle ? (sessionStyle.charAt(0) + sessionStyle.slice(1).toLowerCase()) : "—"}</span>
                                      {sessionLivePrice ? <span style={{ color: sessionLivePrice.color, fontWeight: 950 }}>· Live: {fmtUsd(sessionLivePrice.price)} {sessionLivePrice.arrow}{sessionLivePrice.changePct !== null ? ` ${fmtPct(sessionLivePrice.changePct)}` : ""}</span> : null}
                                    </div>
                                    <div className="muted tiny" style={{ color: "rgba(216,255,241,.72)", fontWeight: 850 }}>Payout: {sess?.payoutAsset || sess?.payout_asset || manualPayoutAsset || "USDC"}</div>
                                    <div className="muted tiny">Current slot: {activeSlot ? `#${activeSlot.slot || activeSlot.slot_id || "—"} · ${String(activeSlot.status || activeSlot.state || "WAIT").toUpperCase()} · ${fmtUsd(getTradingSlotAmountUsd(activeSlot))}` : "—"}</div>
                                    <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,.10)", overflow: "hidden", marginTop: 2 }}>
                                      <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, progressPct))}%`, background: sessionProfit >= 0 ? "linear-gradient(90deg, #22c55e, #7cf7a2)" : "linear-gradient(90deg, #ff6b6b, #ffd166)" }} />
                                    </div>
                                  </div>

                                  <div style={{ display: "grid", gap: 7, minWidth: 126 }}>
                                    <button
                                      className="miniBtn"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!sid) return;
                                        selectTradingSession(sid);
                                        setExpandedTradingSessionDetails((prev) => ({ ...(prev || {}), [sid]: !prev?.[sid] }));
                                      }}
                                      title="Open this session's detailed runtime view"
                                    >
                                      {detailsOpen ? "Hide Details ▲" : "Details"}
                                    </button>
                                    <button
                                      className="miniBtn"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!sid) return;
                                        const now = Date.now();
                                        setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).map((s) => {
                                          const sameSession = tradingSessionIdMatches(getTradingSlotSessionId(s), sid);
                                          return sameSession && ["ACTIVE", "READY"].includes(String(s.status || "").toUpperCase())
                                            ? { ...s, status: "WAIT", pausedAt: now }
                                            : s;
                                        }));
                                        setTradingSessionStatus("PAUSED");
                                        setTradingSessionUpdatedTs(now);
                                        setActiveTradingSessionId(sid);
                                        updateTradingSessionMeta(sid, { status: "PAUSED", pausedAt: now });
                                        updateTradingPreparedSession({ status: "PAUSED", pausedAt: now, userAction: { paused: true, sessionId: sid } });
                                      }}
                                      disabled={!sid || stateLabel === "PAUSED" || stateLabel === "STOPPED" || stateLabel === "STOPPING" || tradingStopBusy}
                                      title="Pause only this Trading session"
                                    >
                                      Pause
                                    </button>
                                    {stateLabel === "PAUSED" ? (
                                      <button
                                        className="miniBtn"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!sid || tradingStopBusy) return;
                                          setActiveTradingSessionId(sid);
                                          handleTradingResumeSession(sess);
                                        }}
                                        disabled={!sid || tradingStopBusy}
                                        title="Resume this Trading session on-chain"
                                      >
                                        Resume
                                      </button>
                                    ) : null}
                                    <button
                                      className="miniBtn"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!sid || stateLabel === "STOPPING" || stateLabel === "STOPPED" || tradingStopBusy) return;
                                        setActiveTradingSessionId(sid);
                                        // Immediate visual feedback: status STOPPING + busy before await returns.
                                        setTradingSessionStatus("STOPPING");
                                        updateTradingSessionMeta(sid, { status: "STOPPING", stoppingAt: Date.now() });
                                        handleTradingStopSession(sid, sess);
                                      }}
                                      disabled={!sid || stateLabel === "STOPPED" || stateLabel === "STOPPING" || tradingStopBusy}
                                      style={{
                                        color: "#fff",
                                        background: (stateLabel === "STOPPING" || tradingStopBusy)
                                          ? "rgba(180, 40, 40, 0.35)"
                                          : "linear-gradient(180deg, #e53935 0%, #b71c1c 100%)",
                                        borderColor: (stateLabel === "STOPPING" || tradingStopBusy)
                                          ? "rgba(255, 100, 100, 0.25)"
                                          : "rgba(255, 80, 80, 0.65)",
                                        opacity: (stateLabel === "STOPPING" || tradingStopBusy) ? 0.45 : 1,
                                        fontWeight: 900,
                                        boxShadow: (stateLabel === "STOPPING" || tradingStopBusy)
                                          ? "none"
                                          : "0 0 0 1px rgba(255,120,120,.2), 0 2px 8px rgba(180,0,0,.25)",
                                        transition: "opacity 0.2s ease, background 0.2s ease, box-shadow 0.2s ease",
                                        cursor: (stateLabel === "STOPPING" || tradingStopBusy) ? "not-allowed" : "pointer",
                                      }}
                                      title="Stop this Trading session (stays visible as stopping until closed)"
                                    >
                                      {(stateLabel === "STOPPING" || tradingStopBusy) ? "Stopping…" : "Protect / Stop"}
                                    </button>
                                    <button className="miniBtn" type="button" onClick={(e) => { e.stopPropagation(); setExpandedTradingSessionSlots((prev) => ({ ...(prev || {}), [sid]: !prev?.[sid] })); }} style={{ color: "#8bdcff", borderColor: "rgba(139,220,255,.25)" }} title="Show or hide this session's slots">
                                      {slotsOpen ? "Hide Slots ▲" : `Show Slots (${sessionSlots.length}) ▼`}
                                    </button>
                                  </div>
                                </div>

                                {detailsOpen ? (
                                  <div style={{ borderTop: "1px solid rgba(139,220,255,.10)", paddingTop: 8, display: "grid", gap: 8 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 8 }}>
                                      {[
                                        ["Session", sid],
                                        ["Chain", sessionChain || "—"],
                                        ["Performance", sessionStyle || "—"],
                                        ["Risk", sessionStyle === "AGGRESSIVE" ? "LEGACY PROTECTION" : (sessionRiskMode || "—")],
                                        ["Max Trades", sessionStyle === "AGGRESSIVE" ? "No Limit" : (Number.isFinite(sessionMaxTrades) && sessionMaxTrades > 0 ? sessionMaxTrades : "—")],
                                        ["Reuse", `${Number.isFinite(reusePct) ? reusePct.toFixed(0) : "0"}%`],
                                        ["Hard Stop", Number.isFinite(sessionHardStop) && sessionHardStop > 0 ? `${sessionHardStop}%` : "—"],
                                        ["Profit Lock", Number.isFinite(sessionProfitLock) && sessionProfitLock > 0 ? `${sessionProfitLock}%` : "—"],
                                        ["Slippage", Number.isFinite(sessionSlippage) && sessionSlippage > 0 ? `${sessionSlippage}%` : "—"],
                                      ].map(([label, value]) => (
                                        <div key={`${sid}-${label}`} style={{ border: "1px solid rgba(139,220,255,.10)", borderRadius: 8, padding: "7px 8px", background: "rgba(0,0,0,.14)", minWidth: 0 }}>
                                          <div className="muted tiny" style={{ fontWeight: 900 }}>{label}</div>
                                          <div className="tiny" style={{ fontWeight: 950, color: "#eafff5", overflowWrap: "anywhere" }}>{String(value || "—")}</div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="muted tiny" style={{ color: "#ffd166", fontWeight: 900 }}>Strategist / Slot log</div>
                                    <div style={{ display: "grid", gap: 5 }}>
                                      {sessionSlots.slice(0, 6).map((slot, idx) => {
                                        const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                                        const st = String(slot?.status || slot?.state || "WAIT").toUpperCase();
                                        const score = Number(slot?.priority ?? slot?.confidence ?? slot?.confidence_score ?? meta.priority ?? meta.confidence ?? 0);
                                        return (
                                          <div key={`${sid}-log-${idx}`} className="muted tiny" style={{ border: "1px solid rgba(139,220,255,.10)", borderRadius: 8, padding: "6px 8px", background: "rgba(0,0,0,.12)" }}>
                                            <b style={{ color: st === "ACTIVE" ? "#7cf7a2" : st === "READY" ? "#8bdcff" : "#ffd166" }}>Slot #{slot?.slot || slot?.slot_id || idx + 1} · {st}</b> · score {Number.isFinite(score) ? Math.round(score) : 0}<br />
                                            {toUserFacingMessage(slot?.condition || slot?.reason || slot?.message || slot?.note, "No detailed decision reason recorded yet.")}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : null}

                                {slotsOpen ? (
                                  <div style={{ borderTop: "1px solid rgba(139,220,255,.10)", paddingTop: 8, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                                    {sessionSlots.map((slot, idx) => {
                                      const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                                      const st = String(slot.status || slot.state || "WAIT").toUpperCase();
                                      const gross = Number(slot.paper_gross_pnl_usd ?? meta.paper_gross_pnl_usd ?? 0);
                                      const costs = Math.abs(Number(slot.paper_estimated_costs_usd ?? meta.paper_estimated_costs_usd ?? 0));
                                      const net = Number(slot.paper_net_pnl_usd ?? meta.paper_net_pnl_usd ?? slot.paper_pnl_usd ?? meta.paper_pnl_usd ?? 0);
                                      const cycle = Number(slot.paper_cycle_realized_usd ?? meta.paper_cycle_realized_usd ?? 0);
                                      const isActive = st === "ACTIVE";
                                      const isReady = st === "READY";
                                      const isWait = st === "WAIT";
                                      const isExit = st === "SIMULATED_EXIT";
                                      const border = isActive ? "rgba(34,197,94,.42)" : isReady ? "rgba(34,197,94,.30)" : isWait ? "rgba(255,193,7,.28)" : isExit ? "rgba(139,220,255,.24)" : "rgba(255,255,255,.10)";
                                      const bg = isActive ? "rgba(34,197,94,.09)" : isReady ? "rgba(34,197,94,.055)" : isWait ? "rgba(255,193,7,.06)" : isExit ? "rgba(139,220,255,.055)" : "rgba(255,255,255,.025)";
                                      return (
                                        <div key={`${getTradingSlotSessionId(slot) || sid || "session"}-${slot.slot || slot.slot_id || idx}`} style={{ border: `1px solid ${border}`, background: bg, borderRadius: 10, padding: "8px 9px", display: "grid", gap: 5 }}>
                                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                                            <b style={{ color: "#eafff5" }}>Slot {slot.slot || slot.slot_id || idx + 1} · {fmtUsd(getTradingSlotAmountUsd(slot))}</b>
                                            <span className="tiny" style={{ color: isActive || isReady ? "#7cf7a2" : isWait ? "#ffc107" : isExit ? "#8bdcff" : "rgba(235,255,247,.72)", fontWeight: 950 }}>{st}</span>
                                          </div>
                                          <div className="muted tiny">{slot.symbol || slot.asset || sessionAsset} · {getTradingSlotDecisionReason(slot)}</div>
                                          <div className="tiny" style={{ fontWeight: 900, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                            <span style={{ color: gross >= 0 ? "#7cf7a2" : "#ff8a8a" }}>gross {gross >= 0 ? "+" : "-"}{fmtUsd(Math.abs(gross))}</span>
                                            <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                            <span style={{ color: costs > 0 ? "#ffd166" : "rgba(216,255,241,.68)" }}>costs {costs > 0 ? `-${fmtUsd(costs)}` : fmtUsd(0)}</span>
                                            <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                            <span style={{ color: net >= 0 ? "#7cf7a2" : "#ff8a8a" }}>net {net >= 0 ? "+" : "-"}{fmtUsd(Math.abs(net))}</span>
                                            <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                            <span style={{ color: cycle >= 0 ? "#7cf7a2" : "#ff8a8a" }}>cycle {cycle >= 0 ? "+" : "-"}{fmtUsd(Math.abs(cycle))}</span>
                                          </div>
                                          <div className="muted tiny">Entry {fmtUsd(Number(slot.paper_entry_price ?? meta.paper_entry_price ?? 0))} · Current {fmtUsd(Number(slot.paper_mark_price ?? meta.paper_mark_price ?? 0))} · Exit {fmtUsd(Number(slot.paper_exit_price ?? meta.paper_exit_price ?? 0))}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : null}

                                {null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className="tradingSetupToggle"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTradingSetupExpanded((v) => !v);
                      }}
                      title="Show or hide Nexus Trading setup and presets"
                    >
                      <span>Trading Setup & Presets</span>
                      <span className="muted tiny" style={{ color: "#8bdcff", fontWeight: 950 }}>
                        {tradingSetupExpanded ? "Hide ▲" : "Show ▼"}
                      </span>
                    </button>

                    <div className={`tradingSetupBody ${!tradingSetupExpanded ? "isCollapsed" : ""}`}>
                    <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "1fr 1fr 1fr", gap: isCompactMobile ? 8 : 10, alignItems: "end" }}>
                      <div className="formRow">
                        <label>{Array.isArray(openTradingSessions) && openTradingSessions.length ? "Next Budget ($)" : "Budget ($)"}</label>
                        <input value={tradingBudgetUsd} onChange={(e) => handleTradingBudgetInputChange(e.target.value)} placeholder={Array.isArray(openTradingSessions) && openTradingSessions.length ? "e.g. next 300" : "e.g. 300"} />
                      </div>
                      <div className="formRow">
                        <label>Budget slots</label>
                        <input
                          value={tradingBudgetSplitInput}
                          onChange={(e) => {
                            // Next-budget draft only. Existing approved session queues stay untouched.
                            handleTradingBudgetSplitInputChange(e.target.value);
                          }}
                          placeholder="100,50,50,50,50"
                          title="Optional: split approved budget into controlled slots"
                        />
                      </div>
                      <div className="formRow">
                        <label>Runtime (h)</label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 6 }}>
                          <input
                            type="number"
                            min="0.01"
                            step="any"
                            value={tradingRuntimeHours}
                            onFocus={() => { tradingRuntimeEditingRef.current = true; }}
                            onBlur={() => { tradingRuntimeEditingRef.current = false; }}
                            onChange={(e) => { tradingRuntimeEditingRef.current = true; setTradingRuntimeHours(e.target.value); }}
                            placeholder="24"
                          />
                          <select
                            value={tradingRuntimeUnit}
                            onChange={(e) => {
                              tradingRuntimeEditingRef.current = true;
                              const nextUnit = String(e.target.value || "hours").toLowerCase();
                              const currentUnit = String(tradingRuntimeUnit || "hours").toLowerCase();
                              const currentValue = Number(String(tradingRuntimeHours || "").replace(",", "."));
                              if (Number.isFinite(currentValue) && currentValue > 0 && nextUnit !== currentUnit) {
                                let converted = currentValue;
                                if (currentUnit === "days" && nextUnit === "hours") converted = currentValue * 24;
                                else if (currentUnit === "hours" && nextUnit === "days") converted = currentValue / 24;
                                const rounded = Math.abs(converted - Math.round(converted)) < 1e-9
                                  ? Math.round(converted)
                                  : Math.round(converted * 100) / 100;
                                setTradingRuntimeHours(String(rounded));
                              }
                              setTradingRuntimeUnit(nextUnit);
                              setTimeout(() => { tradingRuntimeEditingRef.current = false; }, 800);
                            }}
                            title="Runtime unit"
                          >
                            <option value="hours">hours</option>
                            <option value="days">day</option>
                          </select>
                        </div>
                      </div>
                      <div className="formRow">
                        <label>Reuse Profit %</label>
                        <input
                          value={tradingReuseProfitPct}
                          onChange={(e) => setTradingReuseProfitPct(e.target.value)}
                          onBlur={() => setTradingReuseProfitPct(String(normalizeTradingReuseProfitPct()))}
                          placeholder="0"
                          title="How much collected profit may be reused by the Strategist. 0% protects all profit; 100% compounds all profit."
                        />
                      </div>
                      <div className="formRow">
                        <label>Max Combined Slots</label>
                        <select
                          value={tradingMaxCombinedSlots}
                          onChange={(e) => setTradingMaxCombinedSlots(e.target.value)}
                          title="Maximum number of other slots whose capital the Strategist may combine into one stronger slot. 0 disables internal slot-capital rotation."
                        >
                          <option value="0">0 - off</option>
                          <option value="1">1 slot</option>
                          <option value="2">2 slots</option>
                          <option value="3">3 slots</option>
                        </select>
                      </div>
                      <div className="formRow">
                        <label>Capital HOLD (1-12h)</label>
                        <input
                          value={tradingHoldHours}
                          onChange={(e) => setTradingHoldHours(e.target.value)}
                          onBlur={() => setTradingHoldHours(String(clampTradingHoldHours(tradingHoldHours)))}
                          placeholder="1"
                          title="Minimum HOLD after exit/protection. After that Strategist keeps observing; no blind re-entry. Max observation is 12h."
                        />
                      </div>
                      <div className="formRow">
                        <label>Performance</label>
                        <select value={tradingStyle} onChange={(e) => handleTradingPerformanceChange(e.target.value)}>
                          <option value="TACTICAL">Tactical</option>
                          <option value="DEFENSIVE">Defensive</option>
                          <option value="AGGRESSIVE">Aggressive</option>
                        </select>
                      </div>
                    </div>

                    {aggressiveRiskConsentOpen && (
                      <div
                        className="modalBackdrop"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelAggressiveRiskConsent(); }}
                        style={{ zIndex: 99999 }}
                      >
                        <div
                          className="modal modalHelp"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: "linear-gradient(180deg, rgba(34,18,12,1), rgba(12,18,17,1))",
                            border: "1px solid rgba(255,193,7,.35)",
                            maxWidth: 520,
                          }}
                        >
                          <div className="modalHead">
                            <div className="cardTitle">Aggressive Performance Mode Warning</div>
                            <button
                              className="iconBtn"
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelAggressiveRiskConsent(); }}
                            >
                              ×
                            </button>
                          </div>
                          <div className="helpBody">
                            <p><b>Warning:</b> Aggressive Performance uses a faster, legacy-style high-frequency trading profile.</p>
                            <p>This can generate more trades, higher trading costs, stronger short-term fluctuations and possible losses. Soft defensive brakes are reduced; hard safety rules, budget limits, security checks and the net-profit exit guard remain active.</p>
                            <p>Please confirm that you understand the increased risk and still want to activate Aggressive Performance.</p>
                            <div className="row" style={{ gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
                              <button className="btnGhost" type="button" onClick={cancelAggressiveRiskConsent}>Cancel</button>
                              <button className="btn" type="button" onClick={confirmAggressiveRiskConsent}>I Understand and Accept the Risk</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="formRow">
                      <label>Allowed assets</label>
                      <div style={{ border: "1px solid rgba(139,220,255,.18)", borderRadius: 11, padding: 9, background: "rgba(0,0,0,.14)", display: "grid", gap: 8 }}>
                        <div className="muted tiny">Watchlist assets for <b>{String(activeGridChainKey || "ETH").toUpperCase()}</b>. Leave selection empty → Strategist picks the best from the full Watchlist. Selection = optional filter only.</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {traderAssetOptions.map((sym) => {
                            const selected = selectedTraderAssets.includes(sym);
                            return <button key={`trader-asset-${sym}`} type="button" className="btnGhost" onClick={() => toggleTraderAsset(sym)} style={{ height: 28, paddingInline: 9, borderColor: selected ? "rgba(34,197,94,.72)" : "rgba(255,255,255,.12)", background: selected ? "rgba(34,197,94,.18)" : "rgba(255,255,255,.03)", color: selected ? "#dfffee" : undefined }}>{selected ? "✓ " : ""}{sym}</button>;
                          })}
                        </div>
                        <div className="muted tiny">Selected: {selectedTraderAssets.length ? selectedTraderAssets.join(", ") : "none"}</div>
                      </div>
                    </div>



                    {renderPayoutAssetSelector("Payout asset")}

                    <div
                      style={{
                        borderRadius: 12,
                        background: "rgba(255,255,255,.035)",
                        border: "1px solid rgba(255,255,255,.065)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          className="btnGhost"
                          disabled={isTradingAggressivePerformance}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isTradingAggressivePerformance) return;
                            setTradingRiskExpanded((v) => !v);
                          }}
                          title={isTradingAggressivePerformance ? "Advanced Risk Engine is disabled in Aggressive Performance. Core safety protections remain active." : "Open Advanced Risk Engine"}
                          style={{ height: 30, paddingInline: 10, fontSize: 12, opacity: isTradingAggressivePerformance ? 0.55 : 1, cursor: isTradingAggressivePerformance ? "not-allowed" : "pointer" }}
                        >
                          {isTradingAggressivePerformance ? "Advanced Risk Engine disabled in Aggressive" : (tradingRiskExpanded ? "Advanced Risk Engine ▲" : "Advanced Risk Engine ▼")}
                        </button>
                        <InfoButton title="Advanced Risk Engine – Info">
                          <Help showClose dismissable
                            de={
                              <>
                                <p><b>Caution DD</b>: Warnzone. Nexus soll spaeter nicht sofort stoppen, sondern Aggressivitaet reduzieren.</p>
                                <p><b>Hard Stop</b>: echter Notfall-Stopp bei zu hohem Risiko oder Regelbruch.</p>
                                <p><b>Profit Lock</b>: schuetzt Gewinne, indem Risiko nach starkem Profit reduziert wird.</p>
                                <p><b>Max Trades</b>: begrenzt Aktivitaet und verhindert Overtrading.</p>
                              </>
                            }
                            en={
                              <>
                                <p><b>Caution DD</b>: warning zone. Nexus should reduce aggression later, not instantly stop.</p>
                                <p><b>Hard Stop</b>: emergency stop for excessive risk or rule violation.</p>
                                <p><b>Profit Lock</b>: protects gains by reducing risk after strong profit.</p>
                                <p><b>Max Trades</b>: limits activity and avoids overtrading.</p>
                              </>
                            }
                          />
                        </InfoButton>
                        {isTradingAggressivePerformance ? (
                          <div className="muted tiny" style={{ color: "#ffd166", fontWeight: 900 }}>
                            Aggressive Performance uses Legacy Protection Mode. Soft risk-engine brakes are not selectable here; hard safety, budget limits, security checks and net-profit exit remain active.
                          </div>
                        ) : null}
                      </div>

                      {tradingRiskExpanded && !isTradingAggressivePerformance ? (
                        <div style={{ padding: "0 10px 10px 10px", display: "grid", gap: 8 }}>
                          <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "1fr 1fr 1fr", gap: isCompactMobile ? 8 : 10 }}>
                            <div className="formRow">
                              <label>Risk mode</label>
                              <select value={tradingRiskMode} onChange={(e) => handleTradingRiskModeChange(e.target.value)}>
                                <option value="DEFENSIVE">Defensive</option>
                                <option value="BALANCED">Balanced</option>
                                <option value="DYNAMIC">Dynamic</option>
                              </select>
                            </div>
                            <div className="formRow">
                              <label>Caution DD %</label>
                              <input value={tradingCautionDrawdownPct} onChange={(e) => setTradingCautionDrawdownPct(e.target.value)} placeholder="3" />
                            </div>
                            <div className="formRow">
                              <label>Hard stop %</label>
                              <input value={tradingHardStopPct} onChange={(e) => setTradingHardStopPct(e.target.value)} placeholder="12" />
                            </div>
                            <div className="formRow">
                              <label>Profit lock %</label>
                              <input value={tradingProfitLockPct} onChange={(e) => setTradingProfitLockPct(e.target.value)} placeholder="20" />
                            </div>
                            <div className="formRow">
                              <label>Max slippage %</label>
                              <input value={tradingMaxSlippagePct} onChange={(e) => setTradingMaxSlippagePct(e.target.value)} placeholder="1.2" />
                            </div>
                            <div className="formRow">
                              <label>Max trades</label>
                              <input value={tradingMaxTrades} onChange={(e) => setTradingMaxTrades(e.target.value)} placeholder="6" />
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    </div>

                    {false ? (
                      <div
                        style={{
                          padding: "8px 10px",
                          borderRadius: 12,
                          background: "rgba(0,0,0,.18)",
                          border: "1px solid rgba(34,197,94,.20)",
                          display: "grid",
                          gap: 8,
                        }}
                      >
                        <button
                          className="miniBtn"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTradingQueueSlotsExpanded((v) => !v);
                          }}
                          style={{
                            justifySelf: "start",
                            minHeight: 28,
                            paddingInline: 10,
                            fontWeight: 900,
                            color: "#8bdcff",
                          }}
                          title="Show or hide the compact Trading slot list"
                        >
                          {tradingQueueSlotsExpanded ? "Hide Trading Slots ▲" : `Show Trading Slots (${tradingVisibleQueueSummary.queue.length}) ▼`}
                        </button>

                        {(!isCompactMobile || tradingQueueSlotsExpanded) ? (
                        <div style={{ display: "grid", gap: 6 }}>
                          {tradingVisibleQueueSummary.queue.map((slot) => (
                            <div
                              key={`${getTradingSlotSessionId(slot) || selectedTradingSessionId || "session"}-${slot.chain || slot.chain_key || "chain"}-${slot.slot || slot.slot_id || slot.id || "slot"}`}
                              style={(() => {
                                const st = String(slot.status || "").toUpperCase();
                                const isGreen = ["ACTIVE", "READY", "EXECUTED"].includes(st);
                                const isProtect = st === "PROTECT";
                                const isWait = st === "WAIT";
                                const isHold = ["HOLD", "OBSERVE"].includes(st);
                                const isRelease = st === "RELEASE_REQUIRED";
                                const isBlocked = st === "BLOCKED";
                                return {
                                  borderRadius: 10,
                                  border: isGreen ? "1px solid rgba(34,197,94,.45)" : isProtect ? "1px solid rgba(255,193,7,.42)" : isWait ? "1px solid rgba(255,193,7,.34)" : isHold ? "1px solid rgba(64,196,255,.34)" : isRelease ? "1px solid rgba(255,193,7,.42)" : isBlocked ? "1px solid rgba(239,68,68,.28)" : "1px solid rgba(255,255,255,.08)",
                                  background: isGreen ? "rgba(34,197,94,.11)" : isProtect ? "rgba(255,193,7,.095)" : isWait ? "rgba(255,193,7,.085)" : isHold ? "rgba(64,196,255,.075)" : isRelease ? "rgba(255,193,7,.10)" : isBlocked ? "rgba(239,68,68,.075)" : "rgba(255,255,255,.035)",
                                  padding: "7px 8px",
                                  display: "grid",
                                  gap: 3,
                                };
                              })()}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                                <b style={{ color: "#eafff5" }}>Slot {slot.slot} · {fmtUsd(getTradingSlotAmountUsd(slot))}</b>
                                <span
                                  className="tiny"
                                  style={{
                                    color: ["ACTIVE", "READY", "EXECUTED"].includes(String(slot.status || "").toUpperCase()) ? "#7cf7a2" : String(slot.status || "").toUpperCase() === "PROTECT" ? "#ffd166" : String(slot.status || "").toUpperCase() === "WAIT" ? "#ffc107" : ["HOLD", "OBSERVE"].includes(String(slot.status || "").toUpperCase()) ? "#8bdcff" : String(slot.status || "").toUpperCase() === "RELEASE_REQUIRED" ? "#ffd166" : String(slot.status || "").toUpperCase() === "BLOCKED" ? "#ff6b6b" : "rgba(235,255,247,.72)",
                                    fontWeight: 900,
                                  }}
                                >{String(slot.status || "WAIT").toUpperCase()} · priority {Math.round(Number(slot.priority || 0))}</span>
                              </div>
                              <div className="muted tiny">{slot.symbol || slot.asset || "asset pending"} · {slot.riskMode} · {slot.confidence} · {toUserFacingMessage(slot.condition || slot.reason, "")}</div>
                              {(() => {
                                const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                                const pnlPct = Number(slot.paper_pnl_pct ?? meta.paper_pnl_pct);
                                const pnlUsd = Number(slot.paper_pnl_usd ?? meta.paper_pnl_usd);
                                const grossPnl = Number(slot.paper_gross_pnl_usd ?? meta.paper_gross_pnl_usd ?? pnlUsd);
                                const netPnl = Number(slot.paper_net_pnl_usd ?? meta.paper_net_pnl_usd);
                                const estCosts = Number(slot.paper_estimated_costs_usd ?? meta.paper_estimated_costs_usd);
                                const estGas = Number(slot.paper_estimated_gas_usd ?? meta.paper_estimated_gas_usd);
                                const estDex = Number(slot.paper_estimated_dex_fee_usd ?? meta.paper_estimated_dex_fee_usd);
                                const estSlip = Number(slot.paper_estimated_slippage_usd ?? meta.paper_estimated_slippage_usd);
                                const pnlTotal = Number(slot.paper_cycle_realized_usd ?? meta.paper_cycle_realized_usd ?? slot.paper_pnl_total_usd ?? meta.paper_pnl_total_usd);
                                const entryPx = Number(slot.paper_entry_price ?? meta.paper_entry_price);
                                const markPx = Number(slot.paper_mark_price ?? meta.paper_mark_price);
                                const exitPx = Number(slot.paper_exit_price ?? meta.paper_exit_price);
                                if (!Number.isFinite(pnlPct) && !Number.isFinite(pnlUsd) && !Number.isFinite(pnlTotal) && !Number.isFinite(entryPx) && !Number.isFinite(markPx) && !Number.isFinite(exitPx)) return null;
                                const effectivePnl = Number.isFinite(netPnl) ? netPnl : (Number.isFinite(pnlUsd) ? pnlUsd : pnlPct);
                                const positive = effectivePnl >= 0;
                                return (
                                  <div style={{ display: "grid", gap: 2 }}>
                                    <div className="tiny" style={{ color: positive ? "#7cf7a2" : "#ff8a8a", fontWeight: 900 }}>
                                      Paper PnL: {Number.isFinite(pnlPct) ? `${pnlPct >= 0 ? "+" : ""}${pnlPct.toFixed(2)}%` : "—"}
                                      {Number.isFinite(grossPnl) ? ` · gross ${grossPnl >= 0 ? "+" : ""}${fmtUsd(Math.abs(grossPnl))}` : ""}
                                      {Number.isFinite(estCosts) && estCosts > 0 ? ` · costs -${fmtUsd(estCosts)}` : ""}
                                      {Number.isFinite(netPnl) ? ` · net ${netPnl >= 0 ? "+" : ""}${fmtUsd(Math.abs(netPnl))}` : ""}
                                      {Number.isFinite(pnlTotal) ? ` · cycle ${pnlTotal >= 0 ? "+" : ""}${fmtUsd(Math.abs(pnlTotal))}` : ""}
                                    </div>
                                    {Number.isFinite(estCosts) && estCosts > 0 ? (
                                      <div className="muted tiny" style={{ color: "#ffd166" }}>
                                        Estimated costs: Gas {Number.isFinite(estGas) ? fmtUsd(estGas) : "—"} · DEX {Number.isFinite(estDex) ? fmtUsd(estDex) : "—"} · Slippage {Number.isFinite(estSlip) ? fmtUsd(estSlip) : "—"}
                                      </div>
                                    ) : null}
                                    {(Number.isFinite(entryPx) || Number.isFinite(markPx) || Number.isFinite(exitPx)) ? (
                                      <div className="muted tiny">
                                        Paper price / Kurs: Entry {Number.isFinite(entryPx) ? fmtUsd(entryPx) : "—"} · Current {Number.isFinite(markPx) ? fmtUsd(markPx) : "—"}{Number.isFinite(exitPx) ? ` · Exit ${fmtUsd(exitPx)}` : ""}
                                        <span style={{ opacity: 0.72 }}> · not slot capital</span>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}
                              {["PROTECT", "HOLD", "OBSERVE", "RELEASE_REQUIRED"].includes(String(slot.status || "").toUpperCase()) ? (
                                <div className="muted tiny" style={{ display: "grid", gap: 4 }}>
                                  <div>
                                    {String(slot.status || "").toUpperCase() === "PROTECT" ? "Protect mode active" : <>HOLD until {slot.holdUntil ? new Date(Number(slot.holdUntil)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "n/a"} · max observe until {slot.observeUntil ? new Date(Number(slot.observeUntil)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "n/a"}</>}
                                  </div>
                                  <details style={{ border: "1px solid rgba(139,220,255,.18)", borderRadius: 8, padding: "4px 6px", background: "rgba(64,196,255,.045)" }}>
                                    <summary style={{ cursor: "pointer", color: "#8bdcff", fontWeight: 900 }}>Observe info</summary>
                                    <div style={{ marginTop: 4, lineHeight: 1.45 }}>
                                      {toUserFacingMessage(slot.observeReason || slot.condition, "The Strategist is still observing market structure, liquidity, RVOL and risk. Capital will not be reallocated until market quality is clean.")}
                                    </div>
                                  </details>
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        ) : null}
                      </div>
                    ) : null}

                    {shadowUiEnabled ? (
                    <details
                      open={shadowDrawerOpen}
                      onToggle={(e) => setShadowDrawerOpen(!!e.currentTarget.open)}
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(64,196,255,.18)",
                        background: "rgba(0,0,0,.12)",
                        overflow: "hidden",
                      }}
                    >
                      <summary
                        style={{
                          cursor: "pointer",
                          listStyle: "none",
                          padding: "10px 12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                          color: "#8bdcff",
                          fontWeight: 950,
                        }}
                      >
                        <span>{isPro ? "Shadow Simulator" : "Shadow Simulator · Free Mode"}</span>
                        <span className="muted tiny" style={{ color: shadowExecutorState?.runtime_status === "RUNNING" ? "#7cf7a2" : "rgba(216,255,241,.68)" }}>
                          {shadowExecutorState?.runtime_status === "RUNNING" ? "Active" : "Ready"} · {shadowDrawerOpen ? "Hide ▲" : "Open ▼"}
                        </span>
                      </summary>
                    <div
                      style={{
                        padding: "9px 10px",
                        borderRadius: 12,
                        background: "rgba(64,196,255,.055)",
                        border: "1px solid rgba(64,196,255,.18)",
                        display: "grid",
                        gap: 7,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 950, color: "#8bdcff", fontSize: 13 }}>Shadow Executor</div>
                          <div className="muted tiny">Paper execution only: virtual fills, stop/re-entry validation, reallocation observation. No Vault transaction can be triggered here.</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            className="btnGhost"
                            type="button"
                            onClick={() => runShadowRuntimeAction("start")}
                            disabled={shadowExecutorBusy || !wallet}
                            style={{ height: 30, paddingInline: 10 }}
                            title="Start live-like paper Shadow runtime"
                          >
                            {shadowExecutorBusy ? "Working..." : "Start Shadow"}
                          </button>
                          <button className="miniBtn" type="button" onClick={() => runShadowRuntimeAction("pause")} disabled={shadowExecutorBusy || !wallet} style={{ height: 30, paddingInline: 10 }}>Pause</button>
                          <button className="miniBtn" type="button" onClick={() => runShadowRuntimeAction("resume")} disabled={shadowExecutorBusy || !wallet} style={{ height: 30, paddingInline: 10 }}>Resume</button>
                          <button className="btnDanger" type="button" onClick={() => runShadowRuntimeAction("stop")} disabled={shadowExecutorBusy || !wallet} style={{ height: 30, paddingInline: 10 }}>Stop</button>
                          <button className="miniBtn" type="button" onClick={runShadowExecutorValidation} disabled={shadowExecutorBusy || !wallet} style={{ height: 30, paddingInline: 10 }} title="One-shot validation only">Test</button>
                        </div>
                      </div>
                      {(() => {
                        const run = shadowExecutorState?.last_run || shadowExecutorState?.run || null;
                        const summary = run?.summary || {};
                        if (!run && !shadowExecutorMsg) {
                          return <div className="muted tiny">No shadow run yet. Run this before Vault deployment to validate behavior without risking live capital.</div>;
                        }
                        const status = String(summary?.status || run?.status || "pending").toUpperCase();
                        const runtimeStatus = String(shadowExecutorState?.runtime_status || summary?.runtime_status || summary?.runtime?.status || "idle").toUpperCase();
                        const readiness = String(summary?.readiness || "PENDING").replaceAll("_", " ");
                        const score = Number(summary?.safety_score ?? NaN);
                        return (
                          <div style={{ display: "grid", gap: 5 }}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <span className="tiny" style={{ fontWeight: 950, color: runtimeStatus === "RUNNING" ? "#7cf7a2" : runtimeStatus === "PAUSED" ? "#ffd166" : "rgba(235,255,247,.78)" }}>Shadow Runtime: {runtimeStatus === "RUNNING" ? "Active" : runtimeStatus === "PAUSED" ? "Paused" : "Idle"}</span>
                              <span className="tiny" style={{ fontWeight: 900, color: "rgba(235,255,247,.78)" }}>Safety: {Number.isFinite(score) ? `${score}/100` : "—"}</span>
                            </div>
                            {selectedTradingSession ? (() => {
                              const slots = Array.isArray(tradingVisibleQueueSummary?.queue) ? tradingVisibleQueueSummary.queue : [];
                              const snap = getTradingStrategistRuntimeSnapshot(selectedTradingSession, slots, run);
                              return (
                                <div style={{ border: `1px solid ${snap.regimeTone.border}`, borderRadius: 10, padding: "7px 8px", background: snap.regimeTone.bg, display: "grid", gap: 5 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <span className="tiny" style={{ fontWeight: 950, color: snap.regimeTone.color }}>Market Regime: {snap.regimeTone.label}</span>
                                    <span className="tiny" style={{ fontWeight: 900, color: "rgba(235,255,247,.80)" }}>Elapsed {snap.elapsedPct}% · Runtime {snap.runtimeHours}h</span>
                                  </div>
                                  <div className="muted tiny" style={{ color: "#d8fff1" }}>
                                    Performance Mode: {String(selectedTradingSession?.style || selectedTradingSession?.strategy || "Tactical").toUpperCase().charAt(0) + String(selectedTradingSession?.style || selectedTradingSession?.strategy || "Tactical").toLowerCase().slice(1)}
                                  </div>
                                </div>
                              );
                            })() : null}
                            {shadowExecutorMsg ? <div className="muted tiny" style={{ color: "#8bdcff" }}>{shadowExecutorMsg}</div> : null}
                            {Array.isArray(run?.events) && run.events.length ? (
                              <details
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                style={{ border: "1px solid rgba(139,220,255,.16)", borderRadius: 8, padding: "4px 6px", background: "rgba(64,196,255,.035)" }}
                              >
                                <summary
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  style={{ cursor: "pointer", color: "#8bdcff", fontWeight: 900 }}
                                >Latest shadow events</summary>
                                <div style={{ marginTop: 5, display: "grid", gap: 4 }}>
                                  {run.events.slice(0, 8).map((ev, idx) => {
                                    const sid = String(ev?.session_id || ev?.sessionId || "").trim();
                                    const ch = String(ev?.chain || "").trim();
                                    const perf = String(ev?.performance_mode || ev?.performanceMode || "").trim();
                                    const prefix = [ev?.asset || ev?.symbol || "Session", ch, perf].filter(Boolean).join(" · ");
                                    return (
                                      <div key={`${ev?.type || "event"}-${idx}`} className="muted tiny">
                                        {ev?.type || "EVENT"}: {prefix ? `${prefix} — ` : ""}{ev?.message || "Shadow event recorded."}
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            ) : null}
                          </div>
                        );
                      })()}
                    </div>
                    </details>
                    ) : null}


                    {renderFundingPrompt("TRADING")}

                    {Array.isArray(openTradingSessions) && openTradingSessions.length ? (
                      <div style={{ display: isCompactMobile ? "grid" : "none", gap: 8, padding: "8px 10px", borderRadius: 12, background: "rgba(0,0,0,.14)", border: "1px solid rgba(139,220,255,.12)" }}>
                        <div className="muted tiny" style={{ fontWeight: 950, color: "#8bdcff" }}>Active Trading sessions</div>
                        <select
                          value={selectedTradingSessionId || String(openTradingSessions?.[0]?.id || "")}
                          onChange={(e) => selectTradingSession(e.target.value)}
                          style={{
                            width: "100%",
                            border: "1px solid rgba(139,220,255,.22)",
                            borderRadius: 10,
                            background: "rgba(0,0,0,.28)",
                            color: "#eafff5",
                            padding: "8px 10px",
                            fontSize: 12,
                            fontWeight: 900,
                          }}
                          title="Choose which independent Trading session to inspect/control"
                        >
                          {openTradingSessions.slice(0, 20).map((sess) => {
                            const sid = String(sess?.id || "");
                            const primaryAsset = getTradingSessionPrimaryAsset(sess);
                            const sessionProfit = getTradingSessionProfitUsd(sess);
                            const tradeCount = getTradingSessionTradeCount(sess);
                            const profitLabel = `${sessionProfit >= 0 ? "+" : "-"}${fmtUsd(Math.abs(sessionProfit))}`;
                            const label = `${primaryAsset || "ASSET"} · ${fmtUsd(getTradingSessionBudgetUsd(sess))} · Profit ${profitLabel} · Closed trades ${tradeCount} · ${getTradingSessionSlotCount(sess)} slots · ${String(sess.status || "ACTIVE").toUpperCase()} · ${sid.slice(0, 18)}`;
                            return <option key={sid || `session-${sess?.createdAt || Math.random()}`} value={sid}>{label}</option>;
                          })}
                        </select>
                        {(isCompactMobile && selectedTradingSession) ? (() => {
                          const sessionSlots = (() => {
                            const rows = Array.isArray(tradingVisibleQueueSummary?.queue) ? tradingVisibleQueueSummary.queue : [];
                            return isTradingSessionLive(selectedTradingSession) ? rows.map((sl) => sanitizeLiveTradingSlot(sl)) : rows;
                          })();
                          const sessionAsset = getTradingSessionPrimaryAsset(selectedTradingSession) || "ASSET";
                          const sessionBudget = getTradingSessionBudgetUsd(selectedTradingSession);
                          const sessionProfit = getTradingSessionProfitUsd(selectedTradingSession);
                          const sessionProfitPct = sessionBudget > 0 ? (sessionProfit / sessionBudget) * 100 : 0;
                          const reusePct = Number(selectedTradingSession?.reuseProfitPct ?? selectedTradingSession?.reuse_profit_pct ?? selectedTradingSession?.profitReusePct ?? selectedTradingSession?.profit_reuse_pct ?? 0);
                          const maxCombined = Number(selectedTradingSession?.maxCombinedSlots ?? selectedTradingSession?.max_combined_slots ?? selectedTradingSession?.slotDonorCap ?? selectedTradingSession?.slot_donor_cap ?? 0);
                          const timing = getTradingSessionTiming(selectedTradingSession);
                          const counts = sessionSlots.reduce((acc, slot) => {
                            const st = String(slot?.status || "WAIT").toUpperCase();
                            acc[st] = (acc[st] || 0) + 1;
                            return acc;
                          }, {});
                          const activeSlot = sessionSlots.find((slot) => String(slot?.status || "").toUpperCase() === "ACTIVE") || sessionSlots.find((slot) => ["READY", "WAIT", "SIMULATED_EXIT"].includes(String(slot?.status || "").toUpperCase())) || null;
                          const aggregate = sessionSlots.reduce((acc, slot) => {
                            const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                            const gross = Number(slot.paper_gross_pnl_usd ?? meta.paper_gross_pnl_usd ?? 0);
                            const costs = Number(slot.paper_estimated_costs_usd ?? meta.paper_estimated_costs_usd ?? 0);
                            const net = Number(slot.paper_net_pnl_usd ?? meta.paper_net_pnl_usd ?? slot.paper_pnl_usd ?? meta.paper_pnl_usd ?? 0);
                            const cycle = Number(slot.paper_cycle_realized_usd ?? meta.paper_cycle_realized_usd ?? 0);
                            if (Number.isFinite(gross)) acc.gross += gross;
                            if (Number.isFinite(costs)) acc.costs += costs;
                            if (Number.isFinite(net)) acc.net += net;
                            if (Number.isFinite(cycle)) acc.cycle += cycle;
                            return acc;
                          }, { gross: 0, costs: 0, net: 0, cycle: 0 });
                          const progressPct = sessionBudget > 0 ? Math.max(0, Math.min(200, 100 + sessionProfitPct)) : 0;
                          const stateLabel = String(selectedTradingSession?.status || selectedTradingSessionLabel || "ACTIVE").toUpperCase();
                          const tradeCount = getTradingSessionTradeCount(selectedTradingSession, sessionSlots);
                          const selectedSessionLivePrice = getTradingSessionLivePriceInfo(selectedTradingSession, sessionSlots, sessionAsset, watchRows);
                          return (
                          <div
                            style={{
                              border: "1px solid rgba(34,197,94,.32)",
                              borderRadius: 14,
                              background: "linear-gradient(135deg, rgba(34,197,94,.10), rgba(0,0,0,.18))",
                              padding: "10px 12px",
                              display: "grid",
                              gap: 10,
                              boxShadow: "0 0 0 1px rgba(255,255,255,.025) inset",
                            }}
                          >
                            <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "1.1fr 1.1fr 1.1fr auto", gap: 12, alignItems: "center" }}>
                              <div style={{ display: "grid", gap: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                  <div style={{ color: "#eafff5", fontWeight: 950, fontSize: 14 }}>{sessionAsset} · {fmtUsd(sessionBudget)} · {getTradingSessionSlotCount(selectedTradingSession)} slots</div>
                                  <span className="tiny" style={{ padding: "3px 8px", borderRadius: 999, background: stateLabel === "ACTIVE" ? "rgba(34,197,94,.18)" : stateLabel === "PAUSED" ? "rgba(255,193,7,.16)" : "rgba(139,220,255,.12)", color: stateLabel === "ACTIVE" ? "#7cf7a2" : stateLabel === "PAUSED" ? "#ffd166" : "#8bdcff", fontWeight: 950 }}>{stateLabel}</span>
                                </div>
                                <div className="muted tiny" style={{ color: timing.isExpired ? "#ffd166" : "#8bdcff", fontWeight: 900 }}>⏱ Runtime: {timing.elapsedLabel} · Left: {timing.remainingLabel}</div>
                                <div className="muted tiny">Closed trades {tradeCount} · Slots in use {counts.ACTIVE || 0}/{Math.max(1, getTradingSessionSlotCount(selectedTradingSession) || sessionSlots.length || 0)}</div>
                              </div>

                              <div style={{ display: "grid", gap: 5 }}>
                                <div className="muted tiny" style={{ color: sessionProfit >= 0 ? "#22c55e" : "#ff6b6b", fontWeight: 950 }}>Session Profit: {`${sessionProfit >= 0 ? "+" : "-"}${fmtUsd(Math.abs(sessionProfit))}`} {sessionBudget > 0 ? `(${sessionProfitPct >= 0 ? "+" : ""}${sessionProfitPct.toFixed(2)}%)` : ""}</div>
                                <div className="muted tiny" style={{ color: "#7cf7a2", fontWeight: 900 }}>Gross: {aggregate.gross >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.gross))} · Net: {aggregate.net >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.net))}</div>
                                <div className="muted tiny" style={{ color: aggregate.costs > 0 ? "#ffd166" : "rgba(216,255,241,.68)", fontWeight: 850 }}>Costs: {aggregate.costs > 0 ? `-${fmtUsd(aggregate.costs)}` : fmtUsd(0)} · Cycle: {aggregate.cycle >= 0 ? "+" : "-"}{fmtUsd(Math.abs(aggregate.cycle))}</div>
                              </div>

                              <div style={{ display: "grid", gap: 5 }}>
                                <div className="muted tiny" style={{ color: "#8bdcff", fontWeight: 900, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                  <span>Performance: {String(selectedTradingSession?.style || selectedTradingSession?.strategy || "Tactical").toUpperCase().charAt(0) + String(selectedTradingSession?.style || selectedTradingSession?.strategy || "Tactical").toLowerCase().slice(1)}</span>
                                  {selectedSessionLivePrice ? <span style={{ color: selectedSessionLivePrice.color, fontWeight: 950 }}>· Live: {fmtUsd(selectedSessionLivePrice.price)} {selectedSessionLivePrice.arrow}{selectedSessionLivePrice.changePct !== null ? ` ${fmtPct(selectedSessionLivePrice.changePct)}` : ""}</span> : null}
                                </div>
                                <div className="muted tiny" style={{ color: "rgba(216,255,241,.72)", fontWeight: 850 }}>Payout: {selectedTradingSession?.payoutAsset || selectedTradingSession?.payout_asset || manualPayoutAsset || "USDC"}</div>
                                <div className="muted tiny">Current slot: {activeSlot ? `#${activeSlot.slot || activeSlot.slot_id || "—"} · ${String(activeSlot.status || "WAIT").toUpperCase()} · ${fmtUsd(getTradingSlotAmountUsd(activeSlot))}` : "—"}</div>
                                <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,.10)", overflow: "hidden", marginTop: 2 }}>
                                  <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, progressPct))}%`, background: sessionProfit >= 0 ? "linear-gradient(90deg, #22c55e, #7cf7a2)" : "linear-gradient(90deg, #ff6b6b, #ffd166)" }} />
                                </div>
                              </div>

                              <div style={{ display: "grid", gap: 7, minWidth: 116 }}>
                                <button className="miniBtn" type="button" title="Open detailed session view">Details</button>
                                {/* BUILD425: Pause / Stop only on Active Trading Session cards — no duplicate controls here. */}
                                {tradingCanReleaseCapital ? (
                                  <button className="miniBtn" type="button" onClick={handleTradingReleaseCapital} title="Release capital for this selected Trading session">Release Capital</button>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ borderTop: "1px solid rgba(139,220,255,.10)", paddingTop: 8, display: "grid", gap: 8 }}>
                              <button
                                className="miniBtn"
                                type="button"
                                onClick={() => setTradingSessionSlotsExpanded((v) => !v)}
                                style={{ justifySelf: "start", height: 28, paddingInline: 10 }}
                                title="Show or hide the detailed slots for this selected Trading session"
                              >
                                {tradingSessionSlotsExpanded ? "Hide Slots ▲" : `Show Slots (${sessionSlots.length}) ▼`}
                              </button>

                              {tradingSessionSlotsExpanded ? (
                                <div style={{ display: "grid", gap: 7, maxHeight: isCompactMobile ? 360 : 420, overflowY: "auto", paddingRight: 4 }}>
                                  {sessionSlots.map((slot, idx) => {
                                    const meta = slot?.meta && typeof slot.meta === "object" ? slot.meta : {};
                                    const st = String(slot?.status || "WAIT").toUpperCase();
                                    const gross = Number(slot.paper_gross_pnl_usd ?? meta.paper_gross_pnl_usd ?? 0);
                                    const costs = Number(slot.paper_estimated_costs_usd ?? meta.paper_estimated_costs_usd ?? 0);
                                    const net = Number(slot.paper_net_pnl_usd ?? meta.paper_net_pnl_usd ?? slot.paper_pnl_usd ?? meta.paper_pnl_usd ?? 0);
                                    const cycle = Number(slot.paper_cycle_realized_usd ?? meta.paper_cycle_realized_usd ?? 0);
                                    const isActive = st === "ACTIVE";
                                    const isReady = st === "READY";
                                    const isWait = st === "WAIT";
                                    const isExit = st === "SIMULATED_EXIT";
                                    const border = isActive ? "rgba(34,197,94,.48)" : isReady ? "rgba(34,197,94,.32)" : isWait ? "rgba(255,193,7,.30)" : isExit ? "rgba(139,220,255,.28)" : "rgba(255,255,255,.10)";
                                    const bg = isActive ? "rgba(34,197,94,.10)" : isReady ? "rgba(34,197,94,.065)" : isWait ? "rgba(255,193,7,.06)" : isExit ? "rgba(139,220,255,.055)" : "rgba(255,255,255,.025)";
                                    return (
                                      <div key={`${getTradingSlotSessionId(slot) || selectedTradingSessionId || "session"}-${slot.slot || slot.slot_id || idx}`} style={{ border: `1px solid ${border}`, background: bg, borderRadius: 10, padding: "8px 9px", display: "grid", gap: 5 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                                          <b style={{ color: "#eafff5" }}>Slot {slot.slot || slot.slot_id || idx + 1} · {fmtUsd(getTradingSlotAmountUsd(slot))}</b>
                                          <span className="tiny" style={{ color: isActive || isReady ? "#7cf7a2" : isWait ? "#ffc107" : isExit ? "#8bdcff" : "rgba(235,255,247,.72)", fontWeight: 950 }}>{st}</span>
                                        </div>
                                        <div className="muted tiny">{slot.symbol || slot.asset || sessionAsset} · {toUserFacingMessage(slot.reason || slot.message || slot.note, "Strategist slot state")}</div>
                                        <div className="tiny" style={{ fontWeight: 900, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                          <span style={{ color: gross >= 0 ? "#7cf7a2" : "#ff8a8a" }}>gross {gross >= 0 ? "+" : "-"}{fmtUsd(Math.abs(gross))}</span>
                                          <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                          <span style={{ color: costs > 0 ? "#ffd166" : "rgba(216,255,241,.68)" }}>costs {costs > 0 ? `-${fmtUsd(costs)}` : fmtUsd(0)}</span>
                                          <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                          <span style={{ color: net >= 0 ? "#7cf7a2" : "#ff8a8a" }}>net {net >= 0 ? "+" : "-"}{fmtUsd(Math.abs(net))}</span>
                                          <span style={{ color: "rgba(216,255,241,.68)" }}>·</span>
                                          <span style={{ color: cycle >= 0 ? "#7cf7a2" : "#ff8a8a" }}>cycle {cycle >= 0 ? "+" : "-"}{fmtUsd(Math.abs(cycle))}</span>
                                        </div>
                                        <div className="muted tiny">Entry {fmtUsd(Number(slot.paper_entry_price ?? meta.paper_entry_price ?? 0))} · Current {fmtUsd(Number(slot.paper_mark_price ?? meta.paper_mark_price ?? 0))} · Exit {fmtUsd(Number(slot.paper_exit_price ?? meta.paper_exit_price ?? 0))}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>

                            {null}
                          </div>
                          );
                        })() : null}
                      </div>
                    ) : Array.isArray(stoppedTradingSessions) && stoppedTradingSessions.length ? (
                      <div className="muted tiny" style={{ padding: "8px 10px", borderRadius: 12, background: "rgba(0,0,0,.12)", border: "1px solid rgba(255,255,255,.08)", color: "rgba(216,255,241,.68)" }}>
                        No active Trading sessions. {stoppedTradingSessions.length} stopped/released session{stoppedTradingSessions.length === 1 ? "" : "s"} kept only as local history.
                      </div>
                    ) : null}

                    {selectedTradingSession ? (
                    <div
                      style={{
                        padding: "7px 10px",
                        borderRadius: 12,
                        background: selectedTradingSessionLabel === "ACTIVE" ? "rgba(34,197,94,.08)" : selectedTradingSessionLabel === "PROTECT" ? "rgba(255,193,7,.09)" : selectedTradingSessionLabel === "PAUSED" ? "rgba(245,193,108,.08)" : "rgba(245,193,108,.07)",
                        border: selectedTradingSessionLabel === "ACTIVE" ? "1px solid rgba(34,197,94,.22)" : selectedTradingSessionLabel === "PROTECT" ? "1px solid rgba(255,193,7,.28)" : "1px solid rgba(245,193,108,.20)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 900, color: selectedTradingSessionLabel === "ACTIVE" ? "#7cf7a2" : selectedTradingSessionLabel === "PROTECT" ? "#ffd166" : ["HOLD", "OBSERVE"].includes(selectedTradingSessionLabel) ? "#8bdcff" : selectedTradingSessionLabel === "RELEASE_REQUIRED" ? "#ffd166" : "#f5c16c", fontSize: 13 }}>
                          Status: {selectedTradingSessionLabel === "PREPARED" ? "Prepared" : selectedTradingSessionLabel === "ARMED" ? "Armed" : selectedTradingSessionLabel === "ACTIVE" ? "Active" : selectedTradingSessionLabel === "PROTECT" ? "Protect" : selectedTradingSessionLabel === "PAUSED" ? "Paused" : selectedTradingSessionLabel === "HOLD" ? "Capital Hold" : selectedTradingSessionLabel === "OBSERVE" ? "Observe" : selectedTradingSessionLabel === "RELEASE_REQUIRED" ? "Release required" : selectedTradingSessionLabel === "STOPPED" ? "Stopped" : "Prepared"}
                        </div>
                        <div className="muted tiny">{selectedTradingSessionLabel === "PREPARED" ? "Approve a budget to create a new independent Trading session. Later budgets create additional sessions inside your limits." : selectedTradingSessionLabel === "ARMED" ? "Armed. Nexus Trading activates automatically." : selectedTradingSessionLabel === "ACTIVE" ? "Autonomous trading active. Pause and Stop are on the session card." : selectedTradingSessionLabel === "PROTECT" ? "Protect mode active. Strategist detected elevated risk; no new add-ons and exit can be triggered if risk worsens." : selectedTradingSessionLabel === "PAUSED" ? "Paused by user. Resume or Stop remains under user control." : selectedTradingSessionLabel === "HOLD" ? "Capital protected. Minimum HOLD is active; no new allocation can start." : selectedTradingSessionLabel === "OBSERVE" ? "Minimum HOLD completed. Strategist keeps checking; no trade unless market quality is clean." : selectedTradingSessionLabel === "RELEASE_REQUIRED" ? "Max 12h observation reached. User must release/approve capital before new allocation." : "Stopped. Load or approve a setup again to continue."}</div>
                        <div className="muted tiny" style={{ marginTop: 3, color: tradingGlobalRiskState?.status === "COOLDOWN" || tradingGlobalRiskState?.status === "PROTECT" ? "#ffd166" : "rgba(216,255,241,.72)" }}>
                          {tradingGlobalRiskLabel}
                          {tradingGlobalRiskState?.blocked_reason ? ` · ${String(tradingGlobalRiskState.blocked_reason).slice(0, 140)}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        
                        {selectedTradingSessionLabel === "ARMED" ? (
                          <button className="btn" type="button" onClick={handleTradingStartSession} disabled={!tradingCanStart} title="Start the Trader worker with the central Strategist" style={{ height: 30, paddingInline: 10 }}>{tradingStartBusy ? "Starting…" : "Start Trading"}</button>
                        ) : null}
                        {(String(tradingBudgetUsd || "").trim() || String(tradingBudgetSplitInput || "").trim()) ? (
                          <button
                            className="miniBtn"
                            type="button"
                            onClick={() => { resetAggressiveDraftSelection(); setTradingBudgetUsd(""); setTradingBudgetSplitInput(""); }}
                            style={{ height: 30, paddingInline: 10 }}
                            title="Clear only the next-budget draft. Existing Trading sessions remain active."
                          >
                            Clear Next Budget
                          </button>
                        ) : null}
                        
                        {/* BUILD425: Pause / Resume / Stop only on session cards. */}
                        {tradingCanReleaseCapital ? (
                          <button className="btnGhost" type="button" onClick={handleTradingReleaseCapital} style={{ height: 30, paddingInline: 10 }}>Release Capital</button>
                        ) : null}
                      </div>
                    </div>
                    ) : (
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                        <button
                          className="btn"
                          type="button"
                          onClick={handleTradingStartSession}
                          disabled={!tradingCanStart}
                          title={tradingPreflight.ok ? "Confirm the CoreVault budget in the wallet and start Trading automatically" : tradingPreflight.title}
                          style={{ height: 34, paddingInline: 14 }}
                        >
                          Start Trading
                        </button>
                        {(String(tradingBudgetUsd || "").trim() || String(tradingBudgetSplitInput || "").trim()) ? (
                          <button className="miniBtn" type="button" onClick={() => { resetAggressiveDraftSelection(); setTradingBudgetUsd(""); setTradingBudgetSplitInput(""); }} style={{ height: 34, paddingInline: 10 }}>Clear</button>
                        ) : null}
                      </div>
                    )}

                    
                  </div>
                  <EngineEventHistory engine="TRADER" events={traderEventHistory} />
                </div>
              ) : (
                <>

          <div className="gridWrap" style={{ gridTemplateColumns: "1fr", gap: 12 }}>
            {gridOrders.length && gridOrdersOpen ? (
              <div className="gridOrders" style={{ border: "1px solid rgba(46,204,113,.16)", borderRadius: 14, padding: "10px 12px", background: "rgba(0,0,0,.10)", display: "grid", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <b style={{ color: "#eafff5" }}>Grid Orders</b>
                  <span className="pill silver">{gridOrders.length} order{gridOrders.length === 1 ? "" : "s"}</span>
                </div>
                <div style={{ display: "grid", gap: 8, maxHeight: isCompactMobile ? 420 : 320, overflowY: "auto", paddingRight: 4 }}>
                  {gridOrders.map((o, idx) => {
                    const oid = idOf(o);
                    const side = String(o?.side || "ORDER").toUpperCase();
                    const statusTxt = inferOrderStatus(o);
                    const payout = inferOrderPayoutAsset(o);
                    const qty = Number(o?.qty ?? o?.amount ?? o?.quantity ?? 0);
                    const price = Number(o?.price ?? o?.target_price ?? 0);
                    const investedUsd = Number(o?.investedUsd ?? o?.invested_usd ?? o?.invested ?? o?.cost_basis ?? (qty * price)) || 0;
                    const atTargetUsd = Number(o?.targetValue ?? o?.target_value ?? o?.expectedOutUsd ?? o?.expected_out_usd ?? o?.expectedPayoutUsd ?? o?.expected_payout_usd ?? (qty * price)) || 0;
                    const orderItem = String(o?.item || o?.item_id || "").trim();
                    const orderChain = String(o?.chain || o?.chainKey || o?.network || (orderItem.includes(":") ? orderItem.split(":", 1)[0] : "")).toUpperCase();
                    const selectedItem = String(gridItemId || "").trim().toUpperCase();
                    const sameSelectedItem = !!orderItem && orderItem.toUpperCase() === selectedItem;
                    // BUILD436: every wallet-wide order uses its own worker-persisted market price.
                    // Only the currently selected setup item may fall back to the setup price.
                    const livePx = Number(
                      o?.current_price ?? o?.currentPrice ?? o?.live_price ?? o?.livePrice ?? o?.mark_price ?? o?.markPrice ?? o?.last_price ??
                      (sameSelectedItem ? (shownGridPrice || gridLiveFallback || gridMeta?.price || 0) : 0)
                    ) || 0;
                    const markUsd = (Number.isFinite(livePx) && livePx > 0 && Number.isFinite(qty) && qty > 0) ? livePx * qty : 0;
                    const targetUsd = (Number.isFinite(price) && price > 0 && Number.isFinite(qty) && qty > 0) ? price * qty : atTargetUsd;
                    const uPnL = (markUsd > 0 && targetUsd > 0)
                      ? (side === "SELL" ? (markUsd - investedUsd) : (markUsd - investedUsd))
                      : 0;
                    const uPnLPct = investedUsd > 0 ? (uPnL / investedUsd) * 100 : 0;
                    const targetGain = investedUsd > 0 ? (targetUsd - investedUsd) : 0;
                    const targetGainPct = investedUsd > 0 ? (targetGain / investedUsd) * 100 : 0;
                    const pnlColor = uPnL >= 0 ? "#7cf7a2" : "#ff8a8a";
                    const tgtColor = targetGain >= 0 ? "#8bdcff" : "#ffd978";
                    return (
                      <div key={oid || `grid-order-${idx}`} className="orderRow" style={{ padding: "10px 12px", border: "1px solid rgba(255,255,255,.08)", borderRadius: 13, background: "rgba(255,255,255,.035)", display: "grid", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", minWidth: 0 }}>
                            <b style={{ color: "#eafff5" }}>Order #{idx + 1}</b>
                            <span className="pill silver">{orderChain || "—"}</span>
                            <span className={`pill ${side === "BUY" ? "good" : "bad"}`}>{side}</span>
                            <span className="pill silver">{statusTxt}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="miniBtn"
                              disabled={!oid || !["OPEN", "PAUSED"].includes(statusTxt) || gridBusy.stopOrderId === String(oid)}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); (statusTxt === "PAUSED" ? resumeGridOrder(oid) : stopGridOrder(oid)); }}
                              style={{ height: 28, paddingInline: 9 }}
                              title={statusTxt === "PAUSED" ? "Resume order + on-chain session" : "Pause order + on-chain session"}
                            >
                              {gridBusy.stopOrderId === String(oid) ? (statusTxt === "PAUSED" ? "Resuming..." : "Pausing...") : (statusTxt === "PAUSED" ? "Resume" : "Pause")}
                            </button>
                            <button
                              type="button"
                              className="miniBtn"
                              disabled={!oid || gridBusy.deleteOrderId === String(oid) || statusTxt === "FILLED"}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteGridOrder(oid); }}
                              style={{ height: 28, paddingInline: 9, color: "#ff8a8a", borderColor: "rgba(255,107,107,.35)" }}
                              title="Stop order and finalize on-chain GRID session"
                            >
                              {gridBusy.deleteOrderId === String(oid) ? "Stopping..." : "Stop"}
                            </button>
                          </div>
                        </div>
                        <div className="muted tiny" style={{ display: "flex", gap: "5px 14px", flexWrap: "wrap" }}>
                          <span>Target: <b>{Number.isFinite(price) && price > 0 ? fmtUsd(price) : "—"}</b></span>
                          <span>Live: <b>{Number.isFinite(livePx) && livePx > 0 ? fmtUsd(livePx) : "—"}</b></span>
                          <span>Qty: <b>{Number.isFinite(qty) && qty > 0 ? fmtQty(qty, 6) : "—"}</b></span>
                          <span>Payout: <b>{payout}</b></span>
                          <span>Invested: <b>{fmtUsd(investedUsd)}</b></span>
                          <span>At target: <b>{fmtUsd(targetUsd)}</b></span>
                          <span style={{ color: pnlColor }}>
                            P&amp;L now: <b>{fmtUsd(uPnL)} ({uPnLPct >= 0 ? "+" : ""}{uPnLPct.toFixed(2)}%)</b>
                          </span>
                          <span style={{ color: tgtColor }}>
                            If target: <b>{fmtUsd(targetGain)} ({targetGainPct >= 0 ? "+" : ""}{targetGainPct.toFixed(2)}%)</b>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ padding: "11px 12px", borderRadius: 14, border: "1px solid rgba(34,197,94,.24)", background: "rgba(34,197,94,.055)" }}>
                <div style={{ fontWeight: 950, color: "#7cf7a2" }}>Nexus Grid · Token Exit Manager</div>

              </div>

              {(() => {
                const allSessions = Array.isArray(coreVaultSessionPreview?.sessions) ? coreVaultSessionPreview.sessions : [];
                const onchainGridSessions = allSessions
                  .filter((sess) => String(sess?.engine || "").toUpperCase() === "GRID")
                  .filter((sess) => !["FINALIZED", "COMPLETED", "CANCELLED"].includes(String(sess?.statusLabel || "").toUpperCase()));
                const recoveryRunning = onchainGridSessions.some((sess) => {
                  const job = coreVaultRecoveryJobs?.[`GRID:${sess?.sessionId}`];
                  return ["QUEUED", "RUNNING"].includes(String(job?.job_status || "").toUpperCase());
                });
                const localOrderCount = Array.isArray(gridOrders) ? gridOrders.length : 0;
                const onchainCount = onchainGridSessions.length;
                const hasMismatch = onchainCount > 0 && localOrderCount === 0;
                if (!onchainCount && !recoveryRunning) return null;
                return (
                  <div style={{ padding: "10px 11px", borderRadius: 13, border: "1px solid rgba(255,209,102,.45)", background: "rgba(255,209,102,.08)", display: "grid", gap: 7 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ color: "#ffd166", fontWeight: 950 }}>
                        {recoveryRunning ? "Grid recovery running" : hasMismatch ? "On-chain Grid session pending" : "On-chain Grid session active"}
                      </div>
                      <span className="pill silver">{onchainCount} On-chain</span>
                    </div>
                    {onchainGridSessions.map((sess) => {
                      const job = coreVaultRecoveryJobs?.[`GRID:${sess?.sessionId}`];
                      const jobStatus = String(job?.job_status || "").toUpperCase();
                      return (
                        <div key={`grid-onchain-session-${sess.sessionId}`} style={{ padding: "8px 9px", borderRadius: 10, border: "1px solid rgba(255,255,255,.10)", background: "rgba(0,0,0,.14)", display: "grid", gap: 4 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                            <b>Grid session #{sess.sessionId}</b>
                            <b style={{ color: "#ffd166" }}>{jobStatus === "RUNNING" ? "RECOVERY RUNNING" : String(sess.statusLabel || "ACTIVE").toUpperCase()}</b>
                          </div>
                          <div className="muted tiny">
                            Budget {fmtUsd(Number(sess.budget || 0))} · Settlement cash {fmtUsd(Number(sess.settlementCash || 0))} · Open assets {Number(sess.openAssetCount || 0)}
                          </div>
                          {hasMismatch ? (
                            <div className="muted tiny">No local Grid order is visible, but the Core Vault still has an unfinished Grid session. It must remain visible until on-chain finalization.</div>
                          ) : null}
                        </div>
                      );
                    })}
                    <div className="muted tiny">Open System Info to monitor or recover the session. Grid is not considered fully stopped until finalization is confirmed on-chain.</div>
                  </div>
                );
              })()}

              <div className="gridControls" style={{ border: "1px solid rgba(46,204,113,.18)", borderRadius: 14, padding: "12px", background: "rgba(0,0,0,.10)", minWidth: 0, display: "grid", gap: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10, alignItems: "end" }}>
                  <div className="formRow">
                    <label>Network</label>
                    <select value={activeGridChainKey} onChange={(e) => setGridChain(String(e.target.value || "").toUpperCase())}>
                      {gridWalletChains.map((ck) => <option key={ck} value={ck}>{ck}</option>)}
                    </select>
                  </div>
                  <div className="formRow">
                    <label>Coin in Vault</label>
                    <select value={gridItem} onChange={(e) => setGridItem(e.target.value)}>
                      {gridWalletCoins.filter((c) => !["USDC","USDT","DAI","FRAX","FDUSD","TUSD","PYUSD","USDP","LUSD","GUSD"].includes(String(c || "").toUpperCase())).map((c) => (
                        <option key={`${activeGridChainKey}:${c}`} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="formRow">
                    <label>Available</label>
                    <div className="mono" style={{ minHeight: 38, display: "flex", alignItems: "center", padding: "0 10px", borderRadius: 9, border: "1px solid rgba(255,255,255,.10)", background: "rgba(0,0,0,.18)", fontWeight: 900 }}>
                      {manualVaultAvailableQty.toFixed(6)} {String(gridItem || activeGridChainSymbol || "TOKEN").toUpperCase()}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10, alignItems: "end" }}>
                  <div className="formRow">
                    <label>Amount to Use</label>
                    <input value={manualQty} onChange={(e) => setManualQty(e.target.value)} placeholder="e.g. 500" />
                  </div>
                  <div className="formRow">
                    <label>Current Price</label>
                    <div className="mono" style={{ minHeight: 38, display: "flex", alignItems: "center", padding: "0 10px", borderRadius: 9, border: "1px solid rgba(255,255,255,.10)", background: "rgba(0,0,0,.18)", fontWeight: 900 }}>
                      {shownGridPrice ? fmtUsd(Number(shownGridPrice)) : "—"}
                    </div>
                  </div>
                  <div className="formRow">
                    <label>Sell Target</label>
                    <input value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="e.g. 0.45" />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isCompactMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 10, alignItems: "end" }}>
                  <div className="formRow">
                    <label>Maximum Loss (%)</label>
                    <input value={manualMaxLossPct} onChange={(e) => setManualMaxLossPct(e.target.value)} placeholder="e.g. 15" />
                  </div>
                  <div className="formRow">
                    <label>Payout Asset (Vault settlement)</label>
                    <select
                      value={normalizeStablePayoutAsset(manualPayoutAsset, "USDC")}
                      onChange={(e) => setManualPayoutAsset(normalizeStablePayoutAsset(e.target.value, "USDC"))}
                    >
                      <option value="USDC">USDC</option>
                      <option value="USDT">USDT</option>
                    </select>
                  </div>
                  <button className="btn" type="button" onClick={() => addManualOrder()} disabled={!isGridReady || gridBusy.add} style={{ minHeight: 40 }}>
                    {gridBusy.add ? "Starting..." : "Start Grid"}
                  </button>
                </div>


                {errorMsg ? <div style={{ padding: "8px 10px", borderRadius: 9, border: "1px solid rgba(245,193,108,.30)", background: "rgba(245,193,108,.08)", color: "#f5c16c", fontSize: 12 }}>{errorMsg}</div> : null}
              </div>

            </div>
          </div>

                <EngineEventHistory engine="GRID" events={gridEventHistory} />
                </>
              )}

            </div>

          </div></div>
        </section>

        {/* Watchlist */}
        <section className={`card section-watch dashboardPanel ${activePanel === "watchlist" ? "panelActive" : ""}`} onClick={handlePanelActivate("watchlist")}>
          <div className="cardHead">
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
                    <div className="cardTitle">Watchlist</div>
                    <div
                      className="muted tiny"
                      title={"Market Condition based on Overextension (OE) + Relative Volume (RVOL)\nB = Breakout: volume confirms the move\nW = Weak: move is not volume-confirmed\nE = Early: volume builds before strong price extension\nH = Hot: strongly overextended\nN = Normal: no strong anomaly"}
                      style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 10, lineHeight: 1.1, whiteSpace: "nowrap" }}
                    >
                      <span style={{ color: "#16c784", fontWeight: 800 }}>B</span><span>Breakout</span>
                      <span style={{ color: "#ea3943", fontWeight: 800 }}>W</span><span>Weak</span>
                      <span style={{ color: "#f5b300", fontWeight: 800 }}>E</span><span>Early</span>
                      <span style={{ color: "#ff8a3d", fontWeight: 800 }}>H</span><span>Hot</span>
                      <span style={{ color: "var(--muted)", fontWeight: 800 }}>N</span><span>Normal</span>
                    </div>
                  </div>
            <div className="cardActions" style={{ alignItems: "center" }}>
              <button className="btn" onClick={() => setAddOpen(true)}>+ Add</button>
              <button
                type="button"
                className={String(watchSortMode || "manual") === "winner" ? "btn" : "btnGhost"}
                onClick={() => toggleWatchSort("winner")}
                title="Sort watchlist by strongest 24h gain. Click again to return to manual order."
              >
                Winner
              </button>
              <button
                type="button"
                className={String(watchSortMode || "manual") === "loser" ? "btn" : "btnGhost"}
                onClick={() => toggleWatchSort("loser")}
                title="Sort watchlist by strongest 24h loss. Click again to return to manual order."
              >
                Loser
              </button>
              <InfoButton title="Watchlist">
                <Help showClose dismissable
                  de={
                    <>
                      <div style={{ marginBottom: 14, padding: 12, border: "1px solid rgba(34,197,94,.35)", borderRadius: 14, background: "rgba(34,197,94,.06)" }}>
                        <p style={{ marginTop: 0 }}><b>Symbol-Legende / Watchlist Signals</b></p>
                        <p><b>B</b> = Breakout · starke Bewegung / Momentum</p>
                        <p><b>W</b> = Weak · schwaches Asset</p>
                        <p><b>E</b> = Early · frühe mögliche Bewegung</p>
                        <p><b>H</b> = Hot · hohe Aktivität / starkes Volumen</p>
                        <p><b>N</b> = Normal · keine besondere Marktsituation</p>
                        <p><b>NEWS</b> = echte Whale-Aktivität.</p>
                        <p style={{ marginLeft: 10 }}>Wenn <b>NEWS</b> erscheint, war ein echter Whale aktiv. Das kann ein <b>Kauf</b> oder <b>Verkauf</b> sein.</p>
                        <p style={{ marginLeft: 10 }}>Du kannst den <b>NEWS-Text anklicken</b>. Dann öffnet sich das Whale-Fenster mit: <b>Buy/Sell</b>, Betrag in USD, DEX, Zeit, Wallet und falls vorhanden Transaktion.</p>
                        <p style={{ marginLeft: 10 }}>Grün = Whale Buy, Rot = Whale Sell. Keine NEWS bedeutet: keine bestätigte Whale-Aktivität.</p>
                        <p><b>🔥</b> = kein klares Whale-Signal / normale Aktivität.</p>
                        <p><b>Farben:</b> Grün = positiv, Rot = negativ, Gelb = neutral/früh.</p>
                      </div>

                      <p><b>Compare</b> Checkbox steuert die Compare-Auswahl (user-defined).</p>
                      <p><b>Drag & Drop</b> über den Griff links ändert die Reihenfolge. Diese Reihenfolge wird mit deiner Wallet auf dem Server gespeichert.</p>
                      <p><b>Market</b> ist ein Coin über CoinGecko-ID. <b>Token</b> ist ein DEX-Asset und braucht eine Contract-Address.</p>
                    </>
                  }
                  en={
                    <>
                      <div style={{ marginBottom: 14, padding: 12, border: "1px solid rgba(34,197,94,.35)", borderRadius: 14, background: "rgba(34,197,94,.06)" }}>
                        <p style={{ marginTop: 0 }}><b>Symbol Legend / Watchlist Signals</b></p>
                        <p><b>B</b> = Breakout · strong movement / momentum</p>
                        <p><b>W</b> = Weak · weak asset</p>
                        <p><b>E</b> = Early · early potential move</p>
                        <p><b>H</b> = Hot · high activity / strong volume</p>
                        <p><b>N</b> = Normal · no special market condition</p>
                        <p><b>NEWS</b> = real whale activity.</p>
                        <p style={{ marginLeft: 10 }}>When <b>NEWS</b> appears, a real whale was active. It can be a <b>buy</b> or a <b>sell</b>.</p>
                        <p style={{ marginLeft: 10 }}>You can <b>click the NEWS text</b>. This opens the whale window with: <b>Buy/Sell</b>, USD amount, DEX, time, wallet and transaction if available.</p>
                        <p style={{ marginLeft: 10 }}>Green = whale buy, red = whale sell. No NEWS means: no confirmed whale activity.</p>
                        <p><b>🔥</b> = no clear whale signal / normal activity.</p>
                        <p><b>Colors:</b> Green = positive, Red = negative, Yellow = neutral/early.</p>
                      </div>

                      <p><b>Compare</b> checkbox controls the compare set (user-defined).</p>
                      <p><b>Drag & Drop</b> using the handle on the left changes the order. This order is saved on the server for your wallet.</p>
                      <p><b>Market</b> is a coin via CoinGecko ID. <b>Token</b> is a DEX asset and needs a contract address.</p>
                    </>
                  }
                />
              </InfoButton>
            </div>
          </div>

          <div className="panelScroll"><div className="watchTable">
            {!isWatchSidebarCompact ? (
              <>
                <div
                  className="watchHead watchStickyHead"
                  style={{
                    gridTemplateColumns: "34px minmax(74px,.75fr) 68px minmax(120px,1.15fr) minmax(130px,1.25fr) minmax(150px,1.35fr) minmax(84px,.8fr) 150px 32px",
                    gap: 8,
                  }}
                >
                  <div className="center" style={{ textAlign: "center" }}>#</div>
                  <div style={{ paddingLeft: 2 }}>Coin</div>
                  <div className="right">%</div>
                  <div className="right">Price</div>
                  <div className="right">24h Vol</div>
                  <div className="right">Market Cap</div>
                  <div className="center" style={{ textAlign: "center" }}>7D Chart</div>
                  <div className="center" style={{ textAlign: "center" }}>Signals</div>
                  <div className="right"> </div>
                </div>

                <div className="watchScroll">
                  {sortedWatchRows.map((r, idx) => {
                    const sym = String(r.symbol || "").toUpperCase();
                    const checked = compareSymbols.includes(sym);
                    const marketCap = r.marketCap ?? r.market_cap ?? r.mcap ?? r.marketcap ?? null;
                    const onchain = onchainBySymbol?.[sym];
                    const marketCondition = marketConditionBySymbol?.[sym];
                    const mcUi = marketConditionUi(marketCondition?.state);
                    const mcTitle = `${mcUi.code} (${mcUi.code})\n${mcUi.help}\nOE: ${marketCondition?.oe_pct ?? "n/a"}% · RVOL: ${marketCondition?.rvol ?? "n/a"}x${marketCondition?.ai_context?.interpretation ? `\n\n${marketCondition.ai_context.interpretation}` : ""}`;
                    const sysRating = watchFinalRating(r, ratingSummaryBySymbol?.[sym], onchain);
                                        const onchainIcon = String(onchain?.icon || "");
                    const onchainTitle = String(onchain?.summary || onchain?.label || "On-chain signal");
                    const whaleSignal = getWhaleNewsSignal(onchain);
                    return (
                      <div
                        key={`${sym}-${idx}`}
                        className="watchRow"
                        draggable={String(watchSortMode || "manual") === "manual"}
						onMouseEnter={(e) => {
                          if (watchDropKey !== _watchKeyFromRow(r)) {
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
                            e.currentTarget.style.transform = "translateY(-1px)";
                          }
                        }}

                        onMouseLeave={(e) => {
                          if (watchDropKey !== _watchKeyFromRow(r)) {
                            e.currentTarget.style.background =
                              idx % 2 === 1 ? "rgba(255,255,255,0.055)" : "transparent";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "none";
                         }
                       }}  
                        onDragStart={() => { if (String(watchSortMode || "manual") === "manual") handleWatchDragStart(r); }}
                        onDragOver={(e) => { if (String(watchSortMode || "manual") === "manual") handleWatchDragOver(e, r); }}
                        onDrop={(e) => { if (String(watchSortMode || "manual") === "manual") handleWatchDrop(e, r); }}
                        onDragEnd={handleWatchDragEnd}
                        style={{
                          cursor: String(watchSortMode || "manual") === "manual" ? "grab" : "default",
                          border: watchDropKey === _watchKeyFromRow(r) ? "1px dashed var(--line)" : undefined,
                          background: watchDropKey === _watchKeyFromRow(r) ? "rgba(255,255,255,0.04)": (idx % 2 === 1 ? "rgba(255,255,255,0.055)" : "transparent"),
						  transition: "all 0.15s ease",
						  gridTemplateColumns: "34px minmax(74px,.75fr) 68px minmax(120px,1.15fr) minmax(130px,1.25fr) minmax(150px,1.35fr) minmax(84px,.8fr) 150px 32px",
                          gap: 8,
                          alignItems: "center",
                          minHeight: 54,
                        }}
                      >
                        <div
                          className="mono"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            opacity: 0.62,
                            fontSize: 12,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {idx + 1}
                        </div>
                        <div className="watchCoin" style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", minWidth: 0, whiteSpace: "nowrap" }}>
                            <div className="watchSym" style={{ fontSize: 13, lineHeight: 1.1, fontWeight: 800 }}>{sym}</div>
                          </div>
                        </div>
                        <div className={`right mono ${Number(r.change24h) >= 0 ? "txtGood" : "txtBad"}`} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 13, lineHeight: 1.1, color: Number(r.change24h) >= 0 ? "var(--green)" : "var(--red)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtPct(r.change24h)}</div>
                        <div className="right mono" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 13, lineHeight: 1.1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtUsd(r.price)}</div>
                        <div className="right mono" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 12, lineHeight: 1.1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{isCompactWatchNumbers ? fmtCompactUsd(r.volume24h) : fmtUsd(r.volume24h)}</div>
                        <div className="right mono" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 12, lineHeight: 1.1, paddingRight: 2, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{marketCap != null ? (isCompactWatchNumbers ? fmtCompactUsd(marketCap) : fmtUsd(marketCap)) : "—"}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <InlineWatchSpark
                            sym={sym}
                            row={r}
                            idx={idx}
                            seriesMap={compareSeries}
                            colorForSym={colorForSym}
                            lineClassForSym={lineClassForSym}
                          />
                        </div>
                        <div
                          className="watchSignals"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 5,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCompare(sym)}
                            disabled={!checked && compareSymbols.length >= 20}
                            title="Compare"
                            style={{ transform: "scale(0.9)", marginRight: 2 }}
                          />
                          <button
                            type="button"
                            className="pill silver"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRatingModal(r); }}
                            title={`System Rating ${sysRating} · click to rate ${sym}`}
                            style={{ width: 34, minWidth: 34, maxWidth: 34, padding: "2px 0", fontSize: 10, lineHeight: 1.1, cursor: "pointer", textAlign: "center", justifyContent: "center" }}
                          >
                            {sysRating}
                          </button>
                          <button
                            type="button"
                            className="pill silver"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRatingModal(r); }}
                            title={userRatingBySymbol?.[sym] ? `User Rating: ${userRatingBySymbol?.[sym]}` : `Add your rating for ${sym}`}
                            style={{ width: 26, minWidth: 26, maxWidth: 26, padding: "2px 0", fontSize: 9, lineHeight: 1.1, cursor: "pointer", textAlign: "center", justifyContent: "center" }}
                          >
                            {userRatingBySymbol?.[sym] || "-"}
                          </button>
                          {whaleSignal.type !== "neutral" ? (
                            <button
                              type="button"
                              className="pill silver"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const realWhale = onchain?.whale && typeof onchain.whale === "object" ? onchain.whale : onchain;
                                setActiveWhaleNews({
                                  symbol: sym,
                                  type: whaleSignal.type,
                                  title: whaleSignal.title,
                                  summary: realWhale?.summary || onchainTitle,
                                  amountUsd: realWhale?.amountUsd ?? realWhale?.latest?.amountUsd ?? null,
                                  buyUsd: realWhale?.buyUsd ?? null,
                                  sellUsd: realWhale?.sellUsd ?? null,
                                  dex: realWhale?.latest?.dex ?? realWhale?.dex ?? null,
                                  time: realWhale?.latest?.time ?? realWhale?.time ?? null,
                                });
                              }}
                              title={`${whaleSignal.title} · click for details`}
                              style={{
                                width: 36,
                                minWidth: 36,
                                maxWidth: 36,
                                padding: "2px 0",
                                fontSize: 8.5,
                                lineHeight: 1.1,
                                cursor: "pointer",
                                justifyContent: "center",
                                textAlign: "center",
                                fontWeight: 900,
                                color: whaleSignal.color,
                                borderColor: "rgba(255,255,255,.16)",
                                background: "rgba(255,255,255,.035)",
                              }}
                            >
                              NEWS
                            </button>
                          ) : onchainIcon ? (
                            <span
                              className="pill silver"
                              title={onchainTitle}
                              style={{
                                width: 18,
                                minWidth: 18,
                                maxWidth: 18,
                                padding: "2px 0",
                                fontSize: 10,
                                lineHeight: 1.1,
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {onchainIcon || "🔥"}
                            </span>
                          ) : (
                            <span className="pill silver" title="No fresh whale activity" style={{ width: 18, minWidth: 18, maxWidth: 18, padding: "2px 0", fontSize: 10, lineHeight: 1.1, justifyContent: "center", textAlign: "center" }}>
                              🔥
                            </span>
                          )}
                          <span className="pill silver" title={mcTitle} style={{ width: 20, minWidth: 20, maxWidth: 20, padding: "2px 0", justifyContent: "center", textAlign: "center", fontSize: 9, lineHeight: 1.1, fontWeight: 900, color: mcUi.color, borderColor: mcUi.border }}>
                            {mcUi.code}
                          </span>
                        </div>
                        <div className="right" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minWidth: 0 }}>
                          <button
                            className="iconBtn"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 28,
                              height: 28,
                              minWidth: 28,
                              flex: "0 0 28px",
                              fontSize: 12,
                              lineHeight: 1,
                              margin: 0,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const mm = (r.mode || "market");
                              removeWatchItemByKey({
                                symbol: sym,
                                mode: mm,
                                tokenAddress: (mm === "dex" ? (r.contract || "") : ""),
                                contract: (mm === "dex" ? (r.contract || "") : ""),
                              });
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!sortedWatchRows.length ? <div className="muted" style={{ padding: 10 }}>No watchlist data yet.</div> : null}
                </div>
              </>
            ) : (
              <div className="watchCompact">
                {sortedWatchRows.map((r, idx) => {
                  const sym = String(r.symbol || "").toUpperCase();
                  const checked = compareSymbols.includes(sym);
                  const mm = (r.mode || "market");
                  const marketCondition = marketConditionBySymbol?.[sym];
                  const mcUi = marketConditionUi(marketCondition?.state);
                  const mcTitle = `${mcUi.code} (${mcUi.code})\n${mcUi.help}\nOE: ${marketCondition?.oe_pct ?? "n/a"}% · RVOL: ${marketCondition?.rvol ?? "n/a"}x${marketCondition?.ai_context?.interpretation ? `\n\n${marketCondition.ai_context.interpretation}` : ""}`;
                  const sysRating = watchFinalRating(r, ratingSummaryBySymbol?.[sym]);
                  return (
                    <div
                      key={`${sym}-${idx}`}
                      className="watchCompactCard"
                      draggable={String(watchSortMode || "manual") === "manual"}
                      onDragStart={() => { if (String(watchSortMode || "manual") === "manual") handleWatchDragStart(r); }}
                      onDragOver={(e) => { if (String(watchSortMode || "manual") === "manual") handleWatchDragOver(e, r); }}
                      onDrop={(e) => { if (String(watchSortMode || "manual") === "manual") handleWatchDrop(e, r); }}
                      onDragEnd={handleWatchDragEnd}
                      style={{
                        cursor: String(watchSortMode || "manual") === "manual" ? "grab" : "default",
                        border: watchDropKey === _watchKeyFromRow(r) ? "1px dashed var(--line)" : undefined,
                        background: watchDropKey === _watchKeyFromRow(r) ? "rgba(255,255,255,0.04)" : undefined,
                        gridTemplateColumns: "24px minmax(0,1fr) 78px 30px",
                        columnGap: 6,
                        rowGap: 6,
                        paddingRight: 6,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleCompare(sym)} disabled={!checked && compareSymbols.length >= 20} style={{ transform: "scale(0.9)" }} />
                      </div>
                      <div className="watchCompactMain">
                        <div className="watchCompactTop" style={{ gap: 6, minWidth: 0, overflow: "hidden" }}>
                          <div className="watchCompactMeta" style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flexWrap: "wrap", maxWidth: "100%" }}>
                            <div className="watchSym" style={{ fontSize: 13, lineHeight: 1.1, fontWeight: 800, maxWidth: 66, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sym}</div>
                            <button
                              type="button"
                              className="pill silver"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRatingModal(r); }}
                              title={`System Rating ${sysRating} · click to rate ${sym}`}
                              style={{ padding: "2px 6px", fontSize: 10, lineHeight: 1.1, cursor: "pointer" }}
                            >
                              {sysRating}
                            </button>
                            <button
                              type="button"
                              className="pill silver"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRatingModal(r); }}
                              title={userRatingBySymbol?.[sym] ? `User Rating: ${userRatingBySymbol?.[sym]}` : `Add your rating for ${sym}`}
                              style={{ padding: "2px 5px", fontSize: 10, lineHeight: 1.1, cursor: "pointer" }}
                            >
                              {userRatingBySymbol?.[sym] || "-"}
                            </button>
                            <span
                              className="pill silver"
                              title={mcTitle}
                              style={{ padding: "2px 5px", minWidth: 20, justifyContent: "center", textAlign: "center", fontSize: 10, lineHeight: 1.1, fontWeight: 900, color: mcUi.color, borderColor: mcUi.border }}
                            >
                              {mcUi.code}
                            </span>
                            <span className={`mono tiny ${Number(r.change24h) >= 0 ? "txtGood" : "txtBad"}`} style={{ fontSize: 11, lineHeight: 1.1, color: Number(r.change24h) >= 0 ? "var(--green)" : "var(--red)", marginLeft: "auto", whiteSpace: "nowrap" }}>{fmtPct(r.change24h)}</span>
                          </div>
                        </div>
                        <div className="watchCompactStats" style={{ display: "grid", gap: 2, minWidth: 0, marginTop: 4 }}>
                          <span className="muted tiny" style={{ fontSize: 10.5, lineHeight: 1.12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Vol {fmtCompactUsd(r.volume24h)}</span>
                          <span className="muted tiny" style={{ fontSize: 10.5, lineHeight: 1.12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>MCap {((r.marketCap ?? r.market_cap ?? r.mcap ?? r.marketcap) != null) ? fmtCompactUsd(r.marketCap ?? r.market_cap ?? r.mcap ?? r.marketcap) : "—"}</span>
                        </div>
                      </div>
                      <div className="watchCompactPrice" style={{ display: "grid", gap: 2, alignItems: "center", justifyItems: "end", minWidth: 0, overflow: "hidden" }}>
                        <div className="mono" title={fmtUsd(r.price)} style={{ fontWeight: 900, fontSize: 11.5, lineHeight: 1.1, maxWidth: "78px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "right" }}>{fmtUsd(r.price)}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 30 }}>
                        <button
                          className="iconBtn"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            flex: "0 0 28px",
                            fontSize: 12,
                            lineHeight: 1,
                            margin: 0,
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeWatchItemByKey({
                              symbol: sym,
                              mode: mm,
                              tokenAddress: (mm === "dex" ? (r.contract || "") : ""),
                              contract: (mm === "dex" ? (r.contract || "") : ""),
                            });
                          }}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!sortedWatchRows.length ? <div className="muted" style={{ padding: 10 }}>No watchlist data yet.</div> : null}
              </div>
            )}
          </div>

          <div className="muted tiny"></div>
        </div></section>


        {activeWhaleNews && (
          <div className="modalBackdrop" onClick={() => setActiveWhaleNews(null)}>
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 380,
                width: "calc(100vw - 28px)",
                background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
              }}
            >
              <div className="modalHead">
                <div>
                  <div className="cardTitle">Whale Activity · {activeWhaleNews.symbol}</div>
                  <div
                    className="muted tiny"
                    style={{ color: activeWhaleNews.type === "buy" ? "#22c55e" : "#ef4444", fontWeight: 900 }}
                  >
                    {activeWhaleNews.type === "buy" ? "Whale BOUGHT recently" : "Whale SOLD recently"}
                  </div>
                </div>
                <button className="iconBtn" type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveWhaleNews(null); }}>×</button>
              </div>

              <div className="softBox" style={{ padding: 12, marginTop: 12 }}>
                <div style={{ display: "grid", gap: 7, fontSize: 12, lineHeight: 1.45 }}>
                  <div>
                    <b>Amount:</b>{" "}
                    {Number(activeWhaleNews.amountUsd || 0) > 0
                      ? `$${Number(activeWhaleNews.amountUsd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : activeWhaleNews.type === "buy" && Number(activeWhaleNews.buyUsd || 0) > 0
                        ? `$${Number(activeWhaleNews.buyUsd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : activeWhaleNews.type === "sell" && Number(activeWhaleNews.sellUsd || 0) > 0
                          ? `$${Number(activeWhaleNews.sellUsd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                          : "pending"}
                  </div>
                  <div>
                    <b>DEX:</b> {activeWhaleNews.dex || "pending"}
                  </div>
                  <div>
                    <b>Time:</b> {formatWhaleTimeAgo(activeWhaleNews.time)}
                  </div>
                  {activeWhaleNews.summary && (
                    <div className="muted tiny" style={{ marginTop: 4 }}>
                      {activeWhaleNews.summary}
                    </div>
                  )}
                </div>
              </div>

              <button type="button" className="btn" style={{ width: "100%", marginTop: 12 }} onClick={() => setActiveWhaleNews(null)}>
                Close
              </button>
            </div>
          </div>
        )}

        {ratingModal.open && (
          <div className="modalBackdrop" onClick={closeRatingModal}>
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 440,
                width: "calc(100vw - 28px)",
                background: "linear-gradient(180deg, rgba(10,32,28,1), rgba(7,24,22,1))",
              }}
            >
              <div className="modalHead">
                <div>
                  <div className="cardTitle">Coin Rating · {ratingModal.symbol}</div>
                  <div className="muted tiny">
                    System Rating: <b>{ratingModal.systemRating}</b> · Score {ratingModal.systemScore}
                    <br />
                    User Rating: <b>{ratingStatus?.user_rating_today || ratingStatus?.last_user_rating || "-"}</b>
                  </div>
                </div>
                <button className="iconBtn" type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeRatingModal(); }}>×</button>
              </div>

              <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                <div className="softBox" style={{ padding: 12 }}>
                  <div className="muted tiny" style={{ marginBottom: 8 }}>
                    Your personal User Rating (not mixed with System Rating)
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {["AAA", "AA", "A", "BBB", "BB", "B", "CCC", "CC", "C", "RISK"].map((rt) => {
                      const chosen = String(ratingStatus?.user_rating_today || ratingStatus?.last_user_rating || "").toUpperCase() === rt;
                      return (
                        <button
                          key={rt}
                          type="button"
                          className={chosen ? "btn" : "btnGhost"}
                          disabled={ratingBusy}
                          onClick={() => submitUserRating(rt)}
                          title={`Set your rating for ${ratingModal.symbol} to ${rt}`}
                          style={{ minWidth: 62, justifyContent: "center" }}
                        >
                          {rt}
                        </button>
                      );
                    })}
                  </div>
                  <div className="muted tiny" style={{ marginTop: 8 }}>
                    {(ratingStatus?.user_rating_today || ratingStatus?.last_user_rating)
                      ? `Your saved rating: ${ratingStatus?.user_rating_today || ratingStatus?.last_user_rating}`
                      : "No personal rating saved yet."}
                  </div>
                </div>

                {ratingErr && <div style={{ color: "#ffb3b3", fontSize: 12 }}>{ratingErr}</div>}

                {ratingStatus?.coin_info ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 6,
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 12,
                      padding: "8px 10px",
                      background: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div className="muted tiny">Coin</div>
                        <div style={{ fontWeight: 900 }}>
                          {ratingStatus?.coin_info?.name || ratingModal.symbol}
                          {ratingStatus?.coin_info?.coin_id ? (
                            <span className="muted tiny" style={{ marginLeft: 8 }}>
                              {ratingStatus.coin_info.coin_id}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className={ratingStatus?.link_enabled ? "btn" : "btnGhost"}
                          disabled={!ratingStatus?.link_enabled}
                          onClick={openRatingLink}
                          title={ratingStatus?.link_enabled ? "Open official coin page or CoinGecko fallback" : "No coin info link available yet"}
                        >
                          Coin Info
                        </button>
                        <button
                          type="button"
                          className={ratingStatus?.coin_info?.explorer ? "btn" : "btnGhost"}
                          disabled={!ratingStatus?.coin_info?.explorer}
                          onClick={openRatingExplorer}
                          title={ratingStatus?.coin_info?.explorer ? "Open blockchain explorer" : "No explorer link available yet"}
                        >
                          Explorer
                        </button>
                      </div>
                    </div>

                    <div className="muted tiny" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {ratingStatus?.coin_info?.homepage ? <span>Website ✓</span> : <span>Website fallback</span>}
                      {ratingStatus?.coin_info?.explorer ? <span>Explorer ✓</span> : <span>Explorer —</span>}
                      {ratingStatus?.coin_info?.source ? <span>Source: {ratingStatus.coin_info.source}</span> : null}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <div className="muted tiny">
                      Community votes: {ratingStatus?.summary?.count ?? 0}
                    </div>
                    <button
                      type="button"
                      className={ratingStatus?.link_enabled ? "btn" : "btnGhost"}
                      disabled={!ratingStatus?.link_enabled}
                      onClick={openRatingLink}
                      title={ratingStatus?.link_enabled ? "Open coin info page" : "No coin info link available yet"}
                    >
                      Coin Info
                    </button>
                  </div>
                )}

                <div className="muted tiny">
                  Community votes: {ratingStatus?.summary?.count ?? 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI */}
        <section className={`card section-ai dashboardPanel ${activePanel === "ai" ? "panelActive" : ""}`} onClick={handlePanelActivate("ai")}>
          <div className="cardHead">
            <div className="cardTitle">Nexus Strategist</div>
            <div className="cardActions" style={{ alignItems: "center" }}>
              <InfoButton title="Nexus Strategist">
                <Help showClose dismissable
                  de={<><p><b>Nexus Strategist</b> ist dein aktiver Strategie-Arbeitsbereich in Nexus Analyt. Er arbeitet nicht mehr ueber sichtbare Coin-Chips, sondern ueber deine Eingabe.</p><p><b>Unterschied zu AI Insight:</b> AI Insight erklaert kompakt den aktuellen Markt. Nexus Strategist hilft dir aktiv bei Recherche, Strategie-Ideen, Backtests, Pine Script, Tagesberichten, Trade-Review und der Einordnung zwischen Nexus Grid und NKR.</p><p><b>Research:</b> untersucht Marktfragen, Rotation, relative Staerke, Volumen, Watchlist-Themen und auffaellige Bedingungen.</p><p><b>Strategy Builder:</b> verwandelt deine Idee in klare Regeln, Filter, Entry-/Exit-Logik, Risiko-Logik und Alerts.</p><p><b>Backtest Review:</b> bewertet Backtest-Ergebnisse, Drawdown, Trefferquote, Expectancy, Overfitting-Risiko und schwache Marktphasen.</p><p><b>Pine Builder:</b> hilft bei TradingView/Pine Script: Indikatoren, Strategien, Alerts, Debugging und Verbesserungen.</p><p><b>Daily Report:</b> erstellt einen kompakten Bericht aus deiner Aufgabe und dem verfuegbaren App-Kontext.</p><p><b>Trade Review:</b> analysiert Ausfuehrung, Verhalten, Order-Struktur, wiederkehrende Fehler und Trading-Gewohnheiten.</p><p><b>Eingabe:</b> Beschreibe immer kurz, was der Analyst tun soll. Du kannst Coin-Namen, Strategie-Ideen, Backtest-Daten oder Pine Script direkt einfuegen.</p><p><b>Hinweis:</b> Nexus Strategist liefert Analyse, Struktur und taktische Orientierung. Er ist keine Finanzberatung und keine direkte Kauf-/Verkaufsempfehlung.</p></>}
                  en={<><p><b>Nexus Strategist</b> is your active strategy workspace inside Nexus Analyt. It no longer works through visible coin chips; it works through your task input.</p><p><b>Difference from AI Insight:</b> AI Insight gives a compact market interpretation. Nexus Strategist helps actively with research, strategy ideas, backtest review, Pine Script, reports, trade review, and choosing between Nexus Grid and NKR.</p><p><b>Research:</b> investigates market questions, rotation, relative strength, volume, watchlist themes, and unusual conditions.</p><p><b>Strategy Builder:</b> turns your idea into clear rules, filters, entry/exit logic, risk logic, and alerts.</p><p><b>Backtest Review:</b> evaluates backtest results, drawdown, win rate, expectancy, overfitting risk, and weak market regimes.</p><p><b>Pine Builder:</b> helps with TradingView/Pine Script: indicators, strategies, alerts, debugging, and improvements.</p><p><b>Daily Report:</b> creates a compact report from your task and available app context.</p><p><b>Trade Review:</b> analyzes execution, behavior, order structure, repeated mistakes, and trading habits.</p><p><b>Input:</b> Always describe what the analyst should do. You can paste coin names, strategy ideas, backtest data, or Pine Script directly.</p><p><b>Note:</b> Nexus Strategist provides analysis, structure, and tactical orientation. It is not financial advice or a direct buy/sell recommendation.</p></>}
                />
              </InfoButton>
            </div>
          </div>

          <div className="panelScroll"><div className="aiWrap" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
            {!isPro ? (
              <div className="hint" style={{ color: "rgba(255,255,255,0.75)" }}>
                Nexus Strategist is a separate add-on: <b>$20/7 days</b> or <b>$50/30 days</b>. Demo users can try limited AI usage; Core users need Strategist access for full Strategist mode.
              </div>
            ) : null}

            {/* Top context strip: Pulse · Crypto Moves · CoinGecko Trends · Market Brief */}
            {(() => {
              // Pulse must NOT mirror the header banner. Use complementary stats:
              // BTC/ETH dominance, market health score, watchlist breadth, ETH−BTC spread.
              const ctx = shadowMarketContext && typeof shadowMarketContext === "object" ? shadowMarketContext : {};
              const health = Number(ctx.market_score ?? ctx.marketScore ?? ctx.market_risk_score);
              const btcDom = Number(ctx.btc_dominance ?? ctx.btcDominance);
              const ethDom = Number(ctx.eth_dominance ?? ctx.ethDominance);
              const avgVol = Number(ctx.avg_abs_volatility ?? ctx.avgAbsVolatility);
              let watchUp = 0;
              let watchDown = 0;
              const watchChs = [];
              (Array.isArray(watchRows) ? watchRows : []).forEach((row) => {
                const ch = Number(row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? row?.change_24h);
                if (!Number.isFinite(ch)) return;
                watchChs.push(ch);
                if (ch > 0) watchUp += 1;
                else if (ch < 0) watchDown += 1;
              });
              const btcRow = (Array.isArray(watchRows) ? watchRows : []).find((r) => String(r?.symbol || "").toUpperCase() === "BTC");
              const ethRow = (Array.isArray(watchRows) ? watchRows : []).find((r) => String(r?.symbol || "").toUpperCase() === "ETH");
              const btcCh = Number(btcRow?.change24h ?? btcRow?.chg_24h ?? btcRow?.usd_24h_change);
              const ethCh = Number(ethRow?.change24h ?? ethRow?.chg_24h ?? ethRow?.usd_24h_change);
              const ethBtcSpread = (Number.isFinite(ethCh) && Number.isFinite(btcCh)) ? (ethCh - btcCh) : NaN;

              const pulseTone = Number.isFinite(health)
                ? (health >= 62 ? "positive" : health <= 42 ? "negative" : "warning")
                : (watchUp > watchDown ? "positive" : watchDown > watchUp ? "negative" : "neutral");
              const pulseBorder = pulseTone === "positive" || pulseTone === "bull" || pulseTone === "green"
                ? "rgba(34,197,94,.45)"
                : pulseTone === "negative" || pulseTone === "bear" || pulseTone === "red"
                  ? "rgba(239,68,68,.45)"
                  : "rgba(255,209,102,.40)";
              const pulseColor = pulseTone === "positive" ? "#86efac" : pulseTone === "negative" ? "#fca5a5" : "#ffd166";

              const pulseValue = Number.isFinite(health)
                ? `Health ${Math.round(health)}/100`
                : (watchUp + watchDown > 0 ? `Watch ${watchUp}↑ / ${watchDown}↓` : "Market health");
              const pulseLine2 = [
                Number.isFinite(btcDom) ? `BTC.D ${btcDom.toFixed(1)}%` : null,
                Number.isFinite(ethDom) ? `ETH.D ${ethDom.toFixed(1)}%` : null,
                Number.isFinite(ethBtcSpread) ? `ETH−BTC ${ethBtcSpread >= 0 ? "+" : ""}${ethBtcSpread.toFixed(1)}%` : null,
              ].filter(Boolean).join(" · ") || (Number.isFinite(avgVol) ? `Avg move ${avgVol.toFixed(1)}%` : "Dominance & rotation");
              const pulseDetail = (watchUp + watchDown > 0)
                ? `Watchlist ${watchUp} green / ${watchDown} red${Number.isFinite(avgVol) ? ` · vol ${avgVol.toFixed(1)}%` : ""}`
                : "BTC/ETH dominance and watchlist breadth for Strategist context.";

              // Mini chart: watchlist 24h changes (unique vs banner global curves)
              const pulseChartItem = {
                tone: pulseTone === "positive" ? "positive" : pulseTone === "negative" ? "negative" : "warning",
                chartType: "bars",
                chartData: watchChs.length
                  ? watchChs.slice(0, 12).map((v) => Math.abs(v))
                  : (Number.isFinite(health) ? [Math.max(5, health - 20), health, Math.min(100, health + 10)] : []),
              };

              // Crypto moves: rotate through watchlist movers + whale signals (never sticky same 3)
              const whaleBits = [];
              const moverBits = [];
              (Array.isArray(watchRows) ? watchRows : []).forEach((row) => {
                const sym = String(row?.symbol || row?.sym || "").toUpperCase();
                if (!sym) return;
                const oc = onchainBySymbol?.[sym] || row?.onchain || null;
                const whale = getWhaleNewsSignal(oc);
                if (whale?.type === "buy" || whale?.type === "sell") {
                  whaleBits.push({
                    sym,
                    type: whale.type,
                    text: whale.type === "buy" ? `${sym} whale buy` : `${sym} whale sell`,
                    color: whale.color || (whale.type === "buy" ? "#22c55e" : "#ef4444"),
                  });
                }
                const ch = Number(row?.change24h ?? row?.chg_24h ?? row?.usd_24h_change ?? row?.change_24h);
                if (Number.isFinite(ch)) moverBits.push({ sym, ch });
              });
              const gainers = [...moverBits].filter((m) => m.ch > 0).sort((a, b) => b.ch - a.ch);
              const losers = [...moverBits].filter((m) => m.ch < 0).sort((a, b) => a.ch - b.ch);
              // Interleave: top gainer, top loser, next gainer, … then remaining by |change|
              const interleaved = [];
              const gq = [...gainers];
              const lq = [...losers];
              while (gq.length || lq.length) {
                if (gq.length) interleaved.push(gq.shift());
                if (lq.length) interleaved.push(lq.shift());
              }
              const pool = [
                ...whaleBits.map((w) => ({ text: w.text, color: w.color, key: `w-${w.sym}` })),
                ...interleaved.map((m) => ({
                  text: `${m.sym} ${m.ch >= 0 ? "+" : ""}${m.ch.toFixed(1)}% 24h`,
                  color: m.ch >= 0 ? "#22c55e" : "#ef4444",
                  key: `m-${m.sym}`,
                })),
              ];
              // Rotate every ~10s so the card is not stuck on the same three names
              const rot = pool.length ? (Number(strategistMovesTick) || 0) % pool.length : 0;
              const newsLines = pool.length
                ? [0, 1, 2].map((i) => pool[(rot + i) % pool.length]).filter(Boolean)
                : [];
              void strategistMovesTick; // ensure tick is read for re-render

              // CoinGecko Trends: Basic-plan endpoint /search/trending, normalized by Nexus backend.
              const trendRows = Array.isArray(coingeckoTrending) ? coingeckoTrending : [];
              const trendTop = trendRows.slice(0, 3); // display-only: CoinGecko Basic returns the full default trending set upstream
              const trendChanges = trendRows
                .map((row) => Number(row?.change24h))
                .filter((v) => Number.isFinite(v));
              const trendAvg = trendChanges.length ? trendChanges.reduce((a, b) => a + b, 0) / trendChanges.length : NaN;
              const trendPositive = trendChanges.filter((v) => v > 0).length;
              const trendNegative = trendChanges.filter((v) => v < 0).length;
              const trendMaxAbs = trendChanges.length ? Math.max(...trendChanges.map((v) => Math.abs(v))) : NaN;

              // Market Brief combines trending attention with the already-live Pulse/watchlist context.
              const breadthTotal = watchUp + watchDown;
              const breadthPct = breadthTotal ? (watchUp / breadthTotal) * 100 : NaN;
              const briefSentiment = Number.isFinite(health)
                ? (health >= 62 ? "Positive" : health <= 42 ? "Cautious" : "Neutral")
                : (Number.isFinite(breadthPct) ? (breadthPct >= 60 ? "Positive" : breadthPct <= 40 ? "Cautious" : "Neutral") : "Neutral");
              const briefTheme = trendTop.length
                ? `Attention: ${trendTop.slice(0, 2).map((r) => String(r?.symbol || r?.name || "").toUpperCase()).filter(Boolean).join(" · ")}`
                : "Attention: broad market";
              const briefRisk = Number.isFinite(trendMaxAbs) && trendMaxAbs >= 12
                ? "High trend volatility"
                : (trendPositive && trendNegative ? "Mixed momentum" : (trendNegative > trendPositive ? "Downside pressure" : "Controlled"));
              const briefImpact = Number.isFinite(trendAvg)
                ? (trendAvg >= 3 ? "Trending momentum positive" : trendAvg <= -3 ? "Trending momentum weak" : "Selective / mixed")
                : (Number.isFinite(breadthPct) ? (breadthPct >= 60 ? "Broad participation" : breadthPct <= 40 ? "Narrow / defensive" : "Selective / mixed") : "Monitoring");

              const cardBase = {
                borderRadius: 12,
                padding: "8px 10px",
                minHeight: 88,
                display: "grid",
                gap: 4,
                overflow: "hidden",
              };

              return (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isCompactMobile ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))",
                  gap: 8,
                }}>
                  {/* PULSE — complementary to header banner (dominance / health / watch breadth) */}
                  <div style={{
                    ...cardBase,
                    border: `1px solid ${pulseBorder}`,
                    background: pulseTone === "positive"
                      ? "linear-gradient(180deg, rgba(34,197,94,.12), rgba(0,0,0,.22))"
                      : pulseTone === "negative"
                        ? "linear-gradient(180deg, rgba(239,68,68,.12), rgba(0,0,0,.22))"
                        : "linear-gradient(180deg, rgba(255,209,102,.10), rgba(0,0,0,.22))",
                  }}>
                    <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: pulseColor }}>Pulse</div>
                    <div style={{ fontWeight: 850, fontSize: 13, color: "#dfffee", lineHeight: 1.25 }}>{pulseValue}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: pulseColor, lineHeight: 1.3 }}>{pulseLine2}</div>
                    <div style={{ height: 28, marginTop: 2, opacity: 0.9 }}>
                      {typeof renderMarketDeskChart === "function" && pulseChartItem.chartData?.length ? (
                        <div style={{ transform: "scale(0.48)", transformOrigin: "left top", width: 210, height: 76, marginBottom: -48 }}>
                          {renderMarketDeskChart(pulseChartItem)}
                        </div>
                      ) : null}
                    </div>
                    {pulseDetail ? <div className="muted tiny" style={{ lineHeight: 1.2 }}>{pulseDetail}</div> : null}
                  </div>

                  {/* NEWS / MOVES — crypto activity instead of plain watchlist count */}
                  <div style={{
                    ...cardBase,
                    border: "1px solid rgba(255,209,102,.28)",
                    background: "linear-gradient(180deg, rgba(255,209,102,.08), rgba(0,0,0,.22))",
                  }}>
                    <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#ffd166" }}>Crypto Moves</div>
                    {newsLines.length ? (
                      newsLines.map((line, i) => (
                        <div key={`${line.text}-${i}`} style={{ fontSize: 12, fontWeight: 700, color: line.color, lineHeight: 1.3 }}>
                          {line.text}
                        </div>
                      ))
                    ) : (
                      <>
                        <div style={{ fontWeight: 850, fontSize: 12, color: "#dfffee" }}>No fresh whale NEWS</div>
                        <div className="muted tiny" style={{ lineHeight: 1.25 }}>Watchlist movers appear here when 24h moves or whale signals are live.</div>
                      </>
                    )}
                  </div>

                  {/* COINGECKO TRENDS — Basic-plan compatible /search/trending data */}
                  <div style={{
                    ...cardBase,
                    border: "1px solid rgba(139,220,255,.32)",
                    background: "linear-gradient(180deg, rgba(64,196,255,.09), rgba(0,0,0,.22))",
                  }}>
                    <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#8bdcff" }}>CoinGecko Trends</div>
                    {trendTop.length ? (
                      trendTop.map((row, i) => {
                        const sym = String(row?.symbol || "").toUpperCase();
                        const name = String(row?.name || sym || "Trending");
                        const ch = Number(row?.change24h);
                        const tone = Number.isFinite(ch) ? (ch >= 0 ? "#86efac" : "#fca5a5") : "#dfffee";
                        return (
                          <div key={`${row?.id || sym || name}-${i}`} style={{ fontSize: 11, lineHeight: 1.3, color: "#dfffee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
                            <b style={{ color: "#8bdcff" }}>#{i + 1} {sym || name}</b>
                            {Number.isFinite(ch) ? <span style={{ color: tone }}> {ch >= 0 ? "+" : ""}{ch.toFixed(1)}%</span> : null}
                            {Number.isFinite(Number(row?.marketCapRank)) && Number(row.marketCapRank) > 0 ? <span className="muted"> · Rank {Number(row.marketCapRank)}</span> : null}
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div style={{ fontWeight: 850, fontSize: 12, color: "#dfffee" }}>{coingeckoTrendingLoaded ? "No trending data" : "Loading trends…"}</div>
                        <div className="muted tiny">CoinGecko 24h search trends.</div>
                      </>
                    )}
                  </div>

                  {/* MARKET BRIEF — compact Nexus interpretation of live market + trend data */}
                  <div style={{
                    ...cardBase,
                    border: "1px solid rgba(187,134,252,.30)",
                    background: "linear-gradient(180deg, rgba(187,134,252,.08), rgba(0,0,0,.22))",
                  }}>
                    <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#c9a7ff" }}>Market Brief</div>
                    <div style={{ fontSize: 11, lineHeight: 1.3, color: "#dfffee" }}>Sentiment: <b>{briefSentiment}</b></div>
                    <div style={{ fontSize: 11, lineHeight: 1.3, color: "#dfffee", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{briefTheme}</div>
                    <div style={{ fontSize: 11, lineHeight: 1.3, color: "#dfffee" }}>Risk: <b>{briefRisk}</b></div>
                    <div className="muted tiny" style={{ lineHeight: 1.2 }}>Impact: {briefImpact}</div>
                  </div>
                </div>
              );
            })()}

            {/* Quick prompts — only when chat is empty */}
            {!(Array.isArray(aiHistory) && aiHistory.length) && !aiOutput ? (
              <div style={{ display: "grid", gap: 6 }}>
                <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase" }}>Quick start</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    "How is the crypto market today?",
                    "Which watchlist coins look strongest?",
                    "Any rotation edge right now?",
                    "What is the main risk this week?",
                    "Is NKR or Grid better in this market?",
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="chip"
                      onClick={() => {
                        setAiQuestion(prompt);
                      }}
                      title="Fill chat input"
                      style={{ fontSize: 12 }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="aiOut" style={{ display: "grid", gap: 8 }}>
              <div className="label" style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span>Strategist Chat</span>
                {(Array.isArray(aiHistory) && aiHistory.length > 0) ? (
                  <span className="muted tiny">{aiHistory.filter((m) => m?.role === "user").length} turn{aiHistory.filter((m) => m?.role === "user").length === 1 ? "" : "s"} · crypto / Nexus only</span>
                ) : (
                  <span className="muted tiny">crypto · market · Nexus only</span>
                )}
              </div>
              <div className="aiPanel" style={{ minHeight: 200, maxHeight: "min(52vh, 520px)", overflowY: "auto", overflowX: "hidden", display: "grid", gap: 10, alignContent: "start", WebkitOverflowScrolling: "touch" }}>
                {(Array.isArray(aiHistory) && aiHistory.length > 0) || aiOutput ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {(Array.isArray(aiHistory) && aiHistory.length > 0 ? aiHistory : (
                      aiOutput ? [{ role: "assistant", content: aiOutput }] : []
                    )).map((msg, mi) => {
                      const role = String(msg?.role || "").toLowerCase();
                      const content = String(msg?.content || "");
                      if (!content) return null;
                      if (role === "user") {
                        return (
                          <div
                            key={`hist-user-${mi}`}
                            style={{
                              border: "1px solid rgba(139,220,255,.22)",
                              background: "rgba(139,220,255,.08)",
                              borderRadius: 12,
                              padding: "8px 10px",
                              justifySelf: "end",
                              maxWidth: "92%",
                            }}
                          >
                            <div className="muted tiny" style={{ fontWeight: 800, marginBottom: 4, letterSpacing: ".06em", textTransform: "uppercase" }}>You</div>
                            <div className="aiText" style={{ whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{content}</div>
                          </div>
                        );
                      }
                      const isLatestAssistant = mi === (aiHistory?.length ? aiHistory.length - 1 : 0) && role === "assistant";
                      const sections = isLatestAssistant && aiOutputSections?.length
                        ? aiOutputSections
                        : parseAiAnalystOutput(content);
                      return (
                        <div key={`hist-asst-${mi}`} style={{ display: "grid", gap: 8, maxWidth: "96%" }}>
                          <div className="muted tiny" style={{ fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase" }}>
                            Nexus Strategist{isLatestAssistant ? " · latest" : ""}
                          </div>
                          {(sections && sections.length ? sections : [{ key: "output", body: content }]).map((section, idx) => {
                            const meta = aiAnalystSectionMeta[section.key] || aiAnalystSectionMeta.output || { title: section.key || "Output", sub: "" };
                            return (
                              <div
                                key={`${section.key}-${mi}-${idx}`}
                                style={{
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  background: isLatestAssistant
                                    ? "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.025))"
                                    : "rgba(0,0,0,.18)",
                                  borderRadius: 14,
                                  padding: "8px 10px",
                                  opacity: isLatestAssistant ? 1 : 0.88,
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                  <div style={{ fontWeight: 900, fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "#dfffee" }}>
                                    {meta.title}
                                  </div>
                                  <div className="muted tiny" style={{ whiteSpace: "nowrap" }}>{meta.sub}</div>
                                </div>
                                <div className="aiText" style={{ whiteSpace: "pre-wrap", lineHeight: 1.28 }}>
                                  {section.body}
                                </div>
                                {isLatestAssistant && nexusStrategistCanShowAction(section, detectNexusUserIntent(aiQuestion || content || "")) ? (
                                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                    <button
                                      className="btn"
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (section.key === "nexus_grid") applyStrategistToGrid(section.body);
                                        if (section.key === "nexus_rotation" || section.key === "exchange_spread") applyStrategistToRotation(section.body);
                                        if (section.key === "nexus_trading") applyStrategistToTrading(section.body);
                                      }}
                                      style={{ height: 28, paddingInline: 10, fontSize: 12 }}
                                    >
                                      {aiOutputLanguage === "de"
                                        ? (section.key === "nexus_grid" ? "In Grid nutzen" : section.key === "nexus_trading" ? "In Trading nutzen" : "In NKR nutzen")
                                        : (section.key === "nexus_grid" ? "Use in Grid" : section.key === "nexus_trading" ? "Use in Trading" : "Use in Nexus NKR")}
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    {aiLoading ? (
                      <div className="muted tiny" style={{ padding: "6px 2px" }}>Strategist is thinking…</div>
                    ) : null}
                  </div>
                ) : (
                  <div className="muted" style={{ padding: "10px 4px", lineHeight: 1.45 }}>
                    Chat about markets, coins, rotation, risk, NKR, Grid or Trading. Use a quick-start chip or type below.
                  </div>
                )}
              </div>

              {/* Mode chips sit directly above the chat input */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", justifyContent: "space-between" }}>
                <div className="aiChips" style={{ gap: 6 }}>
                  {[
                    ["research", "Research"],
                    ["strategy_builder", "Strategy Builder"],
                    ["backtest_review", "Backtest Review"],
                    ["pine_tradingview", "Pine Builder"],
                    ["daily_report", "Daily Report"],
                    ["diagnostics", "Trade Review"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`chip ${aiKind === value ? "active" : ""}`}
                      onClick={() => setAiKind(value)}
                      title={`Mode: ${label}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  className="btnGhost"
                  type="button"
                  onClick={() => {
                    setAiOutput("");
                    setAiQuestion("");
                    setAiHistory([]);
                    setErrorMsg("");
                  }}
                >
                  Clear chat
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end" }}>
                <textarea
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent?.isComposing) return;
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!aiLoading) void runAi();
                    }
                  }}
                  placeholder="Ask about markets, coins, NKR, Grid, risk… (Enter to send)"
                  rows={2}
                  style={{
                    width: "100%",
                    minHeight: 56,
                    maxHeight: 120,
                    resize: "vertical",
                    borderRadius: 14,
                    padding: "10px 12px",
                    background: "rgba(0,0,0,0.28)",
                    color: "inherit",
                    border: "1px solid rgba(255,255,255,0.14)",
                    outline: "none",
                  }}
                  disabled={aiLoading}
                />
                <button className="btn" type="button" onClick={() => { if (!aiLoading) void runAi(); }} disabled={aiLoading || !(aiQuestion || "").trim()} style={{ height: 44, minWidth: 72 }}>
                  {aiLoading ? "…" : "Send"}
                </button>
              </div>
            </div>
          </div></div>
        </section>
        </div>
      </main>

      {null}

      {/* Add modal */}

      {/* Wallet panel is rendered in the header (top-right dropdown). */}

            {addOpen && (
  <div className="modalBackdrop" onClick={resetAddModal}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <div className="modalHead">
        <div className="cardTitle">{addTab === "dex" ? "Add token (Contract)" : "Select token (CoinGecko)"}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <InfoButton title="Select token (CoinGecko)">
            <Help
              de={
                <>
                  <p>
                    Suche nach <b>Symbol</b> oder <b>Name</b> (z.B. <code>TON</code>, <code>BNB</code>). Bei gleichen Symbolen bitte den richtigen{" "}
                    <b>Namen/Rank</b> auswählen.
                  </p>
                  <p>
                    <b>Market (CEX)</b> nutzt CoinGecko-IDs und ist für normale Markt-Coins gedacht. <b>DEX (Contract)</b> fügt stattdessen einen Token per Contract-Adresse hinzu.
                  </p>
                  <p>
                    Wähle immer den exakten Eintrag, damit Preise, Updates und Watchlist-Daten korrekt bleiben.
                  </p>
                </>
              }
              en={
                <>
                  <p>
                    Search by <b>symbol</b> or <b>name</b> (e.g. <code>TON</code>, <code>BNB</code>). If there are multiple matches, pick the right{" "}
                    <b>name/rank</b>.
                  </p>
                  <p>
                    <b>Market (CEX)</b> uses CoinGecko IDs and is meant for normal market coins. <b>DEX (Contract)</b> instead adds a token by contract address.
                  </p>
                  <p>
                    Always choose the exact entry so prices, updates, and watchlist data stay correct.
                  </p>
                </>
              }
            />
          </InfoButton>
          <button className="iconBtn" onClick={resetAddModal} aria-label="Close">
            ×
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
	          className="btn"
	          style={{ opacity: addTab === "market" ? 1 : 0.7 }}
          onClick={() => setAddTab("market")}
          type="button"
        >
          Market (CEX)
        </button>
        <button
	          className="btn"
	          style={{ opacity: addTab === "dex" ? 1 : 0.7 }}
          onClick={() => setAddTab("dex")}
          type="button"
        >
          DEX (Contract)
        </button>
      </div>

      {addTab === "market" && (
        <>
          <div className="muted" style={{ marginTop: 10 }}>
            Search &mdash; pick the exact coin, so prices & updates are correct.
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input
              className="input"
              placeholder="e.g. TON / BNB / Dogecoin"
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runMarketSearch();
              }}
            />
            <button className="btn" onClick={runMarketSearch} disabled={addSearching || !String(addQuery || "").trim()}>
              {addSearching ? "Searching..." : "Search"}
            </button>
          </div>

          {addSearchErr && !String(addSearchErr).toLowerCase().includes("aborted") ? (
            <div style={{ marginTop: 8, color: "#ffb4b4" }}>
              {addSearchErr}
            </div>
          ) : null}

          <div style={{ maxHeight: 360, overflow: "auto", marginTop: 10 }}>
            {(addResults || []).length === 0 &&
            !addSearching &&
            String(addQuery || "").trim() ? (
              <div className="muted" style={{ padding: 10 }}>
                No results yet. Try a different keyword.
              </div>
            ) : null}

            {(addResults || []).map((coin) => (
<div key={coin.id} className="watchRow" style={{ alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {coin.name} <span className="muted">({String(coin.symbol || "").toUpperCase()})</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    ID: <code>{coin.id}</code>
                    {coin.market_cap_rank != null ? <> &middot; Rank #{coin.market_cap_rank}</> : null}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
	                <button className="btn" onClick={() => addMarketCoin(coin)}>
                    Add
                  </button>
                </div>
              </div>
            
))}
          </div>
        </>
      )}

      {addTab === "dex" && (
        <>
          <div className="muted" style={{ marginTop: 10 }}>
            Add token by contract address (DEX). Backend must support resolving contract metadata.
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
            <div className="muted">Chain</div>
            <select value={"polygon"} disabled>
              <option value="polygon">Polygon</option>

            </select>

            <div className="muted">Contract</div>
            <input className="input" placeholder="0x..." value={addContract} onChange={(e) => setAddContract(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={addDexToken} disabled={!String(addContract || "").trim()}>
              Add
            </button>
            <button className="btnGhost" onClick={resetAddModal}>
              Cancel
            </button>
          </div>
        
      <style>{`
        @media (min-width: 981px){
          .section-compare .liveListBox{
            max-height: clamp(260px, 34vh, 420px) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }
          .section-compare .pairsScroll{
            min-height: 0 !important;
            max-height: clamp(180px, 25vh, 300px) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            padding-right: 6px;
            padding-bottom: 56px !important;
            scroll-padding-bottom: 56px !important;
          }
          .section-compare .liveListBox::-webkit-scrollbar,
          .section-compare .pairsScroll::-webkit-scrollbar{
            width: 10px;
          }
          .section-compare .liveListBox::-webkit-scrollbar-thumb,
          .section-compare .pairsScroll::-webkit-scrollbar-thumb{
            background: rgba(210,220,230,.22);
            border-radius: 999px;
            border: 2px solid rgba(0,0,0,.10);
          }
          .section-compare .liveListBox::-webkit-scrollbar-track,
          .section-compare .pairsScroll::-webkit-scrollbar-track{
            background: rgba(0,0,0,.10);
            border-radius: 999px;
          }
        }

        @media (min-width: 981px) {
          /* Desktop layout v92:
             - column 1 stays empty as reserved future workspace
             - active panel uses the wider center workspace
             - inactive panels are compressed into a narrow right rail
          */
          .dashboardGrid.hasFocus{
            grid-template-columns: minmax(220px, 0.58fr) minmax(0, 2.95fr) minmax(118px, 0.32fr) !important;
            grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
            column-gap: 16px !important;
            row-gap: 12px !important;
            align-items: stretch !important;
          }

          .dashboardGrid.hasFocus .dashboardPanel.panelActive{
            grid-column: 2 !important;
            grid-row: 1 / span 3 !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: none !important;
            height: 100% !important;
            opacity: 1 !important;
            transform: none !important;
          }

          /* Place the three inactive modules in the far-right rail, independent of which panel is active. */
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive){
            grid-column: 3 !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 132px !important;
            justify-self: end !important;
            align-self: stretch !important;
            padding: 8px 7px !important;
            border-radius: 14px !important;
            opacity: .86 !important;
            transform: none !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive):nth-of-type(1){ grid-row: 1 !important; }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive):nth-of-type(2){ grid-row: 2 !important; }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive):nth-of-type(3){ grid-row: 3 !important; }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive):nth-of-type(4){ grid-row: 3 !important; }

          .dashboardGrid.hasFocus.focus-compare .section-grid{ grid-column: 3 !important; grid-row: 1 !important; }
          .dashboardGrid.hasFocus.focus-compare .section-watch{ grid-column: 3 !important; grid-row: 2 !important; }
          .dashboardGrid.hasFocus.focus-compare .section-ai{ grid-column: 3 !important; grid-row: 3 !important; }

          .dashboardGrid.hasFocus.focus-vault .section-compare{ grid-column: 3 !important; grid-row: 1 !important; }
          .dashboardGrid.hasFocus.focus-vault .section-watch{ grid-column: 3 !important; grid-row: 2 !important; }
          .dashboardGrid.hasFocus.focus-vault .section-ai{ grid-column: 3 !important; grid-row: 3 !important; }

          .dashboardGrid.hasFocus.focus-watchlist .section-compare{ grid-column: 3 !important; grid-row: 1 !important; }
          .dashboardGrid.hasFocus.focus-watchlist .section-grid{ grid-column: 3 !important; grid-row: 2 !important; }
          .dashboardGrid.hasFocus.focus-watchlist .section-ai{ grid-column: 3 !important; grid-row: 3 !important; }

          .dashboardGrid.hasFocus.focus-ai .section-compare{ grid-column: 3 !important; grid-row: 1 !important; }
          .dashboardGrid.hasFocus.focus-ai .section-grid{ grid-column: 3 !important; grid-row: 2 !important; }
          .dashboardGrid.hasFocus.focus-ai .section-watch{ grid-column: 3 !important; grid-row: 3 !important; }

          /* Make the inactive cards visually about 50% more compact. */
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardHead{
            gap: 4px !important;
            margin-bottom: 5px !important;
            align-items: flex-start !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardTitle{
            font-size: 11px !important;
            line-height: 1.05 !important;
            letter-spacing: 0 !important;
            max-width: 86px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardActions{
            gap: 3px !important;
            transform: scale(.82) !important;
            transform-origin: top right !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardBody{
            gap: 5px !important;
            overflow: hidden !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .chip,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btn,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnGhost,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnPill,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pill,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .legendItem{
            min-height: 22px !important;
            height: 22px !important;
            padding: 2px 5px !important;
            border-radius: 8px !important;
            font-size: 8px !important;
            line-height: 1 !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .muted,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .tiny,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .text-xs,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .hint{
            font-size: 8px !important;
            line-height: 1.15 !important;
          }

          /* Collapse dense inactive content so the right rail acts like navigation/monitoring tiles. */
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareGrid,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLayout,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridWrap,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiWrap{
            display: block !important;
          }
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareLive,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareChart,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLeft,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridRight,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridControls,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridOrders,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiSelect,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiOut,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchTable,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchScroll,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .ordersList,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .liveListBox,
          .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pairsScroll{
            max-height: 72px !important;
            overflow: hidden !important;
          }

          /* section-grid (NKR / Grid / Trading): clean minimize like other rail tiles */
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridModeSwitcher{
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 4px !important;
            min-width: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridModeSwitcher button{
            height: 28px !important;
            font-size: 10px !important;
            font-weight: 900 !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .cardHead{
            flex-wrap: nowrap !important;
            gap: 4px !important;
            margin-bottom: 4px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .cardActions .pill{
            max-width: 72px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            font-size: 8px !important;
            padding: 2px 4px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .panelScroll{
            max-height: 78px !important;
            overflow: hidden !important;
            padding-right: 0 !important;
            pointer-events: none !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock{
            margin-bottom: 0 !important;
            padding: 6px 7px !important;
            border-radius: 10px !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock > div:nth-child(2),
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock label,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock select,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock input,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .liveCoreVaultBlock button{
            display: none !important;
          }
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .nkrSessionCard,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .nkrSetupBlock,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .tradingSessionCard,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) [class*="Active NKR"],
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridLayout,
          .dashboardGrid.hasFocus .section-grid:not(.panelActive) .gridControls{
            display: none !important;
          }
        }


/* =========================================================
   Nexus desktop layout v93
   Goal:
   - keep left reserve area free for future modules
   - active panel uses the gained space
   - inactive panels form a compact right rail at the page edge
   ========================================================= */
@media (min-width: 981px){
  .main{
    width: 100vw !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 18px 14px 18px !important;
  }

  .dashboardGrid.hasFocus{
    width: 100% !important;
    height: 100% !important;
    display: grid !important;
    grid-template-columns: minmax(260px, 25vw) minmax(0, 1fr) 168px !important;
    grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
    column-gap: 18px !important;
    row-gap: 14px !important;
    align-items: stretch !important;
    justify-items: stretch !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel.panelActive{
    grid-column: 2 !important;
    grid-row: 1 / 4 !important;
    width: 100% !important;
    max-width: none !important;
    min-width: 0 !important;
    height: 100% !important;
    min-height: 0 !important;
    align-self: stretch !important;
    justify-self: stretch !important;
    opacity: 1 !important;
    transform: none !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive){
    grid-column: 3 !important;
    width: 168px !important;
    max-width: 168px !important;
    min-width: 168px !important;
    height: 100% !important;
    min-height: 0 !important;
    align-self: stretch !important;
    justify-self: end !important;
    opacity: .9 !important;
    transform: none !important;
    padding: 8px !important;
    border-radius: 16px !important;
    overflow: hidden !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-grid,
  .dashboardGrid.hasFocus.focus-vault .section-compare,
  .dashboardGrid.hasFocus.focus-watchlist .section-compare,
  .dashboardGrid.hasFocus.focus-ai .section-compare{
    grid-column: 3 !important;
    grid-row: 1 !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-watch,
  .dashboardGrid.hasFocus.focus-vault .section-watch,
  .dashboardGrid.hasFocus.focus-watchlist .section-grid,
  .dashboardGrid.hasFocus.focus-ai .section-grid{
    grid-column: 3 !important;
    grid-row: 2 !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-ai,
  .dashboardGrid.hasFocus.focus-vault .section-ai,
  .dashboardGrid.hasFocus.focus-watchlist .section-ai,
  .dashboardGrid.hasFocus.focus-ai .section-watch{
    grid-column: 3 !important;
    grid-row: 3 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardHead{
    gap: 4px !important;
    margin-bottom: 6px !important;
    align-items: flex-start !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardTitle{
    font-size: 11px !important;
    line-height: 1.08 !important;
    max-width: 118px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardActions{
    gap: 3px !important;
    transform: scale(.82) !important;
    transform-origin: top right !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardBody,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .panelScroll{
    height: calc(100% - 30px) !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: hidden !important;
    gap: 5px !important;
    padding-right: 0 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .chip,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btn,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnGhost,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnPill,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pill,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .legendItem{
    min-height: 22px !important;
    height: 22px !important;
    padding: 2px 6px !important;
    border-radius: 8px !important;
    font-size: 8px !important;
    line-height: 1 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .input,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .select,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) input,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) select,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) textarea{
    font-size: 8px !important;
    min-height: 24px !important;
    padding: 4px 6px !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .muted,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .tiny,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .text-xs,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .hint,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .label{
    font-size: 8px !important;
    line-height: 1.18 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareGrid,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLayout,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridWrap,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiWrap{
    display: block !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareLive,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareChart,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLeft,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridRight,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridControls,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridOrders,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiSelect,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiOut,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchTable,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchScroll,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .ordersList,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .liveListBox,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pairsScroll{
    max-height: none !important;
    height: auto !important;
    overflow: hidden !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) canvas,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) svg{
    max-height: 58px !important;
  }
}

/* =========================================================
   Nexus desktop layout v94
   - left reserve is only as wide as the right compact rail
   - active panel expands left and dominates the workspace
   - right rail remains readable and not vertically squeezed
   - desktop only
   ========================================================= */
@media (min-width: 981px){
  .main{
    width: 100vw !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 18px 14px 18px !important;
  }

  .dashboardGrid.hasFocus{
    width: 100% !important;
    height: 100% !important;
    display: grid !important;
    grid-template-columns: clamp(190px, 13vw, 250px) minmax(0, 1fr) clamp(210px, 13vw, 250px) !important;
    grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
    column-gap: 18px !important;
    row-gap: 14px !important;
    align-items: stretch !important;
    justify-items: stretch !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel.panelActive{
    grid-column: 2 !important;
    grid-row: 1 / 4 !important;
    width: 100% !important;
    max-width: none !important;
    min-width: 0 !important;
    height: 100% !important;
    min-height: 0 !important;
    align-self: stretch !important;
    justify-self: stretch !important;
    opacity: 1 !important;
    transform: none !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive){
    grid-column: 3 !important;
    width: 100% !important;
    max-width: 250px !important;
    min-width: 210px !important;
    height: 100% !important;
    min-height: 0 !important;
    align-self: stretch !important;
    justify-self: stretch !important;
    opacity: .92 !important;
    transform: none !important;
    padding: 10px !important;
    border-radius: 16px !important;
    overflow: hidden !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-grid,
  .dashboardGrid.hasFocus.focus-vault .section-compare,
  .dashboardGrid.hasFocus.focus-watchlist .section-compare,
  .dashboardGrid.hasFocus.focus-ai .section-compare{
    grid-column: 3 !important;
    grid-row: 1 !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-watch,
  .dashboardGrid.hasFocus.focus-vault .section-watch,
  .dashboardGrid.hasFocus.focus-watchlist .section-grid,
  .dashboardGrid.hasFocus.focus-ai .section-grid{
    grid-column: 3 !important;
    grid-row: 2 !important;
  }

  .dashboardGrid.hasFocus.focus-compare .section-ai,
  .dashboardGrid.hasFocus.focus-vault .section-ai,
  .dashboardGrid.hasFocus.focus-watchlist .section-ai,
  .dashboardGrid.hasFocus.focus-ai .section-watch{
    grid-column: 3 !important;
    grid-row: 3 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardHead{
    gap: 6px !important;
    margin-bottom: 8px !important;
    align-items: flex-start !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardTitle{
    font-size: 12px !important;
    line-height: 1.12 !important;
    max-width: 165px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardActions{
    gap: 4px !important;
    transform: scale(.88) !important;
    transform-origin: top right !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .cardBody,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .panelScroll{
    height: calc(100% - 34px) !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: hidden !important;
    gap: 6px !important;
    padding-right: 0 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .chip,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btn,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnGhost,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .btnPill,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pill,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .legendItem{
    min-height: 24px !important;
    height: 24px !important;
    padding: 3px 7px !important;
    border-radius: 9px !important;
    font-size: 9px !important;
    line-height: 1 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .input,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .select,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) input,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) select,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) textarea{
    font-size: 9px !important;
    min-height: 26px !important;
    padding: 5px 7px !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .muted,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .tiny,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .text-xs,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .hint,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .label{
    font-size: 9px !important;
    line-height: 1.22 !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareGrid,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLayout,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridWrap,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiWrap{
    display: block !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareLive,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .compareChart,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridLeft,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridRight,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridControls,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .gridOrders,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiSelect,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .aiOut,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchTable,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .watchScroll,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .ordersList,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .liveListBox,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) .pairsScroll{
    max-height: none !important;
    height: auto !important;
    overflow: hidden !important;
  }

  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) canvas,
  .dashboardGrid.hasFocus .dashboardPanel:not(.panelActive) svg{
    max-height: 66px !important;
  }
}



/* BUILD391-HARD-HIDE-RAIL: gridRailSummary removed from JSX; keep CSS kill-switch */
.gridRailSummary, .gridRailMode, .gridRailStatus, .gridRailLine, .gridRailHint {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  max-height: 0 !important;
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

/* BUILD391: never show rail summary inside an open Grid panel */
.section-grid.panelActive .gridRailSummary,
.section-grid .panelScroll ~ .gridRailSummary,
.gridRailSummary { display: none !important; }
.dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailSummary {
  display: flex !important;
  flex-direction: column !important;
  gap: 4px !important;
  margin-top: 4px !important;
  padding: 8px !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255,255,255,.10) !important;
  background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(0,0,0,.18)) !important;
}

/* Always hide rail on mobile / default; desktop minimize rules below */
.gridRailSummary{ display: none !important; }

/* =========================================================
   Nexus desktop layout v95 — section-grid minimize (final)
   v93/v94 forced panelScroll to full height and gridLayout to
   display:block, which left NKR/Grid/Trading looking un-minimized.
   ========================================================= */
@media (min-width: 981px){
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridModeSwitcher{
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 4px !important;
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin-top: 6px !important;
    padding: 3px !important;
    visibility: visible !important;
    height: auto !important;
    overflow: visible !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .cardHead{
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridModeSwitcher button{
    height: 28px !important;
    min-height: 28px !important;
    font-size: 10px !important;
    font-weight: 900 !important;
    padding: 2px 6px !important;
    border-radius: 9px !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) > .panelScroll,
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .panelScroll{
    display: none !important;
    height: 0 !important;
    max-height: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridLayout,
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .liveCoreVaultBlock,
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridControls,
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridLeft,
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRight{
    display: none !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .cardHead{
    margin-bottom: 0 !important;
    flex-wrap: wrap !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .cardTitle{
    max-width: 100% !important;
    font-size: 12px !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .cardActions .pill{
    max-width: 90px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    font-size: 8px !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive)::after{
    content: none !important;
    display: none !important;
  }
  .gridRailSummary{ display: none !important; }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailSummary{
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    margin-top: 4px !important;
    padding: 8px !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255,255,255,.10) !important;
    background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(0,0,0,.18)) !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailMode{
    font-size: 10px !important; font-weight: 900 !important; letter-spacing: .04em !important;
    text-transform: uppercase !important; color: rgba(139,220,255,.92) !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailStatus{
    font-size: 13px !important; font-weight: 900 !important; line-height: 1.15 !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailLine{
    font-size: 10px !important; font-weight: 700 !important; color: rgba(235,255,247,.78) !important;
  }
  .dashboardGrid.hasFocus .section-grid.dashboardPanel:not(.panelActive) .gridRailHint{
    font-size: 9px !important; color: rgba(235,255,247,.48) !important; margin-top: 2px !important;
  }
  .dashboardGrid .section-grid.panelActive .gridRailSummary,
  .dashboardGrid:not(.hasFocus) .gridRailSummary{ display: none !important; }
}


      `}



</style>
</>
      )}

      <div className="muted" style={{ marginTop: 10 }}>
        Tip: coins already cached on backend appear instantly; new ones may take a moment to fetch.
      </div>
    </div>
  </div>
)}
    </div>
  );
}


export default function App() {
  const { ready, authenticated } = usePrivy();
  const { wallets: systemWallets = [] } = useWallets();
  const { addSigners } = useSigners();
  const _privySignerSetupInFlight = useRef(false);
  const [liveSetupBusy, setLiveSetupBusy] = useState("");
  const [liveSetupMsg, setLiveSetupMsg] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSystemInfo, setShowSystemInfo] = useState(false);
  const [buildInfo, setBuildInfo] = useState(null);
  const [shadowHealth, setShadowHealth] = useState(null);
  const [systemInfoStatus, setSystemInfoStatus] = useState(null);
  const [engineDiagnostics, setEngineDiagnostics] = useState(null);
  const [engineDiagnosticsBusy, setEngineDiagnosticsBusy] = useState("");
  const [engineDiagnosticsError, setEngineDiagnosticsError] = useState("");
  const [nkrSessionAudit, setNkrSessionAudit] = useState(null);
  const [nkrSessionAuditBusy, setNkrSessionAuditBusy] = useState(false);
  const [nkrSessionAuditError, setNkrSessionAuditError] = useState("");
  const [shadowReadiness, setShadowReadiness] = useState(null);
  const [evmRegistryBusy, setEvmRegistryBusy] = useState("");
  const [evmRegistryMsg, setEvmRegistryMsg] = useState("");
  const [ownerAdminBusy, setOwnerAdminBusy] = useState("");
  const ownerSystemInfoFlightRef = useRef(false);
  const coreVaultScanFlightRef = useRef(false);
  const coreVaultScanAbortRef = useRef(null);
  const [ownerAdminMsg, setOwnerAdminMsg] = useState("");
  const [coreVaultSessionPreview, setCoreVaultSessionPreview] = useState(null);
  const [coreVaultRecoveryJobs, setCoreVaultRecoveryJobs] = useState({});
  const [exactOrphanRecoveryBusyKey, setExactOrphanRecoveryBusyKey] = useState("");
  const [ownerAdminHash, setOwnerAdminHash] = useState("");
  const [privyPolicyPreview, setPrivyPolicyPreview] = useState(null);
  const [privyPolicyChainId, setPrivyPolicyChainId] = useState(137);
  const [privyPolicyBusy, setPrivyPolicyBusy] = useState("");
  const [privyPolicyMsg, setPrivyPolicyMsg] = useState("");
  // BUILD452: top-10 EVM catalog for System Info Privy policy
  const [privyPolicyCatalog, setPrivyPolicyCatalog] = useState([]);
  const [privyPolicyCatalogMsg, setPrivyPolicyCatalogMsg] = useState("");
  const [ownerAdminWalletMode, setOwnerAdminWalletMode] = useState(() => {
    try { return window.localStorage.getItem("nexus_owner_admin_wallet_mode_v1") || "METAMASK"; }
    catch { return "METAMASK"; }
  });
  const [ownerAdminWalletStatus, setOwnerAdminWalletStatus] = useState({ owner: "", selected: "", metamask: "", privy: "", matches: false, loading: false, error: "" });
  const [tokenExecuteAfter, setTokenExecuteAfter] = useState(0);
  const [routeExecuteAfter, setRouteExecuteAfter] = useState(0);
  const [ownerAdminNow, setOwnerAdminNow] = useState(() => Math.floor(Date.now() / 1000));
  const [tokenAdmin, setTokenAdmin] = useState({
    token: ETH_USDC_ADDRESS, configured: true, depositEnabled: true, withdrawEnabled: true,
    executionEnabled: true, paymentEnabled: true, settlementToken: true, decimals: "6",
    maxSingleDeposit: "10000000000", maxSessionBudget: "10000000000", profitThreshold: "100000000",
  });
  const [routeAdmin, setRouteAdmin] = useState({
    routeId: "", enabled: true, kind: "0", target: "", oracle: "", tokenIn: ETH_USDC_ADDRESS,
    tokenOut: "", selector: "0x00000000",
  });
  const [walletAdmin, setWalletAdmin] = useState({ guardian: "", revenueWallet: "", liquidityDestination: "" });

  const _loadWethTokenPreset = () => {
    setTokenExecuteAfter(0);
    setTokenAdmin({
      token: ETH_WETH_ADDRESS, configured: true, depositEnabled: false, withdrawEnabled: false,
      executionEnabled: true, paymentEnabled: false, settlementToken: false, decimals: "18",
      maxSingleDeposit: "0", maxSessionBudget: "0", profitThreshold: "0",
    });
    setOwnerAdminMsg("WETH execution preset loaded. Schedule it first, wait for the timelock, then execute it.");
  };

  const _loadRoutePreset = (direction) => {
    const buy = direction === "BUY";
    setRouteExecuteAfter(0);
    setRouteAdmin({
      routeId: buy ? ETH_USDC_WETH_BUY_ROUTE_ID : ETH_WETH_USDC_SELL_ROUTE_ID,
      enabled: true, kind: "0", target: UNISWAP_SWAP_ROUTER_02, oracle: ZERO_ADDRESS,
      tokenIn: buy ? ETH_USDC_ADDRESS : ETH_WETH_ADDRESS,
      tokenOut: buy ? ETH_WETH_ADDRESS : ETH_USDC_ADDRESS,
      selector: UNISWAP_EXACT_INPUT_SINGLE_SELECTOR,
    });
    setOwnerAdminMsg(`${buy ? "USDC → WETH BUY" : "WETH → USDC SELL"} preset loaded. Check the values, then schedule the route.`);
  };


  useEffect(() => {
    const timer = window.setInterval(() => setOwnerAdminNow(Math.floor(Date.now() / 1000)), 1000);
    try {
      const tokenPending = JSON.parse(window.localStorage.getItem("nexus_owner_admin_token_pending_v1") || "null");
      if (tokenPending?.actionHash && Number(tokenPending?.executeAfter) > 0) {
        setOwnerAdminHash(String(tokenPending.actionHash));
        setTokenExecuteAfter(Number(tokenPending.executeAfter));
      }
      const routePending = JSON.parse(window.localStorage.getItem("nexus_owner_admin_route_pending_v1") || "null");
      if (routePending?.executeAfter) {
        setRouteExecuteAfter(Number(routePending.executeAfter));
        if (routePending?.actionHash) setOwnerAdminHash(String(routePending.actionHash));

        // Restore the exact values that were scheduled. executeRoute() must use
        // byte-for-byte identical values or the timelock action hash will differ.
        let restoredRoute = routePending?.route && typeof routePending.route === "object"
          ? routePending.route
          : null;

        // Backward compatibility for routes scheduled by ENGINE-169, which only
        // stored routeId. Known presets can be reconstructed safely.
        if (!restoredRoute && routePending?.routeId) {
          const pendingRouteId = String(routePending.routeId || "").toLowerCase();
          const isBuy = pendingRouteId === String(ETH_USDC_WETH_BUY_ROUTE_ID).toLowerCase();
          const isSell = pendingRouteId === String(ETH_WETH_USDC_SELL_ROUTE_ID).toLowerCase();
          if (isBuy || isSell) {
            restoredRoute = {
              routeId: isBuy ? ETH_USDC_WETH_BUY_ROUTE_ID : ETH_WETH_USDC_SELL_ROUTE_ID,
              enabled: true,
              kind: "0",
              target: UNISWAP_SWAP_ROUTER_02,
              oracle: ZERO_ADDRESS,
              tokenIn: isBuy ? ETH_USDC_ADDRESS : ETH_WETH_ADDRESS,
              tokenOut: isBuy ? ETH_WETH_ADDRESS : ETH_USDC_ADDRESS,
              selector: UNISWAP_EXACT_INPUT_SINGLE_SELECTOR,
            };
          }
        }

        if (restoredRoute) {
          setRouteAdmin({
            routeId: String(restoredRoute.routeId || ""),
            enabled: restoredRoute.enabled !== false,
            kind: String(restoredRoute.kind ?? "0"),
            target: String(restoredRoute.target || ""),
            oracle: String(restoredRoute.oracle || ZERO_ADDRESS),
            tokenIn: String(restoredRoute.tokenIn || ""),
            tokenOut: String(restoredRoute.tokenOut || ""),
            selector: String(restoredRoute.selector || "0x00000000"),
          });
        }
      }
    } catch {}
    return () => window.clearInterval(timer);
  }, []);

  const _formatOwnerAdminCountdown = (executeAfter) => {
    const remaining = Math.max(0, Number(executeAfter || 0) - ownerAdminNow);
    const h = Math.floor(remaining / 3600);
    const m = Math.floor((remaining % 3600) / 60);
    const sec = remaining % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const tokenExecuteReady = tokenExecuteAfter > 0 && ownerAdminNow >= tokenExecuteAfter;
  const routeExecuteReady = routeExecuteAfter > 0 && ownerAdminNow >= routeExecuteAfter;
  const routePendingLocked = routeExecuteAfter > 0;
  const pendingRouteDirection =
    String(routeAdmin.tokenIn || "").toLowerCase() === ETH_USDC_ADDRESS.toLowerCase() &&
    String(routeAdmin.tokenOut || "").toLowerCase() === ETH_WETH_ADDRESS.toLowerCase()
      ? "BUY · USDC → WETH"
      : String(routeAdmin.tokenIn || "").toLowerCase() === ETH_WETH_ADDRESS.toLowerCase() &&
        String(routeAdmin.tokenOut || "").toLowerCase() === ETH_USDC_ADDRESS.toLowerCase()
        ? "SELL · WETH → USDC"
        : "CUSTOM ROUTE";

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/build-info`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setBuildInfo(data);
      })
      .catch(() => {
        if (alive) setBuildInfo(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const backendBuild = buildInfo?.backend_build || "backend ?";
  const frontendTarget = buildInfo?.frontend_target || "frontend target ?";
  const strategistBuild = buildInfo?.strategist_version || "S ?";
  const shadowBuild = buildInfo?.shadow_version || "SH ?";
  const entryMode = buildInfo?.entry_mode || "entry ?";
  const promotionMode = buildInfo?.promotion_mode || "promotion ?";
  const exitMode = buildInfo?.exit_mode || "exit ?";
  const renderCommit = buildInfo?.render_git_commit || "not provided";
  const buildTs = buildInfo?.ts ? new Date(buildInfo.ts * 1000).toLocaleString() : "unknown";
  const backendOnline = !!buildInfo?.backend_build;
  const shadowTickAge = shadowHealth?.seconds_since_tick;
  const shadowLastTick = shadowHealth?.last_tick ? new Date(shadowHealth.last_tick * 1000).toLocaleString() : "not seen";
  const shadowTickCount = shadowHealth?.tick_count ?? "?";
  const shadowRuntimeStatus = shadowHealth?.runtime_status || (shadowHealth?.running ? "running" : "unknown");
  const shadowStalled = !!shadowHealth?.stalled;

  // Developer-only diagnostics button.
  // Keep the System Info trigger hidden for normal users; your wallet can still open it.
  const DEV_SYSTEM_INFO_WALLET = "0x150270ac191ba7caee8f098add651fa9db38b028";
  const footerWallet = resolveWalletAddress(typeof window !== "undefined" ? localStorage.getItem("nexus_wallet") : "");
  const canOpenSystemInfo = String(footerWallet || "").toLowerCase() === DEV_SYSTEM_INFO_WALLET;

  useEffect(() => {
    // Heavy owner diagnostics read several chains and must run only while the
    // System Info modal is actually open. Running them permanently in the
    // footer caused background RPC storms and pending/cancelled preflights.
    if (!canOpenSystemInfo || !showSystemInfo) return;
    let alive = true;

    const walletParam = footerWallet ? `?wallet=${encodeURIComponent(footerWallet)}&wallet_address=${encodeURIComponent(footerWallet)}` : "";

    const loadJson = async (path, timeoutMs = 8000) => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        // Nexus API endpoints authenticate with the backend token. The Privy JWT is
        // only a fallback during the brief login/bootstrap phase.
        const bearer = String(localStorage.getItem("nexus_token") || localStorage.getItem("nexus_privy_jwt") || "").trim();
        const headers = { Accept: "application/json" };
        if (bearer) headers.Authorization = `Bearer ${bearer}`;
        if (footerWallet) headers["X-Wallet-Address"] = footerWallet;
        const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", credentials: "include", headers, signal: controller.signal });
        return res.ok ? await res.json() : null;
      } catch {
        return null;
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const loadOwnerSystemInfo = async () => {
      if (ownerSystemInfoFlightRef.current || document.visibilityState === "hidden") return;
      ownerSystemInfoFlightRef.current = true;
      try {
        const results = await Promise.allSettled([
          loadJson(`/api/shadow/health${walletParam}`),
          loadJson(`/api/nexus/system-info-owner-panel${walletParam}`),
          loadJson(`/api/nexus/shadow-readiness-check${walletParam}`),
          loadJson(`/api/nexus/live-executor/status${walletParam}`),
          loadJson(`/api/nexus/engine-runtime/status${walletParam}`),
        ]);
        if (!alive) return;
        const valueAt = (i) => results[i]?.status === "fulfilled" ? results[i].value : null;
        const health = valueAt(0);
        const statusPanel = valueAt(1);
        const readiness = valueAt(2);
        const liveExecutorStatus = valueAt(3);
        const engineRuntimeStatus = valueAt(4);
        if (health) setShadowHealth(health);
        if (statusPanel || liveExecutorStatus || engineRuntimeStatus) {
          setSystemInfoStatus((prev) => stripGateObjects({ ...(prev || {}), ...(statusPanel || {}), liveExecutorStatus: liveExecutorStatus || prev?.liveExecutorStatus || null, engineRuntimeStatus: engineRuntimeStatus || prev?.engineRuntimeStatus || null }));
        }
        if (readiness) setShadowReadiness(readiness);
      } finally {
        ownerSystemInfoFlightRef.current = false;
      }
    };

    loadOwnerSystemInfo();
    const id = setInterval(loadOwnerSystemInfo, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") loadOwnerSystemInfo(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      ownerSystemInfoFlightRef.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [canOpenSystemInfo, showSystemInfo, footerWallet]);


  const _privyPolicyChainLabel = (chainId) => {
    const cid = Number(chainId);
    const row = (privyPolicyCatalog || []).find((c) => Number(c.chainId) === cid);
    if (row) return `${row.key} · ${row.name}`;
    return ({ 1: "ETH", 56: "BNB", 137: "POL", 8453: "BASE", 42161: "ARBITRUM", 10: "OPTIMISM", 43114: "AVALANCHE", 59144: "LINEA", 534352: "SCROLL", 324: "ZKSYNC" }[cid]) || `Chain ${cid}`;
  };

  const _loadPrivyPolicyCatalog = async () => {
    if (!canOpenSystemInfo) return;
    try {
      const res = await fetch(`${API_BASE}/api/nexus/privy-policy-chain-catalog`, {
        method: "GET", cache: "no-store", credentials: "include", headers: _authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== "ok") throw new Error(data?.error || `Catalog failed (${res.status})`);
      const chains = Array.isArray(data?.chains) ? data.chains : [];
      setPrivyPolicyCatalog(chains);
      setPrivyPolicyCatalogMsg(`${chains.length} chains loaded`);
      // keep current selection if still in catalog, else first available with vault
      if (chains.length) {
        const still = chains.some((c) => Number(c.chainId) === Number(privyPolicyChainId));
        if (!still) {
          const prefer = chains.find((c) => c.vaultConfigured) || chains[0];
          if (prefer) setPrivyPolicyChainId(Number(prefer.chainId));
        }
      }
    } catch (err) {
      setPrivyPolicyCatalogMsg(`Catalog: ${err?.message || err}`);
    }
  };

  const _loadPrivyPolicyPreview = async () => {
    if (!canOpenSystemInfo || privyPolicyBusy) return;
    setPrivyPolicyBusy("preview");
    const policyChainName = _privyPolicyChainLabel(privyPolicyChainId);
    setPrivyPolicyMsg(`Reloading the live Privy policy and current server ENV for ${policyChainName}...`);
    try {
      if (!(privyPolicyCatalog || []).length) {
        await _loadPrivyPolicyCatalog();
      }
      const res = await fetch(`${API_BASE}/api/nexus/system-info/privy-policy/preview`, {
        method: "POST", cache: "no-store", credentials: "include", headers: _authHeaders(),
        body: JSON.stringify({ chainId: Number(privyPolicyChainId) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== "ok") throw new Error(data?.error || `Policy preview failed (${res.status})`);
      setPrivyPolicyPreview(data);
      setPrivyPolicyMsg((data?.toAdd || []).length
        ? `${data.toAdd.length} ${policyChainName} rule(s) are ready to add. Review every target, then confirm.`
        : `${policyChainName} rules are already present. No policy change is required.`);
    } catch (err) {
      setPrivyPolicyPreview(null);
      setPrivyPolicyMsg(`Policy preview failed: ${err?.message || err}`);
    } finally {
      setPrivyPolicyBusy("");
    }
  };

  const _applyPrivyPolicyPreview = async () => {
    if (!canOpenSystemInfo || privyPolicyBusy || !privyPolicyPreview?.confirmationToken) return;
    const count = (privyPolicyPreview?.toAdd || []).length;
    if (!count) return;
    const policyChainName = _privyPolicyChainLabel(privyPolicyChainId);
    const ok = window.confirm(`Add ${count} reviewed ${policyChainName} rule(s) to the existing Privy policy ${privyPolicyPreview?.policy?.name || ""}? Existing rules remain unchanged.`);
    if (!ok) return;
    setPrivyPolicyBusy("apply");
    setPrivyPolicyMsg("Applying signed Privy policy update from the backend...");
    try {
      const res = await fetch(`${API_BASE}/api/nexus/system-info/privy-policy/apply`, {
        method: "POST", cache: "no-store", credentials: "include", headers: _authHeaders(),
        body: JSON.stringify({ chainId: Number(privyPolicyChainId), confirmationToken: privyPolicyPreview.confirmationToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== "ok") throw new Error(data?.error || `Policy update failed (${res.status})`);
      if (data?.finalPreview?.status === "ok") setPrivyPolicyPreview(data.finalPreview);
      setPrivyPolicyMsg(`Privy policy updated and reloaded: ${data.createdCount || 0} rule(s) added; ${data.remainingCount || 0} remaining.`);
    } catch (err) {
      setPrivyPolicyMsg(`Policy update failed: ${err?.message || err}`);
    } finally {
      setPrivyPolicyBusy("");
    }
  };

  useEffect(() => {
    if (!showSystemInfo || !canOpenSystemInfo) return;
    _loadPrivyPolicyCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSystemInfo, canOpenSystemInfo]);


  const _primaryEmbeddedPrivyWallet = () => {
    return (systemWallets || []).find((w) => String(w?.walletClientType || "").toLowerCase() === "privy")
      || (systemWallets || [])[0];
  };

  const _authHeaders = (explicitToken = "") => {
    const bearer = String(explicitToken || localStorage.getItem("nexus_token") || localStorage.getItem("nexus_privy_jwt") || "").trim();
    const headers = { "Content-Type": "application/json", "X-Wallet-Address": String(footerWallet || "") };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;
    return headers;
  };

  const _loadNkrSessionAudit = async () => {
    if (!canOpenSystemInfo || nkrSessionAuditBusy || !footerWallet) return;
    setNkrSessionAuditBusy(true);
    setNkrSessionAuditError("");
    try {
      const q = new URLSearchParams({ wallet: footerWallet, wallet_address: footerWallet });
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 20000);
      let res;
      try {
        res = await fetch(`${API_BASE}/api/nexus/system-info/nkr-session-audit?${q.toString()}`, {
          method: "GET",
          cache: "no-store",
          credentials: "include",
          headers: _authHeaders(),
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `NKR session audit failed (HTTP ${res.status})`);
      setNkrSessionAudit(data);
    } catch (err) {
      setNkrSessionAudit(null);
      setNkrSessionAuditError(err?.name === "AbortError" ? "NKR session audit timed out after 20 seconds." : String(err?.message || err));
    } finally {
      setNkrSessionAuditBusy(false);
    }
  };

  // Privy signer provisioning is intentionally not run during page load.
  // It is triggered explicitly by the first live Start action and must verify
  // the signer attachment before any CoreVault session can be created.
  // BUILD386: On-chain session refresh = NKR + Trader only. Grid excluded
  // (target price / max-loss). Runtime Diagnose still shows NKR / Grid / Trader.
  const CORE_VAULT_SESSION_ENGINES = Object.freeze(["NKR", "TRADER"]);
  const CORE_VAULT_ENGINE_LABEL = Object.freeze({ NKR: "NKR", TRADER: "Trader", GRID: "Grid" });
  const _coreVaultEngineLabel = (engine) => {
    const e = String(engine || "").toUpperCase();
    return CORE_VAULT_ENGINE_LABEL[e] || e || "Unknown";
  };

  const _inspectCoreVaultSessions = async () => {
    if (!canOpenSystemInfo || ownerAdminBusy || coreVaultScanFlightRef.current) return;
    coreVaultScanFlightRef.current = true;
    try { coreVaultScanAbortRef.current?.abort?.(); } catch {}
    const controller = new AbortController();
    coreVaultScanAbortRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(), 45000);
    setOwnerAdminBusy("CoreVault session scan");
    setOwnerAdminMsg("Reading NKR and Trader on-chain sessions on ETH, BNB and POL (Grid excluded)...");
    const chains = [
      { chain: "ETH", chainId: 1, vault: CORE_VAULT_ETH_ADDRESS },
      { chain: "BNB", chainId: 56, vault: CORE_VAULT_BNB_ADDRESS },
      { chain: "POL", chainId: 137, vault: CORE_VAULT_POL_ADDRESS },
    ].filter((x) => /^0x[a-fA-F0-9]{40}$/.test(String(x.vault || "")));
    try {
      let sessions = [];
      let candidates = [];
      let nextByChain = {};
      let failures = [];
      let usedEndpoint = "recover-preview";
      // Prefer BUILD365 endpoint (NKR + Trader, Grid excluded by policy).
      try {
        const q = new URLSearchParams({ wallet: footerWallet, wallet_address: footerWallet });
        const res = await fetch(`${API_BASE}/api/nexus/system-info/onchain-sessions?${q.toString()}`, {
          cache: "no-store", credentials: "include", headers: _authHeaders(), signal: controller.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && (Array.isArray(data?.sessions) || Array.isArray(data?.sections))) {
          usedEndpoint = "onchain-sessions";
          for (const raw of (Array.isArray(data.sessions) ? data.sessions : [])) {
            const engine = String(raw?.engine || "").toUpperCase();
            if (engine === "GRID" || !CORE_VAULT_SESSION_ENGINES.includes(engine)) continue;
            const chainId = Number(raw?.chainId || 0);
            const chain = String(raw?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[chainId] || "")).toUpperCase();
            const session = {
              ...raw, chain, chainId,
              vault: raw?.vault || CORE_VAULT_ADDRESS_BY_CHAIN[chain] || "",
              engine,
              engineLabel: raw?.engineLabel || _coreVaultEngineLabel(engine),
              displayName: raw?.displayName || `${_coreVaultEngineLabel(engine)} Session #${raw?.sessionId}`,
            };
            sessions.push(session);
            if (session.recoverable) candidates.push(session);
            if (chain) nextByChain[chain] = Math.max(Number(nextByChain[chain] || 0), Number(raw?.sessionId || 0) + 1);
          }
          // Prefer authoritative nextSessionId from backend multi-chain scan
          const apiNext = data?.nextSessionIdByChain || {};
          for (const ck of ["ETH", "BNB", "POL"]) {
            const n = Number(apiNext[ck] || 0);
            if (n > 0) nextByChain[ck] = Math.max(Number(nextByChain[ck] || 0), n);
          }
          for (const err of (Array.isArray(data?.errors) ? data.errors : [])) {
            const cLabel = err?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(err?.chainId)] || err?.chainId || "?");
            failures.push(`${err?.engineLabel || err?.engine || "?"} ${cLabel}: ${err?.error || "error"}`);
          }
        } else if (!res.ok) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
      } catch (primaryErr) {
        const jobs = [];
        for (const c of chains) for (const engine of CORE_VAULT_SESSION_ENGINES) jobs.push({ ...c, engine });
        const settled = await Promise.allSettled(jobs.map(async (job) => {
          const q = new URLSearchParams({
            wallet: footerWallet, wallet_address: footerWallet, engine: job.engine,
            chainId: String(job.chainId), vault: job.vault,
          });
          const res = await fetch(`${API_BASE}/api/nexus/live-reservation/recover?${q.toString()}`, {
            cache: "no-store", credentials: "include", headers: _authHeaders(), signal: controller.signal,
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(`${job.chain}/${_coreVaultEngineLabel(job.engine)}: ${data?.error || `HTTP ${res.status}`}`);
          return { ...job, data };
        }));
        const results = settled.filter((x) => x.status === "fulfilled").map((x) => x.value);
        failures = settled.filter((x) => x.status === "rejected").map((x) => String(x.reason?.message || x.reason));
        const sessionMap = new Map(); const candidateMap = new Map();
        for (const { chain, chainId, vault, engine, data } of results) {
          nextByChain[chain] = Math.max(Number(nextByChain[chain] || 0), Number(data?.nextSessionId || 0));
          for (const raw of (Array.isArray(data?.sessions) ? data.sessions : [])) {
            const eng = String(raw?.engine || engine).toUpperCase();
            if (eng === "GRID" || !CORE_VAULT_SESSION_ENGINES.includes(eng)) continue;
            const session = {
              ...raw, chain, chainId, vault, engine: eng,
              engineLabel: raw?.engineLabel || _coreVaultEngineLabel(eng),
              displayName: raw?.displayName || `${_coreVaultEngineLabel(eng)} Session #${raw?.sessionId}`,
            };
            sessionMap.set(`${chainId}:${eng}:${String(session?.sessionId ?? "")}`, session);
          }
          for (const raw of (Array.isArray(data?.candidates) ? data.candidates : [])) {
            const eng = String(raw?.engine || engine).toUpperCase();
            if (eng === "GRID" || !CORE_VAULT_SESSION_ENGINES.includes(eng)) continue;
            const candidate = {
              ...raw, chain, chainId, vault, engine: eng,
              engineLabel: raw?.engineLabel || _coreVaultEngineLabel(eng),
              displayName: raw?.displayName || `${_coreVaultEngineLabel(eng)} Session #${raw?.sessionId}`,
            };
            candidateMap.set(`${chainId}:${eng}:${String(candidate?.sessionId ?? "")}`, candidate);
          }
        }
        sessions = Array.from(sessionMap.values()).sort((a, b) => Number(a.chainId) - Number(b.chainId) || Number(a.sessionId) - Number(b.sessionId));
        candidates = Array.from(candidateMap.values());
        if (!sessions.length && primaryErr) failures.push(`onchain-sessions: ${primaryErr?.message || primaryErr}`);
      }
      const counts = { NKR: 0, TRADER: 0 };
      sessions.forEach((s) => { const e = String(s.engine || "").toUpperCase(); if (e in counts) counts[e] += 1; });
      setCoreVaultSessionPreview({
        nextSessionId: nextByChain, nextSessionIdByChain: nextByChain,
        sessions, candidates, counts, engines: CORE_VAULT_SESSION_ENGINES,
        chains: chains.map((c) => c.chain), failures, usedEndpoint, gridExcluded: true,
      });
      setOwnerAdminMsg(
        `On-chain scan (NKR + Trader): ${sessions.length} session(s) · NKR ${counts.NKR} · Trader ${counts.TRADER} · recoverable ${candidates.length}`
        + (failures.length ? ` · ${failures.length} scan error(s)` : "")
        + " · Grid excluded"
      );
    } catch (err) {
      const aborted = err?.name === "AbortError";
      setOwnerAdminMsg(aborted ? "CoreVault multi-chain scan timed out." : `On-chain session scan failed: ${err?.message || err}`);
    } finally {
      window.clearTimeout(timeoutId);
      if (coreVaultScanAbortRef.current === controller) coreVaultScanAbortRef.current = null;
      coreVaultScanFlightRef.current = false; setOwnerAdminBusy("");
    }
  };

  // BUILD421: silent on-chain session refresh for Trader/NKR controls — no System Info click needed.
  const _refreshOnchainSessionsSilent = useCallback(async () => {
    const wa = String(footerWallet || "").trim();
    if (!wa || !/^0x[a-fA-F0-9]{40}$/i.test(wa)) return;
    if (coreVaultScanFlightRef.current) return;
    coreVaultScanFlightRef.current = true;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 35000);
    try {
      const q = new URLSearchParams({ wallet: wa, wallet_address: wa });
      const res = await fetch(`${API_BASE}/api/nexus/system-info/onchain-sessions?${q.toString()}`, {
        cache: "no-store", credentials: "include", headers: _authHeaders(), signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const sessions = [];
      const candidates = [];
      const nextByChain = {};
      for (const raw of (Array.isArray(data.sessions) ? data.sessions : [])) {
        const engine = String(raw?.engine || "").toUpperCase();
        if (engine === "GRID" || !CORE_VAULT_SESSION_ENGINES.includes(engine)) continue;
        const chainId = Number(raw?.chainId || 0);
        const chain = String(raw?.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[chainId] || "")).toUpperCase();
        const session = {
          ...raw, chain, chainId,
          vault: raw?.vault || CORE_VAULT_ADDRESS_BY_CHAIN[chain] || "",
          engine,
          engineLabel: raw?.engineLabel || _coreVaultEngineLabel(engine),
          displayName: raw?.displayName || `${_coreVaultEngineLabel(engine)} Session #${raw?.sessionId}`,
        };
        sessions.push(session);
        if (session.recoverable) candidates.push(session);
        if (chain) nextByChain[chain] = Math.max(Number(nextByChain[chain] || 0), Number(raw?.sessionId || 0) + 1);
      }
      const apiNext = data?.nextSessionIdByChain || {};
      for (const ck of ["ETH", "BNB", "POL"]) {
        const n = Number(apiNext[ck] || 0);
        if (n > 0) nextByChain[ck] = Math.max(Number(nextByChain[ck] || 0), n);
      }
      const counts = { NKR: 0, TRADER: 0 };
      sessions.forEach((s) => { const e = String(s.engine || "").toUpperCase(); if (e in counts) counts[e] += 1; });
      setCoreVaultSessionPreview({
        nextSessionId: nextByChain, nextSessionIdByChain: nextByChain,
        sessions, candidates, counts, engines: CORE_VAULT_SESSION_ENGINES,
        chains: ["ETH", "BNB", "POL"], failures: [], usedEndpoint: "onchain-sessions-auto", gridExcluded: true,
      });

    } catch (_) {
      /* silent */
    } finally {
      window.clearTimeout(timeoutId);
      coreVaultScanFlightRef.current = false;
    }
  }, [footerWallet]);

  // Auto-refresh on-chain sessions while wallet is connected (Trader/NKR need this for Pause/Stop).
  useEffect(() => {
    const wa = String(footerWallet || "").trim();
    if (!wa || !/^0x[a-fA-F0-9]{40}$/i.test(wa)) return undefined;
    _refreshOnchainSessionsSilent();
    const id = window.setInterval(() => { _refreshOnchainSessionsSilent(); }, 45000);
    return () => window.clearInterval(id);
  }, [footerWallet, _refreshOnchainSessionsSilent]);

  const _recoverExactCoreVaultSession = async (sessionId, engine, chain = "ETH", chainId = 0) => {
    const normalizedEngine = String(engine || "").toUpperCase().replace("TRADING", "TRADER");
    const normalizedChain = String(chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(chainId)] || "")).toUpperCase();
    const sid = Number(sessionId || 0);
    if (!sid || !CORE_VAULT_SESSION_ENGINES.includes(normalizedEngine) || !["ETH", "BNB", "POL"].includes(normalizedChain)) return false;
    const busyKey = `${normalizedEngine}:${normalizedChain}:${sid}`;
    if (exactOrphanRecoveryBusyKey) return false;
    setExactOrphanRecoveryBusyKey(busyKey);
    setOwnerAdminMsg(`Stopping and finalizing exact ${_coreVaultEngineLabel(normalizedEngine)} ${normalizedChain} session #${sid}...`);
    try {
      const res = await fetch(`${API_BASE}/api/live/stop-orphan-session`, {
        method: "POST", cache: "no-store", credentials: "include", headers: _authHeaders(),
        body: JSON.stringify({ engine: normalizedEngine, chain: normalizedChain, chainId: Number(chainId || 0), sessionId: sid }),
      });
      const data = await res.json().catch(() => ({}));
      if (!(res.ok || res.status === 202)) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
      setOwnerAdminMsg(`${_coreVaultEngineLabel(normalizedEngine)} ${normalizedChain} session #${sid}: Stop & Finalize queued. Waiting for on-chain confirmation.`);
      // Refresh authoritative on-chain inventory shortly after queueing. The session
      // remains visible until finalizeSession is actually confirmed on-chain.
      window.setTimeout(() => { try { _inspectCoreVaultSessions(); } catch {} }, 2500);
      // BUILD416: if the exact orphan/recovery action finalizes a Trader session, also
      // reconcile the stale local Trader card. Previously the CoreVault finalized but
      // the old local card could remain ACTIVE forever.
      if (normalizedEngine === "TRADER") {
        const pollTraderFinalized = async (attempt = 0) => {
          try {
            const q = new URLSearchParams({ wallet: footerWallet, wallet_address: footerWallet, engine: "TRADER", chainId: String(Number(chainId || ({ ETH: 1, BNB: 56, POL: 137 }[normalizedChain] || 0))) });
            const rr = await fetch(`${API_BASE}/api/nexus/live-reservation/recover?${q.toString()}`, { cache: "no-store", credentials: "include", headers: _authHeaders() });
            const rd = await rr.json().catch(() => ({}));
            const row = (Array.isArray(rd?.sessions) ? rd.sessions : []).find((x) => Number(x?.sessionId || 0) === sid);
            if (rr.ok && (String(row?.statusLabel || "").toUpperCase() === "FINALIZED" || Number(row?.statusId || 0) === 4)) {
              const doneAt = Date.now();
              setTradingSessions((prev) => (Array.isArray(prev) ? prev : []).map((local) => {
                const lm = local?.meta && typeof local.meta === "object" ? local.meta : {};
                const localSid = Number(local?.onchainSessionId || local?.onchain_session_id || local?.coreVaultSessionId || lm?.onchain_session_id || lm?.coreVaultSessionId || 0) || 0;
                const shouldClose = localSid === sid || ((Array.isArray(prev) ? prev : []).filter((x) => !["STOPPED", "RELEASED", "CLOSED", "EXPIRED"].includes(String(x?.status || "").toUpperCase())).length === 1);
                return shouldClose ? { ...local, status: "STOPPED", active: false, stoppedAt: doneAt, closedAt: doneAt, onchainSessionId: sid, onchain_session_id: sid } : local;
              }));
              setTradingExecutionQueue((prev) => (Array.isArray(prev) ? prev : []).filter((slot) => {
                const ssid = Number(slot?.onchainSessionId || slot?.onchain_session_id || slot?.coreVaultSessionId || slot?.meta?.onchain_session_id || 0) || 0;
                return ssid > 0 ? ssid !== sid : false;
              }));
              setActiveTradingSessionId("");
              setTradingSessionStatus("PREPARED");
              setTradingSessionUpdatedTs(doneAt);
              await refreshNexusBackendState().catch(() => null);
              try { await _inspectCoreVaultSessions(); } catch {}
              return;
            }
          } catch {}
          if (attempt < 40) window.setTimeout(() => pollTraderFinalized(attempt + 1), attempt < 8 ? 1500 : 3000);
        };
        window.setTimeout(() => pollTraderFinalized(0), 900);
      }
      return true;
    } catch (err) {
      setOwnerAdminMsg(`Exact session recovery failed: ${err?.message || err}`);
      return false;
    } finally {
      setExactOrphanRecoveryBusyKey("");
    }
  };

  const _recoverStaleCoreVaultReservation = async (sessionId, engine, chainId = 1, vault = CORE_VAULT_ETH_ADDRESS, chain = "ETH") => {
    const normalizedEngine = String(engine || "").toUpperCase();
    if (normalizedEngine === "GRID") {
      setOwnerAdminMsg("Grid has no CoreVault session recovery here (price target / max-loss only).");
      return;
    }
    if (!canOpenSystemInfo || ownerAdminBusy || !sessionId || !CORE_VAULT_SESSION_ENGINES.includes(normalizedEngine)) return;
    const engineLabel = _coreVaultEngineLabel(normalizedEngine);
    setOwnerAdminBusy(`Stale ${normalizedEngine} recovery ${sessionId}`);
    setOwnerAdminMsg(`Starting ${engineLabel} recovery for CoreVault session #${sessionId}...`);
    try {
      const res = await fetch(`${API_BASE}/api/nexus/live-reservation/recover`, {
        method: "POST", cache: "no-store", credentials: "include", headers: _authHeaders(),
        body: JSON.stringify({ engine: normalizedEngine, chainId: Number(chainId), vault, chain, execute: true, sessionId: Number(sessionId) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status !== 202 || !data?.jobId) throw new Error(data?.error || `Recovery start failed (${res.status})`);
      const jobId = data.jobId;
      const jobKey = `${normalizedEngine}:${sessionId}`;
      setCoreVaultRecoveryJobs((prev) => ({ ...prev, [jobKey]: data.job || { job_id: jobId, job_status: "QUEUED", step: 0, step_label: "QUEUED", engine: normalizedEngine } }));
      const deadline = Date.now() + 7 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const q = new URLSearchParams({ wallet: footerWallet, wallet_address: footerWallet, jobId, engine: normalizedEngine });
        const poll = await fetch(`${API_BASE}/api/nexus/live-reservation/recover?${q.toString()}`, { cache: "no-store", credentials: "include", headers: _authHeaders() });
        const pd = await poll.json().catch(() => ({}));
        if (!poll.ok || !pd?.job) throw new Error(pd?.error || `Recovery status failed (${poll.status})`);
        const job = pd.job;
        setCoreVaultRecoveryJobs((prev) => ({ ...prev, [jobKey]: { ...job, engine: normalizedEngine } }));
        const st = String(job?.job_status || "").toUpperCase();
        setOwnerAdminMsg(`${engineLabel} session #${sessionId}: ${job?.step_label || st} · receipt ${job?.receipt_status || "—"}`);
        if (st === "SUCCESS") {
          setOwnerAdminMsg(`${engineLabel} session #${sessionId} finalized and confirmed on-chain.`);
          await _refreshOwnerSystemInfoNow();
          await _inspectCoreVaultSessions();
          break;
        }
        if (st === "FAILED") throw new Error(job?.last_error || "Recovery failed");
      }
    } catch (err) {
      setOwnerAdminMsg(`${_coreVaultEngineLabel(normalizedEngine)} session #${sessionId} recovery failed: ${err?.message || err}`);
    } finally { setOwnerAdminBusy(""); }
  };

  const _refreshOwnerSystemInfoNow = async () => {
    if (!canOpenSystemInfo) return;
    const walletParam = footerWallet ? `?wallet=${encodeURIComponent(footerWallet)}&wallet_address=${encodeURIComponent(footerWallet)}` : "";
    try {
      const res = await fetch(`${API_BASE}/api/nexus/system-info-owner-panel${walletParam}`, { cache: "no-store", credentials: "include", headers: _authHeaders() });
      const data = await res.json().catch(() => null);
      if (res.ok && data) setSystemInfoStatus(stripGateObjects(data));
    } catch {}
  };

  const _scanEvmRegistry = async () => {
    setEvmRegistryBusy("scan"); setEvmRegistryMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/nexus/evm-token-registry/scan`, { method: "POST", credentials: "include", headers: _authHeaders(), body: "{}" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setEvmRegistryMsg(`Scan complete: ${data?.registry?.counts?.total ?? 0} EVM token contracts tracked.`);
      await _refreshOwnerSystemInfoNow();
    } catch (e) { setEvmRegistryMsg(`Scan failed: ${String(e?.message || e)}`); }
    finally { setEvmRegistryBusy(""); }
  };

  const _decideEvmToken = async (token, decision) => {
    const key = `${decision}-${token?.chain_id}-${token?.token_address}`;
    setEvmRegistryBusy(key); setEvmRegistryMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/nexus/evm-token-registry/decision`, { method: "POST", credentials: "include", headers: _authHeaders(), body: JSON.stringify({ chainId: token?.chain_id, tokenAddress: token?.token_address, decision }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setEvmRegistryMsg(decision === "APPROVE" ? `${token?.symbol || "Token"} approved centrally. On-chain CoreVault configuration is still required before live use.` : `${token?.symbol || "Token"}: ${decision.toLowerCase()} saved.`);
      await _refreshOwnerSystemInfoNow();
    } catch (e) { setEvmRegistryMsg(`Action failed: ${String(e?.message || e)}`); }
    finally { setEvmRegistryBusy(""); }
  };

  const _sendSystemSetupTx = async (kind) => {
    if (kind !== "limit") return;
    const rd = systemInfoStatus?.liveExecutionReadiness || {};
    const vault = String(rd?.vault || ""); const usdc = String(rd?.token || ""); const userWallet = String(rd?.userWallet || footerWallet || "");
    if (!/^0x[0-9a-fA-F]{40}$/.test(vault) || !/^0x[0-9a-fA-F]{40}$/.test(usdc) || !/^0x[0-9a-fA-F]{40}$/.test(userWallet)) { setLiveSetupMsg("Vault setup is incomplete."); return; }
    const wordAddress = (a) => String(a).toLowerCase().replace(/^0x/, "").padStart(64, "0");
    const wordUint = (v) => BigInt(v).toString(16).padStart(64, "0");
    const data = "0xddc68a3e" + wordAddress(userWallet) + wordAddress(usdc) + wordUint(1000000);
    setLiveSetupBusy("limit"); setLiveSetupMsg("");
    try {
      const target = _primaryEmbeddedPrivyWallet(); const provider = await target.getEthereumProvider();
      try { await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x1" }] }); } catch {}
      const txHash = await provider.request({ method: "eth_sendTransaction", params: [{ from: userWallet, to: vault, value: "0x0", data }] });
      setLiveSetupMsg(`1 USDC self-limit submitted: ${txHash}`); setTimeout(() => window.location.reload(), 5000);
    } catch (e) { setLiveSetupMsg(String(e?.message || e || "Limit transaction failed.")); }
    finally { setLiveSetupBusy(""); }
  };

  const _startLiveExecutorTest = async () => {
    setLiveSetupBusy("live-test"); setLiveSetupMsg("");
    try {
      const response = await fetch(`${API_BASE}/api/nexus/privy-trading/test/start`, { method: "POST", credentials: "include", headers: _authHeaders(), body: "{}" });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error((result?.blockers || []).join(", ") || result?.error || `HTTP ${response.status}`);
      setLiveSetupMsg(`Delegated 1 USDC Trader test queued: ${result.jobId}`); setTimeout(() => window.location.reload(), 5000);
    } catch (error) { setLiveSetupMsg(`Live test blocked: ${String(error?.message || error || "unknown error")}`); }
    finally { setLiveSetupBusy(""); }
  };

  const _hexAddressFromCall = (raw) => {
    const h = String(raw || "").replace(/^0x/, "");
    return h.length >= 40 ? `0x${h.slice(-40)}`.toLowerCase() : "";
  };

  const _readCoreVaultOwner = async (provider) => {
    const ownerRaw = await provider.request({
      method: "eth_call",
      params: [{ to: CORE_VAULT_ETH_ADDRESS, data: "0x8da5cb5b" }, "latest"],
    });
    return _hexAddressFromCall(ownerRaw);
  };

  const _selectedOwnerAdminProvider = async ({ requestAccounts = true } = {}) => {
    const mode = String(ownerAdminWalletMode || "METAMASK").toUpperCase();
    if (mode === "METAMASK") {
      const provider = typeof window !== "undefined" ? window.ethereum : null;
      if (!provider?.request) throw new Error("MetaMask is not installed or not available.");
      try { await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x1" }] }); } catch (switchError) {
        if (String(switchError?.code) !== "4001") throw switchError;
      }
      const method = requestAccounts ? "eth_requestAccounts" : "eth_accounts";
      const accounts = await provider.request({ method });
      const account = String(accounts?.[0] || "").toLowerCase();
      if (!account) throw new Error("Connect the MetaMask owner wallet first.");
      return { provider, label: "MetaMask owner wallet", account, mode };
    }

    const embedded = _primaryEmbeddedPrivyWallet();
    if (!embedded?.getEthereumProvider) throw new Error("Privy embedded wallet is not available.");
    const provider = await embedded.getEthereumProvider();
    try { await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x1" }] }); } catch {}
    const method = requestAccounts ? "eth_requestAccounts" : "eth_accounts";
    const accounts = await provider.request({ method });
    const account = String(accounts?.[0] || embedded?.address || "").toLowerCase();
    if (!account) throw new Error("Connect the Privy wallet first.");
    return { provider, label: "Privy owner wallet", account, mode };
  };

  const _ownerAdminProvider = async () => {
    const signer = await _selectedOwnerAdminProvider({ requestAccounts: true });
    const owner = await _readCoreVaultOwner(signer.provider);
    if (!owner) throw new Error("CoreVault owner address could not be read.");
    if (signer.account !== owner) {
      throw new Error(`Selected ${signer.label} is not the CoreVault owner. Owner: ${owner}. Connected: ${signer.account}.`);
    }
    return { ...signer, owner };
  };

  const _refreshOwnerAdminWalletStatus = useCallback(async () => {
    if (!showSystemInfo || !canOpenSystemInfo) return;
    setOwnerAdminWalletStatus((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let metamask = "";
      let privy = "";
      if (typeof window !== "undefined" && window.ethereum?.request) {
        try { metamask = String((await window.ethereum.request({ method: "eth_accounts" }))?.[0] || "").toLowerCase(); } catch {}
      }
      const embedded = _primaryEmbeddedPrivyWallet();
      privy = String(embedded?.address || "").toLowerCase();
      const selectedSigner = await _selectedOwnerAdminProvider({ requestAccounts: false });
      const owner = await _readCoreVaultOwner(selectedSigner.provider);
      setOwnerAdminWalletStatus({ owner, selected: selectedSigner.account, metamask, privy, matches: !!owner && selectedSigner.account === owner, loading: false, error: "" });
    } catch (error) {
      setOwnerAdminWalletStatus((prev) => ({ ...prev, selected: ownerAdminWalletMode === "METAMASK" ? prev.metamask : prev.privy, matches: false, loading: false, error: String(error?.message || error || "Wallet check failed") }));
    }
  }, [showSystemInfo, canOpenSystemInfo, ownerAdminWalletMode, systemWallets]);

  const _connectSelectedOwnerAdminWallet = async () => {
    setOwnerAdminWalletStatus((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const signer = await _selectedOwnerAdminProvider({ requestAccounts: true });
      const owner = await _readCoreVaultOwner(signer.provider);
      let metamask = ownerAdminWalletStatus.metamask;
      let privy = ownerAdminWalletStatus.privy;
      if (signer.mode === "METAMASK") metamask = signer.account;
      if (signer.mode === "PRIVY") privy = signer.account;
      setOwnerAdminWalletStatus({ owner, selected: signer.account, metamask, privy, matches: !!owner && signer.account === owner, loading: false, error: "" });
      setOwnerAdminMsg(signer.account === owner ? `${signer.label} connected and verified as CoreVault owner.` : `${signer.label} connected, but it is not the CoreVault owner.`);
    } catch (error) {
      setOwnerAdminWalletStatus((prev) => ({ ...prev, matches: false, loading: false, error: String(error?.message || error || "Wallet connection failed") }));
    }
  };

  useEffect(() => {
    try { window.localStorage.setItem("nexus_owner_admin_wallet_mode_v1", ownerAdminWalletMode); } catch {}
    _refreshOwnerAdminWalletStatus();
  }, [ownerAdminWalletMode, _refreshOwnerAdminWalletStatus]);

  const _sendOwnerAdminTx = async (data, busyName) => {
    setOwnerAdminBusy(busyName); setOwnerAdminMsg("");
    try {
      if (!/^0x[0-9a-fA-F]+$/.test(String(data || ""))) throw new Error("Backend returned invalid transaction data.");
      const signer = await _ownerAdminProvider();
      const txHash = await signer.provider.request({ method: "eth_sendTransaction", params: [{ from: signer.account, to: CORE_VAULT_ETH_ADDRESS, value: "0x0", data }] });
      setOwnerAdminMsg(`${busyName} submitted through ${signer.label}: ${txHash}`);
      return txHash;
    } catch (e) {
      setOwnerAdminMsg(`${busyName} failed: ${String(e?.message || e || "unknown error")}`);
      return "";
    } finally { setOwnerAdminBusy(""); }
  };

  const _prepareOwnerAdminAction = async (payload) => {
    const response = await fetch(`${API_BASE}/api/nexus/core-vault/admin/prepare-action`, {
      method: "POST", credentials: "include", headers: _authHeaders(), body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.status !== "ok") throw new Error(result?.error || `Admin preparation failed (HTTP ${response.status}).`);
    return result;
  };

  const _tokenAdminPayload = () => ({
    action: "token", token: String(tokenAdmin.token || "").trim(),
    cfg: {
      configured: !!tokenAdmin.configured, depositEnabled: !!tokenAdmin.depositEnabled,
      withdrawEnabled: !!tokenAdmin.withdrawEnabled, executionEnabled: !!tokenAdmin.executionEnabled,
      paymentEnabled: !!tokenAdmin.paymentEnabled, settlementToken: !!tokenAdmin.settlementToken,
      decimals: Number(tokenAdmin.decimals || 0), maxSingleDeposit: String(tokenAdmin.maxSingleDeposit || "0"),
      maxSessionBudget: String(tokenAdmin.maxSessionBudget || "0"), profitThreshold: String(tokenAdmin.profitThreshold || "0"),
    },
  });

  // Restore the timelock from the contract itself. This survives frontend deploys,
  // browser storage clearing, page reloads and use on another owner device.
  useEffect(() => {
    let cancelled = false;
    const restoreTokenTimelockFromChain = async () => {
      try {
        const prepared = await _prepareOwnerAdminAction(_tokenAdminPayload());
        const actionHash = String(prepared?.actionHash || "");
        if (!/^0x[0-9a-fA-F]{64}$/.test(actionHash)) return;
        if (!cancelled) setOwnerAdminHash(actionHash);

        const embedded = _primaryEmbeddedPrivyWallet();
        const provider = embedded?.getEthereumProvider ? await embedded.getEthereumProvider() : null;
        if (!provider) return;

        // pendingAction(bytes32) selector = 0xc8b53038.
        const data = `0xc8b53038${actionHash.slice(2).padStart(64, "0")}`;
        const raw = String(await provider.request({
          method: "eth_call",
          params: [{ to: CORE_VAULT_ETH_ADDRESS, data }, "latest"],
        }) || "").replace(/^0x/, "");
        if (raw.length < 128) return;

        const executeAfter = Number(BigInt(`0x${raw.slice(0, 64)}`));
        const exists = BigInt(`0x${raw.slice(64, 128)}`) !== 0n;
        if (cancelled) return;

        if (exists && executeAfter > 0) {
          setTokenExecuteAfter(executeAfter);
          try {
            window.localStorage.setItem("nexus_owner_admin_token_pending_v1", JSON.stringify({
              actionHash,
              executeAfter,
              token: tokenAdmin.token,
            }));
          } catch {}
        } else {
          setTokenExecuteAfter(0);
          try { window.localStorage.removeItem("nexus_owner_admin_token_pending_v1"); } catch {}
        }
      } catch {
        // The local copy remains a fallback when wallet RPC access is unavailable.
      }
    };
    restoreTokenTimelockFromChain();
    return () => { cancelled = true; };
  }, []);

  const _scheduleToken = async () => {
    setOwnerAdminBusy("Token schedule"); setOwnerAdminMsg("");
    try {
      const prepared = await _prepareOwnerAdminAction(_tokenAdminPayload());
      setOwnerAdminHash(prepared.actionHash || "");
      const txHash = await _sendOwnerAdminTx(prepared.scheduleCalldata, "Token schedule");
      if (txHash && prepared.executeAfter) {
        const executeAfter = Number(prepared.executeAfter);
        setTokenExecuteAfter(executeAfter);
        try { window.localStorage.setItem("nexus_owner_admin_token_pending_v1", JSON.stringify({ actionHash: prepared.actionHash || "", executeAfter, token: tokenAdmin.token })); } catch {}
        setOwnerAdminMsg((prev) => `${prev}${prev ? " | " : ""}Execute after ${new Date(executeAfter * 1000).toLocaleString()}.`);
      }
    } catch (e) { setOwnerAdminMsg(`Token schedule failed: ${String(e?.message || e)}`); }
    finally { setOwnerAdminBusy(""); }
  };

  const _executeToken = async () => {
    setOwnerAdminBusy("Token configuration"); setOwnerAdminMsg("");
    try {
      const prepared = await _prepareOwnerAdminAction(_tokenAdminPayload());
      setOwnerAdminHash(prepared.actionHash || "");
      const txHash = await _sendOwnerAdminTx(prepared.executeCalldata, "Token configuration");
      if (txHash) {
        setTokenExecuteAfter(0);
        try { window.localStorage.removeItem("nexus_owner_admin_token_pending_v1"); } catch {}
      }
    } catch (e) { setOwnerAdminMsg(`Token configuration failed: ${String(e?.message || e)}`); }
    finally { setOwnerAdminBusy(""); }
  };

  const _routeAdminPayload = () => ({
    action: "route", routeId: String(routeAdmin.routeId || "").trim(),
    cfg: {
      enabled: !!routeAdmin.enabled, kind: Number(routeAdmin.kind || 0), target: String(routeAdmin.target || "").trim(),
      oracle: String(routeAdmin.oracle || "").trim(), tokenIn: String(routeAdmin.tokenIn || "").trim(),
      tokenOut: String(routeAdmin.tokenOut || "").trim(), selector: String(routeAdmin.selector || "").trim(),
    },
  });

  const _scheduleRoute = async () => {
    setOwnerAdminBusy("Route schedule"); setOwnerAdminMsg("");
    try {
      const prepared = await _prepareOwnerAdminAction(_routeAdminPayload());
      setOwnerAdminHash(prepared.actionHash || "");
      const txHash = await _sendOwnerAdminTx(prepared.scheduleCalldata, "Route schedule");
      if (txHash && prepared.executeAfter) {
        const executeAfter = Number(prepared.executeAfter);
        setRouteExecuteAfter(executeAfter);
        try {
          window.localStorage.setItem("nexus_owner_admin_route_pending_v1", JSON.stringify({
            actionHash: prepared.actionHash || "",
            executeAfter,
            routeId: routeAdmin.routeId,
            route: {
              routeId: String(routeAdmin.routeId || "").trim(),
              enabled: !!routeAdmin.enabled,
              kind: String(routeAdmin.kind ?? "0"),
              target: String(routeAdmin.target || "").trim(),
              oracle: String(routeAdmin.oracle || ZERO_ADDRESS).trim(),
              tokenIn: String(routeAdmin.tokenIn || "").trim(),
              tokenOut: String(routeAdmin.tokenOut || "").trim(),
              selector: String(routeAdmin.selector || "").trim(),
            },
          }));
        } catch {}
        setOwnerAdminMsg((prev) => `${prev}${prev ? " | " : ""}Execute after ${new Date(executeAfter * 1000).toLocaleString()}.`);
      }
    } catch (e) { setOwnerAdminMsg(`Route schedule failed: ${String(e?.message || e)}`); }
    finally { setOwnerAdminBusy(""); }
  };

  const _executeRoute = async () => {
    setOwnerAdminBusy("Route configuration"); setOwnerAdminMsg("");
    try {
      const prepared = await _prepareOwnerAdminAction(_routeAdminPayload());
      setOwnerAdminHash(prepared.actionHash || "");
      const txHash = await _sendOwnerAdminTx(prepared.executeCalldata, "Route configuration");
      if (txHash) {
        setRouteExecuteAfter(0);
        try { window.localStorage.removeItem("nexus_owner_admin_route_pending_v1"); } catch {}
      }
    } catch (e) { setOwnerAdminMsg(`Route configuration failed: ${String(e?.message || e)}`); }
    finally { setOwnerAdminBusy(""); }
  };

  const _setVaultAddressValue = async (method, value, label) => {
    const actionByMethod = { setGuardian: "guardian", setRevenueWallet: "revenueWallet", setLiquidityDestination: "liquidityDestination" };
    const action = actionByMethod[method];
    if (!action) { setOwnerAdminMsg(`Unsupported owner action: ${method}`); return; }
    setOwnerAdminBusy(label); setOwnerAdminMsg("");
    try {
      const prepared = await _prepareOwnerAdminAction({ action, value: String(value || "").trim() });
      await _sendOwnerAdminTx(prepared.executeCalldata, label);
    } catch (e) { setOwnerAdminMsg(`${label} failed: ${String(e?.message || e)}`); }
    finally { setOwnerAdminBusy(""); }
  };

  const _openEngineDiagnostics = async (engineName = "ALL", engineState = {}) => {
    const engine = String(engineName || "ALL").toUpperCase();
    setEngineDiagnosticsBusy(engine);
    setEngineDiagnosticsError("");
    setEngineDiagnostics({ engine, reports: [], overall: "LOADING" });
    try {
      const states = Array.isArray(engineState?.session_states) ? engineState.session_states : [];
      const selected = states.find((s) => String(s?.status || "").toUpperCase() === "ACTIVE") || states[0] || null;
      const chainMap = { ETH: 1, BNB: 56, POL: 137, MATIC: 137, BASE: 8453, ARB: 42161, ARBITRUM: 42161, OP: 10, OPTIMISM: 10, AVAX: 43114, AVALANCHE: 43114 };
      const chainId = Number(selected?.chainId || selected?.chain_id || chainMap[String(selected?.chain || "").toUpperCase()] || 0);
      const sessionId = selected?.sessionId ?? selected?.session_id ?? "";
      const params = new URLSearchParams({ engine });
      const ownerWallet = String(footerWallet || ownerAdminWalletStatus?.selected || "").trim();
      if (ownerWallet) params.set("wallet", ownerWallet);
      if (chainId > 0) params.set("chain_id", String(chainId));
      if (sessionId !== "" && sessionId != null) params.set("session_id", String(sessionId));
      const res = await fetch(`${API_BASE}/api/nexus/system-info/evm-diagnostics?${params.toString()}`, {
        cache: "no-store",
        credentials: "include",
        headers: _authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || `Diagnostics failed (${res.status})`);
      setEngineDiagnostics(stripGateObjects(data));
    } catch (error) {
      setEngineDiagnosticsError(String(error?.message || error || "Diagnostics failed"));
      setEngineDiagnostics((prev) => ({ ...(prev || {}), overall: "ERROR" }));
    } finally {
      setEngineDiagnosticsBusy("");
    }
  };

  const _diagnosticTone = (ok, status = "") => {
    if (ok) return { color: "#8dffd0", border: "rgba(68,255,180,.25)", bg: "rgba(0,255,140,.045)", icon: "✅" };
    if (String(status).toUpperCase() === "ZERO" || String(status).toUpperCase() === "UNKNOWN") return { color: "#ffe08a", border: "rgba(255,224,138,.28)", bg: "rgba(255,224,138,.045)", icon: "⚠️" };
    return { color: "#ffb4b4", border: "rgba(255,80,80,.34)", bg: "rgba(120,0,0,.12)", icon: "❌" };
  };

  const normalizeStatusRows = (value) => {
    if (!value || typeof value !== "object") return [];
    if (Array.isArray(value)) return value;
    const candidates = [
      value.modules,
      value.module_status,
      value.moduleStatus,
      value.status,
      value.data?.modules,
      value.data?.module_status,
      value.data?.status,
      value.owner_panel?.modules,
      value.ownerPanel?.modules,
      value.system_info?.modules,
      value.systemInfo?.modules,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
      if (c && typeof c === "object") return Object.entries(c).map(([name, row]) => ({ name, ...(row && typeof row === "object" ? row : { status: row }) }));
    }
    return [];
  };

  const statusTone = (statusLike) => {
    const s = String(statusLike || "").toUpperCase();
    if (s.includes("READY") || s.includes("RUNNING") || s.includes("ONLINE") || s.includes("ALLOWED")) return { dot: "🟢", color: "#8dffd0", border: "rgba(68,255,180,0.26)", bg: "rgba(0,255,140,0.055)" };
    if (s.includes("PREVIEW") || s.includes("PREP") || s.includes("DISABLED") || s.includes("ONLY")) return { dot: "🟡", color: "#ffe08a", border: "rgba(255,224,138,0.24)", bg: "rgba(255,224,138,0.055)" };
    if (s.includes("ERROR") || s.includes("MISSING") || s.includes("STALLED") || s.includes("FAILED")) return { dot: "🔴", color: "#ffb4b4", border: "rgba(255,80,80,0.32)", bg: "rgba(120,0,0,0.14)" };
    return { dot: "⚪", color: "rgba(232,242,240,.80)", border: "rgba(255,255,255,0.12)", bg: "rgba(255,255,255,0.035)" };
  };

  const pickSystemInfoValue = (obj, keys, fallback = "unknown") => {
    for (const key of keys) {
      const v = key.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return fallback;
  };

  const defaultModuleRows = [
    { name: "Trading Logic", status: "READY", note: exitMode },
    { name: "Decision Log", status: buildInfo?.decision_log || "READY", note: buildInfo?.decision_log || "DECISION_LOG_V1" },
    { name: "Stable Capital", status: buildInfo?.default_capital_state ? "READY" : "UNKNOWN", note: buildInfo?.default_capital_state || "USDC_USDT" },
    { name: "Asset Router", status: buildInfo?.asset_router ? "READY" : "UNKNOWN", note: buildInfo?.asset_router || "ASSET_ABSTRACTION_ROUTER_V1" },
    { name: "NKR Core", status: pickSystemInfoValue(shadowReadiness || systemInfoStatus || buildInfo, ["nkr.status", "nkr_core", "nkrCore", "nkr_status"], "READY / PREVIEW") },
    { name: "Withdraw Quote", status: pickSystemInfoValue(systemInfoStatus || buildInfo, ["withdraw.status", "withdraw_quote", "withdrawQuote", "withdraw_status"], "PREVIEW ONLY") },
    { name: "Vault Core", status: pickSystemInfoValue(systemInfoStatus || buildInfo, ["vault.status", "vault_core", "vaultCore", "vault_status"], "PREP ONLY") },
    { name: "Live Execution", status: pickSystemInfoValue(systemInfoStatus || shadowReadiness || buildInfo, ["live_execution", "liveExecution", "guards.live_execution"], "DISABLED") },
    { name: "Private Keys", status: pickSystemInfoValue(systemInfoStatus || buildInfo, ["private_keys", "privateKeys", "guards.private_keys"], "NOT IN BACKEND") },
  ];

  const moduleRows = normalizeStatusRows(systemInfoStatus).length ? normalizeStatusRows(systemInfoStatus) : defaultModuleRows;

  return (
    <>
      <AppInner coreVaultSessionPreview={coreVaultSessionPreview} coreVaultRecoveryJobs={coreVaultRecoveryJobs} refreshOnchainSessionsSilent={_refreshOnchainSessionsSilent} onRecoverExactCoreVaultSession={_recoverExactCoreVaultSession} exactOrphanRecoveryBusyKey={exactOrphanRecoveryBusyKey} />

      <div className="nexus-footer-left">
        {canOpenSystemInfo && (
          <button
            type="button"
            className="nexus-build-info"
            title="Open Nexus system/build diagnostics"
            onClick={() => setShowSystemInfo(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              border: "1px solid rgba(68, 255, 180, 0.28)",
              background: "rgba(3, 24, 19, 0.72)",
              color: backendOnline ? "#8dffd0" : "#ffb4b4",
              borderRadius: 999,
              padding: "4px 9px",
              marginBottom: 4,
              fontSize: 11,
              lineHeight: 1.15,
              fontWeight: 800,
              textAlign: "left",
              cursor: "pointer",
              maxWidth: 160,
              whiteSpace: "nowrap",
            }}
          >
            {backendOnline ? "🟢" : "🔴"} System Info
          </button>
        )}

        <div className="nexus-footer-copy">© 2026 Nexus Analyt</div>

        <button
          type="button"
          className="nexus-disclaimer-btn"
          onClick={() => setShowDisclaimer(true)}
        >
          Disclaimer
        </button>

        <div className="nexus-footer-ai">AI-assisted infrastructure</div>
      </div>

      {showSystemInfo && (
        <div
          className="nexus-disclaimer-overlay"
          onClick={() => setShowSystemInfo(false)}
        >
          <div
            className="nexus-disclaimer-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(1540px, calc(100vw - 24px))", maxWidth: 1540, height: "min(88vh, 940px)" }}
          >
            <style>{`
              @media (max-width: 1100px) {
                .nexus-system-dashboard-grid > * { grid-column: 1 / -1 !important; }
              }
            `}</style>
            <div className="nexus-disclaimer-title">NEXUS SYSTEM INFO</div>

            <div className="nexus-disclaimer-content">
              <div className="nexus-system-dashboard-grid" style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 12, fontSize: 13, alignItems: "start" }}>
                <div style={{ gridColumn: "span 4", minWidth: 0 }}>
                  <b>Connection</b>
                  <br />
                  Backend: {backendOnline ? "ONLINE 🟢" : "OFFLINE / NOT LOADED 🔴"}
                  <br />
                  API Base: {API_BASE || "same origin"}
                </div>

                <div style={{ gridColumn: "span 4", minWidth: 0 }}>
                  <b>Build Proof</b>
                  <br />
                  Backend Build: {backendBuild}
                  <br />
                  Frontend Build: {FRONTEND_BUILD_ID}
                  <br />
                  Frontend Target: {frontendTarget}
                  <br />
                  Render Commit: {renderCommit}
                  <br />
                  Backend Timestamp: {buildTs}
                </div>

                <div style={{ gridColumn: "span 4", minWidth: 0 }}>
                  <b>Engine Modes</b>
                  <br />
                  Strategist: {strategistBuild}
                  <br />
                  Shadow: {shadowBuild}
                  <br />
                  Entry: {entryMode}
                  <br />
                  Promotion: {promotionMode}
                  <br />
                  Exit: {exitMode}
                </div>

                <details style={{
                  gridColumn: "span 4",
                  border: `1px solid ${shadowStalled ? "rgba(255,80,80,0.55)" : "rgba(68,255,180,0.22)"}`,
                  borderRadius: 10,
                  padding: 10,
                  background: shadowStalled ? "rgba(120,0,0,0.18)" : "rgba(0,255,140,0.055)",
                }}>
                  <summary style={{ cursor: "pointer", fontWeight: 900 }}>Shadow Runtime Health · {shadowRuntimeStatus} {shadowHealth?.running ? "🟢" : "⚪"}</summary>
                  <div style={{ marginTop: 8 }}>
                    Last Tick: {shadowLastTick}
                    <br />
                    Tick Age: {shadowTickAge == null ? "unknown" : `${shadowTickAge}s`} {shadowStalled ? "⚠ STALLED" : ""}
                    <br />
                    Tick Count: {shadowTickCount}
                    <br />
                    Process Tick: {shadowHealth?.process_tick_count ?? "?"} / {shadowHealth?.process_tick_source || "?"}
                  </div>
                </details>


                <details style={{
                  gridColumn: "span 12",
                  border: "1px solid rgba(64,196,255,0.28)",
                  borderRadius: 10,
                  padding: 10,
                  background: "rgba(64,196,255,0.045)",
                }} open>
                  <summary style={{ cursor: "pointer", fontWeight: 900 }}>Live Engine Runtime Health · NKR / Grid / Trader</summary>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
                    {["NKR", "GRID", "TRADER"].map((engineName) => {
                      const e = systemInfoStatus?.engineRuntimeStatus?.engines?.[engineName] || {};
                      const lastTickText = e?.last_tick_ts ? new Date(Number(e.last_tick_ts) * 1000).toLocaleString() : "not seen";
                      return <div
                        key={engineName}
                        role="button"
                        tabIndex={0}
                        title={`Open ${engineName} / EVM diagnostics`}
                        onClick={() => _openEngineDiagnostics(engineName, e)}
                        onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") _openEngineDiagnostics(engineName, e); }}
                        style={{ border: `1px solid ${e?.stalled || e?.status === "error" ? "rgba(255,80,80,.5)" : "rgba(68,255,180,.2)"}`, borderRadius: 8, padding: 9, cursor: "pointer", position: "relative" }}
                      >
                        <div style={{ position: "absolute", top: 7, right: 8, fontSize: 10, opacity: .7 }}>Diagnose ↗</div>
                        <div style={{ fontWeight: 950, marginBottom: 5 }}>{engineName} · {String(e?.status || "idle").toUpperCase()} {e?.status === "running" ? "🟢" : "⚪"}</div>
                        <div style={{ fontSize: 12, lineHeight: 1.55 }}>
                          {engineName === "NKR" && <>
                            Worker Thread: {e?.worker_thread_alive ? "ALIVE 🟢" : "DEAD 🔴"}{e?.worker_thread_name ? ` · ${e.worker_thread_name}` : ""}<br />
                            Worker Heartbeat: {e?.worker_heartbeat_ts ? new Date(Number(e.worker_heartbeat_ts) * 1000).toLocaleString() : "not seen"}<br />
                            Heartbeat Age: {e?.worker_heartbeat_age_sec == null ? "unknown" : `${e.worker_heartbeat_age_sec}s`} {e?.worker_heartbeat_stalled ? "⚠ STALLED" : ""}<br />
                            Worker Stage: {e?.worker_last_stage || "unknown"}<br />
                            Worker Loop Count: {e?.worker_loop_count ?? 0}<br />
                            Cycle Started: {e?.worker_cycle_started_ts ? new Date(Number(e.worker_cycle_started_ts) * 1000).toLocaleString() : "not seen"}<br />
                            Cycle Completed: {e?.worker_cycle_completed_ts ? new Date(Number(e.worker_cycle_completed_ts) * 1000).toLocaleString() : "not seen"}<br />
                            Session Updated: {e?.session_state_updated_ts ? new Date(Number(e.session_state_updated_ts) * 1000).toLocaleString() : "not seen"}<br />
                          </>}
                          {["NKR", "TRADER"].includes(engineName) && <>
                            Working chain(s): {Array.isArray(e?.working_chains) && e.working_chains.length ? e.working_chains.join(", ") : "—"}<br />
                            Paused chain(s): {Array.isArray(e?.paused_chains) && e.paused_chains.length ? e.paused_chains.join(", ") : "—"}<br />
                            Session states: {Array.isArray(e?.session_states) && e.session_states.length ? e.session_states.map((s) => `${s?.chain || "?"} #${s?.sessionId || "?"} ${s?.status || "?"}`).join(" · ") : "—"}<br />
                            <span style={{ opacity: 0.65 }}>────────────</span><br />
                          </>}
                          Last Completed Tick: {lastTickText}<br />
                          Completed Tick Age: {e?.tick_age_sec == null ? "unknown" : `${e.tick_age_sec}s`} {e?.stalled ? "⚠ OLD TICK" : ""}<br />
                          Completed Tick Count: {e?.tick_count ?? 0}<br />
                          Assets scanned: {e?.assets_scanned ?? 0}<br />
                          Tradable assets: {e?.tradable_assets ?? 0}<br />
                          Best candidate: {e?.best_candidate || "—"}<br />
                          Candidate score: {Number(e?.candidate_score || 0).toFixed(1)}<br />
                          Momentum 24h: {Number(e?.candidate_momentum_24h || 0) >= 0 ? "+" : ""}{Number(e?.candidate_momentum_24h || 0).toFixed(2)}%<br />
                          Candidate price: {Number(e?.candidate_price || 0) > 0 ? `$${Number(e.candidate_price).toLocaleString(undefined, { maximumFractionDigits: 8 })}` : "—"}<br />
                          Gate: {e?.gate_status || "—"}<br />
                          Decision: {toUserFacingMessage(e?.decision, "—")}<br />
                          Decision detail: {toUserFacingMessage(e?.decision_detail, "—")}<br />
                          Reason: {toUserFacingMessage(e?.reason, "—")}<br />
                          Active asset: {e?.active_asset || "—"}<br />
                          Position: {e?.position_state || "—"}<br />
                          Position value: {Number(e?.position_value_usd || 0) > 0 ? fmtUsd(Number(e.position_value_usd)) : "—"}<br />
                          Tx: {e?.tx_hash ? `${String(e.tx_hash).slice(0, 10)}…${String(e.tx_hash).slice(-8)}` : "—"}<br />
                          Pending tx: {e?.pending_tx || "—"}<br />
                          Last error: {toUserFacingMessage(e?.last_error, "—")}
                        </div>
                      </div>;
                    })}
                  </div>
                </details>

                {engineDiagnostics && (
                  <details
                    open
                    style={{
                      gridColumn: "span 12",
                      border: "1px solid rgba(255,224,138,0.30)",
                      borderRadius: 10,
                      padding: 10,
                      background: "rgba(255,224,138,0.035)",
                    }}
                  >
                    <summary style={{ cursor: "pointer", fontWeight: 950 }}>
                      General EVM Diagnostics · {engineDiagnostics?.engine || "ALL"} · {engineDiagnosticsBusy ? "LOADING…" : (engineDiagnostics?.overall || "UNKNOWN")}
                    </summary>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, marginBottom: 8 }}>
                      <button type="button" className="miniBtn" disabled={!!engineDiagnosticsBusy} onClick={(ev) => { ev.stopPropagation(); _openEngineDiagnostics(engineDiagnostics?.engine || "ALL", systemInfoStatus?.engineRuntimeStatus?.engines?.[engineDiagnostics?.engine] || {}); }}>
                        {engineDiagnosticsBusy ? "Checking…" : "Refresh diagnostics"}
                      </button>
                      <button type="button" className="miniBtn" onClick={(ev) => { ev.stopPropagation(); setEngineDiagnostics(null); setEngineDiagnosticsError(""); }}>Close</button>
                      <span className="muted tiny">Read-only: no transaction and no vault change.</span>
                    </div>
                    {engineDiagnosticsError ? <div style={{ color: "#ffb4b4", marginBottom: 8, fontWeight: 800 }}>{engineDiagnosticsError}</div> : null}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: 9 }}>
                      {(engineDiagnostics?.reports || []).map((report) => (
                        <div key={`${report?.chainId}-${report?.engine}`} style={{ border: "1px solid rgba(255,255,255,.12)", borderRadius: 9, padding: 9, background: "rgba(0,0,0,.10)" }}>
                          <div style={{ fontWeight: 950, marginBottom: 6 }}>
                            {report?.chain || report?.chainId} · {report?.engine} {report?.sessionId != null ? `· Session #${report.sessionId}` : ""} · {report?.status}
                          </div>
                          <div className="muted tiny" style={{ wordBreak: "break-all", marginBottom: 6 }}>
                            Vault: {report?.vault || "—"}<br />Router: {report?.router || "—"}<br />Quoter: {report?.quoter || "—"}<br />
                            Settlement: {report?.tokenIn || "—"}<br />Target: {report?.tokenOut || "—"}<br />
                            Session source: {report?.sessionSource || "—"}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 5, marginBottom: 7 }}>
                            <div style={{ border: "1px solid rgba(255,255,255,.10)", borderRadius: 7, padding: 6 }}><div className="muted tiny">Blocker</div><b>{Number(report?.summary?.blockerCount || 0)}</b></div>
                            <div style={{ border: "1px solid rgba(255,255,255,.10)", borderRadius: 7, padding: 6 }}><div className="muted tiny">Warnungen</div><b>{Number(report?.summary?.warningCount || 0)}</b></div>
                            <div style={{ border: "1px solid rgba(255,255,255,.10)", borderRadius: 7, padding: 6 }}><div className="muted tiny">Target</div><b>{report?.summary?.targetSymbol || "—"}</b></div>
                          </div>
                          {report?.rootCause ? <div style={{ border: `1px solid ${report?.rootCause?.status === "READY" ? "rgba(80,255,180,.30)" : "rgba(255,100,100,.38)"}`, borderRadius: 8, padding: 8, marginBottom: 7, background: report?.rootCause?.status === "READY" ? "rgba(0,255,150,.045)" : "rgba(255,70,70,.055)" }}>
                            <div style={{ fontWeight: 950, color: report?.rootCause?.status === "READY" ? "#8dffd0" : "#ffb4b4" }}>Hauptursache · {report?.rootCause?.stage || "—"}</div>
                            <div style={{ fontWeight: 850, marginTop: 3 }}>{report?.rootCause?.title || "—"}</div>
                            <div className="muted tiny" style={{ marginTop: 3, wordBreak: "break-all" }}>
                              Contract: {report?.rootCause?.contract || "—"}<br />Funktion: {report?.rootCause?.function || "—"}
                            </div>
                            {report?.rootCause?.error ? <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.rootCause.error, null, 2)}</pre> : null}
                            {report?.rootCause?.remediation ? <div style={{ marginTop: 5, color: "#ffe08a", fontWeight: 800, fontSize: 11 }}>Korrektur: {report.rootCause.remediation}</div> : null}
                          </div> : null}
                          {Array.isArray(report?.executionTrace) && report.executionTrace.length ? <details open style={{ border: "1px solid rgba(100,220,255,.25)", borderRadius: 8, padding: "6px 8px", marginBottom: 7 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 950, color: "#9de7ff" }}>Vollständiger Ausführungspfad ({report.executionTrace.filter((x) => x?.ok).length}/{report.executionTrace.length})</summary>
                            <div style={{ display: "grid", gap: 5, marginTop: 7 }}>
                              {report.executionTrace.map((step, idx) => <details key={`${step?.stage || "step"}-${idx}`} style={{ border: `1px solid ${step?.ok ? "rgba(80,255,180,.22)" : "rgba(255,90,90,.32)"}`, borderRadius: 7, padding: "5px 7px", background: step?.ok ? "rgba(0,255,150,.025)" : "rgba(255,60,60,.045)" }}>
                                <summary style={{ cursor: "pointer", fontWeight: 850, color: step?.ok ? "#8dffd0" : "#ffb4b4" }}>{step?.ok ? "✅" : "❌"} {idx + 1}. {step?.name} · {step?.stage}</summary>
                                <div className="muted tiny" style={{ marginTop: 4, wordBreak: "break-all" }}>Contract: {step?.contract || "—"}<br />Funktion: {step?.function || "—"}</div>
                                {step?.error ? <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(step.error, null, 2)}</pre> : null}
                                {step?.detail != null ? <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0", opacity: .82 }}>{JSON.stringify(step.detail, null, 2)}</pre> : null}
                                {step?.remediation ? <div style={{ marginTop: 5, color: "#ffe08a", fontSize: 11, fontWeight: 800 }}>Korrektur: {step.remediation}</div> : null}
                              </details>)}
                            </div>
                          </details> : null}
                          {report?.session ? <details style={{ border: "1px solid rgba(255,255,255,.13)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850 }}>Session · On-chain Wahrheit</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.session, null, 2)}</pre>
                          </details> : null}
                          {report?.tradePlan ? <details style={{ border: "1px solid rgba(255,190,90,.22)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850, color: "#ffd28a" }}>Exakter Trade-Plan und alle Preflight-Versuche</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.tradePlan, null, 2)}</pre>
                          </details> : null}
                          {report?.contractMap ? <details style={{ border: "1px solid rgba(180,180,255,.20)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850, color: "#c9c9ff" }}>Contract-Map</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.contractMap, null, 2)}</pre>
                          </details> : null}
                          {(report?.settlementToken || report?.targetToken) ? <details style={{ border: "1px solid rgba(141,232,255,.22)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850, color: "#8de8ff" }}>Token- und Allowance-Details</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify({ settlementToken: report?.settlementToken, targetToken: report?.targetToken }, null, 2)}</pre>
                          </details> : null}
                          {Array.isArray(report?.selectors) && report.selectors.length ? <details style={{ border: "1px solid rgba(187,134,252,.22)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850, color: "#c9a7ff" }}>Router-Selectoren ({report.selectors.length})</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.selectors, null, 2)}</pre>
                          </details> : null}
                          {Array.isArray(report?.feeTiers) && report.feeTiers.length ? <details style={{ border: "1px solid rgba(255,224,138,.22)", borderRadius: 7, padding: "5px 7px", marginBottom: 6 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 850, color: "#ffe08a" }}>Quoter Fee-Tiers ({report.feeTiers.filter((x) => x?.ok).length} gültig)</summary>
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0" }}>{JSON.stringify(report.feeTiers, null, 2)}</pre>
                          </details> : null}
                          {Array.isArray(report?.actionPlan) && report.actionPlan.length ? <div style={{ border: "1px solid rgba(255,107,107,.26)", borderRadius: 8, padding: 7, background: "rgba(255,80,80,.045)", marginBottom: 7 }}>
                            <div style={{ fontWeight: 950, color: "#ffb4b4", marginBottom: 5 }}>Konkreter Aktionsplan</div>
                            {report.actionPlan.slice().sort((a,b) => Number(a?.priority || 99)-Number(b?.priority || 99)).map((step, idx) => <details key={`${step?.code || idx}-${idx}`} style={{ marginTop: 4 }}>
                              <summary style={{ cursor: "pointer", fontWeight: 850 }}>P{step?.priority || "?"} · {step?.title || step?.code}</summary>
                              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "4px 0 0" }}>{JSON.stringify(step?.detail ?? null, null, 2)}</pre>
                            </details>)}
                          </div> : null}
                          <div style={{ display: "grid", gap: 5 }}>
                            {Object.entries(report?.checks || {}).map(([name, check]) => {
                              const tone = _diagnosticTone(!!check?.ok, check?.status);
                              return <details key={name} style={{ border: `1px solid ${tone.border}`, borderRadius: 7, padding: "5px 7px", background: tone.bg }}>
                                <summary style={{ cursor: "pointer", color: tone.color, fontWeight: 850 }}>{tone.icon} {name}: {check?.status || (check?.ok ? "READY" : "FAILED")}</summary>
                                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "5px 0 0", opacity: .82 }}>{JSON.stringify(check?.detail ?? null, null, 2)}</pre>
                              </details>;
                            })}
                          </div>
                          {Array.isArray(report?.blockers) && report.blockers.length ? <div style={{ marginTop: 7, color: "#ffb4b4", fontSize: 11, fontWeight: 800 }}>Blockers: {report.blockers.join(", ")}</div> : <div style={{ marginTop: 7, color: "#8dffd0", fontSize: 11, fontWeight: 800 }}>No blocking read-check found.</div>}
                          {report?.diagnosticError ? <div style={{ marginTop: 6, color: "#ffb4b4", fontSize: 10, wordBreak: "break-all" }}>{report.diagnosticError}</div> : null}
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                <details style={{
                  gridColumn: "span 12",
                  border: "1px solid rgba(64,196,255,0.28)",
                  borderRadius: 10,
                  padding: 10,
                  background: "rgba(64,196,255,0.045)",
                }}>
                  <summary style={{ cursor: "pointer", fontWeight: 950 }}>NKR Session & Strategist Audit · {nkrSessionAudit?.status ? String(nkrSessionAudit.status).toUpperCase() : "NOT RUN"}</summary>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, marginBottom: 8 }}>
                    <button type="button" className="miniBtn" disabled={nkrSessionAuditBusy} onClick={(ev) => { ev.stopPropagation(); _loadNkrSessionAudit(); }}>
                      {nkrSessionAuditBusy ? "Checking…" : "Run authenticated audit"}
                    </button>
                    <span className="muted tiny">Read-only. Uses the same authenticated Nexus session as System Info.</span>
                  </div>
                  {nkrSessionAuditError ? <div style={{ color: "#ffb4b4", fontWeight: 850, marginBottom: 8 }}>{nkrSessionAuditError}</div> : null}
                  {nkrSessionAudit ? <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 7 }}>
                      <div style={{ border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, padding: 8 }}><b>Sessions</b><br />{nkrSessionAudit.sessionCount ?? 0}</div>
                      <div style={{ border: `1px solid ${nkrSessionAudit?.modeIsolation?.ok ? "rgba(80,255,180,.30)" : "rgba(255,90,90,.40)"}`, borderRadius: 8, padding: 8 }}><b>Mode isolation</b><br />{nkrSessionAudit?.modeIsolation?.ok ? "READY" : "BLOCKED"}</div>
                      <div style={{ border: `1px solid ${nkrSessionAudit?.strategistSignals?.complete ? "rgba(80,255,180,.30)" : "rgba(255,190,90,.40)"}`, borderRadius: 8, padding: 8 }}><b>Strategist signals</b><br />{nkrSessionAudit?.strategistSignals?.complete ? "COMPLETE" : "INCOMPLETE"}</div>
                      <div style={{ border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, padding: 8 }}><b>Transactions sent</b><br />{nkrSessionAudit.transactionsSent ?? 0}</div>
                    </div>
                    {(nkrSessionAudit.sessions || []).map((row, idx) => <details key={`${row?.chainId || "?"}:${row?.sessionId || idx}`} open style={{ border: `1px solid ${row?.locked ? "rgba(80,255,180,.24)" : "rgba(255,90,90,.35)"}`, borderRadius: 8, padding: "6px 8px" }}>
                      <summary style={{ cursor: "pointer", fontWeight: 900 }}>{row?.chain || row?.chainId} · Session #{row?.sessionId} · {row?.mode || "?"} · {row?.locked ? "LOCKED" : "NOT LOCKED"}</summary>
                      <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.55 }}>
                        Status: {row?.status || "—"}<br />
                        Config key: {row?.sessionConfigKey || "—"}<br />
                        Observation: {row?.observationWindow ?? "—"}<br />
                        Profit mode: {row?.profitMode || "—"}<br />
                        Period: {row?.periodDays ?? "—"} days<br />
                        Max assets: {row?.maxActiveAssets ?? "—"}<br />
                        Max capital/asset: {row?.maxCapitalPerAssetPct ?? "—"}%<br />
                        Budget: {row?.budgetUsd ?? "—"}<br />
                        Entry threshold: {row?.modeThreshold ?? "—"}
                      </div>
                    </details>)}
                    <details style={{ border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, padding: "6px 8px" }}>
                      <summary style={{ cursor: "pointer", fontWeight: 900 }}>Signal coverage and entry pipeline</summary>
                      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 10, margin: "6px 0 0" }}>{JSON.stringify({ modeIsolation: nkrSessionAudit.modeIsolation, strategistSignals: nkrSessionAudit.strategistSignals, entryPipeline: nkrSessionAudit.entryPipeline }, null, 2)}</pre>
                    </details>
                  </div> : <div className="muted tiny">Run the audit to verify that every running session keeps its own mode and receives the full Strategist signal set.</div>}
                </details>

                <details style={{
                  gridColumn: "span 8",
                  border: "1px solid rgba(68,255,180,0.20)",
                  borderRadius: 10,
                  padding: 10,
                  background: "rgba(0,255,140,0.04)",
                }}>
                  <summary style={{ cursor: "pointer", fontWeight: 900 }}>Module Status ({moduleRows.length})</summary>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 7 }}>
                    {moduleRows.map((row, idx) => {
                      const name = row?.name || row?.module || row?.key || row?.id || `Module ${idx + 1}`;
                      const status = row?.status || row?.state || row?.mode || row?.value || "unknown";
                      const note = row?.note || row?.description || row?.detail || row?.policy || row?.stage || "";
                      const t = statusTone(status);
                      return (
                        <div
                          key={`${name}-${idx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 8,
                            alignItems: "center",
                            border: `1px solid ${t.border}`,
                            borderRadius: 8,
                            padding: "7px 8px",
                            background: t.bg,
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 900 }}>{name}</div>
                            {note ? <div style={{ fontSize: 11, opacity: 0.72, marginTop: 2 }}>{String(note)}</div> : null}
                          </div>
                          <div style={{ color: t.color, fontWeight: 900, whiteSpace: "nowrap", fontSize: 12 }}>
                            {t.dot} {String(status).toUpperCase()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>

                <div style={{ gridColumn: "span 12", marginTop: 2, border: "1px solid rgba(141,232,255,0.28)", borderRadius: 10, padding: 10, background: "rgba(141,232,255,0.045)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                      <b>EVM Token Allowlist · Owner Review</b>
                      <div className="muted" style={{ marginTop: 3, fontSize: 10 }}>Watchlist-Token werden automatisch geprüft. Nur unklare Fälle erscheinen in deiner aufklappbaren Prüfliste.</div>
                    </div>
                    <button type="button" className="miniBtn" disabled={!!evmRegistryBusy} onClick={_scanEvmRegistry}>{evmRegistryBusy === "scan" ? "Scanning..." : "Scan all Watchlists"}</button>
                  </div>
                  {(() => {
                    const reg = systemInfoStatus?.evmTokenRegistry || {};
                    const pending = Array.isArray(reg?.pending) ? reg.pending : [];
                    const blocked = Array.isArray(reg?.blocked) ? reg.blocked : [];
                    const approved = Array.isArray(reg?.approved) ? reg.approved : [];
                    // BUILD389: group by symbol → chains (LINK → ETH, BNB, POL)
                    const approvedGrouped = Array.isArray(reg?.approvedGrouped) && reg.approvedGrouped.length
                      ? reg.approvedGrouped
                      : (() => {
                          const map = {};
                          for (const x of approved) {
                            const sym = String(x?.symbol || "?").toUpperCase();
                            if (!map[sym]) map[sym] = { symbol: sym, chains: [] };
                            const ck = String(x?.chain_key || x?.chain_id || "");
                            if (ck && !map[sym].chains.includes(ck)) map[sym].chains.push(ck);
                          }
                          return Object.values(map).map((g) => ({
                            ...g,
                            label: g.chains.length ? `${g.symbol} → ${g.chains.join(", ")}` : g.symbol,
                          }));
                        })();
                    const liveChains = Array.isArray(reg?.chains) ? reg.chains.join(", ") : "ETH, BNB, POL";
                    const Row = ({ token, blockedRow = false }) => {
                      const risk = token?.risk || {}; const route = token?.route || {};
                      const keyBase = `${token?.chain_id}-${token?.token_address}`;
                      return (
                        <div key={keyBase} style={{ border: "1px solid rgba(255,255,255,0.10)", borderRadius: 9, padding: 8, display: "grid", gridTemplateColumns: "minmax(130px,0.8fr) minmax(220px,1.6fr) minmax(210px,1.4fr) auto", gap: 8, alignItems: "center", fontSize: 10 }}>
                          <div><b>{token?.symbol || "?"}</b> · {token?.chain_key || token?.chain_id}<div className="muted">{token?.name || ""}</div></div>
                          <div style={{ wordBreak: "break-all" }}>{token?.token_address}<div className="muted">GoPlus: {risk?.rawAvailable ? "checked" : "pending"} · sell tax {risk?.sellTax == null ? "—" : `${(Number(risk.sellTax) * 100).toFixed(2)}%`}</div></div>
                          <div><span style={{ color: blockedRow ? "#ffb4b4" : "#ffe08a", fontWeight: 900 }}>{token?.status}</span><div className="muted">{toUserFacingMessage(token?.decision_reason, "")}</div><div className="muted">Router {route?.routerConfigured ? "bereit" : "fehlt"} · Markt {route?.quoteVerified ? "geprüft" : "noch unklar"}</div></div>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            {!blockedRow ? <button type="button" className="miniBtn" disabled={!!evmRegistryBusy} onClick={() => _decideEvmToken(token, "APPROVE")}>{evmRegistryBusy === `APPROVE-${keyBase}` ? "Saving..." : "OK / Freigeben"}</button> : null}
                            <button type="button" className="miniBtn" disabled={!!evmRegistryBusy} onClick={() => _decideEvmToken(token, "RECHECK")}>Recheck</button>
                            <button type="button" className="miniBtn" disabled={!!evmRegistryBusy} onClick={() => _decideEvmToken(token, "REJECT")}>Ablehnen</button>
                          </div>
                        </div>
                      );
                    };
                    return (
                      <div style={{ marginTop: 9, display: "grid", gap: 8 }}>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10 }}>
                          <b>Zu prüfen: {pending.length}</b>
                          <b>Blockiert: {blocked.length}</b>
                          <b>Freigegeben: {approvedGrouped.length} Symbole ({approved.length} Contracts)</b>
                          <span className="muted">Live-Chains: {liveChains}</span>
                        </div>
                        {pending.length ? (
                          <details>
                            <summary style={{ cursor: "pointer", color: "#ffe08a", fontWeight: 900, padding: "5px 0" }}>
                              Manuell zu prüfen ({pending.length})
                            </summary>
                            <div style={{ display: "grid", gap: 6, marginTop: 7, maxHeight: pending.length > 5 ? 390 : "none", overflowY: pending.length > 5 ? "auto" : "visible", paddingRight: pending.length > 5 ? 5 : 0 }}>
                              {pending.map((token) => <Row key={`p-${token.chain_id}-${token.token_address}`} token={token} />)}
                            </div>
                          </details>
                        ) : <div className="muted" style={{ fontSize: 10 }}>Keine Token warten auf deine Prüfung.</div>}
                        {blocked.length ? <details><summary style={{ cursor: "pointer", color: "#ffb4b4", fontWeight: 900 }}>Blockiert / auffällig ({blocked.length})</summary><div style={{ display: "grid", gap: 6, marginTop: 7, maxHeight: blocked.length > 5 ? 390 : "none", overflowY: blocked.length > 5 ? "auto" : "visible", paddingRight: blocked.length > 5 ? 5 : 0 }}>{blocked.map((token) => <Row key={`b-${token.chain_id}-${token.token_address}`} token={token} blockedRow />)}</div></details> : null}
                        {approvedGrouped.length ? (
                          <details>
                            <summary style={{ cursor: "pointer", color: "#8dffd0", fontWeight: 900 }}>
                              Automatisch oder manuell freigegeben ({approvedGrouped.length})
                            </summary>
                            <div style={{ marginTop: 7, fontSize: 10, maxHeight: 220, overflowY: "auto", display: "grid", gap: 4 }}>
                              {approvedGrouped.map((g) => (
                                <div key={`ag-${g.symbol}`} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                                  <b style={{ color: "#eafff5", minWidth: 56 }}>{g.symbol}</b>
                                  <span className="muted">→ {(g.chainKeys || g.chains || []).join(", ") || "—"}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </div>
                    );
                  })()}
                  {evmRegistryMsg ? <div style={{ marginTop: 8, fontSize: 10, color: evmRegistryMsg.includes("failed") ? "#ffb4b4" : "#8de8ff", wordBreak: "break-word" }}>{evmRegistryMsg}</div> : null}
                  <div className="muted" style={{ marginTop: 7, fontSize: 9 }}>Mit „OK / Freigeben“ bestätigst du nur einen unklaren Einzelfall. Automatisch bestandene Token erscheinen nicht in dieser Prüfliste.</div>
                </div>

                <div style={{ gridColumn: "span 4", gridRow: "span 2", marginTop: 2, border: "1px solid rgba(68,255,180,0.24)", borderRadius: 10, padding: 10, background: "rgba(0,255,140,0.045)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <b>CoreVault V5 Status</b>
                    <span style={{ color: systemInfoStatus?.liveExecutionReadiness?.status === "READY" ? "#8dffd0" : "#ffe08a", fontWeight: 900 }}>
                      {systemInfoStatus?.liveExecutionReadiness?.status || "CHECKING"}
                    </span>
                  </div>
                  <div className="muted" style={{ marginTop: 5, fontSize: 11 }}>
                    Active path: Privy user wallet → CoreVault V5 session → backend-controlled dynamic execution.
                  </div>
                  {systemInfoStatus?.liveExecutionReadiness?.status !== "READY" ? (
                    <div style={{ marginTop: 7, padding: "7px 9px", borderRadius: 8, border: "1px solid rgba(255,196,70,0.28)", background: "rgba(255,196,70,0.07)", color: "#ffd978", fontSize: 11, fontWeight: 800 }}>
                      Live execution remains disabled until the CoreVault V5 and Privy checks below are cleared.
                    </div>
                  ) : null}
                  {(() => {
                    const c = systemInfoStatus?.liveExecutionReadiness?.checks || {};
                    const ok = (value) => value ? "READY 🟢" : "NOT READY 🟡";
                    const solvencyRows = Object.entries(c?.solvency || {});
                    const solvencyState = c.solvencyReadOk !== true
                      ? { value: null, display: "UNKNOWN ⚪" }
                      : c.solvent === true
                        ? { value: true, display: "READY 🟢" }
                        : { value: false, display: "NOT SOLVENT 🔴" };
                    const rows = [
                      ["Privy execution service", c.privyAppConfigured && c.tradingSignerConfigured && c.tradingPolicyConfigured],
                      ["CoreVault V5 connected", c.vaultConnected],
                      ["Vault paused", c.vaultPaused === false, c.vaultPaused ? "YES 🔴" : "NO 🟢"],
                      ["Vault solvent", solvencyState.value, solvencyState.display],
                      ["USDC settlement/execution", c.usdcConfigured && c.usdcExecutionEnabled && c.usdcSettlementToken && c.usdcDecimalsCorrect],
                      ["Uniswap router", c.routerHasCode],
                      ["Uniswap quoter", c.quoterHasCode && c.quotePathWorks],
                      ["System mapping NKR / Trader / Grid", c.systemIdConfirmed],
                    ];
                    return (
                      <div style={{ display: "grid", gap: 4, marginTop: 8, fontSize: 11 }}>
                        {rows.map(([label, value, display]) => {
                          const tone = value === true ? "#8dffd0" : value === false ? "#ff9b9b" : "#d7dde8";
                          return (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                              <span>{label}</span>
                              <b style={{ color: tone, textAlign: "right" }}>{display || ok(value)}</b>
                            </div>
                          );
                        })}
                        {solvencyRows.length ? (
                          <details style={{ marginTop: 4 }}>
                            <summary style={{ cursor: "pointer", fontWeight: 800, color: "#8de8ff" }}>Solvency details ({solvencyRows.length})</summary>
                            <div style={{ display: "grid", gap: 5, marginTop: 6 }}>
                              {solvencyRows.map(([symbol, row]) => (
                                <div key={symbol} style={{ border: "1px solid rgba(255,255,255,0.10)", borderRadius: 7, padding: "6px 7px" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                    <b>{symbol}</b>
                                    <b style={{ color: row?.readOk !== true ? "#d7dde8" : row?.solvent ? "#8dffd0" : "#ff9b9b" }}>
                                      {row?.readOk !== true ? "READ FAILED" : row?.solvent ? "SOLVENT" : "NOT SOLVENT"}
                                    </b>
                                  </div>
                                  {row?.readOk === true ? (
                                    <div className="muted" style={{ marginTop: 3 }}>
                                      Assets {Number(row?.assets || 0).toLocaleString(undefined, { maximumFractionDigits: 8 })} · Obligations {Number(row?.obligations || 0).toLocaleString(undefined, { maximumFractionDigits: 8 })} · Surplus {Number(row?.surplus || 0).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                                    </div>
                                  ) : (
                                    <div className="muted" style={{ marginTop: 3, wordBreak: "break-word" }}>{toUserFacingMessage(row?.error, "No valid solvency response")}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </div>
                    );
                  })()}
                  {Array.isArray(systemInfoStatus?.liveExecutionReadiness?.blockers) && systemInfoStatus.liveExecutionReadiness.blockers.length > 0 ? (
                    <details style={{ marginTop: 9 }}>
                      <summary style={{ cursor: "pointer", fontWeight: 800, color: "#ffe08a" }}>Current CoreVault blockers ({systemInfoStatus.liveExecutionReadiness.blockers.length})</summary>
                      <div style={{ marginTop: 7, display: "grid", gap: 5 }}>
                        {systemInfoStatus.liveExecutionReadiness.blockers.map((blocker, idx) => (
                          <div key={`${blocker}-${idx}`} style={{ display: "grid", gridTemplateColumns: "18px 1fr", gap: 6, alignItems: "start", fontSize: 11, color: "#ffd978" }}>
                            <span>⚠</span><span style={{ wordBreak: "break-word" }}>{String(blocker)}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <div style={{ marginTop: 9, fontSize: 11, color: "#8dffd0", fontWeight: 900 }}>CoreVault V5 is ready for Privy-only live execution.</div>
                  )}
                  <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(141,232,255,0.22)", background: "rgba(141,232,255,0.045)", fontSize: 10 }}>
                    Privy automation is provisioned by Nexus after wallet creation and an active subscription. No additional user approval, session signature or MetaMask connection is required.
                  </div>
                  {liveSetupMsg ? (
                    <div
                      style={{
                        marginTop: 7,
                        padding: "7px 9px",
                        borderRadius: 8,
                        border: "1px solid rgba(141,232,255,0.25)",
                        background: "rgba(141,232,255,0.045)",
                        fontSize: 10,
                        wordBreak: "break-word",
                      }}
                    >
                      {liveSetupMsg}
                    </div>
                  ) : null}

                  {systemInfoStatus?.liveExecutorStatus?.jobs?.[0] ? (
                    <div className="muted" style={{ marginTop: 7, fontSize: 10, wordBreak: "break-word" }}>
                      Latest CoreVault job: <b>{systemInfoStatus.liveExecutorStatus.jobs[0].status}</b> · {systemInfoStatus.liveExecutorStatus.jobs[0].stage}
                      {systemInfoStatus.liveExecutorStatus.jobs[0].error_text ? ` · ${systemInfoStatus.liveExecutorStatus.jobs[0].error_text}` : ""}
                    </div>
                  ) : null}
                </div>

                <div style={{ gridColumn: "span 8", marginTop: 2, border: "1px solid rgba(102,220,255,0.28)", borderRadius: 10, padding: 10, background: "rgba(40,180,255,0.045)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <b>CoreVault V5</b>
                    <span style={{ color: "#8de8ff", fontWeight: 900 }}>ON-CHAIN STATUS & RECOVERY</span>
                  </div>
                  <div className="muted" style={{ marginTop: 5, fontSize: 10, wordBreak: "break-all" }}>CoreVault: {CORE_VAULT_ADDRESS_BY_CHAIN[String((systemInfoStatus?.active_chain || systemInfoStatus?.activeChain || "ETH")).toUpperCase()] || "not configured"}</div>
                  <div className="muted" style={{ marginTop: 3, fontSize: 10 }}>V5 keeps backend-controlled dynamic execution and adds Native Coin support. Legacy token, route and wallet configuration panels have been removed.</div>

                  <div style={{ marginTop: 9, padding: 9, borderRadius: 9, border: "1px solid rgba(141,232,255,0.25)", background: "rgba(141,232,255,0.045)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <b style={{ fontSize: 11 }}>Admin signing wallet</b>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button type="button" className="miniBtn" disabled={ownerAdminWalletStatus.loading || !!ownerAdminBusy} onClick={_connectSelectedOwnerAdminWallet}>{ownerAdminWalletStatus.loading ? "Connecting..." : "Connect & verify"}</button>
                        <button type="button" className="miniBtn" disabled={ownerAdminWalletStatus.loading || !!ownerAdminBusy} onClick={_refreshOwnerAdminWalletStatus}>Refresh</button>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 14, marginTop: 7, flexWrap: "wrap" }}>
                      <label style={{ fontSize: 11, cursor: "pointer" }}><input type="radio" name="ownerAdminWalletMode" value="METAMASK" checked={ownerAdminWalletMode === "METAMASK"} onChange={() => setOwnerAdminWalletMode("METAMASK")} /> MetaMask (Owner/Admin)</label>
                      <label style={{ fontSize: 11, cursor: "pointer" }}><input type="radio" name="ownerAdminWalletMode" value="PRIVY" checked={ownerAdminWalletMode === "PRIVY"} onChange={() => setOwnerAdminWalletMode("PRIVY")} /> Privy (Owner/Admin)</label>
                    </div>
                    <div style={{ marginTop: 7, display: "grid", gridTemplateColumns: "110px minmax(0,1fr)", gap: "3px 8px", fontSize: 10 }}>
                      <span className="muted">CoreVault owner</span><span style={{ wordBreak: "break-all" }}>{ownerAdminWalletStatus.owner || CORE_VAULT_OWNER_ADDRESS || "unknown"}</span>
                      <span className="muted">MetaMask</span><span style={{ wordBreak: "break-all" }}>{ownerAdminWalletStatus.metamask || "not connected"}</span>
                      <span className="muted">Privy</span><span style={{ wordBreak: "break-all" }}>{ownerAdminWalletStatus.privy || "not connected"}</span>
                    </div>
                    <div style={{ marginTop: 7, fontSize: 10, fontWeight: 900, color: ownerAdminWalletStatus.matches ? "#8dffd0" : "#ffd978" }}>
                      {ownerAdminWalletStatus.matches ? `READY: Selected ${ownerAdminWalletMode === "METAMASK" ? "MetaMask" : "Privy"} wallet is the CoreVault owner.` : `BLOCKED: Selected ${ownerAdminWalletMode === "METAMASK" ? "MetaMask" : "Privy"} wallet is not the CoreVault owner.`}
                    </div>
                    {ownerAdminWalletStatus.error ? <div style={{ marginTop: 4, fontSize: 10, color: "#ffb4b4", wordBreak: "break-word" }}>{ownerAdminWalletStatus.error}</div> : null}
                    <div className="muted" style={{ marginTop: 5, fontSize: 9 }}>This selector applies only to owner/admin actions. User sessions and automatic trading remain Privy-only.</div>
                  </div>

                  <details open style={{ marginTop: 9, border: "1px solid rgba(255,193,7,0.28)", borderRadius: 9, padding: 9, background: "rgba(255,193,7,0.045)" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 800, color: "#ffe08a" }}>On-chain sessions & recovery · NKR / Trader</summary>
                    <div className="muted" style={{ marginTop: 7, fontSize: 10 }}>
                      Reads real CoreVault sessions for this wallet on <b>NKR</b> and <b>Trader</b> only.
                      Grid is not listed here — Grid sells on target price or max-loss, not via session finalize.
                      Recovery appears only for empty CLOSING or expired empty sessions; healthy ACTIVE/PAUSED sessions are never offered for finalization.
                      Runtime <b>Diagnose</b> for NKR / Grid / Trader stays separate above.
                    </div>
                    <div style={{ marginTop: 8, display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                      <button type="button" className="miniBtn" disabled={!!ownerAdminBusy} onClick={_inspectCoreVaultSessions}>
                        {ownerAdminBusy === "CoreVault session scan" ? "Scanning..." : "Refresh on-chain sessions"}
                      </button>
                      <span className="muted" style={{ fontSize: 9 }}>ETH · BNB · POL · NKR · Trader · Grid excluded</span>
                    </div>
                    {coreVaultSessionPreview ? (
                      <div style={{ marginTop: 9, display: "grid", gap: 6 }}>
                        <div className="muted" style={{ fontSize: 9 }}>
                          Next IDs: ETH {coreVaultSessionPreview?.nextSessionIdByChain?.ETH ?? "—"} · BNB {coreVaultSessionPreview?.nextSessionIdByChain?.BNB ?? "—"} · POL {coreVaultSessionPreview?.nextSessionIdByChain?.POL ?? "—"}
                          {" · "}Found: {(coreVaultSessionPreview?.sessions || []).length}
                          {" "}(NKR {coreVaultSessionPreview?.counts?.NKR || 0}, Trader {coreVaultSessionPreview?.counts?.TRADER || 0})
                          {" · "}Recoverable: {(coreVaultSessionPreview?.candidates || []).length}
                          {" · "}Grid excluded
                        </div>
                        {(coreVaultSessionPreview?.sessions || []).length ? (coreVaultSessionPreview.sessions || []).slice().sort((a, b) => {
                          // BUILD433: session state is the primary sort key across ALL engines.
                          // ACTIVE NKR/Trader sessions must stay together at the top; engine is only a tie-breaker.
                          const rank = (x) => ({ ACTIVE: 0, PAUSED: 1, CLOSING: 2, ERROR: 3, FINALIZED: 9 }[String(x?.statusLabel || "").toUpperCase()] ?? 5);
                          const engRank = (x) => (String(x?.engine || "").toUpperCase() === "NKR" ? 0 : 1);
                          const ra = rank(a), rb = rank(b);
                          if (ra !== rb) return ra - rb;
                          const ea = engRank(a), eb = engRank(b);
                          if (ea !== eb) return ea - eb;
                          return Number(b?.sessionId || 0) - Number(a?.sessionId || 0);
                        }).map((s) => {
                          const engLabel = s.engineLabel || _coreVaultEngineLabel(s.engine);
                          const engColor = String(s.engine || "").toUpperCase() === "TRADER" ? "#c4b5fd" : "#9fe8ff";
                          return (
                          <div key={`core-session-${s.chainId || 0}-${s.engine || "UNKNOWN"}-${s.sessionId}`} style={{ border: `1px solid ${s.recoverable ? "rgba(255,193,7,0.42)" : "rgba(255,255,255,0.10)"}`, borderRadius: 8, padding: 8, background: s.recoverable ? "rgba(255,193,7,0.06)" : "rgba(255,255,255,0.02)", fontSize: 9 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                              <b>{engLabel} · {s.chain || "ETH"} · Session #{s.sessionId}</b>
                              <b style={{ color: s.recoverable ? "#ffe08a" : "#b9d8ce" }}>{s.statusLabel || `Status ${s.statusId}`}</b>
                            </div>
                            <div style={{ marginTop: 4, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px", wordBreak: "break-all" }}>
                              <span className="muted">System</span><span style={{ fontWeight: 900, color: engColor }}>{engLabel}</span>
                              <span className="muted">Owner</span><span>{s.owner}</span>
                              <span className="muted">Settlement token</span><span>{s.settlementToken}</span>
                              <span className="muted">Budget</span><span><b>{s.budget ?? s.budgetUnits}</b> <span className="muted">(decimals {s.settlementDecimals ?? "—"})</span></span>
                              <span className="muted">Settlement cash</span><span><b>{s.settlementCash ?? s.settlementCashUnits}</b></span>
                              <span className="muted">Raw budget units</span><span className="muted">{s.budgetUnits}</span>
                              <span className="muted">Raw cash units</span><span className="muted">{s.settlementCashUnits}</span>
                              <span className="muted">Open assets</span><span>{s.openAssetCount}</span>
                              <span className="muted">Raw contract status</span><span>{s.rawStatusId ?? s.statusId} ({s.statusLabel || "UNKNOWN"})</span>
                              <span className="muted">Recovery</span><span>{s.recoverable ? `READY · ${s.recoveryReason || "EMPTY SESSION"}` : Number(s.openAssetCount || 0) > 0 ? "BLOCKED · OPEN POSITION" : ["ACTIVE", "PAUSED"].includes(String(s.statusLabel || "").toUpperCase()) ? "NOT REQUIRED · LIVE SESSION" : "NOT REQUIRED"}</span>
                            </div>
                            {["ACTIVE", "PAUSED", "CLOSING"].includes(String(s.statusLabel || "").toUpperCase()) && Number(s.openAssetCount || 0) > 0 ? (
                              <div style={{ marginTop: 7, color: "#ffd978", fontSize: 9 }}>
                                Open assets detected. Exit must be completed by the {engLabel} engine before finalization.
                              </div>
                            ) : null}
                            {s.recoverable ? <div style={{ marginTop: 7 }}>
                              <button type="button" className="miniBtn" disabled={!!ownerAdminBusy || !!exactOrphanRecoveryBusyKey} onClick={() => _recoverStaleCoreVaultReservation(s.sessionId, s.engine, s.chainId, s.vault, s.chain)}>
                                {ownerAdminBusy === `Stale ${String(s.engine || "").toUpperCase()} recovery ${s.sessionId}` ? `Processing ${engLabel} session #${s.sessionId}...` : `Recover / Finalize ${engLabel} session #${s.sessionId}`}
                              </button>
                            </div> : null}
                            {!s.recoverable && ["ACTIVE", "PAUSED"].includes(String(s.statusLabel || "").toUpperCase()) ? (() => {
                              const ck = String(s.chain || ({ 1: "ETH", 56: "BNB", 137: "POL" }[Number(s.chainId)] || "")).toUpperCase();
                              const busyKey = `${String(s.engine || "").toUpperCase()}:${ck}:${Number(s.sessionId || 0)}`;
                              return <div style={{ marginTop: 7 }}>
                                <button type="button" className="miniBtn" disabled={!!ownerAdminBusy || !!exactOrphanRecoveryBusyKey} onClick={() => _recoverExactCoreVaultSession(s.sessionId, s.engine, ck, s.chainId)}>
                                  {exactOrphanRecoveryBusyKey === busyKey ? `Processing ${engLabel} session #${s.sessionId}...` : `Stop & Finalize exact ${engLabel} session #${s.sessionId}`}
                                </button>
                              </div>;
                            })() : null}
                            {coreVaultRecoveryJobs?.[`${String(s.engine || "").toUpperCase()}:${s.sessionId}`] ? (() => { const j = coreVaultRecoveryJobs[`${String(s.engine || "").toUpperCase()}:${s.sessionId}`]; return <div style={{ marginTop: 7, padding: 7, borderRadius: 7, background: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.10)", display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px", wordBreak: "break-all" }}>
                              <span className="muted">Job</span><span>{j.job_status || "—"}</span>
                              <span className="muted">Step</span><span>{j.step ?? 0}/5 · {j.step_label || "—"}</span>
                              <span className="muted">Receipt</span><span>{j.receipt_status || "—"}</span>
                              <span className="muted">startClosing tx</span><span>{j.start_closing_tx_hash || "—"}</span>
                              <span className="muted">finalize tx</span><span>{j.finalize_tx_hash || "—"}</span>
                              <span className="muted">Last contract error</span><span style={{ color: j.last_error ? "#ffb4b4" : "inherit" }}>{j.last_error || "—"}</span>
                            </div>; })() : null}
                          </div>
                          );
                        }) : <div className="muted" style={{ fontSize: 9 }}>No NKR or Trader CoreVault sessions found for this wallet.</div>}
                      </div>
                    ) : null}
                  </details>

                  {ownerAdminHash ? <div className="muted" style={{ marginTop: 7, fontSize: 9, wordBreak: "break-all" }}>Action hash: {ownerAdminHash}</div> : null}
                  {ownerAdminMsg ? <div style={{ marginTop: 7, fontSize: 10, wordBreak: "break-all", color: ownerAdminMsg.includes("failed") ? "#ffb4b4" : "#8dffd0" }}>{ownerAdminMsg}</div> : null}
                </div>

                <details style={{ gridColumn: "span 8", border: "1px solid rgba(187,134,252,0.32)", borderRadius: 10, padding: 10, background: "rgba(187,134,252,0.055)" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 900 }}>Privy Trading Policy · Owner only</summary>
                  <div style={{ marginTop: 8, fontSize: 11 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <b>{privyPolicyPreview?.policy?.name || "Existing trading policy"}</b>
                        <div className="muted" style={{ marginTop: 2, wordBreak: "break-all" }}>Policy ID: {privyPolicyPreview?.policy?.id || "Load preview to read"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <button type="button" className="miniBtn" disabled={!!privyPolicyBusy} onClick={_loadPrivyPolicyCatalog}>
                          Load 10-chain catalog
                        </button>
                        <button type="button" className="miniBtn" disabled={!!privyPolicyBusy} onClick={_loadPrivyPolicyPreview}>
                          {privyPolicyBusy === "preview" ? "Reloading..." : `Reload & Preview ${_privyPolicyChainLabel(privyPolicyChainId)} Rules`}
                        </button>
                      </div>
                    </div>
                    <div className="muted" style={{ marginTop: 7 }}>
                      Top-10 EVM chains. Select a chain, preview targets (Vault / Router / WNative / USDC / USDT), then confirm. Later vaults (Base, Arbitrum, …) appear here as soon as addresses are configured — no code change needed.
                    </div>
                    {(privyPolicyCatalog || []).length ? (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 900, marginBottom: 6, color: "#ffffff" }}>Chains ({privyPolicyCatalog.length})</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
                          {privyPolicyCatalog.map((c) => {
                            const active = Number(c.chainId) === Number(privyPolicyChainId);
                            const avail = (c.components || []).filter((x) => x.available).length;
                            return (
                              <button
                                key={c.chainId}
                                type="button"
                                className="miniBtn"
                                disabled={!!privyPolicyBusy}
                                onClick={() => { setPrivyPolicyChainId(Number(c.chainId)); setPrivyPolicyPreview(null); setPrivyPolicyMsg(""); }}
                                style={{
                                  textAlign: "left",
                                  border: active ? "1px solid rgba(0,255,136,0.55)" : "1px solid rgba(255,255,255,0.12)",
                                  background: active ? "rgba(0,255,136,0.10)" : "rgba(0,0,0,0.18)",
                                  fontWeight: active ? 900 : 600,
                                  color: "#f2fffa",
                                }}
                              >
                                <div style={{ color: "#ffffff", fontWeight: 900 }}>{c.key}</div>
                                <div style={{ fontSize: 9, color: "rgba(220,255,240,0.82)" }}>{c.name}</div>
                                <div style={{ fontSize: 9, marginTop: 3, color: c.vaultConfigured ? "#8dffd0" : "#ffd978" }}>
                                  {c.vaultConfigured ? `Vault · ${avail} targets` : `No vault yet · ${avail} targets`}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {(() => {
                          const sel = privyPolicyCatalog.find((c) => Number(c.chainId) === Number(privyPolicyChainId));
                          if (!sel) return null;
                          return (
                            <div style={{ marginTop: 8, border: "1px solid rgba(187,134,252,0.22)", borderRadius: 8, padding: 8 }}>
                              <div style={{ fontWeight: 900 }}>{sel.key} · components for policy</div>
                              <div style={{ marginTop: 5, display: "grid", gap: 4 }}>
                                {(sel.components || []).map((comp) => (
                                  <div key={comp.id} className="muted" style={{ fontSize: 10, wordBreak: "break-all" }}>
                                    <b style={{ color: comp.available ? "#8dffd0" : "#ffb4b4" }}>{comp.title}</b>
                                    {" · "}
                                    {comp.available ? comp.address : "not configured yet"}
                                  </div>
                                ))}
                              </div>
                              {sel.cfgError ? <div style={{ marginTop: 4, color: "#ffb4b4", fontSize: 10 }}>{sel.cfgError}</div> : null}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="muted" style={{ marginTop: 7, fontSize: 10 }}>
                        Click <b>Load 10-chain catalog</b> to list ETH, BNB, POL, Base, Arbitrum, Optimism, Avalanche, Linea, Scroll, zkSync.
                        {privyPolicyCatalogMsg ? ` · ${privyPolicyCatalogMsg}` : ""}
                      </div>
                    )}
                    <div className="muted" style={{ marginTop: 7 }}>
                      Uses existing server ENV: PRIVY_APP_ID, PRIVY_APP_SECRET, PRIVY_TRADING_POLICY_ID, PRIVY_TRADING_KEY_QUORUM_ID and PRIVY_TRADING_AUTHORIZATION_PRIVATE_KEY. Secrets are never sent to the browser.
                    </div>
                    {privyPolicyPreview ? <>
                      <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 6 }}>
                        {Object.entries(privyPolicyPreview.environment || {}).map(([key, value]) => <div key={key} style={{ border: "1px solid rgba(255,255,255,.10)", borderRadius: 7, padding: 6 }}>
                          <span className="muted">{key}</span><br /><b style={{ color: value ? "#8dffd0" : "#ff9f9f" }}>{value ? "READY" : "MISSING"}</b>
                        </div>)}
                      </div>
                      <div className="muted" style={{ marginTop: 7, fontSize: 10 }}>Live reload: {privyPolicyPreview.source || "server"} · {privyPolicyPreview.ts ? new Date(Number(privyPolicyPreview.ts) * 1000).toLocaleString() : "now"}</div>
                      <div style={{ marginTop: 9, fontWeight: 900 }}>{_privyPolicyChainLabel(privyPolicyChainId)} targets to add ({(privyPolicyPreview.toAdd || []).length})</div>
                      <div style={{ marginTop: 5, display: "grid", gap: 5 }}>
                        {(privyPolicyPreview.toAdd || []).map((rule) => <div key={rule.name} style={{ border: "1px solid rgba(187,134,252,.24)", borderRadius: 7, padding: 7 }}>
                          <b>{rule.name}</b><div className="muted" style={{ wordBreak: "break-all", marginTop: 2 }}>{rule.label}: {rule.target}</div>
                        </div>)}
                        {!(privyPolicyPreview.toAdd || []).length ? <div style={{ color: "#8dffd0", fontWeight: 900 }}>All selected-chain targets are already present.</div> : null}
                      </div>
                      {(privyPolicyPreview.alreadyPresent || []).length ? <div className="muted" style={{ marginTop: 7 }}>Already present: {(privyPolicyPreview.alreadyPresent || []).map((r) => r.label).join(", ")}</div> : null}
                      {(privyPolicyPreview.requested || []).length ? <details style={{ marginTop: 7 }}><summary style={{ cursor: "pointer", fontWeight: 800 }}>Loaded targets ({privyPolicyPreview.requested.length})</summary><div style={{ marginTop: 5, display: "grid", gap: 4 }}>{privyPolicyPreview.requested.map((r) => <div key={`${r.label}:${r.target}`} className="muted" style={{ wordBreak: "break-all" }}><b>{r.label}</b> · {r.target}</div>)}</div></details> : null}
                      <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
                        <button type="button" className="miniBtn" disabled={!!privyPolicyBusy || !(privyPolicyPreview.toAdd || []).length} onClick={_applyPrivyPolicyPreview}>
                          {privyPolicyBusy === "apply" ? "Applying signed update..." : `Confirm & Add ${_privyPolicyChainLabel(privyPolicyChainId)} Rules`}
                        </button>
                      </div>
                    </> : null}
                    {privyPolicyMsg ? <div style={{ marginTop: 8, color: /failed|missing|error/i.test(privyPolicyMsg) ? "#ffb4b4" : "#8dffd0", wordBreak: "break-word" }}>{privyPolicyMsg}</div> : null}
                  </div>
                </details>


                <details style={{ gridColumn: "span 8", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: 10 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 800, opacity: 0.82 }}>Raw Build Payload (debug)</summary>
                  <pre style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    background: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    padding: 8,
                    marginTop: 6,
                    maxHeight: 180,
                    overflow: "auto",
                  }}>
                    {JSON.stringify({ buildInfo: buildInfo || { error: "build-info not loaded" }, shadowHealth: shadowHealth || { error: "shadow health not loaded" }, systemInfoStatus: systemInfoStatus || { error: "system-info-owner-panel not loaded" }, shadowReadiness: shadowReadiness || { error: "shadow-readiness-check not loaded" } }, null, 2)}
                  </pre>
                </details>
              </div>
            </div>

            <button
              type="button"
              className="nexus-disclaimer-close"
              onClick={() => setShowSystemInfo(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showDisclaimer && (
        <div
          className="nexus-disclaimer-overlay"
          onClick={() => setShowDisclaimer(false)}
        >
          <div
            className="nexus-disclaimer-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nexus-disclaimer-title">DISCLAIMER</div>

            <div className="nexus-disclaimer-content">
              <h3>EN</h3>
              <p>
                Nexus Analyt provides AI-assisted analytics, market intelligence
                and research tools only. No financial advice is provided.
                Cryptocurrency trading involves risk and users are fully
                responsible for their own decisions. No profits are guaranteed.
              </p>

              <h3>DE</h3>
              <p>
                Nexus Analyt stellt ausschliesslich KI-gestützte Analyse-, Markt-
                und Research-Tools bereit. Es handelt sich nicht um
                Finanzberatung. Der Handel mit Kryptowährungen ist risikoreich.
                Nutzer handeln eigenverantwortlich. Gewinne werden nicht
                garantiert.
              </p>
            </div>

            <button
              type="button"
              className="nexus-disclaimer-close"
              onClick={() => setShowDisclaimer(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// -------------------------
// Nexus Movement Quality Filter v2 (UI Layer)
// -------------------------
function getMovementQualityUi(score) {
  const n = Number(score || 0);
  if (n >= 75) return { label: "HIGH QUALITY", color: "#16c784", border: "rgba(22,199,132,.35)" };
  if (n >= 55) return { label: "MEDIUM", color: "#f5b300", border: "rgba(245,179,0,.35)" };
  return { label: "WEAK", color: "#ea3943", border: "rgba(234,57,67,.35)" };
}
